/**
 * 바이브 코딩(말로 코딩) 1단계 — 한국어 문장 → 스크래치 블록 "투명한 번역기".
 * 설계: docs/ANALYSIS_vibe_coding_2026-09-23.md
 *
 * ⭐ 원칙
 *  - 유료 API 없음. 전부 브라우저 안에서 처리하고 학생 문장을 서버로 보내지 않는다.
 *  - 블록은 여기 적힌 "검증된 조각"으로만 조립한다 → 깨진 블록이 나올 수 없다(LLM 환각 없음).
 *  - 말의 어느 부분이 어느 블록이 됐는지(segments)와, 번역기가 대신 정한 것(notes)을 항상 돌려준다.
 *    학생이 그걸 보면서 "말 → 블록" 대응을 배우는 게 이 기능의 교육 효과다.
 *  - 못 알아들은 말은 버리지 않고 주석으로 남긴다(unparsed) → "이 부분은 직접 만들어 볼래요?"
 *
 * ⚠️ 정규식 규칙: lookbehind 금지(iPad Safari 16.3 이하에서 문법 오류로 에디터 전체가 죽는다).
 * ⚠️ CommonJS 로 쓴 이유: Node 에서 babel 없이 바로 시험(test/vibe/run-corpus.js). webpack 은 CJS 도 import 한다.
 */
'use strict';

// ─────────────────────────────── 숫자·보조 ───────────────────────────────
const RE_NUM = '-?\\d+(?:\\.\\d+)?';
const KNUM = {한: 1, 하나: 1, 두: 2, 둘: 2, 세: 3, 셋: 3, 네: 4, 넷: 4, 다섯: 5, 여섯: 6, 일곱: 7, 여덟: 8, 아홉: 9, 열: 10, 스무: 20, 스물: 20, 반: 0.5, 일: 1, 삼: 3, 사: 4, 오: 5, 육: 6, 칠: 7, 팔: 8, 구: 9, 십: 10};
const RE_KNUM = '한|하나|두|둘|세|셋|네|넷|다섯|여섯|일곱|여덟|아홉|열|스무|스물';
// 한글 수사는 "번·초·칸" 같은 단위가 반드시 뒤따르는 곳에서만 쓴다("세게", "네모" 오인 방지)
const RE_ANY = `${RE_NUM}|${RE_KNUM}`;
// 초 앞에서만: 한자어 수사("삼초")와 "반초"까지. "이"(2)는 조사와 헷갈려서 뺐다.
const RE_SINO = '일|삼|사|오|육|칠|팔|구|십';
const RE_SEC = `${RE_NUM}|${RE_KNUM}|${RE_SINO}|반`;
// 한자어 수 "삼십", "백팔십", "사십오" → 숫자 (도·초 앞에서만 쓰인다)
const SINO_D = {일: 1, 이: 2, 삼: 3, 사: 4, 오: 5, 육: 6, 칠: 7, 팔: 8, 구: 9};
const sinoNum = t => {
    if (!/^[일이삼사오육칠팔구십백]+$/.test(t)) return null;
    let total = 0;
    let cur = 0;
    for (const ch of t) {
        if (ch === '십') { total += (cur || 1) * 10; cur = 0; } else if (ch === '백') { total += (cur || 1) * 100; cur = 0; } else cur = SINO_D[ch];
    }
    return total + cur;
};
const RE_SINO2 = '(?=[일이삼사오육칠팔구십백])(?:[일이삼사오육칠팔구]?백)?(?:[이삼사오육칠팔구]?십)?[일이삼사오육칠팔구]?';
const toNum = s => {
    if (s === undefined || s === null || s === '') return null;
    const t = String(s).replace(/\s+/g, '');
    if (Object.prototype.hasOwnProperty.call(KNUM, t)) return KNUM[t];
    if (sinoNum(t) !== null) return sinoNum(t);
    const n = Number(t);
    return isFinite(n) ? n : null;
};
const fmt = n => String(Math.round(n * 1000) / 1000);

// 막연한 정도 표현 → 기본값. 번역기가 대신 정했다는 걸 항상 notes 로 알린다(교육 장치 2).
const VAGUE = {빨리: 20, 빠르게: 20, 천천히: 5, 느리게: 5, 조금: 5, 살짝: 5, 약간: 5, 많이: 50, 멀리: 50, 엄청: 50, 아주: 50, 크게: 30, 작게: 5};
const RE_VAGUE = Object.keys(VAGUE).join('|');

// 받침 유무로 조사 고르기(은/는, 을/를, 으로/로)
const jongOf = w => {
    const c = String(w).trim().slice(-1).charCodeAt(0);
    return (c >= 0xAC00 && c <= 0xD7A3) ? (c - 0xAC00) % 28 : 0;
};
const hasBatchim = w => jongOf(w) !== 0;
const eul = w => `${w}${hasBatchim(w) ? '을' : '를'}`;
const euro = w => `${w}${jongOf(w) && jongOf(w) !== 8 ? '으로' : '로'}`; // ㄹ받침(8)은 "로"

// 이 말이 나오면 "앞으로 가·멈춰·돌아"를 큐브로이드 로봇(모터)으로 알아듣는다
// "반바퀴·한바퀴"의 바퀴는 도는 양이지 로봇 바퀴가 아니다(연습 세트 실측: "반바퀴만 돌려줘"가 모터가 됐다)
// 흔한 오타·소리 나는 대로 쓴 말(“앞으루 가라 로보트”)
const FIX_KO = [[/앞으루/g, '앞으로'], [/뒤루/g, '뒤로'], [/로보트|로봇트|로벗/g, '로봇'], [/오른쪽으루/g, '오른쪽으로'], [/왼쪽으루/g, '왼쪽으로'], [/깜박/g, '깜빡'], [/무한반북|무한번복/g, '무한반복'], [/야옹이(?=[가는를도의한와랑에]|\s|$)/g, '고양이'], [/(쪽|색|번|초|개|칸|도)이요(?=\s|$|[.!?])/g, '$1'], [/(\d)\s*도만/g, '$1도'], [/\s+(?:뿅|짠)(?=\s*$|\s*[.!?])/g, ' 보여줘'], [/\s*(?:이라는|라는)\s+(?=메시지|메세지|신호)/g, ' ']];
// "빨간 불도 켜줘" — '불도 켜'를 따로 흰 불로 한 번 더 켜던 실측(2026-09-24) → 색 불 켜기 하나로
FIX_KO.push([/(빨간|빨강|주황|노란|노랑|초록|파란|파랑|하늘|보라|분홍|흰|하얀)(색)?\s*불(빛)?도\s*(켜|켰)/g, '$1$2 불$3 $4']);
// "불빛 내기", "불빛을 내줘" — 불빛을 '낸다' = 켠다(2026-09-24 대표 데브 실측 "마스터 블록으로 불빛 내기"). '소리 내'는 그대로.
FIX_KO.push([/불빛(을|도)?\s*내(기|줘|주세요|자|봐|볼래|보자|요|게|고)?(?=\s|$|[.!?])/g, (m, j, e) => `불빛${j || ''} 켜${e || ''}`]);
// "마스터 블록으로 ~", "사운드 블록으로 ~" — 확장 이름은 어떤 블록을 쓸지 알려 주는 말(할 일은 뒤에 있음) → 떼고 알아듣는다
FIX_KO.push([/(?:^|\s)(?:큐브로이드\s*)?(?:사운드|마스터|LED|엘이디|근접\s*센서|라이트\s*터치\s*센서|라이트\s*터치|터치\s*센서|DC\s*모터)\s*(?:블록|블럭|확장)\s*(?:으로|로|을\s*(?:써서|이용해서|사용해서)|를\s*(?:써서|이용해서|사용해서)|에서|의)\s+/g, m => (/LED|엘이디/i.test(m) ? ' LED에 ' : ' 로봇 ')]); // 로봇 이야기라는 건 남긴다("사운드 블록으로 소리 내기"가 야옹이 되던 것)
// "LED 에 하트", "A 를 보여줘" — 영문 뒤 조사를 띄어 쓰면 규칙이 LED 를 못 잡아 '모르는 말'로 남았다(2026-10-16 직원 실측) → 붙인다
// 숫자 뒤는 안 붙인다: "위로 10 아래로 10 을 4번 반복"의 '을'은 앞 동작 전체를 받는 조사(붙였더니 아래 동작이 빠짐, 회귀 확인)
FIX_KO.push([/([A-Za-z])\s+(에서|에다가|에다|에|을|를|으로|로)(?=\s|$|[.,!?])/g, '$1$2']);
// "LED 말고 불빛 노란색" — LED 가 아니라 마스터 블록 불빛을 고르는 말(2026-10-20)
FIX_KO.push([/(?:^|\s)(?:LED|엘이디)\s*(?:말고|대신에?|아니고|아니라)\s*(?=마스터|불)/gi, ' ']);
// "주사위처럼 1부터 6까지 숫자 말해" — 주사위 = 무작위 수(차례로 세기 아님, 2026-10-22)
FIX_KO.push([/주사위\s*(?:처럼|같이)\s*(\d+)\s*(?:부터|에서)\s*(\d+)\s*(?:까지|사이(?:의|에서)?)\s*(?=숫자|수)/g, '$1부터 $2까지 랜덤 ']);
// "근접 센서에 손을 대면 하트와 소리"(교안 미션 2 말투) — 그림 이름만 말한 것을 "하트 보여주고 소리"로(2026-10-22)
FIX_KO.push([/(^|[:,]\s*|면\s+)(하트|웃는\s*얼굴|우는\s*얼굴|화난\s*얼굴|슬픈\s*얼굴|느낌표|물음표|음표|별|꽃)(?:와|과|랑|이랑|하고)\s*(?=소리|삐|경고음)/g, '$1$2 보여주고 ']);
const fixKo = s => FIX_KO.reduce((a, [re, to]) => a.replace(re, to), s);
const RE_ROBOT = /손(?:을|이)?\s*(?:가까이\s*)?(?:대면|가져가면|갖다\s*대면|다가가면|다가오면)|손(?:을|이)?\s*가까이|근접|운전|자동\s*문|삑|핸드폰으로\s*로봇|삐삐|삐\s*소리|경고음|로봇|로보트|모타|(?:빨간|초록|노란|파란)\s*불|자동차|자율\s*주행|모터|(?:^|[^한반두세네몇\s\d])\s*바퀴|주행|큐브로이드|LED|엘이디|불빛|램프|센서|장애물|버튼|밝기|어두|가로등|신호등|손전등|부저|버저|마스터|전진|후진|매트릭스|8\s*[xX×]\s*8|속도\s*\d|좌회전|우회전|(?:앞으로|뒤로)\s*\d+\s*초|무지개(?:색)?(?:으로)?\s*불|(?:^|\s)불(?:을|이|빛을)?\s*(?:켜|꺼|끄|바꿔|깜빡)|(?:회전|돌기|돌아|돌려)\s*\d+(?:\.\d+)?\s*초|\d+\s*초\s*(?:동안\s*)?(?:앞으로|뒤로)/i;

// ─────────────────────────────── 블록 조각 ───────────────────────────────
const num = (v, sh) => ({sh: sh || 'math_number', field: 'NUM', value: fmt(v)});
const txt = v => ({sh: 'text', field: 'TEXT', value: String(v)});
const menu = (sh, field, value) => ({sh, field, value});
const fld = (name, value) => ({[name]: {name, value}});
const act = (opcode, o) => Object.assign({t: 'act', opcode, inputs: {}, fields: {}}, o || {});
// 값 블록(동그란 블록)을 입력 칸에 끼우기 — 모양은 그대로(글자 칸/숫자 칸), 안에 값 블록이 들어간다
const inT = x => (x && typeof x === 'object' ? Object.assign(txt(''), {node: x}) : txt(x));
const inN = x => (x && typeof x === 'object' ? Object.assign(num(0), {node: x}) : num(x));
const V = name => act('data_variable', {varName: name, sig: name, label: name});
const join = (a, b) => act('operator_join', {inputs: {STRING1: inT(a), STRING2: inT(b)}, sig: 'join', label: '결합하기'});
const opN = (op, a, b) => act(op, {inputs: {NUM1: inN(a), NUM2: inN(b)}, sig: op, label: op});
const setVar = (name, v) => act('data_setvariableto', {inputs: {VALUE: inT(typeof v === 'number' ? fmt(v) : v)}, varName: name,
    sig: `${name}=${typeof v === 'number' ? fmt(v) : (v && v.sig) || v}`, label: `${eul(name)} ${typeof v === 'number' ? euro(fmt(v)) : `‘${(v && v.label) || v}’(으)로`} 정하기`});
const chVar = (name, v) => act('data_changevariableby', {inputs: {VALUE: num(v)}, varName: name, sig: `${name}${v >= 0 ? '+' : ''}${fmt(v)}`, label: `${eul(name)} ${fmt(v)}만큼 바꾸기`});
const sayOf = (x, secs, label) => act(secs ? 'looks_sayforsecs' : 'looks_say', {inputs: Object.assign({MESSAGE: inT(x)}, secs ? {SECS: num(secs)} : {}),
    sig: `${secs ? 'sayfor' : 'say'}(${label}${secs ? `,${fmt(secs)}` : ''})`, label: `‘${label}’${secs ? `을(를) ${fmt(secs)}초 동안` : ''} 말하기`});
// 이 블록들은 계속 되풀이해야 뜻이 된다 → '무한 반복' 표시(autoLoop 가 감싼다. '~하면서' 앞 동작도 같이 들어간다)
const loopAll = nodes => { nodes.forEach(n => { n.loop = true; }); return nodes; };
const waitS = sec => act('control_wait', {inputs: {DURATION: num(sec, 'math_positive_number')}, sig: `wait${fmt(sec)}`, label: `${fmt(sec)}초 기다리기`});

// ─────────────────────────────── 공통 패턴 ───────────────────────────────
const RE_EV = '(?:누르면|누를\\s*때|눌렀을\\s*때|누르는\\s*순간|눌리면|클릭하면|클릭할\\s*때|클릭했을\\s*때|클릭\\s*하면|터치하면|치면|칠\\s*때)(?:마다)?';
const RE_KEY = '(?<key>스[페패]이스\\s*바|스[페패]이스|space(?:bar)?|(?:위|윗|아래|밑|왼|오른)쪽?(?:\\s*(?:화살표|방향키|방향))?|아무(?:\\s*키(?:나|든)?|\\s*거나|거나)?|[a-z0-9](?![a-z]))';
const RE_TOUCH = '(?:닿았는가\\??\\s*(?:\\(?이\\)?라면|라면)?|닿으면|닿았을\\s*때|닿을\\s*때|닿는다면|닿았다면|부딪히면|부딪치면|부딪혔을\\s*때|만나면|충돌하면|박으면)(?:마다)?';
const RE_EDGE = '(?:벽|가장자리|화면\\s*끝|끝|모서리|벽면)';
const RE_MOUSE = '(?:마우스(?:\\s*포인터)?|커서|포인터)';
const REST = '[가-힣]*'; // 동사 활용 꼬리(움직"이고", 움직"여줘" …)

const keyValue = raw => {
    const s = String(raw || '').replace(/\s+/g, '').toLowerCase();
    if (/스[페패]이스|space/.test(s)) return 'space';
    if (/^(위|윗)/.test(s)) return 'up arrow';
    if (/^(아래|밑)/.test(s)) return 'down arrow';
    if (/^왼/.test(s)) return 'left arrow';
    if (/^오른/.test(s)) return 'right arrow';
    if (/^아무/.test(s)) return 'any';
    if (/^[a-z0-9]$/.test(s)) return s;
    return null;
};
const KEY_KO = {'space': '스페이스', 'up arrow': '위쪽 화살표', 'down arrow': '아래쪽 화살표', 'left arrow': '왼쪽 화살표', 'right arrow': '오른쪽 화살표', 'any': '아무'};
const keyKo = k => KEY_KO[k] || k;

const NOTE = {
    hat: '언제 시작할지 말하지 않아서 ‘깃발을 클릭했을 때’로 시작하게 했어요.',
    loop: '‘~하면’, ‘따라가기’, ‘튕기기’는 계속 확인해야 해서 ‘무한 반복하기’ 안에 넣었어요.',
    jump: '‘점프’는 올라가기, 기다리기, 내려오기 세 블록으로 나눴어요.',
    say: '무슨 말을 할지 알려 주세요. 예: ‘안녕’이라고 말해',
    color: '스크래치의 색깔 효과는 색 이름이 아니라 숫자로 바뀌어요. 원하는 색이 나올 때까지 숫자를 바꿔 보세요.',
    stop: '‘멈추기’ 블록 뒤에는 다른 블록을 붙일 수 없어서 뺐어요.',
    wait: '‘잠깐’은 1초로 했어요. 숫자로 말하면 정확해져요.',
    noSound: '이 스프라이트에는 소리가 없어요. 소리 탭에서 먼저 추가해 주세요.'
};
const noteDefault = (what, n) => `얼마나 ${what}지 말하지 않아서 ${euro(fmt(n))} 했어요. 숫자로 말하면 정확해져요.`;
const noteVague = (word, n) => `‘${word}’${hasBatchim(word) ? '은' : '는'} ${euro(fmt(n))} 정했어요. 숫자로 말하면 더 정확해져요.`;

// ─────────────────────────────── 의도 사전 ───────────────────────────────
// kind: hat(언제) · act(무엇) · cond(~하면) · repeat(N번) · forever(계속)
// res : 정규식 문자열(여러 개면 각각 따로 시도) · make(g, ctx, alt) → 결과 또는 null(쓰지 않음)
// (언어와 상관없는 블록 조각 — 한국어·영어가 함께 쓴다)
    // ═══════════════ 큐브로이드 로봇 — 도우미 ═══════════════
    // 규칙의 출처 = 파이썬 탭 예제(components/pythoncode-tab/pythoncode-example-data.js).
    // 학생들이 실제로 만드는 것들이고, 같은 프로그램을 파이썬으로도 볼 수 있다(알림에 예제 이름을 적는다).
    //  - 바퀴 두 개가 마주 보고 달려 있다 → 앞으로 = 1번 오른쪽 + 2번 왼쪽, 제자리 돌기 = 두 모터 같은 방향.
    const M1 = 'cubroiddcmotor01';
    const M2 = 'cubroiddcmotor02';
    const mNo = id => (id === M1 ? 1 : 2);
    const DIR_KO = {Right: '오른쪽으로 돌리기', Left: '왼쪽으로 돌리기'};
    const motor = (id, dir) => act(`${id}_dcMotorControl`, {fields: fld('INDEX', dir), sig: `m${mNo(id)}:${dir}`, label: `DC 모터 ${mNo(id)}번을 ${DIR_KO[dir]}`});
    const motorOff = id => act(`${id}_dcMotorStop`, {sig: `m${mNo(id)}:off`, label: `DC 모터 ${mNo(id)}번을 끄기`});
    const waitN = (sec, ask) => act('control_wait', Object.assign({inputs: {DURATION: num(sec, 'math_positive_number')}, sig: `wait${fmt(sec)}`, label: `${fmt(sec)}초 기다리기`}, ask ? {ask} : {}));
    const PAIR = {forward: ['Right', 'Left'], backward: ['Left', 'Right'], right: ['Right', 'Right'], left: ['Left', 'Left']};
    const drive = (kind, sec, stop, ask) => {
        const nodes = [motor(M1, PAIR[kind][0]), motor(M2, PAIR[kind][1])];
        if (sec !== null) nodes.push(waitN(sec, ask));
        if (stop) nodes.push(motorOff(M1), motorOff(M2));
        return nodes;
    };
    const stopBoth = () => [motorOff(M1), motorOff(M2)];
    const NOTE_CAR = '바퀴 두 개가 마주 보고 달려 있어서, 앞으로 가려면 1번 모터는 오른쪽·2번 모터는 왼쪽으로 돌려요(파이썬 예제와 같은 규칙).';
    const NOTE_SPIN = '제자리에서 돌 때는 두 모터를 같은 방향으로 돌려요. 도는 시간은 로봇마다 조금씩 달라서, 돌려 보고 초를 맞춰 보세요.';
    const COLOR_HEX = {빨간: '#ff0000', 빨강: '#ff0000', 초록: '#00ff00', 녹색: '#00ff00', 파란: '#0000ff', 파랑: '#0000ff', 노란: '#ffff00', 노랑: '#ffff00',
        하얀: '#ffffff', 흰: '#ffffff', 하양: '#ffffff', 보라: '#ff00ff', 분홍: '#ff66cc', 핑크: '#ff66cc', 주황: '#ff8800', 하늘: '#00ffff'};
    const HEX_KO = {'#ff0000': '빨간', '#00ff00': '초록', '#0000ff': '파란', '#ffff00': '노란', '#ffffff': '흰', '#ff00ff': '보라', '#ff66cc': '분홍', '#ff8800': '주황', '#00ffff': '하늘'};
    const RE_COLOR = Object.keys(COLOR_HEX).join('|');
    const colorOn = hex => act('cubroidmaster_cubroidMasterColorControl', {inputs: {COLOR: {sh: 'colour_picker', field: 'COLOUR', value: hex}}, sig: `light:${hex}`, label: `${HEX_KO[hex] || hex}색 불빛 켜기`});
    const colorOff = () => act('cubroidmaster_cubroidColorOffControl', {sig: 'light:off', label: '불빛 끄기'});
    const obstacle = () => act('cubroidproximity_isProximity', {sig: 'obstacle', label: '장애물이 감지되었는가?'});
    const button = () => act('cubroidlighttouch_isButtonPressed', {sig: 'button', label: '버튼이 눌러졌는가?'});
    const bright = (sym, n) => act(sym === '<' ? 'operator_lt' : 'operator_gt', {
        inputs: {OPERAND1: Object.assign(txt(''), {node: act('cubroidlighttouch_getBrightness', {sig: 'brightness', label: '밝기'})}), OPERAND2: txt(String(n))},
        sig: `bright${sym}${n}`, label: `밝기 ${sym} ${n}`
    });
    const NOTE_BRIGHT = '밝기 기준은 500이에요. 교실이 밝으면 숫자를 올려서 실험해 보세요(파이썬 예제 ‘스마트 가로등’과 같은 값).';
    const SOLFA = {도: '도 C(60)', 레: '레 D(62)', 미: '미 E(64)', 파: '파 F(65)', 솔: '솔 G(67)', 라: '라 A(69)', 시: '시 B(71)'};
    const noteBlock = (v, beats) => act('cubroidsound_soundBeatControl', {
        inputs: {SOUND: menu('cubroidsound_menu_cubroidSoundAction', 'cubroidSoundAction', v), BEATS: num(beats)},
        sig: `note:${v.split(' ')[0]}${/72/.test(v) ? '^' : ''}`, label: `${v} 음을 ${fmt(beats)}박자 연주하기`
    });
    const beep = kind => act('cubroidsound_errorSoundControl', {
        inputs: {ERROR_SOUND: menu('cubroidsound_menu_cubroidErrorSoundAction', 'cubroidErrorSoundAction', kind)},
        sig: `beep:${kind}`, label: `‘${kind}’ 소리 내기`
    });
    // LED 8×8 그림(확장 메뉴 값 그대로). 한국어 이름 → [메뉴 이름, 16진수]
    const LED_IMG = [
        [/웃는|웃음|스마일|미소/, 'Smile1', '0066660000427e3c'], [/행복|윙크/, 'Happy', '00006600423c0000'], [/우는|울상|눈물/, 'Cry', '0000e7c3003c4200'],
        [/화난|화가|화(?!살)/, 'Angry', '4224006666001824'], [/슬픈|슬픔/, 'Sad', '3c42a581bd81423c'], [/해님|태양|해/, 'Sun', '00281a7c3e581400'],
        [/달|자는|잠자는|졸린|잠/, 'Moon', '00381c0c0c1c3800'], [/네모|사각형/, 'Square', '007e7e7e7e7e7e00'], [/동그라미|원/, 'Circle', '003c7e7e7e7e3c00'],
        [/세모|삼각형/, 'Triangle', '0002060e1e3e7e00'], [/꽃/, 'Flower', '3c3cdbe7e7db3c3c'], [/하트|사랑/, 'Heart', '0066ffffff7e3c18'],
        [/별/, 'Star', '0018187e7e3c6600'], [/느낌표/, 'Exclamation', '00183c3c3c180018'], [/물음표/, 'Question', '003c666038180018'],
        [/음표/, 'Note1', '000c0a0808183830'], [/더하기|플러스/, 'Plus', '0018187e7e181800'], [/빼기|마이너스/, 'Minus', '0000007e7e000000'],
        [/앞\s*화살표/, 'Forward', '00183c5a18181800'], [/뒤\s*화살표/, 'Backward', '001818185a3c1800'],
        [/오른쪽\s*화살표/, 'Right', '0010207e7e201000'], [/왼쪽\s*화살표/, 'Left', '0008047e7e040800'], [/가득|꽉|전부/, 'Full', 'ffffffffffffffff']
    ];
    const RE_IMG = '윙크하는|윙크|웃는|웃음|스마일|미소|행복한|행복|자는|잠자는|졸린|우는|울상|눈물|화난|슬픈|슬픔|해님|태양|해|달|네모|사각형|동그라미|원|세모|삼각형|꽃|하트|사랑|별|느낌표|물음표|음표|더하기|플러스|빼기|마이너스|앞\\s*화살표|뒤\\s*화살표|오른쪽\\s*화살표|왼쪽\\s*화살표|가득|꽉';
    const LED_NUM = {1: '0018181c1818187e', 2: '003c6660300c067e', 3: '003c66603860663c', 4: '00303834327e3030', 5: '007e063e6060663c',
        6: '003c66063e66663c', 7: '007e663030181818', 8: '003c66663c66663c', 9: '003c66667c60663c', 0: '003c66766e66663c'};
    const LED_ABC = {A: '003c66667e666666', B: '003e66663e66663e', C: '003c66060606663c', D: '003e66666666663e', E: '007e06063e06067e', F: '007e06063e060606',
        G: '003c66060676663c', H: '006666667e666666', I: '003c18181818183c', J: '007830303036361c', K: '0066361e0e1e3666', L: '000606060606067e',
        M: '00c6eefed6c6c6c6', N: '00c6cedef6e6c6c6', O: '003c66666666663c', P: '003e6666663e0606', Q: '003c666666763c60', R: '003e66663e1e3666',
        S: '003c66063c60663c', T: '007e5a1818181818', U: '006666666666667c', V: '0066666666663c18', W: '00c6c6c6d6feeec6', X: '00c6c66c386cc6c6',
        Y: '006666663c181818', Z: '007e6030180c067e'};

// 바깥 모듈(vibe-aiext.js 등)이 규칙을 더하는 곳. f(def) 를 buildIntents 끝에서 부른다(언어별).
const EXTRA_INTENTS = {ko: [], en: []};
const registerIntents = (lang, f) => { (EXTRA_INTENTS[lang] = EXTRA_INTENTS[lang] || []).push(f); };
// 번역·고쳐 말하기 결과에 마지막으로 거는 후처리 f(ir, lang) — 예: 카메라·날씨 확장의 '시작/불러오기' 블록을 맨 앞에
const EXTRA_POST = [];
const registerPost = f => { EXTRA_POST.push(f); };
// AI·데이터 확장 opcode 접두사(확장 id) — 쓰면 확장을 불러온다(collectExtensions)
const AI_EXT_IDS = /^(?:cubroidweather|cubroidchart|cubroidChatgpt|cubroidDalle3|cubroidcolor|cubroidobject|facerecognition|facemesh2scratch|handpose2scratch|posenet2scratch|speech2scratch|ic2scratch|ml2scratch)$/;
const buildIntents = soundNames => {
    const I = [];
    const def = (id, cat, kind, res, make, opts) => I.push(Object.assign({
        id, cat, kind, make,
        res: [].concat(res).map(r => new RegExp(r, 'gi'))
    }, opts || {}));

    const LED_WORD = '(?<led>LED\\s*매트릭스|도트\\s*매트릭스|매트릭스|8\\s*[xX×]\\s*8|LED|엘이디|점판|전광판)';
    const CAR_WORD = '(?:(?:로봇\\s*카|로봇ㅇ|로보트|로봇|자동차|차|모터|모타|바퀴)(?:가|이|를|을|는|도)?\\s*)?';

    // ── 언제(모자 블록) ──
    def('flag', 'events', 'hat', [
        `(?:초록\\s*|녹색\\s*)?(?:깃발|깃털|시작\\s*버튼|플래그)(?:을|를|이)?\\s*${RE_EV}`,
        '(?:게임(?:이|을)?\\s*)?(?:시작하면|시작할\\s*때|시작했을\\s*때|시작하자마자|시작되면|시작될\\s*때|처음에|처음부터|실행하면|실행할\\s*때|실행했을\\s*때)'
    ], () => ({hat: act('event_whenflagclicked', {sig: 'flag', label: '깃발을 클릭했을 때'})}));

    def('key', 'events', 'hat',
        `(?:키보드(?:로|에서|의)?\\s*)?${RE_KEY}\\s*(?:키보드\\s*)?(?:키|버튼|화살표\\s*키|화살표|방향키)?\\s*(?:를|을|가|이)?\\s*${RE_EV}`,
        g => {
            const k = keyValue(g.key);
            if (!k) return null;
            return {hat: act('event_whenkeypressed', {fields: fld('KEY_OPTION', k), sig: `key:${k}`, label: `${keyKo(k)} 키를 눌렀을 때`})};
        });

    // "누르면"만 있으면 무엇을 누르는지 모른다 → 주어가 있을 때만 클릭으로 본다
    // (블라인드 시험 실측: "스패이스 누르면" 오타를 "스프라이트 클릭"으로 오역했다 — 틀린 번역이 제일 나쁘다)
    def('click', 'events', 'hat', [
        '(?:(?:고양이|스프라이트|캐릭터|나|이거|이것|얘|주인공|자기|자신|그림|마우스)(?:를|을|로)?\\s*)?(?:클릭하면|클릭할\\s*때|클릭했을\\s*때|클릭\\s*하면|(?<tch>터치하면))(?:마다)?',
        '(?:(?:고양이|스프라이트|캐릭터|이거|이것|얘|주인공|그림|마우스)(?:를|을|로)?|나를|날)\\s*(?:누르면|눌렀을\\s*때|누를\\s*때)(?:마다)?'
    ],
        (g, ctx) => (g.tch && ctx.robot ? null : {hat: act('event_whenthisspriteclicked', {sig: 'click', label: '이 스프라이트를 클릭했을 때'})}));

    // ── 이벤트: 무대 클릭 · 배경 바뀜 · 신호 받기 · 복제되었을 때 ──
    def('stageClick', 'events', 'hat', '무대(?:를|을)?\\s*(?:클릭하면|클릭할\\s*때|클릭했을\\s*때|터치하면|누르면|눌렀을\\s*때)(?:마다)?',
        () => ({hat: act('event_whenstageclicked', {sig: 'stageclick', label: '무대를 클릭했을 때'})}));
    def('backdropIs', 'events', 'hat', '배경이\\s*(?<nm>[^\\s]+?)(?:으로|로)\\s*(?:바뀌었을\\s*때|바뀌면|바뀔\\s*때)',
        g => ({hat: act('event_whenbackdropswitchesto', {fields: fld('BACKDROP', g.nm), sig: `backdropis:${g.nm}`, label: `배경이 ${euro(g.nm)} 바뀌었을 때`})}));
    def('bcastRecv', 'events', 'hat', '(?<nm>[^\\s\'"]+?)\\s*(?:신호|메시지)(?:를|을|가|이)?\\s*(?:받았을\\s*때|받으면|받을\\s*때|보내면|오면|왔을\\s*때)',
        g => ({hat: act('event_whenbroadcastreceived', {fields: {BROADCAST_OPTION: {name: 'BROADCAST_OPTION', value: g.nm, bcast: g.nm}},
            sig: `recv:${g.nm}`, label: `${g.nm} 신호를 받았을 때`})}));
    def('cloneStart', 'control', 'hat', '(?:복제(?:되었을|됐을|될)\\s*때|복제되면|복제본이\\s*(?:생기면|만들어지면))',
        () => ({hat: act('control_start_as_clone', {sig: 'clonestart', label: '복제되었을 때'})}));

    // 방향키로 움직이기 — 방향키 4개마다 스크립트를 하나씩(경쟁 제품 예시 첫 문장이기도 하다)
    const arrowMulti = (keys, ctx) => {
        if (ctx && ctx.robot) {
            const rk = (key, kind) => ({
                hat: act('event_whenkeypressed', {fields: fld('KEY_OPTION', key), sig: `key:${key}`, label: `${keyKo(key)} 키를 눌렀을 때`}),
                body: drive(kind, 0.3, true)
            });
            return {notes: ['키마다 0.3초 움직이고 멈추게 했어요. 계속 누르고 있으면 계속 움직여요.', NOTE_CAR], multi: [
                rk(keys[0], 'forward'), rk(keys[1], 'backward'), rk(keys[2], 'left'), rk(keys[3], 'right')
            ]};
        }
        const k = (key, op, input, v) => ({
            hat: act('event_whenkeypressed', {fields: fld('KEY_OPTION', key), sig: `key:${key}`, label: `${keyKo(key)} 키를 눌렀을 때`}),
            body: [act(op, {inputs: {[input]: num(v)}, sig: `${input === 'DX' ? 'x' : 'y'}${v >= 0 ? '+' : ''}${v}`, label: `${input === 'DX' ? 'x' : 'y'} 좌표를 ${v}만큼 바꾸기`})]
        });
        return {notes: ['키마다 스크립트를 하나씩, 모두 4개 만들었어요.'], multi: [
            k(keys[0], 'motion_changeyby', 'DY', 10), k(keys[1], 'motion_changeyby', 'DY', -10),
            k(keys[2], 'motion_changexby', 'DX', -10), k(keys[3], 'motion_changexby', 'DX', 10)
        ]};
    };
    def('wasd', 'events', 'multi',
        `(?:wasd|WASD|더블유에이에스디)(?:\\s*키)?(?:로|으로|를)?\\s*(?:(?:고양이|캐릭터|스프라이트|로봇\\s*카|로봇|자동차|차)(?:를|을|가)?\\s*)?(?:움직|조종|이동|조작|운전)?${REST}`,
        (g, ctx) => arrowMulti(['w', 's', 'a', 'd'], ctx));
    def('arrows', 'events', 'multi',
        `(?:방향키|화살표\\s*키|화살표|키보드)(?:를|로|으로)?\\s*(?:눌러서\\s*)?(?:(?:고양이|캐릭터|스프라이트|로봇\\s*(?:카|자동차|차)|로봇|자동차|차)(?:를|을|가)?\\s*)?(?:움직|조종|이동|조작|운전)${REST}`,
        (g, ctx) => {
            if (ctx && ctx.robot) {
                const rk = (key, kind) => ({
                    hat: act('event_whenkeypressed', {fields: fld('KEY_OPTION', key), sig: `key:${key}`, label: `${keyKo(key)} 키를 눌렀을 때`}),
                    body: drive(kind, 0.3, true)
                });
                return {notes: ['‘방향키로 조종하기’는 방향키마다 0.3초 움직이고 멈추게 했어요. 계속 누르고 있으면 계속 움직여요.', NOTE_CAR], multi: [
                    rk('up arrow', 'forward'), rk('down arrow', 'backward'), rk('left arrow', 'left'), rk('right arrow', 'right')
                ]};
            }
            const k = (key, op, input, v) => ({
                hat: act('event_whenkeypressed', {fields: fld('KEY_OPTION', key), sig: `key:${key}`, label: `${keyKo(key)} 키를 눌렀을 때`}),
                body: [act(op, {inputs: {[input]: num(v)}, sig: `${input === 'DX' ? 'x' : 'y'}${v >= 0 ? '+' : ''}${v}`, label: `${input === 'DX' ? 'x' : 'y'} 좌표를 ${v}만큼 바꾸기`})]
            });
            return {notes: ['‘방향키로 움직이기’는 방향키마다 스크립트를 하나씩, 모두 4개 만들었어요.'], multi: [
                k('up arrow', 'motion_changeyby', 'DY', 10),
                k('down arrow', 'motion_changeyby', 'DY', -10),
                k('left arrow', 'motion_changexby', 'DX', -10),
                k('right arrow', 'motion_changexby', 'DX', 10)
            ]};
        });

    // "마우스를 클릭할 때까지 기다리기", "벽에 닿을 때까지 움직이기를 반복하기"
    const UNTIL = `(?:(?<mc>마우스(?:를|가)?\\s*(?:클릭|누를|눌릴))|(?<ed>(?:벽|가장자리|끝)(?:에)?\\s*닿을)|(?<mt>마우스(?:\\s*포인터)?(?:에)?\\s*닿을)|(?<ky>${RE_KEY}\\s*키?(?:를|가)?\\s*(?:누를|눌릴))|(?<ob>장애물이?\\s*(?:감지될|보일|있을|나타날))|(?<bt>버튼(?:을|이)?\\s*(?:누를|눌릴)))\\s*(?:할\\s*)?때\\s*까지`;
    const untilCond = g => {
        if (g.mc) return act('sensing_mousedown', {sig: 'mousedown', label: '마우스를 클릭했는가?'});
        if (g.ed || g.mt) {
            const m = g.mt ? '_mouse_' : '_edge_';
            return act('sensing_touchingobject', {inputs: {TOUCHINGOBJECTMENU: menu('sensing_touchingobjectmenu', 'TOUCHINGOBJECTMENU', m)},
                sig: g.mt ? 'touchmouse' : 'touchedge', label: g.mt ? '마우스 포인터에 닿았는가?' : '벽에 닿았는가?'});
        }
        if (g.ky) {
            const k = keyValue(g.key) || 'space';
            return act('sensing_keypressed', {inputs: {KEY_OPTION: menu('sensing_keyoptions', 'KEY_OPTION', k)}, sig: `keyheld:${k}`, label: `${keyKo(k)} 키를 눌렀는가?`});
        }
        if (g.ob) return obstacle();
        return button();
    };
    def('waitUntil', 'control', 'act', `${UNTIL}\\s*(?:기다[리려렸린]|대기|가만히)${REST}`,
        g => {
            const cond = untilCond(g);
            return {nodes: [act('control_wait_until', {boolIn: {CONDITION: cond}, sig: `until:${cond.sig}`, label: `‘${cond.label}’ 까지 기다리기`})]};
        });
    def('repeatUntil', 'control', 'until', `${UNTIL}(?:\\s*계속)?`, g => ({cond: untilCond(g)}));

    // ═══════════════ 큐브로이드 로봇 — 레시피(목표를 말할 때) ═══════════════
    // 학생은 순서보다 목표를 말한다("자동차 만들기"). 목표 → 파이썬 예제와 같은 블록 묶음.
    def('rCar', 'robot', 'act',
        `(?:(?:DC\\s*)?모터\\s*(?:2|두)\\s*개?(?:를|을|로)?\\s*(?:사용하여|사용해서|이용해서|이용하여|써서|가지고|로)?\\s*)?(?:로봇\\s*)?(?:자동차|로봇\\s*카|RC\\s*카|로봇\\s*차)(?:를|을)?\\s*(?:만들|만들어|만들기|만들자|처럼\\s*(?:움직|달리|가))${REST}`,
        () => ({notes: ['‘자동차’는 파이썬 탭 예제 ‘로봇 자동차 Lv2’와 같은 순서로 만들었어요: 앞으로 1초 → 돌기 0.6초 → 앞으로 1초 → 멈추기.', NOTE_CAR],
            nodes: [].concat(drive('forward', 1, false), drive('right', 0.6, false), drive('forward', 1, false), stopBoth())}));
    def('rSelfDrive', 'robot', 'act',
        `(?:자율\\s*주행|스스로\\s*(?:가는|달리는|움직이는|피하는|피해\\s*가는|운전하는)|(?:장애물|벽|물건)(?:을|를)?\\s*(?:피하는|피해서\\s*(?:가는|다니는|달리는|가게|다니게)|피해\\s*(?:가는|다니는)|피하며|피하기|피해))\\s*(?:자동차|로봇|차)?(?:를|을)?\\s*(?:만들|만들어|만들기|해|하게)?${REST}`,
        () => ({notes: ['파이썬 탭 예제 ‘로봇 자동차 Lv3(자율주행)’와 같은 규칙이에요: 장애물이 있으면 돌고, 없으면 앞으로 가요.'],
            nodes: [{t: 'forever', body: [{t: 'ifelse', cond: obstacle(), body: drive('right', 0.6, false), body2: drive('forward', 0.2, false)}]}]}));
    def('rTraffic', 'robot', 'act', `신호등${REST}(?:\\s*(?:만들|만들어|만들기)${REST})?`,
        (g, ctx) => (/(?:빨|노|초록|파|주황)[가-힣]*\s*(?:불\s*)?\d+(?:\.\d+)?\s*초/.test(ctx.src || '') ? {notes: ['말한 색 순서대로 신호등을 만들었어요.'], nodes: []} : {notes: ['파이썬 탭 예제 ‘신호등(Lv2)’과 같은 순서예요: 초록 2초 → 노랑 1초 → 경고음 → 빨강 2초.'],
            nodes: [colorOn('#00ff00'), waitN(2), colorOn('#ffff00'), waitN(1), beep('경고'), waitN(0.5), colorOn('#ff0000'), waitN(2), colorOff()]}));
    def('rStreet', 'robot', 'act', `(?:(?:어두우면|어두워지면|밤에|밤이\\s*되면)\\s*(?:불(?:이)?\\s*)?(?:켜지는|켜지게|켜는)\\s*)?(?:스마트\\s*)?가로등${REST}(?:\\s*(?:만들|만들어|만들기)${REST})?(?:\\s*(?:어두우면|어두워지면|밤에|밤이\\s*되면)\\s*(?:불(?:이)?\\s*)?(?:켜지게|켜지도록|켜져|켜지는|켜)${REST})?`,
        () => ({notes: ['파이썬 탭 예제 ‘스마트 가로등(Lv3)’과 같은 규칙이에요: 어두우면 불을 켜고, 밝으면 꺼요.', NOTE_BRIGHT],
            nodes: [{t: 'forever', body: [{t: 'ifelse', cond: bright('<', 500), body: [colorOn('#ffffff')], body2: [colorOff()]}, waitN(0.2)]}]}));
    def('rFlash', 'robot', 'act', `(?:버튼(?:을)?\\s*(?:누르면|누를\\s*때|누르는\\s*동안)\\s*(?:불(?:이|빛이)?\\s*)?(?:켜지는|켜는)\\s*)?(?:손전등|플래시|후레시|플래쉬)${REST}(?:\\s*(?:만들|만들어|만들기)${REST})?`,
        () => ({notes: ['파이썬 탭 예제 ‘손전등’과 같은 규칙이에요: 버튼을 누르고 있는 동안만 불이 켜져요.'],
            nodes: [{t: 'forever', body: [{t: 'ifelse', cond: button(), body: [colorOn('#ffffff')], body2: [colorOff()]}, waitN(0.1)]}]}));
    def('rGoUntil', 'robot', 'act',
        `${CAR_WORD}(?:앞으로\\s*)?(?:가다가|달리다가|움직이다가|가다\\s*가|가는데)\\s*(?:앞에\\s*(?:[가-힣]{1,4}?(?:가|이)?\\s*)?(?:있으면|나타나면|보이면|가까우면)|장애물(?:이|을)?\\s*(?:있으면|보이면|감지되면|만나면|나타나면)|(?<bt>(?:버튼|단추)(?:을|이|를)?\\s*(?:누르면|눌리면|눌렀을\\s*때)))\\s*(?:멈춰|멈추|멈춤|정지|세워|스톱)${REST}`,
        g => {
            const cond = g.bt ? button() : obstacle();
            return {notes: [`‘가다가 ~하면 멈춰’는 ‘~할 때까지 기다리기’ 블록으로 만들었어요. 모터를 켜 두고, ${g.bt ? '버튼이 눌릴' : '장애물이 보일'} 때까지 기다렸다가 멈춰요.`, NOTE_CAR],
                nodes: [].concat(drive('forward', null, false),
                    [act('control_wait_until', {boolIn: {CONDITION: cond}, sig: `until:${cond.sig}`, label: `‘${cond.label}’ 까지 기다리기`})], stopBoth())};
        });

    // 앞으로 갔다가 뒤로 — "앞으로 갔다가 뒤로 왔다가", "앞으로 3초 뒤로 3초"
    def('rForeBack', 'robot', 'act', [
        `${CAR_WORD}(?:(?<n0>${RE_SEC})\\s*초\\s*(?:동안\\s*)?)?앞으로\\s*(?:(?<n>${RE_SEC})\\s*초\\s*(?:동안\\s*)?)?(?:갔다(?:가)?|가고|가다가|갔다가)\\s*(?:다시\\s*)?뒤로\\s*(?:(?<n2>${RE_SEC})\\s*초\\s*(?:동안\\s*)?)?(?:왔다(?:가)?|오|와|가|갔다|돌아와|돌아오)[가-힣]*(?:\\s*하는\\s*(?:거|것))?`,
        `${CAR_WORD}앞으로\\s*(?<n>${RE_SEC})\\s*초\\s*(?:동안\\s*)?(?:가고\\s*)?뒤로\\s*(?<n2>${RE_SEC})\\s*초(?:\\s*동안)?(?:\\s*(?:오|와|가)[가-힣]*)?`
    ], (g, ctx) => {
        const a = toNum(g.n) !== null ? toNum(g.n) : (toNum(g.n0) !== null ? toNum(g.n0) : 1);
        const b = toNum(g.n2) !== null ? toNum(g.n2) : a;
        if (!ctx.robot) {
            return {notes: ['고양이는 ‘앞으로 → 잠깐 → 뒤로’ 로 만들었어요. 로봇이면 ‘로봇’이라고 말해 주세요.'],
                nodes: [act('motion_movesteps', {inputs: {STEPS: num(50)}, sig: 'move50', label: '50만큼 움직이기'}), waitS(0.5),
                    act('motion_movesteps', {inputs: {STEPS: num(-50)}, sig: 'move-50', label: '-50만큼 움직이기'}), waitS(0.5)]};
        }
        return {notes: [NOTE_CAR], nodes: [].concat(drive('forward', a, false), drive('backward', b, false), stopBoth())};
    });
    // 사각형으로 달리기 — 앞으로 1초 + 오른쪽 돌기 0.6초를 4번
    def('rSquare', 'robot', 'act', `(?:(?:로봇\\s*)?앞으로\\s*(?<fs>${RE_SEC})\\s*초\\s*(?:가고|간\\s*다음|가다가)\\s*(?:오른쪽|왼쪽)(?:으로)?\\s*(?:도는\\s*(?:거|것)|돌고|돌기)(?:를|을)?\\s*(?:(?:\\d|네)\\s*번\\s*(?:해서|하면|반복해서|반복하면)\\s*)?)?${CAR_WORD}(?:정\\s*)?(?:사각형|네모)\\s*(?:모양)?(?:으로|로|을|를)?\\s*(?:움직|달려|달리|돌|가|그려|그리|이동|주행)[가-힣]*(?:\\s*앞으로\\s*(?<fs2>${RE_SEC})\\s*초\\s*(?:가고|간\\s*다음|가다가)?\\s*(?:오른쪽|왼쪽)(?:으로)?\\s*(?:도는\\s*(?:거|것)|돌고|돌기)(?:를|을)?\\s*(?:(?:\\d|네)\\s*번(?:\\s*(?:해서|하면|반복해서|반복하면|반복))?)?)?`,
        (g, ctx) => (ctx.robot ? {notes: ['사각형은 ‘앞으로 1초 → 오른쪽 돌기 0.6초’를 4번 반복해요. 도는 시간은 로봇마다 달라서 맞춰 보세요.', NOTE_SPIN],
            nodes: [{t: 'repeat', times: 4, body: [].concat(drive('forward', 1, false), drive('right', 0.6, false))}].concat(stopBoth())} : null));
    // 유턴 · N바퀴
    def('rUturn', 'robot', 'act', [`${CAR_WORD}(?:유턴|U\\s*턴|뒤로\\s*돌아)[가-힣]*`,
        `${CAR_WORD}(?:제자리(?:에서)?\\s*)?(?<t>한|두|세|반|1|2|3)\\s*바퀴(?:만)?\\s*(?:(?<dir>오른쪽|왼쪽)(?:으로)?\\s*)?(?:돌|회전|빙글)[가-힣]*`,
        `${CAR_WORD}(?<dir>오른쪽|왼쪽)(?:으로)?\\s*(?<deg>\\d+)\\s*도(?:\\s*(?:돌|회전|꺾)[가-힣]*)?`],
    (g, ctx, alt) => {
        if (!ctx.robot) return null;
        const left = /왼/.test(g.dir || '');
        let sec = 1.2;
        let why = '유턴(180도)은 제자리 돌기 1.2초로 했어요.';
        if (alt === 1) { const t = toNum(g.t); sec = Math.round(2.4 * t * 100) / 100; why = `${g.t} 바퀴는 제자리 돌기 ${fmt(sec)}초로 했어요(90도 ≈ 0.6초로 계산).`; }
        if (alt === 2) { sec = Math.round(0.6 * toNum(g.deg) / 90 * 100) / 100; why = `${g.deg}도는 제자리 돌기 ${fmt(sec)}초로 했어요(90도 ≈ 0.6초로 계산).`; }
        return {notes: [why, NOTE_SPIN], nodes: drive(left ? 'left' : 'right', sec, true)};
    });
    // 로봇 춤 — 좌우로 돌며 불빛 바꾸기
    def('rDance', 'robot', 'act', `${CAR_WORD}(?:춤(?:을)?\\s*(?:춰|추|췄|추는|추게|춰줘|춰서|추기)|댄스)[가-힣]*`,
        (g, ctx) => (ctx.robot ? {notes: ['로봇 춤은 ‘오른쪽·왼쪽으로 조금씩 돌면서 불빛 바꾸기’를 4번 반복해요.'], nodes: [{t: 'repeat', times: 4, body: [].concat(
            [colorOn('#ff0000')], drive('right', 0.3, false), [colorOn('#0000ff')], drive('left', 0.3, false))}].concat(stopBoth(), [colorOff()])} : null));
    // 로봇 도망가기 = 뒤로
    def('rFlee', 'robot', 'act', `${CAR_WORD}(?:도망(?:가|쳐|치|다니)|피해|물러나|물러서)[가-힣]*`,
        (g, ctx) => (ctx.robot ? {notes: [NOTE_CAR], nodes: drive('backward', 1, true)} : null));
    // 모터 두 개 같이
    def('rBothMotors', 'robot', 'act', `(?:DC\\s*)?모터\\s*(?:두\\s*개|2\\s*개|둘)\\s*(?:다|모두|같이|동시에)?\\s*(?:(?<n>${RE_SEC})\\s*초\\s*(?:동안|간)?\\s*)?(?:돌려|돌리|켜|움직|돌아)[가-힣]*`,
        g => {
            const sec = toNum(g.n) !== null ? toNum(g.n) : 1;
            return {notes: ['모터 두 개를 같이 돌리면 로봇이 앞으로 가요(1번 오른쪽 + 2번 왼쪽).', NOTE_CAR], nodes: drive('forward', sec, true)};
        });

    // ═══════════════ 큐브로이드 로봇 — 한 동작씩 ═══════════════
    const secOf = g => (toNum(g.n) !== null ? toNum(g.n) : (toNum(g.n2) !== null ? toNum(g.n2) : toNum(g.n3)));
    const askSec = (q, note) => ({input: 'DURATION', q, options: [0.5, 1, 2, 3], note});
    def('rMotorOne', 'robot', 'act',
        [`(?:DC\\s*)?모터\\s*(?<m>1|2|일|이|한|두)\\s*번?\\s*(?:을|를|은|는|이|가)?\\s*(?:(?<n0>${RE_SEC})\\s*초\\s*(?:동안|간)?\\s*)?(?<dir>오른쪽|왼쪽|정방향|역방향|반대\\s*방향|반대로)(?:으로)?\\s*(?:(?<n>${RE_SEC})\\s*초\\s*(?:동안|간)?\\s*)?(?:돌|회전|움직|굴려|가)${REST}`,
            `(?:DC\\s*)?모터\\s*(?<m>1|2)\\s*번?\\s*(?<dir>정방향|역방향|오른쪽|왼쪽)(?=\\s|$|,)`,
            `(?:DC\\s*)?모터\\s*(?<m>1|2|일|이|한)\\s*번?\\s*(?:을|를|만)?\\s*(?:(?<n>${RE_SEC})\\s*초\\s*(?:동안|간)?\\s*)?(?:돌려|돌리|켜|움직여)[가-힣]*`],
        g => {
            const id = /1|일|한/.test(g.m) ? M1 : M2;
            const dir = /왼|역|반대/.test(g.dir || '') ? 'Left' : 'Right';
            const sec = toNum(g.n) !== null ? toNum(g.n) : toNum(g.n0);
            const nodes = [motor(id, dir)];
            if (sec !== null) nodes.push(waitN(sec), motorOff(id));
            return {nodes};
        });
    def('rMotorOneOff', 'robot', 'act',
        `(?:DC\\s*)?모터\\s*(?<m>1|2|일|이|한|두)\\s*번?\\s*(?:을|를|은|는|이|가)?\\s*(?:멈춰|멈추|꺼|끄|정지|세워)${REST}`,
        g => ({nodes: [motorOff(/1|일|한/.test(g.m) ? M1 : M2)]}));
    def('rForward', 'robot', 'act', [
        `${CAR_WORD}(?:(?:천천히|빨리|느리게)\\s*)?(?:(?<n>${RE_SEC})\\s*초\\s*(?:동안|간)?\\s*)?(?:다시\\s*)?(?:앞으로|전진|직진)\\s*(?:(?<n2>${RE_SEC})\\s*초\\s*(?:동안|간)?\\s*)?(?:(?:빨리|천천히|쭉|계속)\\s*)?(?:가|오게|와|달려|달리|움직|이동|나아가|전진|굴러)${REST}(?:\\s*(?:해줘|해|해주세요))?(?:\\s*(?<n3>${RE_SEC})\\s*초\\s*(?:동안|간))?`,
        `${CAR_WORD}(?:(?<n>${RE_SEC})\\s*초\\s*(?:동안|간)?\\s*)?(?:달려|달리|출발(?!할\\s*때|하기\\s*전)|전진|굴러가)${REST}`,
        `${CAR_WORD}(?<n>${RE_SEC})\\s*초\\s*(?:동안|간)?\\s*만?\\s*(?:가|달려|움직|이동)${REST}`,
        // "로봇 앞으로", "스페이스 누르면 로봇 앞으로", "모터 속도 50%로 앞으로" — 동사 없이 끝나는 말
        `${CAR_WORD}(?:(?<n>${RE_SEC})\\s*초\\s*(?:동안\\s*)?)?(?:다시\\s*)?(?:앞으로|전진|직진)(?:\\s*(?:쭉|계속))?(?:\\s*(?<n2>${RE_SEC})\\s*초(?:\\s*동안)?(?:으로|로)?(?:\\s*(?:해줘|해주세요|해|가줘|가게))?)?(?=\\s*$|\\s*[,.!]|\\s+(?:그리고|하고|가다가|후진|뒤로|좌회전|우회전|멈춰|정지))`,
        `(?:천천히|빨리|느리게|빠르게|최대\\s*속도로|최고\\s*속도로)\\s*(?:가|달려|달리|움직|출발)${REST}`,
        `(?:(?:로봇|자동차|차)(?:도|가|이)?\\s*(?:같이|함께)?\\s*(?:움직|움직여|움직이)[가-힣]*|(?:^|\\s)(?:가|달려|출발)(?=\\s*$))`,
        // "2초 앞으로 갔다가 멈춰", "앞으로 1초 가서 멈춰" — 간 다음 멈추라는 말(2026-10-20). 멈춤 = 모터 끄기(전체 멈추기 아님)
        `${CAR_WORD}(?:(?<n>${RE_SEC})\\s*초\\s*(?:동안|간)?\\s*)?(?:다시\\s*)?(?:앞으로|전진|직진)\\s*(?:(?<n2>${RE_SEC})\\s*초\\s*(?:동안|간)?\\s*)?(?:갔다(?:가)?|가서|가고|가다가|간\\s*(?:다음|뒤)에?|하고|해서|했다가)\\s*(?:(?:그\\s*다음에?|그리고|바로)\\s*)?(?:멈춰|멈추|멈춤|정지|서|세워|스톱)${REST}`
    ], (g, ctx, alt, before) => {
        if (!ctx.robot) return null;
        if (/(?:고양이|냥이)(?:가|도|는|를)?\s*(?:같이\s*|함께\s*)?$/.test(before || '')) return null;
        // "계속 앞으로 가" — 무한 반복 안에서는 모터를 켜 두기만 한다(1초마다 멈추면 덜컥거린다)
        if (/계속|무한|영원히|쭉/.test(before || '')) return {notes: [NOTE_CAR], nodes: drive('forward', null, false)};
        let sec = secOf(g);
        let guess = null;
        if (sec === null) { sec = 1; guess = '얼마나 갈지 말하지 않아서 1초 동안 가게 했어요. ‘3초 동안 앞으로 가’처럼 말하면 정확해져요.'; }
        return {notes: guess ? [guess, NOTE_CAR] : [NOTE_CAR], nodes: drive('forward', sec, true, guess ? askSec('몇 초 동안 갈까요?', guess) : null)};
    });
    def('rBack', 'robot', 'act', [
        `${CAR_WORD}(?:(?<n>${RE_SEC})\\s*초\\s*(?:동안|간)?\\s*)?(?:뒤로)\\s*(?:(?<n2>${RE_SEC})\\s*초\\s*(?:동안|간)?\\s*만?\\s*)?(?:가|달려|움직|이동|물러|와|후진)${REST}`,
        `${CAR_WORD}(?:(?<n>${RE_SEC})\\s*초\\s*(?:동안|간)?\\s*)?후진${REST}`,
        `${CAR_WORD}뒤로\\s*(?:(?<k>조금만?|살짝|쪼금만?|쪼끔만?|약간)\\s*)?(?:(?<n>${RE_SEC})\\s*초\\s*(?:동안|간)?\\s*)?(?:가|갔다|물러|와|왔다|후진|빠져)[가-힣]*`,
        // "2초 뒤로 갔다가 멈춰"(2026-10-20)
        `${CAR_WORD}(?:(?<n>${RE_SEC})\\s*초\\s*(?:동안|간)?\\s*)?뒤로\\s*(?:(?<n2>${RE_SEC})\\s*초\\s*(?:동안|간)?\\s*)?(?:갔다(?:가)?|가서|가고|가다가|간\\s*(?:다음|뒤)에?|하고|해서|했다가)\\s*(?:(?:그\\s*다음에?|그리고|바로)\\s*)?(?:멈춰|멈추|멈춤|정지|서|세워|스톱)${REST}`
    ], (g, ctx) => {
        if (!ctx.robot) return null;
        let sec = secOf(g);
        if (sec === null && g.k) sec = 0.5;
        let guess = null;
        if (sec === null) { sec = 1; guess = '얼마나 갈지 말하지 않아서 1초 동안 뒤로 가게 했어요.'; }
        return {notes: guess ? [guess] : [], nodes: drive('backward', sec, true, guess ? askSec('몇 초 동안 뒤로 갈까요?', guess) : null)};
    });
    def('rSpin', 'robot', 'act', [
        `${CAR_WORD}(?:제자리(?:에서)?\\s*)?(?<dir>오른쪽|왼쪽)(?:으로)?\\s*(?:(?<n>${RE_SEC})\\s*초\\s*(?:동안|간)?\\s*)?(?:돌|회전|꺾|턴)${REST}(?:\\s*(?<n2>${RE_SEC})\\s*초(?:\\s*(?:동안|간))?만?)?`,
        `${CAR_WORD}(?:(?<back>뒤로|반대로)\\s*)?(?:제자리(?:에서)?\\s*)?(?:빙글\\s*(?:빙글\\s*)?|빙빙\\s*|뱅글\\s*)?(?:돌|회전)${REST}`
    ], (g, ctx) => {
        if (!ctx.robot) return null;
        if (/^(?:다시\s*)?(?:돌아와|돌아오|되돌아)/.test(g[0] || '')) return null;
        const kind = /왼/.test(g.dir || '') ? 'left' : 'right';
        const sec = toNum(g.n) !== null ? toNum(g.n) : (toNum(g.n2) !== null ? toNum(g.n2) : (g.back ? 1.2 : 0.6));
        // "빙글빙글 돌아"(초를 말하지 않음) — 멈출 때까지 계속 돌기
        if (/빙글\s*빙글|빙빙|뱅글\s*뱅글/.test(g[0] || '') && toNum(g.n) === null && toNum(g.n2) === null) return {notes: ['두 모터를 같은 방향으로 켜 두면 멈출 때까지 제자리에서 계속 돌아요. 멈추려면 빨간 정지 버튼을 누르세요.', NOTE_SPIN], nodes: drive(kind, null, false)};
        return {notes: [NOTE_SPIN], nodes: drive(kind, sec, true)};
    });
    def('rStop', 'robot', 'act',
        `(?:(?:로봇|자동차|차|모터|바퀴)(?:를|을|가|이|는|도)?\\s*(?:다\\s*)?)?(?:멈춰|멈추|멈춤|정지|세워|스톱)${REST}`,
        (g, ctx) => (ctx.robot ? {nodes: stopBoth()} : null));
    def('rSpeed', 'robot', 'act', ['(?<max>최대|최고|풀)\\s*(?:속도|스피드)(?:로)?',
        `(?:(?:DC\\s*)?모터\\s*(?:(?<m>1|2|일|이|한|두)\\s*번?\\s*(?:의)?\\s*)?)?(?:속도|빠르기|스피드)(?:를|을|는)?\\s*(?<n>\\d+)\\s*(?:%|퍼센트|퍼|프로)?\\s*(?:으로|로)?(?:\\s*(?:정하|정해|해|바꿔|맞춰|설정|올려|내려)${REST})?`],
        (g, ctx) => {
            if (!ctx.robot && !g.max) return null;
            const n = g.max ? 100 : Math.max(0, Math.min(100, toNum(g.n)));
            const sp = id => act(`${id}_dcMotorSetSpeed`, {inputs: {SPEED: num(n)}, sig: `m${mNo(id)}:speed${n}`, label: `DC 모터 ${mNo(id)}번 속도를 ${n}%로 정하기`});
            const only = g.m ? [/1|일|한/.test(g.m) ? M1 : M2] : [M1, M2];
            return {notes: n < 30 ? ['속도가 30%보다 낮으면 바퀴가 안 돌거나 한쪽만 돌 수 있어요(실제 로봇으로 잰 값). 30% 이상을 권해요.'] : [], nodes: only.map(sp)};
        });

    // ── 불빛(마스터 블록) ──
    def('rLightOn', 'robot', 'act',
        `(?:(?<c>${RE_COLOR})\\s*(?:색)?(?:으로|로)?\\s*)?(?:불|불빛|빛|조명|램프|전등)(?:을|를|이)?\\s*(?:(?<c2>${RE_COLOR})\\s*(?:색)?(?:으로|로)?\\s*)?(?:켜|켜줘|켜기|켜고|켜지|밝혀|들어오)${REST}`,
        g => {
            const c = g.c || g.c2;
            return {notes: c ? [] : ['색을 말하지 않아서 흰색으로 켰어요. ‘빨간 불 켜’처럼 말할 수 있어요.'], nodes: [colorOn(c ? COLOR_HEX[c] : '#ffffff')]};
        });
    // "LED 파란색", "로봇 불빛 보라색", "빨간불" — 동사 없이 색만 말한 경우
    def('rLightNoun', 'robot', 'act', [
        `(?:로봇\\s*)?(?<w>불빛|불|조명|램프|LED|엘이디|마스터(?:\\s*블록)?(?:\\s*불빛)?)(?:을|를|이|은|의)?\\s*(?:색(?:깔)?(?:을|를)?\\s*)?(?<c>${RE_COLOR})\\s*(?:색|색깔)?(?:\\s*불(?:빛)?)?(?:\\s*(?:으로|로))?(?:\\s*(?:켜|되|돼|바뀌|해|변|만들|바꿔)[가-힣]*)?`,
        `(?<c>${RE_COLOR})\\s*(?:색)?\\s*불(?:빛)?(?:이|을)?(?:\\s*(?:켜|들어와|해|켜줘)[가-힣]*)?(?!\\s*(?:일\\s*때|이면|일때))`
    ], g => ({notes: /LED|엘이디/.test(g.w || '') ? ['LED 판은 한 가지 색이라, 색은 마스터 블록 불빛으로 켰어요.'] : [], nodes: [colorOn(COLOR_HEX[g.c])]}));
    // "불빛 초록색 3초 켰다가 꺼"
    def('rLightTimed', 'robot', 'act', `(?:불빛|불|조명)(?:을|를)?\\s*(?<c>${RE_COLOR})?\\s*(?:색)?\\s*(?<n>${RE_SEC})\\s*초\\s*(?:동안\\s*)?(?:켰다가?|켜고|켜)\\s*(?:다시\\s*)?(?:꺼|끄)[가-힣]*`,
        g => ({nodes: [colorOn(g.c ? COLOR_HEX[g.c] : '#ffffff'), waitN(toNum(g.n)), colorOff()]}));
    // 두 색 번갈아 — "빨강 파랑 번갈아", "경찰차처럼", "빨강 파랑 1초마다 바뀌게"
    def('rLightAlt', 'robot', 'act', [
        `(?:(?<pol>경찰\\s*차|구급\\s*차|소방\\s*차|사이렌)\\s*(?:처럼|같이)?\\s*)?(?:불빛(?:이|을)?\\s*)?(?<c1>${RE_COLOR})\\s*(?:색)?\\s*(?:이랑|하고|과|와|랑|,)?\\s*(?<c2>${RE_COLOR})\\s*(?:색)?\\s*(?:(?<n>${RE_SEC})\\s*초\\s*(?:마다|씩|간격으로)\\s*)?(?:번갈아|번갈아가며|번갈아가면서|교대로|깜빡|바뀌|왔다\\s*갔다)[가-힣]*`,
        `(?<pol>경찰\\s*차|구급\\s*차|소방\\s*차)\\s*(?:처럼|같이)?\\s*(?:불빛|불|사이렌)?(?:\\s*(?:만들|켜|해)[가-힣]*)?`,
        `(?:삐뽀\\s*삐뽀(?:\\s*사이렌)?|사이렌)(?:\\s*(?:소리))?(?:랑|이랑|하고|와)?(?:\\s*(?:빨간|파란)?\\s*불(?:빛)?)?`
    ], (g, ctx, alt) => {
        const c1 = alt === 0 ? COLOR_HEX[g.c1] : '#ff0000';
        const c2 = alt === 0 ? COLOR_HEX[g.c2] : '#0000ff';
        const sec = toNum(g.n) !== null ? toNum(g.n) : (alt === 0 && !g.pol ? 1 : 0.3);
        const siren = alt === 2 || Boolean(g.pol);
        return {notes: [`두 색을 번갈아 켜고, 사이에 ${fmt(sec)}초씩 기다려요.${siren ? ' 경고음도 함께 내요.' : ''}`],
            nodes: loopAll([colorOn(c1)].concat(siren ? [beep('경고')] : [], [waitN(sec), colorOn(c2), waitN(sec)]))};
    }, {once: true});
    // 무지개·색 계속 바뀌게 — 여러 색 차례로
    def('rRainbow', 'robot', 'act', [
        `무지개(?:색)?(?:으로|처럼)?\\s*(?:불빛|불|빛|LED)?(?:이|을)?\\s*(?:바뀌|변하|켜|돌아가|깜빡|반짝|바꿔)[가-힣]*(?:\\s*반복)?`,
        `(?:불빛|불|조명)(?:의)?\\s*색(?:깔)?(?:이|을)?\\s*(?:계속\\s*|막\\s*)?(?:바뀌|변하|바꿔)[가-힣]*`
    ], (g, ctx) => (ctx.robot ? {notes: ['빨강 → 주황 → 노랑 → 초록 → 파랑 → 보라를 0.3초씩 차례로 켜요.'],
        nodes: loopAll(['#ff0000', '#ff8800', '#ffff00', '#00ff00', '#0000ff', '#ff00ff'].reduce((a, h) => a.concat([colorOn(h), waitN(0.3)]), []))} : null), {once: true});
    // 깜빡깜빡
    def('rBlink', 'robot', 'act', `(?:(?:불빛|불|빛|LED|조명)(?:이|을|도)?\\s*)(?:같이\\s*|함께\\s*)?(?<c>${RE_COLOR})?\\s*(?:색)?\\s*(?:(?<n>${RE_SEC})\\s*초\\s*(?:마다|간격으로|씩)\\s*)?(?:(?<cnt>\\d+|${RE_KNUM})\\s*번\\s*)?(?:깜빡\\s*깜빡|깜빡|깜박|반짝\\s*반짝|반짝)[가-힣]*(?:\\s*(?<n2>${RE_SEC})\\s*초\\s*(?:마다|간격으로|씩))?`,
        g => {
            const sec = toNum(g.n) !== null ? toNum(g.n) : (toNum(g.n2) !== null ? toNum(g.n2) : 0.5);
            if (toNum(g.cnt) > 0) return {nodes: [{t: 'repeat', times: Math.round(toNum(g.cnt)), body: [colorOn(g.c ? COLOR_HEX[g.c] : '#ffffff'), waitN(sec), colorOff(), waitN(sec)]}]};
            return {notes: [`깜빡이기는 ‘켜기 → ${fmt(sec)}초 → 끄기 → ${fmt(sec)}초’를 계속 반복해요.`],
                nodes: loopAll([colorOn(g.c ? COLOR_HEX[g.c] : '#ffffff'), waitN(sec), colorOff(), waitN(sec)])};
        }, {once: true});
    // 신호등 순서를 직접 — "초록 5초 노랑 2초 빨강 5초"
    def('rLightSeq', 'robot', 'act',
        `(?:신호등\\s*(?:처럼|같이)?\\s*)?(?<pairs>(?:${RE_COLOR})\\s*(?:색|불)?\\s*\\d+(?:\\.\\d+)?\\s*초(?:\\s*(?:동안)?\\s*,?\\s*(?:다음에?|그다음|→|->)?\\s*(?:${RE_COLOR})\\s*(?:색|불)?\\s*\\d+(?:\\.\\d+)?\\s*초)+)(?:\\s*(?:동안|씩))?`,
        g => {
            const re = new RegExp(`(${RE_COLOR})\\s*(?:색|불)?\\s*(\\d+(?:\\.\\d+)?)\\s*초`, 'g');
            const nodes = [];
            let m;
            while ((m = re.exec(g.pairs)) !== null) nodes.push(colorOn(COLOR_HEX[m[1]]), waitN(Number(m[2])));
            return {nodes};
        });
    def('rLightColor', 'robot', 'act', [`(?<c>${RE_COLOR})\\s*(?:색|색깔)?\\s*(?:으로|로)\\s*(?:바꿔|바꾸|해|켜|변경|바뀌)${REST}`,
        `(?<c>${RE_COLOR})\\s*(?:색|색깔)?\\s*(?:으로|로)?\\s*(?:되|돼|바뀌|변하|켜지)[가-힣]*`],
        (g, ctx) => (ctx.robot ? {nodes: [colorOn(COLOR_HEX[g.c])]} : null));
    def('rLightOff', 'robot', 'act', [`(?:불|불빛|빛|조명|램프|전등)(?:을|를|은|도)?\\s*(?:꺼|끄|끄기|꺼줘|끄고|꺼져|꺼지)${REST}`,
        '(?:^|\\s)(?:꺼지게|꺼지도록|꺼져|꺼지고|꺼지는\\s*(?:것|거)(?:도)?|꺼|꺼줘)(?=\\s|$)'],
        (g, ctx, alt) => (alt === 1 && !ctx.robot ? null : {nodes: [colorOff()]}));
    // ── LED 8×8 ──
    def('rLedClear', 'robot', 'act', [`(?:LED\\s*매트릭스|도트\\s*매트릭스|매트릭스|LED|엘이디|점판|전광판)(?:을|를|에|은)?\\s*(?:다\\s*)?(?:지워|지우|꺼|끄|비워|클리어)${REST}`,
        `(?:LED\\s*)?화면(?:을)?\\s*(?:다\\s*|모두\\s*)?(?:지우|지워|비워)${REST}`],
        (g, ctx, alt) => (alt === 1 && !ctx.robot && /고양이|냥이|펜|그림|그린|선|낙서/.test(ctx.src || '') ? {nodes: [act('pen_clear', {sig: 'pen:clear', label: '모두 지우기'})]} : {nodes: [act('cubroidled_ledAllClearControl', {sig: 'led:clear', label: 'LED 화면 지우기'})]}));
    def('rFace', 'robot', 'act',
        [`(?:${LED_WORD}(?:에|에다|으로|에서)?\\s*)?(?<img>${RE_IMG})\\s*(?:얼굴|모양|표정|그림|표시|이모티콘|거|것)?(?:을|를|이)?\\s*(?:보여|띄워|표시|나타내|그려|켜|띄우|출력|만들|나오|나타)${REST}`,
            // "LED 매트릭스에 웃는 얼굴", "8x8에 하트" — 동사 없이 끝나는 말
            `${LED_WORD}(?:에|에다|으로)?\\s*(?<img>${RE_IMG})\\s*(?:얼굴|모양|표정|그림|이모티콘)?(?:\\s*(?:표시|띄우기|보여주기))?(?=\\s*$|\\s*[,.!]|\\s+(?:아니면|없으면|하고|그리고))`,
            // "슬픈 얼굴 표시", "웃는얼굴" — '얼굴·표정'이 붙으면 LED 그림
            `(?<img>웃는|웃음|스마일|미소|행복한|우는|울상|화난|슬픈|자는|잠자는|졸린)\\s*(?:얼굴|표정)(?:을|를|이)?(?:\\s*(?:표시|보여|띄워|나오|해)[가-힣]*)?`,
            // "하트를 LED에 띄워줘", "웃는 얼굴을 LED에 보여줘" — 그림을 먼저 말하는 어순(2026-10-16 전수 훑기에서 16개 전부 실패)
            `(?<img>${RE_IMG})\\s*(?:얼굴|모양|표정|그림|이모티콘)?(?:을|를|이)\\s*${LED_WORD}(?:에|에다|에다가|으로)?\\s*(?:보여|띄워|표시|나타내|그려|켜|띄우|출력|나오|나타)${REST}`],
        (g, ctx, alt) => {
            // 네모·원·세모·별·꽃은 펜 그리기·모양과 겹쳐 LED 말·로봇 문맥이 있을 때만. 하트·느낌표·물음표·음표는 LED 그림뿐이라 문맥 없이도(2026-10-16)
            if (!g.led && !ctx.robot && alt === 0 && !/^(?:하트|느낌표|물음표|음표)$/.test(g.img)) return null;
            const hit = LED_IMG.find(x => x[0].test(g.img));
            if (!hit) return null;
            return {nodes: [act('cubroidled_lceImgControl', {fields: fld('MATRIX', hit[2]), sig: `led:${hit[1]}`, label: `LED에 ‘${hit[1]}’ 그림 보여주기`})]};
        });
    // 얼굴 번갈아 — "웃는얼굴이랑 화난얼굴 번갈아", "얼굴 웃었다 울었다"
    const FACE_OF = w => (LED_IMG.find(x => x[0].test(w)) || LED_IMG[0]);
    def('rFaceAlt', 'robot', 'act', [
        '(?<f1>웃는|웃음|스마일|행복한|우는|슬픈|화난|하트)\\s*(?:얼굴|표정)?(?:이랑|하고|과|와|랑)?\\s*(?<f2>웃는|웃음|스마일|행복한|우는|슬픈|화난|하트)\\s*(?:얼굴|표정)?\\s*(?:(?<n>\\d+(?:\\.\\d+)?)\\s*초\\s*마다\\s*)?(?:번갈아|번갈아가며|번갈아가면서|교대로|바뀌|왔다\\s*갔다)[가-힣]*',
        '(?:얼굴(?:이)?\\s*)?(?<w1>웃|울|화내)었다(?:가)?\\s*(?<w2>웃|울|화내)었다(?:가)?[가-힣]*(?:\\s*(?:바뀌|반복)[가-힣]*)?'
    ], (g, ctx, alt) => {
        const W = {웃: '웃는', 울: '우는', 화내: '화난'};
        const a = FACE_OF(alt === 0 ? g.f1 : W[g.w1]);
        const b = FACE_OF(alt === 0 ? g.f2 : W[g.w2]);
        const sec = toNum(g.n) !== null ? toNum(g.n) : 1;
        const img = h => act('cubroidled_lceImgControl', {fields: fld('MATRIX', h[2]), sig: `led:${h[1]}`, label: `LED에 ‘${h[1]}’ 그림 보여주기`});
        return {notes: [`두 그림을 ${fmt(sec)}초씩 번갈아 계속 보여줘요.`], nodes: loopAll([img(a), waitN(sec), img(b), waitN(sec)])};
    }, {once: true});
    // LED 카운트다운 — "매트릭스에 5 4 3 2 1 카운트다운"
    def('rLedCount', 'robot', 'act', [
        `${LED_WORD}(?:에|에다|으로|로)?\\s*(?:숫자(?:로)?\\s*)?(?:카운트\\s*다운\\s*)?(?<seq>[0-9](?:\\s*,\\s*[0-9]|\\s+[0-9]){1,9})\\s*(?:카운트\\s*다운)?(?:\\s*(?:하고|하기|해|해줘|보여줘))?`,
        `${LED_WORD}(?:에|에다|으로)?\\s*카운트\\s*다운[가-힣]*`
    ], g => {
        const ds = g.seq ? g.seq.replace(/[^0-9]/g, '').split('') : ['5', '4', '3', '2', '1'];
        const nodes = [];
        ds.forEach(d => nodes.push(act('cubroidled_lceNumberControl', {fields: fld('MATRIX', LED_NUM[d]), sig: `led:${d}`, label: `LED에 숫자 ${d} 보여주기`}), waitN(1)));
        return {notes: ['숫자마다 1초씩 보여줘요.'], nodes};
    });
    def('rLedNum', 'robot', 'act', [
        `${LED_WORD}(?:에|에다)?\\s*(?:숫자\\s*)?(?<d>[0-9])(?:\\s*(?:숫자|표시|보여주기))?(?=\\s*$|\\s*[,.!])`,
        `(?:${LED_WORD}(?:에|에다)?\\s*)?숫자\\s*(?<d>[0-9])(?:을|를|이)?\\s*(?:보여|띄워|표시|띄우|출력)${REST}`,
        `${LED_WORD}(?:에|에다)?\\s*(?<d>[0-9])(?:을|를|이)?\\s*(?:보여|띄워|표시|띄우|출력)${REST}`,
        // "3을 LED에 보여줘", "숫자 5를 LED에 띄워" — 숫자를 먼저 말하는 어순(2026-10-16)
        `(?:숫자\\s*)?(?<d>[0-9])(?:을|를|이)\\s*${LED_WORD}(?:에|에다)?\\s*(?:보여|띄워|표시|띄우|출력)${REST}`
    ], (g, ctx, alt) => ((g.led || ctx.robot || alt === 1) ? {nodes: [act('cubroidled_lceNumberControl', {fields: fld('MATRIX', LED_NUM[g.d]), sig: `led:${g.d}`, label: `LED에 숫자 ${g.d} 보여주기`})]} : null));
    def('rLedLetter', 'robot', 'act', [
        `${LED_WORD}(?:에|에다)?\\s*(?<a>[a-zA-Z])\\s*(?:글자|알파벳)?(?:\\s*(?:표시|보여주기))?(?=\\s*$|\\s*[,.!])`,
        `(?:${LED_WORD}(?:에|에다)?\\s*)?(?:글자|알파벳|영어)\\s*(?<a>[a-z])(?:를|을|이)?\\s*(?:보여|띄워|표시|띄우|출력)${REST}`,
        `${LED_WORD}(?:에|에다)?\\s*(?<a>[a-z])\\s*글자(?:를|을)?\\s*(?:보여|띄워|표시)[가-힣]*`,
        `${LED_WORD}(?:에|에다)?\\s*(?<a>[a-z])(?:\\s*(?:표시|모양|글자))?(?:를|을|이|도)?\\s*(?:보여|띄워|표시|띄우|출력)${REST}`,
        // "A를 LED에 보여줘", "글자 B를 LED에 띄워" — 글자를 먼저 말하는 어순(2026-10-16)
        `(?:^|\\s)(?:글자\\s*|알파벳\\s*)?(?<a>[a-zA-Z])\\s*(?:글자)?(?:를|을|이)\\s*${LED_WORD}(?:에|에다)?\\s*(?:보여|띄워|표시|띄우|출력)${REST}`
    ], (g, ctx, alt) => {
        if (!g.led && !ctx.robot && alt !== 1) return null;
        const a = g.a.toUpperCase();
        return {nodes: [act('cubroidled_lceAlpabetControl', {fields: fld('MATRIX', LED_ABC[a]), sig: `led:${a}`, label: `LED에 글자 ${a} 보여주기`})]};
    });
    // ── 사운드 블록(음 연주) ──
    def('rNotes', 'robot', 'act', [
        '(?:^|[^가-힣])(?<seq>(?:높은\\s*)?(?:도|레|미|파|솔|라|시)(?:\\s*[,·]?\\s*(?:높은\\s*)?(?:도|레|미|파|솔|라|시)){1,15})\\s*(?:를|을|음을?)?\\s*(?:(?<bt>한|두|반|\\d+(?:\\.\\d+)?)\\s*박자?\\s*씩\\s*)?(?:(?<slow>천천히|느리게|빠르게)\\s*)?(?:연주|소리|내|쳐|치|울려|불러|해|노래)[가-힣]*',
        '(?<seq>(?:(?:높은|낮은)\\s*)?(?:도|레|미|파|솔|라|시))\\s*(?:음|소리)(?:을|를)?(?:\\s*(?:내|연주|쳐|울려|나)[가-힣]*|(?=\\s*$|\\s*[,.!]))',
        '(?<scale>도레미파솔라시도?|계이름|음계)\\s*(?:를|을)?\\s*(?:연주|노래|내|쳐|울려|해|올라가|차례로|순서대로)[가-힣]*',
        '(?:^|[^가-힣])(?<seq>(?:도|레|미|파|솔|라|시)(?:\\s*[,·]?\\s*(?:높은\\s*)?(?:도|레|미|파|솔|라|시)){1,15})\\s*(?:까지|도)?(?:\\s*(?:추가|더)[가-힣]*)?(?=\\s*$|\\s*[,.!])'
    ], g => {
        const seq = g.scale ? '도레미파솔라시높은도' : g.seq;
        const re = /(높은\s*|낮은\s*)?(도|레|미|파|솔|라|시)/g;
        const LOW_N = {도: '도 C(48)', 레: '레 D(50)', 미: '미 E(52)', 파: '파 F(53)', 솔: '솔 G(55)', 라: '라 A(57)', 시: '시 B(59)'};
        const nodes = [];
        let m;
        let prevN = null;
        while ((m = re.exec(seq)) !== null) {
            const low = Boolean(m[1] && /낮은/.test(m[1]));
            const hiV = m[1] && /높은/.test(m[1]) ? HI[m[2]] : null;
            const high = !low && m[2] === '도' && (m[1] || prevN === '시' || prevN === '라');
            nodes.push(noteBlock(low ? LOW_N[m[2]] : hiV || (high ? '도 C(72)' : SOLFA[m[2]]), g.bt ? ({한: 1, 두: 2, 반: 0.5}[g.bt] || toNum(g.bt) || 0.5) : g.slow === '천천히' || g.slow === '느리게' ? 1 : g.slow === '빠르게' ? 0.25 : 0.5));
            prevN = m[2];
        }
        return {notes: ['음 이름은 사운드 블록 메뉴와 같아요. 숫자가 클수록 높은 음이에요(도 C(60)이 가운데 도).'], nodes};
    });
    // 노래 — 학생들이 아는 동요(사운드 블록 음 이름 그대로). 높은 음은 메뉴의 72 이상.
    const HI = {도: '도 C(72)', 레: '레 D(74)', 미: '미 E(76)', 파: '파 F(77)', 솔: '솔 G(79)'};
    const SONGS = [
        [/학교\s*종/, '학교종', '솔솔라라솔솔미 솔솔미미레 솔솔라라솔솔미 솔미레미도'],
        [/작은\s*별|반짝\s*반짝/, '작은 별', '도도솔솔라라솔 파파미미레레도'],
        [/나비야/, '나비야', '솔미미 파레레 도레미파 솔솔솔'],
        [/비행기/, '비행기', '미레도레미미미 레레레 미솔솔 미레도레미미미 레레미레도'],
        [/생일/, '생일 축하', '솔솔라솔^도시 솔솔라솔^레^도 솔솔^솔^미^도시라 ^파^파^미^도^레^도']
    ];
    def('rSong', 'robot', 'act', `(?<song>학교\\s*종이?|작은\\s*별|반짝\\s*반짝(?:\\s*작은\\s*별)?|생일\\s*축하(?:\\s*합니다)?|비행기(?!\\s*(?:슈팅|게임|쏘는|총))|나비야)(?:\\s*(?:노래|곡|음악))?(?:를|을|도)?(?:\\s*(?:연주|틀어|틀|불러|노래|쳐|해|재생|들려)[가-힣]*)?`,
        g => {
            const song = SONGS.find(x => x[0].test(g.song));
            const nodes = [];
            const re = /(\^?)(도|레|미|파|솔|라|시)/g;
            let m;
            while ((m = re.exec(song[2])) !== null) nodes.push(noteBlock(m[1] && HI[m[2]] ? HI[m[2]] : SOLFA[m[2]], 0.5));
            return {notes: [`‘${song[1]}’ 계이름을 사운드 블록으로 차례로 연주해요(한 음 0.5박자). 긴 음은 박자 숫자를 늘려 보세요.`], nodes};
        });
    // "부저로 도 1초"
    def('rOneNote', 'robot', 'act', '(?:부저|버저|사운드\\s*블록)(?:로|에서)?\\s*(?<lh>낮은|높은)?\\s*(?<v>도|레|미|파|솔|라|시)(?![도레미파솔라시])\\s*(?:음)?\\s*(?:(?<b>\\d+(?:\\.\\d+)?)\\s*(?:초|박자?))?(?:\\s*(?:연주|내|울려|쳐)[가-힣]*)?',
        g => {
            const LOW = {도: '도 C(48)', 레: '레 D(50)', 미: '미 E(52)', 파: '파 F(53)', 솔: '솔 G(55)', 라: '라 A(57)', 시: '시 B(59)'};
            const v = g.lh === '낮은' ? LOW[g.v] : (g.lh === '높은' && HI[g.v] ? HI[g.v] : SOLFA[g.v]);
            return {notes: g.b ? ['박자는 빠르기에 따라 달라요. 1박자는 대략 1초쯤이에요.'] : [], nodes: [noteBlock(v, g.b ? Number(g.b) : 0.5)]};
        });
    def('rBeepNoun', 'robot', 'act', '(?<w>삐삐|삐|띵동|딩동|경고음|알림음)\\s*(?:소리)?(?:\\s*(?:한\\s*번|한번))?(?=\\s*$|\\s*[,.!]|\\s+(?:나게|내|울려))',
        () => ({nodes: [beep('경고')]}));
    def('rBeep', 'robot', 'act', `(?<w>삐삐|삐|경고음|알림음|부저|버저|띵동|딩동)(?:\\s*소리)?(?:를|을|가)?\\s*(?:내|울려|울리|켜|해|나)${REST}`,
        (g, ctx) => ((ctx.robot || /부저|버저|경고음|알림음|삐/.test(g.w)) ? {nodes: [beep('경고')]} : null));

    // ═══════════════ 큐브로이드 로봇 — ~하면(센서) ═══════════════
    def('rObstacleNot', 'robot', 'cond',
        '(?:장애물|앞에\\s*(?:뭐가|아무것도|아무\\s*것도|뭔가))\\s*(?:이|가)?\\s*(?:없으면|안\\s*보이면|없을\\s*때|없는\\s*동안)',
        () => ({cond: act('operator_not', {boolIn: {OPERAND: obstacle()}, sig: 'not:obstacle', label: '장애물이 감지되지 않았는가?'})}));
    def('rObstacle', 'robot', 'cond',
        '(?:(?:근접\\s*센서|센서)(?:에|에다)?\\s*(?:손(?:을)?\\s*)?(?:가까이\\s*)?(?:대면|가져가면|갖다\\s*대면|가까이\\s*하면)|손(?:을)?\\s*(?:가까이\\s*)?(?:대면|가져가면|갖다\\s*대면)|앞에\\s*(?:뭐가|뭐|무언가|뭔가|물체가?|물건이?|벽이|벽|장애물이?|사람이?|손이?|누가|누군가)?\\s*(?:있으면|있을\\s*때|나타나면|보이면|오면|가까우면)|장애물(?:이|을|에)?\\s*(?:있으면|감지되면|감지되었을\\s*때|감지됐을\\s*때|보이면|만나면|나타나면|가까우면|감지하면|걸리면|닿으면)|(?:(?:근접\\s*센서|센서)\\s*(?:에|에다|앞에|쪽에|쪽으로)?\\s*)?(?:무언가|뭔가|뭐가|물체가|물건이|손이|누가|누군가|사람이)\\s*가까이\\s*(?:오면|가면|있으면|다가오면)|가까워지면|(?:앞이|길이|앞길이)\\s*(?:막히면|막혀\\s*있으면|막혔으면))',
        () => ({cond: obstacle()}));
    def('rProxValue', 'robot', 'cond', '(?:근접\\s*센서|거리\\s*센서|센서)?\\s*(?:값|거리)(?:이|가)?\\s*\\d+\\s*(?:보다)?\\s*(?:넘으면|크면|작으면|가까우면|이하면|이상이면)',
        (g, ctx) => (ctx.robot ? {notes: ['근접 센서는 거리 숫자 대신 ‘가까이에 뭔가 있는지(참/거짓)’만 알려 줘요. 그래서 ‘장애물이 감지되었는가?’로 바꿨어요.'], cond: obstacle()} : null));
    def('rButton', 'robot', 'cond', ['(?:터치(?:\\s*센서)?(?:를|을|가)?\\s*(?:하면|누르면|눌리면)|터치하면)(?:마다)?',
        '(?:버튼|단추)(?:을|를|이)?\\s*(?:눌러졌을\\s*때|눌러지면|누르면|눌리면|눌렀을\\s*때|누를\\s*때|누르고\\s*있으면|눌려\\s*있으면|누르는\\s*동안|눌러져\\s*있으면)(?:마다)?'],
        (g, ctx, alt) => (alt === 0 && !ctx.robot ? null : {cond: button()}));
    def('rDark', 'robot', 'cond',
        '(?:(?:주변이|방이|교실이|주위가)\\s*)?(?:어두워지면|어두우면|어두울\\s*때|깜깜하면|깜깜해지면|밤이\\s*되면|불이\\s*꺼지면|빛이\\s*없으면)',
        () => ({notes: [NOTE_BRIGHT], cond: bright('<', 500)}));
    def('rBright', 'robot', 'cond',
        '(?:(?:주변이|방이|교실이|주위가)\\s*)?(?:밝아지면|밝으면|밝을\\s*때|환해지면|환하면|낮이\\s*되면)',
        () => ({cond: bright('>', 500)}));
    def('rBrightNum', 'robot', 'cond',
        '(?:(?:빛|밝기|조도)\\s*센서(?:의)?\\s*)?밝기?(?:\\s*센서)?\\s*(?:값)?(?:이|가)?\\s*(?<n>\\d+)\\s*(?:보다)?\\s*(?<op>작으면|낮으면|적으면|크면|높으면|많으면|이하면|미만이면|이상이면|넘으면)',
        g => ({cond: bright(/작|낮|적|이하|미만/.test(g.op) ? '<' : '>', toNum(g.n))}));

    def('rSensorSay', 'robot', 'act', `(?<s>근접\\s*센서|거리\\s*센서|밝기\\s*센서|빛\\s*센서|조도\\s*센서|밝기)\\s*(?:값|거리)?(?:을|를)?\\s*(?:(?:고양이|로봇)(?:가|이)?\\s*)?(?:(?:무대|화면)(?:에|에다)?\\s*)?(?:(?<lp>계속)\\s*)?(?:말|알려|보여|띄워|표시|출력)[가-힣]*`,
        g => {
            const prox = /근접|거리/.test(g.s);
            const r = prox ? obstacle() : act('cubroidlighttouch_getBrightness', {sig: 'brightness', label: '밝기'});
            return {loopBefore: Boolean(g.lp), notes: prox ? ['근접 센서는 거리 숫자 대신 ‘감지되었는가’(참/거짓)만 알려 줘요.'] : [],
                nodes: [sayOf(r, null, r.label)]};
        });
    def('rSensorVar', 'robot', 'act', `(?<s>밝기|근접\\s*센서|거리)\\s*(?:센서\\s*)?(?:값)?(?:을|를)?\\s*(?:(?<v>[가-힣A-Za-z]{1,6})\\s*)?변수(?:에|에다)?\\s*(?:저장|넣|담)[가-힣]*`,
        g => {
            const prox = /근접|거리/.test(g.s);
            const r = prox ? obstacle() : act('cubroidlighttouch_getBrightness', {sig: 'brightness', label: '밝기'});
            const name = g.v || (prox ? '장애물' : '밝기');
            return {notes: ['센서 값은 계속 바뀌어서, 저장하는 순간의 값이 변수에 들어가요.'], nodes: [setVar(name, r)]};
        });

    // ── 아니면(else) ── "~하면 A 아니면 B"
    def('else', 'control', 'else',
        '(?:아니면|안\\s*그러면|그렇지\\s*않으면|그게\\s*아니면|없으면|안\\s*보이면|안\\s*닿으면|안닿으면|손을?\\s*떼면|떼면|안\\s*누르면|안누르면|안\\s*눌리면|평소에는|평소엔)',
        () => ({}));

    // ── ~하면(조건) ──
    def('touchEdge', 'sensing', 'cond', `${RE_EDGE}(?:에|을|이|과|와|랑|이랑)?\\s*${RE_TOUCH}`,
        () => ({cond: act('sensing_touchingobject', {
            inputs: {TOUCHINGOBJECTMENU: menu('sensing_touchingobjectmenu', 'TOUCHINGOBJECTMENU', '_edge_')},
            sig: 'touchedge', label: '벽에 닿았는가?'
        })}));
    def('touchMouse', 'sensing', 'cond', `${RE_MOUSE}(?:에|를|와|과|랑|이랑|하고|가|이)?\\s*${RE_TOUCH}`,
        () => ({cond: act('sensing_touchingobject', {
            inputs: {TOUCHINGOBJECTMENU: menu('sensing_touchingobjectmenu', 'TOUCHINGOBJECTMENU', '_mouse_')},
            sig: 'touchmouse', label: '마우스 포인터에 닿았는가?'
        })}));
    // "닿으면"만 있을 때 — 앞에 마우스 이야기가 있으면 마우스, 아니면 벽. "튕길 때마다"도 벽.
    def('touchBare', 'sensing', 'cond', [`${RE_TOUCH}`, '(?:튕길\\s*때(?:마다)?|튕기면)'], (g, ctx, alt, before) => {
        const mouse = alt === 0 && /마우스|커서|포인터/.test(before || '');
        return {notes: [`무엇에 닿는지 말하지 않아서 ‘${mouse ? '마우스 포인터' : '벽'}’로 했어요.`], cond: act('sensing_touchingobject', {
            inputs: {TOUCHINGOBJECTMENU: menu('sensing_touchingobjectmenu', 'TOUCHINGOBJECTMENU', mouse ? '_mouse_' : '_edge_')},
            sig: mouse ? 'touchmouse' : 'touchedge', label: mouse ? '마우스 포인터에 닿았는가?' : '벽에 닿았는가?'
        })};
    });
    def('keyHeld', 'sensing', 'cond',
        `${RE_KEY}\\s*(?:키|화살표|방향키)?\\s*(?:가|이|를|을)?\\s*(?:눌려\\s*있으면|누르고\\s*있으면|눌러져\\s*있으면|누르고\\s*있는\\s*동안|누르고\\s*있을\\s*때|누르는\\s*동안|눌렸으면|눌렀으면|눌러졌으면|눌렀다면)`,
        g => {
            const k = keyValue(g.key);
            if (!k) return null;
            return {cond: act('sensing_keypressed', {
                inputs: {KEY_OPTION: menu('sensing_keyoptions', 'KEY_OPTION', k)},
                sig: `keyheld:${k}`, label: `${keyKo(k)} 키를 눌렀는가?`
            })};
        });
    def('mouseDown', 'sensing', 'cond', '마우스(?:를|가|버튼을|버튼이)?\\s*(?:누르고\\s*있으면|눌려\\s*있으면|클릭하고\\s*있으면|누르는\\s*동안|클릭하는\\s*동안|(?:누르고|클릭하고|눌러)\\s*있는\\s*동안)',
        () => ({cond: act('sensing_mousedown', {sig: 'mousedown', label: '마우스를 클릭했는가?'})}));

    // 점수 조건 — "점수가 10점 되면"(게임 규칙의 핵심). 변수 값 블록을 비교 블록 안에 넣는다.
    def('scoreIs', 'operators', 'cond',
        [`(?<v>점수|스코어|포인트|카운트)(?:가|이)?\\s*(?<n>${RE_NUM})\\s*(?:점)?\\s*(?:이|가)?\\s*(?<op>되면|넘으면|넘어가면|보다\\s*크면|보다\\s*많으면|이상이면|이면|일\\s*때|됐을\\s*때|될\\s*때)`,
            `(?<n>${RE_NUM})\\s*점\\s*(?:이|가)?\\s*(?<op>되면|넘으면|이면|됐을\\s*때|될\\s*때)`],
        g => {
            const name = g.v === '카운트' ? '카운트' : '점수';
            let n = toNum(g.n);
            if (n === null) return null;
            const notes = [];
            let opcode = 'operator_equals';
            let sym = '=';
            if (/넘|크|많/.test(g.op)) { opcode = 'operator_gt'; sym = '>'; }
            if (/이상/.test(g.op)) {
                opcode = 'operator_gt'; sym = '>';
                n -= 1;
                notes.push(`‘${fmt(n + 1)} 이상’은 ‘${fmt(n)}보다 크다’로 바꿨어요. 스크래치에는 ‘이상’ 블록이 없어서 이렇게 써요.`);
            }
            const reporter = act('data_variable', {varName: name, sig: name, label: name});
            return {notes, cond: act(opcode, {
                inputs: {OPERAND1: Object.assign(txt(''), {node: reporter}), OPERAND2: txt(fmt(n))},
                sig: `${name === '점수' ? 'score' : name}${sym}${fmt(n)}`, label: `${name} ${sym} ${fmt(n)}`
            })};
        });

    // ═══════════════ 블록 이름 그대로도 알아듣기(도구상자 전수 점검, 2026-09-23 대표 지적) ═══════════════
    // 미끄러지기 — "1초 동안 무작위 위치로 이동하기", "미끄러지듯이 가"
    const GLIDE_TO = '(?:(?<rnd>무작위\\s*위치|랜덤\\s*위치|아무\\s*데)|(?<ms>마우스(?:\\s*포인터)?)|x\\s*:?\\s*(?<x>-?\\d+)\\s*,?\\s*y\\s*:?\\s*(?<y>-?\\d+)|(?<ct>가운데|중앙))';
    def('glide', 'motion', 'act', [
        `(?<n>${RE_SEC})\\s*초\\s*(?:동안|만에)\\s*${GLIDE_TO}\\s*(?:으로|로|쪽으로)?\\s*(?:이동|가|미끄러)${REST}`,
        `(?:${GLIDE_TO}\\s*(?:으로|로|쪽으로)?\\s*)?(?:미끄러지듯이?|미끄러져|스르르|스르륵|부드럽게)\\s*(?:이동|가|움직|따라)?${REST}`,
        `(?:미끄러지듯이?|미끄러져|스르르|스르륵|부드럽게)\\s*${GLIDE_TO}\\s*(?:으로|로|쪽으로)?\\s*(?:이동|가|움직|따라)${REST}`
    ], g => {
        const sec = toNum(g.n) !== null ? toNum(g.n) : 1;
        const notes = toNum(g.n) === null ? ['얼마나 걸려 갈지 말하지 않아서 1초로 했어요.'] : [];
        if (g.x !== undefined || g.ct) {
            const x = g.ct ? 0 : toNum(g.x);
            const y = g.ct ? 0 : toNum(g.y);
            return {notes, nodes: [act('motion_glidesecstoxy', {inputs: {SECS: num(sec), X: num(x), Y: num(y)}, sig: `glide${fmt(sec)}:${x},${y}`, label: `${fmt(sec)}초 동안 x: ${x} y: ${y} 으로 이동하기`})]};
        }
        const to = g.ms ? '_mouse_' : '_random_';
        return {notes, nodes: [act('motion_glideto', {inputs: {SECS: num(sec), TO: menu('motion_glideto_menu', 'TO', to)},
            sig: `glide${fmt(sec)}:${g.ms ? 'mouse' : 'random'}`, label: `${fmt(sec)}초 동안 ${g.ms ? '마우스 포인터' : '무작위 위치'}로 이동하기`})]};
    });
    // 회전 방식 — "회전 방식을 왼쪽-오른쪽으로 정하기", "좌우로만 돌게", "뒤집히지 않게"
    def('rotStyle', 'motion', 'act', [
        '회전\\s*방식(?:을|를)?\\s*(?<st>왼쪽\\s*-?\\s*오른쪽|좌우|회전하지\\s*않기|회전\\s*안\\s*하기|안\\s*돌게|회전하기|모든\\s*방향)(?:으로|로)?\\s*(?:정하|정해|해|바꿔)[가-힣]*',
        '(?<lr>좌우로만|좌우만|왼쪽\\s*오른쪽(?:으로)?만)\\s*(?:돌|보|뒤집|바라)[가-힣]*|(?:(?:뒤집히는데|뒤집어지는데|뒤집혀서|거꾸로\\s*되는데)\\s*)?(?:(?<lr2>뒤집히지|뒤집어지지|거꾸로\\s*되지)\\s*않게|(?<lr4>안\\s*뒤집히|안뒤집히|안\\s*뒤집어지|안\\s*거꾸로)[가-힣]*)',
        '(?:거꾸로\\s*)?뒤집혀(?:서)?\\s*(?:이상해|싫어|이상함|불편해|보여)?\\s*(?<lr3>좌우로만|왼쪽\\s*오른쪽으로만)\\s*(?:돌|보|바라)[가-힣]*'
    ], g => {
        const st = g.lr || g.lr2 || g.lr3 || g.lr4 || /왼쪽|좌우/.test(g.st || '') ? 'left-right' : (/않|안/.test(g.st || '') ? "don't rotate" : 'all around');
        const ko = {'left-right': '왼쪽-오른쪽', "don't rotate": '회전하지 않기', 'all around': '회전하기'}[st];
        return {nodes: [act('motion_setrotationstyle', {fields: fld('STYLE', st), sig: `rotstyle:${st}`, label: `회전 방식을 ${ko}으로 정하기`})]};
    }, {negOk: true});

    // ═══════════════ 보강(연습 5차에서 나온 말투) ═══════════════
    def('arrowsLoose', 'events', 'multi', `(?:방향키|화살표\\s*키|화살표)(?:를|로|으로)?\\s*(?:(?:고양이|캐릭터|로봇|자동차)(?:를|가)?\\s*)?(?:상하좌우|위아래\\s*좌우|사방|위아래로|상하로)\\s*(?:다\\s*)?(?:움직|조종|이동|조작|운전)${REST}`,
        (g, ctx) => arrowMulti(['up arrow', 'down arrow', 'left arrow', 'right arrow'], ctx));
    def('wasdLoose', 'events', 'multi', `(?:키보드\\s*)?w\\s*a\\s*s\\s*d(?:\\s*키)?(?:로|으로|를)?\\s*(?:(?:고양이|캐릭터|로봇\\s*카|로봇|자동차|차)(?:를|을|가)?\\s*)?(?:움직|조종|이동|조작|운전)?${REST}`,
        (g, ctx) => arrowMulti(['w', 's', 'a', 'd'], ctx));
    def('keyNounHat', 'events', 'hat', `${RE_KEY}\\s*(?:화살표|방향키|키)(?:는|은)?(?=\\s*(?:앞으로|뒤로|왼쪽|오른쪽|위로|아래로|점프|멈춰|멈춤|멈추기|정지|전진|후진|좌회전|우회전))`,
        g => {
            const k = keyValue(g.key);
            if (!k) return null;
            return {hat: act('event_whenkeypressed', {fields: fld('KEY_OPTION', k), sig: `key:${k}`, label: `${keyKo(k)} 키를 눌렀을 때`})};
        });
    def('costumeRev', 'looks', 'act', [
        '(?:모양|코스튬|옷)\\s*다음(?:으로|걸로|거로|것으로)?(?:\\s*(?:바꿔|바꾸|넘겨|넘기)[가-힣]*)?',
        '(?:원래|처음)\\s*(?:모양|옷|코스튬)(?:으로|로)(?:\\s*(?:바꿔|돌아와|돌아가|해)[가-힣]*)?'
    ], (g, ctx, alt) => {
        if (alt === 0) return {nodes: [act('looks_nextcostume', {sig: 'nextcostume', label: '다음 모양으로 바꾸기'})]};
        const first = ((ctx && ctx.costumeNames) || ['모양1'])[0];
        return {nodes: [act('looks_switchcostumeto', {inputs: {COSTUME: menu('looks_costume', 'COSTUME', first)}, sig: 'costume=first', label: `모양을 ${euro(first)} 바꾸기`})]};
    });
    def('gotoXY2', 'motion', 'act', `x\\s*(?:좌표)?\\s*:?\\s*(?<x>${RE_NUM})\\s*,?\\s*y\\s*(?:좌표)?\\s*:?\\s*(?<y>${RE_NUM})\\s*(?:으로|로|에)?\\s*(?:보내|옮겨|옮기|가|이동)[가-힣]*`,
        g => ({nodes: [act('motion_gotoxy', {inputs: {X: num(toNum(g.x)), Y: num(toNum(g.y))}, sig: `goto${fmt(toNum(g.x))},${fmt(toNum(g.y))}`, label: `x: ${fmt(toNum(g.x))} y: ${fmt(toNum(g.y))} 으로 이동하기`})]}));
    def('glideFromTo', 'motion', 'act', `x\\s*(?<x1>${RE_NUM})\\s*(?:에서|부터)\\s*(?:시작해서|시작하고|출발해서|시작)?\\s*x\\s*(?<x2>${RE_NUM})\\s*까지\\s*(?<sl>천천히\\s*)?(?:미끄러|이동|가)[가-힣]*`,
        g => {
            const sec = g.sl ? 3 : 1;
            return {notes: ['시작 x를 먼저 정하고, 도착할 x까지 미끄러져요(y는 지금 그대로).'], nodes: [
                act('motion_setx', {inputs: {X: num(toNum(g.x1))}, sig: `x=${g.x1}`, label: `x 좌표를 ${euro(g.x1)} 정하기`}),
                act('motion_glidesecstoxy', {inputs: {SECS: num(sec), X: num(toNum(g.x2)), Y: inN(act('motion_yposition', {sig: 'ypos', label: 'y 좌표'}))}, sig: `glide${sec}:x${g.x2}`, label: `${sec}초 동안 x: ${g.x2} 로 이동하기`})]};
        });
    def('rotStyle2', 'motion', 'act', [
        '(?:뒤집어지지|뒤집히지|거꾸로\\s*되지)\\s*않게(?:\\s*(?:회전\\s*방식(?:을)?\\s*)?(?:왼쪽\\s*-?\\s*오른쪽|좌우)(?:으로|로)?만?)?(?:\\s*(?:해|해줘|정해)[가-힣]*)?',
        '회전\\s*방식(?:을|를)?\\s*(?:왼쪽\\s*-?\\s*오른쪽|좌우)(?:으로|로)?만?(?:\\s*(?:정하|정해|해|바꿔)[가-힣]*)?'
    ], () => ({nodes: [act('motion_setrotationstyle', {fields: fld('STYLE', 'left-right'), sig: 'rotstyle:left-right', label: '회전 방식을 왼쪽-오른쪽으로 정하기'})]}), {negOk: true});
    const VARW = '목숨|생명|체력|점수|스코어|코인|레벨|에너지|라이프|돈|카운트|횟수';
    const lastVarIn = (before, ctx) => {
        const m = (String(before || '').match(new RegExp(`(?:${VARW})`, 'g')) || []).pop();
        return m ? (/스코어/.test(m) ? '점수' : m) : (ctx && ctx.lastVar) || null;
    };
    def('varBareChange', 'variables', 'act', `(?<n>\\d+)\\s*(?:씩\\s*)?(?<d>줄이|줄여|깎|빼|올리|올려|늘리|늘려|더하|더해)[가-힣]*`, (g, ctx, alt, before) => {
        const v = lastVarIn(before, ctx);
        if (!v) return null;
        const n = toNum(g.n);
        return {nodes: [chVar(v, /줄|깎|빼/.test(g.d) ? -n : n)]};
    });
    def('varBareIs', 'operators', 'cond', `(?:(?<v>${VARW})(?:이|가)?\\s*)?(?<n>-?\\d+)\\s*(?:이|가)?\\s*(?:되면|이면|이\\s*되면)`, (g, ctx, alt, before) => {
        const v = g.v ? (/스코어/.test(g.v) ? '점수' : g.v) : lastVarIn(before, ctx);
        if (!v) return null;
        const n = toNum(g.n);
        return {cond: act('operator_equals', {inputs: {OPERAND1: inT(V(v)), OPERAND2: txt(fmt(n))}, sig: `${v === '점수' ? 'score' : v}=${fmt(n)}`, label: `${v} = ${fmt(n)}`})};
    });
    def('varSetAny2', 'variables', 'act', [
        `(?<v>[가-힣A-Za-z]{1,8}?)\\s*(?<n>\\d+)\\s*(?:개|점)?\\s*(?:짜리\\s*)?(?:로\\s*)?변수(?:를)?\\s*(?:만들|추가|정해)[가-힣]*`,
        `변수\\s*(?<v>[가-힣A-Za-z]{1,8}?)(?:를|을)?\\s*(?<n>\\d+)\\s*(?:으로|로|에서)\\s*(?:시작|정하|정해|해)[가-힣]*`
    ], g => ({notes: ['변수는 번역기가 만들어요. 변수 칸에서 확인해 보세요.'], nodes: [setVar(/^(?:스코어|포인트)$/.test(g.v) ? '점수' : g.v, toNum(g.n))]}));
    def('sayThinkSec', 'looks', 'act', `(?<v>말|생각)(?:을|를)?\\s*(?:해|하|하기)${REST}\\s*(?<s>${RE_SEC})\\s*초(?:\\s*동안)?`, g => {
        const m = (g.m || '').trim() || '음...';
        const think = g.v === '생각';
        const sec = toNum(g.s);
        return {nodes: [act(think ? 'looks_thinkforsecs' : 'looks_sayforsecs', {inputs: {MESSAGE: txt(m), SECS: num(sec)},
            sig: `${think ? 'thinkfor' : 'sayfor'}(${m},${fmt(sec)})`, label: `‘${m}’을(를) ${fmt(sec)}초 동안 ${think ? '생각하기' : '말하기'}`})]};
    }, {left: [0]});
    def('clickCounter', 'variables', 'multi', `(?:(?<v>[가-힣]{1,6})\\s*변수로\\s*)?(?:고양이(?:를)?\\s*)?몇\\s*번\\s*(?:클릭|눌렀|누른|터치)[가-힣]*\\s*(?:세|셀|카운트)[가-힣]*(?:\\s*(?:보여|말|표시)[가-힣]*)?`,
        g => {
            const v = g.v || '클릭 수';
            return {notes: [`‘${v}’ 변수를 0으로 정하고 무대에 보여요. 고양이를 클릭할 때마다 1씩 늘어요.`], multi: [
                {hat: act('event_whenflagclicked', {sig: 'flag', label: '깃발을 클릭했을 때'}), body: [setVar(v, 0), act('data_showvariable', {varName: v, sig: `varshow:${v}`, label: `${v} 변수 보이기`})]},
                {hat: act('event_whenthisspriteclicked', {sig: 'click', label: '이 스프라이트를 클릭했을 때'}), body: [chVar(v, 1)]}]};
        });
    def('rButtonCounter', 'robot', 'act', `(?:버튼|터치\\s*센서|터치)(?:을|를)?\\s*(?:몇\\s*번\\s*(?:눌렀|누른)[가-힣]*\\s*(?:세|셀)[가-힣]*|(?:누를|할|했을)\\s*때\\s*마다\\s*(?:(?:LED|led|매트릭스)(?:에|의)?\\s*)?(?:숫자(?:가|를)?\\s*)?(?:1씩\\s*)?(?:올라|늘어|세|올려|커)[가-힣]*)(?:\\s*(?:매트릭스|LED)?(?:에)?\\s*(?:보여|표시|말)[가-힣]*)?`,
        () => ({notes: ['버튼을 누를 때마다 ‘횟수’를 1씩 올리고, 고양이가 말해요. LED 판은 숫자 0~9 그림만 있어서 변수 값을 바로 띄울 수는 없어요.'],
            nodes: [setVar('횟수', 0), {t: 'forever', body: [act('control_wait_until', {boolIn: {CONDITION: button()}, sig: 'until:button', label: '‘버튼이 눌러졌는가?’ 까지 기다리기'}),
                chVar('횟수', 1), sayOf(V('횟수'), null, '횟수'),
                act('control_wait_until', {boolIn: {CONDITION: act('operator_not', {boolIn: {OPERAND: button()}, sig: 'not:button', label: '버튼이 눌러지지 않았는가?'})}, sig: 'until:not:button', label: '‘버튼이 눌러지지 않았는가?’ 까지 기다리기'})]}]}));
    def('xCond', 'operators', 'cond', '(?:고양이(?:의)?\\s*)?(?<ax>x|y)\\s*(?:좌표)?(?:가|이|는)?\\s*(?<n>-?\\d+)\\s*보다\\s*(?<op>크면|작으면)', g => {
        const y = g.ax === 'y';
        const r = act(y ? 'motion_yposition' : 'motion_xposition', {sig: y ? 'ypos' : 'xpos', label: `${g.ax} 좌표`});
        const gt = /크/.test(g.op);
        return {cond: act(gt ? 'operator_gt' : 'operator_lt', {inputs: {OPERAND1: inT(r), OPERAND2: txt(g.n)}, sig: `${g.ax}${gt ? '>' : '<'}${g.n}`, label: `${g.ax} 좌표 ${gt ? '>' : '<'} ${g.n}`})};
    });
    def('setAxisTo', 'motion', 'act', '(?<n>-?\\d+)\\s*(?:으로|로)\\s*(?:옮겨|옮기|보내|보내줘)[가-힣]*', (g, ctx, alt, before) => {
        const m = (String(before || '').match(/(?:^|[^a-z])([xy])(?=\s|좌표|가|이|는|$)/g) || []).pop();
        if (!m) return null;
        const ax = /y/.test(m) ? 'y' : 'x';
        return {nodes: [act(ax === 'x' ? 'motion_setx' : 'motion_sety', {inputs: {[ax.toUpperCase()]: num(toNum(g.n))}, sig: `${ax}=${g.n}`, label: `${ax} 좌표를 ${euro(g.n)} 정하기`})]};
    });
    def('diagDir', 'motion', 'act', '(?<d1>오른쪽|왼쪽)\\s*(?<d2>위|아래)(?:쪽)?(?:으로|로)?\\s*(?:가|이동|움직|날아)[가-힣]*', g => {
        const dx = /왼/.test(g.d1) ? -10 : 10;
        const dy = /아래/.test(g.d2) ? -10 : 10;
        return {nodes: [act('motion_changexby', {inputs: {DX: num(dx)}, sig: `x${dx >= 0 ? '+' : ''}${dx}`, label: `x 좌표를 ${dx}만큼 바꾸기`}),
            act('motion_changeyby', {inputs: {DY: num(dy)}, sig: `y${dy >= 0 ? '+' : ''}${dy}`, label: `y 좌표를 ${dy}만큼 바꾸기`})]};
    });
    def('dirNum', 'motion', 'act', '(?<d>위|아래|오른쪽|왼쪽)(?:쪽)?(?:으로|로)\\s*(?<n>\\d+)(?=\\s|$)', g => {
        const n = toNum(g.n);
        const vert = /위|아래/.test(g.d);
        const v = /아래|왼/.test(g.d) ? -n : n;
        return {nodes: [act(vert ? 'motion_changeyby' : 'motion_changexby', {inputs: {[vert ? 'DY' : 'DX']: num(v)}, sig: `${vert ? 'y' : 'x'}${v >= 0 ? '+' : ''}${v}`, label: `${vert ? 'y' : 'x'} 좌표를 ${v}만큼 바꾸기`})]};
    });
    def('dirNoun2', 'motion', 'act', '(?<d>위|아래)(?:쪽)?(?:으로|로)(?=\\s*$|\\s*[,.!]|\\s+\\S+\\s*(?:키|화살표)?\\s*(?:를|을)?\\s*누르면)', (g, ctx) => {
        if (ctx.robotOnly) return null;
        const v = /아래/.test(g.d) ? -10 : 10;
        return {nodes: [act('motion_changeyby', {inputs: {DY: num(v)}, sig: `y${v >= 0 ? '+' : ''}${v}`, label: `y 좌표를 ${v}만큼 바꾸기`})]};
    });
    def('moveGo', 'motion', 'act', `(?<n>${RE_NUM})\\s*(?:걸음|칸|발자국|만큼)\\s*(?:앞으로\\s*)?(?:가|가고|가서|가줘|가게)(?![가-힣]*다가)[가-힣]*`, (g, ctx) => {
        if (ctx.robotOnly) return null;
        const n = toNum(g.n);
        return {nodes: [act('motion_movesteps', {inputs: {STEPS: num(n)}, sig: `move${fmt(n)}`, label: `${fmt(n)}만큼 움직이기`})]};
    });
    def('gapWait', 'control', 'act', `(?:사이사이|사이에|중간중간|중간에)\\s*(?<n>${RE_SEC})\\s*초\\s*(?:씩\\s*)?(?:쉬|기다)[가-힣]*`,
        g => ({notes: ['‘사이사이 쉬기’는 반복 안 맨 끝에 기다리기를 넣어요.'], nodes: [Object.assign(waitS(toNum(g.n)), {gap: true})]}));
    def('jumpWait', 'motion', 'act', `점프(?:했다가|하고|해서)\\s*(?<s>${RE_SEC})\\s*초\\s*(?:뒤에|후에|있다가|동안\\s*있다가)\\s*(?:내려오|내려와|떨어지)[가-힣]*`, g => {
        const s0 = toNum(g.s);
        return {notes: [NOTE.jump], nodes: [
            act('motion_changeyby', {inputs: {DY: num(50)}, sig: 'y+50', label: 'y 좌표를 50만큼 바꾸기', part: 'jumpUp'}),
            act('control_wait', {inputs: {DURATION: num(s0, 'math_positive_number')}, sig: `wait${fmt(s0)}`, label: `${fmt(s0)}초 기다리기`, part: 'jumpWait'}),
            act('motion_changeyby', {inputs: {DY: num(-50)}, sig: 'y-50', label: 'y 좌표를 -50만큼 바꾸기', part: 'jumpDown'})]};
    });
    def('ghostBlink', 'looks', 'act', '투명(?:하게|해지게|해졌다)?\\s*(?:반짝|깜빡)[가-힣]*', () => ({notes: ['투명하게 반짝이기는 유령 효과 70과 0을 번갈아 정하는 걸 계속 반복해요.'],
        nodes: loopAll([act('looks_seteffectto', {fields: fld('EFFECT', 'GHOST'), inputs: {VALUE: num(70)}, sig: 'ghost=70', label: '유령 효과를 70으로 정하기'}), waitS(0.3),
            act('looks_seteffectto', {fields: fld('EFFECT', 'GHOST'), inputs: {VALUE: num(0)}, sig: 'ghost=0', label: '유령 효과를 0으로 정하기'}), waitS(0.3)])}));
    def('centerStart', 'motion', 'act', '처음\\s*(?:위치|자리)(?:로|으로)(?=\\s*$|\\s*[,.!])',
        () => ({nodes: [act('motion_gotoxy', {inputs: {X: num(0), Y: num(0)}, sig: 'goto0,0', label: 'x: 0 y: 0 으로 이동하기'})]}));
    def('numberGuess', 'sensing', 'act', '숫자\\s*(?:맞추기|맞히기|맞추는|맞히는)(?:\\s*게임)?(?:[^.!?]*?(?<a>\\d+)\\s*(?:에서|부터)\\s*(?<b>\\d+)\\s*(?:사이|까지))?[가-힣\\s]*',
        g => {
            const b = toNum(g.b) || 100;
            const a = toNum(g.a) || 1;
            const ans = () => act('sensing_answer', {sig: 'answer', label: '대답'});
            return {notes: [`‘정답’ 변수에 ${a}~${b} 난수를 넣고, 맞힐 때까지 물어봐요. 크면 ‘더 작아’, 작으면 ‘더 커’라고 알려 줘요.`], nodes: [
                setVar('정답', act('operator_random', {inputs: {FROM: num(a), TO: num(b)}, sig: `random${a}-${b}`, label: `${a}부터 ${b} 사이의 난수`})),
                {t: 'forever', body: [act('sensing_askandwait', {inputs: {QUESTION: txt(`${a}부터 ${b} 사이 숫자를 맞혀 봐!`)}, sig: 'ask(guess)', label: '숫자를 맞혀 보라고 묻고 기다리기'}),
                    {t: 'ifelse', noLoop: true, cond: act('operator_equals', {inputs: {OPERAND1: inT(ans()), OPERAND2: inT(V('정답'))}, sig: 'answer=정답', label: '대답 = 정답'}),
                        body: [sayOf('정답!', 2, '정답!'), act('control_stop', {fields: fld('STOP_OPTION', 'all'), mutation: {tagName: 'mutation', children: [], hasnext: 'false'}, sig: 'stop', label: '멈추기 (모두)', cap: true})],
                        body2: [{t: 'ifelse', noLoop: true, cond: act('operator_gt', {inputs: {OPERAND1: inT(ans()), OPERAND2: inT(V('정답'))}, sig: 'answer>정답', label: '대답 > 정답'}),
                            body: [sayOf('더 작아!', 1, '더 작아!')], body2: [sayOf('더 커!', 1, '더 커!')]}]}]}]};
        });
    def('penColorChange', 'pen', 'act', '(?:펜|선)\\s*(?:색|색깔)(?:이|을)?\\s*(?:바뀌|바꿔|바꾸|변하)[가-힣]*',
        () => ({nodes: [act('pen_changePenColorParamBy', {inputs: {COLOR_PARAM: menu('pen_menu_colorParam', 'colorParam', 'color'), VALUE: num(10)}, sig: 'pen:color+10', label: '펜 색깔을 10만큼 바꾸기'})]}));
    def('penDrawWhile', 'pen', 'act', '선(?:을)?\\s*그리(?:면서|며|고)', () => ({nodes: [act('pen_penDown', {sig: 'pen:down', label: '펜 내리기'})]}));
    def('penStamp', 'pen', 'act', '도장(?:을)?\\s*(?:찍|찍어|찍기)[가-힣]*', () => ({notes: ['도장 찍기는 펜 확장의 ‘도장 찍기’ 블록이에요. 지금 모습 그대로 무대에 찍혀요.'], nodes: [act('pen_stamp', {sig: 'pen:stamp', label: '도장 찍기'})]}));
    def('penTrailColor', 'pen', 'act', `(?:지나간|지나가는|움직인|다닌)\\s*(?:자리|곳|길|데)(?:에|를|마다)?\\s*(?<c>${RE_COLOR}|검은|까만|검정)\\s*(?:색)?\\s*선(?:을|이)?\\s*(?:그리|그려|남기|남겨|생기)[가-힣]*`,
        g => ({notes: ['‘펜 내리기’를 하면 움직이는 곳마다 선이 그려져요.'], nodes: [
            act('pen_setPenColorToColor', {inputs: {COLOR: {sh: 'colour_picker', field: 'COLOUR', value: COLOR_HEX[g.c] || '#000000'}}, sig: `pen:color${COLOR_HEX[g.c] || '#000000'}`, label: `펜 색깔을 ${g.c}색으로 정하기`}),
            act('pen_penDown', {sig: 'pen:down', label: '펜 내리기'})]}));
    // 로봇
    def('rStop2', 'robot', 'act', `(?:(?:로봇|로보트|자동차|차|모터|모타|바퀴)(?:를|을|가|이|는|도)?\\s*)(?:둘\\s*다|모두|다|전부)?\\s*(?:멈춰|멈추|멈춤|정지|세워|스톱|꺼)[가-힣]*`,
        (g, ctx) => (ctx.robot ? {nodes: stopBoth()} : null));
    def('rLightOn2', 'robot', 'act', `(?:마스터\\s*블록|마스터)(?:의)?\\s*(?:불(?:빛)?)?(?:을|를)?\\s*(?:(?<c>${RE_COLOR})\\s*(?:색)?\\s*(?:불)?(?:으로|로)?\\s*)?(?:켜|켜줘|켜주고|켜고)[가-힣]*`,
        g => ({nodes: [colorOn(g.c ? COLOR_HEX[g.c] : '#ffffff')]}));
    def('rLightCycle', 'robot', 'act', `(?:(?:마스터\\s*)?불빛\\s*)?(?<list>(?:${RE_COLOR})(?:색)?(?:\\s*(?:,|이랑|하고|랑)?\\s*(?:${RE_COLOR})(?:색)?){2,6})\\s*(?:(?<n>${RE_SEC})\\s*초\\s*(?:씩|마다|간격으로)\\s*)?(?:번갈아|차례로|돌아가며|순서대로|바뀌)[가-힣]*`,
        g => {
            const re = new RegExp(`(${RE_COLOR})`, 'g');
            const cs = [];
            let m;
            while ((m = re.exec(g.list)) !== null) cs.push(COLOR_HEX[m[1]]);
            const sec = toNum(g.n) !== null ? toNum(g.n) : 1;
            return {notes: [`색을 ${fmt(sec)}초씩 차례로 켜는 걸 계속 반복해요.`], nodes: loopAll(cs.reduce((a, h) => a.concat([colorOn(h), waitN(sec)]), []))};
        }, {once: true});
    def('rBeepCount', 'robot', 'act', `(?:부저|버저)?\\s*(?<w>삐삐삐|삐삐|삐|띠띠띠|띠띠|띠|빵빵|경적|클랙슨)(?:\\s*(?:경적|클랙슨))?\\s*(?:소리)?(?:를|을)?\\s*(?:(?<n>\\d+|${RE_KNUM})\\s*번)?(?:\\s*(?:울려|울리기|울리게|내|내줘|내기|하고))?`,
        g => {
            let n = toNum(g.n);
            if (n === null) n = /띠띠띠|삐삐삐/.test(g.w) ? 3 : (/띠띠|삐삐|빵빵/.test(g.w) ? 2 : 1);
            const one = [beep('경고'), waitN(0.2)];
            return {nodes: n <= 1 ? [beep('경고')] : [{t: 'repeat', times: Math.round(n), body: one}]};
        });
    def('rObstacle2', 'robot', 'cond', '(?:장애물|물체|뭔가|무언가|벽|누가|누군가|사람|손|사람이|손이|친구가?)(?:이|가)?\\s*(?:더\\s*)?가까이\\s*(?:오면|있으면|다가오면)|(?:근접\\s*센서|센서)(?:가|에|에서)?\\s*(?:가까우면|가까워지면|감지되면|반응하면)',
        () => ({cond: obstacle()}));
    def('rObstacleFar', 'robot', 'cond', '(?:장애물|물체|앞)?(?:이|가)?\\s*(?:멀면|멀어지면|멀리\\s*있으면)', (g, ctx) => (ctx.robot ? {cond: act('operator_not', {boolIn: {OPERAND: obstacle()}, sig: 'not:obstacle', label: '장애물이 감지되지 않았는가?'})} : null));
    def('rWheelSide', 'robot', 'act', `(?<side>왼쪽|오른쪽)\\s*(?:바퀴|모터)(?:만)?(?:을|를)?\\s*(?:(?<n>${RE_SEC})\\s*초\\s*(?:동안\\s*)?)?(?:(?<rev>반대로|거꾸로|뒤로|반대\\s*방향으로)\\s*)?(?:돌려|돌리|돌아|움직|켜)[가-힣]*`, g => {
        const id = g.side === '왼쪽' ? M1 : M2;
        const sec = toNum(g.n);
        const fwdDir = id === M1 ? 'Right' : 'Left';
        const nodes = [motor(id, g.rev ? (fwdDir === 'Right' ? 'Left' : 'Right') : fwdDir)];
        if (sec !== null) nodes.push(waitN(sec), motorOff(id));
        return {notes: [`${g.side} 바퀴를 ${id === M1 ? 1 : 2}번 모터로 봤어요. 로봇마다 달 수 있으니 반대면 번호를 바꿔 보세요.`], nodes};
    });
    def('rLetters', 'robot', 'act', [
        '(?<seq>[A-Z](?:\\s*,?\\s*[A-Z]){1,8})\\s*(?:도)?\\s*(?:차례로|순서대로|하나씩)?\\s*(?:보여|띄워|표시)[가-힣]*',
        '(?<a>[A-Z])\\s*(?:글자)?(?:를|을)?\\s*(?:보여|띄워|표시)[가-힣]*',
        '(?:^|\\s)(?<a>[A-Z])(?=\\s*$)'
    ], (g, ctx, alt) => {
        if (!ctx.robot && !/LED|led|글자|매트릭스/i.test(ctx.src || '')) return null;
        const L1 = a => act('cubroidled_lceAlpabetControl', {fields: fld('MATRIX', LED_ABC[a]), sig: `led:${a}`, label: `LED에 글자 ${a} 보여주기`});
        if (alt === 0) {
            const ls = g.seq.replace(/[^A-Z]/g, '').split('');
            return {notes: ['글자마다 1초씩 보여줘요.'], nodes: ls.reduce((acc, a, i) => acc.concat(i ? [waitN(1), L1(a)] : [L1(a)]), [])};
        }
        return {nodes: [L1(g.a)]};
    });
    def('rToggleDrive', 'robot', 'act', '(?:버튼|터치)(?:을|를)?\\s*누르면\\s*(?:로봇(?:이)?\\s*)?(?:앞으로\\s*)?(?:가|출발|달리|움직)[가-힣]*\\s*(?:다시|한\\s*번\\s*더)\\s*누르면\\s*(?:멈추|멈춰|서|정지)[가-힣]*',
        () => ({notes: ['‘상태’ 변수로 가고 있는지 기억해요. 버튼을 누를 때마다 가기 ↔ 멈추기를 바꾸고, 손을 뗄 때까지 기다려요.'],
            nodes: [setVar('상태', 0), {t: 'forever', body: [
                act('control_wait_until', {boolIn: {CONDITION: button()}, sig: 'until:button', label: '‘버튼이 눌러졌는가?’ 까지 기다리기'}),
                {t: 'ifelse', noLoop: true, cond: act('operator_equals', {inputs: {OPERAND1: inT(V('상태')), OPERAND2: txt('0')}, sig: '상태=0', label: '상태 = 0'}),
                    body: drive('forward', null, false).concat([setVar('상태', 1)]), body2: stopBoth().concat([setVar('상태', 0)])},
                act('control_wait_until', {boolIn: {CONDITION: act('operator_not', {boolIn: {OPERAND: button()}, sig: 'not:button', label: '버튼이 눌러지지 않았는가?'})}, sig: 'until:not:button', label: '‘버튼이 눌러지지 않았는가?’ 까지 기다리기'})]}]}));
    def('rSpeedWord', 'robot', 'act', [
        '(?:속도|스피드|세기|파워|힘)(?:를|을|는|가)?\\s*(?:좀\\s*|조금\\s*|더\\s*|제일\\s*)?(?<w>느리게|천천히|낮게|빠르게|높게|최대로|최고로|최대|최고|약하게|세게|강하게|줄여|낮춰|올려|높여)(?:\\s*(?:해서|하고|해|해줘|줘|해주세요))?',
        '(?:DC\\s*)?모터\\s*(?<n>\\d+)\\s*(?:%|퍼센트)\\s*(?:으로|로|속도로)?'
    ], (g, ctx) => {
        if (!ctx.robot) return null;
        const n = g.n !== undefined ? Math.max(0, Math.min(100, toNum(g.n))) : (/느리|천천|낮|약|줄/.test(g.w) ? 50 : 100);
        const sp = id => act(`${id}_dcMotorSetSpeed`, {inputs: {SPEED: num(n)}, sig: `m${id === M1 ? 1 : 2}:speed${n}`, label: `DC 모터 ${id === M1 ? 1 : 2}번 속도를 ${n}%로 정하기`});
        return {nodes: [sp(M1), sp(M2)]};
    });
    def('rForeBack2', 'robot', 'act', `${CAR_WORD}(?<n>${RE_SEC})\\s*초\\s*(?:동안\\s*)?앞으로\\s*(?:갔다가?|가고)\\s*(?:바로\\s*)?뒤로\\s*(?<n2>${RE_SEC})\\s*초`,
        (g, ctx) => (ctx.robot ? {notes: [NOTE_CAR], nodes: [].concat(drive('forward', toNum(g.n), false), drive('backward', toNum(g.n2), false), stopBoth())} : null));
    def('rFaceNoun', 'robot', 'act', `(?<img>${RE_IMG})\\s*(?:모양|그림)(?=\\s*$|\\s*[,.!]|\\s+(?:아니면|없으면))`, (g, ctx) => {
        if (!ctx.robot) return null;
        const hit = LED_IMG.find(x => x[0].test(g.img));
        return hit ? {nodes: [act('cubroidled_lceImgControl', {fields: fld('MATRIX', hit[2]), sig: `led:${hit[1]}`, label: `LED에 ‘${hit[1]}’ 그림 보여주기`})]} : null;
    });
    def('rFaceBlink', 'robot', 'act', '(?:LED|엘이디|led)?\\s*(?:얼굴|표정)(?:을|이)?\\s*(?:깜빡|윙크)[가-힣]*', () => ({notes: ['LED 판에는 윙크 그림이 없어서, 웃는 얼굴을 켰다 지웠다 해요.'],
        nodes: loopAll([act('cubroidled_lceImgControl', {fields: fld('MATRIX', LED_IMG[0][2]), sig: 'led:Smile1', label: 'LED에 ‘Smile1’ 그림 보여주기'}), waitN(0.6),
            act('cubroidled_ledAllClearControl', {sig: 'led:clear', label: 'LED 화면 지우기'}), waitN(0.2)])}), {simile: true});
    def('rTurnKo', 'robot', 'act', `${CAR_WORD}(?<d>좌|우)\\s*회전(?:\\s*(?<n>${RE_SEC})\\s*초)?[가-힣]*`, (g, ctx) => {
        if (!ctx.robot) return null;
        const sec = toNum(g.n) !== null ? toNum(g.n) : 0.6;
        return {notes: [NOTE_SPIN], nodes: drive(g.d === '좌' ? 'left' : 'right', sec, true)};
    });

    def('dirTimes', 'motion', 'act', `(?<d>오른쪽|왼쪽|위쪽|아래쪽|위|아래)(?:으로|로)\\s*(?<t>\\d+|${RE_KNUM})\\s*번\\s*(?:움직|이동|가)[가-힣]*`, (g, ctx) => {
        if (ctx.robotOnly) return null;
        const vert = /위|아래/.test(g.d);
        const v = /아래|왼/.test(g.d) ? -10 : 10;
        return {nodes: [{t: 'repeat', times: Math.round(toNum(g.t)), body: [act(vert ? 'motion_changeyby' : 'motion_changexby', {inputs: {[vert ? 'DY' : 'DX']: num(v)}, sig: `${vert ? 'y' : 'x'}${v >= 0 ? '+' : ''}${v}`, label: `${vert ? 'y' : 'x'} 좌표를 ${v}만큼 바꾸기`})]}]};
    });
    def('scoreNoun', 'variables', 'act', `(?<n>${RE_ANY})\\s*점(?:\\s*씩)?(?=\\s*$|\\s*[,.!])`, (g, ctx, alt, before) => {
        if (!/점수|클릭|닿|누르|받/.test(before || '')) return null;
        const n = Math.abs(toNum(g.n) || 1);
        return {nodes: [chVar('점수', n)]};
    });
    def('scoreFrom', 'variables', 'act', `(?:점수|스코어)(?:를|을|는)?\\s*(?<n>${RE_NUM})\\s*(?:점)?\\s*부터\\s*(?:다시\\s*)?(?:시작|해)[가-힣]*`,
        g => ({nodes: [setVar('점수', toNum(g.n))]}));
    def('sizeGr2', 'looks', 'act', '크기(?:가|를)?\\s*(?<gr>점점|조금씩|서서히)\\s*(?<d>작아지|작아져|작게|줄어|줄여|커지|커져|크게|키워)[가-힣]*', g => {
        const v = /작|줄/.test(g.d) ? -5 : 5;
        return {notes: [`‘${g.gr}’${hasBatchim(g.gr) ? '은' : '는'} ‘조금씩 10번 반복하기’로 나눴어요.`], nodes: [{t: 'repeat', times: 10, body: [act('looks_changesizeby', {inputs: {CHANGE: num(v)}, sig: `size${v >= 0 ? '+' : ''}${v}`, label: `크기를 ${v}만큼 바꾸기`}), waitS(0.05)]}]};
    });
    def('waitRev', 'control', 'act', `(?:기다리기|기다려|기다리|쉬기|쉬어)\\s*(?<n>${RE_SEC})\\s*초`,
        g => ({nodes: [waitS(toNum(g.n))]}));
    def('nounFwdBack', 'motion', 'act', '(?:^|\\s)(?<d>앞으로|뒤로)(?=\\s*$|\\s*[,.!]|\\s+\\S+\\s*(?:화살표|키))', (g, ctx) => {
        if (ctx.robot) return {notes: [NOTE_CAR], nodes: drive(g.d === '앞으로' ? 'forward' : 'backward', 0.5, true)};
        const n = g.d === '앞으로' ? 10 : -10;
        return {nodes: [act('motion_movesteps', {inputs: {STEPS: num(n)}, sig: `move${n}`, label: `${n}만큼 움직이기`})]};
    });
    def('rBackNoun', 'robot', 'act', `${CAR_WORD}뒤로\\s*(?<n>${RE_SEC})\\s*초(?=\\s*$|\\s*[,.!])`,
        (g, ctx) => (ctx.robot ? {nodes: drive('backward', toNum(g.n), true)} : null));
    def('rLedClearBare', 'robot', 'act', '(?:^|\\s)(?:지워|지우|지워줘|지우기)(?=\\s*$)', (g, ctx) => (ctx.robot && /LED|매트릭스|얼굴|하트|그림/.test(String(ctx.src || '') + String(ctx.ledCtx || '')) || ctx.ledCtx ? {nodes: [act('cubroidled_ledAllClearControl', {sig: 'led:clear', label: 'LED 화면 지우기'})]} : null));
    def('sayAnswerName', 'looks', 'act', '이름(?:을|를)?\\s*(?:말|외치)[가-힣]*', (g, ctx) => ((ctx.hasAsk || /대답|물어|묻/.test(ctx.src || '')) ?
        {nodes: [act('looks_say', {inputs: {MESSAGE: Object.assign(txt(''), {node: act('sensing_answer', {sig: 'answer', label: '대답'})})}, sig: 'say(대답)', label: '‘대답’ 말하기'})]} : null));
    def('typingGame', 'sensing', 'act', '(?:타자\\s*연습|타자|받아\\s*쓰기|단어\\s*맞추기)(?:\\s*게임)?[^.!?\\n]*', () => {
        const words = ['사과', '바나나', '고양이', '로봇', '코딩'];
        const L = (op, o) => act(op, Object.assign({listName: '단어들'}, o));
        return {notes: ['‘단어들’ 리스트에서 무작위 단어를 보여 주고, 똑같이 치면 점수 +1이에요. 리스트에 단어를 더 넣어 보세요.'], nodes: [
            L('data_deletealloflist', {sig: 'list:단어들:clear', label: '단어들의 항목을 모두 삭제하기'}),
            ...words.map(w => L('data_addtolist', {inputs: {ITEM: txt(w)}, sig: `list:단어들+${w}`, label: `${w}을(를) 단어들에 추가하기`})),
            setVar('점수', 0),
            {t: 'repeat', times: 5, body: [
                setVar('단어', L('data_itemoflist', {inputs: {INDEX: inN(act('operator_random', {inputs: {FROM: num(1), TO: num(words.length)}, sig: 'random1-5', label: '1부터 5 사이의 난수'}))}, sig: 'item(단어들)', label: '단어들의 무작위 항목'})),
                act('sensing_askandwait', {inputs: {QUESTION: inT(join('따라 쳐 봐: ', V('단어')))}, sig: 'ask(단어)', label: '‘따라 쳐 봐: (단어)’라고 묻고 기다리기'}),
                {t: 'ifelse', noLoop: true, cond: act('operator_equals', {inputs: {OPERAND1: inT(act('sensing_answer', {sig: 'answer', label: '대답'})), OPERAND2: inT(V('단어'))}, sig: 'answer=단어', label: '대답 = 단어'}),
                    body: [sayOf('정답!', 1, '정답!'), chVar('점수', 1)], body2: [sayOf('틀렸어요', 1, '틀렸어요')]}]},
            sayOf(join('점수: ', V('점수')), null, '점수: (점수)')]};
    });

    // ═══════════════ 게임·템플릿(한 스프라이트로 되는 것) ═══════════════
    // 잡기 게임 — "게임 만들어줘"(무슨 게임인지 모를 때의 출발점), "고양이 잡기 게임"
    def('gameCatch', 'control', 'multi', '(?:(?:고양이|캐릭터)\\s*)?(?:잡기|클릭)\\s*게임(?:을|를)?\\s*(?:만들|만들어|하고\\s*싶)?[가-힣]*|^\\s*(?:간단한\\s*|재밌는\\s*|고양이\\s*)?게임(?:을|를|하나)?\\s*(?:만들어|만들자|만들래|만들고\\s*싶|만들기)[가-힣]*',
        () => ({notes: ['‘잡기 게임’을 만들었어요: 고양이가 1초마다 순간이동하고, 클릭하면 점수 +1, 10점이면 이겨요. 다른 게임은 한 문장씩 나눠 말해 보세요(예: 방향키로 움직이게 / 마우스에 닿으면 점수 1 올려).'], multi: [
            {hat: act('event_whenflagclicked', {sig: 'flag', label: '깃발을 클릭했을 때'}),
                body: [setVar('점수', 0), {t: 'forever', body: [act('motion_goto', {inputs: {TO: menu('motion_goto_menu', 'TO', '_random_')}, sig: 'gotorandom', label: '무작위 위치로 이동하기'}), waitS(1)]}]},
            {hat: act('event_whenthisspriteclicked', {sig: 'click', label: '이 스프라이트를 클릭했을 때'}),
                body: [chVar('점수', 1), {t: 'if', noLoop: true, cond: act('operator_equals', {inputs: {OPERAND1: inT(V('점수')), OPERAND2: txt('10')}, sig: '점수=10', label: '점수 = 10'}),
                    body: [sayOf('이겼다!', 2, '이겼다!'), act('control_stop', {fields: fld('STOP_OPTION', 'all'), mutation: {tagName: 'mutation', children: [], hasnext: 'false'}, sig: 'stop', label: '멈추기 (모두)', cap: true})]}]}
        ]}));
    const K = (key, body) => ({hat: act('event_whenkeypressed', {fields: fld('KEY_OPTION', key), sig: `key:${key}`, label: `${keyKo(key)} 키를 눌렀을 때`}), body});
    const FLAG = body => ({hat: act('event_whenflagclicked', {sig: 'flag', label: '깃발을 클릭했을 때'}), body});
    const mvx = v => act('motion_changexby', {inputs: {DX: num(v)}, sig: `x${v >= 0 ? '+' : ''}${v}`, label: `x 좌표를 ${v}만큼 바꾸기`});
    const mvy = v => act('motion_changeyby', {inputs: {DY: num(v)}, sig: `y${v >= 0 ? '+' : ''}${v}`, label: `y 좌표를 ${v}만큼 바꾸기`});
    const edgeT = () => act('sensing_touchingobject', {inputs: {TOUCHINGOBJECTMENU: menu('sensing_touchingobjectmenu', 'TOUCHINGOBJECTMENU', '_edge_')}, sig: 'touchedge', label: '벽에 닿았는가?'});
    const stopAll = () => act('control_stop', {fields: fld('STOP_OPTION', 'all'), mutation: {tagName: 'mutation', children: [], hasnext: 'false'}, sig: 'stop', label: '멈추기 (모두)', cap: true});
    const gotoXY = (x, y) => act('motion_gotoxy', {inputs: {X: num(x), Y: num(y)}, sig: `goto${x},${y}`, label: `x: ${x} y: ${y} 으로 이동하기`});
    def('gameTemplates', 'control', 'multi', '(?<kind>슈팅|총\\s*쏘는|미로|플래피\\s*버드|플래피|마리오|점프\\s*게임|벽돌\\s*깨기|피하기|사과\\s*(?:받기|먹기)|떨어지는\\s*(?:거|것)\\s*받는)[^.!?\\n]*',
        g => {
            const k = g.kind.replace(/\s+/g, '');
            if (/슈팅|총/.test(k)) {
                return {notes: ['슈팅 게임 틀이에요: 좌우 화살표로 움직이고, 스페이스를 누르면 복제본(총알)이 위로 날아가요. 적은 다른 스프라이트를 추가해 ‘위에서 떨어지게’로 만들어 보세요.'], multi: [
                    FLAG([gotoXY(0, -140), act('looks_show', {sig: 'show', label: '보이기'})]),
                    K('left arrow', [mvx(-10)]), K('right arrow', [mvx(10)]),
                    K('space', [act('control_create_clone_of', {inputs: {CLONE_OPTION: menu('control_create_clone_of_menu', 'CLONE_OPTION', '_myself_')}, sig: 'clone', label: '나 자신 복제하기'})]),
                    {hat: act('control_start_as_clone', {sig: 'clonestart', label: '복제되었을 때'}), body: [act('looks_setsizeto', {inputs: {SIZE: num(30)}, sig: 'size=30', label: '크기를 30 %로 정하기'}),
                        {t: 'until', cond: edgeT(), body: [mvy(10)]}, act('control_delete_this_clone', {sig: 'clonedelete', label: '이 복제본 삭제하기', cap: true})]}]};
            }
            if (/미로/.test(k)) {
                return {notes: ['미로 게임 틀이에요: 방향키로 움직이고, 검은색 벽에 닿으면 처음 자리로 돌아가요. 배경에 검은색으로 미로를 그려 보세요(모양 탭의 그리기 도구).'], multi: [
                    K('up arrow', [mvy(5)]), K('down arrow', [mvy(-5)]), K('left arrow', [mvx(-5)]), K('right arrow', [mvx(5)]),
                    FLAG([act('looks_setsizeto', {inputs: {SIZE: num(30)}, sig: 'size=30', label: '크기를 30 %로 정하기'}), gotoXY(-200, -150),
                        {t: 'forever', body: [{t: 'if', cond: act('sensing_touchingcolor', {inputs: {COLOR: {sh: 'colour_picker', field: 'COLOUR', value: '#000000'}}, sig: 'touchcolor:#000000', label: '검은색에 닿았는가?'}), body: [gotoXY(-200, -150)]}]}])]};
            }
            if (/플래피/.test(k)) {
                return {notes: ['플래피 버드 틀이에요: 계속 조금씩 떨어지고, 스페이스를 누르면 위로 날아올라요. 바닥이나 천장에 닿으면 끝나요. 기둥은 다른 스프라이트로 만들어 보세요.'], multi: [
                    FLAG([gotoXY(-150, 0), {t: 'forever', body: [mvy(-3), {t: 'if', cond: edgeT(), body: [sayOf('게임 끝!', 1, '게임 끝!'), stopAll()]}]}]),
                    K('space', [{t: 'repeat', times: 5, body: [mvy(8)]}])]};
            }
            if (/마리오|점프게임/.test(k)) {
                return {notes: ['점프 게임 틀이에요: 좌우 화살표로 걷고, 스페이스로 점프해요. 발판·적은 다른 스프라이트로 더해 보세요.'], multi: [
                    FLAG([gotoXY(-180, -120), act('motion_setrotationstyle', {fields: fld('STYLE', 'left-right'), sig: 'rotstyle:left-right', label: '회전 방식을 왼쪽-오른쪽으로 정하기'})]),
                    K('left arrow', [act('motion_pointindirection', {inputs: {DIRECTION: num(-90, 'math_angle')}, sig: 'dir-90', label: '-90도 방향 보기'}), act('motion_movesteps', {inputs: {STEPS: num(10)}, sig: 'move10', label: '10만큼 움직이기'}), act('looks_nextcostume', {sig: 'nextcostume', label: '다음 모양으로 바꾸기'})]),
                    K('right arrow', [act('motion_pointindirection', {inputs: {DIRECTION: num(90, 'math_angle')}, sig: 'dir90', label: '90도 방향 보기'}), act('motion_movesteps', {inputs: {STEPS: num(10)}, sig: 'move10', label: '10만큼 움직이기'}), act('looks_nextcostume', {sig: 'nextcostume', label: '다음 모양으로 바꾸기'})]),
                    K('space', [{t: 'repeat', times: 10, body: [mvy(10)]}, {t: 'repeat', times: 10, body: [mvy(-10)]}])]};
            }
            if (/벽돌/.test(k)) {
                return {notes: ['벽돌깨기의 공 부분이에요: 공이 비스듬히 날아가며 벽에서 튕겨요. 막대(패들)와 벽돌은 다른 스프라이트로 만들어 ‘닿으면 튕기기’를 넣어 보세요.'], multi: [
                    FLAG([act('looks_setsizeto', {inputs: {SIZE: num(40)}, sig: 'size=40', label: '크기를 40 %로 정하기'}), gotoXY(0, 0),
                        act('motion_pointindirection', {inputs: {DIRECTION: num(45, 'math_angle')}, sig: 'dir45', label: '45도 방향 보기'}),
                        {t: 'forever', body: [act('motion_movesteps', {inputs: {STEPS: num(8)}, sig: 'move8', label: '8만큼 움직이기'}), act('motion_ifonedgebounce', {sig: 'bounce', label: '벽에 닿으면 튕기기'})]}])]};
            }
            // 피하기·받기 — 고양이는 좌우로, 떨어지는 건 다른 스프라이트
            return {notes: ['받기·피하기 게임의 고양이 부분이에요: 좌우 화살표로 움직여요. 떨어지는 물건은 다른 스프라이트를 추가해서 ‘위에서 떨어지게’라고 말해 보세요. 닿으면 점수를 올리면 돼요.'], multi: [
                FLAG([gotoXY(0, -140), setVar('점수', 0)]), K('left arrow', [mvx(-15)]), K('right arrow', [mvx(15)])]};
        });
    // 퀴즈 — 두 수를 뽑아 더하기 문제 N개
    def('quiz', 'sensing', 'act', `(?:덧셈\\s*|수학\\s*|더하기\\s*)?퀴즈(?:\\s*게임)?(?:를|을)?\\s*(?:만들|만들어|내)?[가-힣]*(?:\\s*(?:문제\\s*)?(?<n>\\d+|${RE_KNUM})\\s*(?:개|문제|번))?`,
        (g, ctx) => {
            if (/이름\s*(?:물어|묻|알아)|\d+\s*(?:더하기|빼기|곱하기|나누기|[x×*+\-])\s*\d+|수도|어디게|뭐게/.test((ctx && ctx.src) || '')) return null;
            const n = Math.min(20, Math.round(toNum(g.n) || 5));
            const q = join(join(V('가'), ' + '), join(V('나'), ' = ?'));
            const ok = act('operator_equals', {inputs: {OPERAND1: inT(act('sensing_answer', {sig: 'answer', label: '대답'})), OPERAND2: inT(opN('operator_add', V('가'), V('나')))}, sig: 'answer=가+나', label: '대답 = 가 + 나'});
            return {notes: [`덧셈 퀴즈 ${n}문제예요: ‘가’·‘나’에 1~9 난수를 넣고 묻고, 맞으면 점수 +1. 문제를 바꾸려면 연산 블록을 바꿔 보세요.`], nodes: [setVar('점수', 0), {t: 'repeat', times: n, body: [
                setVar('가', act('operator_random', {inputs: {FROM: num(1), TO: num(9)}, sig: 'random1-9', label: '1부터 9 사이의 난수'})),
                setVar('나', act('operator_random', {inputs: {FROM: num(1), TO: num(9)}, sig: 'random1-9', label: '1부터 9 사이의 난수'})),
                act('sensing_askandwait', {inputs: {QUESTION: inT(q)}, sig: 'ask(가+나)', label: '‘가 + 나 = ?’라고 묻고 기다리기'}),
                {t: 'ifelse', cond: ok, body: [sayOf('정답!', 1, '정답!'), chVar('점수', 1)], body2: [sayOf('틀렸어요', 1, '틀렸어요')]}
            ]}, sayOf(join('점수: ', V('점수')), null, '점수: (점수)')]};
        });
    // 위에서 떨어지기 — 복제본이 하늘에서 떨어진다
    def('fallClones', 'control', 'multi', '(?:(?:적|사과|별|공|물건|장애물|눈|비)(?:이|가)?\\s*)?(?:복제(?:돼서|되어|해서|되서|된)?\\s*(?:고양이들?|애들)?(?:이|가)?\\s*)?(?:위에서|하늘에서)(?:\\s*아래로)?\\s*(?:계속\\s*)?(?:떨어지|내려오|쏟아지)[가-힣]*',
        () => ({notes: ['원래 것은 숨고, 1초마다 복제본을 만들어요. 복제본은 위쪽 아무 x 에서 나타나 아래로 떨어지고, 바닥에 닿으면 사라져요.'], multi: [
            {hat: act('event_whenflagclicked', {sig: 'flag', label: '깃발을 클릭했을 때'}),
                body: [act('looks_hide', {sig: 'hide', label: '숨기기'}), {t: 'forever', body: [act('control_create_clone_of', {inputs: {CLONE_OPTION: menu('control_create_clone_of_menu', 'CLONE_OPTION', '_myself_')}, sig: 'clone', label: '나 자신 복제하기'}), waitS(1)]}]},
            {hat: act('control_start_as_clone', {sig: 'clonestart', label: '복제되었을 때'}),
                body: [act('motion_gotoxy', {inputs: {X: inN(act('operator_random', {inputs: {FROM: num(-220), TO: num(220)}, sig: 'random-220-220', label: '-220부터 220 사이의 난수'})), Y: num(170)}, sig: 'goto?,170', label: 'x: (난수) y: 170 으로 이동하기'}),
                    act('looks_show', {sig: 'show', label: '보이기'}),
                    {t: 'until', cond: act('operator_lt', {inputs: {OPERAND1: inT(act('motion_yposition', {sig: 'ypos', label: 'y 좌표'})), OPERAND2: txt('-170')}, sig: 'y<-170', label: 'y 좌표 < -170'}),
                        body: [act('motion_changeyby', {inputs: {DY: num(-5)}, sig: 'y-5', label: 'y 좌표를 -5만큼 바꾸기'})]},
                    act('control_delete_this_clone', {sig: 'clonedelete', label: '이 복제본 삭제하기', cap: true})]}
        ]}));
    // 피아노 — "a는 도 s는 레", "키보드 피아노 만들어줘"
    const PIANO = ['a', 's', 'd', 'f', 'g', 'h', 'j'];
    const SOL7 = ['도', '레', '미', '파', '솔', '라', '시'];
    def('piano', 'robot', 'multi', [
        '(?:키보드(?:로)?\\s*(?:치는\\s*)?)?피아노(?:를|을)?\\s*(?:만들|만들어)?[가-힣]*(?<pairs>(?:\\s*[a-z]\\s*(?:는|은|키는|키\\s*누르면|누르면)?\\s*(?:도|레|미|파|솔|라|시)){1,8})?',
        '(?<pairs>(?:[a-z]\\s*(?:는|은|키는|키\\s*누르면|누르면)\\s*(?:도|레|미|파|솔|라|시)\\s*){2,8})'
    ], g => {
        const pairs = [];
        const re = /([a-z])\s*(?:는|은|키는|키\s*누르면|누르면)?\s*(도|레|미|파|솔|라|시)/g;
        let m;
        while (g.pairs && (m = re.exec(g.pairs)) !== null) pairs.push([m[1], m[2]]);
        if (!pairs.length) PIANO.forEach((k, i) => pairs.push([k, SOL7[i]]));
        return {notes: ['키마다 스크립트를 하나씩 만들었어요. 소리는 큐브로이드 사운드 블록(부저)으로 나요.'], multi: pairs.map(([k, v]) => ({
            hat: act('event_whenkeypressed', {fields: fld('KEY_OPTION', k), sig: `key:${k}`, label: `${k} 키를 눌렀을 때`}),
            body: [noteBlock(SOLFA[v], 0.5)]}))};
    });
    // 최고 점수
    def('highScore', 'variables', 'act', '(?:최고\\s*점수|최고\\s*기록|하이\\s*스코어)(?:를|을|가)?\\s*(?:저장|기록|남|만들|기억)[가-힣]*',
        () => ({notes: ['점수가 최고 점수보다 크면 최고 점수를 바꿔요. (프로젝트를 저장해야 다음에도 남아요.)'],
            nodes: loopAll([{t: 'if', cond: act('operator_gt', {inputs: {OPERAND1: inT(V('점수')), OPERAND2: inT(V('최고 점수'))}, sig: '점수>최고 점수', label: '점수 > 최고 점수'}), body: [setVar('최고 점수', V('점수'))]}])}));
    // 리스트 — "리스트 만들어서 사과 바나나 포도 넣어줘"
    def('listAdd', 'variables', 'act', '(?:(?<ln>[가-힣A-Za-z]{1,8})\\s*)?(?:리스트|목록)(?:를|을)?\\s*(?:만들(?:어서|고|어)?\\s*)?(?<items>(?:[가-힣A-Za-z0-9]{1,10}\\s*,?\\s*){1,10}?)(?:를|을)?\\s*(?:넣어|넣|추가|저장|담아)[가-힣]*',
        g => {
            const name = g.ln && !/^(?:새|빈)$/.test(g.ln) ? g.ln : '목록';
            const items = String(g.items || '').split(/[\s,]+/).filter(Boolean).slice(0, 10);
            const L = (op, o) => act(op, Object.assign({listName: name}, o));
            return {notes: [`‘${name}’ 리스트를 만들고 비운 다음 ${items.length}개를 넣어요. 리스트 칸에서 볼 수 있어요.`],
                nodes: [L('data_deletealloflist', {sig: `list:${name}:clear`, label: `${name}의 항목을 모두 삭제하기`})].concat(items.map(it => L('data_addtolist', {inputs: {ITEM: txt(it)}, sig: `list:${name}+${it}`, label: `${it}을(를) ${name}에 추가하기`})))};
        });
    // 버튼 누를 때마다 켜졌다 꺼졌다(토글)
    def('rToggle', 'robot', 'act', '(?:버튼|터치\\s*센서|터치)(?:을|를)?\\s*(?:누를\\s*때\\s*마다|누르면|누를\\s*때|할\\s*때\\s*마다)\\s*(?:불(?:이|빛이)?\\s*)?(?:켜졌다(?:가)?\\s*꺼졌다|켜고\\s*끄|껐다\\s*켰다|켜졌다\\s*꺼지|켜짐\\s*꺼짐)[가-힣]*',
        () => ({notes: ['‘불’ 변수로 켜졌는지 기억해요. 버튼을 누를 때마다 바꾸고, 손을 뗄 때까지 기다려요(안 그러면 누르는 동안 계속 깜빡여요).'],
            nodes: [setVar('불', 0), {t: 'forever', body: [
                act('control_wait_until', {boolIn: {CONDITION: button()}, sig: 'until:button', label: '‘버튼이 눌러졌는가?’ 까지 기다리기'}),
                {t: 'ifelse', cond: act('operator_equals', {inputs: {OPERAND1: inT(V('불')), OPERAND2: txt('0')}, sig: '불=0', label: '불 = 0'}),
                    body: [colorOn('#ffffff'), setVar('불', 1)], body2: [colorOff(), setVar('불', 0)]},
                act('control_wait_until', {boolIn: {CONDITION: act('operator_not', {boolIn: {OPERAND: button()}, sig: 'not:button', label: '버튼이 눌러지지 않았는가?'})}, sig: 'until:not:button', label: '‘버튼이 눌러지지 않았는가?’ 까지 기다리기'})]}]}));
    // 지그재그 · 8자 · 원(로봇)
    const speeds = (a, b) => [act(`${M1}_dcMotorSetSpeed`, {inputs: {SPEED: num(a)}, sig: `m1:speed${a}`, label: `DC 모터 1번 속도를 ${a}%로 정하기`}),
        act(`${M2}_dcMotorSetSpeed`, {inputs: {SPEED: num(b)}, sig: `m2:speed${b}`, label: `DC 모터 2번 속도를 ${b}%로 정하기`})];
    def('rCurves', 'robot', 'act', `${CAR_WORD}(?:(?<zz>지그재그)|(?<eight>8\\s*자|팔\\s*자)|(?<circ>원(?:을)?\\s*(?:그리며|그리면서|모양으로)|빙글빙글\\s*원))(?:로|으로|를)?\\s*(?:달려|달리|움직|가|주행|돌|그려)?[가-힣]*`,
        (g, ctx) => {
            if (!ctx.robot && g.zz) {
                const pd = d => act('motion_pointindirection', {inputs: {DIRECTION: num(d, 'math_angle')}, sig: `dir${d}`, label: `${d}도 방향 보기`});
                const mv = act('motion_movesteps', {inputs: {STEPS: num(30)}, sig: 'move30', label: '30만큼 움직이기'});
                return {notes: ['지그재그는 45도(오른쪽 위)와 135도(오른쪽 아래)를 번갈아 보며 30만큼씩 움직여요.'],
                    nodes: [{t: 'repeat', times: 4, body: [pd(45), mv, waitS(0.2), pd(135), clone(mv), waitS(0.2)]}]};
            }
            if (!ctx.robot) return null;
            if (g.zz) {
                return {notes: ['지그재그는 먼저 오른쪽으로 45도(0.3초) 튼 다음, ‘앞으로 → 왼쪽 90도 → 앞으로 → 오른쪽 90도’를 반복해요.'],
                    nodes: [].concat(drive('right', 0.3, false), [{t: 'repeat', times: 3, body: [].concat(drive('forward', 0.6, false), drive('left', 0.6, false), drive('forward', 0.6, false), drive('right', 0.6, false))}], stopBoth())};
            }
            const note = '두 바퀴 속도를 다르게 하면 휘어져 가요(느린 쪽으로 돌아요). 속도 숫자를 바꿔 원 크기를 맞춰 보세요.';
            if (g.circ) return {notes: [note], nodes: [].concat(speeds(100, 40), drive('forward', 4, true), speeds(100, 100))};
            return {notes: [note, '8자는 한쪽으로 한 바퀴, 반대쪽으로 한 바퀴예요.'],
                nodes: [].concat(speeds(100, 40), drive('forward', 4, false), speeds(40, 100), drive('forward', 4, true), speeds(100, 100))};
        });
    // 미끄러져 끝까지 — "2초 동안 오른쪽 끝으로 미끄러지기"
    def('glideEdge', 'motion', 'act', `(?:(?<n>${RE_SEC})\\s*초\\s*(?:동안|만에)\\s*)?(?<ed>오른쪽|왼쪽|위쪽|아래쪽|맨\\s*위|맨\\s*아래)\\s*(?:끝|벽|가장자리)(?:으로|로|까지)?\\s*(?:미끄러|이동|가|스르르)[가-힣]*`,
        g => {
            const sec = toNum(g.n) !== null ? toNum(g.n) : 1;
            const side = g.ed.replace(/\s+/g, '');
            const hx = /오른/.test(side) ? 220 : /왼/.test(side) ? -220 : null;
            const vy = /위/.test(side) ? 160 : /아래/.test(side) ? -160 : null;
            const X = hx !== null ? num(hx) : inN(act('motion_xposition', {sig: 'xpos', label: 'x 좌표'}));
            const Y = vy !== null ? num(vy) : inN(act('motion_yposition', {sig: 'ypos', label: 'y 좌표'}));
            return {notes: ['무대 끝은 x = ±240, y = ±180 이에요. 몸이 조금 보이게 220·160 으로 했어요.'],
                nodes: [act('motion_glidesecstoxy', {inputs: {SECS: num(sec), X, Y}, sig: `glide${fmt(sec)}:${side}`, label: `${fmt(sec)}초 동안 ${side} 끝으로 이동하기`})]};
        });
    // 아무 말이나
    def('anySay', 'looks', 'act', '아무\\s*말(?:이나|이라도)?\\s*(?:하|해|말)[가-힣]*', () => {
        const note = '무슨 말을 할지 말하지 않아서 ‘안녕!’이라고 했어요.';
        return {notes: [note], nodes: [act('looks_say', {inputs: {MESSAGE: txt('안녕!')}, sig: 'say(안녕!)', label: '‘안녕!’ 말하기', ask: {input: 'MESSAGE', q: '뭐라고 말할까요?', options: null, text: true, note}})]};
    });
    // 한 걸음씩 걸어가기 — "천천히 한걸음씩 걸어가게"
    def('stepWalk', 'motion', 'act', `(?<sl>천천히\\s*|느리게\\s*)?(?:(?<k>${RE_KNUM})|(?<n>\\d+))\\s*(?:걸음|칸)씩\\s*(?:걸어|가|움직|이동)[가-힣]*`,
        (g, ctx, alt, before) => {
            if (/번|반복|계속/.test(before || '')) return null;
            const n = toNum(g.n) !== null ? toNum(g.n) : KNUM[g.k];
            return {notes: [`‘${n}걸음씩’ 움직이고 ${g.sl ? '0.3' : '0.1'}초 쉬기를 계속 반복해요.`], nodes: loopAll([
                act('motion_movesteps', {inputs: {STEPS: num(n * 5)}, sig: `move${n * 5}`, label: `${n * 5}만큼 움직이기`}), waitS(g.sl ? 0.3 : 0.1)])};
        });

    // ═══════════════ 목표 레시피(고양이) — 걷기·춤·숨쉬기·둥둥·서서히 ═══════════════
    const gradual = (op, field, input, step, times, wait) => ({t: 'repeat', times, body: [
        act(op, Object.assign({inputs: {[input]: num(step)}, sig: `${field || op}${step >= 0 ? '+' : ''}${fmt(step)}`, label: `${field ? `${field === 'GHOST' ? '유령' : field} 효과를` : input === 'DY' ? 'y 좌표를' : input === 'CHANGE' ? '크기를' : ''} ${fmt(step)}만큼 바꾸기`}, field ? {fields: fld('EFFECT', field)} : {})),
        waitS(wait)]});
    def('walk', 'motion', 'act', [
        '(?:모양(?:을)?\\s*(?:계속\\s*)?바꿔(?:서|가며|가면서)?\\s*)?(?:걷는|걸어가는|걷기|걸어다니는|걸어\\s*다니는)\\s*(?:것|거)?\\s*(?:처럼|같이)(?:\\s*(?:보이게|보이도록|보여|움직|해|만들))?[가-힣]*',
        '(?:걷는|걸어가는)\\s*(?:모습|애니메이션|동작|모션)[가-힣]*',
        '(?:다리|발)(?:를|을)?\\s*(?:움직이|움직여|움직이며|움직이면서)[가-힣]*',
        '모양(?:을)?\\s*(?:계속\\s*)?바꾸면서\\s*(?:움직|걸|가|이동)[가-힣]*',
        '모양(?:을)?\\s*(?:계속\\s*)?바꿔(?:서|가며|가면서)\\s*(?:걷|걸어|움직|가)[가-힣]*'
    ], () => ({notes: ['걷기는 ‘움직이기 → 다음 모양 → 0.2초 기다리기’를 계속 반복하는 거예요. 벽에 닿으면 튕기게 했어요.'],
        nodes: [act('motion_setrotationstyle', {fields: fld('STYLE', 'left-right'), sig: 'rotstyle:left-right', label: '회전 방식을 왼쪽-오른쪽으로 정하기'}),
            {t: 'forever', body: [act('motion_movesteps', {inputs: {STEPS: num(10)}, sig: 'move10', label: '10만큼 움직이기'}),
                act('looks_nextcostume', {sig: 'nextcostume', label: '다음 모양으로 바꾸기'}), waitS(0.2),
                act('motion_ifonedgebounce', {sig: 'bounce', label: '벽에 닿으면 튕기기'})]}]}), {simile: true, once: true});
    def('dance', 'motion', 'act', '(?:신나서\\s*|막\\s*)?(?:춤(?:을)?\\s*(?:춰|추|췄|추는|추게|춰줘|춰서|추기|췄으면)|댄스)[가-힣]*(?:\\s*(?:좋겠|하는\\s*거|하는거)[가-힣]*)?',
        (g, ctx) => (ctx.robot ? null : {notes: ['춤은 ‘오른쪽으로 돌기 → 다음 모양 → 왼쪽으로 돌기 → 다음 모양’을 기다리기와 함께 계속 반복해요.'],
            nodes: [{t: 'forever', body: [
                act('motion_turnright', {inputs: {DEGREES: num(15)}, sig: 'right15', label: '오른쪽으로 15도 돌기'}), act('looks_nextcostume', {sig: 'nextcostume', label: '다음 모양으로 바꾸기'}), waitS(0.2),
                act('motion_turnleft', {inputs: {DEGREES: num(15)}, sig: 'left15', label: '왼쪽으로 15도 돌기'}), act('looks_nextcostume', {sig: 'nextcostume', label: '다음 모양으로 바꾸기'}), waitS(0.2)]}]}));
    def('pulse', 'looks', 'act', '(?:(?:점점\\s*)?커졌다(?:가)?\\s*(?:다시\\s*)?작아(?:졌다(?:가)?|지게|져)|작아졌다(?:가)?\\s*(?:다시\\s*)?커졌다(?:가)?|커졌다\\s*줄었다|숨\\s*쉬듯이?|두근\\s*두근|심장처럼)[가-힣]*(?:\\s*(?:반복|하게|해)[가-힣]*)?',
        (g, ctx) => {
            const a = gradual('looks_changesizeby', null, 'CHANGE', 10, 5, 0.05);
            a.loop = !/클릭|누르면|닿으면|받으면/.test(ctx.src || '') || /계속|반복|무한/.test(ctx.src || '');
            return {notes: ['커졌다 작아졌다는 ‘크기 +10을 5번’과 ‘크기 -10을 5번’을 계속 반복해요.'], nodes: [a, gradual('looks_changesizeby', null, 'CHANGE', -10, 5, 0.05)]};
        });
    def('float', 'motion', 'act', '(?:(?:위아래로|아래위로|위\\s*아래로)\\s*(?:둥둥|살살|천천히|계속)?\\s*(?:떠다니|움직|왔다\\s*갔다|흔들|출렁|떠|둥실)|(?:위아래로|아래위로)\\s*(?:둥둥|둥실)|둥둥\\s*떠|둥실\\s*둥실)[가-힣]*',
        () => {
            const a = gradual('motion_changeyby', null, 'DY', 2, 10, 0.03);
            a.loop = true;
            return {notes: ['둥둥 떠다니기는 ‘y 좌표 +2를 10번’과 ‘-2를 10번’을 계속 반복해요.'], nodes: [a, gradual('motion_changeyby', null, 'DY', -2, 10, 0.03)]};
        });
    def('fadeInOut', 'looks', 'act', [
        '(?:투명하게\\s*)?(?:투명해졌다(?:가)?|사라졌다(?:가)?|희미해졌다(?:가)?)\\s*(?:다시\\s*)?(?:나타나|보이|선명해|돌아와)[가-힣]*',
        '투명해지면서\\s*(?:사라|없어)[가-힣]*'
    ], (g, ctx, alt) => {
        if (alt === 1) return {notes: ['서서히 사라지기는 유령 효과를 10씩 10번 올린 뒤 숨겨요.'], nodes: [gradual('looks_changeeffectby', 'GHOST', 'CHANGE', 10, 10, 0.05), act('looks_hide', {sig: 'hide', label: '숨기기'})]};
        return {notes: ['유령 효과를 10씩 10번 올렸다가(사라짐) 다시 10번 내려요(나타남).'],
            nodes: [gradual('looks_changeeffectby', 'GHOST', 'CHANGE', 10, 10, 0.05), gradual('looks_changeeffectby', 'GHOST', 'CHANGE', -10, 10, 0.05)]};
    });
    // 효과 숫자 — "유령효과 50", "소용돌이 효과 줘"
    const FX = {색깔: 'COLOR', 색: 'COLOR', 어안: 'FISHEYE', 소용돌이: 'WHIRL', 픽셀화: 'PIXELATE', 픽셀: 'PIXELATE', 모자이크: 'MOSAIC', 밝기: 'BRIGHTNESS', 유령: 'GHOST', 투명: 'GHOST'};
    const FX_KO = {COLOR: '색깔', FISHEYE: '어안', WHIRL: '소용돌이', PIXELATE: '픽셀화', MOSAIC: '모자이크', BRIGHTNESS: '밝기', GHOST: '유령'};
    def('effect', 'looks', 'act', [
        `(?<fx>어안|소용돌이|픽셀화|픽셀|모자이크|유령|투명)\\s*효과(?:를|을)?\\s*(?<n>${RE_NUM})\\s*(?<by>만큼)?(?:\\s*(?:으로|로))?(?:\\s*(?:정하|정해|해|바꿔|바꾸|설정|줘|주)[가-힣]*)?`,
        '(?<fx>어안|소용돌이|픽셀화|픽셀|모자이크)\\s*(?:효과)?(?:를|을)?\\s*(?:줘|주|넣어|넣|해|걸어|걸|바꿔|만들)[가-힣]*'
    ], (g, ctx, alt) => {
        const f = FX[g.fx];
        if (alt === 1 || g.by) {
            const v = alt === 1 ? 25 : toNum(g.n);
            return {nodes: [act('looks_changeeffectby', {fields: fld('EFFECT', f), inputs: {CHANGE: num(v)}, sig: `fx:${f}+${fmt(v)}`, label: `${FX_KO[f]} 효과를 ${fmt(v)}만큼 바꾸기`})]};
        }
        const v = toNum(g.n);
        return {nodes: [act('looks_seteffectto', {fields: fld('EFFECT', f), inputs: {VALUE: num(v)}, sig: `fx:${f}=${fmt(v)}`, label: `${FX_KO[f]} 효과를 ${euro(fmt(v))} 정하기`})]};
    });
    // 클릭한 곳으로 가기
    def('clickGo', 'motion', 'multi', '(?:마우스(?:를|로)?\\s*|무대(?:를)?\\s*)?클릭(?:하면|할\\s*때마다|한\\s*곳으로|한\\s*데로)\\s*(?:(?:고양이|캐릭터)(?:가|를)?\\s*)?(?:그\\s*쪽|그곳|거기|그\\s*자리|클릭한\\s*(?:곳|데|쪽|자리))(?:으로|로)?\\s*(?:가|이동|달려|날아|미끄러|따라)[가-힣]*',
        () => ({notes: ['‘무대를 클릭했을 때’ 마우스 위치로 0.5초 동안 미끄러져 가요.'], multi: [{
            hat: act('event_whenstageclicked', {sig: 'stageclick', label: '무대를 클릭했을 때'}),
            body: [act('motion_glideto', {inputs: {SECS: num(0.5), TO: menu('motion_glideto_menu', 'TO', '_mouse_')}, sig: 'glide0.5:mouse', label: '0.5초 동안 마우스 포인터로 이동하기'})]
        }]}));
    // 복제해서 N마리
    def('cloneMany3', 'control', 'multi', `(?:고양이(?:를)?\\s*)?(?<n>\\d+|${RE_KNUM})\\s*(?:마리|개)(?:로|를|을|만)?\\s*복제[가-힣]*`, g => {
        const n = Math.min(50, Math.round(toNum(g.n) || 5));
        return {notes: ['복제본은 원래 자리에 겹쳐 생겨서, ‘복제되었을 때’ 무작위 위치로 가게 했어요.'], multi: [
            {hat: act('event_whenflagclicked', {sig: 'flag', label: '깃발을 클릭했을 때'}), body: [{t: 'repeat', times: n, body: [act('control_create_clone_of', {inputs: {CLONE_OPTION: menu('control_create_clone_of_menu', 'CLONE_OPTION', '_myself_')}, sig: 'clone', label: '나 자신 복제하기'})]}]},
            {hat: act('control_start_as_clone', {sig: 'clonestart', label: '복제되었을 때'}), body: [act('motion_goto', {inputs: {TO: menu('motion_goto_menu', 'TO', '_random_')}, sig: 'gotorandom', label: '무작위 위치로 이동하기'})]}]};
    });
    def('cloneMany2', 'control', 'multi', `(?:고양이\\s*)?(?:여러\\s*(?:마리|개)|많이)(?:를|을)?\\s*복제[가-힣]*`, () => ({notes: ['복제본 5개를 만들어 무작위 위치로 보냈어요.'], multi: [
        {hat: act('event_whenflagclicked', {sig: 'flag', label: '깃발을 클릭했을 때'}), body: [{t: 'repeat', times: 5, body: [act('control_create_clone_of', {inputs: {CLONE_OPTION: menu('control_create_clone_of_menu', 'CLONE_OPTION', '_myself_')}, sig: 'clone', label: '나 자신 복제하기'})]}]},
        {hat: act('control_start_as_clone', {sig: 'clonestart', label: '복제되었을 때'}), body: [act('motion_goto', {inputs: {TO: menu('motion_goto_menu', 'TO', '_random_')}, sig: 'gotorandom', label: '무작위 위치로 이동하기'})]}]}));
    def('cloneMany', 'control', 'multi', `(?:(?:고양이|나\\s*자신|자기\\s*자신|자신|나)(?:를|을)?\\s*)?복제(?:해서|로|하여|해)?\\s*(?<n>\\d+|${RE_KNUM}|여러|많이)\\s*(?:마리|개)?(?:를|을)?\\s*(?:만들|만들어|복제|생기|생겨)?[가-힣]*`,
        g => {
            const n = Math.min(50, Math.round(toNum(g.n) || 5));
            return {notes: ['복제본은 원래 자리에 겹쳐 생겨서, ‘복제되었을 때’ 무작위 위치로 가게 했어요.'], multi: [
                {hat: act('event_whenflagclicked', {sig: 'flag', label: '깃발을 클릭했을 때'}),
                    body: [{t: 'repeat', times: n, body: [act('control_create_clone_of', {inputs: {CLONE_OPTION: menu('control_create_clone_of_menu', 'CLONE_OPTION', '_myself_')}, sig: 'clone', label: '나 자신 복제하기'})]}]},
                {hat: act('control_start_as_clone', {sig: 'clonestart', label: '복제되었을 때'}),
                    body: [act('motion_goto', {inputs: {TO: menu('motion_goto_menu', 'TO', '_random_')}, sig: 'gotorandom', label: '무작위 위치로 이동하기'})]}
            ]};
        });
    // ── 조건 더: 색에 닿으면 · 바닥에 닿으면 · 마우스가 가까이 오면 · 대답이 ~면 ──
    def('touchColor', 'sensing', 'cond', `(?<c>${RE_COLOR}|검은|까만|검정)\\s*(?:색|색깔)(?:에|을|이|과|와|이랑|랑)?\\s*${RE_TOUCH}`, g => {
        const hex = COLOR_HEX[g.c] || '#000000';
        return {notes: ['색에 닿기는 무대에 그 색이 있어야 해요. 색 칸을 눌러 무대의 색을 골라 보세요.'],
            cond: act('sensing_touchingcolor', {inputs: {COLOR: {sh: 'colour_picker', field: 'COLOUR', value: hex}}, sig: `touchcolor:${hex}`, label: `${g.c}색에 닿았는가?`})};
    });
    def('yBottom', 'sensing', 'cond', '(?:바닥|땅|화면\\s*아래(?:쪽)?\\s*끝|맨\\s*아래)(?:까지|에|으로)?\\s*(?:떨어지면|닿으면|내려가면|도착하면|떨어졌을\\s*때|닿았을\\s*때)',
        () => ({notes: ['바닥은 y 좌표가 -150보다 작을 때로 정했어요(무대 아래 끝은 -180).'],
            cond: act('operator_lt', {inputs: {OPERAND1: inT(act('motion_yposition', {sig: 'ypos', label: 'y 좌표'})), OPERAND2: txt('-150')}, sig: 'y<-150', label: 'y 좌표 < -150'})}));
    def('mouseNear', 'sensing', 'cond', `${RE_MOUSE}(?:가|이|와|랑|하고)?\\s*(?:가까이\\s*(?:오면|있으면|가면|다가오면)|가까우면|가까워지면|다가오면)`,
        () => ({notes: ['‘가까이’는 거리가 100보다 작을 때로 정했어요.'],
            cond: act('operator_lt', {inputs: {OPERAND1: inT(act('sensing_distanceto', {inputs: {DISTANCETOMENU: menu('sensing_distancetomenu', 'DISTANCETOMENU', '_mouse_')}, sig: 'dist:mouse', label: '마우스 포인터까지 거리'})), OPERAND2: txt('100')},
                sig: 'distmouse<100', label: '마우스 포인터까지 거리 < 100'})}));
    def('answerIs', 'sensing', 'cond', [
        '대답(?:이|가)?\\s*(?<n>-?\\d+(?:\\.\\d+)?)\\s*(?:보다)?\\s*(?<op>크면|많으면|높으면|작으면|적으면|낮으면|이면|면|과\\s*같으면|와\\s*같으면)',
        '대답(?:이|가)\\s*[\'"“”‘’]?(?<w>[^\\s\'"“”‘’]{1,10}?)[\'"“”‘’]?\\s*(?:이?면|이?라면)'
    ], g => {
        const ans = act('sensing_answer', {sig: 'answer', label: '대답'});
        let op = 'operator_equals';
        let sym = '=';
        if (/크|많|높/.test(g.op || '')) { op = 'operator_gt'; sym = '>'; }
        if (/작|적|낮/.test(g.op || '')) { op = 'operator_lt'; sym = '<'; }
        const v = g.w !== undefined ? g.w : g.n;
        return {cond: act(op, {inputs: {OPERAND1: inT(ans), OPERAND2: txt(v)}, sig: `answer${sym}${v}`, label: `대답 ${sym} ${v}`})};
    });
    // ── 세기·계산·무작위 수 ──
    def('countRangeLed', 'robot', 'act', '(?<a>\\d)\\s*(?:부터|에서)\\s*(?<b>\\d)\\s*까지\\s*(?:LED|엘이디)(?:로|에)?\\s*(?:카운트\\s*다운|세|보여|표시)[가-힣]*', g => {
        const a = toNum(g.a);
        const b = toNum(g.b);
        const nodes = [];
        for (let v = a; a >= b ? v >= b : v <= b; v += a >= b ? -1 : 1) nodes.push(act('cubroidled_lceNumberControl', {fields: fld('MATRIX', LED_NUM[v]), sig: `led:${v}`, label: `LED에 숫자 ${v} 보여주기`}), waitN(1));
        return {nodes};
    });
    def('countRange', 'variables', 'act', [
        `(?<a>\\d+)\\s*(?:부터|에서)\\s*(?<b>\\d+)\\s*까지\\s*(?:(?<eo>짝수|홀수)만?\\s*)?(?:숫자(?:를)?\\s*)?(?:(?:차례대로|차례로|순서대로|하나씩)\\s*)?(?:(?:${RE_SEC})\\s*초\\s*마다\\s*)?(?:세|셀|말|카운트|출력|외치|써|숫자\\s*세|줄어|내려|바뀌|보여|표시)[가-힣]*(?:\\s*(?:말하|말해)[가-힣]*)?`,
        '(?<a>\\d+)\\s*(?:초)?\\s*부터\\s*(?:거꾸로|0까지|1까지|카운트\\s*다운)\\s*(?:세|셀|카운트|줄|말)?[가-힣]*',
        '(?:타이머|카운트\\s*다운)(?:를|을)?\\s*(?:만들|만들어)?[가-힣]*\\s*(?<a>\\d+)\\s*초?\\s*부터(?:\\s*(?:거꾸로|0까지|1까지))?[가-힣]*',
        '(?:타이머|카운트\\s*다운)(?:를|을)?\\s*(?<a>\\d+)\\s*초(?:\\s*짜리)?(?:\\s*(?:만들|만들어|해|맞춰|설정)[가-힣]*)?'
    ], (g, ctx, alt) => {
        let a = toNum(g.a);
        const b = alt === 0 ? toNum(g.b) : 1;
        const step0 = a <= b ? 1 : -1;
        if (g.eo) { const want = g.eo === '짝수' ? 0 : 1; while (Math.abs(a % 2) !== want) a += step0; }
        const step = g.eo ? step0 * 2 : step0;
        const times = Math.max(1, Math.min(100, Math.floor(Math.abs(b - a) / Math.abs(step)) + 1));
        // 로봇 이야기(LED)면 숫자를 LED에 — "5부터 1까지 카운트다운"
        if (ctx.robot && a >= 0 && a <= 10 && b >= 0 && b <= 10 && !g.eo) {
            if (a === 10) a = 9;
            const nodes = [];
            for (let k = 0, v = a; k < times; k++, v += step) nodes.push(act('cubroidled_lceNumberControl', {fields: fld('MATRIX', LED_NUM[v]), sig: `led:${v}`, label: `LED에 숫자 ${v} 보여주기`}), waitS(1));
            return {notes: ['숫자마다 1초씩 LED에 보여줘요.'], nodes};
        }
        return {notes: [`‘숫자’ 변수를 ${a}로 정하고, 말하기 → ${step > 0 ? '+' : ''}${step} 을 ${times}번 반복해요. 변수 칸에서 ‘숫자’가 바뀌는 걸 볼 수 있어요.`],
            nodes: [Object.assign(setVar('숫자', a), {countStart: true}), {t: 'repeat', times, count: {a, step}, body: [sayOf(V('숫자'), 1, '숫자'), chVar('숫자', step)]}]};
    });
    def('countdown', 'control', 'act', [
        '카운트\\s*다운\\s*(?<seq>\\d(?:\\s*,\\s*\\d|\\s+\\d){1,9})?(?:\\s*(?:하고|하기|해|세고|센\\s*다음))?',
        '(?<seq>\\d(?:\\s*,\\s*\\d|\\s+\\d){1,9})\\s*(?:카운트\\s*다운|세고|세기|하고)(?:\\s*(?:하고|하기|해))?'
    ], g => {
        const ds = g.seq ? g.seq.replace(/[^0-9]/g, '').split('') : ['3', '2', '1'];
        return {notes: ['숫자마다 1초씩 말해요.'], nodes: ds.map(d => sayOf(d, 1, d))};
    });
    def('gugudan', 'operators', 'act', '구구단\\s*(?<d>[2-9])\\s*단(?:을|를)?\\s*(?:말|외|출력|보여|세)?[가-힣]*', g => {
        const d = Number(g.d);
        const msg = join(join(`${d} × `, V('숫자')), join(' = ', opN('operator_multiply', d, V('숫자'))));
        return {notes: [`‘숫자’를 1부터 9까지 바꾸면서 ‘${d} × 숫자 = 곱’을 말해요. 결합하기·곱하기 블록을 봐 보세요.`],
            nodes: [setVar('숫자', 1), {t: 'repeat', times: 9, body: [act('looks_sayforsecs', {inputs: {MESSAGE: inT(msg), SECS: num(1)}, sig: `gugudan${d}`, label: `‘${d} × 숫자 = …’을(를) 1초 동안 말하기`}), chVar('숫자', 1)]}]};
    });
    const OPS = {더하기: 'operator_add', '+': 'operator_add', 빼기: 'operator_subtract', '-': 'operator_subtract', 곱하기: 'operator_multiply', x: 'operator_multiply', '×': 'operator_multiply', '*': 'operator_multiply', 나누기: 'operator_divide', '/': 'operator_divide', '÷': 'operator_divide'};
    const OPS_KO = {operator_add: '+', operator_subtract: '-', operator_multiply: '×', operator_divide: '÷', operator_mod: '나머지'};
    def('calc', 'operators', 'act', '(?<a>-?\\d+(?:\\.\\d+)?)\\s*(?<op>더하기|빼기|곱하기|나누기|[+\\-x×*/÷])\\s*(?<b>-?\\d+(?:\\.\\d+)?)\\s*(?:(?:은|는|의|을|를)\\s*)?(?:(?<mod>나머지)(?:를|을|는)?\\s*)?(?:(?:계산해서|계산해|계산하고|계산|답을?|결과를?|값을?)\\s*)?(?:말|알려|보여|외치|써)[가-힣]*',
        g => {
            const op = g.mod ? 'operator_mod' : OPS[g.op];
            const node = opN(op, toNum(g.a), toNum(g.b));
            const lab = g.mod ? `${g.a} 나누기 ${g.b}의 나머지` : `${g.a} ${OPS_KO[op]} ${g.b}`;
            return {notes: ['계산은 연산 블록이 해요. 말하기 안에 연산 블록을 넣었어요.'], nodes: [sayOf(node, null, lab)]};
        });
    def('randSay', 'operators', 'act', [
        '(?<a>-?\\d+)\\s*(?:에서|부터|~|-)\\s*(?<b>-?\\d+)\\s*(?:사이|까지|중)?(?:의|에서|에)?\\s*(?:랜덤|무작위|아무|임의의?)\\s*(?:숫자|수|번호)?(?:를|을|하나)?\\s*(?:(?:골라|뽑아)(?:서)?\\s*)?(?:말|알려|보여|외치|골라|뽑)[가-힣]*',
        '(?:랜덤|무작위|아무)\\s*(?:숫자|수)(?:를|을|하나)?\\s*(?:(?:골라|뽑아)(?:서)?\\s*)?(?:말|알려|보여|외치|골라|뽑)[가-힣]*',
        '주사위(?:를|을)?\\s*(?:굴려|굴리|던져|던지)[가-힣]*'
    ], (g, ctx) => {
        const dice = /주사위/.test((ctx && ctx.src) || '');
        const a = g.a !== undefined ? toNum(g.a) : 1;
        const b = g.b !== undefined ? toNum(g.b) : (dice ? 6 : 10);
        const node = act('operator_random', {inputs: {FROM: num(a), TO: num(b)}, sig: `random${a}-${b}`, label: `${a}부터 ${b} 사이의 난수`});
        return {notes: g.a === undefined ? [dice ? '주사위라서 1부터 6 사이로 했어요.' : '범위를 말하지 않아서 1부터 10 사이로 했어요.'] : [], nodes: [sayOf(node, null, `${a}~${b} 난수`)]};
    });
    def('sayReporter', 'sensing', 'act', '(?:(?<ms>마우스)\\s*(?:포인터(?:의)?\\s*)?|(?:고양이|내|스프라이트|자기)(?:의)?\\s*)(?<ax>x|y|엑스|와이)\\s*(?:좌표|값|위치)?(?:를|을)?\\s*(?:(?<lp>계속)\\s*)?(?:말|알려|보여|띄워|표시)[가-힣]*',
        g => {
            const y = /y|와이/i.test(g.ax);
            const r = g.ms ? act(y ? 'sensing_mousey' : 'sensing_mousex', {sig: g.ms ? `mouse${y ? 'y' : 'x'}` : '', label: `마우스의 ${y ? 'y' : 'x'}좌표`}) :
                act(y ? 'motion_yposition' : 'motion_xposition', {sig: y ? 'ypos' : 'xpos', label: `${y ? 'y' : 'x'} 좌표`});
            return {loopBefore: Boolean(g.lp), nodes: [sayOf(r, null, r.label)]};
        });
    // 대답하면 인사 — "이름 물어보고 대답하면 인사"
    def('varSaveSay', 'variables', 'act', '(?:(?<who>[가-힣]{1,6}?)(?:의)?\\s*)?이름(?:을|를)?\\s*변수에\\s*(?:저장|넣)[가-힣]*\\s*(?:말|보여|외치)[가-힣]*',
        g => {
            const who = g.who && !/고양이|나|내/.test(g.who) ? g.who : '야옹이';
            return {notes: [`‘이름’ 변수에 ‘${who}’를 넣고, 변수 블록을 말하기 안에 넣었어요. 변수 값을 바꾸면 말도 바뀌어요.`], nodes: [setVar('이름', who), sayOf(V('이름'), 2, '이름')]};
        });
    def('greetAnswer', 'looks', 'act', '(?:대답(?:하면|을\\s*하면|을\\s*받으면)\\s*(?:(?:그\\s*)?이름(?:으로|을\\s*넣어서)?\\s*)?|대답(?:한|받은)\\s*(?:이름|거|것)(?:으로|을\\s*넣어서)?\\s*(?:[가-힣]+\\s*(?:하고|이라고|라고)\\s*)?)(?:인사|반갑다고)[가-힣]*',
        () => ({notes: ['‘안녕, ’과 대답을 결합하기 블록으로 붙여서 말해요.'], nodes: [sayOf(join('안녕, ', act('sensing_answer', {sig: 'answer', label: '대답'})), 2, '안녕, (대답)')]}));
    // ── 변수(점수 말고도) ──
    const VARS = '목숨|생명|체력|레벨|코인|돈|HP|에너지|라이프';
    def('varSetAny', 'variables', 'act', `(?<v>[가-힣A-Za-z0-9]{1,8}?)\\s*(?:이라는\\s*|라는\\s*)?변수(?:를|을)?\\s*(?:만들(?:고|어서|어|기)?\\s*)?(?:(?:값을?|을|를)\\s*)?(?<n>-?\\d+(?:\\.\\d+)?)\\s*(?:개\\s*)?(?:으로|로|에서)\\s*(?:정하|정해|해|시작|설정|만들|두)[가-힣]*`,
        g => {
            const name = /^(?:스코어|포인트)$/.test(g.v) ? '점수' : g.v;
            return {notes: ['변수는 번역기가 만들어요. 변수 칸에서 확인해 보세요.'], nodes: [setVar(name, toNum(g.n))]};
        });
    def('varNamedSet', 'variables', 'act', `(?<v>${VARS})(?:을|를)?\\s*(?<n>${RE_NUM})\\s*(?:개)?\\s*(?:으로|로)\\s*(?:정하|정해|해|시작|설정|바꿔)[가-힣]*`,
        g => ({nodes: [setVar(g.v, toNum(g.n))]}));
    def('varNamedChange', 'variables', 'act', `(?<v>${VARS})(?:을|를|이|가|도)?\\s*(?:(?<n>${RE_ANY})\\s*(?:개\\s*)?(?:만큼\\s*|씩\\s*)?)?(?<d>올려|올리|올라|늘려|늘리|늘어|더해|더하|증가|추가|깎|빼|줄게|줄여|줄이|줄어|줄|감소|잃|닳)[가-힣]*`,
        g => {
            const n = Math.abs(toNum(g.n) || 1);
            return {nodes: [chVar(g.v, /깎|빼|줄|감소|잃/.test(g.d) ? -n : n)]};
        });
    def('varNamedIs', 'operators', 'cond', [
        `(?<v>${VARS})(?:이|가)?\\s*(?<n>${RE_NUM})\\s*(?:개)?\\s*(?:이|가)?\\s*(?<op>되면|이면|보다\\s*작으면|보다\\s*크면|넘으면)`,
        '(?<v>목숨|생명|체력|에너지|라이프)(?:이|가)?\\s*(?:다\\s*)?(?:없어지면|떨어지면|바닥나면)'
    ], g => {
        const n = g.n !== undefined ? toNum(g.n) : 0;
        let op = 'operator_equals';
        let sym = '=';
        if (/크|넘/.test(g.op || '')) { op = 'operator_gt'; sym = '>'; }
        if (/작/.test(g.op || '')) { op = 'operator_lt'; sym = '<'; }
        return {cond: act(op, {inputs: {OPERAND1: inT(V(g.v)), OPERAND2: txt(fmt(n))}, sig: `${g.v}${sym}${fmt(n)}`, label: `${g.v} ${sym} ${fmt(n)}`})};
    });
    // ── 펜(선 그리기) ──
    const PEN = (op, o) => act(op, o);
    const penDown = () => PEN('pen_penDown', {sig: 'pen:down', label: '펜 내리기'});
    const penUp = () => PEN('pen_penUp', {sig: 'pen:up', label: '펜 올리기'});
    const penClear = () => PEN('pen_clear', {sig: 'pen:clear', label: '모두 지우기'});
    const SHAPES = {정사각형: [4, 100, 90], 사각형: [4, 100, 90], 네모: [4, 100, 90], 삼각형: [3, 100, 120], 세모: [3, 100, 120], 오각형: [5, 80, 72], 육각형: [6, 70, 60], 별: [5, 100, 144], 원: [36, 10, 10], 동그라미: [36, 10, 10]};
    def('penShape', 'pen', 'act', `(?:(?<pen>펜|선)(?:으로|을\\s*써서)\\s*)?(?<sh>정사각형|사각형|네모|삼각형|세모|오각형|육각형|별|원|동그라미)(?:을|를)?\\s*(?:하나\\s*|한\\s*개\\s*)?(?:(?<cnt>\\d+|${RE_KNUM})\\s*개\\s*)?(?:(?<many>여러\\s*(?:군데|곳|개))(?:에)?\\s*)?(?:모양(?:을|으로)?\\s*)?(?:그려|그리|그리기|그림)(?![가-힣]*\\s*(?:돌|움직|이동))[가-힣]*(?:\\s*,?\\s*한\\s*변(?:은|의\\s*길이는|길이는?|이|을)?\\s*\\d+(?:으로|로)?)?`,
        (g, ctx) => {
            if (ctx.robot && !g.pen) return null;
            const [k, len0, ang] = SHAPES[g.sh];
            const side = /한\s*변(?:은|의\s*길이는|길이|이|을)?\s*(\d+)/.exec((ctx && ctx.src) || '');
            const len = side ? Number(side[1]) : len0;
            const shape = {t: 'repeat', times: k, body: [act('motion_movesteps', {inputs: {STEPS: num(len)}, sig: `move${len}`, label: `${len}만큼 움직이기`}),
                act('motion_turnright', {inputs: {DEGREES: num(ang)}, sig: `right${ang}`, label: `오른쪽으로 ${ang}도 돌기`})]};
            const cnt = toNum(g.cnt) || (g.many ? 5 : 1);
            if (cnt > 1) {
                return {notes: [`무작위 위치로 가서 그리기를 ${cnt}번 반복해요. 옮겨 갈 때는 펜을 올려야 선이 안 생겨요.`],
                    nodes: [penClear(), {t: 'repeat', times: cnt, body: [penUp(), act('motion_goto', {inputs: {TO: menu('motion_goto_menu', 'TO', '_random_')}, sig: 'gotorandom', label: '무작위 위치로 이동하기'}), penDown(), shape]}, penUp()]};
            }
            return {notes: [`펜을 내리고 ‘${len}만큼 움직이기 → ${ang}도 돌기’를 ${k}번 반복해요. 도는 각도 × 횟수 = ${ang * k}도.`],
                nodes: [penClear(), penDown(), shape, penUp()]};
        });
    def('penTrail', 'pen', 'act', [
        '(?:지나간|지나가는|움직인|다닌)\\s*(?:자리|곳|길|데)(?:에|를|마다)?\\s*(?:선(?:이|을)?\\s*)?(?:그리|그려|남기|남겨|생기)[가-힣]*',
        '(?:선|자국)(?:이|을)?\\s*(?:그려지|그려|남)[가-힣]*',
        '(?:펜|선)(?:을)?\\s*(?:내리|내려|그리기\\s*시작)[가-힣]*'
    ], (g, ctx) => (/(?:사각형|네모|삼각형|세모|오각형|육각형|별|원|동그라미)(?:을|를)?\s*(?:하나\s*|\d+\s*개\s*)?(?:그려|그리)/.test((ctx && ctx.src) || '') ? {nodes: []} // 도형 그리기가 이미 펜을 내린다
        : {notes: ['‘펜 내리기’를 하면 움직이는 곳마다 선이 그려져요. 펜 확장을 추가했어요.'], nodes: [penDown()]}));
    def('penUp', 'pen', 'act', '펜(?:을)?\\s*(?:올리|올려)[가-힣]*', () => ({nodes: [penUp()]}));
    def('penClear', 'pen', 'act', '(?:펜|그림|낙서|선|그린\\s*(?:것|거))(?:을|를)?\\s*(?:다\\s*|모두\\s*|전부\\s*)?(?:지우|지워)[가-힣]*', () => ({nodes: [penClear()]}));
    def('penColor', 'pen', 'act', `(?:펜|선)\\s*(?:색|색깔)(?:을|를)?\\s*(?<c>${RE_COLOR}|검은|까만|검정)\\s*(?:색)?(?:으로|로)?(?:\\s*(?:바꿔|바꾸|해|정해|정하)[가-힣]*)?`,
        g => ({nodes: [PEN('pen_setPenColorToColor', {inputs: {COLOR: {sh: 'colour_picker', field: 'COLOUR', value: COLOR_HEX[g.c] || '#000000'}}, sig: `pen:color${COLOR_HEX[g.c] || '#000000'}`, label: `펜 색깔을 ${g.c}색으로 정하기`})]}));
    def('penSize', 'pen', 'act', ['(?:펜|선)\\s*(?:굵기|두께|크기)(?:를|을)?\\s*(?<n>\\d+)\\s*(?:으로|로)?\\s*(?:정해|해|바꿔|정하)?[가-힣]*', '(?:굵기|두께)(?:를|을|는)?\\s*(?<n>\\d+)\\s*(?:으로|로)?\\s*(?:정해|해|바꿔|정하)?[가-힣]*'],
        g => ({nodes: [PEN('pen_setPenSizeTo', {inputs: {SIZE: num(toNum(g.n))}, sig: `pen:size${g.n}`, label: `펜 굵기를 ${g.n}(으)로 정하기`})]}));

    // ── 동작 ──
    def('bounce', 'motion', 'act',
        [`(?:${RE_EDGE}(?:에|을|이|과|와)?\\s*${RE_TOUCH}\\s*)?(?:튕기|튕겨|튕김|튕|튀기|튀어|반사)${REST}`, `${RE_EDGE}(?:에|을|이)?\\s*${RE_TOUCH}\\s*(?:다시\\s*)?(?:돌아오|되돌아오|반대로\\s*가|방향\\s*바꿔)${REST}`],
        () => ({nodes: [act('motion_ifonedgebounce', {sig: 'bounce', label: '벽에 닿으면 튕기기', loop: true})]}));

    def('move', 'motion', 'act', [
        `(?:(?<n>${RE_NUM})\\s*(?:만큼|칸|걸음|발자국|픽셀)?\\s*씩?\\s*|(?<k>${RE_KNUM})\\s*(?:칸|걸음|발자국)\\s*씩?\\s*|(?<v>${RE_VAGUE})만?\\s*)?(?:앞으로\\s*)?(?:움직|움지|음직|이동하|이동해|이동|걸어|걷|전진|달려|달리)${REST}`,
        `앞으로\\s*(?:(?<n>${RE_NUM})\\s*(?:만큼|칸|걸음|발자국)?\\s*|(?<k>${RE_KNUM})\\s*(?:칸|걸음)\\s*|(?<v>${RE_VAGUE})만?\\s*)?(?:가|나아가|달려|뛰어가|움직|이동|걸어)${REST}`,
        `뒤로\\s*(?:(?<n>${RE_NUM})\\s*(?:만큼|칸|걸음)?\\s*)?(?:가|이동|움직|물러|걸어|달려|오|와)${REST}`,
        `(?:(?<n>${RE_NUM})\\s*(?:만큼|칸|걸음|발자국)|(?<k>${RE_KNUM})\\s*(?:칸|걸음|발자국))\\s*앞으로\\s*(?:가|나아가|달려|걸어|이동|움직)${REST}`,
        `(?<k>${RE_KNUM})\\s*(?:걸음|칸)\\s*(?:가|걸어가|움직|이동)${REST}`,
        `다시\\s*(?:가|걸어가|움직이|달려|오|와|돌아오|돌아와)${REST}`,
        `(?<n>${RE_NUM})\\s*(?:걸음|발자국|칸)(?=\\s*$|\\s*[,.!]|\\s+(?:가|가고|그리고|하고|움직))`
    ], (g, ctx, alt) => {
        if (ctx.robotOnly) return null;
        const notes = [];
        let n = toNum(g.n);
        if (n === null && g.k) n = KNUM[g.k];
        if (n === null && g.v) { n = VAGUE[g.v]; notes.push(noteVague(g.v, n)); }
        if (n === null) { n = 10; notes.push(noteDefault('움직일', n)); }
        if (alt === 2) n = -Math.abs(n);
        const speedy = g.v && /빨리|빠르게|천천히|느리게/.test(g.v) && toNum(g.n) === null;
        return {notes, nodes: [act('motion_movesteps', {inputs: {STEPS: num(n)}, sig: `move${fmt(n)}`, label: `${fmt(n)}만큼 움직이기`, loop: speedy || undefined})]};
    });

    // 뒤로 돌기 — "반대로 돌아", "거꾸로 돌려서 반대쪽 봐", "방향 바꿔"
    def('turnBack', 'motion', 'act', [
        `(?:거꾸로|반대로|뒤로)\\s*(?:돌려서|돌아서)?\\s*(?:반대쪽|뒤쪽|뒤)(?:을|를|으로)?\\s*(?:보|봐|향하|향해)${REST}`,
        `(?:거꾸로|반대로|뒤로)\\s*(?:돌|회전)${REST}`,
        `(?:반대쪽|뒤쪽)(?:을|를|으로)?\\s*(?:보|봐|향하|향해|쳐다)${REST}`,
        `방향(?:을|를|이)?\\s*(?:반대로\\s*)?(?:바꿔|바꾸|바뀌|돌려|틀어)${REST}`,
        `(?:거꾸로\\s*)?뒤집(?:어|어져|어지|혀|기)${REST}`,
        `뒤\\s*돌(?:아|아서|기|아봐)[가-힣]*`
    ], (g, ctx, alt) => ({
        notes: alt === 3 ? ['‘방향 바꾸기’는 180도 돌기로 했어요. 벽에서 튕기게 하려면 ‘튕겨’라고 말해 보세요.'] : [],
        nodes: [act('motion_turnright', {inputs: {DEGREES: num(180)}, sig: 'right180', label: '오른쪽으로 180도 돌기'})]
    }));
    // 왔다갔다 — 움직이기 + 벽에서 튕기기(계속 해야 의미가 있다)
    def('patrol', 'motion', 'act', [
        `(?:좌우로\\s*|양옆으로\\s*|왼쪽\\s*오른쪽\\s*|오른쪽\\s*왼쪽\\s*)?(?:왔다\\s*갔다|왔다리\\s*갔다리|왕복)${REST}`,
        `(?:막\\s*)?(?:돌아다니|돌아댕기|걸어다니|뛰어다니|쏘다니)${REST}`,
        `(?:오른쪽|왼쪽)\\s*끝까지\\s*(?:갔다가?|가고|가서)\\s*(?:다시\\s*)?(?:(?:왼쪽|오른쪽)\\s*끝까지\\s*)?(?:왔다\\s*갔다|와|오|돌아와|돌아오)?${REST}`
    ],
        () => ({notes: ['‘왔다갔다’는 ‘움직이기’와 ‘벽에 닿으면 튕기기’ 두 블록으로 나눴어요.'], nodes: [
            act('motion_movesteps', {inputs: {STEPS: num(10)}, sig: 'move10', label: '10만큼 움직이기', loop: true}),
            act('motion_ifonedgebounce', {sig: 'bounce', label: '벽에 닿으면 튕기기', loop: true})
        ]}));

    // 도망가기 — 마우스 쪽을 보고 → 뒤로 돌아 → 움직이기
    def('flee', 'motion', 'act', [
        `${RE_MOUSE}(?:를|을|에서|로부터)?\\s*(?:피해서|피해|피하)[가-힣]*(?:\\s*도망[가-힣]*)?`,
        '도망(?:가|다니|쳐|치|다녀)[가-힣]*'
    ], () => ({notes: ['‘도망가기’는 ‘마우스 쪽 보기 → 뒤로 돌기 → 움직이기’ 세 블록으로 나눴어요.'], nodes: [
        act('motion_pointtowards', {inputs: {TOWARDS: menu('motion_pointtowards_menu', 'TOWARDS', '_mouse_')}, sig: 'pointmouse', label: '마우스 포인터 쪽 보기', loop: true}),
        act('motion_turnright', {inputs: {DEGREES: num(180)}, sig: 'right180', label: '오른쪽으로 180도 돌기'}),
        act('motion_movesteps', {inputs: {STEPS: num(10)}, sig: 'move10', label: '10만큼 움직이기'})
    ]}));
    // 원 그리기 — 조금 움직이고 조금 돌기를 계속
    def('circle', 'motion', 'act', '(?:원(?:을|으로)?\\s*(?:그리|그려|모양으로)[가-힣]*(?:\\s*(?:돌|움직|가|이동)[가-힣]*)?|빙\\s*돌아[가-힣]*)',
        () => ({notes: ['‘원 그리기’는 ‘조금 움직이고 조금 돌기’를 계속 반복하는 거예요.'], nodes: [
            act('motion_movesteps', {inputs: {STEPS: num(10)}, sig: 'move10', label: '10만큼 움직이기', loop: true}),
            act('motion_turnright', {inputs: {DEGREES: num(15)}, sig: 'right15', label: '오른쪽으로 15도 돌기', loop: true})
        ]}));
    // 떨기·흔들기 — 오른쪽·왼쪽으로 조금씩 번갈아
    def('shake', 'motion', 'act', '(?:부들부들|덜덜|달달|벌벌|흔들흔들|흔들)[가-힣]*(?:\\s*(?:떨|거리|거려|흔들)[가-힣]*)?',
        () => ({notes: ['‘떨기·흔들기’는 오른쪽·왼쪽으로 조금씩 번갈아 도는 걸 계속 반복해요.'], nodes: [
            act('motion_turnright', {inputs: {DEGREES: num(10)}, sig: 'right10', label: '오른쪽으로 10도 돌기', loop: true}),
            act('control_wait', {inputs: {DURATION: num(0.05, 'math_positive_number')}, sig: 'wait0.05', label: '0.05초 기다리기'}),
            act('motion_turnleft', {inputs: {DEGREES: num(10)}, sig: 'left10', label: '왼쪽으로 10도 돌기'}),
            act('control_wait', {inputs: {DURATION: num(0.05, 'math_positive_number')}, sig: 'wait0.05', label: '0.05초 기다리기'})
        ]}));
    // 대각선 — x와 y를 함께
    def('diagonal', 'motion', 'act', '대각선(?:으로)?\\s*(?:가|이동|움직|걸어|달려)[가-힣]*',
        () => ({notes: ['‘대각선’은 x와 y를 함께 바꾸는 거예요.'], nodes: [
            act('motion_changexby', {inputs: {DX: num(10)}, sig: 'x+10', label: 'x 좌표를 10만큼 바꾸기'}),
            act('motion_changeyby', {inputs: {DY: num(10)}, sig: 'y+10', label: 'y 좌표를 10만큼 바꾸기'})
        ]}));

    def('turn', 'motion', 'act', [
        `(?:(?<dir>오른쪽|왼쪽|오른|왼|시계\\s*반대\\s*방향|반시계\\s*방향|시계\\s*방향)(?:\\s*방향)?(?:으로|쪽으로|로)?\\s*)?(?:(?<n>${RE_NUM}|${RE_SINO2})\\s*도\\s*(?:만큼\\s*|씩\\s*)?|(?<half>반\\s*바퀴)(?:만)?\\s*|(?<full>(?<nb>한|두|세|네|1|2|3|4)\\s*바퀴)(?:만)?\\s*|(?<v>${RE_VAGUE})만?\\s*)?(?:(?<bb>빙글\\s*빙글|빙빙)\\s*(?<lp>계속\\s*)?)?(?<lp2>계속\\s*|무한\\s*)?(?:돌(?!아다니|아댕기|아와|아오)|회전|(?<bv>빙글|빙빙|뱅글)|턴)${REST}`,
        `(?<dir>오른쪽|왼쪽)(?:\\s*방향)?(?:으로|쪽으로|로)?\\s*(?<n>${RE_NUM})\\s*도(?:\\s*씩)?(?=\\s*$|\\s*[,.!])`
    ], (g, ctx) => {
            if (ctx.robotOnly) return null;
            const notes = [];
            let n = toNum(g.n);
            if (n === null && g.half) n = 180;
            if (n === null && g.full) n = 360 * (KNUM[g.nb] || toNum(g.nb) || 1);
            if (n === null && g.v) { n = VAGUE[g.v]; notes.push(noteVague(g.v, n)); }
            if (n === null) { n = 15; notes.push(noteDefault('돌', n)); }
            const left = /왼|반시계|반대/.test(g.dir || '');
            // 고양이 "한 바퀴 돌기" — 360도를 한 번에 돌면 제자리라 안 보인다 → 10도씩 나눠 돌기
            // "한바퀴 빙글"(계속이라는 말 없음) 도 한 번만 · "한 바퀴 돌 때마다 야옹" 은 반복 전체를 계속
            const keep = /계속|무한|반복|때마다|마다/.test((ctx && ctx.src) || '');
            if ((g.full || g.half) && toNum(g.n) === null && !(g.lp || g.lp2) && (!(g.bb || g.bv) || !/계속|무한|반복/.test((ctx && ctx.src) || ''))) {
                const k = Math.round(n / 10);
                return {notes: [`${fmt(n)}도를 한 번에 돌면 눈에 안 보여서, 10도씩 ${k}번 나눠 돌게 했어요.`], nodes: [{t: 'repeat', times: k, loop: keep || undefined, body: [
                    act(left ? 'motion_turnleft' : 'motion_turnright', {inputs: {DEGREES: num(10)}, sig: `${left ? 'left' : 'right'}10`, label: `${left ? '왼쪽' : '오른쪽'}으로 10도 돌기`}), waitS(0.02)]}]};
            }
            return {notes, loopBefore: Boolean(g.lp || g.lp2), nodes: [act(left ? 'motion_turnleft' : 'motion_turnright', {
                inputs: {DEGREES: num(n)}, sig: `${left ? 'left' : 'right'}${fmt(n)}`, loop: Boolean(g.bb || g.bv) || undefined,
                label: `${left ? '왼쪽' : '오른쪽'}으로 ${fmt(n)}도 돌기`
            })]};
        });

    def('moveDir', 'motion', 'act', [
        `(?<d>위쪽|윗쪽|위|아래쪽|아래|밑|오른쪽|왼쪽)(?:으로|쪽으로|로)?\\s*(?:(?<n>${RE_NUM}|${RE_SINO2})\\s*(?:만큼|칸|픽셀|걸음|발자국|씩)?\\s*|(?<k>${RE_KNUM})\\s*(?:칸|걸음|발자국)\\s*|(?<v>${RE_VAGUE})만?\\s*)?(?:가|이동|움직|올라가|내려가|올라|내려|날아|걸어|달려|뛰어|올려|올리|내리)${REST}`,
        `(?:(?<n>${RE_NUM})\\s*(?:만큼|칸|픽셀)?\\s*)?(?<d>올라가|올라|내려가|내려|날아올라)${REST}`,
        // "a키 누르면 왼쪽" 처럼 동사 없이 방향만 말한 경우 — 문장 끝이거나 다음 "~키 누르면" 바로 앞일 때만
        '(?<d>오른쪽|왼쪽|위쪽|아래쪽)(?:으로|쪽으로|로)?(?=\\s*$|\\s*[,.]|\\s+\\S+\\s*(?:키|화살표)?\\s*(?:를|을)?\\s*누르면)'
    ], (g, ctx) => {
        const notes = [];
        if (/올라/.test(g.d || '') && /점수|목숨|생명|변수|점/.test((ctx && ctx.src) || '') && !/위로|y\s*좌표|하늘|공중/.test((ctx && ctx.src) || '')) return {nodes: [chVar(/목숨|생명/.test(ctx.src) ? (/목숨/.test(ctx.src) ? '목숨' : '생명') : '점수', 1)]};
        let n = toNum(g.n);
        if (n === null && g.k) n = KNUM[g.k];
        if (n === null && g.v) { n = VAGUE[g.v]; notes.push(noteVague(g.v, n)); }
        if (n === null) { n = 10; notes.push(noteDefault('움직일', n)); }
        const d = g.d || '';
        const vertical = /위|윗|아래|밑|올라|내려|날아/.test(d);
        const neg = /아래|밑|내려|왼/.test(d);
        const v = neg ? -Math.abs(n) : Math.abs(n);
        const axis = vertical ? 'y' : 'x';
        return {notes, nodes: [act(vertical ? 'motion_changeyby' : 'motion_changexby', {
            inputs: {[vertical ? 'DY' : 'DX']: num(v)},
            sig: `${axis}${v >= 0 ? '+' : ''}${fmt(v)}`, label: `${axis} 좌표를 ${fmt(v)}만큼 바꾸기`
        })]};
    });

    def('center', 'motion', 'act', [
        `(?:화면\\s*)?(?:처음\\s*자리|처음\\s*위치|원래\\s*자리|원래\\s*위치|제자리(?!에서|서)|한가운데|가운데|중앙|중심)(?:로|으로|에|에서|서)?\\s*(?:가|이동|돌아가|돌아와|옮기|옮겨|보내|위치|서|있어|시작|출발)${REST}`,
        '위치(?:는|를|은)?\\s*(?:가운데|중앙)', '(?:한가운데|가운데|중앙)(?:로|으로)(?=\\s*$|\\s*[,.!])'],
        () => ({nodes: [act('motion_gotoxy', {inputs: {X: num(0), Y: num(0)}, sig: 'goto0,0', label: 'x: 0 y: 0 으로 이동하기'})]}));

    def('gotoXY', 'motion', 'act', [
        `(?:위치(?:를)?\\s*)?x\\s*:?\\s*(?<x>${RE_NUM})\\s*,?\\s*y\\s*:?\\s*(?<y>${RE_NUM})(?:\\s*(?:으로|로|에))?(?:\\s*(?:가|이동|옮기|옮겨|순간이동)${REST})?`,
        `(?:x\\s*(?:좌표)?\\s*:?\\s*(?<x>${RE_NUM})\\s*,?\\s*y\\s*(?:좌표)?\\s*:?\\s*(?<y>${RE_NUM})|\\(?\\s*(?<x2>${RE_NUM})\\s*,\\s*(?<y2>${RE_NUM})\\s*\\)?)\\s*(?:으로|로|에|위치로)?\\s*(?:가|이동|옮기|옮겨|순간이동)${REST}`
    ], g => {
            const x = toNum(g.x !== undefined ? g.x : g.x2);
            const y = toNum(g.y !== undefined ? g.y : g.y2);
            if (x === null || y === null) return null;
            return {nodes: [act('motion_gotoxy', {inputs: {X: num(x), Y: num(y)}, sig: `goto${fmt(x)},${fmt(y)}`, label: `x: ${fmt(x)} y: ${fmt(y)} 으로 이동하기`})]};
        });

    def('setXY', 'motion', 'act', [
        `(?<ax>x|엑스|y|와이)\\s*(?:좌표|값|위치)?\\s*(?:를|을)?\\s*(?<n>${RE_NUM})\\s*(?:으로|로)\\s*(?:정하|정해|바꾸|바꿔|설정|해|만들)${REST}`,
        `(?<ax>x|엑스|y|와이)\\s*(?:좌표|값|위치)?\\s*(?:를|을)?\\s*(?<n>${RE_NUM})\\s*만큼\\s*(?:바꾸|바꿔|늘|올|움직|이동|더|변경)${REST}`
    ], (g, ctx, alt) => {
        const n = toNum(g.n);
        const axis = /y|와이/i.test(g.ax) ? 'y' : 'x';
        if (alt === 0) {
            return {nodes: [act(axis === 'x' ? 'motion_setx' : 'motion_sety', {
                inputs: {[axis.toUpperCase()]: num(n)}, sig: `${axis}=${fmt(n)}`, label: `${axis} 좌표를 ${euro(fmt(n))} 정하기`
            })]};
        }
        return {nodes: [act(axis === 'x' ? 'motion_changexby' : 'motion_changeyby', {
            inputs: {[axis === 'x' ? 'DX' : 'DY']: num(n)}, sig: `${axis}${n >= 0 ? '+' : ''}${fmt(n)}`, label: `${axis} 좌표를 ${fmt(n)}만큼 바꾸기`
        })]};
    });

    def('gotoMouse', 'motion', 'act',
        [
            `${RE_MOUSE}(?:를|을|쪽으로|로|에게|한테|의\\s*위치로|위치로)?\\s*(?:졸졸\\s*|쫄쫄\\s*|계속\\s*)?(?<vb>따라가|따라다니|따라와|따라|쫓아가|쫓아다니|쫓아오|쫓아와|쫓아|쫓|로\\s*가|이동)${REST}`,
            `${RE_MOUSE}\\s*(?:있는|쪽|위치|자리)\\s*(?:데|곳)?\\s*(?:로|으로)\\s*(?<vb>가|이동|순간\\s*이동|옮겨)${REST}`,
            `(?<vb>따라오|따라와|쫓아오|쫓아와)${REST}`
        ],
        // "따라가"는 계속 해야 따라가진다(loop). "마우스 있는 데로 가"는 한 번만.
        g => ({nodes: [act('motion_goto', {
            inputs: {TO: menu('motion_goto_menu', 'TO', '_mouse_')},
            sig: 'gotomouse', label: '마우스 포인터로 이동하기', loop: /따라|쫓/.test(g.vb || '')
        })]}));

    def('randomNoun', 'motion', 'act', '(?:랜덤|무작위|아무)\\s*(?:위치|곳|데)(?:로|으로)(?=\\s*$|\\s*[,.!])',
        () => ({nodes: [act('motion_goto', {inputs: {TO: menu('motion_goto_menu', 'TO', '_random_')}, sig: 'gotorandom', label: '무작위 위치로 이동하기'})]}));
    def('random', 'motion', 'act',
        `(?:(?:아무\\s*데|아무\\s*곳|아무데|딴\\s*데|딴\\s*곳|다른\\s*(?:데|곳)|랜덤(?:한)?(?:\\s*(?:위치|곳|자리))?|무작위(?:\\s*위치)?|여기저기)(?:로나|으로나|로|으로|에|에서|나)?\\s*(?:(?:막|그냥)\\s*)?(?<vb>가|이동|순간\\s*이동|나타나|움직|뛰어|옮기|옮겨|도망|돌아다니|돌아댕기|다니)|순간\\s*이동)${REST}`,
        g => {
            const wander = /돌아다니|돌아댕기|다니/.test(g.vb || '');
            const go = act('motion_goto', {inputs: {TO: menu('motion_goto_menu', 'TO', '_random_')}, sig: 'gotorandom', label: '무작위 위치로 이동하기', loop: wander || undefined});
            if (!wander) return {nodes: [go]};
            return {notes: ['‘돌아다니기’는 ‘무작위 위치로 이동하기’와 ‘1초 기다리기’를 계속 반복하게 했어요.'],
                nodes: [go, act('control_wait', {inputs: {DURATION: num(1, 'math_positive_number')}, sig: 'wait1', label: '1초 기다리기'})]};
        });

    def('pointMouse', 'motion', 'act',
        `${RE_MOUSE}(?:\\s*쪽|\\s*방향)?\\s*(?:을|를|으로|로)?\\s*(?:봐|보|바라보|바라봐|향하|향해|쳐다보|쳐다봐)${REST}`,
        () => ({nodes: [act('motion_pointtowards', {
            inputs: {TOWARDS: menu('motion_pointtowards_menu', 'TOWARDS', '_mouse_')},
            sig: 'pointmouse', label: '마우스 포인터 쪽 보기', loop: true
        })]}));

    def('pointDir', 'motion', 'act', [
        `(?<n>${RE_NUM})\\s*도\\s*(?:방향|쪽)(?:을|으로|로)?\\s*(?:봐|보|바라보|바라봐|향하|향해|정하|정해)${REST}`,
        `(?<d>위쪽|위|아래쪽|아래|오른쪽|왼쪽)\\s*(?:을|를|으로|쪽을)?\\s*(?:봐|보|바라보|바라봐|향하|향해|쳐다보|쳐다봐)${REST}`,
        `방향(?:을|를|은)?\\s*(?<n>${RE_NUM})\\s*도?(?:\\s*(?:로|으로))?(?:\\s*(?:바꿔|바꾸|정해|정하|해)[가-힣]*)?(?=\\s|$|[,.!])`
    ], g => {
        let n = toNum(g.n);
        if (n === null) {
            const d = g.d || '';
            n = /위/.test(d) ? 0 : /아래/.test(d) ? 180 : /왼/.test(d) ? -90 : 90;
        }
        return {nodes: [act('motion_pointindirection', {inputs: {DIRECTION: num(n, 'math_angle')}, sig: `dir${fmt(n)}`, label: `${fmt(n)}도 방향 보기`})]};
    });

    def('jump', 'motion', 'act',
        [`(?:위로\\s*)?올라갔다가?\\s*(?:다시\\s*)?(?:내려오|내려와)${REST}`,
        `(?:(?<n>${RE_NUM})\\s*(?:만큼\\s*)?(?:높이\\s*)?)?(?:위로\\s*)?(?:점프|점푸|쩜프|폴짝|깡충|뛰어\\s*올라|뛰어\\s*오르|높이\\s*뛰)${REST}(?:\\s*(?:다시\\s*)?(?:내려오|내려와|떨어지|착지)${REST})?`,
        `(?:점프(?:할\\s*때|하면서)?\\s*)?(?:위로\\s*)?(?:부드럽게\\s*)?올라갔다가?\\s*(?:다시\\s*)?(?<sm>부드럽게|천천히|스르르|살살)\\s*(?:내려오|내려와)${REST}`],
        (g, ctx) => {
            const h = Math.abs(toNum(g.n) || 50);
            if (g.sm || /중력|자연스럽|부드럽|포물선|진짜처럼/.test((ctx && ctx.src) || '')) {
                return {notes: ['부드러운 점프는 y 좌표를 5씩 10번 올렸다가 10번 내려요.'], nodes: [
                    {t: 'repeat', times: 10, body: [act('motion_changeyby', {inputs: {DY: num(5)}, sig: 'y+5', label: 'y 좌표를 5만큼 바꾸기'}), waitS(0.02)]},
                    {t: 'repeat', times: 10, body: [act('motion_changeyby', {inputs: {DY: num(-5)}, sig: 'y-5', label: 'y 좌표를 -5만큼 바꾸기'}), waitS(0.02)]}]};
            }
            return {notes: [NOTE.jump], nodes: [
                act('motion_changeyby', {inputs: {DY: num(h)}, sig: `y+${fmt(h)}`, label: `y 좌표를 ${fmt(h)}만큼 바꾸기`, part: 'jumpUp'}),
                act('control_wait', {inputs: {DURATION: num(0.3, 'math_positive_number')}, sig: 'wait0.3', label: '0.3초 기다리기', part: 'jumpWait'}),
                act('motion_changeyby', {inputs: {DY: num(-h)}, sig: `y-${fmt(h)}`, label: `y 좌표를 -${fmt(h)}만큼 바꾸기`, part: 'jumpDown'})
            ]};
        });

    // "인사해" → '안녕!' 말하기(무슨 말로 인사할지 고를 수 있게)
    def('greet', 'looks', 'act', `인사(?:를|도)?\\s*(?:하|해)${REST}`, () => {
        const note = '‘인사’는 ‘안녕!’이라고 말하게 했어요.';
        return {notes: [note], nodes: [act('looks_say', {inputs: {MESSAGE: txt('안녕!')}, sig: 'say(안녕!)', label: '‘안녕!’ 말하기',
            ask: {input: 'MESSAGE', q: '뭐라고 인사할까요?', options: null, text: true, note}})]};
    });

    // 크기를 N만큼 바꾸기(블록 글자 그대로)
    def('sizeBy', 'looks', 'act', `크기(?:를|을)?\\s*(?<n>-?\\d+(?:\\.\\d+)?)\\s*만큼\\s*(?:바꾸|바꿔|변경)${REST}`,
        g => ({nodes: [act('looks_changesizeby', {inputs: {CHANGE: num(toNum(g.n))}, sig: `size${toNum(g.n) >= 0 ? '+' : ''}${fmt(toNum(g.n))}`, label: `크기를 ${fmt(toNum(g.n))}만큼 바꾸기`})]}));
    // 특정 모양·배경으로 — "모양을 모양2로 바꾸기", "배경을 배경1로 바꾸기"
    const NOT_NAME = /^(?:다른|다음|이전|원래|랜덤|무작위|아무|걸|거)/;
    def('costumeTo', 'looks', 'act', [`모양(?:을|를)\\s*(?<nm>[^\\s]+?)(?:으로|로)\\s*(?:바꾸|바꿔|변경|해)${REST}`,
        `(?<nm>[^\\s]+?)(?:으로|로)(?:\\s*(?:모양(?:을)?\\s*)?(?:바꾸|바꿔|갈아입|변신)${REST}|(?=\\s*$))`], (g, ctx, alt) => {
        if (NOT_NAME.test(g.nm)) return null;
        const names = (ctx && ctx.costumeNames) || [];
        if (alt === 1 && names.indexOf(g.nm) < 0) return null; // 모양 이름을 그대로 말했을 때만
        const ok = !names.length || names.indexOf(g.nm) >= 0 || /^\d+$/.test(g.nm);
        return {notes: ok ? [] : [`‘${g.nm}’라는 모양이 없어요. 모양 탭에 있는 이름을 써 주세요.`],
            nodes: [act('looks_switchcostumeto', {inputs: {COSTUME: menu('looks_costume', 'COSTUME', g.nm)}, sig: `costume=${g.nm}`, label: `모양을 ${euro(g.nm)} 바꾸기`})]};
    });
    def('backdropTo', 'looks', 'act', [`배경(?:을|를)\\s*(?<nm>[^\\s]+?)(?:으로|로)\\s*(?:바꾸|바꿔|변경|해)${REST}`,
        `(?<nm>[^\\s]+?)(?:으로|로)\\s*(?:배경(?:을)?\\s*)?(?:바꾸|바꿔)${REST}`], (g, ctx, alt) => {
        if (NOT_NAME.test(g.nm)) return null;
        const names = (ctx && ctx.backdropNames) || [];
        if (alt === 1 && names.indexOf(g.nm) < 0) return null;
        if (names.indexOf(g.nm) < 0 && new RegExp(`^(?:${RE_COLOR})(?:색|색깔)?$`).test(g.nm)) return null; // "배경을 파란색으로" = 색
        const ok = !names.length || names.indexOf(g.nm) >= 0 || /^\d+$/.test(g.nm);
        return {notes: ok ? [] : [`‘${g.nm}’라는 배경이 없어요. 무대의 배경 탭에 있는 이름을 써 주세요.`],
            nodes: [act('looks_switchbackdropto', {inputs: {BACKDROP: menu('looks_backdrops', 'BACKDROP', g.nm)}, sig: `backdrop=${g.nm}`, label: `배경을 ${euro(g.nm)} 바꾸기`})]};
    });
    // 순서 — "맨 앞쪽으로 순서 바꾸기", "앞으로 1단계 보내기"
    def('layer', 'looks', 'act', [
        '맨\\s*(?<fb>앞|뒤)(?:쪽)?(?:으로)?\\s*(?:순서\\s*)?(?:바꾸|바꿔|보내|가|나오|이동)[가-힣]*',
        '(?<fb2>앞|뒤)(?:쪽)?으로\\s*(?<n>\\d+)\\s*단계\\s*(?:보내|이동|가)[가-힣]*'
    ], (g, ctx, alt) => {
        if (alt === 0) {
            const f = g.fb === '앞' ? 'front' : 'back';
            return {nodes: [act('looks_gotofrontback', {fields: fld('FRONT_BACK', f), sig: `layer:${f}`, label: `맨 ${g.fb}쪽으로 순서 바꾸기`})]};
        }
        const f = g.fb2 === '앞' ? 'forward' : 'backward';
        return {nodes: [act('looks_goforwardbackwardlayers', {fields: fld('FORWARD_BACKWARD', f), inputs: {NUM: num(toNum(g.n), 'math_integer')},
            sig: `layers:${f}${g.n}`, label: `${g.fb2}으로 ${g.n}단계 보내기`})]};
    });

    // ── 신호 보내기 ──
    def('bcastSend', 'events', 'act', ['(?<nm>[^\\s\'"]+?)\\s*(?:신호|메시지)(?:를|을)?\\s*(?<w>보내고\\s*기다리|보내|전달|쏴|방송)[가-힣]*',
        '(?<nm>[^\\s\'"]+?)\\s*(?:을|를)?\\s*(?<w>방송)[가-힣]*'], g => {
        let nm = g.nm;
        const notes = [];
        if (/한테|에게|스프라이트/.test(nm)) { nm = '메시지1'; notes.push('신호 이름을 말하지 않아서 ‘메시지1’로 했어요. 받는 쪽에서 ‘메시지1 신호를 받았을 때’를 쓰면 돼요.'); }
        const wait = /기다리/.test(g.w);
        return {notes, nodes: [act(wait ? 'event_broadcastandwait' : 'event_broadcast', {
            inputs: {BROADCAST_INPUT: {sh: 'event_broadcast_menu', field: 'BROADCAST_OPTION', value: nm, bcast: nm}},
            sig: `${wait ? 'sendwait' : 'send'}:${nm}`, label: `${nm} 신호 보내기${wait ? '고 기다리기' : ''}`
        })]};
    });
    // ── 복제 ──
    def('cloneMake', 'control', 'act', '(?:(?:나\\s*자신|자기\\s*자신|자신|나)(?:을|를)?\\s*)?복제(?:하기|해|해줘|하고|하자|하면서|해서)',
        () => ({nodes: [act('control_create_clone_of', {inputs: {CLONE_OPTION: menu('control_create_clone_of_menu', 'CLONE_OPTION', '_myself_')},
            sig: 'clone', label: '나 자신 복제하기'})]}));
    def('cloneDelete', 'control', 'act', '(?:이\\s*)?복제본(?:을|를)?\\s*(?:삭제|지워|지우|없애)[가-힣]*',
        () => ({nodes: [act('control_delete_this_clone', {sig: 'clonedelete', label: '이 복제본 삭제하기', cap: true})]}));
    // ── 묻고 기다리기 · 대답 ──
    def('ask', 'sensing', 'act', [
        '(?<q>이?라고|하고|[?？])\\s*(?:묻|물어|질문)[가-힣]*(?:\\s*기다리[가-힣]*)?',
        '(?<topic>이름|나이)(?:을|를)?\\s*(?:물어|묻|질문)[가-힣]*(?:\\s*기다리[가-힣]*)?'
    ], g => {
        const m = g.m ? (/[?？]/.test(g.q || '') ? `${g.m}?` : g.m) : (g.topic === '나이' ? '몇 살이야?' : '이름이 뭐야?');
        return {notes: ['‘묻고 기다리기’는 대답을 칠 때까지 기다려요. 친 대답은 감지의 ‘대답’ 블록에 들어가요.'],
            nodes: [act('sensing_askandwait', {inputs: {QUESTION: txt(m)}, sig: `ask(${m})`, label: `‘${m}’라고 묻고 기다리기`})]};
    }, {left: [0]});
    def('sayAnswer', 'looks', 'act', '(?:대답(?:을|를)?\\s*(?:따라\\s*)?|(?:대답(?:한|받은)?|그|친)\\s*이름(?:을|를)?\\s*)(?:말|외치)[가-힣]*',
        () => ({nodes: [act('looks_say', {inputs: {MESSAGE: Object.assign(txt(''), {node: act('sensing_answer', {sig: 'answer', label: '대답'})})},
            sig: 'say(대답)', label: '‘대답’ 말하기'})]}));
    // ── 감지 기타 ──
    def('dragMode', 'sensing', 'act', [
        '드래그\\s*모드를?\\s*(?:드래그\\s*)?(?<no>할\\s*수\\s*없는|못\\s*하는)?(?:할\\s*수\\s*있는)?\\s*상태로\\s*(?:정하|정해|해)[가-힣]*',
        '(?:마우스로\\s*)?(?:드래그|끌어서\\s*옮길|끌어서\\s*움직일)\\s*수\\s*(?<no2>없게|있게)[가-힣]*'
    ], g => {
        const no = Boolean(g.no) || g.no2 === '없게';
        return {nodes: [act('sensing_setdragmode', {fields: fld('DRAG_MODE', no ? 'not draggable' : 'draggable'), sig: `drag:${no ? 'no' : 'yes'}`,
            label: `드래그 모드를 드래그 할 수 ${no ? '없는' : '있는'} 상태로 정하기`})]};
    });
    def('timerReset', 'sensing', 'act', '타이머(?:를)?\\s*(?:초기화|리셋|0으로)[가-힣]*',
        () => ({nodes: [act('sensing_resettimer', {sig: 'timer:reset', label: '타이머 초기화'})]}));

    // ── 모양 ──
    const Q = '[\'"“”‘’]';
    const NQ = '[^\'"“”‘’]';
    def('say', 'looks', 'act', [
        `${Q}(?<m>${NQ}{1,60})${Q}\\s*(?:이?라고|하고|를|을)?\\s*(?:(?<s>${RE_ANY})\\s*초\\s*(?:동안|간)?\\s*)?(?<v>말|외치|외쳐|얘기|대답|속삭|생각)${REST}`,
        `(?<s>${RE_ANY})\\s*초\\s*(?:동안|간)\\s*${Q}?(?<m>[^\\s'"“”‘’]{1,20}?)${Q}?\\s*(?:이?라고)?\\s*(?<v>말|외치|외쳐|얘기|대답|생각)${REST}`,
        // 따옴표 없는 "X라고 말해": "라고"를 닻으로 잡고, 할 말(X)은 번역기가 앞 블록 끝~"라고" 사이에서 잘라 낸다(left)
        // (블라인드 실측: "안녕하세요 제 이름은 나비예요 라고" 가 "나비예요"만 남았다)
        `(?<q>이?라고|다고|하고)\\s*(?:(?<s>${RE_SEC})\\s*초\\s*(?:동안|간)?\\s*)?(?<v>말|외치|외쳐|얘기|대답|속삭|생각|띄워|띄우)${REST}(?:\\s*(?:해|해줘|해주세요|해봐))?(?:\\s*(?<s2>${RE_SEC})\\s*초\\s*(?:동안|간)?)?`,
        // "안녕 말하기"(라고 없이) — 할 말은 앞에서 잘라 온다. 없거나 "대답을"처럼 목적어면 기본 말
        `(?<v>말|생각)(?:을|를)?\\s*(?:해|하|하기)${REST}`,
        // "말하기 나는 큐로야" — 문장 첫머리의 "말하기" 뒤가 할 말
        '^\\s*(?:말하기|말해|말해줘)\\s*[:：]?\\s*(?<m>[^.!?]{1,40})$',
        `(?<m>[^\\s'"“”‘’]{1,20}?)(?:을|를)\\s*(?<s>${RE_SEC})\\s*초\\s*(?:동안|간)?\\s*(?<v>말|생각)${REST}`,
        // "나는 고양이다 3초 말하기" — 할 말은 앞에서 잘라 온다
        `(?<s>${RE_SEC})\\s*초\\s*(?:동안|간)?\\s*(?<v>말|생각)${REST}`
    ], (g, ctx) => {
        const notes = [];
        const think = g.v === '생각';
        let m = (g.m || '').trim();
        if (m && g.q === '다고') m += '다'; // "반갑다고" → "반갑다"
        if (!m) { m = '안녕!'; notes.push(NOTE.say); }
        const s = toNum(g.s) !== null ? toNum(g.s) : toNum(g.s2);
        // "대답으로 ~라고 말해", "대답이랑 같이" — 친 대답을 말 뒤에 붙인다
        const tailAns = (ctx && (ctx.hasAsk || /대답하면|물어|묻/.test(ctx.src || ''))) ? /^(.+?)\s+대답$/.exec(m) : null;
        if (tailAns) return {notes: ['‘결합하기’ 블록으로 할 말 뒤에 대답을 붙였어요.'], nodes: [sayOf(join(`${tailAns[1]} `, act('sensing_answer', {sig: 'answer', label: '대답'})), s, `${tailAns[1]} (대답)`)]};
        const withAns = /^대답(?:한|받은|친)?\s*(?:이름|거|것|말)?\s*(?:으로|이랑|하고|을\s*넣어서|을\s*붙여서|넣어서)\s*/.exec(m);
        if (withAns) {
            const rest = m.slice(withAns[0].length).trim() || '안녕';
            return {notes: ['‘결합하기’ 블록으로 할 말 뒤에 대답을 붙였어요.'], nodes: [sayOf(join(`${rest} `, act('sensing_answer', {sig: 'answer', label: '대답'})), s, `${rest} (대답)`)]};
        }
        if (s !== null) {
            return {notes, nodes: [act(think ? 'looks_thinkforsecs' : 'looks_sayforsecs', {
                inputs: {MESSAGE: txt(m), SECS: num(s)},
                sig: `${think ? 'thinkfor' : 'sayfor'}(${m},${fmt(s)})`, label: `‘${m}’을(를) ${fmt(s)}초 동안 ${think ? '생각하기' : '말하기'}`
            })]};
        }
        return {notes, nodes: [act(think ? 'looks_think' : 'looks_say', {
            inputs: {MESSAGE: txt(m)}, sig: `${think ? 'think' : 'say'}(${m})`, label: `‘${m}’ ${think ? '생각하기' : '말하기'}`
        })]};
    }, {left: [2, 3, 6], leftOptional: [3]});

    def('costume', 'looks', 'act',
        `(?:(?:다음\\s*)?(?:모양|옷|코스튬)(?:을|를|으로|로|이|도)?\\s*(?:(?:다른\\s*(?:걸로|것으로|거로)|막|자꾸|좀|(?<lp>계속))\\s*)?(?:바꾸|바꿔|넘기|넘겨|갈아입|변신|변해|바뀌)|다음\\s*(?:모양|코스튬)(?:으로|을|로)?|변신)${REST}`,
        g => ({loopBefore: Boolean(g.lp), nodes: [act('looks_nextcostume', {sig: 'nextcostume', label: '다음 모양으로 바꾸기'})]}));
    def('backdrop', 'looks', 'act', [
        `(?:(?:다음\\s*)?(?:배경|장면)(?:을|를|으로|로|이)?\\s*(?:(?:다른\\s*(?:걸로|것으로|거로|배경으로)|막|자꾸|좀)\\s*)?(?:바꾸|바꿔|넘기|넘겨|변경|바뀌)|다음\\s*배경(?:으로|을|로)?)${REST}`,
        `배경(?:을|를)?\\s*(?:랜덤|무작위|아무거나|아무\\s*거나)(?:으로|로)?\\s*(?:바꾸|바꿔|정하|해)${REST}`
    ], (g, ctx, alt) => (alt === 1 ?
        {nodes: [act('looks_switchbackdropto', {inputs: {BACKDROP: menu('looks_backdrops', 'BACKDROP', 'random backdrop')}, sig: 'backdrop=random', label: '무작위 배경으로 바꾸기'})]} :
        {nodes: [act('looks_nextbackdrop', {sig: 'nextbackdrop', label: '다음 배경으로 바꾸기'})]}));
    // 유령(투명) 효과 — "투명하게", "서서히 사라지게"
    def('ghost', 'looks', 'act', [
        '(?:(?<half>반쯤|반|절반|살짝|조금|약간|완전히|완전)\\s*)?(?:투명(?:하게|해|해지|해져|해줘)|반투명)[가-힣]*',
        '(?<gr>서서히|천천히|점점)\\s*(?:투명|사라지|사라져|없어지|없어져)[가-힣]*',
        '투명(?:하게|해지면서|해지며|해져서)\\s*(?<gr2>사라지|사라져|없어지|없어져)[가-힣]*'
    ], (g, ctx, alt) => {
        if (alt === 1 || alt === 2) {
            return {notes: [`‘${g.gr || '투명하게'}’ 사라지기는 유령 효과를 조금씩 10번 올려서 만들어요.`], nodes: [{t: 'repeat', times: 10, body: [
                act('looks_changeeffectby', {fields: fld('EFFECT', 'GHOST'), inputs: {CHANGE: num(10)}, sig: 'ghost+10', label: '유령 효과를 10만큼 바꾸기'}),
                act('control_wait', {inputs: {DURATION: num(0.05, 'math_positive_number')}, sig: 'wait0.05', label: '0.05초 기다리기'})
            ]}]};
        }
        const gv = /반|절반/.test(g.half || '') ? 50 : (/살짝|조금|약간/.test(g.half || '') ? 30 : (/완전/.test(g.half || '') ? 100 : 70));
        return {notes: [`‘${g.half ? `${g.half} ` : ''}투명하게’는 유령 효과를 ${gv}으로 정했어요. 0이면 원래대로, 100이면 완전히 안 보여요.`],
            nodes: [act('looks_seteffectto', {fields: fld('EFFECT', 'GHOST'), inputs: {VALUE: num(gv)}, sig: `ghost=${gv}`, label: `유령 효과를 ${gv}으로 정하기`})]};
    });
    def('brightness', 'looks', 'act', [
        `밝기(?:를|을)?\\s*(?:좀\\s*)?(?:(?<n>${RE_NUM})\\s*만큼\\s*)?(?<d>올려|올리|높여|키워|내려|낮춰|줄여)[가-힣]*`,
        '(?<d>밝게|어둡게)\\s*(?:해|하|만들|바꿔)[가-힣]*'
    ], g => {
        const n = Math.abs(toNum(g.n) || 25);
        const v = /내려|낮춰|줄여|어둡/.test(g.d) ? -n : n;
        return {nodes: [act('looks_changeeffectby', {fields: fld('EFFECT', 'BRIGHTNESS'), inputs: {CHANGE: num(v)}, sig: `brighten${v >= 0 ? '+' : ''}${fmt(v)}`, label: `밝기 효과를 ${fmt(v)}만큼 바꾸기`})]};
    });
    def('hide', 'looks', 'act', `(?:숨(?!겨진|겨져|어\\s*있|어있)|사라지|사라져|사라|안\\s*보이|보이지\\s*않|없어지|없어져)${REST}`,
        (g, ctx, alt, before) => (/(?:목|한)$/.test(before || '') ? null : {nodes: [act('looks_hide', {sig: 'hide', label: '숨기기'})]}));
    def('show', 'looks', 'act', [`(?:다시\\s*)?(?:나타나|나타|보이게|보이도록|등장|보이기)${REST}`, `(?:다시\\s*|짠\\s*하고\\s*|짜잔\\s*(?:하고\\s*)?)(?:나오|나와)${REST}`],
        () => ({nodes: [act('looks_show', {sig: 'show', label: '보이기'})]}));

    def('size', 'looks', 'act', [
        `(?:(?<gr>점점|조금씩|서서히)\\s*(?:더\\s*)?)?(?:크기(?:를|가|을)?\\s*)?(?:(?<n>${RE_NUM})\\s*(?:만큼|%|퍼센트|씩)?\\s*|(?<v>${RE_VAGUE})만?\\s*)?(?:커지|커져|커짐|크게|키우|키워|늘려|늘리|확대|거대해)${REST}`,
        `(?:(?<gr>점점|조금씩|서서히)\\s*(?:더\\s*)?)?(?:크기(?:를|가|을)?\\s*)?(?:(?<n>${RE_NUM})\\s*(?:만큼|%|퍼센트|씩)?\\s*|(?<v>${RE_VAGUE})만?\\s*)?(?:작아지|작아져|작아|작게|줄이|줄여|줄어|축소)${REST}`
    ], (g, ctx, alt, before) => {
        // "속도 좀 줄여", "소리 줄여" 의 "줄여"는 크기가 아니다
        if (/(?:속도|빠르기|스피드|소리|음량|볼륨|점수)\s*(?:를|을|는|가)?\s*(?:좀\s*)?$/.test(before || '')) return null;
        if (/커지다가\s*(?:다시\s*)?원래/.test((ctx && ctx.src) || '')) return null;
        if (/타이머|카운트|숫자|초부터|거꾸로/.test((ctx && ctx.src) || '')) return null;
        const notes = [];
        let n = toNum(g.n);
        if (n === null && g.v && g.v !== '크게' && g.v !== '작게') { n = VAGUE[g.v]; notes.push(noteVague(g.v, n)); }
        if (n === null) { n = 10; notes.push(noteDefault(alt === 0 ? '커질' : '작아질', n)); }
        let v = alt === 0 ? Math.abs(n) : -Math.abs(n);
        if (g.gr && toNum(g.n) === null) v = v > 0 ? 5 : -5;
        const node = act('looks_changesizeby', {inputs: {CHANGE: num(v)}, sig: `size${v >= 0 ? '+' : ''}${fmt(v)}`, label: `크기를 ${fmt(v)}만큼 바꾸기`});
        if (!g.gr) return {notes, nodes: [node]};
        // "점점 커져" = 조금씩 여러 번 → 반복으로 나눠 보여 준다(한 번에 확 커지면 '점점'이 아니다)
        return {notes: [`‘${g.gr}’${hasBatchim(g.gr) ? '은' : '는'} ‘조금씩 10번 반복하기’로 나눴어요.`], nodes: [
            {t: 'repeat', times: 10, body: [node, act('control_wait', {inputs: {DURATION: num(0.05, 'math_positive_number')}, sig: 'wait0.05', label: '0.05초 기다리기'})]}
        ]};
    });
    def('setSize', 'looks', 'act', [
        `크기(?:를|을)?\\s*(?<n>${RE_NUM})\\s*(?:%|퍼센트)?\\s*(?:으로|로)\\s*(?:정하|정해|바꾸|바꿔|해|만들|설정|맞춰|줄이|줄여|키워|키우|늘려|늘리|초기화|돌려|되돌)${REST}`,
        `(?:원래|처음|보통)\\s*크기(?:로|으로)?\\s*(?:돌아와|돌아가|돌아|바꿔|바꾸|해|만들|되|돌려)?${REST}`,
        `크기(?:를|가|을)?\\s*(?<mul>두|세|네|2|3|4|반)\\s*배?\\s*(?:로|으로)?(?:\\s*(?:키워|키우|만들|늘려|늘리|해|커지|커져|줄여|줄이)${REST})?`,
        `원래대로\\s*(?:작아|커|돌아|되돌|해)${REST}`,
        `크기(?:는|를|도)?\\s*(?<n>${RE_NUM})\\s*(?:%|퍼센트)?(?:\\s*(?:으로|로))?(?=\\s*$|\\s*[,.!]|\\s+(?:방향|위치|이고|하고))`,
        '원래(?:대로|\\s*크기로)(?=\\s*$|\\s*[,.!])'
    ], (g, ctx, alt, before) => {
        if (alt === 5 && !/커|작|크기|키우|키워/.test(before || '')) return null;
        const notes = [];
        const n = toNum(g.n);
        let v = n === null ? 100 : n;
        if (g.mul) {
            v = g.mul === '반' ? 50 : 100 * toNum(g.mul);
            notes.push(`지금 크기를 몰라서 처음 크기(100%)의 ${g.mul === '반' ? '반' : `${toNum(g.mul)}배`}인 ${fmt(v)}%로 정했어요.`);
        }
        return {notes, nodes: [act('looks_setsizeto', {inputs: {SIZE: num(v)}, sig: `size=${fmt(v)}`, label: `크기를 ${fmt(v)} %로 정하기`})]};
    });

    def('color', 'looks', 'act', [
        `(?:색깔|색상|색)(?:\\s*효과)?(?:을|를|이|가|깔을)?\\s*(?:(?:무지개|알록달록)(?:처럼|같이)?\\s*)?(?:(?<n>${RE_NUM})\\s*(?:만큼|씩)\\s*)?(?:(?:막|자꾸|좀)\\s*)?(?:(?<lp>계속)\\s*)?(?:바꾸|바꿔|변하|변해|변신|바뀌|바껴|달라)${REST}(?:\\s*(?:좋겠어|좋겠다|좋겠어요))?`,
        `(?:무지개|알록달록|반짝반짝)(?:\\s*색)?(?:으로|로)?\\s*(?:바꾸|바꿔|변하|변해|바뀌|빛나|반짝|하게|해)${REST}`,
        `(?<c>빨간|빨강|파란|파랑|노란|노랑|초록|녹색|보라|분홍|핑크|하얀|흰|검은|까만|주황)\\s*(?:색|색깔)?\\s*(?:으로|로)\\s*(?:바꾸|바꿔|바꿀|변하|변해|변신|돼|되|바뀌)${REST}`,
        `(?:색깔|색상|색)\\s*효과(?:를|을)?\\s*(?<n>${RE_NUM})\\s*(?:으로|로)\\s*(?:정하|정해|바꾸|바꿔|해|만들|설정)${REST}`
    ], (g, ctx, alt) => {
        const n = toNum(g.n) === null ? 25 : toNum(g.n);
        if (alt === 3) {
            return {nodes: [act('looks_seteffectto', {
                fields: fld('EFFECT', 'COLOR'), inputs: {VALUE: num(n)}, sig: `color=${fmt(n)}`, label: `색깔 효과를 ${euro(fmt(n))} 정하기`
            })]};
        }
        return {loopBefore: Boolean(g.lp), notes: alt === 2 ? [NOTE.color] : [], nodes: [act('looks_changeeffectby', {
            fields: fld('EFFECT', 'COLOR'), inputs: {CHANGE: num(n)}, sig: `color${fmt(n)}`, label: `색깔 효과를 ${fmt(n)}만큼 바꾸기`
        })]};
    });
    // 소리 효과 — "음 높이 효과를 10만큼 바꾸기", "소리 효과 지우기"
    def('soundFx', 'sound', 'act', [
        `(?<fx>음\\s*높이|피치|음향\\s*위치|좌우\\s*음향)\\s*효과(?:를|을)?\\s*(?<n>-?\\d+(?:\\.\\d+)?)\\s*만큼\\s*(?:바꾸|바꿔|변경|올려|내려)${REST}`,
        `(?<fx>음\\s*높이|피치|음향\\s*위치|좌우\\s*음향)\\s*효과(?:를|을)?\\s*(?<n>-?\\d+(?:\\.\\d+)?)\\s*(?:으로|로)\\s*(?:정하|정해|바꿔|해)${REST}`,
        '소리\\s*효과(?:를|을)?\\s*(?:지우|지워|없애|초기화)[가-힣]*',
        '(?<fx>음\\s*높이|피치|음정)(?:\\s*효과)?(?:를|을)?\\s*(?<d>올려|높여|높게|내려|낮춰|낮게)[가-힣]*'
    ], (g, ctx, alt) => {
        if (alt === 2) return {nodes: [act('sound_cleareffects', {sig: 'soundfx:clear', label: '소리 효과 지우기'})]};
        if (alt === 3) {
            const v = /내려|낮/.test(g.d) ? -50 : 50;
            return {nodes: [act('sound_changeeffectby', {fields: fld('EFFECT', 'PITCH'), inputs: {VALUE: num(v)}, sig: `soundfx:PITCH+${v}`, label: `음 높이 효과를 ${v}만큼 바꾸기`})]};
        }
        const fx = /위치|좌우/.test(g.fx) ? 'PAN' : 'PITCH';
        const ko = fx === 'PAN' ? '음향 위치' : '음 높이';
        const v = toNum(g.n);
        if (alt === 0) return {nodes: [act('sound_changeeffectby', {fields: fld('EFFECT', fx), inputs: {VALUE: num(v)}, sig: `soundfx:${fx}+${fmt(v)}`, label: `${ko} 효과를 ${fmt(v)}만큼 바꾸기`})]};
        return {nodes: [act('sound_seteffectto', {fields: fld('EFFECT', fx), inputs: {VALUE: num(v)}, sig: `soundfx:${fx}=${fmt(v)}`, label: `${ko} 효과를 ${euro(fmt(v))} 정하기`})]};
    });
    def('clearFx', 'looks', 'act',
        `(?:그래픽\\s*효과|효과|원래\\s*색|원래\\s*모습)(?:를|을|으로|로)?\\s*(?:다\\s*|모두\\s*|전부\\s*)?(?:지우|지워|없애|되돌리|되돌려|돌아와|돌아가|돌리|돌려)${REST}`,
        () => ({nodes: [act('looks_cleargraphiceffects', {sig: 'cleareffects', label: '그래픽 효과 지우기'})]}));

    // ── 소리 ── (스프라이트가 가진 소리 이름을 그대로 알아듣는다)
    const esc = s => String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const ONOMA = ['미야옹', '야옹', '냐옹', '냥', '멍멍', '삐', '딩동', '빵빵', '꽥', '음메', '짹짹', '펑'];
    const names = soundNames.filter(Boolean).map(esc).concat(ONOMA).join('|');
    const pickSound = nm => {
        if (!soundNames.length) return {name: null, notes: [NOTE.noSound]};
        if (nm) {
            const hit = soundNames.find(s => s === nm) || soundNames.find(s => s.indexOf(nm) >= 0 || nm.indexOf(s) >= 0);
            if (hit) return {name: hit, notes: []};
            return {name: soundNames[0], notes: [`‘${nm}’ 소리가 없어서 ‘${soundNames[0]}’ 소리를 넣었어요. 소리 탭에서 추가할 수 있어요.`]};
        }
        return {name: soundNames[0], notes: []};
    };
    def('sound', 'sound', 'act', [
        `(?:배경\\s*)?(?:(?<nm>${names})\\s*)?(?:소리|효과음|음악|노래|사운드)(?:를|을|가|도)?\\s*(?:(?<rn>${RE_ANY})\\s*번\\s*)?(?:(?<u>끝까지)\\s*)?(?:(?<lp>계속)\\s*)?(?:(?<ad>엄청\\s*크게|아주\\s*크게|크게|작게|막|좀)\\s*)?(?:내|재생|틀|들려|울리|울려|켜|나오|나게|플레이)${REST}`,
        `(?<nm>${names})(?:도|을|를)?\\s*(?:(?<rn>${RE_ANY})\\s*번\\s*)?(?:(?:하고|하며|이라고|라고)\\s*)?(?:울|해|하|거리|거려)${REST}`,
        `(?:울어|울고|울기|울게)${REST}`,
        // "…하고 야옹", "무한으로 야옹야옹야옹" — 소리 이름만 말한 경우(문장 끝이나 이어지는 말 앞)
        `(?<nm>${names})(?:\\s*(?:${names}))*(?=\\s*$|\\s*[,.!]|\\s+(?:그리고|이거|계속|하고)|\\s+(?:${RE_ANY})\\s*번|\\s+\\S{1,6}\\s*(?:누르면|클릭하면|키\\s*누르면))`,
        `(?<nm>${names})\\s*(?:을|를)?\\s*(?<u>끝까지\\s*)?(?:재생|틀어|틀)${REST}`,
        `(?:(?<nm>${names})\\s*)?(?:소리)?(?:를|을)?\\s*(?:재생|틀|내)[가-힣]*\\s*(?<u>끝날)\\s*때\\s*까지\\s*(?:기다리|기다려)[가-힣]*`,
        `(?:(?<nm>${names})\\s*)?(?:소리|효과음|음악)(?:도|를)?(?:\\s*(?:같이|함께|한번|한\\s*번))?(?=\\s*$|\\s*[,.!]|\\s+(?:${RE_ANY})\\s*번|\\s+(?:가끔|가끔씩|이따금|계속|자주))`
    ], (g, ctx) => {
        // 로봇을 만드는 중에 "소리도 내줘" = 로봇 사운드 블록(대표 실측: 자동차에 고양이 야옹이 붙었다)
        if (ctx && ctx.robot && !g.nm) {
            return {notes: ['로봇 사운드 블록으로 ‘기본’ 소리를 내요. 화면 속 고양이 소리를 원하면 ‘야옹 소리’라고 말해 보세요.'],
                nodes: [act('cubroidsound_errorSoundControl', {inputs: {ERROR_SOUND: menu('cubroidsound_menu_cubroidErrorSoundAction', 'cubroidErrorSoundAction', '기본')}, sig: 'beep:기본', label: '‘기본’ 소리 내기'})]};
        }
        const p = pickSound(g.nm);
        if (!p.name) return {notes: p.notes, nodes: []};
        const times = toNum(g.rn);
        // 여러 번 낼 때 "재생하기"는 소리가 겹쳐 한 번처럼 들린다 → "끝까지 재생하기"로
        const wait = Boolean(g.u) || (times !== null && times > 1);
        const node = act(wait ? 'sound_playuntildone' : 'sound_play', {
            inputs: {SOUND_MENU: menu('sound_sounds_menu', 'SOUND_MENU', p.name)},
            sig: `${wait ? 'soundwait' : 'sound'}(${p.name})`, label: `${p.name} ${wait ? '끝까지 ' : ''}재생하기`
        });
        // "엄청 크게 내" — 소리 크기는 음량 블록으로
        const vol = /크게/.test(g.ad || '') ? 100 : (/작게/.test(g.ad || '') ? 30 : null);
        const volNode = vol === null ? [] : [act('sound_setvolumeto', {inputs: {VOLUME: num(vol)}, sig: `vol=${vol}`, label: `음량을 ${vol}%로 정하기`})];
        const vnote = vol === null ? [] : ['소리 크기는 ‘음량을 ~%로 정하기’ 블록으로 바꿔요.'];
        if (times === null || times <= 1) return {loopBefore: Boolean(g.lp), notes: p.notes.concat(vnote), nodes: volNode.concat([node])};
        return {notes: p.notes.concat(vnote, ['여러 번 낼 때는 ‘끝까지 재생하기’를 써야 소리가 겹치지 않아요.']), nodes: volNode.concat([
            {t: 'repeat', times: Math.round(times), body: [node]}
        ])};
    });
    def('soundHigh', 'sound', 'act', `(?:(?<nm>${names})\\s*)?소리(?:를|가)?\\s*(?<d>높게|낮게|높은|낮은)(?:\\s*(?:해|내|나게|만들|바꿔서|해서)[가-힣]*)?(?:\\s*(?<nm2>${names})(?:\\s*(?:해|소리|해줘))?)?`, g => {
        const p = pickSound(g.nm);
        if (!p.name) return {notes: p.notes, nodes: []};
        const v = /낮/.test(g.d) ? -50 : 50;
        return {nodes: [act('sound_changeeffectby', {fields: fld('EFFECT', 'PITCH'), inputs: {VALUE: num(v)}, sig: `soundfx:PITCH+${v}`, label: `음 높이 효과를 ${v}만큼 바꾸기`}),
            act('sound_playuntildone', {inputs: {SOUND_MENU: menu('sound_sounds_menu', 'SOUND_MENU', p.name)}, sig: `soundwait(${p.name})`, label: `${p.name} 끝까지 재생하기`})]};
    });
    def('soundAgain', 'sound', 'act', `(?:(?<nm>${names})|음악|노래|소리)\\s*(?:을|를)?\\s*끝까지\\s*(?:재생|틀)[가-힣]*\\s*(?:끝나면\\s*)?다시\\s*(?:재생|틀)[가-힣]*`, g => {
        const p = pickSound(g.nm);
        if (!p.name) return {notes: p.notes, nodes: []};
        return {notes: ['‘끝까지 재생하기’를 계속 반복하면, 끝나자마자 다시 재생돼요.'], nodes: [{t: 'forever', body: [act('sound_playuntildone', {inputs: {SOUND_MENU: menu('sound_sounds_menu', 'SOUND_MENU', p.name)}, sig: `soundwait(${p.name})`, label: `${p.name} 끝까지 재생하기`})]}]};
    });
    def('stopSound', 'sound', 'act', `(?:모든\\s*)?(?:소리|음악|노래|사운드)(?:를|을|도)?\\s*(?:다\\s*|모두\\s*|전부\\s*)?(?:끄|꺼|멈추|멈춰|정지|그만|끝내)${REST}`,
        () => ({nodes: [act('sound_stopallsounds', {sig: 'stopsounds', label: '모든 소리 끄기'})]}));
    def('volume', 'sound', 'act', [
        `(?:음량|볼륨|소리\\s*크기)(?:을|를)?\\s*(?<n>${RE_NUM})\\s*(?:%|퍼센트)?\\s*(?:으로|로)?\\s*(?:정하|정해|바꾸|바꿔|해|맞추|맞춰|설정)?${REST}`,
        `(?:소리|음량|볼륨)(?:\\s*크기)?(?:를|을|가)?\\s*(?:좀\\s*)?(?:(?<n>${RE_NUM})\\s*만큼\\s*)?(?<d>키워|키우|크게|높여|높이|올려|줄여|줄이|작게|낮춰|낮추|내려)${REST}`,
        `(?:음량|볼륨|소리\\s*크기)(?:을|를)?\\s*(?<n>-?\\d+(?:\\.\\d+)?)\\s*만큼\\s*(?:바꾸|바꿔|변경)${REST}`,
        '(?:소리|음량|볼륨)(?:가|를|이)?\\s*(?:점점|조금씩|서서히)\\s*(?<d>커지|커져|크게|키워|작아지|작아져|작게|줄어|줄여)[가-힣]*'
    ], (g, ctx, alt) => {
        if (alt === 3) {
            const v = /작|줄/.test(g.d) ? -10 : 10;
            const p = pickSound(null);
            const body = [act('sound_changevolumeby', {inputs: {VOLUME: num(v)}, sig: `vol${v >= 0 ? '+' : ''}${v}`, label: `음량을 ${v}만큼 바꾸기`})];
            if (p.name) body.push(act('sound_playuntildone', {inputs: {SOUND_MENU: menu('sound_sounds_menu', 'SOUND_MENU', p.name)}, sig: `soundwait(${p.name})`, label: `${p.name} 끝까지 재생하기`}));
            return {notes: ['음량을 조금씩 바꾸면서 소리를 내요.'], nodes: [act('sound_setvolumeto', {inputs: {VOLUME: num(v > 0 ? 20 : 100)}, sig: `vol=${v > 0 ? 20 : 100}`, label: `음량을 ${v > 0 ? 20 : 100}%로 정하기`}), {t: 'repeat', times: 8, body}]};
        }
        if (alt === 0) {
            const n = toNum(g.n);
            return {nodes: [act('sound_setvolumeto', {inputs: {VOLUME: num(n)}, sig: `vol=${fmt(n)}`, label: `음량을 ${fmt(n)}%로 정하기`})]};
        }
        const n = Math.abs(toNum(g.n) || 10);
        const v = alt === 2 ? toNum(g.n) : (/줄|작|낮|내려/.test(g.d) ? -n : n);
        return {nodes: [act('sound_changevolumeby', {inputs: {VOLUME: num(v)}, sig: `vol${v >= 0 ? '+' : ''}${fmt(v)}`, label: `음량을 ${fmt(v)}만큼 바꾸기`})]};
    });

    // ── 제어 ──
    def('wait', 'control', 'act', [
        `(?<n>${RE_SEC})\\s*초\\s*(?:동안|간)?\\s*(?:기다리|기다려|기다린|기다렸|쉬|쉬어|쉰|멈추|멈춰|멈춘|대기|있다가|있다|있어|정지|가만히)${REST}`,
        `(?<n>${RE_SEC})\\s*초\\s*(?:후에|후|뒤에|뒤|있다가|지나면|지나서|지난\\s*다음|다음에)`,
        `(?<q>잠깐|잠시)\\s*(?:기다리|기다려|기다렸|쉬|쉬어|멈추|멈춰|있다가|대기|후에|뒤에)${REST}`,
        `(?<n>${RE_SEC})\\s*초\\s*(?:마다|에\\s*한\\s*번씩|간격으로)`,
        '(?:^|\\s)(?:쉬고|쉬어|쉬었다가|쉬기|쉬었다|쉬면서)(?=\\s|$|[,.])'
    ], (g, ctx, alt) => {
        const notes = [];
        let n = toNum(g.n);
        if (n === null && alt === 4) { n = 1; notes.push('얼마나 쉴지 말하지 않아서 1초로 했어요. 숫자로 말하면 정확해져요.'); }
        if (n === null) { n = 1; notes.push(NOTE.wait); }
        // "N초마다" — 기다리기를 반복 안에 넣는다(앞 동작과 함께). every 표시는 nest 가 본다.
        const extra = alt === 3 ? {every: true, loop: true} : {};
        if (alt === 3) notes.push(`‘${fmt(n)}초마다’는 ‘${fmt(n)}초 기다리기’를 반복 안에 넣어서 만들어요.`);
        return {notes, nodes: [act('control_wait', Object.assign({inputs: {DURATION: num(n, 'math_positive_number')}, sig: `wait${fmt(n)}`, label: `${fmt(n)}초 기다리기`}, extra))]};
    });
    def('repeat', 'control', 'repeat', [
        `(?<n>${RE_ANY})\\s*번(?:\\s*(?:반복|되풀이|씩|이나|정도|동안))?(?:해서|하면서|하기|하고|해|하는|하도록|하게|을|를)?`,
        `(?:반복|되풀이)(?:를|을)?\\s*(?<n>${RE_ANY})\\s*번(?:\\s*(?:해서|해|하기|하고))?`
    ], g => {
        const n = toNum(g.n);
        if (n === null || n < 1) return null;
        return {times: Math.round(n)};
    });
    def('forever', 'control', 'forever',
        '(?:반복(?=\\s*$|\\s*[,.!])|계속\\s*반복(?:해서|해|하기|하면서|하는데|하다가)?|계속(?:해서|해|하면서)?|무한\\s*반복(?:으로|해서|해|하기)?|무한(?:으로|히|대로)?|영원히|끝없이|쭉|반복해서|반복하면서|반복(?:하기|해줘|해))',
        () => ({}));
    def('stop', 'control', 'act', [
        `(?:(?:모두|모든\\s*(?:것|거|스크립트|코드)?|스크립트|코드|프로그램|전부|게임|다)(?:을|를|이|가)?\\s*)*(?:멈춰|멈추|멈춤|정지|끝내|종료|그만)${REST}`,
        `게임\\s*(?:끝(?!나면|났을|나고|나서)|오버(?!되면|\\s*되면)|종료(?!되면))(?:이야|이에요|이다|내|나|내줘)?${REST}`,
        '끝나(?![면고])[가-힣]*'
    ],
        () => ({nodes: [act('control_stop', {
            fields: fld('STOP_OPTION', 'all'),
            mutation: {tagName: 'mutation', children: [], hasnext: 'false'},
            sig: 'stop', label: '멈추기 (모두)', cap: true
        })]}));

    // ── 변수(점수) ──
    const varOf = v => (v === '카운트' ? '카운트' : '점수');
    def('scoreBy', 'variables', 'act', `(?<v>점수|스코어|포인트|카운트)(?:를|을)?\\s*(?<n>-?\\d+(?:\\.\\d+)?)\\s*만큼\\s*(?:바꾸|바꿔|변경)${REST}`,
        g => {
            const name = varOf(g.v);
            const v = toNum(g.n);
            return {nodes: [act('data_changevariableby', {inputs: {VALUE: num(v)}, varName: name, sig: `score${v >= 0 ? '+' : ''}${fmt(v)}`, label: `${eul(name)} ${fmt(v)}만큼 바꾸기`})]};
        });
    def('varShow', 'variables', 'act', `(?<v>점수|스코어|포인트|카운트)(?:\\s*변수)?(?:를|을)?\\s*(?<d>보이|보여|숨|안\\s*보이)${REST}`,
        g => {
            const name = varOf(g.v);
            const show = !/숨|안/.test(g.d);
            return {nodes: [act(show ? 'data_showvariable' : 'data_hidevariable', {varName: name, sig: `var${show ? 'show' : 'hide'}:${name}`, label: `${name} 변수 ${show ? '보이기' : '숨기기'}`})]};
        });
    def('scoreChange', 'variables', 'act', [
        `(?<v>점수|스코어|포인트|카운트)(?:를|을|가|이|도)?\\s*(?:(?<n>${RE_ANY})\\s*(?:점\\s*)?(?:만큼\\s*|씩\\s*)?)?(?<d>올려|올리|올라|늘려|늘리|늘어|더해|더하|증가|추가|높여|깎|빼|줄여|줄이|줄어|감소|잃)${REST}`,
        `(?<n>${RE_ANY})\\s*점\\s*(?:을|를|이|씩)?\\s*(?<d>올려|올라|오르|늘어|더해|추가|얻|받|따|깎|잃|빼|내려가|줄어)${REST}`,
        // "점수 … 1점씩", "1씩 올려" — 앞에 점수 이야기가 있을 때만
        `(?<n>${RE_ANY})\\s*(?:점\\s*)?씩\\s*(?<d>올려|올리|더해|추가|늘려|올라|얻|받|깎|빼|줄여)?${REST}`
    ], (g, ctx, alt, before) => {
        if (alt === 2 && !/점수|스코어|포인트/.test(before || '')) return null;
        const name = varOf(g.v);
        const n = Math.abs(toNum(g.n) || 1);
        const v = /깎|빼|줄|감소|잃/.test(g.d || '') ? -n : n;
        return {nodes: [act('data_changevariableby', {
            inputs: {VALUE: num(v)}, varName: name,
            sig: `score${v >= 0 ? '+' : ''}${fmt(v)}`, label: `${eul(name)} ${fmt(v)}만큼 바꾸기`
        })]};
    });
    def('scoreSet', 'variables', 'act', [
        `(?<v>점수|스코어|포인트|카운트)(?:를|을)?\\s*(?<n>${RE_NUM})\\s*(?:점)?\\s*(?:으로|로)?\\s*(?:정하|정해|바꿔|바꾸|만들|해|시작|초기화|설정|맞춰)${REST}`,
        `(?<v>점수|스코어|포인트|카운트)(?:를|을)?\\s*(?:초기화|리셋)${REST}`
    ], g => {
        const name = varOf(g.v);
        const n = toNum(g.n) === null ? 0 : toNum(g.n);
        return {nodes: [act('data_setvariableto', {
            inputs: {VALUE: txt(fmt(n))}, varName: name,
            sig: `score=${fmt(n)}`, label: `${eul(name)} ${euro(fmt(n))} 정하기`
        })]};
    });
    // "점수 변수 만들어서" — 변수는 쓸 때 번역기가 만든다(블록은 없음)
    // ═══ 10차: 연습 6차 실패에서 일반화한 것 ═══
    // "점수 변수 만들고 시작할때 0으로 해줘" — 변수 만들기 + 처음 값
    def('varInit', 'variables', 'act', `(?<v>[가-힣A-Za-z0-9]{1,8}?)\\s*(?:이라는\\s*|라는\\s*)?변수(?:를|을)?\\s*(?:하나\\s*)?(?:만들|만들어|만들고|추가)[가-힣]*\\s*(?:(?:게임\\s*)?(?:시작할\\s*때|시작하면|시작하자마자|처음에|처음엔|처음)\\s*)?(?:값(?:을|은)?\\s*)?(?<n>-?\\d+(?:\\.\\d+)?)\\s*(?:으로|로|에서)\\s*(?:정하|정해|해|시작|설정|만들|두|초기화|맞춰)[가-힣]*`,
        g => {
            const name = /^(?:스코어|포인트)$/.test(g.v) ? '점수' : g.v;
            return {notes: [`‘${name}’ 변수를 만들고 처음 값을 ${g.n}(으)로 정했어요.`], nodes: [setVar(name, toNum(g.n))]};
        });
    // "대답한거 말하게", "대답한 내용 따라 말해"
    def('sayAnswer2', 'looks', 'act', '대답(?:한|받은|친|들은|쓴)\\s*(?:거|것|걸|내용|말|이름)(?:을|를|도)?\\s*(?:그대로\\s*|따라\\s*|같이\\s*|함께\\s*)?(?:말|외치|따라\\s*하|보여)[가-힣]*',
        () => ({nodes: [act('looks_say', {inputs: {MESSAGE: Object.assign(txt(''), {node: act('sensing_answer', {sig: 'answer', label: '대답'})})},
            sig: 'say(대답)', label: '‘대답’ 말하기'})]}));
    // "다 지우고 펜 색깔 빨간색으로" — 펜 이야기 속의 "다 지우고"
    def('penClear2', 'pen', 'act', '(?:^|\\s)(?:화면(?:을)?\\s*)?(?:다|모두|전부|싹)\\s*(?:지우|지워)[가-힣]*', (g, ctx, alt, before) => (/펜|그리|그려|선\s|선을|도장|처음부터/.test(ctx.src || '') || !String(before || '').trim() ? {nodes: [penClear()]} : null));
    // "선 그어줘", "직선 하나 그려"
    def('penLine', 'pen', 'act', '(?:직선|선|줄)(?:을|를)?\\s*(?:하나\\s*|쭉\\s*)?(?:그어|긋|그으|그려|그리)[가-힣]*', (g, ctx) => (ctx.robot ? null : {
        notes: ['선은 펜을 내리고 움직이면 그려져요: 펜 내리기 → 100만큼 움직이기 → 펜 올리기.'],
        nodes: [penDown(), act('motion_movesteps', {inputs: {STEPS: num(100)}, sig: 'move100', label: '100만큼 움직이기'}), penUp()]}));
    // "30초 타이머", "1분 카운트다운"
    def('timerCount', 'variables', 'act', [`(?<n>\\d+)\\s*(?<u>초|분)\\s*(?:짜리\\s*|(?<up>세는|까지\\s*세는)\\s*)?(?:타이머|카운트\\s*다운|시계)(?:를|을)?(?:\\s*(?:만들|해|시작|맞춰|돌려)[가-힣]*)?`,
        `타이머(?:를|을)?\\s*(?:만들[가-힣]*\\s*)?(?<n>\\d+)\\s*(?<u>초|분)\\s*(?:(?:지나면|되면|끝나면|후에)\\s*(?<end>게임\\s*(?:끝|오버|종료)|멈춰|끝|종료)[가-힣]*)?`], g => {
        const n = toNum(g.n) * (g.u === '분' ? 60 : 1);
        if (g.end) {
            return {notes: [`‘남은 시간’을 ${n}에서 1초마다 1씩 줄이며 말하고, 0이 되면 ‘게임 끝!’이라고 말하고 모두 멈춰요.`],
                nodes: [setVar('남은 시간', n), {t: 'repeat', times: n, body: [sayOf(V('남은 시간'), null, '남은 시간'), waitS(1), chVar('남은 시간', -1)]}, sayOf('게임 끝!', 2, '게임 끝!'),
                    act('control_stop', {fields: fld('STOP_OPTION', 'all'), mutation: {tagName: 'mutation', children: [], hasnext: 'false'}, sig: 'stop:all', label: '멈추기 (모두)', cap: true})]};
        }
        if (g.up) {
            return {notes: [`‘시간’ 변수를 0에서 시작해 1초마다 1씩 올리면서 ${n}까지 말해요.`],
                nodes: [setVar('시간', 0), {t: 'repeat', times: n, body: [waitS(1), chVar('시간', 1), sayOf(V('시간'), null, '시간')]}]};
        }
        return {notes: [`‘남은 시간’ 변수를 ${n}으로 정하고, 1초마다 1씩 줄이면서 말해요. 0이 되면 ‘끝!’이라고 말해요.`],
            nodes: [setVar('남은 시간', n), {t: 'repeat', times: n, body: [sayOf(V('남은 시간'), null, '남은 시간'), waitS(1), chVar('남은 시간', -1)]}, sayOf('끝!', 2, '끝!')]};
    });
    // "마우스 x위치 따라 좌우로만 움직이게"
    def('followAxis', 'motion', 'act', [
        `${RE_MOUSE}(?:의)?\\s*(?<ax>x|y|엑스|와이)\\s*(?:좌표|위치|값)?(?:를|을|만)?\\s*(?:따라|따라서|따라가)[가-힣]*(?:\\s*(?:(?:고양이|캐릭터)(?:가|를|는)?\\s*)?(?:좌우|옆|가로|위아래|상하|세로)(?:로|으로)(?:만)?\\s*(?:움직|이동|가|다니)[가-힣]*)?`,
        `(?<lr>좌우|옆|가로|위아래|상하|세로)(?:로|으로)만\\s*${RE_MOUSE}(?:를|을)?\\s*(?:따라|따라서)[가-힣]*`
    ], g => {
        const y = g.ax ? /y|와이/i.test(g.ax) : /위아래|상하|세로/.test(g.lr);
        const r = act(y ? 'sensing_mousey' : 'sensing_mousex', {sig: `mouse${y ? 'y' : 'x'}`, label: `마우스의 ${y ? 'y' : 'x'}좌표`});
        return {notes: [`계속 반복하면서 ${y ? 'y' : 'x'} 좌표를 마우스의 ${y ? 'y' : 'x'}좌표로 정해요. 그래서 ${y ? '위아래로만' : '좌우로만'} 따라가요.`],
            nodes: [{t: 'forever', body: [act(y ? 'motion_sety' : 'motion_setx', {inputs: {[y ? 'Y' : 'X']: inN(r)}, sig: `set${y ? 'y' : 'x'}=mouse`, label: `${y ? 'y' : 'x'} 좌표를 마우스의 ${y ? 'y' : 'x'}좌표로 정하기`})]}]};
    });
    // "로봇 다시 3초" — 앞에서 말한 방향(앞/뒤)으로 다시
    def('rAgainSec', 'robot', 'act', `다시\\s*(?<n>${RE_SEC})\\s*초(?:\\s*(?:동안\\s*)?(?:더\\s*)?(?:가|앞으로|직진|달려|달리|움직)[가-힣]*)?`, (g, ctx, alt, before) => {
        if (!ctx.robot) return null;
        const b = String(before || '');
        const back = b.lastIndexOf('뒤로') > b.lastIndexOf('앞으로');
        return {notes: [`‘다시 ${g.n}초’는 앞에서 말한 대로 ${back ? '뒤로' : '앞으로'} 가는 걸로 봤어요.`], nodes: [].concat(drive(back ? 'backward' : 'forward', toNum(g.n), false), stopBoth())};
    });
    // "로봇이 청소기처럼 방 청소하게" — 부딪히면 돌고 아니면 가는 자율주행
    def('rClean', 'robot', 'act', '(?:로봇\\s*)?청소기(?:처럼|같이)?(?:\\s*(?:방|교실|바닥)(?:을)?)?(?:\\s*(?:청소|돌아다니|다니|움직)[가-힣]*)?|(?:방|교실|바닥)(?:을)?\\s*청소[가-힣]*', (g, ctx) => (!ctx.robot ? null : /피해|피하|자율/.test(ctx.src || '') ? {nodes: []} : {
        notes: ['로봇 청소기처럼, 계속 앞으로 가다가 장애물이 있으면 돌아서 다른 쪽으로 가요(자율주행과 같은 규칙).'],
        nodes: [{t: 'forever', body: [{t: 'ifelse', cond: obstacle(), body: drive('right', 0.6, false), body2: drive('forward', 0.2, false)}]}]}));
    // "고양이랑 대화하는 챗봇" — 묻고, 대답에 따라 말하기를 계속
    def('chatbot', 'sensing', 'act', '(?:대화(?:하는|할\\s*수\\s*있는)?\\s*)?(?:챗봇|채팅\\s*봇|대화\\s*(?:로봇|프로그램))(?:을|를)?(?:\\s*(?:만들|해)[가-힣]*)?|(?:고양이|캐릭터)(?:랑|와|하고)\\s*대화(?:하게|하기|할\\s*수\\s*있게)[가-힣]*', () => {
        const ans = act('sensing_answer', {sig: 'answer', label: '대답'});
        const eq = w => act('operator_equals', {inputs: {OPERAND1: inT(ans), OPERAND2: txt(w)}, sig: `answer=${w}`, label: `대답 = ${w}`});
        return {notes: ['계속 묻고, 대답이 ‘안녕’이면 인사하고, 아니면 대답을 따라 말해요. ‘만약’을 더 붙여서 말할 수 있는 걸 늘려 보세요.'],
            nodes: [{t: 'forever', body: [act('sensing_askandwait', {inputs: {QUESTION: txt('나한테 말해 봐!')}, sig: 'ask(나한테 말해 봐!)', label: '‘나한테 말해 봐!’라고 묻고 기다리기'}),
                {t: 'ifelse', cond: eq('안녕'), noLoop: true, body: [sayOf('안녕! 반가워', 2, '안녕! 반가워')], body2: [sayOf(join(ans, '(이)라고? 그렇구나!'), 2, '(대답)(이)라고? 그렇구나!')]}]}]};
    });
    // "스네이크 게임" — 방향키로 방향 바꾸고 계속 가며 꼬리(펜)를 남긴다
    def('gameSnake', 'control', 'act', '(?:스네이크|뱀)\\s*게임(?:을|를)?(?:\\s*(?:만들|해)[가-힣]*)?', () => {
        const dirKey = (k, d) => ({hat: act('event_whenkeypressed', {fields: fld('KEY_OPTION', k), sig: `key:${k}`, label: `${keyKo(k)} 키를 눌렀을 때`}),
            body: [act('motion_pointindirection', {inputs: {DIRECTION: num(d, 'math_angle')}, sig: `dir${d}`, label: `${d}도 방향 보기`})]});
        return {notes: ['뱀 게임 뼈대: 방향키로 방향만 바꾸고, 뱀은 계속 앞으로 가며 펜으로 꼬리를 그려요. 벽에 닿으면 게임 끝. 먹이·꼬리 길이는 한 문장씩 더 말해 보세요.'],
            multi: [
                {hat: act('event_whenflagclicked', {sig: 'flag', label: '깃발을 클릭했을 때'}), body: [penClear(), act('motion_gotoxy', {inputs: {X: num(0), Y: num(0)}, sig: 'goto0,0', label: 'x: 0 y: 0 으로 이동하기'}),
                    act('motion_pointindirection', {inputs: {DIRECTION: num(90, 'math_angle')}, sig: 'dir90', label: '90도 방향 보기'}), penDown(),
                    {t: 'forever', body: [act('motion_movesteps', {inputs: {STEPS: num(3)}, sig: 'move3', label: '3만큼 움직이기'}),
                        {t: 'if', cond: act('sensing_touchingobject', {inputs: {TOUCHINGOBJECTMENU: menu('sensing_touchingobjectmenu', 'TOUCHINGOBJECTMENU', '_edge_')}, sig: 'touchedge', label: '벽에 닿았는가?'}),
                            body: [sayOf('게임 끝!', 2, '게임 끝!'), act('control_stop', {fields: fld('STOP_OPTION', 'all'), sig: 'stop:all', label: '멈추기 (모두)'})]}]}]},
                dirKey('up arrow', 0), dirKey('down arrow', 180), dirKey('left arrow', -90), dirKey('right arrow', 90)
            ]};
    });
    // ═══ 23차: 연습 15차 실패에서 ═══
    const mvK = v => act('motion_movesteps', {inputs: {STEPS: num(v)}, sig: `move${fmt(v)}`, label: `${fmt(v)}만큼 움직이기`});
    const sizeK = v => act('looks_changesizeby', {inputs: {CHANGE: num(v)}, sig: `size${v >= 0 ? '+' : ''}${v}`, label: `크기를 ${v}만큼 바꾸기`});
    const ledNumK = d => act('cubroidled_lceNumberControl', {fields: fld('MATRIX', LED_NUM[d]), sig: `led:${d}`, label: `LED에 숫자 ${d} 보여주기`});
    const ledIfChainK = (name, top) => { const ifs = []; for (let v = 1; v <= top; v++) ifs.push({t: 'if', noLoop: true, cond: act('operator_equals', {inputs: {OPERAND1: inT(V(name)), OPERAND2: txt(String(v))}, sig: `${name}=${v}`, label: `${name} = ${v}`}), body: [ledNumK(v)]}); return ifs; };
    def('soundAddKo', 'sound', 'act', '(?:(?<nm>[가-힣A-Za-z]{1,6}?)\\s*)?소리(?:도|를|을|좀)?\\s*(?:하나\\s*)?(?:넣어|넣|추가|붙여|깔아|넣기)[가-힣]*', (g, ctx) => {
        const names = (ctx && ctx.soundNames) || [];
        const nm = (g.nm && names.find(x => x === g.nm || x.indexOf(g.nm) >= 0)) || names[0] || '야옹';
        return {nodes: [act('sound_play', {inputs: {SOUND_MENU: menu('sound_sounds_menu', 'SOUND_MENU', nm)}, sig: `sound(${nm})`, label: `${nm} 재생하기`})]};
    });
    // "목숨 3개 게임"
    def('livesGameKo', 'variables', 'act', '(?<v>목숨|생명|라이프|체력)(?:이|을|는)?\\s*(?<n>\\d+|세|다섯|열)\\s*(?:개|칸)?(?:짜리|인)?\\s*게임(?:을|를)?(?:\\s*(?:만들|해)[가-힣]*)?', g => {
        const n = toNum(g.n) || 3;
        const touch = act('sensing_touchingobject', {inputs: {TOUCHINGOBJECTMENU: menu('sensing_touchingobjectmenu', 'TOUCHINGOBJECTMENU', '_mouse_')}, sig: 'touchmouse', label: '마우스 포인터에 닿았는가?'});
        const notT = act('operator_not', {boolIn: {OPERAND: clone(touch)}, sig: 'not:touchmouse', label: '마우스 포인터에 닿지 않았는가?'});
        const zero = act('operator_equals', {inputs: {OPERAND1: inT(V(g.v)), OPERAND2: txt('0')}, sig: `${g.v}=0`, label: `${g.v} = 0`});
        return {notes: [`‘${g.v}’을(를) ${n}으로 시작해요. 마우스에 닿으면 하나 줄고(떨어질 때까지 기다림), 0이 되면 ‘게임 오버’를 말하고 멈춰요. 닿는 대상을 바꾸려면 ‘적에 닿으면’처럼 말해 보세요.`],
            nodes: [setVar(g.v, n), {t: 'forever', body: [{t: 'if', noLoop: true, cond: touch, body: [chVar(g.v, -1), act('control_wait_until', {boolIn: {CONDITION: notT}, sig: 'until:not:touchmouse', label: '‘마우스 포인터에 닿지 않았는가?’ 까지 기다리기'})]},
                {t: 'if', noLoop: true, cond: zero, body: [sayOf('게임 오버', 2, '게임 오버'), act('control_stop', {fields: fld('STOP_OPTION', 'all'), mutation: {tagName: 'mutation', children: [], hasnext: 'false'}, sig: 'stop', label: '멈추기 (모두)', cap: true})]}]}]};
    });
    // "타이머가 10초 넘어가면"
    def('timerOverKo', 'sensing', 'cond', '타이머(?:가|는|값이)?\\s*(?<n>\\d+(?:\\.\\d+)?)\\s*초(?:를|가)?\\s*(?:넘어가면|넘으면|지나면|되면|이상이면|보다\\s*크면)', g => ({
        cond: act('operator_gt', {inputs: {OPERAND1: inT(act('sensing_timer', {sig: 'timer', label: '타이머'})), OPERAND2: txt(fmt(toNum(g.n)))}, sig: `timer>${fmt(toNum(g.n))}`, label: `타이머 > ${fmt(toNum(g.n))}`})}));
    // "아야! 하기"
    def('sayExclKo', 'looks', 'act', '(?<m>[^\\s\'"“”‘’]{1,10}!)\\s*(?:하기|하게|해)(?=\\s*$|\\s*[.!?])', g => ({nodes: [sayOf(g.m, 2, g.m)]}));
    // "원래 자리 (0,0)으로"
    def('homeKo', 'motion', 'act', '(?:(?:원래|처음|시작|제)\\s*(?:자리|위치)(?:\\s*인)?\\s*)?\\(\\s*(?<x>-?\\d+)\\s*,\\s*(?<y>-?\\d+)\\s*\\)\\s*(?:으로|로)(?:\\s*(?:가|이동|돌아가|돌아와|옮겨)[가-힣]*)?|(?:원래|처음|시작)\\s*(?:자리|위치)(?:으로|로)(?:\\s*(?:가|이동|돌아가|돌아와|옮겨)[가-힣]*)?', (g, ctx) => (ctx.robot ? null : {nodes: [gotoXY(toNum(g.x) || 0, toNum(g.y) || 0)]}));
    // "빨간 펜으로 삼각형"
    def('penColorShapeKo', 'pen', 'act', `(?<c>${RE_COLOR})\\s*(?:색\\s*)?펜(?:으로|을\\s*써서)\\s*(?<sh>정사각형|사각형|네모|삼각형|세모|오각형|육각형|별)(?:을|를)?(?:\\s*(?:그려|그리)[가-힣]*)?`, g => {
        const S = {정사각형: [4, 100, 90], 사각형: [4, 100, 90], 네모: [4, 100, 90], 삼각형: [3, 100, 120], 세모: [3, 100, 120], 오각형: [5, 80, 72], 육각형: [6, 70, 60], 별: [5, 100, 144]}[g.sh];
        return {nodes: [penClear(), act('pen_setPenColorToColor', {inputs: {COLOR: {sh: 'colour_picker', field: 'COLOUR', value: COLOR_HEX[g.c] || '#000000'}}, sig: `pen:color${COLOR_HEX[g.c] || '#000000'}`, label: `펜 색깔을 ${g.c}색으로 정하기`}),
            penDown(), {t: 'repeat', times: S[0], body: [mvK(S[1]), turnK(true, S[2])]}, penUp()]};
    });
    // "신나는 배경음악 깔아줘"
    def('bgmKo', 'sound', 'act', '(?:신나는\\s*|잔잔한\\s*|좋은\\s*|멋진\\s*)?(?:배경\\s*음악|배경음|BGM|브금)(?:을|를|이|이라도|도)?\\s*(?:깔아|틀어|넣어|재생|나오게|틀|계속|반복)[가-힣]*|음악(?:을|를)?\\s*깔아[가-힣]*', (g, ctx) => {
        const names = (ctx && ctx.soundNames) || [];
        const nm = names[0] || '야옹';
        return {notes: [`배경음악은 소리 탭에서 고른 소리를 ‘끝까지 재생하기’로 계속 반복해요. 지금은 ‘${nm}’ 소리를 넣었어요 — 소리 탭에서 음악을 추가하면 블록에서 고를 수 있어요.`],
            nodes: [{t: 'forever', body: [act('sound_playuntildone', {inputs: {SOUND_MENU: menu('sound_sounds_menu', 'SOUND_MENU', nm)}, sig: `soundwait(${nm})`, label: `${nm} 끝까지 재생하기`})]}]};
    });
    // "앞으로 갔다 뒤로 갔다 5번 왔다갔다"
    def('backForthNKo', 'motion', 'act', `${CAR_WORD}앞으로\\s*(?:갔다(?:가)?|가고)\\s*(?:다시\\s*)?뒤로\\s*(?:갔다(?:가)?|가고|오고|왔다(?:가)?)\\s*(?:(?:이렇게|하는\\s*거)\\s*)?(?<k>\\d+|${RE_KNUM})\\s*번\\s*(?:왔다\\s*갔다|왕복|반복)[가-힣]*`, (g, ctx) => {
        const k = Math.max(1, Math.round(toNum(g.k) || 1));
        if (ctx.robot) return {notes: [NOTE_CAR], nodes: [{t: 'repeat', times: k, body: drive('forward', 1, false).concat(drive('backward', 1, false))}].concat(stopBoth())};
        return {nodes: [{t: 'repeat', times: k, body: [mvK(50), waitS(0.5), mvK(-50), waitS(0.5)]}]};
    });
    // "어두우면 LED 다 켜고 밝으면 끄기"
    def('rDarkOnBrightOff', 'robot', 'act', '(?:어두우면|어두울\\s*때|어두워지면)\\s*(?:LED|엘이디|불빛|불|조명)?(?:을|를|은|는)?\\s*(?:다\\s*|전부\\s*)?(?:켜고|켜|켜기|켜지고)[가-힣]*\\s*(?:밝으면|밝을\\s*때|밝아지면)\\s*(?:다시\\s*)?(?:끄기|꺼|끄고|꺼지게|끄)[가-힣]*', () => ({
        notes: ['파이썬 탭 예제 ‘스마트 가로등’과 같아요: 어두우면 불을 켜고, 밝으면 꺼요.', NOTE_BRIGHT],
        nodes: [{t: 'forever', body: [{t: 'ifelse', noLoop: true, cond: bright('<', 500), body: [colorOn('#ffffff')], body2: [colorOff()]}, waitN(0.1)]}]}));
    // "크기가 점점 커졌다가 다시 작아지는거 반복"
    def('sizePulseKo', 'looks', 'act', '(?:크기(?:가|를)?\\s*)?(?:점점\\s*|조금씩\\s*)?(?:커졌다가|커졌다|커지고|커졌다\\s*가)\\s*(?:다시\\s*|점점\\s*)?(?:작아지|작아졌|줄어들|줄어)[가-힣]*(?:\\s*(?:반복|계속)[가-힣]*)?', () => ({
        notes: ['5씩 10번 커졌다가 5씩 10번 작아지기를 계속 반복해요(두근두근).'],
        nodes: [{t: 'forever', body: [{t: 'repeat', times: 10, body: [sizeK(5), waitS(0.05)]}, {t: 'repeat', times: 10, body: [sizeK(-5), waitS(0.05)]}]}]}));
    // "짝수만 말하기 2 4 6 8 10"
    def('evenSayKo', 'operators', 'act', '(?<k>짝수|홀수)(?:만|를|만\\s*차례대로)?\\s*(?:말하기|말해|말하게|세기|세게|외치기)(?:\\s*(?<seq>(?:\\d+\\s*){2,}))?', g => {
        const odd = g.k === '홀수';
        const nums = g.seq ? g.seq.trim().split(/\s+/).map(Number) : null;
        const top = nums ? nums[nums.length - 1] : 10;
        const start = odd ? 1 : 2;
        const times = Math.max(1, Math.floor((top - start) / 2) + 1);
        return {notes: [`${start}부터 2씩 더하면서 ${times}번 말해요.`], nodes: [setVar('숫자', start), {t: 'repeat', times, body: [sayOf(V('숫자'), 1, '숫자'), chVar('숫자', 2)]}]};
    });
    // "리스트에 사과 바나나 포도 넣고 하나씩 말하기"
    def('listSayKo', 'variables', 'act', '(?:(?<nm>[가-힣A-Za-z]{1,6})\\s*)?리스트에\\s*(?<items>(?:[가-힣A-Za-z]+\\s+){0,7}[가-힣A-Za-z]+?)\\s*(?:을|를)?\\s*(?:넣고|추가하고|담고|넣어서)\\s*(?:하나씩\\s*|차례로\\s*|순서대로\\s*|차례대로\\s*)?(?:말|외치|보여)[가-힣]*', g => {
        const name = g.nm && !/^(?:이|그|새|내)$/.test(g.nm) ? g.nm : '목록';
        const items = g.items.trim().split(/\s+/);
        const L = (op, o) => act(op, Object.assign({listName: name}, o));
        const item = L('data_itemoflist', {inputs: {INDEX: inN(V('번째'))}, sig: `item(${name})`, label: `${name}의 (번째)번째 항목`});
        return {notes: [`‘${name}’ 리스트에 ${items.length}개를 넣고, ‘번째’를 1씩 올리며 하나씩 말해요.`],
            nodes: [L('data_deletealloflist', {sig: `list:${name}:clear`, label: `${name}의 항목을 모두 삭제하기`})].concat(
                items.map(it => L('data_addtolist', {inputs: {ITEM: txt(it)}, sig: `list:${name}+${it}`, label: `${it}을(를) ${name}에 추가하기`})),
                [setVar('번째', 1), {t: 'repeat', times: items.length, body: [sayOf(item, 1, `${name}의 항목`), chVar('번째', 1)]}])};
    });
    // "모양2로 바꿨다가 1초 뒤 모양1로"
    def('costumeSeqKo', 'looks', 'act', '모양\\s*(?<a>\\d)(?:로|으로)\\s*(?:바꿨다가|바꾸고|바꾼\\s*(?:다음|뒤)|했다가|바꾸었다가)\\s*(?:(?<n>\\d+(?:\\.\\d+)?)\\s*초\\s*(?:뒤에?|후에?|있다가|기다렸다가|지나면)\\s*)?(?:다시\\s*)?모양\\s*(?<b>\\d)(?:로|으로)?(?:\\s*(?:바꿔|바꾸|돌아|해|바뀌)[가-힣]*)?', g => ({
        nodes: [act('looks_switchcostumeto', {inputs: {COSTUME: menu('looks_costume', 'COSTUME', `모양${g.a}`)}, sig: `costume=모양${g.a}`, label: `모양을 모양${g.a}(으)로 바꾸기`}), waitS(toNum(g.n) || 1),
            act('looks_switchcostumeto', {inputs: {COSTUME: menu('looks_costume', 'COSTUME', `모양${g.b}`)}, sig: `costume=모양${g.b}`, label: `모양을 모양${g.b}(으)로 바꾸기`})]}));
    // "다시 돌아와"(로봇) — 돌아서서 갔던 만큼 되돌아오기
    def('rComeBackKo', 'robot', 'act', '(?:다시\\s*|제자리로\\s*|원래\\s*자리로\\s*)?(?:돌아와|돌아오|되돌아와|되돌아오)[가-힣]*', (g, ctx) => {
        if (!ctx.robot) return null;
        const m = /(\d+(?:\.\d+)?)\s*초/.exec(ctx.src || '');
        const sec = m ? Number(m[1]) : 1;
        return {notes: ['돌아오기는 제자리에서 180도(1.2초) 돈 뒤, 갔던 시간만큼 앞으로 가요.', NOTE_SPIN], nodes: drive('right', 1.2, false).concat(drive('forward', sec, true))};
    });
    // "게임 끝나면 모든 스크립트 멈추기" — 게임 끝 신호를 받았을 때
    def('gameOverHatKo', 'events', 'hat', '게임(?:이)?\\s*(?:끝나면|끝났을\\s*때|오버되면|오버\\s*되면|오버\\s*(?:되었|됐)을\\s*때)', g => ({
        notes: ['‘게임 끝’ 신호를 받았을 때 시작해요. 게임이 끝나는 곳에서 ‘게임 끝 신호 보내기’를 하면 돼요.'],
        hat: (nm => act('event_whenbroadcastreceived', {fields: {BROADCAST_OPTION: {name: 'BROADCAST_OPTION', value: nm, bcast: nm}}, sig: `recv:${nm}`, label: `${nm} 신호를 받았을 때`}))(/오버/.test(g[0] || '') ? '게임오버' : '게임 끝')}));
    // "버튼 누를때마다 숫자 1씩 올라가서 매트릭스에 표시"
    def('rButtonCountLedKo', 'robot', 'act', `(?:버튼|터치\\s*센서|터치)(?:을|를)?\\s*(?:누를|눌릴|할)\\s*때\\s*마다\\s*(?:숫자(?:가|를)?\\s*)?(?:1씩\\s*)?(?:올라가서|올려서|늘려서|올라가고|늘어나서|올라가면서|세서)\\s*${LED_WORD}(?:에|에다)?\\s*(?:숫자(?:를|로)?\\s*)?(?:표시|보여|띄워|나오게)[가-힣]*`, () => ({
        notes: ['버튼을 누를 때마다 ‘횟수’를 1씩 올려요. LED 숫자 블록은 숫자를 직접 골라야 해서 1~9를 ‘만약’으로 하나씩 보여줘요(9 다음은 0으로).'],
        nodes: [setVar('횟수', 0), {t: 'forever', body: [act('control_wait_until', {boolIn: {CONDITION: button()}, sig: 'until:button', label: '‘버튼이 눌러졌는가?’ 까지 기다리기'}), chVar('횟수', 1),
            {t: 'if', noLoop: true, cond: act('operator_gt', {inputs: {OPERAND1: inT(V('횟수')), OPERAND2: txt('9')}, sig: '횟수>9', label: '횟수 > 9'}), body: [setVar('횟수', 0), ledNumK(0)]}].concat(ledIfChainK('횟수', 9),
            [act('control_wait_until', {boolIn: {CONDITION: act('operator_not', {boolIn: {OPERAND: button()}, sig: 'not:button', label: '버튼이 눌러지지 않았는가?'})}, sig: 'until:not:button', label: '‘버튼이 눌러지지 않았는가?’ 까지 기다리기'})])}]}));
    // ═══ 22차: 연습 14차 실패에서 ═══
    const spd = (id, k, v) => act(`${id}_dcMotorSetSpeed`, {inputs: {SPEED: num(v)}, sig: `m${k}:speed${v}`, label: `DC 모터 ${k}번 속도를 ${v}%로 정하기`});
    const xposK = () => act('motion_xposition', {sig: 'xpos', label: 'x 좌표'});
    const yposK = () => act('motion_yposition', {sig: 'ypos', label: 'y 좌표'});
    // "로봇 딱 30도만 오른쪽으로 돌아" — 도는 시간 = 0.6초 × 각도/90
    def('rTurnDegKo', 'robot', 'act', '(?:로봇(?:이|을)?\\s*)?(?:딱\\s*)?(?:(?<n>\\d+)\\s*도\\s*(?<d>오른쪽|왼쪽)(?:으로)?|(?<d2>오른쪽|왼쪽)(?:으로)?\\s*(?<n2>\\d+)\\s*도)\\s*(?:만\\s*)?(?:돌|회전|꺾)[가-힣]*', (g, ctx) => {
        if (!ctx.robot) return null;
        const deg = toNum(g.n || g.n2);
        const dir = (g.d || g.d2) === '오른쪽' ? 'right' : 'left';
        const sec = Math.max(0.05, Math.round(0.6 * deg / 90 * 20) / 20);
        return {notes: [`90도에 0.6초로 계산해서 ${deg}도는 ${fmt(sec)}초 돌아요. 로봇마다 조금씩 달라서 돌려 보고 맞춰 보세요.`, NOTE_SPIN], nodes: drive(dir, sec, true)};
    });
    // "led 매트릭스에 하트", "LED에 놀란 얼굴" — 동사 없이
    def('ledImgBareKo', 'robot', 'act', `${LED_WORD}(?:에|에다|에다가)?\\s*(?<w>놀란|깜짝|${RE_IMG})\\s*(?:얼굴|표정|그림|모양|이모티콘)?(?:\\s*(?:뿅|짠|나오게|보이게|띄우기|표시|그리기|그려|보여|띄워)[가-힣]*)?`, g => {
        if (/놀란|깜짝/.test(g.w)) return {notes: ['LED에 놀란 얼굴 그림은 없어서 느낌표(!)를 보여줘요.'], nodes: [ledImgKo('느낌표')]};
        return {nodes: [ledImgKo(g.w)]};
    });
    // "y좌표 10 올리기"
    def('xyChangeKo', 'motion', 'act', '(?<ax>x|y|엑스|와이)\\s*(?:좌표|값)?(?:를|을)?\\s*(?<n>\\d+)\\s*(?:만큼\\s*)?(?<d>올려|올리|늘려|늘리|더해|더하|내려|내리|줄여|줄이|빼)[가-힣]*', g => {
        const v = toNum(g.n) * (/내|줄|빼/.test(g.d) ? -1 : 1);
        return {nodes: [/y|와이/i.test(g.ax) ? mvy(v) : mvx(v)]};
    });
    // "모터 속도 절반으로"
    def('rSpeedHalfKo', 'robot', 'act', '(?:모터\\s*|로봇\\s*)?속도(?:를|을)?\\s*(?:절반|반)(?:으로|만)?(?:\\s*(?:해|줄여|낮춰|만들)[가-힣]*)?', () => ({notes: ['절반 = 50%예요.'], nodes: [spd(M1, 1, 50), spd(M2, 2, 50)]}));
    // "무지개색으로 불 색깔 차례대로 바뀌게"
    def('rRainbowKo2', 'robot', 'act', ['무지개\\s*(?:색|빛)?(?:으로|로|처럼)?\\s*(?:불(?:빛)?|빛|LED|마스터)\\s*(?:색깔?)?(?:이|을|를)?\\s*(?:차례대로\\s*|계속\\s*|하나씩\\s*)?(?:바뀌|바꿔|변하|켜)[가-힣]*', '(?:(?:불(?:빛)?|빛|LED|마스터|조명)(?:이|을|를|의)?\\s*(?:색(?:깔)?(?:이|을)?\\s*)?)?빨주노초파남보(?:\\s*순서)?(?:으로|로|대로)?\\s*(?:불(?:빛)?|LED)?(?:이|을)?\\s*(?:차례대로\\s*|계속\\s*)?(?:바뀌|바꿔|변하|켜|반복)[가-힣]*'], () => ({notes: ['빨·주·노·초·파·보라 불빛을 0.3초씩 계속 바꿔요.'],
        nodes: [{t: 'forever', body: [].concat(...['#ff0000', '#ff8800', '#ffff00', '#00ff00', '#0000ff', '#ff00ff'].map(h => [colorOn(h), waitN(0.3)]))}]}));
    // "지금 x좌표가 몇인지 말하게"
    def('sayPosKo', 'sensing', 'act', '(?:지금\\s*)?(?:고양이(?:의)?\\s*)?(?<ax>x|y|엑스|와이)\\s*좌표(?:가|를|는|값)?\\s*(?:몇인지|얼마인지|몇이야|뭔지|몇)?\\s*(?:말|알려|보여)[가-힣]*', g => {
        const y = /y|와이/i.test(g.ax);
        return {nodes: [sayOf(y ? yposK() : xposK(), 2, `${y ? 'y' : 'x'} 좌표`)]};
    });
    // "구구단 2단 차례대로 말하게"
    def('timesTableKo', 'operators', 'act', '(?:구구단\\s*)?(?<n>[2-9])\\s*단(?:을|를)?\\s*(?:차례대로\\s*|순서대로\\s*)?(?:말|외|보여|알려|해)[가-힣]*|구구단(?:을|를)?\\s*(?:말|외|보여)[가-힣]*', g => {
        const n = toNum(g.n) || 2;
        const line = join(join(`${n} × `, V('곱')), join(' = ', opN('operator_multiply', n, V('곱'))));
        return {notes: [`‘곱’ 변수를 1부터 9까지 늘리면서 ${n}단을 1초씩 말해요.`],
            nodes: [setVar('곱', 1), {t: 'repeat', times: 9, body: [sayOf(line, 1, `${n} × 곱 = …`), chVar('곱', 1)]}]};
    });
    // "고양이 한칸 뒤로 보내줘 다른거 뒤에 숨게"
    def('layerBackKo', 'looks', 'act', '(?:(?<n>\\d+|한)\\s*칸\\s*뒤로\\s*(?:보내|물러나)[가-힣]*(?:\\s*다른\\s*(?:거|것|스프라이트)\\s*뒤에\\s*숨[가-힣]*)?|(?<all>맨\\s*뒤)(?:로|쪽으로)\\s*(?:보내|가)[가-힣]*|다른\\s*(?:거|것|스프라이트)\\s*뒤(?:에|로)\\s*(?:숨|가)[가-힣]*)', g => {
        if (g.all) return {nodes: [act('looks_gotofrontback', {fields: fld('FRONT_BACK', 'back'), sig: 'layer:back', label: '맨 뒤쪽으로 순서 바꾸기'})]};
        const n = toNum(g.n) || 1;
        return {notes: ['앞뒤 순서(층)를 바꾸는 블록이에요. 움직이는 게 아니에요.'], nodes: [act('looks_goforwardbackward', {fields: fld('FORWARD_BACKWARD', 'backward'), inputs: {NUM: num(n, 'math_integer')}, sig: `layer-${n}`, label: `${n}단계 뒤로 순서 바꾸기`})]};
    });
    // "화면 끝에 닿으면 반대쪽에서 나오게"
    def('wrapAroundKo', 'motion', 'act', '(?:화면\\s*)?(?:끝|가장자리|벽)(?:에)?\\s*(?:닿으면|가면|나가면)\\s*반대(?:쪽|편)(?:에서|으로)\\s*(?:나오|나타나|다시\\s*나오)[가-힣]*(?:\\s*(?:하는\\s*법|방법))?', () => ({
        notes: ['x가 240을 넘으면 -240으로, -240보다 작으면 240으로 옮겨요(팩맨처럼).'],
        nodes: [{t: 'forever', body: [mv10(),
            {t: 'if', noLoop: true, cond: act('operator_gt', {inputs: {OPERAND1: inT(xposK()), OPERAND2: txt('240')}, sig: 'x>240', label: 'x 좌표 > 240'}), body: [act('motion_setx', {inputs: {X: num(-240)}, sig: 'x=-240', label: 'x 좌표를 -240(으)로 정하기'})]},
            {t: 'if', noLoop: true, cond: act('operator_lt', {inputs: {OPERAND1: inT(xposK()), OPERAND2: txt('-240')}, sig: 'x<-240', label: 'x 좌표 < -240'}), body: [act('motion_setx', {inputs: {X: num(240)}, sig: 'x=240', label: 'x 좌표를 240(으)로 정하기'})]}]}]}));
    // "삼각형 3개 돌려가면서 겹쳐 그려봐"
    def('shapesRotKo', 'pen', 'act', '(?<sh>정사각형|사각형|네모|삼각형|세모|오각형|육각형|별)(?:을|를)?\\s*(?<n>\\d+|두|세|네|다섯|여섯)\\s*개(?:를)?\\s*(?:돌려가면서|돌리면서|돌려\\s*가며|회전하면서|빙\\s*돌면서)\\s*(?:겹쳐\\s*|겹치게\\s*)?(?:그려|그리)[가-힣]*', g => {
        const S = {정사각형: [4, 100, 90], 사각형: [4, 100, 90], 네모: [4, 100, 90], 삼각형: [3, 100, 120], 세모: [3, 100, 120], 오각형: [5, 80, 72], 육각형: [6, 70, 60], 별: [5, 100, 144]}[g.sh];
        const n = toNum(g.n) || 3;
        const rot = Math.round(360 / n / S[0]);
        return {notes: [`${g.sh}을(를) 하나 그릴 때마다 ${rot}도씩 돌려서 ${n}개를 겹쳐 그려요.`],
            nodes: [penClear(), penDown(), {t: 'repeat', times: n, body: [{t: 'repeat', times: S[0], body: [act('motion_movesteps', {inputs: {STEPS: num(S[1])}, sig: `move${S[1]}`, label: `${S[1]}만큼 움직이기`}), turnK(true, S[2])]}, turnK(true, rot)]}, penUp()]};
    });
    // "주사위 굴려서 1~6 중에 하나 말하게"
    def('diceSayKo', 'operators', 'act', '주사위(?:를)?\\s*(?:굴려서|굴리고|던져서|굴려|던져)\\s*(?:(?<a>\\d+)\\s*(?:~|부터|에서)\\s*(?<b>\\d+)\\s*(?:까지)?\\s*(?:중(?:에)?(?:서)?\\s*)?(?:하나|숫자|수|아무거나)?\\s*)?(?:말|보여|알려|나오)[가-힣]*', g => {
        const a = g.a !== undefined ? toNum(g.a) : 1;
        const b = g.b !== undefined ? toNum(g.b) : 6;
        return {nodes: [sayOf(act('operator_random', {inputs: {FROM: num(a), TO: num(b)}, sig: `random${a}-${b}`, label: `${a}부터 ${b} 사이의 난수`}), 2, `${a}~${b} 난수`)]};
    });
    // "글자가 타자치듯이 한글자씩 나오게"
    def('typewriterKo', 'looks', 'act', '(?:글자(?:가|를)?\\s*)?(?:타자\\s*(?:치듯이?|치는\\s*(?:것|거)\\s*처럼)|한\\s*글자씩)\\s*(?:한\\s*글자씩\\s*)?(?:나오|보이|말하|써지|쳐지)[가-힣]*', () => {
        const msg = '안녕하세요 반가워요';
        const letter = act('operator_letter_of', {inputs: {LETTER: inN(V('번째')), STRING: txt(msg)}, sig: 'letterof', label: `(번째)번째 글자`});
        return {notes: [`‘${msg}’를 한 글자씩 붙이며 0.2초마다 말해요. 문장은 결합하기 블록 안에서 바꾸세요.`],
            nodes: [setVar('글', ''), setVar('번째', 1), {t: 'repeat', times: msg.length, body: [setVar('글', join(V('글'), letter)), sayOf(V('글'), null, '글'), waitN(0.2), chVar('번째', 1)]}]};
    });
    // "제자리에서 한바퀴 뺑 돌기"(고양이)
    def('spinOnceKo2', 'motion', 'act', '(?:제자리(?:에서)?\\s*)?한\\s*바퀴\\s*(?:뺑|핑|휙|빙그르르?|빙)\\s*(?:돌|회전)[가-힣]*', (g, ctx) => (ctx.robot ? {notes: [NOTE_SPIN], nodes: drive('right', 2.4, true)} : {nodes: [{t: 'repeat', times: 36, body: [turnK(true, 10), waitN(0.02)]}]}));
    // "버튼 누르면 주사위처럼 LED에 랜덤 숫자"
    def('rLedRandKo', 'robot', 'act', `(?:(?<bt>버튼|터치)(?:을|를)?\\s*누르면\\s*)?(?:주사위\\s*(?:처럼|같이)\\s*)?${LED_WORD}(?:에|에다)?\\s*(?:랜덤|무작위|아무)\\s*(?:숫자|수)(?:가|를)?\\s*(?:나오|보여|띄워|표시)?[가-힣]*`, g => {
        const top = /주사위/.test(g[0]) ? 6 : 9;
        const ifs = [];
        for (let v = 1; v <= top; v++) {
            ifs.push({t: 'if', noLoop: true, cond: act('operator_equals', {inputs: {OPERAND1: inT(V('숫자')), OPERAND2: txt(String(v))}, sig: `숫자=${v}`, label: `숫자 = ${v}`}),
                body: [act('cubroidled_lceNumberControl', {fields: fld('MATRIX', LED_NUM[v]), sig: `led:${v}`, label: `LED에 숫자 ${v} 보여주기`})]});
        }
        const pick = [setVar('숫자', act('operator_random', {inputs: {FROM: num(1), TO: num(top)}, sig: `random1-${top}`, label: `1부터 ${top} 사이의 난수`}))].concat(ifs);
        const notes = ['LED 숫자 블록은 숫자를 직접 골라야 해서, 난수를 ‘숫자’ 변수에 넣고 같은 숫자 그림을 보여줘요.'];
        if (!g.bt) return {notes, nodes: pick};
        return {notes, nodes: [{t: 'forever', body: [act('control_wait_until', {boolIn: {CONDITION: button()}, sig: 'until:button', label: '‘버튼이 눌러졌는가?’ 까지 기다리기'})].concat(pick,
            [act('control_wait_until', {boolIn: {CONDITION: act('operator_not', {boolIn: {OPERAND: button()}, sig: 'not:button', label: '버튼이 눌러지지 않았는가?'})}, sig: 'until:not:button', label: '‘버튼이 눌러지지 않았는가?’ 까지 기다리기'})])}]};
    });
    // "색깔 효과 줘"
    def('colorFxGiveKo', 'looks', 'act', '(?:색깔|색)\\s*효과(?:를|을)?\\s*(?:줘|주|넣어|넣|걸어|걸)[가-힣]*', () => ({nodes: [act('looks_changeeffectby', {fields: fld('EFFECT', 'COLOR'), inputs: {CHANGE: num(25)}, sig: 'fx:COLOR+25', label: '색깔 효과를 25만큼 바꾸기'})]}));
    // "누를 때마다 1점씩"
    def('scorePerKo', 'variables', 'act', '(?<n>\\d+)\\s*점\\s*씩(?:\\s*(?:올려|올라|더해|줘|추가|올리기|받기)[가-힣]*)?', g => ({nodes: [chVar('점수', toNum(g.n))]}));
    // "스페이스는 경적 소리" — "○○ 키는 …" = 그 키를 눌렀을 때
    def('keyTopicKo', 'events', 'hat', '(?<key>스[페패]이스\\s*바?|(?:위|아래|왼|오른)쪽?\\s*(?:화살표|방향키))\\s*(?:키|버튼)?(?:는|은)(?=\\s)', g => {
        const k = keyValue(g.key) || 'space';
        return {hat: act('event_whenkeypressed', {fields: fld('KEY_OPTION', k), sig: `key:${k}`, label: `${keyKo(k)} 키를 눌렀을 때`})};
    });
    def('shapeBareKo', 'pen', 'act', '^\\s*(?:그냥\\s*|그럼\\s*)?(?<sh>정사각형|사각형|네모|삼각형|세모|오각형|육각형|별)(?:으로|로|을|를)?(?:\\s*(?:해|해줘|그려|그려줘|하나)[가-힣]*)?\\s*$', g => {
        const S = {정사각형: [4, 100, 90], 사각형: [4, 100, 90], 네모: [4, 100, 90], 삼각형: [3, 100, 120], 세모: [3, 100, 120], 오각형: [5, 80, 72], 육각형: [6, 70, 60], 별: [5, 100, 144]}[g.sh];
        return {nodes: [penClear(), penDown(), {t: 'repeat', times: S[0], body: [act('motion_movesteps', {inputs: {STEPS: num(S[1])}, sig: `move${S[1]}`, label: `${S[1]}만큼 움직이기`}), turnK(true, S[2])]}, penUp()]};
    });
    // "왼쪽 끝으로"(동사 없이)
    def('edgeBareKo', 'motion', 'act', '^\\s*(?<d>오른쪽|왼쪽|위쪽|아래쪽|맨\\s*위|맨\\s*아래)\\s*끝(?:으로|까지)\\s*(?:가|이동)?[가-힣]*\\s*$', (g, ctx) => {
        if (ctx.robot) return null;
        const side = g.d.replace(/\s+/g, '');
        const hx = /오른/.test(side) ? 220 : /왼/.test(side) ? -220 : null;
        const vy = /위/.test(side) ? 160 : /아래/.test(side) ? -160 : null;
        const X = hx !== null ? num(hx) : inN(xposK());
        const Y = vy !== null ? num(vy) : inN(yposK());
        return {nodes: [act('motion_glidesecstoxy', {inputs: {SECS: num(1), X, Y}, sig: `glide1:${side}`, label: `1초 동안 ${side} 끝으로 이동하기`})]};
    });
    // ═══ 21차: 연습 13차 실패에서 ═══
    const mv10 = () => act('motion_movesteps', {inputs: {STEPS: num(10)}, sig: 'move10', label: '10만큼 움직이기'});
    const bounceK = () => act('motion_ifonedgebounce', {sig: 'bounce', label: '벽에 닿으면 튕기기'});
    const rotLR = () => act('motion_setrotationstyle', {fields: fld('STYLE', 'left-right'), sig: 'rotstyle:left-right', label: '회전 방식을 왼쪽-오른쪽으로 정하기'});
    const turnK = (right, d) => act(right ? 'motion_turnright' : 'motion_turnleft', {inputs: {DEGREES: num(d)}, sig: `${right ? 'right' : 'left'}${d}`, label: `${right ? '오른쪽' : '왼쪽'}으로 ${d}도 돌기`});
    const answerK = () => act('sensing_answer', {sig: 'answer', label: '대답'});
    // "빨간색으로 선 긋기"
    def('penColorLine', 'pen', 'act', `(?<c>${RE_COLOR})\\s*(?:색|색깔)?\\s*(?:펜)?(?:으로|로)\\s*(?:선|줄)(?:을|을\\s*하나)?\\s*(?:긋|그어|그려|그리)[가-힣]*`, g => ({
        nodes: [act('pen_setPenColorToColor', {inputs: {COLOR: {sh: 'colour_picker', field: 'COLOUR', value: COLOR_HEX[g.c] || '#000000'}}, sig: `pen:color${COLOR_HEX[g.c] || '#000000'}`, label: `펜 색깔을 ${g.c}색으로 정하기`}),
            penDown(), act('motion_movesteps', {inputs: {STEPS: num(100)}, sig: 'move100', label: '100만큼 움직이기'}), penUp()]}));
    // "5라고 하면 정답 아니면 땡"
    def('ansOkNgKo', 'sensing', 'act', '(?<a>[^\\s?.!]{1,10}?)\\s*(?:이)?라고\\s*(?:하면|대답하면|답하면|쓰면|치면)\\s*(?<ok>[^\\s.!?]{1,10}?)\\s*(?:(?:이)?라고\\s*(?:하고|말하고)?\\s*)?(?:아니면|틀리면|다르면)\\s*(?<ng>[^\\s.!?]{1,10}?)(?:\\s*(?:(?:이)?라고\\s*)?(?:해|말해|하기|말하기)[가-힣]*)?(?=\\s*$|\\s*[.!?])', g => {
        if (/^(?:이|그|뭐|뭐라고)$/.test(g.a)) return null;
        const eq = act('operator_equals', {inputs: {OPERAND1: inT(answerK()), OPERAND2: txt(g.a)}, sig: `answer=${g.a}`, label: `대답 = ${g.a}`});
        return {notes: ['대답이 맞는지 ‘만약 ~라면 / 아니면’으로 확인해요.'], nodes: [{t: 'ifelse', noLoop: true, cond: eq, body: [sayOf(g.ok, 2, g.ok)], body2: [sayOf(g.ng, 2, g.ng)]}]};
    });
    // "점수 +1"
    def('varPlusSign', 'variables', 'act', `(?<v>점수|${VARS})(?:를|을|에)?\\s*(?<s>[+\\-])\\s*(?<n>\\d+)(?:\\s*(?:점|개))?(?:\\s*(?:해|하기|해줘|되게|씩))?`, g => {
        const n = toNum(g.n) || 1;
        return {nodes: [chVar(g.v, g.s === '-' ? -n : n)]};
    });
    // "속도 라는 변수 만들고 그만큼씩 이동하게"
    def('varMoveBy', 'variables', 'act', '(?<v>[가-힣A-Za-z]{1,6}?)\\s*(?:이라는|라는)\\s*변수(?:를|을)?\\s*(?:하나\\s*)?만들[가-힣]*\\s*(?:그\\s*값|그\\s*수|그)\\s*만큼(?:씩)?\\s*(?:계속\\s*)?(?:이동|움직|가|걸어)[가-힣]*', g => ({
        notes: [`‘${g.v}’ 변수를 10으로 정하고, 움직이기 칸에 변수를 넣었어요. 변수 값을 바꾸면 빨라지거나 느려져요.`],
        nodes: [setVar(g.v, 10), {t: 'forever', body: [act('motion_movesteps', {inputs: {STEPS: inN(V(g.v))}, sig: `move(${g.v})`, label: `(${g.v})만큼 움직이기`}), bounceK()]}]}));
    // "오른쪽으로 2초 동안 스르륵 미끄러지기"
    def('glideDirKo', 'motion', 'act', '(?<d>오른쪽|왼쪽|위|아래)(?:쪽)?(?:으로|로)\\s*(?<n>\\d+(?:\\.\\d+)?)\\s*초\\s*(?:동안|만에)?\\s*(?:스르륵|스르르|슥|쓱|부드럽게|천천히)?\\s*(?:미끄러|이동|움직|날아|가)[가-힣]*', (g, ctx) => {
        if (ctx.robot) return null;
        const s = toNum(g.n);
        const hx = /오른|왼/.test(g.d);
        const dv = /오른|위/.test(g.d) ? 200 : -200;
        const xr = act('motion_xposition', {sig: 'xpos', label: 'x 좌표'});
        const yr = act('motion_yposition', {sig: 'ypos', label: 'y 좌표'});
        const X = hx ? inN(opN('operator_add', xr, dv)) : inN(xr);
        const Y = hx ? inN(yr) : inN(opN('operator_add', yr, dv));
        return {notes: [`지금 자리에서 ${g.d}으로 200만큼, ${fmt(s)}초 동안 미끄러져요.`],
            nodes: [act('motion_glidesecstoxy', {inputs: {SECS: num(s), X, Y}, sig: `glide${fmt(s)}:${hx ? `x${dv}` : `y${dv}`}`, label: `${fmt(s)}초 동안 ${g.d}으로 200만큼 미끄러지기`})]};
    });
    // "가장자리 닿으면 뒤돌아서 반대로 걸어가"
    def('edgeReverseKo', 'motion', 'act', `${RE_EDGE}(?:에)?\\s*${RE_TOUCH}\\s*(?:뒤\\s*돌아서|뒤돌아|돌아서|방향(?:을)?\\s*바꿔서)?\\s*(?:반대(?:로|쪽으로)|거꾸로)\\s*(?:걸어|가|움직|이동|돌아)[가-힣]*(?:\\s*(?:계속\\s*)?(?:왔다\\s*갔다|왔다리\\s*갔다리|돌아다니|다니)[가-힣]*)?(?:\\s*(?:하게|해)[가-힣]*)?`, (g, ctx) => (ctx.robot || /다가\s*(?:벽|끝|가장자리|화면)/.test(ctx.src || '') ? null : {
        notes: ['계속 걸어가다가 벽에 닿으면 튕겨서 반대로 가요. 회전 방식을 왼쪽-오른쪽으로 해서 뒤집히지 않아요.'],
        nodes: [rotLR(), {t: 'forever', body: [mv10(), bounceK()]}]}));
    // "벽 만나면 오른쪽으로 돌고 다시 직진 계속"
    def('edgeTurnGoKo', 'motion', 'act', `${RE_EDGE}(?:에)?\\s*${RE_TOUCH}\\s*(?<d>오른쪽|왼쪽)(?:으로)?\\s*(?:(?<n>\\d+)\\s*도\\s*)?(?:돌고|돌아서|회전하고|꺾고)\\s*(?:다시\\s*)?(?:직진|앞으로\\s*가|계속\\s*가|걸어가|가|움직)[가-힣]*(?:\\s*계속)?`, (g, ctx) => {
        const right = g.d === '오른쪽';
        if (ctx.robot) {
            return {notes: ['앞에 뭔가 있으면 돌고, 없으면 앞으로 가기를 계속 되풀이해요.', NOTE_SPIN],
                nodes: [{t: 'forever', body: [{t: 'ifelse', cond: obstacle(), body: drive(right ? 'right' : 'left', 0.6, false), body2: drive('forward', null, false)}]}]};
        }
        const d = toNum(g.n) || 90;
        return {notes: ['계속 앞으로 가다가, 벽에 닿으면 돌아요(움직이기는 ‘만약’ 밖에 있어야 늘 걸어요).'],
            nodes: [{t: 'forever', body: [mv10(), {t: 'if', noLoop: true, cond: act('sensing_touchingobject', {inputs: {TOUCHINGOBJECTMENU: menu('sensing_touchingobjectmenu', 'TOUCHINGOBJECTMENU', '_edge_')}, sig: 'touchedge', label: '벽에 닿았는가?'}), body: [turnK(right, d), mv10()]}]}]};
    });
    // "밝으면 LED 끄고 어두우면 켜기"
    def('rBrightOffDarkOn', 'robot', 'act', '(?:밝으면|밝을\\s*때)\\s*(?:LED|엘이디|불빛|불)?(?:을|를|은|는)?\\s*(?:끄고|꺼|끄기|꺼지고)[가-힣]*\\s*(?:어두우면|어두울\\s*때|어두워지면)\\s*(?:다시\\s*)?(?:켜|켜기|켜고|켜줘|켜지게)[가-힣]*', () => ({
        notes: ['파이썬 탭 예제 ‘스마트 가로등’과 같아요: 밝으면 불을 끄고, 어두우면 켜요.', NOTE_BRIGHT],
        nodes: [{t: 'forever', body: [{t: 'ifelse', noLoop: true, cond: bright('>', 500), body: [colorOff()], body2: [colorOn('#ffffff')]}, waitN(0.1)]}]}));
    // "1초마다 불 깜빡깜빡"
    def('rBlinkEvery', 'robot', 'act', `(?<n>\\d+(?:\\.\\d+)?)\\s*초\\s*마다\\s*(?:(?<c>${RE_COLOR})\\s*(?:색)?\\s*)?(?:불|불빛|LED|엘이디)(?:이|을)?\\s*(?:깜빡\\s*깜빡|깜박\\s*깜박|깜빡|깜박)[가-힣]*`, g => {
        const s = toNum(g.n) || 1;
        const on = (g.c && COLOR_HEX[g.c]) ? colorOn(COLOR_HEX[g.c]) : colorOn('#ffffff');
        return {notes: [`켜기 ${fmt(s / 2)}초 + 끄기 ${fmt(s / 2)}초 = ${fmt(s)}초마다 한 번 깜빡여요.`], nodes: [{t: 'forever', body: [on, waitN(s / 2), colorOff(), waitN(s / 2)]}]};
    });
    // "LED 초록색으로 2초 켰다가 꺼"
    def('rLightOnFor', 'robot', 'act', `(?:(?:LED|엘이디|불빛|불)(?:을|를)?\\s*)?(?<c>${RE_COLOR})\\s*(?:색|색깔)?(?:으로|로)?\\s*(?:불(?:을)?\\s*)?(?<n>\\d+(?:\\.\\d+)?)\\s*초(?:\\s*동안)?\\s*(?:만\\s*)?(?:켰다가|켜고|켜졌다가|켜다가|켜놨다가)\\s*(?:꺼|끄|꺼져|끄기|꺼줘)[가-힣]*`, g => ({
        nodes: [colorOn(COLOR_HEX[g.c] || '#ffffff'), waitN(toNum(g.n)), colorOff()]}));
    // "뒤로 갈때 삐 삐 소리 나게 트럭처럼"
    def('rBackBeepKo', 'robot', 'act', '뒤로\\s*(?:갈\\s*때|가면|갈때|가면서|가는\\s*동안)\\s*(?:삐\\s*삐|삐삐|삑삑|삑\\s*삑|경고음|소리)[^.!?]*', () => ({
        notes: ['트럭처럼 뒤로 가는 동안 0.5초마다 삐 소리를 내요(3번, 1.5초).', NOTE_CAR],
        nodes: drive('backward', null, false).concat([{t: 'repeat', times: 3, body: [beep('경고'), waitN(0.5)]}], stopBoth())}));
    // "터치센서 누르고 있는 동안만 앞으로 가"
    def('rHoldDriveKo', 'robot', 'act', `(?:(?<bt>버튼|단추|터치\\s*센서|터치)|${RE_KEY}\\s*(?:키|화살표)?)(?:을|를|이|가)?\\s*(?:누르고\\s*있는\\s*동안|누르는\\s*동안|누르고\\s*있으면|눌려\\s*있는\\s*동안|눌려\\s*있으면)(?:만|에만)?\\s*(?:로봇(?:이|을)?\\s*)?(?<d>앞으로|뒤로|직진|후진)\\s*(?:가|달려|달리|움직|이동|해)?[가-힣]*`, (g, ctx) => {
        if (!g.bt && !ctx.robot) return null;
        let cond = button();
        if (!g.bt) {
            const k = keyValue(g.key) || 'up arrow';
            cond = act('sensing_keypressed', {inputs: {KEY_OPTION: menu('sensing_keyoptions', 'KEY_OPTION', k)}, sig: `key:${k}?`, label: `${keyKo(k)} 키를 눌렀는가?`});
        }
        return {notes: ['누르고 있는 동안에는 모터를 켜 두고, 떼면 ‘아니면’ 칸에서 멈춰요.', NOTE_CAR],
            nodes: [{t: 'forever', body: [{t: 'ifelse', noLoop: true, cond, body: drive(/뒤|후진/.test(g.d) ? 'backward' : 'forward', null, false), body2: stopBoth()}]}]};
    });
    // "매트릭스에 화살표 그려서 가는 방향 보여줘"
    def('rArrowDirKo', 'robot', 'act', '(?:매트릭스|LED|엘이디|점판)(?:에)?\\s*화살표(?:를)?\\s*(?:그려서|그리고|보여|띄워|표시)[가-힣]*\\s*(?:가는|움직이는|달리는|이동하는)\\s*방향[^.!?]*', () => ({
        notes: ['가기 전에 LED에 그 방향 화살표를 보여 줘요: 앞 → 오른쪽 → 왼쪽 → 뒤.', NOTE_CAR],
        nodes: [ledImgKo('앞 화살표')].concat(drive('forward', 1, false), [ledImgKo('오른쪽 화살표')], drive('right', 0.6, false),
            [ledImgKo('왼쪽 화살표')], drive('left', 0.6, false), [ledImgKo('뒤 화살표')], drive('backward', 1, true))}));
    // "경기 시작하면 3초 기다렸다가 출발"
    def('goStartKo', 'motion', 'act', '(?:다시\\s*)?출발(?:해|하기|하자|해줘|하게|시켜)?(?![가-힣])(?:\\s*[ㄱ!]+)?', (g, ctx) => {
        if (ctx.robot || /경기|레이스|경주|시합/.test(ctx.src || '')) return {notes: ['‘출발’은 로봇이 앞으로 가기로 했어요(멈추라고 할 때까지).', NOTE_CAR], nodes: drive('forward', null, false)};
        return {notes: ['‘출발’은 계속 걸어가기로 했어요(벽에서 튕겨요).'], nodes: [{t: 'forever', body: [mv10(), bounceK()]}]};
    });
    def('flagGameKo', 'events', 'hat', '(?:경기|게임|시합|레이스|경주)(?:가|를)?\\s*(?:시작하면|시작할\\s*때|시작되면)', () => ({hat: act('event_whenflagclicked', {sig: 'flag', label: '깃발을 클릭했을 때'})}));
    // "야옹 소리 반복해줘"
    def('soundRepeatKo', 'sound', 'act', '(?<nm>[가-힣A-Za-z]{1,6}?)\\s*소리(?:를|을)?\\s*(?:계속\\s*)?(?:반복|무한\\s*반복)(?:해|해줘|하기|하게|재생|해서)?[가-힣]*', (g, ctx) => {
        const names = (ctx && ctx.soundNames) || [];
        const nm = names.find(x => x === g.nm || x.indexOf(g.nm) >= 0) || names[0] || '야옹';
        return {notes: ['‘끝까지 재생하기’를 무한 반복해서 계속 나와요. 멈추려면 빨간 정지 버튼을 누르세요.'], nodes: [{t: 'forever', body: [act('sound_playuntildone', {inputs: {SOUND_MENU: menu('sound_sounds_menu', 'SOUND_MENU', nm)}, sig: `soundwait(${nm})`, label: `${nm} 끝까지 재생하기`})]}]};
    });
    // "퀴즈 만들어줘 수도가 어디게" — 계산 문제가 아닌 한 문제 퀴즈
    const CAPITAL = [[/일본/, '도쿄'], [/중국/, '베이징'], [/미국/, '워싱턴'], [/프랑스/, '파리'], [/영국/, '런던'], [/베트남/, '하노이'], [/독일/, '베를린']];
    def('quizFactKo', 'sensing', 'act', '퀴즈(?:를|을)?\\s*(?:하나\\s*)?(?:만들어\\s*줘|만들어|만들기|내\\s*줘|내줘|내)?\\s*(?<q>[가-힣A-Za-z0-9 ]{2,20}?(?:어디게|뭐게|누구게|몇\\s*개게|어디야|뭐야|뭘까|어디일까|누굴까))', g => {
        const q = g.q.trim();
        let ans = null;
        if (/수도/.test(q)) { const hit = CAPITAL.find(x => x[0].test(q)); ans = hit ? hit[1] : '서울'; }
        const a = ans || '정답';
        const eq = act('operator_equals', {inputs: {OPERAND1: inT(answerK()), OPERAND2: txt(a)}, sig: `answer=${a}`, label: `대답 = ${a}`});
        return {notes: [ans ? `정답을 ‘${ans}’(으)로 했어요. 다른 답이면 ‘정답은 ○○야’라고 말해 주세요.` : '정답을 몰라서 ‘정답’이라고 넣어 두었어요. ‘정답은 ○○야’라고 말해 주세요.'],
            nodes: [act('sensing_askandwait', {inputs: {QUESTION: txt(`${q}?`)}, sig: 'ask(q)', label: `‘${q}?’라고 묻고 기다리기`}),
                {t: 'ifelse', noLoop: true, cond: eq, body: [sayOf('정답!', 2, '정답!')], body2: [sayOf('틀렸어요', 2, '틀렸어요')]}]};
    });
    // "최대속도로 직진"
    def('straightKo', 'motion', 'act', '(?:다시\\s*)?직진(?:해|하기|하자|해줘|하게|으로\\s*가)?', (g, ctx) => {
        // 장애물 피하기의 "다시 직진" — 아니면 칸이 이미 앞으로 간다
        if (/장애물|피해|피하|앞에\s*뭐/.test(ctx.src || '')) return null;
        if (ctx.robot || /속도|모터|바퀴/.test(ctx.src || '')) return {notes: [NOTE_CAR], nodes: drive('forward', 1, true)};
        return {nodes: [mv10()]};
    });
    // "스모 로봇처럼 상대 찾아서 밀어내게"
    def('rSumoKo', 'robot', 'act', '(?:스모|씨름)(?:\\s*로봇)?[^.!?]*', () => ({
        notes: ['근접 센서로 상대를 찾아요: 앞에 있으면 힘껏 밀고, 없으면 제자리에서 돌며 찾아요.', NOTE_SPIN],
        nodes: [{t: 'forever', body: [{t: 'ifelse', noLoop: true, cond: obstacle(), body: drive('forward', null, false), body2: drive('right', null, false)}]}]}));
    // "로봇 춤추게 해줘 앞뒤로 흔들흔들"
    def('rDanceFwdBackKo', 'robot', 'act', '(?:(?:로봇(?:이|을)?\\s*)?춤(?:을)?\\s*(?:추게|춰|추기|추)[가-힣]*(?:\\s*해\\s*줘|\\s*해\\s*주세요|\\s*해)?\\s*)?앞\\s*뒤로\\s*(?:흔들\\s*흔들|흔들|왔다\\s*갔다|왔다갔다)[가-힣]*', (g, ctx) => (ctx.robot ? {
        notes: ['앞으로 0.3초, 뒤로 0.3초를 4번 되풀이해서 흔들어요.', NOTE_CAR],
        nodes: [{t: 'repeat', times: 4, body: drive('forward', 0.3, false).concat(drive('backward', 0.3, false))}].concat(stopBoth())} : null));
    // "주사위 만들어줘 1부터 6까지 랜덤"
    def('diceMakeKo', 'operators', 'multi', '주사위(?:를|을)?\\s*(?:하나\\s*)?(?:만들|만들어)[가-힣]*(?:\\s*(?<a>\\d+)\\s*(?:부터|에서)\\s*(?<b>\\d+)\\s*까지(?:\\s*(?:랜덤|무작위|난수)(?:으로|로)?)?)?', g => {
        const a = g.a !== undefined ? toNum(g.a) : 1;
        const b = g.b !== undefined ? toNum(g.b) : 6;
        const node = act('operator_random', {inputs: {FROM: num(a), TO: num(b)}, sig: `random${a}-${b}`, label: `${a}부터 ${b} 사이의 난수`});
        return {notes: ['고양이를 클릭할 때마다 주사위를 굴려서 나온 수를 말해요.'], multi: [{hat: act('event_whenthisspriteclicked', {sig: 'click', label: '이 스프라이트를 클릭했을 때'}), body: [sayOf(node, 2, `${a}~${b} 난수`)]}]};
    });
    // "마우스 누르고 움직이면 그림 그려지게"
    def('penMouseMoveKo', 'pen', 'act', '마우스(?:를)?\\s*(?:누르고|클릭하고|누른\\s*채로?)\\s*(?:움직이면|끌면|드래그하면|움직일\\s*때)\\s*(?:그림|선|펜)(?:이|을)?\\s*(?:그려|그리|그어)[가-힣]*', () => ({notes: ['마우스를 따라가면서, 누르고 있으면 펜 내리기·떼면 펜 올리기를 계속 확인해요.'],
        nodes: [{t: 'forever', body: [act('motion_goto', {inputs: {TO: menu('motion_goto_menu', 'TO', '_mouse_')}, sig: 'gotomouse', label: '마우스 포인터로 이동하기'}),
            {t: 'ifelse', noLoop: true, cond: act('sensing_mousedown', {sig: 'mousedown', label: '마우스를 클릭했는가?'}), body: [penDownN()], body2: [penUpN()]}]}]}));
    // "배경 바뀌면 …" — 배경이 바뀌었을 때(이벤트)
    def('backdropHatKo', 'events', 'hat', '배경(?:이)?\\s*(?:바뀌면|바뀌었을\\s*때|바뀔\\s*때)(?:마다)?\\s*(?:고양이(?:가|는)?\\s*)?', (g, ctx) => {
        const names = (ctx && ctx.backdropNames) || [];
        const nm = names[1] || names[0] || '배경1';
        return {notes: [`‘배경이 ${nm}(으)로 바뀌었을 때’로 시작해요. 다른 배경이면 블록의 배경 이름을 골라 주세요.`],
            hat: act('event_whenbackdropswitchesto', {fields: fld('BACKDROP', nm), sig: `backdrop-hat:${nm}`, label: `배경이 ${nm}(으)로 바뀌었을 때`})};
    });
    // "복제된 애들이 랜덤한 곳으로 가게"
    def('cloneRandomKo', 'control', 'multi', '(?:복제된|복제본|복사된|분신)\\s*(?:애들|것들|녀석들|고양이들?|애|거)?(?:이|은|는|들이|들은)?\\s*(?:각자|모두|다|알아서)?\\s*(?:랜덤한|무작위|아무|랜덤)\\s*(?:곳|위치|자리|데)(?:으로|로|에|나)?\\s*(?:가|이동|움직|나타나|흩어)[가-힣]*', () => ({
        notes: ['‘복제되었을 때’ 스크립트에 넣어야 복제본이 움직여요(원본 말고).'],
        multi: [{hat: act('control_start_as_clone', {sig: 'clonestart', label: '복제되었을 때'}), body: [act('motion_goto', {inputs: {TO: menu('motion_goto_menu', 'TO', '_random_')}, sig: 'gotorandom', label: '무작위 위치로 이동하기'})]}]}));
    // ═══ 20차: 연습 12차 실패에서 ═══
    def('rSpinBingle', 'robot', 'act', '(?:로봇(?:이|을)?\\s*)?(?:빙글빙글|뱅글뱅글|빙빙)\\s*(?:계속\\s*)?(?:돌아|돌려|돌기|돌게|회전)[가-힣]*', (g, ctx) => (ctx.robot ? {notes: ['두 모터를 같은 방향으로 켜 두면 멈출 때까지 제자리에서 계속 돌아요. 멈추려면 빨간 정지 버튼을 누르세요.', NOTE_SPIN], nodes: drive('right', null, false)} : null));
    def('rDriveWithLight', 'robot', 'act', `(?:로봇(?:이)?\\s*)?앞으로\\s*가면서\\s*(?<c>${RE_COLOR})\\s*(?:색\\s*)?불(?:빛)?(?:을)?(?:\\s*(?:켜|켜고|켜줘|켜기|나오게))?(?![^.!?]*(?:멈추면|멈출\\s*때|서면))`, g => {
        const on = COLOR_HEX[g.c] ? colorOn(COLOR_HEX[g.c]) : null;
        return {notes: ['불을 먼저 켜고 앞으로 가요(가는 동안 켜져 있어요).'], nodes: (on ? [on] : []).concat(drive('forward', 1, true))};
    });
    const ledImgKo = w => { const hit = LED_IMG.find(x => x[0].test(w)) || LED_IMG[0]; return act('cubroidled_lceImgControl', {fields: fld('MATRIX', hit[2]), sig: `led:${hit[1]}`, label: `LED에 ‘${hit[1]}’ 그림 보여주기`}); };
    const colorOnKo = c => (COLOR_HEX[c] ? colorOn(COLOR_HEX[c]) : null);
    def('quizOkNg', 'looks', 'act', '^\\s*(?:맞으면|맞추면|정답이면)\\s*[^\\s]{1,10}\\s*(?:이?라고\\s*(?:하고)?)?\\s*(?:틀리면|아니면)\\s*[^\\s]{1,10}(?:\\s*(?:이?라고)?\\s*(?:해|말해)[가-힣]*)?', (g, ctx) => (/퀴즈|문제|더하기|빼기|곱하기|\+|\?/.test(ctx.src || '') ? {nodes: []} : null));
    // "오른쪽으로 쭉 걸어가다가 끝에 가면 다시 돌아오게"
    def('walkBounceKo', 'motion', 'act', '(?:(?<d>오른쪽|왼쪽)(?:으로)?\\s*)?(?:쭉\\s*)?(?:걸어가|가|움직이|걷)다가\\s*(?:벽|끝|가장자리)(?:에)?\\s*(?:가면|닿으면|도착하면)\\s*(?:다시\\s*)?(?:돌아오|돌아와|튕기|되돌아|돌아)[가-힣]*', (g, ctx) => (ctx.robot ? null : {notes: ['끝에 닿으면 튕겨서 돌아와요(왼쪽-오른쪽 회전 방식이라 뒤집히지 않아요).'],
        nodes: [act('motion_setrotationstyle', {fields: fld('STYLE', 'left-right'), sig: 'rotstyle:left-right', label: '회전 방식을 왼쪽-오른쪽으로 정하기'}), act('motion_pointindirection', {inputs: {DIRECTION: num(g.d === '왼쪽' ? -90 : 90, 'math_angle')}, sig: `dir${g.d === '왼쪽' ? -90 : 90}`, label: `${g.d === '왼쪽' ? -90 : 90}도 방향 보기`}),
            {t: 'forever', body: [act('motion_movesteps', {inputs: {STEPS: num(10)}, sig: 'move10', label: '10만큼 움직이기'}), act('looks_nextcostume', {sig: 'nextcostume', label: '다음 모양으로 바꾸기'}), waitS(0.1), act('motion_ifonedgebounce', {sig: 'bounce', label: '벽에 닿으면 튕기기'})]}]}));
    // "키보드로 고양이 상하좌우 움직이게"
    def('arrowsKo2', 'events', 'multi', '(?:키보드|방향키|화살표(?:\\s*키)?)(?:로|를\\s*눌러서)?\\s*(?:고양이(?:를|가)?\\s*)?(?:상하좌우|위아래\\s*(?:양옆|좌우)|사방으로|앞뒤좌우)(?:로)?\\s*(?:움직|이동|다니)[가-힣]*', (g, ctx) => (ctx.robot ? null : {notes: ['방향키마다 스크립트를 하나씩, 모두 네 개 만들었어요.'], multi: [
        K('up arrow', [act('motion_changeyby', {inputs: {DY: num(10)}, sig: 'y+10', label: 'y 좌표를 10만큼 바꾸기'})]), K('down arrow', [act('motion_changeyby', {inputs: {DY: num(-10)}, sig: 'y-10', label: 'y 좌표를 -10만큼 바꾸기'})]),
        K('left arrow', [act('motion_changexby', {inputs: {DX: num(-10)}, sig: 'x-10', label: 'x 좌표를 -10만큼 바꾸기'})]), K('right arrow', [act('motion_changexby', {inputs: {DX: num(10)}, sig: 'x+10', label: 'x 좌표를 10만큼 바꾸기'})])]}));
    // "마우스 쪽 계속 쳐다보게"
    def('lookMouse', 'motion', 'act', '마우스(?:\\s*포인터)?\\s*(?:쪽|방향)(?:을|으로)?\\s*(?:계속\\s*)?(?:쳐다보|바라보|보|향하)[가-힣]*', () => ({nodes: [{t: 'forever', body: [act('motion_pointtowards', {inputs: {TOWARDS: menu('motion_pointtowards_menu', 'TOWARDS', '_mouse_')}, sig: 'towards:mouse', label: '마우스 포인터 쪽 보기'})]}]}));
    // "무지개색 나선 그리기"
    def('spiralRainbow', 'pen', 'act', '(?:무지개\\s*(?:색|빛)?(?:으로)?\\s*)?(?:나선|소용돌이|달팽이)(?:형|\\s*모양)?(?:으로|을|를)?(?:\\s*(?:뱅글\\s*뱅글|빙글\\s*빙글|빙빙))?(?:\\s*그림(?:을)?)?\\s*(?:그리|그려)[가-힣]*', (g, ctx) => {
        const rb = /무지개/.test(ctx.src || '');
        return {notes: ['선을 조금씩 길게 하면서 돌면 나선이 돼요.' + (rb ? ' 한 번 움직일 때마다 펜 색깔을 바꿔 무지개처럼 그려요.' : '')],
            nodes: [act('pen_clear', {sig: 'pen:clear', label: '모두 지우기'}), act('motion_gotoxy', {inputs: {X: num(0), Y: num(0)}, sig: 'goto0,0', label: 'x: 0 y: 0 으로 이동하기'}), setVar('길이', 2), penDownN(), {t: 'repeat', times: 60, body: [
                act('motion_movesteps', {inputs: {STEPS: inN(V('길이'))}, sig: 'move(길이)', label: '(길이)만큼 움직이기'}), act('motion_turnright', {inputs: {DEGREES: num(20)}, sig: 'right20', label: '오른쪽으로 20도 돌기'}), chVar('길이', 1)].concat(rb ? [act('pen_changePenColorParamBy', {inputs: {COLOR_PARAM: menu('pen_menu_colorParam', 'colorParam', 'color'), VALUE: num(5)}, sig: 'pen:color+5', label: '펜 색깔을 5만큼 바꾸기'})] : [])}, penUpN()]};
    });
    // "스페이스 누르면 복제되게"
    def('cloneActKo', 'control', 'act', '(?:나\\s*자신|자기\\s*자신|고양이)?(?:를|이|가)?\\s*복제(?:되게|돼|되도록|하게|해줘|해|하기|됨)(?![가-힣]*본)', () => ({nodes: [act('control_create_clone_of', {inputs: {CLONE_OPTION: menu('control_create_clone_of_menu', 'CLONE_OPTION', '_myself_')}, sig: 'clone', label: '나 자신 복제하기'})]}));
    // "복제본은 아래로 떨어지다가 바닥 닿으면 삭제"
    def('cloneFallDel', 'control', 'multi', '복제본(?:은|이|들은)?\\s*(?:아래로\\s*)?(?:떨어지|내려가|내려오)(?:다가|고|면서)\\s*(?:바닥|아래\\s*끝|밑|땅)(?:에)?\\s*(?:닿으면|가면)\\s*(?:삭제|지워|사라지|없어지|없애)[가-힣]*', () => ({notes: ['복제본마다 아래로 5씩 떨어지다가 바닥(y < -170)에 닿으면 스스로 지워져요.'],
        multi: [{hat: act('control_start_as_clone', {sig: 'clonestart', label: '복제되었을 때'}), body: [{t: 'until', cond: act('operator_lt', {inputs: {OPERAND1: inT(act('motion_yposition', {sig: 'ypos', label: 'y 좌표'})), OPERAND2: txt('-170')}, sig: 'y<-170', label: 'y 좌표 < -170'}), body: [act('motion_changeyby', {inputs: {DY: num(-5)}, sig: 'y-5', label: 'y 좌표를 -5만큼 바꾸기'})]},
            act('control_delete_this_clone', {sig: 'clonedelete', label: '이 복제본 삭제하기', cap: true})]}]}));
    // "고양이 좀 작게 50%"
    def('sizePctKo', 'looks', 'act', '(?:크기(?:를|는)?\\s*)?(?:좀\\s*)?(?:작게|크게|줄여|키워)?\\s*(?<n>\\d+)\\s*(?:%|퍼센트|프로)(?:로|으로)?(?:\\s*(?:작게|크게|줄여|키워|만들|해|바꿔)[가-힣]*)?', (g, ctx) => (/크기|작게|크게|줄여|키워|커|작아/.test(ctx.src || '') && !/속도|음량|볼륨|소리|모터/.test(ctx.src || '') ? {nodes: [act('looks_setsizeto', {inputs: {SIZE: num(toNum(g.n))}, sig: `size=${g.n}`, label: `크기를 ${g.n} %로 정하기`})]} : null));
    // "펜으로 집 그려줘 지붕도"
    def('penHouse', 'pen', 'act', '(?:펜으로\\s*)?집(?:을)?\\s*(?:모양\\s*)?(?:그려|그리)[가-힣]*(?:\\s*지붕(?:도|까지)?)?', () => ({notes: ['네모(벽)를 그리고, 위에 세모(지붕)를 얹었어요.'],
        nodes: [act('pen_clear', {sig: 'pen:clear', label: '모두 지우기'}), act('motion_gotoxy', {inputs: {X: num(-50), Y: num(-80)}, sig: 'goto-50,-80', label: 'x: -50 y: -80 으로 이동하기'}), act('motion_pointindirection', {inputs: {DIRECTION: num(90, 'math_angle')}, sig: 'dir90', label: '90도 방향 보기'}), penDownN(),
            {t: 'repeat', times: 4, body: [act('motion_movesteps', {inputs: {STEPS: num(100)}, sig: 'move100', label: '100만큼 움직이기'}), act('motion_turnleft', {inputs: {DEGREES: num(90)}, sig: 'left90', label: '왼쪽으로 90도 돌기'})]},
            act('motion_turnleft', {inputs: {DEGREES: num(90)}, sig: 'left90', label: '왼쪽으로 90도 돌기'}), act('motion_movesteps', {inputs: {STEPS: num(100)}, sig: 'move100', label: '100만큼 움직이기'}), act('motion_turnright', {inputs: {DEGREES: num(30)}, sig: 'right30', label: '오른쪽으로 30도 돌기'}),
            act('motion_movesteps', {inputs: {STEPS: num(100)}, sig: 'move100', label: '100만큼 움직이기'}), act('motion_turnright', {inputs: {DEGREES: num(120)}, sig: 'right120', label: '오른쪽으로 120도 돌기'}), act('motion_movesteps', {inputs: {STEPS: num(100)}, sig: 'move100', label: '100만큼 움직이기'}), penUpN()]}));
    // "고양이 두마리가 서로 대화하게"
    def('twoCatsTalk', 'looks', 'act', '(?:고양이\\s*)?(?:두\\s*마리|2\\s*마리|둘)(?:가|이)?\\s*(?:서로\\s*)?(?:대화|이야기|얘기|말)(?:를)?\\s*(?:하게|해|주고받)[가-힣]*', () => ({notes: ['이 고양이는 “안녕!”이라고 말하고 ‘대답해’ 신호를 보내요. 스프라이트 목록에서 고양이를 오른쪽 클릭 → 복사한 뒤, 둘째 고양이에 “대답해 신호 받으면 반가워 라고 말해”라고 말해 보세요.'],
        nodes: [sayOf('안녕!', 2, '안녕!'), act('event_broadcast', {inputs: {BROADCAST_INPUT: {sh: 'event_broadcast_menu', field: 'BROADCAST_OPTION', value: '대답해', bcast: '대답해'}}, sig: 'bcast:대답해', label: '대답해 신호 보내기'})]}));
    // "모양2로 바꾸고 0.5초 기다렸다가 모양1"
    def('costumeBareKo', 'looks', 'act', '(?:다시\\s*)?모양\\s*(?<n>\\d)(?:로|으로)?(?=\\s*$|\\s*[,.!])', (g, ctx) => ((ctx.src || '').match(/모양\s*\d/g) || []).length >= 2 ? {nodes: [act('looks_switchcostumeto', {inputs: {COSTUME: menu('looks_costume', 'COSTUME', `모양${g.n}`)}, sig: `costume=모양${g.n}`, label: `모양을 모양${g.n}로 바꾸기`})]} : null);
    // "야옹 소리 배경음악처럼 계속 틀어줘"
    def('soundLoopKo', 'sound', 'act', '(?:야옹|소리|음악|노래)(?:\\s*소리)?(?:를|을)?\\s*(?:배경\\s*음악(?:처럼|으로)|배경음처럼|계속|무한으로|반복해서)\\s*(?:계속\\s*)?(?:틀어|재생|나오게|들리게|울리게|깔아)[가-힣]*', (g, ctx) => {
        const nm = ((ctx && ctx.soundNames) || ['야옹'])[0] || '야옹';
        return {notes: ['‘끝까지 재생하기’를 무한 반복해서 배경음악처럼 계속 나와요.'], nodes: [{t: 'forever', body: [act('sound_playuntildone', {inputs: {SOUND_MENU: menu('sound_sounds_menu', 'SOUND_MENU', nm)}, sig: `soundwait(${nm})`, label: `${nm} 끝까지 재생하기`})]}]};
    });
    // "고양이 좌표를 계속 말하게"
    def('sayXY', 'sensing', 'act', '(?:고양이(?:의)?\\s*)?(?:위치|좌표)(?:를|을)?\\s*(?:계속\\s*)?(?:말|알려|보여)[가-힣]*', (g, ctx) => (/뭐|어떤|무슨|\?/.test(ctx.src || '') ? null : {nodes: [sayOf(join(join(act('motion_xposition', {sig: 'xpos', label: 'x 좌표'}), ', '), act('motion_yposition', {sig: 'ypos', label: 'y 좌표'})), null, 'x, y')]}));
    // "뒤로 조금 갔다가"
    def('backLittle', 'motion', 'act', '뒤로\\s*(?:조금|살짝|약간|좀)\\s*(?:갔다가|가고|가서|물러났다가|물러나서|와서)', (g, ctx) => (ctx.robot ? {nodes: drive('backward', 0.3, true)} : {nodes: [act('motion_movesteps', {inputs: {STEPS: num(-20)}, sig: 'move-20', label: '-20만큼 움직이기'})]}));
    // "led 화면에 A 글자"
    def('ledLetterScreen', 'robot', 'act', '(?:LED|엘이디|led|매트릭스)\\s*(?:화면|판)?(?:에|에다)?\\s*(?:영어\\s*)?(?<a>[A-Za-z])\\s*(?:글자|자|문자)?(?:\\s*(?:보여|띄워|나오게|표시|써|출력)[가-힣]*)?(?=\\s*$|\\s*[,.!]|\\s+(?:그리고|다음))', g => ({nodes: [act('cubroidled_lceAlpabetControl', {fields: fld('MATRIX', LED_ABC[g.a.toUpperCase()]), sig: `led:${g.a.toUpperCase()}`, label: `LED에 글자 ${g.a.toUpperCase()} 보여주기`})]}));
    // "3 2 1 카운트다운 LED에 보여주고 출발"
    def('ledCountSeq', 'robot', 'act', '(?<seq>\\d(?:\\s*,?\\s*\\d){1,8})\\s*(?:카운트\\s*다운|세기)(?:을|를)?\\s*(?:LED|엘이디|매트릭스)(?:에|로)\\s*(?:보여주고|띄우고|하고|보여|띄워)[가-힣]*', g => {
        const nodes = [];
        g.seq.match(/\d/g).forEach(d => nodes.push(act('cubroidled_lceNumberControl', {fields: fld('MATRIX', LED_NUM[d]), sig: `led:${d}`, label: `LED에 숫자 ${d} 보여주기`}), waitN(1)));
        return {nodes};
    });
    // "버튼 누를때마다 LED 숫자 1씩 올라가게"
    def('rCountLed2', 'robot', 'act', '버튼(?:을)?\\s*누를\\s*때마다\\s*(?:LED|엘이디|매트릭스)\\s*(?:의\\s*|에\\s*)?숫자(?:가)?\\s*(?:1씩|하나씩)\\s*(?:올라|늘어|커지)[가-힣]*', () => {
        const shows = [];
        for (let d = 0; d <= 9; d++) shows.push({t: 'if', noLoop: true, cond: act('operator_equals', {inputs: {OPERAND1: inT(V('횟수')), OPERAND2: txt(String(d))}, sig: `횟수=${d}`, label: `횟수 = ${d}`}), body: [act('cubroidled_lceNumberControl', {fields: fld('MATRIX', LED_NUM[d]), sig: `led:${d}`, label: `LED에 숫자 ${d} 보여주기`})]});
        return {notes: ['LED 판은 숫자 0~9 그림만 있어서, 횟수가 몇인지 확인해 그 숫자 그림을 보여줘요(9 다음은 0부터).'],
            nodes: [setVar('횟수', 0), {t: 'forever', body: [act('control_wait_until', {boolIn: {CONDITION: button()}, sig: 'until:button', label: '‘버튼이 눌러졌는가?’ 까지 기다리기'}),
                chVar('횟수', 1), {t: 'if', noLoop: true, cond: act('operator_gt', {inputs: {OPERAND1: inT(V('횟수')), OPERAND2: txt('9')}, sig: '횟수>9', label: '횟수 > 9'}), body: [setVar('횟수', 0)]}].concat(shows, [
                act('control_wait_until', {boolIn: {CONDITION: act('operator_not', {boolIn: {OPERAND: button()}, sig: 'not:button', label: '버튼이 눌러지지 않았는가?'})}, sig: 'until:not:button', label: '‘버튼이 눌러지지 않았는가?’ 까지 기다리기'})])}]};
    });
    // "삑삑 두번 울려줘"
    def('rBeepN', 'robot', 'act', '(?:삑+|삐+)(?:\\s*소리)?(?:를|을)?\\s*(?:(?<n>\\d+|한|두|세|네|다섯)\\s*번\\s*)?(?:울려|내|나게|소리|울리)[가-힣]*', g => {
        const n = toNum(g.n) || KNUM[g.n] || 1;
        return {nodes: n > 1 ? [{t: 'repeat', times: n, body: [beep('경고'), waitN(0.2)]}] : [beep('경고')]};
    });
    // "어두우면 LED에 달 밝으면 해"
    def('rDarkBrightLed', 'robot', 'act', '어두(?:우면|워지면)\\s*(?:LED|엘이디)?(?:에|에는)?\\s*(?<a>달|해|별|하트|웃는\\s*얼굴)[가-힣]*\\s*,?\\s*(?:그리고\\s*)?밝(?:으면|아지면)\\s*(?:LED|엘이디)?(?:에|에는)?\\s*(?<b>해|달|별|하트|웃는\\s*얼굴)[가-힣]*', g => ({notes: [NOTE_BRIGHT],
        nodes: [{t: 'forever', body: [{t: 'ifelse', cond: bright('<', 500), body: [ledImgKo(g.a)], body2: [ledImgKo(g.b)]}, waitN(0.2)]}]}));
    // "초록불 5번 깜빡깜빡"
    def('rBlinkN', 'robot', 'act', `(?<c>${RE_COLOR})\\s*(?:색\\s*)?(?:불|불빛)?\\s*(?<n>\\d+|두|세|네|다섯)\\s*번\\s*(?:깜빡|반짝)[가-힣]*`, g => {
        const n = toNum(g.n) || KNUM[g.n] || 3;
        const on = colorOnKo(g.c);
        return on ? {nodes: [{t: 'repeat', times: n, body: [on, waitN(0.5), colorOff(), waitN(0.5)]}]} : null;
    });
    // "로봇 앞으로 가면서 빨간불 켜고 멈추면 불 꺼"
    def('rDriveLight', 'robot', 'act', `(?:로봇(?:이)?\\s*)?앞으로\\s*가면서\\s*(?<c>${RE_COLOR})\\s*(?:색\\s*)?불(?:빛)?(?:을)?\\s*(?:켜고|켜)[^.!?]*?(?:멈추면|멈출\\s*때|서면|멈춘\\s*다음에?)\\s*(?:불(?:빛)?(?:을)?\\s*)?(?:꺼|끄|끄기)[가-힣]*`, g => {
        const on = colorOnKo(g.c);
        return {notes: ['불을 켜고 1초 앞으로 간 다음, 멈추면 불을 꺼요.'], nodes: (on ? [on] : []).concat(drive('forward', 1, true), [colorOff()])};
    });
    def('askReply', 'looks', 'act', ['(?:물어보고|묻고|물어봐서)\\s*(?:그\\s*)?대답(?:을|한\\s*거)?\\s*(?:말하기|하기|해|하게|말해|따라\\s*하기)[가-힣]*', '(?:^|\\s)(?:그\\s*)?대답(?:을)?\\s*(?:말하기|하기|해줘|하게|말해)(?=\\s*$)'], (g, ctx, alt) => (alt === 1 && !/물어|묻/.test(ctx.src || '') ? null : ({nodes: [sayOf(act('sensing_answer', {sig: 'answer', label: '대답'}), null, '대답')]})));
    // "복제본 다섯개 만들어줘", "복제본 만들어서 여기저기 흩어지게"
    def('cloneMake', 'control', 'multi', `복제본?(?:을|를)?\\s*(?:(?<n>\\d+|${RE_KNUM})\\s*(?:개|마리)\\s*)?(?:만들|만들어|생기)[가-힣]*(?:\\s*(?:여기저기|아무\\s*데나|무작위로|랜덤으로|사방으로)?\\s*(?:흩어지|퍼지|이동|보내)[가-힣]*)?`, g => {
        const n = Math.min(50, Math.round(toNum(g.n) || 5));
        return {notes: ['복제본은 원래 자리에 겹쳐 생겨서, ‘복제되었을 때’ 무작위 위치로 가게 했어요.'], multi: [
            {hat: act('event_whenflagclicked', {sig: 'flag', label: '깃발을 클릭했을 때'}), body: [{t: 'repeat', times: n, body: [act('control_create_clone_of', {inputs: {CLONE_OPTION: menu('control_create_clone_of_menu', 'CLONE_OPTION', '_myself_')}, sig: 'clone', label: '나 자신 복제하기'})]}]},
            {hat: act('control_start_as_clone', {sig: 'clonestart', label: '복제되었을 때'}), body: [act('motion_goto', {inputs: {TO: menu('motion_goto_menu', 'TO', '_random_')}, sig: 'gotorandom', label: '무작위 위치로 이동하기'})]}]};
    });
    // "무지개색으로 선 그리기"
    def('penRainbow', 'pen', 'act', '무지개\\s*(?:색|빛)?(?:으로|로)?\\s*(?:선|줄|그림)(?:을)?\\s*(?:그리|그려|긋|그어)[가-힣]*|(?:선|줄)(?:을)?\\s*무지개\\s*(?:색|빛)?(?:으로|로)\\s*(?:그리|그려)[가-힣]*', () => ({notes: ['펜을 내리고 10만큼 움직일 때마다 펜 색깔을 10씩 바꿔서 무지개 선을 그려요.'],
        nodes: [penClear(), penDownN(), {t: 'repeat', times: 30, body: [act('motion_movesteps', {inputs: {STEPS: num(10)}, sig: 'move10', label: '10만큼 움직이기'}),
            act('pen_changePenColorParamBy', {inputs: {COLOR_PARAM: menu('pen_menu_colorParam', 'colorParam', 'color'), VALUE: num(10)}, sig: 'pen:color+10', label: '펜 색깔을 10만큼 바꾸기'})]}, penUpN()]}));
    // "한바퀴 천천히 회전", "제자리 회전"
    def('spinOnce', 'motion', 'act', '(?:(?<nb>한|두|세|1|2|3)\\s*바퀴\\s*(?<sp>천천히|느리게|빠르게|빨리)?\\s*(?:빙\\s*|휙\\s*|빙그르르?\\s*)?(?:회전|돌|빙글)[가-힣]*|제자리(?:에서)?\\s*(?<sp2>천천히|빠르게)?\\s*(?:회전|돌기|돌아|빙글)[가-힣]*)', (g, ctx) => {
        if (ctx.robot || /빙글빙글|계속|무한/.test(ctx.src || '')) return null;
        const k = 36 * (KNUM[g.nb] || toNum(g.nb) || 1);
        const slow = /천천히|느리게/.test(g.sp || g.sp2 || '');
        return {notes: ['360도를 한 번에 돌면 안 보여서 10도씩 나눠 돌아요.'], nodes: [{t: 'repeat', times: k, body: [act('motion_turnright', {inputs: {DEGREES: num(10)}, sig: 'right10', label: '오른쪽으로 10도 돌기'}), waitS(slow ? 0.1 : 0.02)]}]};
    });
    // "앞으로 가다가 가장자리 닿으면 멈춰"(고양이) — 계속 가면서 확인
    def('moveUntilEdge', 'motion', 'act', '(?:앞으로\\s*)?(?:가다가|걷다가|움직이다가)\\s*(?:벽|가장자리|끝)(?:에)?\\s*닿으면\\s*(?:멈춰|멈추|정지|서)[가-힣]*', (g, ctx) => (ctx.robot ? null : {notes: ['닿을 때까지 10만큼씩 움직이고, 가장자리에 닿으면 멈춰요.'],
        nodes: [{t: 'until', cond: act('sensing_touchingobject', {inputs: {TOUCHINGOBJECTMENU: menu('sensing_touchingobjectmenu', 'TOUCHINGOBJECTMENU', '_edge_')}, sig: 'touchedge', label: '벽에 닿았는가?'}), body: [act('motion_movesteps', {inputs: {STEPS: num(10)}, sig: 'move10', label: '10만큼 움직이기'}), waitS(0.05)]}]}));
    // "목숨 3개 만들어서 닿을 때마다 하나씩 줄이기"
    def('livesKo', 'variables', 'act', '(?:목숨|생명|라이프)(?:을|이|를)?\\s*(?<n>\\d+|세|셋|다섯)\\s*(?:개|번)?\\s*(?:만들|주고|로|으로)?[가-힣]*\\s*(?:(?<w>벽|가장자리|마우스|적)(?:에)?\\s*)?닿을\\s*때마다\\s*(?:하나씩|1씩|한\\s*개씩)?\\s*(?:줄|빼|깎|잃)[가-힣]*', g => {
        const n = toNum(g.n) || KNUM[g.n] || 3;
        const mouse = /마우스/.test(g.w || '');
        const c = act('sensing_touchingobject', {inputs: {TOUCHINGOBJECTMENU: menu('sensing_touchingobjectmenu', 'TOUCHINGOBJECTMENU', mouse ? '_mouse_' : '_edge_')}, sig: mouse ? 'touchmouse' : 'touchedge', label: mouse ? '마우스 포인터에 닿았는가?' : '벽에 닿았는가?'});
        return {notes: [`‘목숨’을 ${n}으로 시작해 ${mouse ? '마우스' : '벽'}에 닿을 때마다 1씩 줄이고 가운데로 돌아와요. 0이 되면 게임 끝이에요. 방향키로 움직이게 하려면 ‘방향키로 움직이게’라고 말해 보세요.`],
            nodes: [setVar('목숨', n), {t: 'forever', body: [{t: 'if', noLoop: true, cond: c, body: [chVar('목숨', -1), act('motion_gotoxy', {inputs: {X: num(0), Y: num(0)}, sig: 'goto0,0', label: 'x: 0 y: 0 으로 이동하기'}), waitS(0.5)]},
                {t: 'if', noLoop: true, cond: act('operator_lt', {inputs: {OPERAND1: inT(V('목숨')), OPERAND2: txt('1')}, sig: '목숨<1', label: '목숨 < 1'}), body: [sayOf('게임 끝!', 2, '게임 끝!'), act('control_stop', {fields: fld('STOP_OPTION', 'all'), mutation: {tagName: 'mutation', children: [], hasnext: 'false'}, sig: 'stop:all', label: '멈추기 (모두)', cap: true})]}]}]};
    });
    // "계단 모양으로 그려줘"
    def('penStairs', 'pen', 'act', '계단(?:\\s*모양|\\s*식)?(?:으로|을|처럼)?\\s*(?:선(?:을)?\\s*)?(?:그리|그려)[가-힣]*', () => ({notes: ['오른쪽으로 30, 위로 30을 5번 반복해서 계단을 그려요.'],
        nodes: [penClear(), act('motion_pointindirection', {inputs: {DIRECTION: num(90, 'math_angle')}, sig: 'dir90', label: '90도 방향 보기'}), penDownN(), {t: 'repeat', times: 5, body: [
            act('motion_movesteps', {inputs: {STEPS: num(30)}, sig: 'move30', label: '30만큼 움직이기'}), act('motion_turnleft', {inputs: {DEGREES: num(90)}, sig: 'left90', label: '왼쪽으로 90도 돌기'}),
            act('motion_movesteps', {inputs: {STEPS: num(30)}, sig: 'move30', label: '30만큼 움직이기'}), act('motion_turnright', {inputs: {DEGREES: num(90)}, sig: 'right90', label: '오른쪽으로 90도 돌기'})]}, penUpN()]}));
    // "속도 변수 만들어서 점점 빨라지게"
    def('speedVarFaster', 'variables', 'act', '(?:점점|계속)\\s*(?:빨라지|빠르게|빨리)[가-힣]*', (g, ctx) => {
        const nm = (/([가-힣A-Za-z]{1,8})\s*(?:이?라는\s*)?변수/.exec(ctx.src || '') || [])[1];
        if (!nm) return null;
        return {notes: [`‘${nm}’를 1씩 올리고 그만큼 움직여서 점점 빨라져요. 벽에 닿으면 튕겨요.`],
            nodes: [{t: 'forever', body: [chVar(nm, 1), act('motion_movesteps', {inputs: {STEPS: inN(V(nm))}, sig: `move(${nm})`, label: `(${nm})만큼 움직이기`}), act('motion_ifonedgebounce', {sig: 'bounce', label: '벽에 닿으면 튕기기'}), waitS(0.2)]}]};
    });
    def('varMadeSpeed', 'variables', 'act', '(?<nm>[가-힣A-Za-z]{1,8}?)\\s*(?:이라는|라는)?\\s*변수(?:를|을)?\\s*(?:하나\\s*)?(?:만들|만들어)[가-힣]*', g => (/^(?:새|어떤|이|그)$/.test(g.nm) ? null : {nodes: [setVar(g.nm, 0)]}));
    // "시작 메세지 받으면"
    def('recvNamed', 'events', 'hat', '(?<nm>[가-힣A-Za-z0-9]{1,8})\\s*(?:메세지|메시지|신호)(?:를|을)?\\s*(?:받으면|받았을\\s*때|받을\\s*때)', g => ({hat: act('event_whenbroadcastreceived', {fields: {BROADCAST_OPTION: {name: 'BROADCAST_OPTION', value: g.nm, bcast: g.nm}}, sig: `recv:${g.nm}`, label: `${g.nm} 신호를 받았을 때`})}));
    // "네모 모양으로 운전하기"
    def('rSquareDrive', 'robot', 'act', '(?:네모|사각형|정사각형)(?:\\s*모양)?(?:으로|로)\\s*(?:운전|주행|달려|달리|돌아|움직)[가-힣]*', () => ({notes: ['앞으로 1초 → 오른쪽 90도(0.6초)를 4번 해요. 로봇마다 도는 정도가 달라서 0.6초는 맞춰 보세요.'],
        nodes: [{t: 'repeat', times: 4, body: drive('forward', 1, false).concat(drive('right', 0.6, false))}].concat(stopBoth())}));
    // "신호등 빨강 노랑 초록 순서로"
    def('rTrafficOrder', 'robot', 'act', '신호등[가-힣]*(?:\\s*(?:만들|만들어|만들기)[가-힣]*)?\\s*(?<seq>(?:(?:빨강|빨간|노랑|노란|초록|녹색|파랑|파란)(?:색|불)?\\s*,?\\s*(?:다음|그다음|→|->)?\\s*){2,4})\\s*(?:순서로|순서대로|순으로|차례로)', g => {
        const H = {빨강: '#ff0000', 빨간: '#ff0000', 노랑: '#ffff00', 노란: '#ffff00', 초록: '#00ff00', 녹색: '#00ff00', 파랑: '#0000ff', 파란: '#0000ff'};
        const cs = g.seq.match(/빨강|빨간|노랑|노란|초록|녹색|파랑|파란/g);
        const nodes = [];
        cs.forEach(c => nodes.push(colorOn(H[c]), waitN(/노/.test(c) ? 1 : 2)));
        nodes.push(colorOff());
        return {notes: ['말한 순서대로 켜요(노랑은 1초, 나머지는 2초).'], nodes};
    });
    // "가까이 가면 삐삐", "가까우면"
    def('rNear', 'robot', 'cond', '(?:(?:무언가|뭔가|물체|사람|손|장애물)(?:이|가)?\\s*)?가까(?:이|워지)?\\s*(?:가면|오면|있으면|다가가면|다가오면|지면)|가까우면', (g, ctx) => (/마우스/.test(ctx.src || '') ? null : {cond: obstacle()}));
    // "사람 오면 문 열리는 자동문"
    def('rAutoDoor', 'robot', 'act', '자동\\s*문|(?:사람|누가)\\s*(?:오면|다가오면)\\s*(?:문(?:이)?\\s*)?열리[가-힣]*', () => ({notes: ['근접 센서로 사람이 오면 모터 1번을 1초 돌려 문을 열고, 3초 뒤에 반대로 돌려 닫아요. 모터에 문을 연결해 보세요.'],
        nodes: [{t: 'forever', body: [act('control_wait_until', {boolIn: {CONDITION: obstacle()}, sig: 'until:obstacle', label: '‘장애물이 감지되었는가?’ 까지 기다리기'}),
            motor(M1, 'Right'), waitN(1), motorOff(M1), waitN(3), motor(M1, 'Left'), waitN(1), motorOff(M1)]}]}));
    // "도 1박자 레 1박자 미 2박자"
    def('rNoteBeats', 'robot', 'act', '(?:(?:도|레|미|파|솔|라|시)\\s*(?:\\d+(?:\\.\\d+)?|반)\\s*박자?\\s*,?\\s*){2,16}', g => {
        const nodes = [];
        const re = /(도|레|미|파|솔|라|시)\s*(\d+(?:\.\d+)?|반)\s*박/g;
        let m;
        while ((m = re.exec(g[0] || '')) !== null) nodes.push(noteBlock(SOLFA[m[1]], m[2] === '반' ? 0.5 : Number(m[2])));
        return nodes.length ? {nodes} : null;
    });
    // "버튼 누를때마다 숫자 1씩 올라가서 매트릭스에 나오게"
    def('rCountLed', 'robot', 'act', '버튼(?:을)?\\s*누를\\s*때마다\\s*(?:숫자(?:가)?\\s*)?(?:1씩|하나씩)\\s*(?:올라|늘어|커지)[가-힣]*\\s*(?:LED|엘이디|매트릭스|화면)(?:에|로)\\s*(?:나오|보이|표시|보여)[가-힣]*', () => {
        const shows = [];
        for (let d = 0; d <= 9; d++) shows.push({t: 'if', noLoop: true, cond: act('operator_equals', {inputs: {OPERAND1: inT(V('횟수')), OPERAND2: txt(String(d))}, sig: `횟수=${d}`, label: `횟수 = ${d}`}), body: [act('cubroidled_lceNumberControl', {fields: fld('MATRIX', LED_NUM[d]), sig: `led:${d}`, label: `LED에 숫자 ${d} 보여주기`})]});
        return {notes: ['LED 판은 숫자 0~9 그림만 있어서, 횟수가 몇인지 확인해 그 숫자 그림을 보여줘요(9 다음은 0부터).'],
            nodes: [setVar('횟수', 0), {t: 'forever', body: [act('control_wait_until', {boolIn: {CONDITION: button()}, sig: 'until:button', label: '‘버튼이 눌러졌는가?’ 까지 기다리기'}),
                chVar('횟수', 1), {t: 'if', noLoop: true, cond: act('operator_gt', {inputs: {OPERAND1: inT(V('횟수')), OPERAND2: txt('9')}, sig: '횟수>9', label: '횟수 > 9'}), body: [setVar('횟수', 0)]}].concat(shows, [
                act('control_wait_until', {boolIn: {CONDITION: act('operator_not', {boolIn: {OPERAND: button()}, sig: 'not:button', label: '버튼이 눌러지지 않았는가?'})}, sig: 'until:not:button', label: '‘버튼이 눌러지지 않았는가?’ 까지 기다리기'})])}]};
    });
    // "오른쪽 모터는 거꾸로 왼쪽 모터는 앞으로"
    def('rTwoWheels', 'robot', 'act', '(?<s1>오른쪽|왼쪽)\\s*(?:모터|바퀴)(?:는|가|을|를)?\\s*(?<d1>거꾸로|뒤로|앞으로|반대로|정방향)\\s*(?:돌리고|가고|하고|,)?\\s*(?<s2>오른쪽|왼쪽)\\s*(?:모터|바퀴)(?:는|가|을|를)?\\s*(?<d2>거꾸로|뒤로|앞으로|반대로|정방향)[가-힣]*', g => {
        const fwd = d => /앞으로|정방향/.test(d);
        const dir = (side, d) => (side === '왼쪽' ? (fwd(d) ? 'Right' : 'Left') : (fwd(d) ? 'Left' : 'Right'));
        const lw = g.s1 === '왼쪽' ? g.d1 : g.d2;
        const rw = g.s1 === '오른쪽' ? g.d1 : g.d2;
        return {notes: ['왼쪽 바퀴 = 모터 1번, 오른쪽 바퀴 = 모터 2번이에요(반대로 조립했다면 바꿔 보세요). 두 바퀴가 반대로 돌면 제자리에서 돌아요.'],
            nodes: [motor(M1, dir('왼쪽', lw)), motor(M2, dir('오른쪽', rw)), waitN(1)].concat(stopBoth())};
    });
    // "위에서 공 떨어지게"
    def('fallTop', 'motion', 'act', '(?:위|하늘|꼭대기|맨\\s*위)에서\\s*(?:공|고양이|사과|별|비|눈)?(?:이|가)?\\s*(?:떨어지|내려오|떨어져)[가-힣]*', (g, ctx) => (ctx.robot || /복제|여러|게임|받는|받게|잡는|잡게/.test(ctx.src || '') ? null : {notes: ['맨 위(y 170)에서 시작해 바닥(y -150)에 닿을 때까지 5씩 내려와요.'],
        nodes: [act('motion_sety', {inputs: {Y: num(170)}, sig: 'sety=170', label: 'y 좌표를 170(으)로 정하기'}), {t: 'until', cond: act('operator_lt', {inputs: {OPERAND1: inT(act('motion_yposition', {sig: 'ypos', label: 'y 좌표'})), OPERAND2: txt('-150')}, sig: 'y<-150', label: 'y 좌표 < -150'}),
            body: [act('motion_changeyby', {inputs: {DY: num(-5)}, sig: 'y-5', label: 'y 좌표를 -5만큼 바꾸기'}), waitS(0.02)]}]}));
    // "오른쪽으로 갔다가 왼쪽으로"
    def('goThere', 'motion', 'act', '(?<a>오른쪽|왼쪽|위|아래)(?:으로|로)\\s*(?:갔다가|가다가|갔다)\\s*(?:다시\\s*)?(?<b>오른쪽|왼쪽|위|아래)(?:으로|로)?(?:\\s*(?:와|오|가|돌아와|돌아오|와줘|가줘)[가-힣]*)?', (g, ctx) => {
        if (ctx.robot) return null;
        const step = d => (d === '오른쪽' ? act('motion_changexby', {inputs: {DX: num(10)}, sig: 'x+10', label: 'x 좌표를 10만큼 바꾸기'}) : d === '왼쪽' ? act('motion_changexby', {inputs: {DX: num(-10)}, sig: 'x-10', label: 'x 좌표를 -10만큼 바꾸기'})
            : d === '위' ? act('motion_changeyby', {inputs: {DY: num(10)}, sig: 'y+10', label: 'y 좌표를 10만큼 바꾸기'}) : act('motion_changeyby', {inputs: {DY: num(-10)}, sig: 'y-10', label: 'y 좌표를 -10만큼 바꾸기'}));
        return {notes: ['10만큼씩 10번 가고, 반대쪽으로 10번 와요.'], nodes: [{t: 'repeat', times: 10, body: [step(g.a), waitS(0.05)]}, {t: 'repeat', times: 10, body: [step(g.b), waitS(0.05)]}]};
    });
    // "모양 바꾸기 반복해서 걷게"
    def('walkCostume', 'motion', 'act', '모양(?:을)?\\s*(?:바꾸기|바꾸면서|바꿔\\s*가며|바꿔\\s*가면서|바꿔서)\\s*(?:반복(?:해서|하면서)?\\s*)?(?:걷|걸어|걸)[가-힣]*', () => ({notes: ['걷기 = 움직이기 → 다음 모양 → 잠깐 기다리기를 계속 반복해요.'],
        nodes: [{t: 'forever', body: [act('motion_movesteps', {inputs: {STEPS: num(10)}, sig: 'move10', label: '10만큼 움직이기'}), act('looks_nextcostume', {sig: 'nextcostume', label: '다음 모양으로 바꾸기'}), waitS(0.2), act('motion_ifonedgebounce', {sig: 'bounce', label: '벽에 닿으면 튕기기'})]}]}));
    // ═══ 18차: 연습 10차 실패에서 ═══
    def('moveBackN', 'motion', 'act', '(?<n>\\d+)\\s*(?:걸음|만큼|칸|픽셀)\\s*뒤로(?:\\s*(?:가|이동|움직|물러)[가-힣]*)?', (g, ctx) => (ctx.robot ? null : {nodes: [act('motion_movesteps', {inputs: {STEPS: num(-toNum(g.n))}, sig: `move-${g.n}`, label: `-${g.n}만큼 움직이기`})]}));
    def('slowFwdCat', 'motion', 'act', '(?:천천히|느리게|살살)\\s*(?:앞으로\\s*)?(?:가|걸어|움직|이동)[가-힣]*', (g, ctx) => (ctx.robot ? null : {notes: ['천천히 = 5만큼씩 짧게 기다리며 20번(모두 100만큼) 움직여요.'],
        nodes: [{t: 'repeat', times: 20, body: [act('motion_movesteps', {inputs: {STEPS: num(5)}, sig: 'move5', label: '5만큼 움직이기'}), waitS(0.05)]}]}));
    // K2 "30초 타이머 만들고 시간 끝나면 게임오버 라고 말해"
    def('timerEndSay', 'variables', 'act', '(?<n>\\d+)\\s*(?<u>초|분)\\s*(?:짜리\\s*)?타이머(?:를|을)?\\s*(?:만들고|만들어서|만들어줘|만들어|하고|돌리고)?\\s*(?:시간(?:이)?\\s*)?(?:다\\s*)?(?:끝나면|0\\s*(?:이\\s*)?되면|되면)\\s*[\'"‘“]?(?<em>[^\'"’”]{1,15}?)[\'"’”]?\\s*(?:이?라고|하고)\\s*(?:말|해|외치)[가-힣]*', g => {
        const n = toNum(g.n) * (g.u === '분' ? 60 : 1);
        const em = g.em.trim();
        const over = /게임\s*오버|게임\s*끝|끝/.test(em);
        return {notes: [`‘남은 시간’을 ${n}에서 1초마다 1씩 줄이며 말하고, 0이 되면 ‘${em}’이라고 말해요.`],
            nodes: [setVar('남은 시간', n), {t: 'repeat', times: n, body: [sayOf(V('남은 시간'), null, '남은 시간'), waitS(1), chVar('남은 시간', -1)]}, sayOf(em, 2, em)].concat(over ? [act('control_stop', {fields: fld('STOP_OPTION', 'all'), mutation: {tagName: 'mutation', children: [], hasnext: 'false'}, sig: 'stop:all', label: '멈추기 (모두)', cap: true})] : [])};
    });
    // K3 "대답하면 반가워 라고 해줘"
    def('sayAfterAns', 'looks', 'act', '대답(?:하면|을\\s*하면|받으면|치면|쓰면)\\s*[\'"‘“]?(?<m>[^\'"’”]{1,20}?)[\'"’”]?\\s*(?:이?라고|하고)\\s*(?:말|해|대답)[가-힣]*', g => {
        const m = g.m.trim();
        const withAns = /\s*(?:대답|이름)$/.exec(m);
        if (withAns) { const w = m.slice(0, withAns.index).trim(); return {notes: ['‘결합하기’로 말 뒤에 대답을 붙였어요.'], nodes: [sayOf(join(`${w} `, act('sensing_answer', {sig: 'answer', label: '대답'})), 2, `${w} (대답)`)]}; }
        return {nodes: [sayOf(m, 2, m)]};
    });
    // K4 "3 더하기 5는? 문제 내고 맞으면 정답 틀리면 땡"
    def('quizKo', 'sensing', 'act', '(?:(?:퀴즈|문제)(?:를)?\\s*(?:내줘|내|내고|만들어)[가-힣]*\\s*:?\\s*)?(?<a>\\d+)\\s*(?<op>더하기|빼기|곱하기|\\+|-|x|×|\\*)\\s*(?<b>\\d+)\\s*(?:은|는|이|가|=)?\\s*\\??(?:\\s*(?:(?:문제|퀴즈)(?:를)?\\s*(?:내고|내서|내줘|내|만들어)[가-힣]*|(?:물어|묻)[가-힣]*))?(?:\\s*(?:맞으면|맞추면|정답이면)\\s*[\'"‘“]?(?<ok>[^\'"’”\\s]{1,10})[\'"’”]?\\s*(?:이?라고\\s*(?:하고|해|말하고)?)?\\s*(?:틀리면|아니면)\\s*[\'"‘“]?(?<ng>[^\'"’”\\s]{1,10})[\'"’”]?(?:\\s*(?:이?라고)?\\s*(?:해|말해)[가-힣]*)?)?', (g, ctx) => {
        if (!/문제|퀴즈|물어|묻|맞으면|맞추면|몇이야|몇이게|는\s*\?|은\s*\?/.test(ctx.src || '') || /LED|엘이디|매트릭스/.test(ctx.src || '')) return null;
        const a = toNum(g.a);
        const b = toNum(g.b);
        const mul = /곱하기|x|×|\*/.test(g.op);
        const sub = /빼기|-/.test(g.op);
        const r = mul ? a * b : sub ? a - b : a + b;
        const sym = mul ? '×' : sub ? '-' : '+';
        const on = /(?:맞으면|맞추면|정답이면)\s*['"‘“]?([^'"’”\s]{1,10})['"’”]?\s*(?:이?라고\s*(?:하고|해|말하고)?)?\s*(?:틀리면|아니면)\s*['"‘“]?([^'"’”\s]{1,10})/.exec(ctx.src || '');
        const ok = (g.ok || (on && on[1]) || '정답!').trim();
        const ng = (g.ng || (on && on[2]) || '틀렸어!').trim();
        return {notes: [`‘${a}${sym}${b}=?’ 을 묻고, 대답이 ${r}이면 ‘${ok}’, 아니면 ‘${ng}’라고 말해요.`],
            nodes: [act('sensing_askandwait', {inputs: {QUESTION: txt(`${a}${sym}${b}=?`)}, sig: 'ask(quiz)', label: `‘${a}${sym}${b}=?’라고 묻고 기다리기`}),
                {t: 'ifelse', noLoop: true, cond: act('operator_equals', {inputs: {OPERAND1: inT(act('sensing_answer', {sig: 'answer', label: '대답'})), OPERAND2: txt(String(r))}, sig: `answer=${r}`, label: `대답 = ${r}`}),
                    body: [sayOf(ok, 2, ok)], body2: [sayOf(ng, 2, ng)]}]};
    });
    // K5 "고양이 복제해서 하늘에서 여러마리 떨어지게"
    def('cloneFall', 'control', 'multi', '(?:고양이(?:를)?\\s*)?(?:복제|클론|복사)[가-힣]*\\s*(?:해서|하고|해)?\\s*(?:(?:하늘|위|꼭대기)에서\\s*)?(?:여러\\s*마리|많이|계속|여러\\s*개)?\\s*(?:떨어지|내려오|쏟아지)[가-힣]*', () => {
        const edge = act('operator_lt', {inputs: {OPERAND1: inT(act('motion_yposition', {sig: 'ypos', label: 'y 좌표'})), OPERAND2: txt('-170')}, sig: 'y<-170', label: 'y 좌표 < -170'});
        return {notes: ['원본은 숨고 1초마다 복제해요. 복제본은 맨 위 아무 x에서 나타나 아래로 떨어지고, 바닥에 닿으면 지워져요.'], multi: [
            {hat: act('event_whenflagclicked', {sig: 'flag', label: '깃발을 클릭했을 때'}), body: [act('looks_hide', {sig: 'hide', label: '숨기기'}), {t: 'forever', body: [act('control_create_clone_of', {inputs: {CLONE_OPTION: menu('control_create_clone_of_menu', 'CLONE_OPTION', '_myself_')}, sig: 'clone', label: '나 자신 복제하기'}), waitS(1)]}]},
            {hat: act('control_start_as_clone', {sig: 'clonestart', label: '복제되었을 때'}), body: [
                act('motion_gotoxy', {inputs: {X: inN(act('operator_random', {inputs: {FROM: num(-220), TO: num(220)}, sig: 'random-220-220', label: '-220부터 220 사이의 난수'})), Y: num(170)}, sig: 'goto-rand,170', label: 'x: 난수 y: 170 으로 이동하기'}),
                act('looks_show', {sig: 'show', label: '보이기'}), {t: 'until', cond: edge, body: [act('motion_changeyby', {inputs: {DY: num(-5)}, sig: 'y-5', label: 'y 좌표를 -5만큼 바꾸기'})]},
                act('control_delete_this_clone', {sig: 'clonedelete', label: '이 복제본 삭제하기', cap: true})]}]};
    });
    // K7 "a누르면 왼쪽 d누르면 오른쪽 w는 위 s는 아래"
    def('keysDirs', 'events', 'multi', '(?:[a-z]\\s*(?:키\\s*)?(?:를\\s*)?(?:누르면|는|은|:)\\s*(?:왼쪽|오른쪽|위|아래)(?:으로|로)?\\s*,?\\s*){2,4}(?:가게|가|움직이게|움직여|이동하게|이동|해줘|해)?', (g, ctx) => {
        const re = /([a-z])\s*(?:키\s*)?(?:를\s*)?(?:누르면|는|은|:)\s*(왼쪽|오른쪽|위|아래)/g;
        const multi = [];
        let m;
        while ((m = re.exec(g[0] || '')) !== null) {
            const d = m[2];
            const node = ctx.robot ? null : (d === '왼쪽' || d === '오른쪽' ? act('motion_changexby', {inputs: {DX: num(d === '왼쪽' ? -10 : 10)}, sig: `x${d === '왼쪽' ? '-' : '+'}10`, label: `x 좌표를 ${d === '왼쪽' ? -10 : 10}만큼 바꾸기`}) : act('motion_changeyby', {inputs: {DY: num(d === '아래' ? -10 : 10)}, sig: `y${d === '아래' ? '-' : '+'}10`, label: `y 좌표를 ${d === '아래' ? -10 : 10}만큼 바꾸기`}));
            if (node) multi.push({hat: act('event_whenkeypressed', {fields: fld('KEY_OPTION', m[1]), sig: `key:${m[1]}`, label: `${m[1]} 키를 눌렀을 때`}), body: [node]});
        }
        return multi.length >= 2 ? {notes: ['키마다 스크립트를 하나씩 만들었어요.'], multi} : null;
    });
    // K8 "72도 도는걸"
    def('turnDoneun', 'motion', 'act', '(?<n>\\d+)\\s*도\\s*(?:씩\\s*)?도는\\s*(?:걸|것|거)?', g => ({nodes: [act('motion_turnright', {inputs: {DEGREES: num(toNum(g.n))}, sig: `right${g.n}`, label: `오른쪽으로 ${g.n}도 돌기`})]}));
    // K15 "이겼다! 하고 끝"
    def('sayAndEnd', 'looks', 'act', '(?:[\'"‘“](?<m>[^\'"’”]{1,12})[\'"’”]|(?<m2>[^\\s\'"’”!]{1,12}!+)|(?<m3>[^\\s\'"’”]{1,12})\\s*이?라고)\\s*(?:하고|말하고)?\\s*(?:끝|멈춰|게임\\s*끝|종료|끝내)[가-힣]*', g => ({nodes: [sayOf(g.m || g.m2 || g.m3, 2, g.m || g.m2 || g.m3), act('control_stop', {fields: fld('STOP_OPTION', 'all'), mutation: {tagName: 'mutation', children: [], hasnext: 'false'}, sig: 'stop:all', label: '멈추기 (모두)', cap: true})]}));
    // K16 "속도라는 변수 만들고 계속 1씩 늘어나게"
    def('varNamed', 'variables', 'act', '(?<nm>[가-힣A-Za-z]{1,8})(?:이?라는|이?라고\\s*하는)\\s*변수(?:를|을)?\\s*(?:하나\\s*)?(?:만들|만들어)[가-힣]*', g => ({notes: [`‘${g.nm}’ 변수를 만들고 깃발을 누르면 0으로 정해요.`], nodes: [setVar(g.nm, 0)]}));
    def('varIncForever', 'variables', 'act', '(?:계속|1초마다|자꾸)\\s*(?<n>\\d+)\\s*씩\\s*(?:늘어|커지|올라|증가)[가-힣]*', (g, ctx) => {
        const nm = (/([가-힣A-Za-z]{1,8})(?:이?라는|이?라고\s*하는)\s*변수/.exec(ctx.src || '') || [])[1] || ctx.lastVar || '점수';
        return {nodes: [{t: 'forever', body: [chVar(nm, toNum(g.n)), waitS(1)]}]};
    });
    // K17 "업다운 게임"
    def('guessKo', 'sensing', 'act', '(?:업\\s*다운|숫자\\s*맞(?:히|추)기|숫자\\s*맞(?:히|추)는)\\s*(?:게임)?', () => {
        const ans = () => act('sensing_answer', {sig: 'answer', label: '대답'});
        return {notes: ['‘비밀 수’는 1~100 사이 난수예요. 맞힐 때까지 묻고, 작으면 ‘업!’, 크면 ‘다운!’이라고 말해요.'],
            nodes: [setVar('비밀 수', act('operator_random', {inputs: {FROM: num(1), TO: num(100)}, sig: 'random1-100', label: '1부터 100 사이의 난수'})),
                {t: 'until', cond: act('operator_equals', {inputs: {OPERAND1: inT(ans()), OPERAND2: inT(V('비밀 수'))}, sig: 'answer=비밀 수', label: '대답 = 비밀 수'}), body: [
                    act('sensing_askandwait', {inputs: {QUESTION: txt('1~100 중 숫자를 맞혀 봐!')}, sig: 'ask(guess)', label: '‘1~100 중 숫자를 맞혀 봐!’라고 묻고 기다리기'}),
                    {t: 'if', noLoop: true, cond: act('operator_lt', {inputs: {OPERAND1: inT(ans()), OPERAND2: inT(V('비밀 수'))}, sig: 'answer<비밀 수', label: '대답 < 비밀 수'}), body: [sayOf('업!', 1, '업!')]},
                    {t: 'if', noLoop: true, cond: act('operator_gt', {inputs: {OPERAND1: inT(ans()), OPERAND2: inT(V('비밀 수'))}, sig: 'answer>비밀 수', label: '대답 > 비밀 수'}), body: [sayOf('다운!', 1, '다운!')]}]},
                sayOf('정답!', 2, '정답!')]};
    });
    // K18 "복제본 클릭하면 사라지게"
    def('cloneClickDel', 'control', 'multi', '복제본(?:을|를)?\\s*(?:클릭|누르)[가-힣]*\\s*(?:사라지|없어지|지워|삭제)[가-힣]*', () => ({notes: ['클릭하면 ‘이 복제본 삭제하기’를 해요(원본은 지워지지 않아요).'],
        multi: [{hat: act('event_whenthisspriteclicked', {sig: 'click', label: '이 스프라이트를 클릭했을 때'}), body: [act('control_delete_this_clone', {sig: 'clonedelete', label: '이 복제본 삭제하기', cap: true})]}]}));
    // K19 "마우스 누르고 있는 동안만 그림 그려지게"
    def('penMouseHold', 'pen', 'act', '마우스(?:를)?\\s*(?:누르고\\s*있(?:는|을)|누를|클릭하고\\s*있는)\\s*(?:동안|때)(?:만)?\\s*(?:그림(?:이)?|선(?:이)?|펜(?:이)?)?\\s*(?:그려|그리|그어)[가-힣]*', () => ({notes: ['마우스를 따라가면서, 누르고 있으면 펜 내리기·떼면 펜 올리기를 계속 확인해요.'],
        nodes: [{t: 'forever', body: [act('motion_goto', {inputs: {TO: menu('motion_goto_menu', 'TO', '_mouse_')}, sig: 'gotomouse', label: '마우스 포인터로 이동하기'}),
            {t: 'ifelse', noLoop: true, cond: act('sensing_mousedown', {sig: 'mousedown', label: '마우스를 클릭했는가?'}), body: [penDownN()], body2: [penUpN()]}]}]}));
    // K20 "투명도 0으로 다시 원래대로"
    def('ghostKo', 'looks', 'act', '투명(?:도|하게|한\\s*정도)?(?:를|을|는)?\\s*(?<n>\\d+)\\s*(?:으로|로|%)?(?:\\s*(?:다시\\s*)?(?:원래대로|돌려|해|정해|바꿔)[가-힣]*)?', g => ({nodes: [act('looks_seteffectto', {fields: fld('EFFECT', 'GHOST'), inputs: {VALUE: num(Math.max(0, Math.min(100, toNum(g.n))))}, sig: `GHOST=${g.n}`, label: `유령 효과를 ${g.n}(으)로 정하기`})]}));
    // K21 "시간 재서 몇 초 걸렸는지 알려줘"
    def('measureTime', 'sensing', 'multi', '(?:시간(?:을)?\\s*재(?:서|고)?|몇\\s*초\\s*걸렸는지)[가-힣\\s]*(?:알려|말해|보여)[가-힣]*', () => ({notes: ['깃발을 누르면 타이머를 0으로 돌리고, 스페이스를 누르면 걸린 시간(초)을 말해요.'],
        multi: [{hat: act('event_whenflagclicked', {sig: 'flag', label: '깃발을 클릭했을 때'}), body: [act('sensing_resettimer', {sig: 'resettimer', label: '타이머 초기화'})]},
            {hat: act('event_whenkeypressed', {fields: fld('KEY_OPTION', 'space'), sig: 'key:space', label: '스페이스 키를 눌렀을 때'}), body: [sayOf(join(act('sensing_timer', {sig: 'timer', label: '타이머'}), '초 걸렸어!'), 2, '(타이머)초 걸렸어!')]}]}));
    // K22 "버튼 한번 누르면 켜지고 또 누르면 꺼지게"
    def('rToggleKo', 'robot', 'act', '버튼(?:을)?\\s*(?:한\\s*번\\s*)?누르면\\s*(?:(?:불(?:빛)?|손전등|라이트|램프|전등)(?:이)?\\s*)?켜지고\\s*(?:또|다시|한\\s*번\\s*더)\\s*누르면\\s*(?:(?:불(?:빛)?|손전등|라이트|램프|전등)(?:이)?\\s*)?꺼지[가-힣]*', () => ({
        notes: ['누를 때마다 ‘불 상태’를 0↔1로 바꿔요. 손을 뗄 때까지 기다려서 한 번 누르면 한 번만 바뀌어요.'],
        nodes: [setVar('불 상태', 0), {t: 'forever', body: [act('control_wait_until', {boolIn: {CONDITION: button()}, sig: 'until:button', label: '‘버튼이 눌러졌는가?’ 까지 기다리기'}),
            {t: 'ifelse', noLoop: true, cond: act('operator_equals', {inputs: {OPERAND1: inT(V('불 상태')), OPERAND2: txt('0')}, sig: '불 상태=0', label: '불 상태 = 0'}), body: [colorOn('#ffffff'), setVar('불 상태', 1)], body2: [colorOff(), setVar('불 상태', 0)]},
            act('control_wait_until', {boolIn: {CONDITION: act('operator_not', {boolIn: {OPERAND: button()}, sig: 'not:button', label: '버튼이 눌러지지 않았는가?'})}, sig: 'until:not:button', label: '‘버튼이 눌러지지 않았는가?’ 까지 기다리기'})]}]}));
    // K23 "불빛 무지개색으로 계속 바뀌게"
    def('rRainbowKo', 'robot', 'act', '(?:불(?:빛)?|빛|램프|마스터|라이트)\\s*(?:을|이|를|의)?\\s*(?:색깔?\\s*(?:을|이)?\\s*)?무지개\\s*(?:색|처럼|같이)?(?:으로|로)?\\s*(?:계속\\s*)?(?:바뀌|바꿔|변하|켜|돌려)[가-힣]*', () => ({notes: ['빨·주·노·초·파·보라 불빛을 0.3초씩 계속 바꿔요.'],
        nodes: [{t: 'forever', body: ['#ff0000', '#ff8800', '#ffff00', '#00ff00', '#0000ff', '#ff00ff'].reduce((a, h) => a.concat([colorOn(h), waitN(0.3)]), [])}]}));
    // K24 "8자로 달리게"
    def('figure8', 'motion', 'act', '8\\s*자(?:\\s*모양)?(?:으로|로|를)?\\s*(?:달리|달려|가|돌|움직|그리|주행)[가-힣]*', (g, ctx) => (ctx.robot || /달리|달려|주행/.test(g[0]) ? {notes: ['한쪽 모터를 느리게 해서 오른쪽으로 크게 한 바퀴, 왼쪽으로 크게 한 바퀴 돌아요. 시간은 로봇에 맞게 바꿔 보세요.'],
        nodes: [{t: 'forever', body: [act(`${M1}_dcMotorSetSpeed`, {inputs: {SPEED: num(100)}, sig: 'm1:speed100', label: 'DC 모터 1번 속도를 100%로 정하기'}), act(`${M2}_dcMotorSetSpeed`, {inputs: {SPEED: num(40)}, sig: 'm2:speed40', label: 'DC 모터 2번 속도를 40%로 정하기'})].concat(drive('forward', 4, false),
            [act(`${M1}_dcMotorSetSpeed`, {inputs: {SPEED: num(40)}, sig: 'm1:speed40', label: 'DC 모터 1번 속도를 40%로 정하기'}), act(`${M2}_dcMotorSetSpeed`, {inputs: {SPEED: num(100)}, sig: 'm2:speed100', label: 'DC 모터 2번 속도를 100%로 정하기'})], drive('forward', 4, false))}]} : {
        notes: ['오른쪽으로 원 하나, 왼쪽으로 원 하나 — 8자 모양으로 움직여요.'],
        nodes: [{t: 'forever', body: [{t: 'repeat', times: 36, body: [act('motion_movesteps', {inputs: {STEPS: num(8)}, sig: 'move8', label: '8만큼 움직이기'}), act('motion_turnright', {inputs: {DEGREES: num(10)}, sig: 'right10', label: '오른쪽으로 10도 돌기'})]},
            {t: 'repeat', times: 36, body: [act('motion_movesteps', {inputs: {STEPS: num(8)}, sig: 'move8', label: '8만큼 움직이기'}), act('motion_turnleft', {inputs: {DEGREES: num(10)}, sig: 'left10', label: '왼쪽으로 10도 돌기'})]}]}]}));
    // ═══ 16차: 연습 9차 실패에서 ═══
    def('stampMany', 'pen', 'act', `도장(?:을|으로)?\\s*(?:찍기로\\s*|찍어서\\s*)?(?:고양이(?:를)?\\s*)?(?:(?<n>\\d+|${RE_KNUM})\\s*(?:개|번|마리)|여러\\s*(?:개|마리|번)|많이)\\s*(?:찍|남기)?[가-힣]*`, g => {
        const n = toNum(g.n) || 5;
        return {notes: ['무작위 위치로 옮겨 가며 도장을 찍어요.'], nodes: [{t: 'repeat', times: n, body: [act('motion_goto', {inputs: {TO: menu('motion_goto_menu', 'TO', '_random_')}, sig: 'gotorandom', label: '무작위 위치로 이동하기'}), act('pen_stamp', {sig: 'pen:stamp', label: '도장 찍기'})]}]};
    });
    def('growBack', 'looks', 'act', '점점\\s*커지다가\\s*(?:다시\\s*)?원래\\s*(?:크기)?(?:로|으로)?\\s*(?:돌아|되돌아|작아)[가-힣]*', () => ({nodes: [
        {t: 'repeat', times: 10, body: [act('looks_changesizeby', {inputs: {CHANGE: num(5)}, sig: 'size+5', label: '크기를 5만큼 바꾸기'}), waitS(0.05)]}, waitS(0.3),
        {t: 'repeat', times: 10, body: [act('looks_changesizeby', {inputs: {CHANGE: num(-5)}, sig: 'size-5', label: '크기를 -5만큼 바꾸기'}), waitS(0.05)]}]}));
    def('floorCond', 'motion', 'cond', '(?:바닥|아래\\s*끝|화면\\s*아래)(?:\\s*(?:밑|아래))?(?:으로|로|에)?\\s*(?:내려가면|떨어지면|닿으면|나가면)', () => ({cond: act('operator_lt', {inputs: {OPERAND1: inT(act('motion_yposition', {sig: 'ypos', label: 'y 좌표'})), OPERAND2: txt('-170')}, sig: 'y<-170', label: 'y 좌표 < -170'})}));
    def('backToTop', 'motion', 'act', '(?:다시\\s*)?(?:위로|맨\\s*위로|위쪽으로)\\s*(?:올라오|올라가|보내|가)[가-힣]*', (g, ctx) => (/바닥|아래\s*끝|떨어지/.test(ctx.src || '') ? {nodes: [act('motion_sety', {inputs: {Y: num(170)}, sig: 'sety=170', label: 'y 좌표를 170(으)로 정하기'})]} : null));
    def('moveByVar', 'motion', 'act', '(?:그만큼|그\\s*값만큼|변수만큼|그\\s*속도로)\\s*(?:움직|이동|가)[가-힣]*', (g, ctx, alt, before) => {
        const m = /([가-힣A-Za-z]{1,6}?)(?:이라는|라는)?\s*변수/.exec(String(before || '')) || /([가-힣A-Za-z]{1,6}?)(?:을|를)\s*\d+/.exec(String(before || ''));
        const v = m ? m[1] : (ctx.lastVar || '속도');
        return {nodes: [act('motion_movesteps', {inputs: {STEPS: inN(V(v))}, sig: `move(${v})`, label: `(${v})만큼 움직이기`})]};
    });
    def('sayDate', 'sensing', 'act', '(?:오늘\\s*)?(?:몇\\s*월\\s*며칠|날짜|몇\\s*일)(?:인지|이야|을|를)?\\s*(?:고양이가\\s*)?(?:말|알려|보여)[가-힣]*', () => {
        const cur = w => act('sensing_current', {fields: fld('CURRENTMENU', w), sig: `current:${w}`, label: w === 'MONTH' ? '현재 월' : '현재 일'});
        return {notes: ['‘현재 월·일’ 블록과 결합하기로 오늘 날짜를 말해요.'], nodes: [sayOf(join(join(cur('MONTH'), '월 '), join(cur('DATE'), '일')), 2, '(월)월 (일)일')]};
    });
    def('stopwatchReset', 'sensing', 'act', '(?:초시계|스톱워치|타이머)(?:를)?\\s*(?:초기화|리셋|0으로|처음부터)[가-힣]*', () => ({nodes: [act('sensing_resettimer', {sig: 'timer:reset', label: '타이머 초기화'})]}));
    def('stopwatch', 'variables', 'act', '(?:초(?:를)?\\s*세는|시간(?:을)?\\s*재는|시간\\s*세는)\\s*(?:타이머|시계)|스톱워치|초시계(?=\\s*(?:만들|$))', () => ({notes: ['‘시간’ 변수를 1초마다 1씩 올리면서 말해요.'],
        nodes: [setVar('시간', 0), {t: 'forever', body: [sayOf(V('시간'), null, '시간'), waitS(1), chVar('시간', 1)]}]}));
    def('rps', 'operators', 'act', '가위\\s*바위\\s*보(?:\\s*게임)?(?:을|를)?(?:\\s*(?:만들|해|하자)[가-힣]*)?', () => {
        const eq = n => act('operator_equals', {inputs: {OPERAND1: inT(V('선택')), OPERAND2: txt(String(n))}, sig: `선택=${n}`, label: `선택 = ${n}`});
        return {notes: ['1~3 난수로 가위·바위·보 중 하나를 골라 말해요. 대답과 비교하는 건 ‘대답이 가위면’처럼 이어서 말해 보세요.'],
            nodes: [act('sensing_askandwait', {inputs: {QUESTION: txt('가위, 바위, 보!')}, sig: 'ask(가위, 바위, 보!)', label: '‘가위, 바위, 보!’라고 묻고 기다리기'}),
                setVar('선택', act('operator_random', {inputs: {FROM: num(1), TO: num(3)}, sig: 'random1-3', label: '1부터 3 사이의 난수'})),
                {t: 'ifelse', noLoop: true, cond: eq(1), body: [sayOf('가위!', 2, '가위!')], body2: [{t: 'ifelse', noLoop: true, cond: eq(2), body: [sayOf('바위!', 2, '바위!')], body2: [sayOf('보!', 2, '보!')]}]}]};
    });
    def('gravityJump', 'motion', 'multi', '중력(?:을|이)?\\s*(?:넣|있게|주|적용)[가-힣]*(?:[^.!?]*점프[가-힣]*)?', () => ({notes: ['중력: 바닥(y -120)보다 위에 있으면 계속 조금씩 떨어져요. 스페이스를 누르면 위로 뛰어요.'],
        multi: [{hat: act('event_whenflagclicked', {sig: 'flag', label: '깃발을 클릭했을 때'}), body: [{t: 'forever', body: [{t: 'if', noLoop: true, cond: act('operator_gt', {inputs: {OPERAND1: inT(act('motion_yposition', {sig: 'ypos', label: 'y 좌표'})), OPERAND2: txt('-120')}, sig: 'y>-120', label: 'y 좌표 > -120'}),
            body: [act('motion_changeyby', {inputs: {DY: num(-5)}, sig: 'y-5', label: 'y 좌표를 -5만큼 바꾸기'})]}]}]},
            {hat: act('event_whenkeypressed', {fields: fld('KEY_OPTION', 'space'), sig: 'key:space', label: '스페이스 키를 눌렀을 때'}), body: [{t: 'repeat', times: 10, body: [act('motion_changeyby', {inputs: {DY: num(12)}, sig: 'y+12', label: 'y 좌표를 12만큼 바꾸기'})]}]}]}));
    def('rFlashHold', 'robot', 'act', '(?:버튼|터치\\s*센서)(?:을|를)?\\s*(?:누르고\\s*있을\\s*때(?:만)?|누르는\\s*동안(?:만)?|누르고\\s*있으면)\\s*(?:불|불빛|빛)(?:을|이)?\\s*(?:켜|켜기|켜지|켜줘)[가-힣]*', () => ({
        notes: ['누르고 있는 동안만 켜지도록 ‘아니면’ 칸에 불 끄기를 넣었어요(손전등).'], nodes: [{t: 'forever', body: [{t: 'ifelse', noLoop: true, cond: button(), body: [colorOn('#ffffff')], body2: [colorOff()]}, waitN(0.1)]}]}));
    def('rLedBlinkImg', 'robot', 'act', `(?<img>${RE_IMG})\\s*(?:얼굴|모양|그림)?(?:이|을|를)?\\s*(?:깜빡\\s*깜빡|깜빡|깜박|반짝\\s*반짝|반짝)[가-힣]*(?:\\s*거리[가-힣]*)?`, g => {
        const hit = LED_IMG.find(e => e[0].test(g.img)) || LED_IMG[11];
        return {notes: ['그림 보여주기 → 0.5초 → 지우기 → 0.5초를 반복해요.'], nodes: [{t: 'forever', body: [act('cubroidled_lceImgControl', {fields: fld('MATRIX', hit[2]), sig: `led:${hit[1]}`, label: `LED에 ‘${hit[1]}’ 그림 보여주기`}), waitN(0.5),
            act('cubroidled_ledAllClearControl', {sig: 'led:clear', label: 'LED 화면 지우기'}), waitN(0.5)]}]};
    });
    def('rNoteSec', 'robot', 'act', '(?:^|\\s)(?<v>도|레|미|파|솔|라|시)\\s*(?:음|소리)?(?:을|를)?\\s*(?<b>\\d+(?:\\.\\d+)?)\\s*(?:초|박자?)\\s*(?:동안)?\\s*(?:내|연주|울려|쳐)[가-힣]*', g => ({nodes: [noteBlock(SOLFA[g.v], Number(g.b))]}));
    def('rColorPressCycle', 'robot', 'act', `(?:버튼|터치\\s*센서)(?:을|를)?\\s*누를\\s*때\\s*마다\\s*(?:불|불빛)?(?:의)?\\s*(?:색(?:깔)?)?(?:이|을)?\\s*바뀌[가-힣]*(?<list>(?:\\s*(?:${RE_COLOR})(?:색)?){2,6})?(?:\\s*(?:순서로|순서대로|차례로))?`, g => {
        const cols = g.list ? (g.list.match(new RegExp(RE_COLOR, 'g')) || []) : ['빨강', '초록', '파랑'];
        const notBtn = act('operator_not', {boolIn: {OPERAND: button()}, sig: 'not:button', label: '버튼이 눌러지지 않았는가?'});
        return {notes: ['누를 때마다 다음 색으로 바뀌어요(손을 뗄 때까지 기다려서 한 번에 한 색씩).'],
            nodes: [{t: 'forever', body: [].concat(...cols.map(c => [act('control_wait_until', {boolIn: {CONDITION: button()}, sig: 'until:button', label: '‘버튼이 눌러졌는가?’ 까지 기다리기'}),
                colorOn(COLOR_HEX[c] || '#ffffff'), act('control_wait_until', {boolIn: {CONDITION: clone(notBtn)}, sig: 'until:not:button', label: '‘버튼이 눌러지지 않았는가?’ 까지 기다리기'})]))}]};
    });
    def('rTwoSpeeds', 'robot', 'act', '(?<s1>오른쪽|왼쪽)\\s*(?:모터|바퀴)\\s*(?<n1>\\d+)\\s*(?:%|퍼센트)?\\s*(?:으로|로)?\\s*,?\\s*(?<s2>오른쪽|왼쪽)\\s*(?:모터|바퀴)\\s*(?<n2>\\d+)\\s*(?:%|퍼센트)?\\s*(?:으로|로)?[가-힣]*', g => {
        const L = g.s1 === '왼쪽' ? toNum(g.n1) : toNum(g.n2);
        const R = g.s1 === '오른쪽' ? toNum(g.n1) : toNum(g.n2);
        return {notes: ['왼쪽 바퀴 = 1번 모터, 오른쪽 바퀴 = 2번 모터로 봤어요. 속도가 다르면 느린 쪽으로 휘어져 가요.'],
            nodes: [act(`${M1}_dcMotorSetSpeed`, {inputs: {SPEED: num(L)}, sig: `m1:speed${L}`, label: `DC 모터 1번 속도를 ${L}%로 정하기`}), act(`${M2}_dcMotorSetSpeed`, {inputs: {SPEED: num(R)}, sig: `m2:speed${R}`, label: `DC 모터 2번 속도를 ${R}%로 정하기`})].concat(drive('forward', 2, true))};
    });
    def('rRamp', 'robot', 'act', '(?:천천히|느리게)\\s*(?:출발|시작)(?:해서|하고|하다가|했다가|했다|하고\\s*나서)?\\s*(?:점점|조금씩|서서히)\\s*(?:빨라|빠르게)[가-힣]*|점점\\s*빨라지게\\s*(?:달려|가|출발)[가-힣]*', () => ({
        notes: ['‘속도’ 변수를 30에서 10씩 올리며 모터 속도를 바꿔요(0.3초마다).'],
        nodes: [setVar('속도', 30)].concat(drive('forward', null, false), [{t: 'repeat', times: 8, body: [
            act(`${M1}_dcMotorSetSpeed`, {inputs: {SPEED: inN(V('속도'))}, sig: 'm1:speed(속도)', label: 'DC 모터 1번 속도를 (속도)%로 정하기'}),
            act(`${M2}_dcMotorSetSpeed`, {inputs: {SPEED: inN(V('속도'))}, sig: 'm2:speed(속도)', label: 'DC 모터 2번 속도를 (속도)%로 정하기'}), chVar('속도', 10), waitN(0.3)]}], stopBoth())}));
    def('rMaze', 'robot', 'act', '(?:로봇(?:으로|이)?\\s*)미로(?:를)?\\s*(?:탈출|빠져나가|통과|풀)[가-힣]*', () => ({notes: ['로봇은 미로 지도를 모르니, 벽을 만나면 오른쪽으로 돌고 없으면 앞으로 가는 규칙(자율주행)으로 탈출해 봐요.'],
        nodes: [{t: 'forever', body: [{t: 'ifelse', cond: obstacle(), noLoop: true, body: drive('right', 0.6, false), body2: drive('forward', 0.2, false)}]}]}));
    def('glideEdgeBack', 'motion', 'act', '(?:끝에|끝까지)\\s*(?:가면|가서|닿으면)\\s*(?:다시\\s*)?(?<d>왼쪽|오른쪽)(?:으로|\\s*끝으로)?\\s*(?:오게|가게|와|돌아와|돌아오|가)[가-힣]*', g => {
        const x = g.d === '왼쪽' ? -200 : 200;
        return {nodes: [act('motion_glidesecstoxy', {inputs: {SECS: num(1), X: num(x), Y: inN(act('motion_yposition', {sig: 'ypos', label: 'y 좌표'}))}, sig: `glide1:${x},y`, label: `1초 동안 ${g.d} 끝으로 이동하기`})]};
    });
    def('rLedClear2', 'robot', 'act', `(?:${RE_IMG}|얼굴|그림|숫자|글자)(?:를|을)?\\s*(?:지워|지우|꺼|없애)[가-힣]*`, (g, ctx) => (ctx.robot || ctx.ledCtx ? {nodes: [act('cubroidled_ledAllClearControl', {sig: 'led:clear', label: 'LED 화면 지우기'})]} : null));
    // ═══ 14차: 연습 8차 실패에서 ═══
    // "신호 받으면", "신호 보내면 춤추게" — 이름 없는 신호
    def('recvBare', 'events', 'hat', '(?:^|\\s)(?:신호|메시지)(?:를|을|가|이)?\\s*(?:받으면|받았을\\s*때|받을\\s*때|오면|왔을\\s*때|보내면)(?:마다)?',
        () => ({notes: ['‘신호’ 신호를 받았을 때 실행돼요. 다른 곳에서 ‘신호 보내기’를 해 주세요.'],
            hat: act('event_whenbroadcastreceived', {fields: {BROADCAST_OPTION: {name: 'BROADCAST_OPTION', value: '신호', bcast: '신호'}}, sig: 'recv:신호', label: '신호 신호를 받았을 때'})}));
    // "마우스 클릭한 곳으로 가", "무대 클릭하면 거기로 미끄러지듯" — 스프라이트는 무대 클릭을 못 받으니 마우스 누름을 기다린다
    def('clickGoTo', 'motion', 'act', '(?:무대(?:를)?\\s*(?:클릭하면|누르면|클릭할\\s*때(?:마다)?)\\s*(?:고양이(?:가)?\\s*)?(?:거기로|그곳으로|그\\s*곳으로|클릭한\\s*(?:곳|데)으로)|(?:마우스(?:로|를)?\\s*)?클릭한\\s*(?:곳|데|자리)(?:으로|로))\\s*(?:고양이(?:가)?\\s*)?(?<gl>미끄러지듯|미끄러져|스르륵)?\\s*(?:가|이동|옮겨|와|오|가게|이동하게)?[가-힣]*', g => ({
        notes: ['무대를 클릭한 곳은 ‘마우스를 클릭했는가?’를 계속 확인해서 알아내요(스프라이트는 ‘무대를 클릭했을 때’를 받지 못해요).'],
        nodes: [{t: 'forever', body: [act('control_wait_until', {boolIn: {CONDITION: act('sensing_mousedown', {sig: 'mousedown', label: '마우스를 클릭했는가?'})}, sig: 'until:mousedown', label: '‘마우스를 클릭했는가?’ 까지 기다리기'}),
            act('motion_glideto', {inputs: {SECS: num(0.5), TO: menu('motion_glideto_menu', 'TO', '_mouse_')}, sig: 'glide0.5:mouse', label: '0.5초 동안 마우스 포인터로 이동하기'})]}]}));
    // "동그라미 그리면서 돌게"
    def('circleDraw', 'pen', 'act', '(?:동그라미|원)(?:를|을)?\\s*그리(?:면서|며)\\s*(?:돌|움직|가|다니)[가-힣]*', () => ({notes: ['펜을 내리고 10만큼 움직이며 10도씩 36번 돌면 동그라미가 그려져요.'],
        nodes: [penClear(), penDown(), {t: 'repeat', times: 36, body: [act('motion_movesteps', {inputs: {STEPS: num(10)}, sig: 'move10', label: '10만큼 움직이기'}),
            act('motion_turnright', {inputs: {DEGREES: num(10)}, sig: 'right10', label: '오른쪽으로 10도 돌기'})]}, penUp()]}));
    // "안녕 나는 고양이 잘가 순서대로 말하게"
    def('saySeq', 'looks', 'act', '(?<list>[가-힣A-Za-z0-9!?~ ,]{2,40}?)\\s*(?:을|를)?\\s*(?:순서대로|차례대로|차례로|하나씩)\\s*(?:말|외치|얘기)[가-힣]*', g => {
        const raw = g.list.trim().replace(/^(?:고양이(?:가|는)?\s*)/, '');
        let parts = raw.split(/\s*,\s*/).filter(Boolean);
        if (parts.length < 2) {
            const w = raw.split(/\s+/);
            parts = [];
            w.forEach(x => { if (parts.length && /(?:는|은|를|을|도)$/.test(parts[parts.length - 1]) && !/[!?.]$/.test(parts[parts.length - 1])) parts[parts.length - 1] += ` ${x}`; else parts.push(x); });
        }
        if (parts.length < 2) return null;
        return {notes: ['말마다 2초씩 차례로 말해요.'], nodes: parts.map(x => sayOf(x, 2, x))};
    });
    // "말풍선 없애줘"
    def('bubbleClear', 'looks', 'act', '말풍선(?:을|를|도)?\\s*(?:없애|지워|치워|꺼|안\\s*보이게|사라지게)[가-힣]*', () => ({notes: ['빈 말을 말하면 말풍선이 없어져요.'],
        nodes: [act('looks_say', {inputs: {MESSAGE: txt('')}, sig: 'say()', label: '‘’ 말하기(말풍선 지우기)'})]}));
    // "우는 소리 날때까지 기다렸다 말하기"
    def('saySoundWait', 'sound', 'act', `(?:(?<nm>${names})|우는|고양이)?\\s*소리(?:가|를)?\\s*(?:다\\s*)?(?:날|끝날|나올|다\\s*날)\\s*때\\s*까지\\s*(?:기다렸다(?:가)?|기다리고|기다린\\s*(?:다음|뒤)(?:에)?)`, g => {
        const p = pickSound(g.nm);
        if (!p.name) return {notes: p.notes, nodes: []};
        return {nodes: [act('sound_playuntildone', {inputs: {SOUND_MENU: menu('sound_sounds_menu', 'SOUND_MENU', p.name)}, sig: `soundwait(${p.name})`, label: `${p.name} 끝까지 재생하기`})]};
    });
    // "처음으로 돌아가게" (위치)
    def('backToStart', 'motion', 'act', '처음(?:으로|\\s*자리로|\\s*위치로|\\s*자리에)\\s*(?:돌아가|돌아와|가|보내|되돌아가)[가-힣]*', (g, ctx) => (ctx.robot ? null : {nodes: [act('motion_gotoxy', {inputs: {X: num(0), Y: num(0)}, sig: 'goto0,0', label: 'x: 0 y: 0 으로 이동하기'})]}));
    // 로봇 "제자리에서 빙글빙글"
    def('rSpinForever', 'robot', 'act', `${CAR_WORD}(?:제자리(?:에서)?\\s*)?(?:빙글\\s*빙글|빙빙|뱅글\\s*뱅글)(?:\\s*(?:돌|회전)[가-힣]*)?(?=\\s*$|\\s*[,.!]|\\s+(?:계속|해|돌))`, (g, ctx) => (ctx.robot ? {
        notes: ['두 모터를 같은 방향으로 켜 두면 제자리에서 계속 돌아요. 무대 위 빨간 정지 버튼으로 멈춰요.', NOTE_SPIN], nodes: drive('right', null, false)} : null));
    // "스페이스 누르면 로봇 앞으로 떼면 멈춤"
    def('rHoldKeyKo', 'robot', 'act', `${RE_KEY}\\s*(?:키|버튼)?(?:를|을)?\\s*(?:누르면|누르고\\s*있으면|누르는\\s*동안)\\s*(?:로봇(?:이)?\\s*|자동차(?:가)?\\s*)?(?<dir>앞으로|뒤로|전진|후진)[가-힣]*\\s*(?:가고\\s*|가다가\\s*)?(?:떼면|놓으면|안\\s*누르면)\\s*(?:멈|정지|서)[가-힣]*`, g => {
        const k = keyValue(g.key) || 'space';
        const cond = act('sensing_keypressed', {inputs: {KEY_OPTION: menu('sensing_keyoptions', 'KEY_OPTION', k)}, sig: `keyheld:${k}`, label: `${keyKo(k)} 키를 눌렀는가?`});
        const back = /뒤|후진/.test(g.dir);
        return {notes: ['키를 계속 확인해서, 누르고 있으면 가고 떼면 멈춰요.'], nodes: [{t: 'forever', body: [{t: 'ifelse', cond, noLoop: true, body: drive(back ? 'backward' : 'forward', null, false), body2: stopBoth()}]}]};
    });
    // "매트릭스에 HELLO 흘러가게"
    def('rLedWord', 'robot', 'act', `(?:${LED_WORD})(?:에|에다|으로|로)?\\s*(?<w>[A-Z]{2,8})\\s*(?:를|을|이|가)?\\s*(?:흘러가|지나가|보여|띄워|표시|써|나오)[가-힣]*`, g => {
        const nodes = [];
        g.w.split('').forEach(a => { if (LED_ABC[a]) nodes.push(act('cubroidled_lceAlpabetControl', {fields: fld('MATRIX', LED_ABC[a]), sig: `led:${a}`, label: `LED에 글자 ${a} 보여주기`}), waitN(0.5)); });
        return {notes: ['LED 판에 글자를 하나씩 0.5초마다 보여 줘서 흘러가는 것처럼 보여요.'], nodes};
    });
    // "LED 숫자 1부터 9까지"
    def('rLedRange', 'robot', 'act', `(?:${LED_WORD})(?:에|에다|으로|로)?\\s*(?:숫자\\s*)?(?<a>\\d)\\s*(?:부터|에서)\\s*(?<b>\\d)\\s*까지(?:\\s*(?:보여|세|띄워|표시|카운트)[가-힣]*)?`, g => {
        const a = Number(g.a);
        const b = Number(g.b);
        const nodes = [];
        for (let v = a; a <= b ? v <= b : v >= b; v += a <= b ? 1 : -1) nodes.push(act('cubroidled_lceNumberControl', {fields: fld('MATRIX', LED_NUM[String(v)]), sig: `led:${v}`, label: `LED에 숫자 ${v} 보여주기`}), waitN(1));
        return {notes: ['숫자마다 1초씩 보여줘요.'], nodes};
    });
    // "도난 경보기" — 조건을 따로 말하면 조건 쪽이, 아니면 통째로
    def('rAlarm', 'robot', 'act', '(?:도난\\s*)?경보기|방범\\s*장치|도둑\\s*잡는\\s*(?:장치|기계|로봇)?', (g, ctx) => (/면\s|면$|때/.test(ctx.src || '') ? {nodes: []} : {
        notes: ['누가 가까이 오면 빨강·파랑 불이 번갈아 깜빡이고 경고음이 나요. 아니면 불을 꺼요.'],
        nodes: [{t: 'forever', body: [{t: 'ifelse', cond: obstacle(), noLoop: true, body: [colorOn('#ff0000'), beep('경고'), waitN(0.3), colorOn('#0000ff'), waitN(0.3)], body2: [colorOff()]}]}]}));
    // "빨간불 깜빡이고"
    def('rBlinkColor', 'robot', 'act', `(?<c>${RE_COLOR})\\s*(?:색\\s*)?(?:불빛|불)(?:이|을|도)?\\s*(?:(?<n>${RE_SEC})\\s*초\\s*(?:간격으로|마다|씩)\\s*)?(?:깜빡\\s*깜빡|깜빡|깜박|반짝\\s*반짝|반짝)[가-힣]*`, (g, ctx) => {
        const sec = toNum(g.n) !== null ? toNum(g.n) : 0.3;
        const body = [colorOn(COLOR_HEX[g.c] || '#ff0000'), waitN(sec), colorOff(), waitN(sec)];
        return {notes: [`깜빡이기는 ‘켜기 → ${fmt(sec)}초 → 끄기 → ${fmt(sec)}초’를 반복해요.`], nodes: /면|때/.test(ctx.src || '') ? [{t: 'repeat', times: 3, body}] : [{t: 'forever', body}]};
    });
    // "도착 표시" — LED에 기쁜 얼굴
    def('rArrive', 'robot', 'act', `(?:${LED_WORD})?(?:에|에다)?\\s*(?:도착|완료|성공|골인)\\s*(?:표시|했다고|모양)[가-힣]*`, (g, ctx) => (ctx.robot || g.led ? {nodes: [act('cubroidled_lceImgControl', {fields: fld('MATRIX', LED_IMG[1][2]), sig: `led:${LED_IMG[1][1]}`, label: `LED에 ‘${LED_IMG[1][1]}’ 그림 보여주기`})]} : null));
    // ═══ 11차: 연습 7차 실패에서 ═══
    // "박수 치면", "소리가 크면" — 소리 크기 모자 블록(마이크)
    def('loudHat', 'events', 'hat', '(?:박수(?:를)?\\s*(?:치면|칠\\s*때|쳤을\\s*때)|(?:소리가|주변이)\\s*(?:크면|커지면|시끄러우면)|시끄러우면|큰\\s*소리가?\\s*(?:나면|들리면)|소리를?\\s*지르면)(?:마다)?',
        () => ({notes: ['‘음량 > 30일 때’ 모자 블록이에요. 컴퓨터 마이크를 써요(처음에 마이크 허용을 눌러 주세요). 30을 바꿔 민감도를 맞춰요.'],
            hat: act('event_whengreaterthan', {fields: fld('WHENGREATERTHANMENU', 'LOUDNESS'), inputs: {VALUE: num(30)}, sig: 'loud>30', label: '음량 > 30 일 때'})}));
    // "게임오버 되면 전부 멈춰" — 이름 있는 상황은 신호로
    def('stateHat', 'events', 'hat', '(?<nm>게임\\s*오버|게임오버|게임\\s*끝|게임이\\s*끝|패배|승리|클리어)(?:가|이)?\\s*(?:되면|나면|하면|이면|될\\s*때)',
        g => {
            const nm = g.nm.replace(/\s+/g, '').replace(/게임이끝/, '게임끝');
            return {notes: [`‘${nm}’ 신호를 받았을 때 실행돼요. 게임이 끝나는 곳에서 ‘${nm} 신호 보내기’를 해 주세요.`],
                hat: act('event_whenbroadcastreceived', {fields: {BROADCAST_OPTION: {name: 'BROADCAST_OPTION', value: nm, bcast: nm}}, sig: `recv:${nm}`, label: `${nm} 신호를 받았을 때`})};
        });
    // "키 누를때마다" — 어떤 키든
    def('anyKeyHat', 'events', 'hat', '(?:^|\\s)(?:아무\\s*)?키(?:보드)?(?:를|가)?\\s*(?:누를\\s*때|눌렀을\\s*때|누르면|눌리면)(?:마다)?',
        () => ({hat: act('event_whenkeypressed', {fields: fld('KEY_OPTION', 'any'), sig: 'key:any', label: '아무 키를 눌렀을 때'})}));
    // "오른쪽 끝까지 보내줘", "맨 위로 가"
    def('edgeGo', 'motion', 'act', '(?<p>오른쪽|왼쪽|위쪽|아래쪽|맨\\s*위|맨\\s*아래|맨\\s*오른쪽|맨\\s*왼쪽)\\s*(?:끝|가장자리|벽)?(?:까지|으로|로|에)\\s*(?:보내|가|이동|옮겨|옮기|가게)[가-힣]*', (g, ctx) => {
        if (ctx.robot) return null;
        const p = g.p.replace(/\s+/g, '');
        const [ax, v] = /오른/.test(p) ? ['x', 220] : /왼/.test(p) ? ['x', -220] : /위/.test(p) ? ['y', 160] : ['y', -160];
        if (!/끝|가장자리|벽|맨/.test(ctx.src || '')) return null;
        return {nodes: [act(ax === 'x' ? 'motion_setx' : 'motion_sety', {inputs: {[ax.toUpperCase()]: num(v)}, sig: `set${ax}=${v}`, label: `${ax} 좌표를 ${v}(으)로 정하기`})]};
    });
    // "위치 초기화"
    def('posReset', 'motion', 'act', '위치(?:를|도)?\\s*(?:초기화|리셋|처음으로|원래대로)[가-힣]*', () => ({nodes: [act('motion_gotoxy', {inputs: {X: num(0), Y: num(0)}, sig: 'goto0,0', label: 'x: 0 y: 0 으로 이동하기'})]}));
    // "고양이 방향 위쪽으로 바꿔"
    def('dirWord', 'motion', 'act', '방향(?:을|를)?\\s*(?<d>위쪽|위|아래쪽|아래|오른쪽|왼쪽)(?:으로|로)?\\s*(?:바꿔|바꾸|정해|해|보게|돌려)[가-힣]*', g => {
        const n = /위/.test(g.d) ? 0 : /아래/.test(g.d) ? 180 : /왼/.test(g.d) ? -90 : 90;
        return {nodes: [act('motion_pointindirection', {inputs: {DIRECTION: num(n, 'math_angle')}, sig: `dir${n}`, label: `${n}도 방향 보기`})]};
    });
    // "사각형 따라 걷는", "네모로 걸어" — 고양이가 네모를 따라 걷기(펜 없이)
    def('walkSquare', 'motion', 'act', '(?:정\\s*)?(?:사각형|네모)\\s*(?:모양)?(?:으로|로|을|를)?\\s*(?:따라(?:서)?\\s*)?(?:걷|걸어|돌|움직|이동|산책)[가-힣]*', (g, ctx) => (ctx.robot ? null : {
        notes: ['네 변을 걷고(10걸음씩 10번, 모양 바꾸며) 오른쪽으로 90도 도는 걸 4번 반복해요.'],
        nodes: [{t: 'repeat', times: 4, body: [{t: 'repeat', times: 10, body: [act('motion_movesteps', {inputs: {STEPS: num(10)}, sig: 'move10', label: '10만큼 움직이기'}),
            act('looks_nextcostume', {sig: 'nextcostume', label: '다음 모양으로 바꾸기'}), waitS(0.1)]},
        act('motion_turnright', {inputs: {DEGREES: num(90)}, sig: 'right90', label: '오른쪽으로 90도 돌기'})]}]}));
    // "뛰는거", "뛰게" = 점프
    def('jumpWord', 'motion', 'act', '(?:뛰는\\s*(?:거|것)|뛰게|뛰기|뛰어\\s*봐|뛰어)(?!\\s*(?:다니|가|와|오|서|놀))', (g, ctx) => (ctx.robot ? null : {notes: [NOTE.jump], nodes: [
        act('motion_changeyby', {inputs: {DY: num(50)}, sig: 'y+50', label: 'y 좌표를 50만큼 바꾸기', part: 'jumpUp'}), waitS(0.3),
        act('motion_changeyby', {inputs: {DY: num(-50)}, sig: 'y-50', label: 'y 좌표를 -50만큼 바꾸기', part: 'jumpDown'})]}));
    // "앞에 뭐 있으면 오른쪽으로 피해서 계속 가"
    def('rAvoidDir', 'robot', 'act', '(?:(?:앞에|앞쪽에)\\s*(?:뭐가?|무언가|뭔가|장애물이?|벽이?|물건이?)?\\s*(?:있으면|보이면|나타나면|감지되면)\\s*)?(?:(?<dir>오른쪽|왼쪽)(?:으로)?\\s*)?(?:피해서|피해|비켜서|비켜)\\s*(?:계속\\s*)?(?:가|달려|달리|주행|움직|다녀)[가-힣]*', (g, ctx) => {
        if (!ctx.robot && !/앞에|장애물|벽|센서/.test(ctx.src || '')) return null;
        if (/자율\s*주행|청소기/.test(ctx.src || '')) return {nodes: []};
        const d = /왼/.test(g.dir || '') ? 'left' : 'right';
        return {notes: [`장애물이 있으면 ${d === 'left' ? '왼쪽' : '오른쪽'}으로 돌고, 없으면 앞으로 가요(자율주행과 같은 규칙).`],
            nodes: [{t: 'forever', body: [{t: 'ifelse', cond: obstacle(), noLoop: true, body: drive(d, 0.6, false), body2: drive('forward', 0.2, false)}]}]};
    });
    // 조건 뒤의 "켜기" — "어두우면 켜기"
    def('rOnBare', 'robot', 'act', '(?:^|\\s)(?:켜기|켜줘|켜|켜고|켜지게)(?=\\s*$|\\s*[,.!])', (g, ctx) => (/불|빛|램프|밝|어두/.test(ctx.src || '') ? {nodes: [colorOn('#ffffff')]} : null));
    // "목소리로 안녕하세요 읽어줘" — 말하기 + 안내(텍스트 음성 변환 확장)
    def('readAloud', 'looks', 'act', '(?:목소리로|소리\\s*내서|큰\\s*소리로)\\s*(?<m>[^\\s].{0,30}?)\\s*(?:을|를)?\\s*(?:읽어|말해|읽기|읽게)[가-힣]*', g => ({
        notes: ['진짜 목소리로 읽으려면 확장 추가(왼쪽 아래)에서 ‘텍스트 음성 변환(TTS)’을 추가해 ‘말하기’ 블록을 쓰세요. 우선 말풍선으로 보여 줘요.'],
        nodes: [sayOf(g.m.trim(), 2, g.m.trim())]}));
    def('varDecl', 'variables', 'act', '(?:(?<v>점수|목숨|생명|체력|레벨|코인|돈|시간|횟수|속도|스코어)\\s*)?변수(?:를|도)?\\s*(?:하나\\s*)?(?:만들|만들어|만들고|추가)[가-힣]*',
        g => (g.v ? {notes: [`‘${g.v === '스코어' ? '점수' : g.v}’ 변수를 만들고 처음 값을 0으로 정했어요. 변수 칸에서 체크하면 무대에 보여요.`], nodes: [setVar(g.v === '스코어' ? '점수' : g.v, 0)]} :
            {notes: ['변수는 필요할 때 번역기가 알아서 만들어요. 변수 칸에서 확인해 보세요.'], nodes: []}));

    EXTRA_INTENTS.ko.forEach(f => f(def));
    return I;
};

// ─────────────────────────────── 언어 꾸러미 ───────────────────────────────
// 번역 과정(후보 고르기·감싸기·반복 넣기·블록 조립)은 언어와 상관없다. 언어마다 다른 것 = 의도 사전·군말·알림 문구·표시 이름.
// 한국어는 이 파일, 영어는 vibe-en.js 가 registerLang 으로 등록한다(순환 import 를 피하려고).
const LANGS = {};
let CUR = null; // 지금 번역 중인 언어(translate 가 동기라 안전)
const registerLang = (id, pack) => { LANGS[id] = pack; };
const langOf = ir => (ir && ir.lang && LANGS[ir.lang]) || LANGS.ko;

// ─────────────────────────────── 문장 나누기 ───────────────────────────────
// 마침표·물음표·느낌표·줄바꿈에서 나눈다. 단, 소수점(0.5)과 따옴표 안("게임 시작!")은 나누지 않는다.
const splitSentences = src => {
    const out = [];
    let s = 0;
    let inQ = false;
    for (let i = 0; i <= src.length; i++) {
        const ch = src[i];
        const apos = (ch === '\'' || ch === '’') && /[A-Za-z]/.test(src[i - 1] || '') && /[A-Za-z]/.test(src[i + 1] || '');
        if (ch && '\'"“”‘’'.indexOf(ch) >= 0 && !apos) inQ = !inQ;
        // 소수점(0.5)·말줄임표(음...)는 문장 끝이 아니다 / "안녕 -> 기다리기 1초 -> …" 의 화살표는 문장 경계
        const dot = ch === '.' && !(/\d/.test(src[i - 1] || '') && /\d/.test(src[i + 1] || '')) && src[i + 1] !== '.' && src[i - 1] !== '.';
        if (!inQ && ((ch === '-' && src[i + 1] === '>') || ch === '→')) {
            if (i > s) out.push({start: s, text: src.slice(s, i)});
            s = i + (ch === '-' ? 2 : 1);
            if (ch === '-') i++;
            continue;
        }
        // "아야! 라고 말해", "음... 이라고" — 뒤에 "라고/하고"가 오면 할 말의 일부다
        const quoteTail = ((CUR || LANGS.ko).quoteTail).test(src.slice(i + 1));
        const glued = /[가-힣A-Za-z0-9]/.test(src[i + 1] || '') && /[.!?]/.test(ch || ''); // "안녕!을 2초 동안"
        const end = i === src.length || ch === '\n' || (!inQ && !quoteTail && !glued && (dot || ch === '!' || ch === '?' || ch === '。'));
        if (end) {
            if (i > s) out.push({start: s, text: src.slice(s, i)});
            s = i + 1;
            if (ch === '\n') inQ = false;
        }
    }
    return out;
};

// ─────────────────────────────── 남은 말 정리 ───────────────────────────────
// 의도에 안 걸린 부분 중 "주어·접속어·말끝"은 버리고, 뜻이 있는 말만 "못 알아들은 말"로 남긴다.
const FILLER = [
    /^(?:무대|화면|고양이|냥이|냥|스프라이트|캐릭터|로봇|로봇카|로봇ㅇ|얘|걔|쟤|너|나|내가|제가|저|그거|이거|이것|그것|주인공|친구|애|우리|선생님|쌤|자동차|차|모터|초기화)(?:가|는|이|은|를|을|도|야|아|랑|한테|에게)?$/,
    /^(?:그리고|그다음(?:에)?|다음에|다음|그런다음(?:에)?|그러고|그러고나서|그리고나서|나서|후에|뒤에|그후(?:에)?|그뒤(?:에)?|또|또한|그러면|그럼|그래서|그러다가|그런데|동시에|같이|하고|고|면서|하면서|해서|그러면서)$/,
    /^(?:하게|되게|하도록|되도록|해|해요|줘|줘요|요|주세요|만들어|좀|하기|게|기|도록|할래|하자|합니다|해라|해봐|제발|부탁해|싶어|시켜|시켜줘|시켜주세요|해주세여|해주세욤|해조|해죠|해줘여|해줘용|해주셈|해주라|해줭|하게요|해줄래|않게|말고|가만히|서있게|서있어|ㅎ+|ㅋ+)+$/,
    /^(?:줄어드는거|줄어드는|줄어들게|근데|다시|그냥|막|진짜|시연용으로|시연용|예제|예시|나왔다가|바뀌게|바뀌도록|부저로|버저로|넣어줘|넣어|넣어주세요|추가해줘|좋겠어|좋겠다|좋겠어요|예시용으로|예시로|코드|학생들|수업용|수업|통째로|전부|ㄱㄱ|고고|이것도|이거도|해줘봐|앞에|앞에서|제자리에서|제자리서|그자리에서|또다시|이제|만약|만약에|대답하면|들으면|거기서|매번|차례대로|각각|사이사이|피하고|대답|통통|\d+초씩|말풍선에|말풍선으로|숨겨진|숨어있는|어떻게|어케|어떡해|나오게|나오도록|보세요|볼래요|해볼래요|어케|써요|적당히|이상해|이상함|싫어|볼게요|해쥬|해줘요|주세욤|이어서|추가로|거기에|것도|거도|것까지|것도요|있어|있어요|하는거|되나|돼|가능|가능해|안됨|안돼|되나요|그걸|그거|이런거|이렇게|좀더|좀|수있어|할수있어|맞지)$/,
    /^\S*(?:처럼|같이)$/ // 꾸며 주는 말("무지개처럼")은 블록이 아니다
];
// 한국어 꾸러미
const koSkip = (c0, o) => {
    const {after, before, self} = o;
    // 꾸밈말 안의 동사는 명령이 아니다: "걷는 것처럼 보이게" 의 "걷는", "보이게"
        if (!c0.it.simile && (/^\s*(?:것\s*처럼|것\s*같이|듯이|듯|척)/.test(after) || /(?:것|거)\s*(?:처럼|같이)$/.test(self) ||
            (/(?:처럼|같이)\s*$/.test(before) && /^(?:보이|보여|느껴)/.test(self)))) return true;
        // "원이 너무 작아 더 크게" — '너무 ~'는 불평이지 명령이 아니다
        if (/너무\s*$/.test(before)) return true;
        // "게임오버 라고 말해", "게임오버 말하고" — 인용 안의 말은 명령이 아니다(할 말이다)
        if (!c0.it.left && (/^\s*(?:이?라고|다고)(?:\s|$)/.test(after) || (/^\s*(?:말하|말해|외치|외쳐)/.test(after) && c0.it.kind === 'act' && !/^(?:say|greet|sayAnswer)/.test(c0.it.id)))) return true;
        // "움직이지 않게", "안 움직이게" — 부정은 명령이 아니다
        if (!c0.it.negOk && ((/지(?:는|도|만)?$/.test(self) && /^\s*(?:않|말|마세|마요|마$|마\s)/.test(after)) || /지\s*(?:말고|마|않)/.test(self) || /(?:^|\s)안\s*$/.test(before))) return true;
    return false;
};
LANGS.ko = {
    id: 'ko',
    fix: s => fixKo(s),
    build: soundNames => buildIntents(soundNames),
    filler: FILLER,
    simile: /\S+\s*것\s*(?:처럼|같이)(?:\s*(?:보이게|보이도록|보여|느껴지게))?/g,
    quoteTail: /^[.!?…]*\s*(?:이?라고|이?라면|하고|하며|하기|하게|말하|말해|물어|묻|질문)/,
    firstPerson: /^(?:나는|내가|제가|저는|난|전)$/,
    robotWords: RE_ROBOT,
    catWords: /고양이|냥이|스프라이트|캐릭터|무대|화면|좌표/,
    skip: koSkip,
    unknown: t => `‘${t}’${hasBatchim(t) ? '은' : '는'} 아직 모르는 말이에요. 이 부분은 직접 블록으로 만들어 볼래요?`,
    isUnknown: n => (n.indexOf('아직 모르는 말') >= 0 ? 1 : 0),
    hatEmpty: hat => `‘${hat.label}’ 다음에 무엇을 할지 말해 주세요.`,
    comment: t => `아직 모르는 말: ‘${t}’\n이 부분은 직접 만들어 볼래요?`,
    varMade: n => `‘${n}’ 변수를 만들었어요. 변수 칸에서 체크하면 무대에 보여요.`,
    listMade: n => `‘${n}’ 리스트를 만들었어요.`,
    extAdded: id => (AI_EXT_IDS.test(id) ? `‘${EXT_KO[id] || id}’ 확장을 추가했어요.` : `‘${EXT_KO[id] || id}’ 블록을 추가했어요. 로봇을 연결해야 실제로 움직여요.`),
    wrap: {repeat: n => `${n}번 반복하기`, until: c => `‘${c}’ 까지 반복하기`, forever: () => '무한 반복하기', if: c => `만약 ‘${c}’ 라면`, else: () => '아니면'},
    label: null
};
// "X라고 말해" 의 X 잘라 내기 — 앞쪽의 주어·접속어("고양이가", "그리고")는 빼고, 말 자체는 그대로 둔다
const leadingTrim = (src, a, b) => {
    const seg = src.slice(a, b);
    const re = /\S+/g;
    let m;
    let from = null;
    while ((m = re.exec(seg)) !== null) {
        const tok = m[0].replace(/^[,.!?~…·;:]+/, '');
        // "나는 고양이다 라고" — 1인칭은 할 말의 일부다(지우지 않는다)
        const LXf = CUR || LANGS.ko;
        const firstPerson = LXf.firstPerson.test(tok);
        if (tok && (firstPerson || !LXf.filler.some(f => f.test(tok)))) { from = m.index + (m[0].length - tok.length); break; }
    }
    if (from === null) return null;
    const text = seg.slice(from).replace(/\s+$/, '');
    return text ? {start: a + from, text} : null;
};
const leftovers = (src, a, b) => {
    // "걷는 것처럼 보이게" 같은 꾸밈말은 통째로 지운다(길이는 그대로 → 위치 계산 안 깨짐)
    const LXf = CUR || LANGS.ko;
    const seg = LXf.simile ? src.slice(a, b).replace(LXf.simile, x => ' '.repeat(x.length)) : src.slice(a, b);
    const re = /[^\s,.!?~…·;:'"“”‘’()[\]]+/g;
    const chunks = [];
    let cur = null;
    let m;
    while ((m = re.exec(seg)) !== null) {
        const tok = m[0];
        const meaningful = !LXf.filler.some(f => f.test(tok.toLowerCase ? tok.toLowerCase() : tok)) && tok.replace(/[^가-힣a-zA-Z0-9]/g, '').length >= 2;
        if (meaningful) {
            const st = a + m.index;
            const en = st + tok.length;
            if (cur) { cur.end = en; cur.text = src.slice(cur.start, en); } else { cur = {start: st, end: en, text: tok}; }
        } else if (cur) { chunks.push(cur); cur = null; }
    }
    if (cur) chunks.push(cur);
    return chunks;
};

// ─────────────────────────────── 구조 만들기 ───────────────────────────────
const wrap = (el, inner) => {
    if (el.kind === 'repeat') return el.times === 1 ? inner : [{t: 'repeat', times: el.times, body: inner}];
    if (el.kind === 'forever') return [{t: 'forever', body: inner}];
    if (el.kind === 'until') return [{t: 'until', cond: el.cond, body: inner}];
    return [{t: 'if', cond: el.cond, body: inner}];
};
// seq: [{kind:'act',nodes}|{kind:'cond',cond}|{kind:'repeat',times}|{kind:'forever'}]
//  - "N번 / 계속" 은 뒤따르는 말 전부를 감싼다. 뒤에 아무것도 없으면 앞의 말을 감싼다("움직이기를 5번 반복해").
//  - "~하면" 은 다음 "~하면" 전까지를 감싼다.
const nest = (seqIn, note) => {
    let seq = seqIn;
    const out = [];
    for (let i = 0; i < seq.length; i++) {
        const el = seq[i];
        if (el.kind === 'act') { out.push(...el.nodes); continue; }
        if (el.kind === 'repeat' || el.kind === 'forever' || el.kind === 'until') {
            let j = i + 1;
            // "~까지 … 반복하기" — 뒤에 붙은 '반복하기'는 이미 ~까지 반복에 들어 있다
            if (el.kind === 'until') seq = seq.filter((x, k) => k <= i || x.kind !== 'forever');
            while (el.kind === 'forever' && j < seq.length && seq[j].kind === 'forever') j++;
            const rest = seq.slice(j);
            // "모양 바꾸기 다섯 번 0.5초마다" — 뒤에 '~마다 기다리기'만 있으면 앞의 동작과 함께 감싼다
            const everyOnly = rest.length && rest.every(x => x.kind === 'act' && x.nodes.every(n => n.every));
            if (everyOnly && out.length) {
                const inner = out.splice(0, out.length).concat(nest(rest, note));
                out.push(...wrap(el, inner));
            } else if (rest.some(x => x.kind === 'act' || x.kind === 'cond')) {
                out.push(...wrap(el, nest(rest, note)));
            } else if (out.length) {
                const inner = out.splice(0, out.length);
                out.push(...wrap(el, inner));
            } else {
                note('무엇을 반복할지 말해 주세요. 예: ‘10번 반복해서 움직여’');
            }
            return out;
        }
        if (el.kind === 'else') {
            // "~하면 A 아니면 B" — 바로 앞의 '만약'을 '만약~아니면'으로 바꾼다
            let k = i + 1;
            while (k < seq.length && seq[k].kind !== 'cond' && seq[k].kind !== 'else') k++;
            const inner2 = nest(seq.slice(i + 1, k), note);
            const prevIf = out[out.length - 1];
            if (prevIf && prevIf.t === 'if' && !prevIf.body2) {
                if (inner2.length) { prevIf.t = 'ifelse'; prevIf.body2 = inner2; }
            } else {
                note('‘아니면’ 앞에 ‘~하면’이 있어야 해요. 예: ‘장애물이 있으면 돌고 아니면 앞으로 가’');
                out.push(...inner2);
            }
            i = k - 1;
            continue;
        }
        // cond
        let j = i + 1;
        while (j < seq.length && seq[j].kind !== 'cond' && seq[j].kind !== 'else') j++;
        const inner = nest(seq.slice(i + 1, j), note);
        if (inner.length) out.push(...wrap(el, inner));
        else note(`‘${el.cond.label}’ 다음에 무엇을 할지 말해 주세요.`);
        i = j - 1;
    }
    return out;
};

// "야옹 3번 사이사이 0.5초 쉬고" — 반복 바로 뒤의 '사이' 기다리기는 반복 안 끝으로
const moveGapWaits = list => {
    for (let i = 1; i < list.length; i++) {
        const n = list[i];
        const p = list[i - 1];
        if (n.gap && (p.t === 'repeat' || p.t === 'forever')) { p.body.push(n); list.splice(i, 1); i--; }
    }
    list.forEach(n => { if (n.body) moveGapWaits(n.body); if (n.body2) moveGapWaits(n.body2); });
};
// 로봇 "장애물 있으면 멈춰/돌아" 만 있으면 없을 때 안 움직인다 → '아니면 앞으로'(말한 것에 '다시 직진·가다가·없으면'이 있을 때, 또는 멈추기만 있을 때)
const hasDrive = list => { let f = false; walk(list, x => { if (/_dcMotorControl$/.test(x.opcode || '')) f = true; }); return f; };
const robotElse = (list, src, note, root) => list.forEach(n => {
    if (n.body) robotElse(n.body, src, note, root || list);
    // "가다가 … 있으면 A 없으면 B" — '아니면'에 모터가 없으면 앞으로 가기를 붙인다
    if (n.t === 'ifelse' && n.cond && n.cond.sig === 'obstacle' && /가다가|직진|다니|달리|주행/.test(src) && !n.body2.some(x => /_dcMotorControl$/.test(x.opcode || ''))) {
        n.body2.push(act('cubroiddcmotor01_dcMotorControl', {fields: fld('INDEX', 'Right'), sig: 'm1:Right', label: 'DC 모터 1번을 오른쪽으로 돌리기'}),
            act('cubroiddcmotor02_dcMotorControl', {fields: fld('INDEX', 'Left'), sig: 'm2:Left', label: 'DC 모터 2번을 왼쪽으로 돌리기'}));
        note('‘가다가’는 장애물이 없을 때 계속 가는 거라서 ‘아니면’ 칸에 앞으로 가기를 넣었어요.');
        return;
    }
    if (n.t !== 'if' || !n.cond || n.cond.sig !== 'obstacle') return;
    const b = n.body;
    const last2 = b.slice(-2);
    const fwdAtEnd = last2.length === 2 && last2[0].sig === 'm1:Right' && last2[1].sig === 'm2:Left';
    const stopsOnly = b.length && b.every(x => /_dcMotorStop$|^cubroid(?:master|sound|led)/.test(x.opcode || '') || x.opcode === 'control_stop');
    // 모터를 켜는 블록이 어디에도 없으면 "멈춰"가 할 일이 없다 → 평소엔 앞으로 가게(연습 6차: '장애물 있으면 멈추게')
    const nothingDrives = stopsOnly && b.some(x => /_dcMotorStop$/.test(x.opcode || '') || x.opcode === 'control_stop') && !hasDrive(root || list);
    if (!(fwdAtEnd || nothingDrives || (stopsOnly && /가다가|직진|다니|달리|주행|피하/.test(src)) || /없으면\s*(?:앞으로|직진)|다시\s*(?:직진|앞으로)/.test(src))) return;
    const fwd = [act('cubroiddcmotor01_dcMotorControl', {fields: fld('INDEX', 'Right'), sig: 'm1:Right', label: 'DC 모터 1번을 오른쪽으로 돌리기'}),
        act('cubroiddcmotor02_dcMotorControl', {fields: fld('INDEX', 'Left'), sig: 'm2:Left', label: 'DC 모터 2번을 왼쪽으로 돌리기'})];
    if (fwdAtEnd) b.splice(b.length - 2, 2);
    n.body = b.map(x => (x.opcode === 'control_stop' ? act('cubroiddcmotor01_dcMotorStop', {sig: 'm1:off', label: 'DC 모터 1번을 끄기'}) : x));
    n.t = 'ifelse';
    n.body2 = fwd;
    if (nothingDrives) n.autoElse = true;
    note('장애물이 없을 때는 앞으로 가도록 ‘아니면’ 칸에 모터 켜기를 넣었어요(파이썬 예제 ‘자율주행’과 같은 모양).');
});
const needsLoop = n => ((n.t === 'if' || n.t === 'ifelse') && !n.noLoop && !/^answer/.test((n.cond && n.cond.sig) || '')) || ((n.t === 'act' || n.t === 'repeat') && n.loop);
// 같은 값으로 다시 정해도 결과가 같은 블록 — 연달아 두 번 나오면 하나로
const IDEMPOTENT = /^(?:motion_gotoxy|motion_goto|motion_pointindirection|looks_setsizeto|looks_seteffectto|looks_show|looks_hide|sound_setvolumeto|cubroidmaster_cubroid(?:MasterColor|ColorOff)Control|cubroiddcmotor0[12]_dcMotor(?:Stop|SetSpeed)|data_setvariableto|motion_setrotationstyle)$/;
// 모터 두 개 끄기가 연달아 두 번(“2초 가다가 멈춰”) → 한 번만
const dedupeStops = list => {
    for (let i = 0; i < list.length; i++) {
        const n = list[i];
        if (n.body) dedupeStops(n.body);
        if (n.body2) dedupeStops(n.body2);
        if (n.t === 'act' && /_dcMotorStop$/.test(n.opcode)) {
            const prevSame = list.slice(Math.max(0, i - 2), i).some(p => p.t === 'act' && p.sig === n.sig);
            if (prevSame) { list.splice(i, 1); i--; }
        }
    }
};
// 속도는 설정이다 → 스크립트 맨 앞으로(“천천히 가게 속도 30”)
const hoistSpeed = list => {
    const sp = list.filter(n => n.t === 'act' && /_dcMotorSetSpeed$/.test(n.opcode));
    if (!sp.length || list.indexOf(sp[0]) === 0) return;
    sp.forEach(n => list.splice(list.indexOf(n), 1));
    list.unshift(...sp);
};
// "~하면·따라가기·튕기기" 가 반복 밖에 있으면 한 번만 확인하고 끝나 버린다 → 무한 반복 안으로.
const autoLoop = (script, note) => {
    const b = script.body;
    const fi = b.findIndex(n => n.t === 'forever');
    let first = b.findIndex(needsLoop);
    if (first < 0 || (fi >= 0 && fi < first)) return;
    while (first > 0 && b[first - 1].withNext) first--; // "빙글빙글 돌면서 커졌다"

    if (fi >= 0) {
        // 뒤에 "계속" 이 있으면 그 안 맨 앞으로 옮긴다("벽에 닿으면 튕기고 계속 움직여")
        const moved = b.splice(first, fi - first);
        b[b.findIndex(n => n.t === 'forever')].body.unshift(...moved);
    } else {
        const moved = b.splice(first, b.length - first);
        b.push({t: 'forever', body: moved});
    }
    note(NOTE.loop);
};

// 말대로 두면 뜻과 달라지는 구조를 바로잡는다(바로잡을 때마다 이유를 알린다)
const tidyStructure = (list, note) => {
    for (let i = 0; i < list.length; i++) {
        const n = list[i];
        if (n.body) tidyStructure(n.body, note);
        if (n.body2) tidyStructure(n.body2, note);
        if (n.t === 'repeat' && n.body.length === 1 && n.body[0].cap) {
            list[i] = n.body[0];
            note('반복할 동작 없이 ‘멈추기’만 남아서 반복을 뺐어요. 무엇을 반복할지 말해 주세요.');
            continue;
        }
        if (n.t === 'repeat') {
            // "세 번 야옹하고 멈춰" — 반복 안에서 멈추면 한 번만 하고 끝난다 → 반복이 끝난 뒤에 멈춘다
            const last = n.body[n.body.length - 1];
            if (last && last.cap && n.body.length > 1) {
                n.body.pop();
                list.splice(i + 1, 0, last);
                note('‘멈추기’를 반복 안에 두면 한 번만 하고 끝나서, 반복이 끝난 뒤로 옮겼어요.');
            }
            // 반복 안의 '재생하기'는 소리가 겹쳐 한 번처럼 들린다
            n.body.forEach(b => {
                if (b.opcode === 'sound_play') {
                    b.opcode = 'sound_playuntildone';
                    b.sig = String(b.sig).replace(/^sound\(/, 'soundwait(');
                    b.label = String(b.label).replace(' 재생하기', ' 끝까지 재생하기');
                    note('여러 번 낼 때는 ‘끝까지 재생하기’를 써야 소리가 겹치지 않아요.');
                }
            });
        }
        // "야옹 소리 내고 멈춰" — 재생하기 바로 뒤에 멈추기면 소리가 곧바로 끊긴다
        if (n.opcode === 'sound_play' && list[i + 1] && list[i + 1].cap) {
            n.opcode = 'sound_playuntildone';
            n.sig = String(n.sig).replace(/^sound\(/, 'soundwait(');
            n.label = String(n.label).replace(' 재생하기', ' 끝까지 재생하기');
            note('멈추기 바로 앞의 소리는 ‘끝까지 재생하기’로 해야 끊기지 않아요.');
        }
        if (n.body) {
            const seen = n.body.filter(b => b.opcode === 'motion_ifonedgebounce');
            if (seen.length > 1) n.body = n.body.filter((b, k) => b.opcode !== 'motion_ifonedgebounce' || k === n.body.lastIndexOf(seen[seen.length - 1]));
        }
        if ((n.t === 'repeat' || n.t === 'forever') && n.body.length >= 2 && n.body.every(b => /^motion_change[xy]by$/.test(b.opcode || '')) &&
            n.body.some(b => Number((b.inputs.DX || b.inputs.DY).value) > 0) && n.body.some(b => Number((b.inputs.DX || b.inputs.DY).value) < 0)) {
            n.body = n.body.reduce((a, b) => a.concat([b, act('control_wait', {inputs: {DURATION: num(0.1, 'math_positive_number')}, sig: 'wait0.1', label: '0.1초 기다리기'})]), []);
            note('올라갔다 내려오는 게 보이게 사이사이 ‘0.1초 기다리기’를 넣었어요.');
        }
        if (n.t === 'forever' && n.body.length && n.body.every(b => b.opcode === 'motion_ifonedgebounce')) {
            n.body.unshift(act('motion_movesteps', {inputs: {STEPS: num(10)}, sig: 'move10', label: '10만큼 움직이기'}));
            note('튕기려면 움직여야 해서 ‘10만큼 움직이기’를 함께 넣었어요.');
        }
        // 무한 반복 안의 '재생하기'도 소리가 끝나기 전에 처음부터 다시 시작된다 → '끝까지 재생하기'
        if (n.t === 'forever') {
            n.body.forEach(b => {
                if (b.opcode === 'sound_play') {
                    b.opcode = 'sound_playuntildone';
                    b.sig = String(b.sig).replace(/^sound\(/, 'soundwait(');
                    b.label = String(b.label).replace(' 재생하기', ' 끝까지 재생하기');
                    note('계속 반복할 때는 ‘끝까지 재생하기’를 써야 소리가 처음부터 끊기지 않아요.');
                }
            });
        }
        // "떼면 멈추게" — '아니면' 칸의 '모두 멈추기'는 프로그램을 끝내 버린다 → 아무것도 안 하면 된다
        if (n.t === 'ifelse' && n.body2.length === 1 && n.body2[0].cap) {
            n.t = 'if';
            delete n.body2;
            note('‘떼면 멈추기’는 따로 할 일이 없어서 ‘아니면’ 칸을 비웠어요. 거기에 ‘모두 멈추기’를 넣으면 프로그램이 끝나 버려요.');
        }
    }
};
// 멈추기(모두)는 뒤에 블록을 못 붙인다 → 맨 끝이 아니면 뺀다.
// "안녕 말하고 잘가 말해", "이겼다 말하고 멈춰", "슬픈 얼굴 나왔다가 웃는 얼굴" — 바로 덮이면 안 보인다
// "타이머 만들어줘 고양이가 숫자 말하게" — 변수 값을 이미 말하는데 '숫자'라는 글자를 또 말하는 블록은 뺀다
const ECHO_SAY = /^(?:숫자|숫자를|시간|초|남은\s*시간|점수|횟수|값)$/;
const dropEchoSay = list => {
    let varSay = false;
    walk(list, n => { if (/^looks_(?:say|think)/.test(n.opcode || '') && n.inputs.MESSAGE && n.inputs.MESSAGE.node && n.inputs.MESSAGE.node.opcode === 'data_variable') varSay = true; });
    if (!varSay) return;
    for (let i = list.length - 1; i >= 0; i--) {
        const n = list[i];
        if (/^looks_(?:say|think)$/.test(n.opcode || '') && n.inputs.MESSAGE && !n.inputs.MESSAGE.node && ECHO_SAY.test(String(n.inputs.MESSAGE.value).trim())) list.splice(i, 1);
    }
};
const fixOverwrite = (list, note) => {
    list.forEach(n => { if (n.body) fixOverwrite(n.body, note); if (n.body2) fixOverwrite(n.body2, note); });
    for (let i = 0; i + 1 < list.length; i++) {
        const a = list[i];
        const b = list[i + 1];
        if ((a.opcode === 'looks_say' || a.opcode === 'looks_think') && typeof (a.inputs.MESSAGE || {}).value === 'string' && a.inputs.MESSAGE.value !== '' &&
            /^(?:looks_say|looks_think|looks_sayforsecs|looks_thinkforsecs|sensing_askandwait|control_stop)$/.test(b.opcode || '')) {
            const think = a.opcode === 'looks_think';
            a.opcode = think ? 'looks_thinkforsecs' : 'looks_sayforsecs';
            a.inputs = Object.assign({}, a.inputs, {SECS: num(2)});
            a.label = String(a.label).replace(/ (말하기|생각하기)$/, (m0, v) => `을(를) 2초 동안 ${v}`);
            note('다음 블록이 말풍선을 바로 덮지 않게 ‘2초 동안 말하기’로 했어요.');
        }
        if (/^cubroidled_lce(?:Img|Number|Alpabet)Control$/.test(a.opcode || '') && /^cubroidled_lce(?:Img|Number|Alpabet)Control$/.test(b.opcode || '')) {
            list.splice(i + 1, 0, waitS(1));
            note('LED 그림이 바로 바뀌면 앞 그림이 안 보여서 사이에 ‘1초 기다리기’를 넣었어요.');
        }
        // 숨겼다 바로 보이기 · 불 켰다 바로 끄기 — 눈에 안 보인다
        const pairHS = (a.opcode === 'looks_hide' && b.opcode === 'looks_show') || (a.opcode === 'looks_show' && b.opcode === 'looks_hide');
        const pairLight = /MasterColorControl$/.test(a.opcode || '') && /ColorOffControl$/.test(b.opcode || '');
        if (pairHS || pairLight) {
            list.splice(i + 1, 0, pairLight ? act('control_wait', {inputs: {DURATION: num(1, 'math_positive_number')}, sig: 'wait1', label: '1초 기다리기'}) : waitS(1));
            note('바로 이어지면 눈에 안 보여서 사이에 ‘1초 기다리기’를 넣었어요.');
        }
        // 말한 뒤 바로 숨기·움직이기 — 말풍선을 읽을 시간이 없다
        if ((a.opcode === 'looks_say' || a.opcode === 'looks_think') && typeof (a.inputs.MESSAGE || {}).value === 'string' && a.inputs.MESSAGE.value !== '' && /^(?:looks_hide|motion_movesteps|motion_glide|looks_switchcostumeto|looks_nextcostume)/.test(b.opcode || '')) {
            const think = a.opcode === 'looks_think';
            a.opcode = think ? 'looks_thinkforsecs' : 'looks_sayforsecs';
            a.inputs = Object.assign({}, a.inputs, {SECS: num(2)});
            a.label = String(a.label).replace(/ (말하기|생각하기)$/, (m0, v) => `을(를) 2초 동안 ${v}`);
            note('다음 블록이 말풍선을 바로 덮지 않게 ‘2초 동안 말하기’로 했어요.');
        }
    }
    // 반복 안에서 마지막 그림(LED·모양)이 곧바로 첫 그림으로 바뀌면 안 보인다 — 끝에 기다리기
    list.forEach(n => {
        if ((n.t === 'forever' || n.t === 'repeat') && n.body.length >= 2) {
            const f = n.body[0];
            const l = n.body[n.body.length - 1];
            const vis = x => /^cubroidled_lce(?:Img|Number|Alpabet)Control$|^looks_switchcostumeto$|MasterColorControl$/.test((x && x.opcode) || '');
            if (vis(f) && vis(l) && n.body.some(x => x.opcode === 'control_wait')) {
                const w = n.body.find(x => x.opcode === 'control_wait');
                n.body.push(clone(w));
                note('반복 끝에서 바로 첫 그림으로 바뀌지 않게 끝에도 기다리기를 넣었어요.');
            }
        }
    });
};
const fixCaps = (list, note) => {
    for (let i = 0; i < list.length; i++) {
        const n = list[i];
        if (n.body) fixCaps(n.body, note);
        if (n.body2) fixCaps(n.body2, note);
        if (n.cap && i < list.length - 1) { list.splice(i, 1); i--; note(NOTE.stop); }
    }
};

// 무한 반복 안의 무한 반복은 빠져나올 수 없다(안쪽에 갇힌다). 바깥이 이미 되풀이하므로 안쪽 것을 푼다.
// (블라인드 실측: "스페이스바 누르고 있으면 계속 앞으로" 가 한 번 누르면 영원히 가는 블록이 됐다)
const flattenLoops = (list, inLoop) => {
    const out = [];
    list.forEach(n => {
        if (n.t === 'forever' && inLoop) { out.push(...flattenLoops(n.body, true)); return; }
        if (n.body) n.body = flattenLoops(n.body, inLoop || n.t === 'forever');
        if (n.body2) n.body2 = flattenLoops(n.body2, inLoop || n.t === 'forever');
        out.push(n);
    });
    return out;
};
// 모양을 반복해서 바꾸는데 기다리기가 없으면 너무 빨라 안 보인다 → 알려만 준다(고쳐 주지 않는다: 학생이 넣게)
const hintAnimation = (list, note) => list.forEach(n => {
    if ((n.t === 'forever' || n.t === 'repeat') && n.body.some(b => b.opcode === 'looks_nextcostume') &&
        !n.body.some(b => b.opcode === 'control_wait')) {
        note('모양을 반복해서 바꿀 때 기다리기가 없으면 너무 빨라서 눈에 안 보여요. ‘0.2초 기다리기’를 넣어 보세요.');
    }
    if (n.body) hintAnimation(n.body, note);
});
// 숨기기 바로 뒤에 보이기 → 사이에 기다리기가 없으면 사라진 걸 볼 틈이 없다(블라인드 실측 문장)
const hintBlink = (list, note) => {
    list.forEach((n, i) => {
        const next = list[i + 1];
        if (n.opcode === 'looks_hide' && next && next.opcode === 'looks_show') {
            note('숨기기 바로 뒤에 보이기가 있으면 너무 빨라서 사라진 걸 볼 수 없어요. 사이에 ‘1초 기다리기’를 넣어 보세요.');
        }
        if (n.body) hintBlink(n.body, note);
    });
};
const walkNode = (n, fn) => {
    fn(n);
    Object.keys(n.inputs || {}).forEach(k => { if (n.inputs[k].node) walkNode(n.inputs[k].node, fn); });
    if (n.cond) walkNode(n.cond, fn);
    Object.keys(n.boolIn || {}).forEach(k => walkNode(n.boolIn[k], fn));
    if (n.body) n.body.forEach(b => walkNode(b, fn));
    if (n.body2) n.body2.forEach(b => walkNode(b, fn));
};
const walk = (list, fn) => list.forEach(n => walkNode(n, fn));
const collectBroadcasts = ir => {
    const out = [];
    const add = nm => { if (nm && out.indexOf(nm) < 0) out.push(nm); };
    const look = n => {
        Object.keys(n.fields || {}).forEach(k => add(n.fields[k] && n.fields[k].bcast));
        Object.keys(n.inputs || {}).forEach(k => add(n.inputs[k] && n.inputs[k].bcast));
    };
    ir.scripts.forEach(sc => { walkNode(sc.hat, look); walk(sc.body, look); });
    return out;
};
const collectLists = ir => {
    const out = [];
    ir.scripts.forEach(sc => walk(sc.body, n => { if (n.listName && out.indexOf(n.listName) < 0) out.push(n.listName); }));
    return out;
};
// 큐브로이드 확장 블록이 쓰였으면 그 확장을 먼저 불러와야 한다(안 그러면 블록이 깨진다)
const collectExtensions = ir => {
    const ids = [];
    ir.scripts.forEach(sc => walkNode(sc.hat, n => {
        const p = String(n.opcode || '').split('_')[0];
        if ((/^cubroid/.test(p) || p === 'pen' || AI_EXT_IDS.test(p)) && ids.indexOf(p) < 0) ids.push(p);
    }));
    ir.scripts.forEach(sc => walk(sc.body, n => {
        const p = String(n.opcode || '').split('_')[0];
        if ((/^cubroid/.test(p) || p === 'pen' || AI_EXT_IDS.test(p)) && ids.indexOf(p) < 0) ids.push(p);
    }));
    return ids;
};

/**
 * 문장 → 번역 결과(아직 VM 에 넣지 않은 설계도).
 * @param {string} text 학생 문장
 * @param {{soundNames?: string[]}} ctx 스프라이트 정보(소리 이름)
 * @returns {object} {text, scripts:[{hat, body, implicit}], segments:[{start,end,cat}], unparsed:[{start,end,text,script}], notes, variables}
 */
const translate = (text, ctx) => {
    const lang = (ctx && ctx.lang && LANGS[ctx.lang]) ? ctx.lang : 'ko';
    const prevCUR = CUR;
    CUR = LANGS[lang];
    try {
        let ir = translateIn(text, ctx, CUR);
        const usesRobot = JSON.stringify(ir.scripts).indexOf('"cubroid') >= 0;
        const catMove = JSON.stringify(ir.scripts).indexOf('"motion_') >= 0;
        if (usesRobot && catMove && !(ctx && ctx.robot) && !CUR.catWords.test(String(text || ''))) {
            ir = translateIn(text, Object.assign({}, ctx, {robot: true}), CUR);
        }
        return runPost(ir, lang);
    } finally {
        CUR = prevCUR;
    }
};
const runPost = (ir, lang) => {
    if (!ir || !EXTRA_POST.length) return ir;
    EXTRA_POST.forEach(f => f(ir, lang));
    ir.extensions = collectExtensions(ir);
    return ir;
};
const translateIn = (text, ctx, LX) => {
    const soundNames = (ctx && ctx.soundNames) || [];
    // 흔한 오타를 먼저 고친다(글자 수가 바뀌어도 이후 위치는 고친 글 기준)
    const src = LX.fix ? LX.fix(String(text || '')) : String(text || '');
    const res = {text: src, lang: LX.id, scripts: [], segments: [], unparsed: [], notes: [], variables: []};
    const note = s0 => { const s = s0 && LX.localize ? LX.localize(s0) : s0; if (s && res.notes.indexOf(s) < 0) res.notes.push(s); };
    const intents = LX.build(soundNames);
    // robotHint = 프로젝트에 로봇 블록·확장이 있음(창이 넘김, 2026-10-16). 고양이·스프라이트를 말하면 무시 — 약한 힌트
    const robot = Boolean(ctx && ctx.robot) || LX.robotWords.test(src) || Boolean(ctx && ctx.robotHint && !LX.catWords.test(src));
    const ctx2 = Object.assign({}, ctx, {robot, robotOnly: robot && !LX.catWords.test(src), src});
    let current = null;
    const newScript = (hat, implicit) => {
        current = {hat, body: [], implicit: !!implicit};
        res.scripts.push(current);
    };
    const attach = nodes => {
        if (!nodes.length) return;
        if (!current) {
            newScript(act('event_whenflagclicked', {sig: 'flag', label: '깃발을 클릭했을 때'}), true);
            note(NOTE.hat);
        }
        const last = current.body[current.body.length - 1];
        // "계속" 뒤에 이어지는 문장은 같은 반복 안에 넣는다(무한 반복 뒤에는 블록을 붙일 수 없다)
        if (last && last.t === 'forever') last.body.push(...nodes);
        else current.body.push(...nodes);
    };

    splitSentences(src).forEach(sen => {
        const scripts0 = res.scripts.length;
        const cands = [];
        intents.forEach((it, pi) => it.res.forEach((re, alt) => {
            re.lastIndex = 0;
            let m;
            while ((m = re.exec(sen.text)) !== null) {
                if (m[0].length) {
                    cands.push({it, pi, alt, g: Object.assign({0: m[0]}, m.groups), start: sen.start + m.index, end: sen.start + m.index + m[0].length});
                }
                re.lastIndex = m.index + 1; // 겹치는 후보도 모두 모은다
            }
        }));
        // 앞에서부터, 같은 자리면 긴 것, 그래도 같으면 사전 순서
        // prio: 같은 자리·같은 길이일 때만 앞선다(바깥 모듈 규칙이 "say …" 같은 넓은 규칙에 지지 않게 — vibe-aiext.js)
        cands.sort((a, b) => (a.start - b.start) || ((b.end - b.start) - (a.end - a.start)) || ((b.it.prio ? 1 : 0) - (a.it.prio ? 1 : 0)) || (a.pi - b.pi));
        const chosen = [];
        let lastEnd = sen.start;
        const senEnd = sen.start + sen.text.length;
        cands.forEach(c0 => {
            if (c0.start < lastEnd) return;
            if (LX.skip(c0, {src, sen, after: src.slice(c0.end, senEnd), before: src.slice(sen.start, c0.start), self: src.slice(c0.start, c0.end)})) return;
            let c = c0;
            if (c.it.left && c.it.left.indexOf(c.alt) >= 0) {
                // "X라고 말해": 앞 블록이 끝난 곳부터 "라고" 앞까지가 할 말(X)
                const optional = c.it.leftOptional && c.it.leftOptional.indexOf(c.alt) >= 0;
                let msg = leadingTrim(src, lastEnd, c.start);
                // "대답을 말해"처럼 목적어면 할 말이 아니다 / 너무 길면 다른 문장이 섞인 것
                if (msg && optional && (/(?:을|를)$/.test(msg.text) || msg.text.length > 20)) msg = null;
                if (!msg && !optional) return;
                if (msg) c = Object.assign({}, c, {start: msg.start, g: Object.assign({}, c.g, {m: msg.text})});
            }
            const out = c.it.make(c.g, ctx2, c.alt, src.slice(sen.start, c.start));
            if (!out) return;
            if (ctx2.debug) ctx2.debug.push(`${c.it.id}[${c.alt}] “${src.slice(c.start, c.end)}”`);
            chosen.push({c, out});
            lastEnd = c.end;
        });

        let seq = [];
        const flush = () => {
            if (!seq.length) return;
            // 영어: "A when/if X" — 뒤에 온 조건을 앞의 동작 앞으로(조건 뒤에 할 일이 없을 때만)
            if (LX.trailingCond && seq.length >= 2 && seq[seq.length - 1].kind === 'cond') {
                let j = seq.length - 2;
                const setup = it => it.nodes.every(n => n.t === 'act' && /^(?:data_setvariableto|data_deletealloflist|looks_setsizeto|motion_gotoxy|motion_setrotationstyle|pen_clear)$/.test(n.opcode));
                while (j >= 0 && seq[j].kind === 'act' && !setup(seq[j])) j--;
                if (j < seq.length - 2) seq.splice(j + 1, 0, seq.pop());
            }
            // 영어: "change color by 25 every second" — 뒤에 온 '~마다'가 앞의 동작까지 반복하게(앞 동작을 반복 시작점으로 표시)
            if (LX.trailingCond && seq.length >= 2 && seq.every(x => x.kind === 'act')) {
                const lastIt = seq[seq.length - 1];
                if (lastIt.nodes.length && lastIt.nodes.every(n => n.every)) {
                    const setup = it => it.nodes.every(n => n.t === 'act' && /^(?:data_setvariableto|data_deletealloflist|looks_setsizeto|motion_gotoxy|motion_setrotationstyle|pen_clear|pen_penDown)$/.test(n.opcode));
                    let j = seq.length - 2;
                    while (j >= 0 && !setup(seq[j])) j--;
                    if (j + 1 < seq.length - 1) seq[j + 1].nodes.forEach(n => { n.loop = true; });
                }
            }
            // 영어: "change x by 5 forever and if …" — 뒤에 붙은 forever 는 바로 앞 동작부터 감싼다
            if (LX.trailingCond) {
                const fi = seq.findIndex((x, i) => i > 0 && x.kind === 'forever' && seq[i - 1].kind === 'act' && !seq[i - 1].nodes.every(n => n.every) && i < seq.length - 1);
                if (fi > 0) { const f = seq.splice(fi, 1)[0]; seq.splice(fi - 1, 0, f); }
            }
            attach(nest(seq, note));
            seq = [];
        };
        const gaps = [];
        let pos = sen.start;
        chosen.forEach(({c, out}) => {
            if (c.start > pos) gaps.push([pos, c.start]);
            pos = c.end;
            res.segments.push({start: c.start, end: c.end, cat: c.it.cat});
            (out.notes || []).forEach(note);
            if (/면서$/.test(src.slice(c.start, c.end)) && out.nodes) out.nodes.forEach(n => { n.withNext = true; });
            const guess = (out.notes || []).find(LX.isGuess || isGuess);
            if (guess && out.nodes && out.nodes[0] && out.nodes[0].t === 'act') attachAsk(out.nodes[0], guess);
            if (out.hat) { flush(); newScript(out.hat); } else if (out.multi) {
                // 스크립트 여러 개를 한꺼번에(방향키 4개). 뒤에 오는 말은 새 기본 스크립트로.
                flush();
                out.multi.forEach(sc => res.scripts.push({hat: sc.hat, body: sc.body, implicit: false}));
                current = null;
            } else if (c.it.kind === 'until') seq.push({kind: 'until', cond: out.cond});
            else if (out.cond) seq.push({kind: 'cond', cond: out.cond});
            else if (c.it.kind === 'repeat') seq.push({kind: 'repeat', times: out.times});
            else if (c.it.kind === 'forever') seq.push({kind: 'forever'});
            else if (c.it.kind === 'else') seq.push({kind: 'else'});
            else if (out.nodes && out.nodes.length) {
                if (out.loopBefore) seq.push({kind: 'forever'}); // "모양 계속 바꿔" 처럼 말 중간의 "계속"
                const prevItem = seq[seq.length - 1];
                // "뛰어오르게 위로 올라갔다가 다시 내려오게" — 같은 점프를 풀어서 다시 말한 것은 한 번만
                // 같은 '정하기' 블록을 두 번 말한 것(“가운데로 보내줘 x 0 y 0”, “방향 180도로 바꿔 아래보게”)은 한 번만
                const sameSet = prevItem && prevItem.kind === 'act' && prevItem.nodes.length === out.nodes.length &&
                    out.nodes.every((n, k) => n.t === 'act' && IDEMPOTENT.test(n.opcode) && prevItem.nodes[k].sig === n.sig);
                if (!((c.it.id === 'jump' || c.it.once) && prevItem && prevItem.id === c.it.id) && !sameSet) {
                    // "show it again after 3 seconds" — 뒤에 말한 기다리기는 앞 동작보다 먼저
                    if (out.nodes[0] && out.nodes[0].swapPrev && prevItem && prevItem.kind === 'act') seq.splice(seq.length - 1, 0, {kind: 'act', nodes: out.nodes, id: c.it.id});
                    else seq.push({kind: 'act', nodes: out.nodes, id: c.it.id});
                }
            }
        });
        if (sen.start + sen.text.length > pos) gaps.push([pos, sen.start + sen.text.length]);
        flush();
        // 영어: "grow when i press up and shrink when i press down" — 모자가 뒤에 오면 앞의 동작을 그 모자로
        if (LX.trailingHat) {
            const made = res.scripts.slice(scripts0);
            if (made.length >= 2 && made[0].implicit && !made[made.length - 1].body.length) {
                const SETUP = /^(?:data_setvariableto|data_deletealloflist|looks_setsizeto|motion_gotoxy|motion_setrotationstyle|pen_clear|looks_show|sensing_resettimer)$/;
                let k = 0;
                // 모자가 깃발이면 설정 블록도 같은 스크립트로(깃발 스크립트 두 개로 나뉘지 않게)
                while (!(made[1].hat && made[1].hat.opcode === 'event_whenflagclicked') && k < made[0].body.length - 1 && made[0].body[k].t === 'act' && SETUP.test(made[0].body[k].opcode)) k++;
                for (let i = made.length - 1; i >= 2; i--) made[i].body = made[i - 1].body;
                made[1].body = made[0].body.slice(k);
                if (k) made[0].body = made[0].body.slice(0, k); else res.scripts.splice(scripts0, 1);
                current = made[made.length - 1];
            } else if (made.length === 2 && made[0].implicit && made[1].body.length && made[0].body.length && !made[0].body.some(n => n.t === 'forever') &&
                !/(?:,|\band)\s+(?:when|whenever|once|if)\s/i.test(sen.text)) {
                const SETUP = /^(?:data_setvariableto|data_deletealloflist|looks_setsizeto|motion_gotoxy|motion_setrotationstyle|pen_clear|looks_show|sensing_resettimer)$/;
                let k = 0;
                while (!(made[1].hat && made[1].hat.opcode === 'event_whenflagclicked') && k < made[0].body.length && made[0].body[k].t === 'act' && SETUP.test(made[0].body[k].opcode)) k++;
                const moved = made[0].body.slice(k);
                if (moved.length) {
                    made[1].body = moved.concat(made[1].body);
                    if (k) made[0].body = made[0].body.slice(0, k); else res.scripts.splice(scripts0, 1);
                }
            }
        }
        gaps.forEach(([a, b]) => leftovers(src, a, b).forEach(u => {
            u.scriptRef = current;
            res.unparsed.push(u);
            res.segments.push({start: u.start, end: u.end, cat: 'unparsed'});
            note(LX.unknown(u.text));
        }));
    });

    // 할 일이 없는 "언제" 블록은 만들지 않는다("a키 누르면 왼쪽" 처럼 뒤를 못 알아들은 경우)
    res.scripts = res.scripts.filter(s => {
        if (s.body.length) return true;
        if (!s.implicit) note(LX.hatEmpty(s.hat));
        return false;
    });
    if (!res.scripts.some(s => s.implicit)) {
        const hn = LX.localize ? LX.localize(NOTE.hat) : NOTE.hat;
        res.notes = res.notes.filter(n => n !== hn && n !== NOTE.hat);
    }
    res.unparsed.forEach(u => { u.script = res.scripts.indexOf(u.scriptRef); delete u.scriptRef; });
    res.scripts.forEach(s => {
        autoLoop(s, note);
        s.body = flattenLoops(s.body, false);
        moveGapWaits(s.body);
        dedupeStops(s.body);
        hoistSpeed(s.body);
        if (robot) {
            robotElse(s.body, src, note);
            // "앞으로 가다가 … " — 반복 앞에 따로 있는 1초 앞으로 가기는 빼고(반복 안에서 가니까)
            const fi = s.body.findIndex(x => x.t === 'forever');
            if (/가다가/.test(src) && fi > 0) {
                let hasGo = false;
                walk(s.body[fi].body, x => { if (x.sig === 'm1:Right') hasGo = true; });
                if (hasGo) s.body = s.body.slice(0, fi).filter(x => !/_dcMotor|^control_wait$/.test(x.opcode || '')).concat(s.body.slice(fi));
            }
        }
        const each = /각각\s*(\d+(?:\.\d+)?)\s*초|(\d+(?:\.\d+)?)\s*초\s*씩/.exec(src);
        if (robot && each) {
            const v = Number(each[1] || each[2]);
            const setW = list => list.forEach((x, i) => { if (x.body) setW(x.body); if (x.opcode === 'control_wait' && i >= 2 && /_dcMotorControl$/.test(list[i - 1].opcode || '')) { x.inputs.DURATION = num(v, 'math_positive_number'); x.sig = `wait${fmt(v)}`; x.label = `${fmt(v)}초 기다리기`; } });
            setW(s.body);
        }
        tidyStructure(s.body, note);
        fixCaps(s.body, note);
        dropEchoSay(s.body);
        fixOverwrite(s.body, note);
        hintAnimation(s.body, note);
        hintBlink(s.body, note);
    });
    {
        const extra = [];
        res.scripts.forEach(s => {
            const fi = s.body.findIndex(n => n.t === 'forever');
            if (fi >= 0 && fi < s.body.length - 1) {
                const opsL = list => { const o = []; walk(list, n => { if (n.opcode) o.push(n.opcode); }); return o; };
                const fOps = opsL([s.body[fi]]);
                const CLEAN = /_dcMotorStop$|^cubroidmaster_cubroidColorOffControl$|^cubroidled_ledAllClearControl$|^pen_penUp$|^sound_stopallsounds$|^control_stop$/;
                const tail = s.body.slice(fi + 1).filter(n => !n.cap || s.body.length - fi > 2).filter(n => {
                    const o = opsL([n]);
                    if (o.length && o.every(op => CLEAN.test(op))) return false;
                    if (o.length && o.every(op => fOps.indexOf(op) >= 0)) return false; // 반복 안에 이미 있는 블록을 또 말함
                    return true;
                });
                s.body = s.body.slice(0, fi + 1);
                if (tail.length) {
                    extra.push({hat: clone(s.hat), body: tail, implicit: false});
                    note('무한 반복 뒤에 둔 블록은 실행되지 않아서, 같은 시작 블록으로 따로 만들어 동시에 실행되게 했어요.');
                }
            }
        });
        res.scripts.push(...extra);
    }
    // ═══ 23차 구조 후처리(양쪽 언어) ═══
    {
        const opsIn = list => { const o = []; walk(list, n => { if (n.opcode) o.push(n.opcode); }); return o; };
        const compl = (a, b) => {
            const ma = /^bright([<>])(\d+)$/.exec(String(a || ''));
            const mb = /^bright([<>])(\d+)$/.exec(String(b || ''));
            return Boolean(ma && mb && ma[1] !== mb[1] && ma[2] === mb[2]);
        };
        const fixList = list => {
            for (let i = 0; i < list.length; i++) {
                const n = list[i];
                if (n.body) fixList(n.body);
                if (n.body2) fixList(n.body2);
                // ① "밝으면 … 어두우면 …" 이 서로 반대 조건으로 겹쳐 안쪽이 절대 참이 안 됨 → 안쪽만 남긴다
                if ((n.t === 'if' || n.t === 'ifelse') && n.cond && n.body.length >= 1 && (n.body[0].t === 'if' || n.body[0].t === 'ifelse') && n.body[0].cond && compl(n.cond.sig, n.body[0].cond.sig)) {
                    list.splice(i, 1, ...n.body);
                    continue;
                }
                // ② 무한 반복 첫 칸의 '방향 보기'는 튕기기를 매번 되돌린다 → 반복 앞으로
                if (n.t === 'forever' && n.body.length > 1 && n.body[0].opcode === 'motion_pointindirection' && n.body.some(b => b.opcode === 'motion_ifonedgebounce')) {
                    list.splice(i, 0, n.body.shift());
                    i++;
                }
                // ③ 닿으면 목숨 -1 — 닿아 있는 동안 계속 깎인다 → 떨어질 때까지 기다리기
                if (n.t === 'if' && n.cond && /^touch/.test(String(n.cond.sig)) && n.body.length === 1 && n.body[0].opcode === 'data_changevariableby' && Number(n.body[0].inputs.VALUE.value) < 0 && !n.body.some(b => b.opcode === 'control_wait_until')) {
                    n.body.push(act('control_wait_until', {boolIn: {CONDITION: act('operator_not', {boolIn: {OPERAND: clone(n.cond)}, sig: `not:${n.cond.sig}`, label: LX.id === 'en' ? '' : `${String(n.cond.label || '').replace(/(?:았|었)?는가\?$/, '지 않았는가?')}`})}, sig: `until:not:${n.cond.sig}`, label: LX.id === 'en' ? '' : `‘${String(n.cond.label || '').replace(/(?:았|었)?는가\?$/, '지 않았는가?')}’ 까지 기다리기`}));
                    note(LX.lang === 'en' ? 'It takes one away per touch (it waits until they are apart).' : '닿아 있는 동안 계속 깎이지 않게, 떨어질 때까지 기다린 뒤 다시 세요.');
                }
                // ④ '이 복제본 삭제' 뒤에 붙은 기다리기는 삭제 앞으로
                if (n.opcode === 'control_delete_this_clone' && list[i + 1] && list[i + 1].opcode === 'control_wait') {
                    const w = list.splice(i + 1, 1)[0];
                    list.splice(i, 0, w);
                    i++;
                }
            }
        };
        res.scripts.forEach(s => fixList(s.body));
        // ⑤ 복제본 스크립트: 무한 반복 안에 삭제가 들어가면 첫 바퀴에 죽는다 → 반복을 N번으로 바꾸고 삭제는 밖으로
        res.scripts.forEach(s => {
            if (!s.hat || s.hat.opcode !== 'control_start_as_clone') return;
            s.body.forEach((n, i) => {
                if (n.t !== 'forever') return;
                const di = n.body.findIndex(b => b.opcode === 'control_delete_this_clone');
                if (di < 0) return;
                const del = n.body.splice(di, 1)[0];
                const wi = n.body.findIndex(b => b.opcode === 'control_wait' && Number(b.inputs.DURATION.value) >= 1);
                let times = 30;
                if (wi >= 0) { times = Math.max(1, Math.round(Number(n.body[wi].inputs.DURATION.value) * 10)); n.body[wi].inputs.DURATION = num(0.1, 'math_positive_number'); n.body[wi].sig = 'wait0.1'; n.body[wi].label = LX.lang === 'en' ? '' : '0.1초 기다리기'; }
                s.body.splice(i, 1, {t: 'repeat', times, body: n.body}, del);
            });
        });
        void opsIn;
    }
    // 펜 굵기·색을 그린 뒤에 정함 — 그리기 전에 옮긴다("draw a triangle with pen size 5")
    res.scripts.forEach(s => {
        const b0 = s.body;
        const firstDraw = b0.findIndex(n => n.opcode === 'pen_clear' || n.opcode === 'pen_penDown');
        if (firstDraw < 0) return;
        for (let i = b0.length - 1; i > firstDraw; i--) {
            if (/^pen_setPen(?:Size|Color)To|^pen_setPenColorParamTo/.test(b0[i].opcode || '') && b0.slice(firstDraw, i).some(n => n.t === 'repeat' || n.opcode === 'motion_movesteps')) {
                const [m] = b0.splice(i, 1);
                const at = b0[firstDraw].opcode === 'pen_clear' ? firstDraw + 1 : firstDraw;
                b0.splice(at, 0, m);
            }
        }
    });
    // 속도만 정하고 모터를 돌리지 않음 — 앞으로 가기를 붙인다
    {
        let sp = null;
        let runs = false;
        res.scripts.forEach(s => walk(s.body, n => { if (/_dcMotorSetSpeed$/.test(n.opcode || '')) sp = sp || s; if (/_dcMotorControl$/.test(n.opcode || '')) runs = true; }));
        // 가라는 말(직진·go·drive…)이 있는데 모터 블록이 못 나온 경우만 — "속도 50%로 해"처럼 속도만 정하는 말은 그대로 둔다
        if (sp && !runs && /직진|달려|달리|출발|앞으로|\b(?:go|goes|drive|drives|forward|straight|run|runs)\b/i.test(String(ctx2.src || ''))) {
            sp.body.push(...drive('forward', 1, true));
            note('속도만 정하면 로봇이 움직이지 않아서 1초 앞으로 가기를 붙였어요.');
        }
    }
    // "대답이 안녕이면 반가워" — 대답을 쓰는데 묻는 블록이 하나도 없으면 맨 앞에 묻기를 넣는다(없으면 대답이 늘 비어 있다)
    {
        let usesAns = false;
        let hasAsk = Boolean(ctx && ctx.hasAsk);
        res.scripts.forEach(s => walk(s.body, n => { if (n.opcode === 'sensing_askandwait') hasAsk = true; if (n.cond && /^answer/.test(String(n.cond.sig))) usesAns = true; }));
        const s0 = res.scripts.find(s => { let u = false; walk(s.body, n => { if (n.cond && /^answer/.test(String(n.cond.sig))) u = true; }); return u; });
        if (usesAns && !hasAsk && s0) {
            const q = LX.id === 'ko' ? '뭐라고 할래?' : 'Type something!';
            s0.body.unshift(act('sensing_askandwait', {inputs: {QUESTION: txt(q)}, sig: `ask(${q})`, label: LX.id === 'ko' ? `‘${q}’라고 묻고 기다리기` : ''}));
            note(LX.id === 'ko' ? '대답을 쓰려면 먼저 물어봐야 해서 ‘묻고 기다리기’를 맨 앞에 넣었어요.' : 'An answer needs a question first, so “ask and wait” was added at the start.');
        }
    }
    if (LX.sameTime && LX.sameTime.test(src)) {
        const extra = [];
        res.scripts.forEach(s => {
            const fi = s.body.findIndex(n => n.t === 'forever');
            if (fi > 0 && s.body.slice(0, fi).some(n => n.t === 'act' && !/^(?:data_set|looks_set|motion_goto|motion_set|pen_clear|pen_set)/.test(n.opcode))) {
                extra.push({hat: clone(s.hat), body: s.body.slice(fi), implicit: false});
                s.body = s.body.slice(0, fi);
            }
        });
        res.scripts.push(...extra);
    }
    res.scripts.forEach(s => { walkNode(s.hat, n => { if (n.varName && res.variables.indexOf(n.varName) < 0) res.variables.push(n.varName); }); walk(s.body, n => { if (n.varName && res.variables.indexOf(n.varName) < 0) res.variables.push(n.varName); }); });
    collectAsks(res);
    res.extensions = collectExtensions(res);
    res.broadcasts = collectBroadcasts(res);
    res.lists = collectLists(res);
    res.segments.sort((a, b) => a.start - b.start);
    // 알림 순서: 못 알아들은 말을 맨 뒤로(먼저 "무엇을 만들었는지"를 보게)
    res.notes.sort((a, b) => LX.isUnknown(a) - LX.isUnknown(b));
    return res;
};

// ─────────────────────────────── 요약·표시 ───────────────────────────────
const sigOf = n => {
    if (n.t === 'repeat') return `rep${n.times}{${n.body.map(sigOf).join(',')}}`;
    if (n.t === 'forever') return `loop{${n.body.map(sigOf).join(',')}}`;
    if (n.t === 'if') return `if:${n.cond.sig}{${n.body.map(sigOf).join(',')}}`;
    if (n.t === 'until') return `repuntil:${n.cond.sig}{${n.body.map(sigOf).join(',')}}`;
    if (n.t === 'ifelse') return `ifelse:${n.cond.sig}{${n.body.map(sigOf).join(',')}}{${n.body2.map(sigOf).join(',')}}`;
    return n.sig;
};
/** 번역 결과를 한 줄 요약(시험용). 예: "key:space>move10,sound(야옹)" */
const signature = ir => ir.scripts.map(s => `${s.hat.sig}>${s.body.map(sigOf).join(',')}`).join(' | ');

const catOfOpcode = op => {
    const p = op.split('_')[0];
    if (AI_EXT_IDS.test(p)) return 'sensing';
    if (/^cubroid/.test(p)) return 'robot';
    return {event: 'events', motion: 'motion', looks: 'looks', sound: 'sound', control: 'control', sensing: 'sensing', data: 'variables', operator: 'operators'}[p] || 'control';
};
/** 화면에 보여줄 블록 목록(들여쓰기 포함). 스크립트마다 [{depth, label, cat}] */
const describe = ir => {
    const LX = langOf(ir);
    const L = n => (LX.label && LX.label(n)) || n.label;
    return ir.scripts.map(s => {
        const rows = [{depth: 0, label: L(s.hat), cat: 'events'}];
        const add = (list, d) => list.forEach(n => {
            if (n.t === 'repeat') rows.push({depth: d, label: LX.wrap.repeat(n.times), cat: 'control'});
            else if (n.t === 'until') rows.push({depth: d, label: LX.wrap.until(L(n.cond)), cat: 'control'});
            else if (n.t === 'forever') rows.push({depth: d, label: LX.wrap.forever(), cat: 'control'});
            else if (n.t === 'if' || n.t === 'ifelse') rows.push({depth: d, label: LX.wrap.if(L(n.cond)), cat: 'control'});
            else rows.push({depth: d, label: L(n), cat: catOfOpcode(n.opcode)});
            if (n.body) add(n.body, d + 1);
            if (n.body2) { rows.push({depth: d, label: LX.wrap.else(), cat: 'control'}); add(n.body2, d + 1); }
        });
        add(s.body, 1);
        return rows;
    });
};

// ─────────────────────────────── 블록 조립 ───────────────────────────────
// VM 내부 형식으로 만든다(curo-templates.js 와 같은 규칙): 값 입력은 shadow 블록을 실제로 만들고
// inputs[NAME] = {name, block:<id>, shadow:<id>}. C블록 안쪽은 SUBSTACK, 조건은 CONDITION(shadow 없음).
const WRAP_OP = {repeat: 'control_repeat', forever: 'control_forever', if: 'control_if', ifelse: 'control_if_else', until: 'control_repeat_until'};
const buildBlocks = (ir, varIds, prefix) => {
    let c = 0;
    const nid = () => `${prefix}${(c++).toString(36)}`;
    const blocks = {};
    let emitStack = null;
    const emit = (node, parentId) => {
        const id = nid();
        const opcode = node.t === 'act' ? node.opcode : WRAP_OP[node.t];
        const b = {id, opcode, next: null, parent: parentId, inputs: {}, fields: {}, shadow: false, topLevel: false};
        blocks[id] = b;
        Object.keys(node.fields || {}).forEach(k => {
            b.fields[k] = Object.assign({}, node.fields[k]);
            if (b.fields[k].bcast) { // 신호 이름 → 무대의 신호 변수 id
                b.fields[k].id = varIds[`bc:${b.fields[k].bcast}`];
                b.fields[k].variableType = 'broadcast_msg';
                delete b.fields[k].bcast;
            }
        });
        if (node.varName) b.fields.VARIABLE = {name: 'VARIABLE', value: node.varName, id: varIds[node.varName], variableType: ''};
        if (node.listName) b.fields.LIST = {name: 'LIST', value: node.listName, id: varIds[`list:${node.listName}`], variableType: 'list'};
        const inputs = Object.assign({}, node.inputs || {});
        if (node.t === 'repeat') inputs.TIMES = num(node.times, 'math_whole_number');
        Object.keys(inputs).forEach(name => {
            const inp = inputs[name];
            const sid = nid();
            blocks[sid] = {id: sid, opcode: inp.sh, next: null, parent: id, inputs: {},
                fields: {[inp.field]: Object.assign({name: inp.field, value: inp.value},
                    inp.bcast ? {id: varIds[`bc:${inp.bcast}`], variableType: 'broadcast_msg'} : {})}, shadow: true, topLevel: false};
            // 값 칸에 다른 블록(예: 점수 변수)이 들어가면: block = 그 블록, shadow = 밑에 깔린 빈 칸
            const inner = inp.node ? emit(inp.node, id) : null;
            b.inputs[name] = {name, block: inner || sid, shadow: sid};
        });
        if (node.t === 'if' || node.t === 'ifelse' || node.t === 'until') {
            const cid = emit(node.cond, id);
            b.inputs.CONDITION = {name: 'CONDITION', block: cid, shadow: null};
        }
        // 조건 칸(육각형)에 들어가는 블록 — 예: '~할 때까지 기다리기'의 CONDITION
        Object.keys(node.boolIn || {}).forEach(name => {
            b.inputs[name] = {name, block: emit(node.boolIn[name], id), shadow: null};
        });
        if (node.body && node.body.length) {
            b.inputs.SUBSTACK = {name: 'SUBSTACK', block: emitStack(node.body, id), shadow: null};
        }
        if (node.body2 && node.body2.length) {
            b.inputs.SUBSTACK2 = {name: 'SUBSTACK2', block: emitStack(node.body2, id), shadow: null};
        }
        if (node.mutation) b.mutation = JSON.parse(JSON.stringify(node.mutation));
        return id;
    };
    emitStack = (list, parentId) => {
        let prev = null;
        let first = null;
        list.forEach(n => {
            const id = emit(n, prev || parentId);
            if (prev) blocks[prev].next = id;
            else first = id;
            prev = id;
        });
        return first;
    };
    const tops = ir.scripts.map(s => {
        const hid = emit(s.hat, null);
        blocks[hid].topLevel = true;
        if (s.body.length) blocks[hid].next = emitStack(s.body, hid);
        return hid;
    });
    return {blocks, tops};
};

// 스택 높이 대략 계산(새 스크립트를 기존 것과 안 겹치게 아래에 놓기 위해)
const getB = (blocks, id) => (blocks.getBlock ? blocks.getBlock(id) : blocks[id]);
const countStack = (blocks, id) => {
    let n = 0;
    let cur = id;
    while (cur) {
        const b = getB(blocks, cur);
        if (!b) break;
        n++;
        if (b.inputs && b.inputs.SUBSTACK && b.inputs.SUBSTACK.block) n += countStack(blocks, b.inputs.SUBSTACK.block) + 1;
        if (b.inputs && b.inputs.SUBSTACK2 && b.inputs.SUBSTACK2.block) n += countStack(blocks, b.inputs.SUBSTACK2.block) + 1;
        cur = b.next;
    }
    return n;
};
const reach = (blocks, id, set) => {
    if (!id || set.has(id)) return;
    const b = blocks.getBlock(id);
    if (!b) return;
    set.add(id);
    Object.keys(b.inputs || {}).forEach(k => {
        reach(blocks, b.inputs[k].block, set);
        reach(blocks, b.inputs[k].shadow, set);
    });
    reach(blocks, b.next, set);
};
const EXT_KO = {
    cubroiddcmotor01: 'DC 모터 1번', cubroiddcmotor02: 'DC 모터 2번', cubroidproximity: '근접 센서', cubroidled: 'LED',
    cubroidmaster: '마스터', cubroidsound: '사운드', cubroidlighttouch: '라이트 터치 센서', pen: '펜',
    cubroidweather: '날씨', cubroidchart: '데이터 차트', cubroidChatgpt: '큐브로이드 챗GPT', cubroidDalle3: '큐브로이드 AI 이미지', cubroidcolor: '큐브로이드 색상 감지',
    cubroidobject: '큐브로이드 사물 인식', facerecognition: '큐브로이드 얼굴 인식', facemesh2scratch: '큐브로이드 얼굴 추적', handpose2scratch: '큐브로이드 손가락 추적',
    posenet2scratch: '큐브로이드 포즈', speech2scratch: '큐브로이드 음성인식', ic2scratch: '큐브로이드 이미지분류', ml2scratch: '큐브로이드 머신러닝'
};
const rid = () => `vibe_${Math.random().toString(36)
    .slice(2, 10)}${Date.now().toString(36)}`;

/**
 * 번역 결과를 지금 편집 중인 스프라이트에 넣는다.
 * @param {VirtualMachine} vm 스크래치 VM
 * @param {object} ir translate() 결과
 * @returns {object} {error?, targetId, tops, blockIds, commentIds, createdVars, notes}
 */
// 모르는 말을 작업 영역 주석으로도 남길지(2026-10-06 직원 의견으로 끔). 켜면 예전처럼 스크립트 옆에 노란 메모
const UNKNOWN_AS_COMMENT = false;
const applyToVm = (vm, ir) => {
    const target = vm.editingTarget;
    if (!target || target.isStage) return {error: 'stage'};
    const stage = vm.runtime.getTargetForStage();
    const notes = [];
    const varIds = {};
    const createdVars = [];
    (ir.broadcasts || []).forEach(name => {
        const found = stage.lookupBroadcastMsg ? stage.lookupBroadcastMsg(null, name) : null;
        if (found) { varIds[`bc:${name}`] = found.id; return; }
        const id = rid();
        stage.createVariable(id, name, 'broadcast_msg');
        varIds[`bc:${name}`] = id;
        createdVars.push(id);
    });
    (ir.lists || []).forEach(name => {
        const found = target.lookupVariableByNameAndType(name, 'list');
        if (found) { varIds[`list:${name}`] = found.id; return; }
        const id = rid();
        stage.createVariable(id, name, 'list');
        varIds[`list:${name}`] = id;
        createdVars.push(id);
        notes.push(langOf(ir).listMade(name));
    });
    ir.variables.forEach(name => {
        const found = target.lookupVariableByNameAndType(name, '');
        if (found) { varIds[name] = found.id; return; }
        const id = rid();
        stage.createVariable(id, name, '');
        varIds[name] = id;
        createdVars.push(id);
        notes.push(langOf(ir).varMade(name));
    });
    // 로봇 블록을 쓰면 그 확장을 먼저 불러온다(도구상자에도 나타난다). 로봇 연결은 학생이 한다.
    (ir.extensions || []).forEach(id => {
        const em = vm.extensionManager;
        if (!em || em.isExtensionLoaded(id)) return;
        try {
            em.loadExtensionIdSync(id);
        } catch (e) {
            return; // 확장 생성자가 실패해도(카메라·모델 없음) 블록은 넣는다 — 도구상자는 확장 추가로 다시 불러올 수 있다
        }
        notes.push(langOf(ir).extAdded(id));
    });
    const {blocks, tops} = buildBlocks(ir, varIds, `${rid()}_`);

    // 기존 스크립트 아래에 놓는다
    let y = 40;
    const x = 40;
    target.blocks.getScripts().forEach(id => {
        const b = target.blocks.getBlock(id);
        if (b && typeof b.y === 'number') y = Math.max(y, b.y + (countStack(target.blocks, id) * 48) + 80);
    });
    const scriptY = [];
    tops.forEach((id, i) => {
        blocks[id].x = x;
        blocks[id].y = y;
        scriptY[i] = y;
        y += (countStack(blocks, id) * 48) + 80;
    });
    Object.keys(blocks).forEach(id => target.blocks.createBlock(blocks[id]));

    // 못 알아들은 말 → (예전엔) 스크립트 오른쪽에 주석. 2026-10-06 직원 의견 "주석이 너무 많이 생겨요" → 기본은 안 만든다.
    // 모르는 말은 바이브 코딩 창에 점선과 안내로 보인다. 되돌리기: UNKNOWN_AS_COMMENT = true
    const commentIds = [];
    if (UNKNOWN_AS_COMMENT) ir.unparsed.forEach((u, i) => {
        const cid = rid();
        const sy = u.script >= 0 && scriptY[u.script] !== undefined ? scriptY[u.script] : y;
        target.createComment(cid, null, langOf(ir).comment(u.text), x + 460, sy + (i * 130), 260, 110, false);
        commentIds.push(cid);
    });
    if (vm.emitWorkspaceUpdate) vm.emitWorkspaceUpdate();
    if (vm.runtime.emitProjectChanged) vm.runtime.emitProjectChanged();
    return {targetId: target.id, tops, blockIds: Object.keys(blocks), commentIds, createdVars, notes};
};

/**
 * 방금 넣은 것을 지운다. ⭐ 학생이 그 블록을 이미 고쳤으면 지우지 않는다(학생 작업이 항상 우선).
 * @param {VirtualMachine} vm 스크래치 VM
 * @param {object} applied applyToVm() 결과
 * @returns {object} {ok?:true, error?:'gone'|'changed'}
 */
/**
 * 방금 넣은 블록이 그대로인지(학생이 끼우거나 빼거나 옮기지 않았는지). 지우지 않고 보기만 한다.
 * @param {VirtualMachine} vm 스크래치 VM
 * @param {object} applied applyToVm() 결과
 * @returns {string|null} null=그대로, 'gone'=스프라이트 없음, 'changed'=학생이 고침
 */
const checkApplied = (vm, applied) => {
    const target = vm.runtime.getTargetById(applied.targetId);
    if (!target) return 'gone';
    const now = new Set();
    for (const top of applied.tops) {
        const b = target.blocks.getBlock(top);
        if (!b || !b.topLevel) return 'changed';
        reach(target.blocks, top, now);
    }
    const mine = new Set(applied.blockIds);
    if (now.size !== mine.size || [...now].some(id => !mine.has(id))) return 'changed';
    return null;
};
const isIntact = (vm, applied) => checkApplied(vm, applied) === null;

const undoApply = (vm, applied) => {
    const target = vm.runtime.getTargetById(applied.targetId);
    const bad = checkApplied(vm, applied);
    if (bad) return {error: bad};
    applied.tops.forEach(id => target.blocks.deleteBlock(id));
    applied.commentIds.forEach(id => { delete target.comments[id]; });
    const stage = vm.runtime.getTargetForStage();
    applied.createdVars.forEach(vid => {
        const used = vm.runtime.targets.some(t => Object.keys(t.blocks._blocks).some(k => {
            const f = t.blocks._blocks[k].fields;
            return f && ((f.VARIABLE && f.VARIABLE.id === vid) || (f.BROADCAST_OPTION && f.BROADCAST_OPTION.id === vid) || (f.LIST && f.LIST.id === vid));
        }));
        if (!used) stage.deleteVariable(vid);
    });
    if (vm.emitWorkspaceUpdate) vm.emitWorkspaceUpdate();
    if (vm.runtime.emitProjectChanged) vm.runtime.emitProjectChanged();
    return {ok: true};
};


// ─────────────────────────────── 되묻기(값 고르기) ───────────────────────────────
// 번역기가 추측한 값(기본값·"빨리" 같은 막연한 말)은 학생이 고를 수 있게 보기를 붙인다.
// 아티보 "파이썬 도우미"의 빈칸 채우기에서 가져온 생각 — 다만 실행을 막지는 않는다(1단계는 부담 없이).
const isGuess = n => /^얼마나 .*지 말하지 않아서|’[은는] .*정했어요|^무슨 말을 할지|^‘잠깐’은/.test(n);
const ASK_MAP = {
    motion_movesteps: ['STEPS', '얼마나 움직일까요?', [5, 10, 30, 50]],
    motion_changexby: ['DX', '얼마나 움직일까요?', [5, 10, 30, 50]],
    motion_changeyby: ['DY', '얼마나 움직일까요?', [5, 10, 30, 50]],
    motion_turnright: ['DEGREES', '몇 도 돌까요?', [15, 45, 90, 180]],
    motion_turnleft: ['DEGREES', '몇 도 돌까요?', [15, 45, 90, 180]],
    looks_changesizeby: ['CHANGE', '얼마나 바꿀까요?', [5, 10, 30]],
    looks_say: ['MESSAGE', '뭐라고 말할까요?', null],
    looks_think: ['MESSAGE', '뭐라고 생각할까요?', null],
    looks_sayforsecs: ['MESSAGE', '뭐라고 말할까요?', null],
    looks_thinkforsecs: ['MESSAGE', '뭐라고 생각할까요?', null],
    control_wait: ['DURATION', '몇 초 기다릴까요?', [0.5, 1, 2]]
};
const attachAsk = (node, guess) => {
    const spec = ASK_MAP[node.opcode];
    if (!spec || !node.inputs[spec[0]]) return;
    const sign = Number(node.inputs[spec[0]].value) < 0 ? -1 : 1;
    node.ask = {input: spec[0], q: spec[1], options: spec[2] ? spec[2].map(v => v * sign) : null, text: !spec[2], note: guess};
};
const collectAsks = ir => {
    let k = 0;
    ir.asks = [];
    ir.scripts.forEach(sc => walk(sc.body, n => {
        if (!n.ask) return;
        n.ask.id = k++;
        ir.asks.push({id: n.ask.id, q: n.ask.q, options: n.ask.options, text: n.ask.text, current: n.inputs[n.ask.input].value});
    }));
};
const clone = o => JSON.parse(JSON.stringify(o));
const setNumber = (node, input, v) => {
    const old = node.inputs[input].value;
    const nv = fmt(v);
    node.inputs[input] = Object.assign({}, node.inputs[input], {value: nv});
    node.label = String(node.label).replace(old, nv);
    node.sig = String(node.sig).replace(old, nv);
    return {old, nv};
};
const setText = (node, m) => {
    const old = node.inputs.MESSAGE.value;
    node.inputs.MESSAGE = Object.assign({}, node.inputs.MESSAGE, {value: m});
    node.label = String(node.label).replace(old, m);
    node.sig = String(node.sig).replace(`(${old}`, `(${m}`);
    return {old, nv: m};
};
/** 되묻기 답 반영 → 새 번역 결과(원본은 그대로). */
const setAsk = (ir, id, value) => {
    const out = clone(ir);
    let hit = null;
    out.scripts.forEach(sc => walk(sc.body, n => { if (n.ask && n.ask.id === id) hit = n; }));
    if (!hit) return null;
    const {q, input, text, note: guessNote} = hit.ask;
    const ch = text ? setText(hit, String(value)) : setNumber(hit, input, Number(value));
    out.notes = out.notes.filter(x => x !== guessNote);
    delete hit.ask;
    collectAsks(out);
    return {ir: out, changes: [`${q} ${ch.old} → ${ch.nv}`]};
};

// ─────────────────────────────── 고쳐 말하기(바이브 루프) ───────────────────────────────
// 방금 만든 것을 말로 고친다. 고치는 말이 아니면 null → 새 문장으로 번역한다.
// ⭐ 무엇을 몇에서 몇으로 바꿨는지 항상 돌려준다 — "빨리"가 어떤 숫자에 달려 있는지 배우는 순간이다.
const tidy = t => String(t).replace(/[!?~…,;]+/g, ' ').replace(/\.(?!\d)/g, ' ').replace(/\s+/g, ' ').trim()
    .replace(/(?:\s*(?:좀|막|그냥|제발|ㅋ+|ㅎ+|ㄱ+|이요|요|줘|주세요|해\s*줘|해\s*주세요|해|하게|해봐|시켜줘|볼래|안돼|안됨|가능))+$/, '')
    .replace(/^(?:(?:좀|막|그냥|아니|아니야|안돼|안\s*돼|다시|음+|어+|야|앗|아|그럼|근데|이제|좋아|좋아요|좋다|오케이|오케|ㅇㅋ|굿|좋은데)\s+|좀(?=더))+/, '')
    .replace(/^\S*지\s*말고\s*/, '') // "멈추지말고 계속" → "계속"
    .replace(/^(?:그걸|그거|이거|그것)\s*/, '')
    .trim();
const RE_UNDO = /^(?:(?:취소\s*)+(?:해|할래|하기)?|되돌려|되돌리기|되돌릴래|원래대로(?:\s*(?:돌려|해|돌아가|되돌려|바꿔)[가-힣]*)?|(?:아까|방금|전)\s*(?:걸로|거로|것으로|꺼로)\s*(?:돌려|되돌려|바꿔|해)[가-힣]*|(?:둘\s*다\s*|다\s*)?처음(?:으로|처럼)\s*(?:돌려|되돌려|해)[가-힣]*|(?:그만\s*)?원래\s*(?:색|모습|상태)(?:으로|로)?(?:\s*(?:돌려|되돌려|해|바꿔)[가-힣]*)?|무르기|없던\s*걸로|(?:방금|아까)\s*(?:거|것|꺼)(?:\s*다)?\s*(?:되돌리기|되돌려|되돌릴래|취소)[가-힣]*|방금\s*(?:추가한|넣은|만든|붙인)\s*(?:거|것|블록)?\s*(?:빼|지워|없애|취소)[가-힣]*|(?:아까|방금)\s*(?:거|것|꺼)(?:\s*(?:다|좀))?\s*(?:지워|없애|취소)|다\s*지워)$/;
const RE_FOREVER = /^(?:계속\s*반복|계속|무한\s*반복|무한|영원히|끝없이|반복)(?:해|해서|하기|으로|되게|하게|돼|되도록|시켜)?$/;
const RE_REPEAT = new RegExp(`^(?<n>${RE_ANY})\\s*번(?:\\s*(?:반복|더|씩))?(?:해|해서|하기|하게|돼|되게|시켜)?$`);
const RE_SETNUM = /^(?:(?<tw>[가-힣 ]+?)\s*)?(?<n>-?\d+(?:\.\d+)?)\s*(?<u>초|번|도|만큼|점|칸|걸음|%|퍼센트)?\s*(?:으로|로)\s*(?:바꿔|바꾸|해|정해|고쳐|수정|만들어|변경|줄여|줄이|늘려|늘리|올려|내려)/;
// "5초로", "5번으로", "0.5초" — 숫자만 말한 경우
const RE_SETNUM2 = /^(?<n>-?\d+(?:\.\d+)?)\s*(?<u>초|번|도|만큼|점|칸|걸음|발자국|%|퍼센트)?\s*(?:으로|로|만)?\s*(?:해|바꿔|하게|해줘)?$/;
// "아 너무 작다 80" — 끝에 숫자(다른 명령이 없을 때만)
const RE_SETNUM3 = /(?:^|\s)(?<n>-?\d+(?:\.\d+)?)\s*(?<u>초|번|도|%|퍼센트)?\s*(?:으로|로)?$/;
const RE_SAYSWAP = [
    /^(?<old>.+?)\s*말고\s*['"“”‘’]?(?<m>.+?)['"“”‘’]?\s*(?:으로|로)\s*(?:바꿔|바꾸|해|하|고쳐)/,
    /^(?:말|대사|하는\s*말|말하는\s*(?:거|것)|인사)(?:을|를|은|는)?\s*['"“”‘’]?(?<m>.+?)['"“”‘’]?\s*(?:으로|로)\s*(?:바꿔|바꾸|해|고쳐)/,
    /^(?:대신|말고)\s*['"“”‘’]?(?<m>.+?)['"“”‘’]?\s*(?:이?라고)\s*(?:말|해|하)/,
    /^['"“”‘’]?(?<m>.+?)['"“”‘’]?\s*(?:이?라고)\s*(?:말하게|하게|말하도록)?\s*(?:바꿔|고쳐)/
];
const SCALE = [
    {re: /(?:^|\s|더)(?:빨리|빠르게|빨랑)|너무\s*느려|느려|답답|속도\s*(?:를|을)?\s*(?:좀\s*)?(?:올려|높여|빠르게)|(?:두|2)\s*배/, amount: 2, wait: 0.5, what: '빠르게'},
    {re: /(?:^|\s|더)(?:천천히|느리게|느릿)|너무\s*빨라|너무\s*빠르|빨라서|안\s*보여|사라져|속도\s*(?:를|을)?\s*(?:좀\s*)?(?:줄|낮|내려)/, amount: 0.5, wait: 2, what: '느리게'},
    {re: /더\s*(?:많이|멀리|높이|높게|크게|세게|쎄게|길게|확)|확\s*(?:바뀌|변하|커)|팍|너무\s*(?:조금|쪼금|쪼끔|짧|작아|낮아|적게)|(?:쪼금|쪼끔|조금)\s*(?:밖에|만)?\s*(?:바뀜|바뀌|변함|움직)/, amount: 2, wait: 1, what: '더 크게'},
    {re: /(?:^|\s|더)(?:조금만|쪼금만|쪼끔만|쪼끔|살짝만|덜|적게|작게|낮게|짧게)|너무\s*(?:많이|멀리|높아|커|길어)|반\s*정도만?|반만|절반/, amount: 0.5, wait: 1, what: '더 작게'}
];
const AMOUNT = {STEPS: 'move', DX: 'move', DY: 'move', DEGREES: 'turn', CHANGE: 'size', SIZE: 'size', VALUE: 'fx', VOLUME: 'size'};
const catOfWord = (tw, u) => {
    const w = `${tw || ''} ${u || ''}`;
    if (/기다|쉬|시간|초/.test(w)) return 'wait';
    if (/반복|번/.test(w)) return 'times';
    if (/점프|높이|뛰/.test(w)) return 'jump';
    if (/돌|회전|각도|방향|도$/.test(w) || u === '도') return 'turn';
    if (/크기|커|작/.test(w) || u === '%' || u === '퍼센트') return 'size';
    if (/움직|이동|걸음|칸|거리|가는|앞|만큼|느려|느리|빨라|빠르|속도/.test(w)) return 'move';
    return null;
};
// 방금 결과 안의 "바꿀 수 있는 숫자" 목록(점프 세 블록은 한 덩어리)
const numberItems = ir => {
    const items = [];
    ir.scripts.forEach(sc => walk(sc.body, n => {
        if (n.t === 'repeat') { items.push({cat: 'times', node: n, get: () => n.times, set: v => { const o = n.times; n.times = Math.max(1, Math.round(v)); return {old: String(o), nv: String(n.times)}; }, label: '반복 횟수'}); return; }
        if (n.t !== 'act') return;
        if (n.part === 'jumpUp') { items.push({cat: 'jump', node: n, label: '점프 높이'}); return; }
        if (n.part === 'jumpDown' || n.part === 'jumpWait') return;
        Object.keys(n.inputs || {}).forEach(k => {
            if (AMOUNT[k]) items.push({cat: AMOUNT[k], node: n, input: k, label: n.label});
            if (k === 'DURATION' || k === 'BEATS') items.push({cat: 'wait', node: n, input: k, label: n.label});
        });
    }));
    return items;
};
const jumpSet = (ir, up, h) => {
    // 같은 몸통 안에서 jumpUp 바로 뒤의 wait, jumpDown 을 함께 바꾼다
    let done = null;
    ir.scripts.forEach(sc => walk(sc.body, n => {
        if (!n.body && n !== sc) return;
    }));
    const fix = list => list.forEach((n, i) => {
        if (n === up) {
            const down = list[i + 2];
            const o = setNumber(n, 'DY', Math.abs(h));
            if (down && down.part === 'jumpDown') setNumber(down, 'DY', -Math.abs(h));
            done = o;
        }
        if (n.body) fix(n.body);
    });
    ir.scripts.forEach(sc => fix(sc.body));
    return done;
};
// 같은 종류의 블록(동작끼리, 모양끼리 …)이면 "그걸 고치는 말"로 본다
const famOf = op => String(op).split('_')[0];
// 기다리기 넣기 — "안 보여 너무 빨리 사라져", "사이에 좀 쉬었다가"
const insertWaits = (ir, sec) => {
    let added = 0;
    const fix = list => {
        for (let i = 0; i < list.length; i++) {
            const n = list[i];
            if (n.body) fix(n.body);
            if (n.body2) fix(n.body2);
            const next = list[i + 1];
            if (n.opcode === 'looks_hide' && next && next.opcode === 'looks_show') {
                list.splice(i + 1, 0, act('control_wait', {inputs: {DURATION: num(sec, 'math_positive_number')}, sig: `wait${fmt(sec)}`, label: `${fmt(sec)}초 기다리기`}));
                added++;
            }
        }
        list.forEach(n => {
            if ((n.t === 'repeat' || n.t === 'forever') && !n.body.some(b => b.opcode === 'control_wait')) {
                n.body.push(act('control_wait', {inputs: {DURATION: num(sec, 'math_positive_number')}, sig: `wait${fmt(sec)}`, label: `${fmt(sec)}초 기다리기`}));
                added++;
            }
        });
    };
    ir.scripts.forEach(sc => fix(sc.body));
    return added;
};
const opsOf = ir => {
    const ops = [];
    ir.scripts.forEach(sc => walk(sc.body, n => { if (n.t === 'act') ops.push(n.opcode); }));
    return ops;
};

// 이어 붙일 자리 — 방금 만든 마지막 동작이 '만약' 안이면 그 안, '무한 반복' 안이면 그 안(뒤는 실행이 안 된다),
// 'N번 반복' 안이면 반복이 끝난 뒤. ("멈추고 나서 뒤로 가게", "다 세고 나서 끝이라고 말해")
const appendPoint = (script, colorHex) => {
    if (colorHex) {
        let hit = null;
        const find = list => list.forEach(n => {
            if (hit) return;
            if (n.body && n.body.some(b => b.t === 'act' && b.sig === `light:${colorHex}`)) hit = n.body;
            else if (n.body2 && n.body2.some(b => b.t === 'act' && b.sig === `light:${colorHex}`)) hit = n.body2;
            if (!hit && n.body) find(n.body);
            if (!hit && n.body2) find(n.body2);
        });
        find(script.body);
        if (hit) return hit;
    }
    const chain = [{list: script.body, owner: null}];
    let list = script.body;
    for (;;) {
        const last = list[list.length - 1];
        if (!last || last.t === 'act') break;
        const inner = last.body2 && last.body2.length && !last.autoElse ? last.body2 : last.body;
        if (!inner || !inner.length) break;
        chain.push({list: inner, owner: last});
        list = inner;
    }
    for (let i = chain.length - 1; i > 0; i--) {
        const o = chain[i].owner;
        if (o.t === 'if' || o.t === 'ifelse' || o.t === 'forever') return chain[i].list;
    }
    return script.body;
};
// '다시 말하기' — 새로 말한 것이 방금 것과 같은 종류면 통째로 바꾼다("빨강 파랑 1초마다 바뀌게", "한바퀴만 돌고 멈춰")
const hasCond = list => list.some(n => n.t === 'if' || n.t === 'ifelse' || n.t === 'until' || ((n.t === 'forever' || n.t === 'repeat') && hasCond(n.body)));
const RE_THEN = /^(.*?)(?:고\s*나서|고나서|한\s*다음(?:에)?|한\s*뒤(?:에)?|하고\s*나서|그\s*다음에?|그다음에?|그러고\s*나서|(?:다\s*)?끝나면|끝나고|끝난\s*(?:다음|뒤)(?:에)?|다\s*되면)\s+(.+)$/;
const RE_KEEP_GOING = /계속\s*(?:확인|체크|검사|돌|움직|반복|하|되|따라|바뀌|변하|나오|나|울|반짝|깜빡|가)|왜\s*.{0,6}(?:한\s*번|한번)|한\s*번만\s*(?:움직|돌|해|하|나|되|가)|멈추지\s*말고|끝나지\s*(?:않게|말고)|한\s*번(?:만)?\s*(?:하고|움직이고|돌고|되고)?\s*(?:끝|멈)|(?:왜|안)\s*(?:안\s*)?(?:따라|움직|돌|계속)|한\s*번만\s*(?:함|하네|해)/;
const toGlide = n => {
    if (n.opcode === 'motion_goto') {
        const to = n.inputs.TO.value;
        n.opcode = 'motion_glideto';
        n.inputs = {SECS: num(1), TO: menu('motion_glideto_menu', 'TO', to)};
        n.sig = `glide1:${to === '_mouse_' ? 'mouse' : 'random'}`;
        n.label = `1초 동안 ${to === '_mouse_' ? '마우스 포인터' : '무작위 위치'}로 이동하기`;
        return true;
    }
    if (n.opcode === 'motion_gotoxy') {
        n.opcode = 'motion_glidesecstoxy';
        n.inputs = {SECS: num(1), X: n.inputs.X, Y: n.inputs.Y};
        n.sig = `glide1:${n.inputs.X.value},${n.inputs.Y.value}`;
        n.label = `1초 동안 x: ${n.inputs.X.value} y: ${n.inputs.Y.value} 으로 이동하기`;
        return true;
    }
    return false;
};
const KEYDIR = {'up arrow': ['y', 1], 'down arrow': ['y', -1], 'left arrow': ['x', -1], 'right arrow': ['x', 1], w: ['y', 1], s: ['y', -1], a: ['x', -1], d: ['x', 1]};

/**
 * @param {object} prev 방금 만든 번역 결과(VM 에 들어가 있는 것)
 * @param {string} text 학생이 이어서 한 말
 * @param {object} ctx {soundNames}
 * @returns {null|object} {kind:'undo'} · {kind, ir, changes, notes, echo} · {kind, ir:null, message}
 */
const penUpN = () => act('pen_penUp', {sig: 'pen:up', label: '펜 올리기'});
const LED_NUM_K = {1: '0018181c1818187e', 2: '003c6660300c067e', 3: '003c66603860663c', 4: '00303834327e3030', 5: '007e063e6060663c',
    6: '003c66063e66663c', 7: '007e663030181818', 8: '003c66663c66663c', 9: '003c66667c60663c', 0: '003c66766e66663c'};
const penDownN = () => act('pen_penDown', {sig: 'pen:down', label: '펜 내리기'});
// 반복으로 감쌀 몸통: 처음·끝이 보이는 블록(모양·LED·말·불빛)이고 가운데 기다리기가 있으면 끝에도 같은 기다리기 — 없으면 마지막 모습이 바로 지나간다
const RE_VISIBLE = /^looks_(?:switchcostume|nextcostume|say$|think$|switchbackdrop|nextbackdrop)|^cubroidled_|^cubroidmaster_cubroidMasterColorControl$/;
const tailWait = body => {
    if (body.length < 3) return null;
    const w = body.find(n => n.opcode === 'control_wait');
    const lastN = body[body.length - 1];
    if (!w || !RE_VISIBLE.test(body[0].opcode || '') || !RE_VISIBLE.test(lastN.opcode || '')) return null;
    const c = clone(w);
    body.push(c);
    return `마지막 모습도 보이게 끝에 ‘${c.label}’를 넣었어요.`;
};
const refine = (prev, text, ctx) => {
    if (!prev || !prev.scripts || !prev.scripts.length) return null;
    if (prev.lang && prev.lang !== 'ko' && LANGS[prev.lang] && LANGS[prev.lang].refine) return LANGS[prev.lang].refine(prev, text, ctx);
    const t = tidy(text);
    if (!t) return null;
    if (/점수/.test(t) && /있어야|넣어|만들어/.test(t) && opsOf(prev).some(op => /variable/.test(op))) {
        let hasScore = false;
        prev.scripts.forEach(sc => walk(sc.body, x => { if (x.varName === '점수') hasScore = true; }));
        if (hasScore) return {kind: 'explain', ir: null, message: '점수는 이미 있어요. 변수 칸의 ‘점수’를 체크하면 무대에 보여요.'};
    }
    const out = clone(prev);
    const last = out.scripts[out.scripts.length - 1];
    const echo = {text: String(text), segments: []};
    const finish = (kind, changes, notes) => {
        collectAsks(out);
        out.variables = [];
        out.scripts.forEach(sc => walk(sc.body, n => { if (n.varName && out.variables.indexOf(n.varName) < 0) out.variables.push(n.varName); }));
        out.extensions = collectExtensions(out);
        out.broadcasts = collectBroadcasts(out);
        out.lists = collectLists(out);
        return {kind, ir: out, changes, notes: notes || [], echo};
    };

    // "이제 멈춰", "전부 다 멈춰" — '모두 멈추기'를 새로 만들면 시작하자마자 멈춰 버린다 → 방법을 알려 준다
    // "꺼", "불 꺼" — 불만 켜 둔 거라면 끄는 블록을 붙인다(정지 안내가 아니라)
    if (/^(?:이제\s*|다시\s*|그만\s*|그냥\s*)?(?:불(?:빛)?(?:을|도)?\s*)?(?:꺼|끄기|꺼줘|끄자|꺼주세요|끄게\s*해줘)$/.test(t) && opsOf(prev).indexOf('cubroidmaster_cubroidMasterColorControl') >= 0 &&
        !opsOf(prev).some(op => /_dcMotorControl$/.test(op))) {
        const out0 = clone(prev);
        const l0 = out0.scripts[out0.scripts.length - 1];
        const blinkF = l0.body.find(n => n.t === 'forever' && !hasCond(n.body) && n.body.some(b => b.opcode === 'cubroidmaster_cubroidMasterColorControl'));
        if (blinkF) {
            l0.body = l0.body.filter(n => n !== blinkF).concat([colorOff()]);
            collectAsks(out0);
            out0.extensions = collectExtensions(out0);
            return {kind: 'restate', ir: out0, changes: ['깜빡이기를 멈추고 불을 끄게 바꿨어요.'], notes: [], echo: {text: String(text), segments: []}};
        }
        if (l0.body.some(n => n.t === 'forever')) return {kind: 'explain', ir: null, message: '계속 도는 중이라 무대 위 빨간 정지 버튼을 누르면 멈춰요. 불까지 끄려면 ‘불 꺼’ 블록을 클릭하세요.'};
        l0.body.push(waitN(1), colorOff());
        collectAsks(out0);
        out0.extensions = collectExtensions(out0);
        return {kind: 'append', ir: out0, changes: ['1초 뒤에 불을 끄게 ‘1초 기다리기 → 불빛 끄기’를 붙였어요.'], notes: [], echo: {text: String(text), segments: []}};
    }
    if (/^(?:(?:됐다|됐어|됐어요|됐고|이제|이제는|그만|전부|다|모두|아|자|좀|그럼)\s*)*(?:그만|멈춰|멈추|멈춤|정지|스톱|꺼|끝내)(?:게)?(?:\s*해)?(?:줘|요|라|어|주세요)?(?:\s*(?:그만|멈춰|정지|스톱|멈춰요))*$/.test(t)) {
        return {kind: 'explain', ir: null, message: '실행 중인 걸 멈추려면 무대 위 빨간 정지 버튼을 눌러요. 몇 번만 하고 멈추게 하려면 ‘5번만’, 시간을 정하려면 ‘10초만 하고 멈춰’처럼 말해 보세요.'};
    }
    // "원래대로 되돌려줘" — '되돌려' 는 방금 고친 걸 취소하는 뜻(모습 되돌리기보다 먼저)
    if (/되돌/.test(t) && t.length <= 14 && RE_UNDO.test(t)) return {kind: 'undo'};
    // "원래대로", "처음으로 돌려놔" — 모양을 바꾸는 블록(효과·크기·모양·배경)이 있으면 처음 모습으로 되돌리는 블록을 붙인다
    if (/원래(?:대로|\s*(?:색|모습|상태|크기|배경|모양))|처음(?:으로|\s*모습|처럼|\s*배경)/.test(t)) {
        const ops0 = opsOf(prev);
        const resets = [];
        if (ops0.some(op => /effect/.test(op))) resets.push(act('looks_cleargraphiceffects', {sig: 'cleareffects', label: '그래픽 효과 지우기'}));
        if (ops0.some(op => /size/.test(op))) resets.push(act('looks_setsizeto', {inputs: {SIZE: num(100)}, sig: 'size=100', label: '크기를 100 %로 정하기'}));
        if (ops0.some(op => /costume/.test(op))) resets.push(act('looks_switchcostumeto', {inputs: {COSTUME: menu('looks_costume', 'COSTUME', ((ctx && ctx.costumeNames) || ['모양1'])[0])}, sig: 'costume=first', label: `모양을 ${((ctx && ctx.costumeNames) || ['모양1'])[0]}(으)로 바꾸기`}));
        if (ops0.some(op => /backdrop/.test(op))) resets.push(act('looks_switchbackdropto', {inputs: {BACKDROP: menu('looks_backdrops', 'BACKDROP', ((ctx && ctx.backdropNames) || ['배경1'])[0])}, sig: 'backdrop=first', label: `배경을 ${((ctx && ctx.backdropNames) || ['배경1'])[0]}(으)로 바꾸기`}));
        if (resets.length && /클릭하면|클릭할\s*때|클릭했을\s*때/.test(t)) {
            // "클릭하면 원래 크기로" — 클릭했을 때 새 스크립트(깃발 스크립트에 붙이면 바로 덮어쓴다)
            out.scripts.push({hat: act('event_whenthisspriteclicked', {sig: 'click', label: '이 스프라이트를 클릭했을 때'}), body: resets});
            return finish('add', resets.map(r => `클릭하면 ‘${r.label}’ 하는 스크립트를 새로 만들었어요.`));
        }
        if (resets.length) {
            last.body = last.body.filter(x => x.t !== 'forever').concat(resets);
            return finish('reset', resets.map(r => `‘${r.label}’를 넣어 처음 모습으로 돌아가게 했어요.`));
        }
    }
    if (/^(?:다|모두|전부|그림(?:을)?|화면(?:을)?)\s*(?:다\s*)?지워(?:줘|주세요)?$/.test(t) && opsOf(prev).some(op => /^pen_/.test(op))) {
        return {kind: 'explain', ir: null, message: '그림은 깃발을 누를 때마다 맨 앞의 ‘모두 지우기’ 블록이 지워 줘요. 지금 바로 지우려면 펜 블록의 ‘모두 지우기’를 한 번 클릭하세요. 만든 블록을 없애려면 ‘방금 거 취소해’라고 말하거나 ‘만든 블록 지우기’ 버튼을 눌러요.'};
    }
    if (t.length <= 14 && RE_UNDO.test(t)) return {kind: 'undo'};
    if (t.length <= 18 && !/말고|대신/.test(t) && /(?:되돌려|되돌리|취소|무르|없던\s*걸로)(?:줘|주세요|해|해줘|해주세요|하자)?$/.test(t)) return {kind: 'undo'};
    if (/점수/.test(t) && /있어야|넣어|만들어/.test(t) && opsOf(prev).some(op => /variable/.test(op))) {
        let hasScore = false;
        prev.scripts.forEach(sc => walk(sc.body, x => { if (x.varName === '점수') hasScore = true; }));
        if (hasScore) return {kind: 'explain', ir: null, message: '점수는 이미 있어요. 변수 칸의 ‘점수’를 체크하면 무대에 보여요.'};
    }
    // 방금 만든 게 로봇 블록이면 "속도 줄여", "3초만 가" 도 로봇 이야기다(대화 보류 시험에서 고양이로 오역한 실측)
    let lastVar = null;
    prev.scripts.forEach(sc => walk(sc.body, n => { if (n.varName && n.varName !== '숫자') lastVar = n.varName; }));
    const rctx = Object.assign({}, ctx, {robot: opsOf(prev).some(op => /^cubroid/.test(op)), lastVar,
        hasAsk: opsOf(prev).indexOf('sensing_askandwait') >= 0, ledCtx: opsOf(prev).some(op => /^cubroidled_/.test(op)) ? 'LED' : ''});
    const probe0 = translate(text, rctx);
    const probeOps = opsOf(probe0);
    const prevFams = opsOf(prev).map(famOf);
    const prevOps = opsOf(prev);
    const sameKind = probeOps.every(op => prevFams.indexOf(famOf(op)) >= 0) && !probe0.scripts.some(sc => !sc.implicit);
    // ═══ 20차 이어 말하기(연습 12차 실패에서) ═══
    // "됐어 고마워"
    if (/^(?:(?:아|오|와)\s*)?(?:(?:됐어|됐다|됐어요|좋아|좋아요|고마워|고마워요|감사|감사합니다|굿|완벽|최고|ㅇㅋ|오케이|오키|잘\s*됐어?|멋져|대박)\s*)+$/.test(t)) {
        return {kind: 'explain', ir: null, message: '좋아요! 깃발을 눌러 실행해 보세요. 더 바꾸고 싶으면 말해 주세요.'};
    }
    // "세모 말고 네모" — 도형 바꾸기
    const shSw = /(?:정사각형|사각형|네모|삼각형|세모|오각형|육각형|별|원|동그라미)\s*(?:말고|대신에?|아니고|아니라)\s*(?<sh>정사각형|사각형|네모|삼각형|세모|오각형|육각형|별|원|동그라미)/.exec(t);
    if (shSw && prevOps.indexOf('pen_penDown') >= 0) {
        const pr = translate(`펜으로 ${shSw.groups.sh} 그려줘`, rctx);
        if (pr.scripts.length) { last.body = pr.scripts[0].body; return finish('restate', [`${shSw.groups.sh}(으)로 바꿨어요.`], pr.notes.filter(x => x !== NOTE.hat)); }
    }
    // "복제본들 다 다른 색으로"
    const cloneSc0 = out.scripts.find(sc => sc.hat.opcode === 'control_start_as_clone');
    if (cloneSc0 && /다른\s*색|색(?:깔)?(?:이|을)?\s*(?:다\s*)?다르게|색깔\s*랜덤|무작위\s*색|알록달록/.test(t)) {
        cloneSc0.body.unshift(act('looks_changeeffectby', {fields: fld('EFFECT', 'COLOR'), inputs: {CHANGE: inN(act('operator_random', {inputs: {FROM: num(1), TO: num(200)}, sig: 'random1-200', label: '1부터 200 사이의 난수'}))}, sig: 'color+random', label: '색깔 효과를 (난수)만큼 바꾸기'}));
        return finish('append', ['복제될 때마다 색깔 효과를 무작위로 바꿔서 다 다른 색이 돼요.']);
    }
    // "별 여러개"(도형) — 여기저기 여러 개
    const manyShape = /(?:(?<n>\d+)\s*번\s*반복(?:해서|하고)?\s*)?(?:별|도형|네모|세모|원|모양)?\s*(?:여러\s*개|(?<n2>\d+)\s*개)(?:\s*(?:그려|그리|만들))?/.exec(t);
    if (manyShape && /여러|개/.test(t) && prevOps.indexOf('pen_penDown') >= 0) {
        const shapeRp = last.body.find(n => n.t === 'repeat' && n.body.some(b => /^motion_turn/.test(b.opcode || '')));
        if (shapeRp) {
            const n = Number(manyShape.groups.n || manyShape.groups.n2) || 5;
            last.body = [act('pen_clear', {sig: 'pen:clear', label: '모두 지우기'}), {t: 'repeat', times: n, body: [penUpN(), act('motion_goto', {inputs: {TO: menu('motion_goto_menu', 'TO', '_random_')}, sig: 'gotorandom', label: '무작위 위치로 이동하기'}), penDownN(), shapeRp, penUpN()]}];
            return finish('repeat', [`무작위 위치로 옮겨 가며 ${n}개를 그려요(모두 지우기는 맨 앞에 한 번만).`]);
        }
    }
    // "다시 처음처럼 하나만"(여러 개 그리기) — 하나로
    if (/처음처럼|원래대로|하나만|한\s*개만/.test(t) && prevOps.indexOf('pen_penDown') >= 0) {
        const outer = last.body.find(n => n.t === 'repeat' && n.body.some(b => b.t === 'repeat'));
        if (outer) {
            const inner = outer.body.find(b => b.t === 'repeat');
            last.body = [act('pen_clear', {sig: 'pen:clear', label: '모두 지우기'}), penDownN(), inner, penUpN()];
            return finish('restate', ['처음처럼 하나만 그리게 되돌렸어요.']);
        }
    }
    // "너무 커 150으로"(크기) — 크기를 그 값으로 정하기
    const sizeTo20 = /(?<n>\d+)\s*(?:%|퍼센트|프로)?\s*(?:으로|로)(?:\s*(?:해|정해|바꿔|줄여|키워))?[가-힣]*$/.exec(t);
    if (sizeTo20 && /너무\s*(?:커|작아|크|작)|크기|사이즈/.test(t) && prevOps.some(op => /^looks_(?:change|set)size/.test(op))) {
        const v = Number(sizeTo20.groups.n);
        let hit = 0;
        out.scripts.forEach(sc => walk(sc.body, n => { if (n.opcode === 'looks_changesizeby' || n.opcode === 'looks_setsizeto') { n.opcode = 'looks_setsizeto'; n.inputs = {SIZE: num(v)}; delete n.ask; n.sig = `size=${v}`; n.label = `크기를 ${v} %로 정하기`; hit++; } }));
        if (hit) return finish('number', [`크기를 ${v}%로 정하게 바꿨어요.`]);
    }
    // "더 촘촘하게"(나선)
    if (/촘촘|빽빽|좁게|조밀/.test(t) && prevOps.indexOf('pen_penDown') >= 0) {
        let ch = null;
        walk(last.body, n => { if (!ch && n.opcode === 'data_changevariableby') ch = n; });
        if (ch) { setNumber(ch, 'VALUE', Number(ch.inputs.VALUE.value) / 2); return finish('number', ['한 바퀴마다 조금만 길어지게 해서 선이 더 촘촘해요.']); }
    }
    // "색깔 무지개로 바뀌면서"(펜) — 펜 색 바꾸기
    if (/무지개|색(?:깔)?(?:이)?\s*(?:계속\s*)?바뀌|알록달록/.test(t) && prevOps.indexOf('pen_penDown') >= 0) {
        let rp = null;
        walk(last.body, n => { if (!rp && (n.t === 'repeat' || n.t === 'forever') && n.body.some(b => b.opcode === 'motion_movesteps')) rp = n; });
        if (rp && !rp.body.some(b => b.opcode === 'pen_changePenColorParamBy')) {
            rp.body.push(act('pen_changePenColorParamBy', {inputs: {COLOR_PARAM: menu('pen_menu_colorParam', 'colorParam', 'color'), VALUE: num(5)}, sig: 'pen:color+5', label: '펜 색깔을 5만큼 바꾸기'}));
            return finish('append', ['한 번 움직일 때마다 펜 색깔을 조금씩 바꿔서 무지개처럼 그려요.']);
        }
    }
    // "다시 보여줘"(숨긴 뒤)
    if (/^(?:다시\s*)?(?:보여|나타나|보이게|나와)|다시\s*(?:보여|나타나|보이게|나와)|도로\s*보여/.test(t) && /다시|도로|또/.test(text) && prevOps.indexOf('looks_hide') >= 0 && prevOps.indexOf('looks_show') < 0) {
        const secS = /(\d+(?:\.\d+)?)\s*초/.exec(t);
        const v = secS ? Number(secS[1]) : 1;
        last.body.push(waitS(v), act('looks_show', {sig: 'show', label: '보이기'}));
        return finish('append', [`${fmt(v)}초 뒤에 다시 보이게 했어요.`]);
    }
    // "뒤로 가고 오른쪽으로 돌아서 다시 출발"(장애물 반복) — 조건 안만 바꾸기
    {
        let br = null;
        walk(last.body, n => { if (!br && (n.t === 'if' || n.t === 'ifelse') && /obstacle|proximity/.test(String(n.cond && n.cond.sig))) br = n; });
        if (br && probe0.scripts.length === 1 && probe0.scripts[0].implicit && probeOps.length && probeOps.every(op => /_dcMotor|^control_wait$/.test(op)) && !hasCond(probe0.scripts[0].body) && /뒤로|출발|다시\s*앞으로/.test(t) && !/멈추고|멈춘\s*(?:다음|뒤)|멈추면|그\s*다음|그리고\s*나서|다음에/.test(t)) {
            br.body = probe0.scripts[0].body.filter(x => x.t === 'act');
            dedupeStops(br.body);
            if (br.t === 'if') { br.t = 'ifelse'; br.body2 = drive('forward', null, false); }
            return finish('restate', ['장애물이 있을 때 할 일을 새로 말한 대로 바꿨어요(없으면 계속 앞으로 가요).']);
        }
    }
    // "솔라시도까지 다해줘" — 음계 이어서 끝까지
    if (/까지\s*(?:다|전부|쭉|이어서|연주|계속)|높은\s*도\s*까지|끝까지\s*(?:다|쭉)|[도레미파솔라시]{2,}\s*까지/.test(t) && prevOps.indexOf('cubroidsound_soundBeatControl') >= 0) {
        const notes0 = [];
        walk(last.body, n => { if (n.opcode === 'cubroidsound_soundBeatControl') notes0.push(n); });
        const order = ['도', '레', '미', '파', '솔', '라', '시'];
        const lastN = notes0[notes0.length - 1];
        const k = order.find(x => String(lastN.inputs.SOUND.value || '').indexOf(x) === 0) || '미';
        const beats = Number(lastN.inputs.BEATS.value) || 0.5;
        const add = order.slice(order.indexOf(k) + 1).map(x => noteBlock(SOLFA[x], beats));
        add.push(act('cubroidsound_soundBeatControl', {inputs: {SOUND: menu('cubroidsound_menu_cubroidSoundAction', 'cubroidSoundAction', '도 C(72)'), BEATS: num(beats)}, sig: 'note:도^', label: `높은 도 C(72) 음을 ${beats}박자 연주하기`}));
        appendPoint(last, null).push(...add);
        return finish('append', [`${add.length}음을 이어서 높은 도까지 연주해요.`]);
    }
    // "안되는데요 계속 켜져있어요"(밤 조명)
    // ═══ 23차 이어 말하기(연습 15차 실패에서) ═══
    const mvR3 = v => act('motion_movesteps', {inputs: {STEPS: num(v)}, sig: `move${fmt(v)}`, label: `${fmt(v)}만큼 움직이기`});
    const turnR3 = (right, d) => act(right ? 'motion_turnright' : 'motion_turnleft', {inputs: {DEGREES: num(d)}, sig: `${right ? 'right' : 'left'}${d}`, label: `${right ? '오른쪽' : '왼쪽'}으로 ${d}도 돌기`});
    const stopAllR3 = () => act('control_stop', {fields: fld('STOP_OPTION', 'all'), mutation: {tagName: 'mutation', children: [], hasnext: 'false'}, sig: 'stop', label: '멈추기 (모두)', cap: true});
    // "소리가 안 나는데?"
    if (/소리(?:가)?\s*안\s*(?:나|들려|남|나요|들림)/.test(t) && prevOps.some(op => /^sound_play/.test(op))) {
        return {kind: 'explain', ir: null, message: '블록은 맞아요. ① 컴퓨터 음량과 무대 오른쪽 위 스피커, ② 소리 탭에 그 소리가 있는지, ③ 시작 조건(깃발·클릭)을 했는지 확인해 보세요. 그래도 안 나면 ‘음량 100으로’라고 말해 보세요.'};
    }
    // "안 멈추는데요?"(로봇) — 끄기 블록이 있으면 점검 안내
    if (/안\s*멈|안멈|계속\s*(?:가요|가는데|돌아요|돌아|움직여)/.test(t) && prevOps.some(op => /_dcMotorStop$/.test(op)) && !last.body.some(n => n.t === 'forever')) {
        const w = []; walk(last.body, n => { if (n.opcode === 'control_wait') w.push(Number(n.inputs.DURATION.value)); });
        return {kind: 'explain', ir: null, message: `‘끄기’ 블록이 있어서 ${w.length ? `${fmt(w[0])}초 뒤에 ` : ''}멈춰요. 안 멈추면 ① 로봇 연결(주황 버튼)이 끊기지 않았는지 ② 깃발을 다시 눌렀는지 확인하고, 지금 당장 멈추려면 무대 위 빨간 정지 버튼을 누르세요. 시간을 바꾸려면 ‘2초만 가고 멈춰’처럼 말해요.`};
    }
    // "따라오는게 좀 부드러웠으면" — 마우스 따라가기를 미끄러지기로
    if (/부드럽|부드러|스무스|뚝뚝|끊기|끊겨|덜컥/.test(t)) {
        let g0 = 0;
        out.scripts.forEach(sc => walk(sc.body, x => { if (x.opcode === 'motion_goto' && x.inputs.TO.value === '_mouse_' && toGlide(x)) g0++; }));
        if (g0) return finish('scale', ['순간이동 대신 ‘1초 동안 마우스 포인터로 이동하기’로 바꿔서 부드럽게 따라가요.']);
    }
    // "1점 말고 10점씩" — 올리는 양
    {
        const pm = /(?:(?<a>\d+)\s*점\s*말고\s*)?(?<b>\d+)\s*점\s*씩(?:으로)?(?:\s*(?:올려|올라|해|바꿔|줘)[가-힣]*)?$/.exec(t);
        const chs = []; out.scripts.forEach(sc => walk(sc.body, n => { if (n.opcode === 'data_changevariableby' && /점수|score/i.test(n.varName || '')) chs.push(n); }));
        if (pm && chs.length) {
            const v = Number(pm.groups.b);
            chs.forEach(n => { n.inputs.VALUE = num(v); n.sig = `${n.varName}+${v}`; n.label = `${eul(n.varName)} ${v}만큼 바꾸기`; });
            return finish('number', [`한 번에 ${v}점씩 올라가요.`]);
        }
    }
    // "문제를 5개로 늘려"(퀴즈)
    {
        const qm = /문제(?:를|가|는)?\s*(?<n>\d+)\s*(?:개|문제|번)(?:로|으로)?(?:\s*(?:늘려|줄여|해|바꿔|만들어)[가-힣]*)?/.exec(t);
        let rq = null; walk(last.body, n => { if (!rq && n.t === 'repeat' && n.body.some(b => b.opcode === 'sensing_askandwait')) rq = n; });
        if (qm && rq) {
            const n = Number(qm.groups.n);
            if (rq.times === n) return {kind: 'explain', ir: null, message: `이미 ${n}문제예요(‘${n}번 반복하기’). 다른 개수로 하려면 숫자를 말해 주세요.`};
            const o = rq.times; rq.times = n;
            return finish('repeat', [`문제 수 ${o} → ${n}`]);
        }
    }
    // "맞으면 점수 1 올려" — 이미 정답 칸에서 점수를 올리면 안내
    if (/맞으면|맞추면|정답이면/.test(t) && /점수/.test(t) && /올려|올리|더해|추가|\+/.test(t)) {
        let has = false;
        out.scripts.forEach(sc => walk(sc.body, n => { if ((n.t === 'if' || n.t === 'ifelse') && n.cond && /^answer/.test(String(n.cond.sig)) && n.body.some(b => b.opcode === 'data_changevariableby')) has = true; }));
        if (has) return {kind: 'explain', ir: null, message: '이미 맞히면 ‘점수를 1만큼 바꾸기’가 들어 있어요(정답 칸). 변수 칸의 ‘점수’를 체크하면 무대에 보여요.'};
    }
    // "두개 겹쳐서 그려줘"(펜) — 조금씩 돌려가며 N번
    {
        const ov = /(?<n>\d+|두|세|네|다섯)\s*(?:개|번)(?:를|을)?\s*(?:겹쳐(?:서)?|겹치게|돌려가면서|돌리면서)\s*(?:그려|그리)[가-힣]*/.exec(t);
        if (ov && prevOps.indexOf('pen_penDown') >= 0) {
            const n = Math.max(2, Math.round(toNum(ov.groups.n) || 2));
            const ci = last.body.findIndex(x => x.opcode === 'pen_clear');
            const head = ci >= 0 ? last.body.slice(0, ci + 1) : [];
            const draw = last.body.slice(ci + 1);
            last.body = head.concat([{t: 'repeat', times: n, body: draw.concat([turnR3(true, Math.round(180 / n))])}]);
            return finish('repeat', [`같은 그림을 ${Math.round(180 / n)}도씩 돌려가며 ${n}번 그려서 겹쳐요.`]);
        }
    }
    // "0 되면 게임오버 말하고 멈추게"(목숨 같은 변수가 있을 때)
    {
        const zm = /^(?<n>-?\d+)\s*(?:이|가|점|개)?\s*(?:되면|이면|이\s*되면|가\s*되면)\s+(?<rest>.+)$/.exec(t);
        let hasCounter = false; walk(last.body, x => { if (x.t === 'repeat' && x.count) hasCounter = true; });
        const vn = lastVar;
        if (zm && vn && /^(?:목숨|생명|체력|라이프|에너지|HP)$/i.test(vn) && !hasCounter && !/타이머|초/.test(t)) {
            const tr = translate(zm.groups.rest, rctx);
            const body = tr.scripts.length ? tr.scripts[0].body : [];
            if (body.length) {
                const cond = act('operator_equals', {inputs: {OPERAND1: inT(V(vn)), OPERAND2: txt(zm.groups.n)}, sig: `${vn}=${zm.groups.n}`, label: `${vn} = ${zm.groups.n}`});
                const node = {t: 'if', noLoop: true, cond, body};
                const fe = last.body.find(x => x.t === 'forever');
                if (fe) fe.body.push(node); else last.body.push({t: 'forever', body: [node]});
                return finish('append', [`‘${vn}’이(가) ${zm.groups.n}이 되면 ${body.map(b => b.label || b.t).join(', ')} 하도록 조건을 넣었어요.`], tr.notes.filter(x => x !== NOTE.hat));
            }
        }
    }
    // "한 숫자당 0.5초씩", "0.5초씩" — 기다리기·말하기 시간 전부
    {
        const sm2 = /(?:(?:한\s*(?:숫자|개|번|글자)|숫자|하나)\s*당\s*)?(?<n>\d+(?:\.\d+)?)\s*초\s*씩(?:\s*(?:으로|만|해|기다려)[가-힣]*)?$/.exec(t);
        if (sm2) {
            const v = Number(sm2.groups.n); let hit = 0;
            walk(last.body, n => {
                if (n.opcode === 'control_wait' && !n.inputs.DURATION.node) { n.inputs.DURATION = num(v, 'math_positive_number'); n.sig = `wait${fmt(v)}`; n.label = `${fmt(v)}초 기다리기`; hit++; }
                if (/^looks_(?:say|think)forsecs$/.test(n.opcode || '')) { n.inputs.SECS = num(v); n.sig = String(n.sig).replace(/,[\d.]+\)$/, `,${fmt(v)})`); n.label = String(n.label).replace(/[\d.]+초 동안/, `${fmt(v)}초 동안`); hit++; }
            });
            if (hit) return finish('number', [`하나에 ${fmt(v)}초씩으로 바꿨어요.`]);
        }
    }
    // "200까지만" — 좌표·크기 한계
    {
        const lm2 = /^(?<n>-?\d+)\s*(?:까지만|까지|넘지\s*않게|이상\s*안\s*가게)$/.exec(t);
        let ch = null; walk(last.body, n => { if (!ch && /^motion_change[xy]by$|^looks_changesizeby$/.test(n.opcode || '')) ch = n; });
        if (lm2 && ch) {
            const v = Number(lm2.groups.n);
            const rep = ch.opcode === 'looks_changesizeby' ? act('looks_size', {sig: 'size', label: '크기'}) : ch.opcode === 'motion_changeyby' ? act('motion_yposition', {sig: 'ypos', label: 'y 좌표'}) : act('motion_xposition', {sig: 'xpos', label: 'x 좌표'});
            const up = Number((ch.inputs.DY || ch.inputs.DX || ch.inputs.CHANGE).value) >= 0;
            const cond = act(up ? 'operator_gt' : 'operator_lt', {inputs: {OPERAND1: inT(rep), OPERAND2: txt(String(v))}, sig: `${rep.sig}${up ? '>' : '<'}${v}`, label: `${rep.label} ${up ? '>' : '<'} ${v}`});
            const fi = last.body.findIndex(n => n.t === 'forever');
            const body = fi >= 0 ? last.body[fi].body : [ch];
            const node = {t: 'until', cond, body};
            if (fi >= 0) last.body[fi] = node; else last.body[last.body.indexOf(ch)] = node;
            return finish('restate', [`${rep.label}이(가) ${v}${up ? '을 넘을' : ' 아래로 갈'} 때까지만 되풀이해요.`]);
        }
    }
    // "이번엔 왼쪽" — 도는 방향만
    {
        const dm = /^(?:이번엔|이번에는|이제|이번은|다음엔)?\s*(?<d>왼쪽|오른쪽)(?:으로)?(?:\s*(?:돌|돌아|돌려|회전)[가-힣]*)?$/.exec(t);
        const turns = []; out.scripts.forEach(sc => walk(sc.body, n => { if (/^motion_turn(?:left|right)$/.test(n.opcode || '')) turns.push(n); }));
        if (dm && turns.length && !probeOps.some(op => /^motion_change/.test(op) && false)) {
            const right = dm.groups.d === '오른쪽';
            turns.forEach(n => { n.opcode = right ? 'motion_turnright' : 'motion_turnleft'; n.sig = String(n.sig).replace(/^(?:right|left)/, right ? 'right' : 'left'); n.label = String(n.label).replace(/오른쪽|왼쪽/, dm.groups.d); });
            return finish('flip', [`${dm.groups.d}으로 돌게 바꿨어요(각도는 그대로).`]);
        }
    }
    // "끝나면 하트", "끝나고 야옹" — 다 끝난 뒤에 붙이기
    {
        const em2 = /^(?:다\s*)?(?:끝나면|끝난\s*(?:다음|뒤)에?|마지막에|끝에|끝나고|다\s*하고\s*나서)\s+(?<rest>.+)$/.exec(t);
        if (em2 && !/이?라고/.test(em2.groups.rest) && !last.body.some(n => n.t === 'forever')) {
            const tr0 = translate(em2.groups.rest, rctx);
            const tr = (tr0.scripts.length && tr0.scripts[0].body.length) || !prevOps.some(op => /^cubroidled_/.test(op)) ? tr0 : translate(`LED에 ${em2.groups.rest} 보여줘`, rctx);
            const add = tr.scripts.length ? tr.scripts[0].body : [];
            if (add.length) {
                last.body.push(...add);
                return finish('append', [`다 끝난 뒤에 ${add.map(b => b.label || b.t).join(', ')}를 하게 붙였어요.`], tr.notes.filter(x => x !== NOTE.hat));
            }
        }
    }
    // "숫자가 안 바뀌어", "계속 업데이트되게" — 값 말하기를 계속
    if (/안\s*바뀌|안바뀌|업데이트|갱신|계속\s*(?:바뀌|보여|말|재|나오)/.test(t) && !last.body.some(n => n.t === 'forever')) {
        let rep = false; walk(last.body, n => { if (/^looks_(?:say|think)$/.test(n.opcode || '') && n.inputs.MESSAGE && n.inputs.MESSAGE.node) rep = true; });
        if (rep) {
            last.body = [{t: 'forever', body: last.body.concat([waitS(0.2)])}];
            return finish('forever', ['한 번만 말하고 끝나서 숫자가 안 바뀌었어요. ‘무한 반복하기’ 안에 넣어 0.2초마다 다시 말해요.']);
        }
    }
    // "소리 좀 길게"(N번 삐) — 횟수 두 배
    if (/길게|오래/.test(t) && !/짧게/.test(t)) {
        let rp = null; walk(last.body, n => { if (!rp && n.t === 'repeat' && n.body.some(b => /errorSoundControl$|^sound_play/.test(b.opcode || ''))) rp = n; });
        if (rp) { const o = rp.times; rp.times = o * 2; return finish('repeat', [`이 소리는 길이를 정할 수 없어서 횟수를 ${o} → ${o * 2}번으로 늘려 더 오래 나게 했어요.`]); }
    }
    void mvR3; void stopAllR3;
    // ═══ 22차 이어 말하기(연습 14차 실패에서) ═══
    // "스페이스는 경적 소리"(키 스크립트가 있는 작품) — 새 키 스크립트를 더한다(작품을 갈아엎지 않게)
    {
        const ps = probe0.scripts;
        const keyOf = sc => (sc.hat && sc.hat.opcode === 'event_whenkeypressed' ? sc.hat.fields.KEY_OPTION.value : null);
        const prevKeys = prev.scripts.map(keyOf).filter(Boolean);
        const readsKeys = prevOps.indexOf('sensing_keypressed') >= 0;
        if (ps.length === 1 && keyOf(ps[0]) && ps[0].body.length && (prevKeys.length || readsKeys) && prevKeys.indexOf(keyOf(ps[0])) < 0) {
            out.scripts.push(clone(ps[0]));
            return finish('add', [`‘${keyKo(keyOf(ps[0]))} 키를 눌렀을 때’ 스크립트를 하나 더 만들었어요. 원래 것은 그대로예요.`], probe0.notes.filter(x => x !== NOTE.hat));
        }
    }
    // "더 천천히 깜빡여"(같은 동작을 다시 말하며 빠르기만) — 기다리기만 늘리거나 줄인다
    if (/천천히|느리게|더\s*빠르게|더\s*빨리/.test(t) && !/너무\s*빨|내려|떨어|올라|사라/.test(t) && probeOps.length && probeOps.every(op => prevOps.indexOf(op) >= 0) && prevOps.indexOf('control_wait') >= 0) {
        const slow = /천천히|느리게/.test(t);
        const ch = [];
        walk(last.body, n => {
            if (n.opcode === 'control_wait' && !n.inputs.DURATION.node) {
                const o = Number(n.inputs.DURATION.value);
                const v = Math.round((slow ? o * 2 : o / 2) * 100) / 100;
                n.inputs.DURATION = num(v, 'math_positive_number');
                n.sig = `wait${fmt(v)}`;
                n.label = `${fmt(v)}초 기다리기`;
                ch.push(`${fmt(o)} → ${fmt(v)}초`);
            }
        });
        if (ch.length) return finish('scale', [`기다리는 시간을 ${slow ? '두 배로 늘렸어요' : '반으로 줄였어요'} (${ch[0]}).`]);
    }
    // "처음부터 다시 해볼래"
    if (/^(?:처음부터\s*다시|다시\s*처음부터|처음부터)(?:\s*(?:해|할래|해볼래|해봐|시작)[가-힣]*)?$/.test(t)) {
        return {kind: 'explain', ir: null, message: '초록 깃발을 다시 누르면 처음부터 다시 실행돼요. 아예 새로 만들려면 만들고 싶은 걸 한 문장으로 말해 주세요.'};
    }
    // "아무것도 안 바뀌는데요?"(배경 바꾸기) — 배경이 하나뿐
    if (/안\s*바뀌|안바뀌|그대로|똑같/.test(t) && prevOps.some(op => op === 'looks_nextbackdrop') && ((ctx && ctx.backdropNames) || ['배경1']).length < 2) {
        return {kind: 'explain', ir: null, message: '배경이 ‘배경1’ 하나뿐이라 다음 배경으로 바꿔도 똑같아요. 무대 → 배경 고르기에서 배경을 하나 더 추가한 뒤 깃발을 눌러 보세요.'};
    }
    // "0초 되면 끝 이라고 말하게" — 끝난 뒤에 그 말만
    {
        const em = /^(?:\d+\s*초?\s*(?:이|가)?\s*되면|끝나면|다\s*세면|다\s*끝나면|다\s*되면)\s*['"“”‘’]?(?<m>[^'"“”‘’]+?)['"“”‘’]?\s*(?:이?라고)\s*(?:말|해|하|띄)[가-힣]*$/.exec(t);
        if (em && last.body.some(n => n.t === 'repeat') && !last.body.some(n => n.t === 'forever')) {
            const want = em.groups.m.trim().replace(/[!?.~]+$/, '');
            const dup = last.body.some(n => /^looks_say/.test(n.opcode || '') && n.inputs.MESSAGE && String(n.inputs.MESSAGE.value || '').replace(/[!?.~]+$/, '') === want);
            if (dup) return {kind: 'explain', ir: null, message: `이미 다 세면 ‘${em.groups.m.trim()}’이라고 말해요(맨 끝 블록).`};
            last.body.push(sayOf(em.groups.m.trim(), 2, em.groups.m.trim()));
            return finish('append', [`다 끝나면 ‘${em.groups.m.trim()}’를 2초 동안 말하게 맨 끝에 붙였어요.`]);
        }
    }
    // "너무 커져서 화면 밖으로 나가 적당히 멈추게" — 계속 커지기 → 10번만
    if (/적당히|너무\s*커|그만\s*커|더\s*안\s*커|밖으로\s*나가/.test(t) && /멈|그만|적당/.test(t)) {
        const fi = last.body.findIndex(n => n.t === 'forever' && n.body.some(b => /^looks_changesizeby$|^looks_changeeffectby$/.test(b.opcode || '')));
        if (fi >= 0) {
            last.body[fi] = {t: 'repeat', times: 10, body: last.body[fi].body};
            return finish('repeat', ['계속 커지던 걸 10번만 커지고 멈추게 바꿨어요. 더 크게/작게는 ‘15번으로’처럼 말해 보세요.']);
        }
    }
    // "대답한 이름 넣어서 안녕 ○○ 하게"
    {
        const gm = /(?<g>안녕|반가워|하이|환영해|반갑다)/.exec(t);
        let saysAns = false;
        prev.scripts.forEach(sc => walk(sc.body, n => { if (/^looks_say/.test(n.opcode || '') && n.inputs.MESSAGE && n.inputs.MESSAGE.node) saysAns = true; }));
        if (gm && /대답|이름/.test(t) && /넣어|붙여|으로|같이|써서|넣고/.test(t) && !/앞에|뒤에/.test(t) && !saysAns && prevOps.indexOf('sensing_askandwait') >= 0) {
            const node = sayOf(join(`${gm.groups.g} `, act('sensing_answer', {sig: 'answer', label: '대답'})), 2, `${gm.groups.g} (대답)`);
            const askSc = out.scripts.find(sc => { let h = false; walk(sc.body, n => { if (n.opcode === 'sensing_askandwait') h = true; }); return h; }) || last;
            const ai = askSc.body.findIndex(n => n.opcode === 'sensing_askandwait');
            askSc.body.splice(ai + 1, 0, node);
            return finish('append', [`묻고 기다린 바로 뒤에 ‘${gm.groups.g} (대답)’을 말하게 넣었어요. ‘대답’ 블록에 학생이 쓴 이름이 들어가요.`]);
        }
    }
    // "맞추면 박수소리" — 정답 칸에 소리
    {
        const sm = /^(?:맞추면|맞히면|정답이면|맞으면)\s*(?<w>[가-힣A-Za-z]*?)\s*(?:소리|음)/.exec(t);
        let okBr = null;
        out.scripts.forEach(sc => walk(sc.body, n => { if (!okBr && (n.t === 'ifelse' || n.t === 'if') && n.cond && /^answer/.test(String(n.cond.sig))) okBr = n; }));
        if (sm && !okBr) {
            const si = last.body.findIndex(n => /정답|맞았/.test(String(n.sig || '')));
            if (si >= 0) okBr = {body: {push: x => last.body.splice(si + 1, 0, x)}};
        }
        if (sm && okBr) {
            const names = (ctx && ctx.soundNames) || [];
            const nm = names.find(x => sm.groups.w && (x === sm.groups.w || x.indexOf(sm.groups.w) >= 0)) || names[0] || '야옹';
            okBr.body.push(act('sound_play', {inputs: {SOUND_MENU: menu('sound_sounds_menu', 'SOUND_MENU', nm)}, sig: `sound(${nm})`, label: `${nm} 재생하기`}));
            const miss = sm.groups.w && nm !== sm.groups.w ? [`‘${sm.groups.w}’ 소리가 없어서 ‘${nm}’를 넣었어요. 소리 탭에서 추가하면 블록에서 고를 수 있어요.`] : [];
            return finish('append', ['맞혔을 때(‘만약’ 칸)에 소리를 넣었어요.'].concat(miss));
        }
    }
    // "너무 예민해서 계속 깜빡여"(밝기) — 기준을 낮춰 확실히 어두울 때만
    if (/예민|깜빡|왔다\s*갔다|껐다\s*켰다|켜졌다\s*꺼졌다/.test(t) && prevOps.indexOf('cubroidlighttouch_getBrightness') >= 0) {
        let v0 = null;
        out.scripts.forEach(sc => walk(sc.body, n => {
            if ((n.t === 'if' || n.t === 'ifelse') && n.cond && /^bright/.test(String(n.cond.sig))) {
                const lt = n.cond.opcode === 'operator_lt';
                const nv = lt ? 300 : 700;
                n.cond.inputs.OPERAND2 = txt(String(nv));
                n.cond.sig = `bright${lt ? '<' : '>'}${nv}`;
                n.cond.label = `밝기 ${lt ? '<' : '>'} ${nv}`;
                v0 = nv;
            }
        }));
        if (v0 !== null) return finish('number', [`밝기 기준을 ${v0}(으)로 바꿔서 확실히 ${v0 === 300 ? '어두울' : '밝을'} 때만 바뀌게 했어요. 그래도 깜빡이면 숫자를 더 바꿔 보세요.`]);
    }
    // "3으로 바꿔"(LED 숫자)
    {
        const lm = /^(?<n>\d)\s*(?:으로|로)?(?:\s*(?:바꿔|해|변경|고쳐)[가-힣]*)?$/.exec(t);
        const leds = [];
        out.scripts.forEach(sc => walk(sc.body, n => { if (n.opcode === 'cubroidled_lceNumberControl') leds.push(n); }));
        if (lm && leds.length === 1) {
            const d = lm.groups.n;
            leds[0].fields = fld('MATRIX', LED_NUM_K[d]);
            leds[0].sig = `led:${d}`;
            leds[0].label = `LED에 숫자 ${d} 보여주기`;
            return finish('swap', [`LED 숫자를 ${d}(으)로 바꿨어요.`]);
        }
    }
    // "너무 느려서 안 움직여요 올려줘"(모터 속도)
    if (/느려|올려|높여|안\s*움직|더\s*빠르|빠르게|세게/.test(t) && !/\d/.test(t) && prevOps.some(op => /_dcMotorSetSpeed$/.test(op))) {
        let nv = null;
        out.scripts.forEach(sc => walk(sc.body, n => {
            if (/_dcMotorSetSpeed$/.test(n.opcode || '') && !n.inputs.SPEED.node) {
                const v = Math.min(100, Number(n.inputs.SPEED.value) + 30);
                n.inputs.SPEED = num(v);
                n.sig = String(n.sig).replace(/speed\d+/, `speed${v}`);
                n.label = String(n.label).replace(/\d+%/, `${v}%`);
                nv = v;
            }
        }));
        if (nv !== null) return finish('number', [`모터 속도를 ${nv}%로 올렸어요. 30%보다 낮으면 바퀴가 잘 안 돌 수 있어요.`]);
    }
    // "소리 더 길게"(삐 한 번) — 3번 이어서
    if (/길게|오래/.test(t) && last.body.length === 1 && /errorSoundControl$|^sound_play/.test(last.body[0].opcode || '')) {
        last.body = [{t: 'repeat', times: 3, body: [last.body[0], waitN(0.3)]}];
        return finish('repeat', ['이 소리는 길이를 정할 수 없어서, 0.3초 간격으로 3번 이어서 나게 했어요.']);
    }
    // "그냥 삼각형으로"(펜 도형) — 도형만 바꿔 다시
    {
        const shm = /^(?<sh>정사각형|사각형|네모|삼각형|세모|오각형|육각형|별|원|동그라미)(?:으로|로)(?:\s*(?:바꿔|해|그려)[가-힣]*)?$/.exec(t);
        if (shm && prevOps.some(op => /^pen_/.test(op))) {
            const tr = translate(`${shm.groups.sh} 그려`, rctx);
            if (tr.scripts.length === 1 && tr.scripts[0].body.length) {
                last.body = tr.scripts[0].body;
                return finish('restate', [`${shm.groups.sh}(으)로 바꿔 그려요.`]);
            }
        }
    }
    // ═══ 21차 이어 말하기(연습 13차 실패에서) ═══
    // "밝아져도 불이 안꺼져요" — '만약'만 있고 '아니면'(끄기)이 없음 → 아니면 칸에 끄기
    if (/안\s*꺼|안꺼|계속\s*켜져|안\s*돼|안되/.test(t) && prevOps.indexOf('cubroidlighttouch_getBrightness') >= 0) {
        let fixed = false;
        out.scripts.forEach(sc => walk(sc.body, n => {
            if (!fixed && n.t === 'if' && n.cond && /^bright/.test(String(n.cond.sig)) && n.body.some(b => b.opcode === 'cubroidmaster_cubroidMasterColorControl') && !n.body.some(b => b.opcode === 'cubroidmaster_cubroidColorOffControl')) {
                n.t = 'ifelse';
                n.body2 = [colorOff()];
                fixed = true;
            }
        }));
        if (fixed) return finish('restate', ['‘아니면’ 칸이 비어 있어서 한 번 켜진 불이 안 꺼졌어요. 밝아지면 끄도록 ‘아니면’에 ‘불빛 끄기’를 넣었어요.']);
    }
    // "그거 말고 밝기 효과로" — 효과 종류만 바꾼다
    {
        const FXR = {색깔: 'COLOR', 색: 'COLOR', 어안: 'FISHEYE', 소용돌이: 'WHIRL', 픽셀: 'PIXELATE', 모자이크: 'MOSAIC', 밝기: 'BRIGHTNESS', 유령: 'GHOST', 투명: 'GHOST', 투명도: 'GHOST'};
        const FXK = {COLOR: '색깔', FISHEYE: '어안', WHIRL: '소용돌이', PIXELATE: '픽셀화', MOSAIC: '모자이크', BRIGHTNESS: '밝기', GHOST: '유령'};
        const fm = /(?<fx>색깔|색|어안|소용돌이|픽셀|모자이크|밝기|유령|투명도|투명)\s*효과(?:로|으로|를|을)?(?:\s*(?:바꿔|해|하자|줘))?$/.exec(t);
        const effs = [];
        out.scripts.forEach(sc => walk(sc.body, n => { if (/^looks_(?:change|set)effect/.test(n.opcode || '')) effs.push(n); }));
        if (fm && effs.length) {
            const f = FXR[fm.groups.fx];
            effs.forEach(n => {
                n.fields = fld('EFFECT', f);
                const v = n.inputs.CHANGE ? n.inputs.CHANGE.value : n.inputs.VALUE ? n.inputs.VALUE.value : '';
                n.sig = String(n.sig).replace(/fx:[A-Z]+/, `fx:${f}`);
                n.label = String(n.label).replace(/^\S+ 효과/, `${FXK[f]} 효과`);
                if (!/효과/.test(n.label)) n.label = `${FXK[f]} 효과 ${v}`;
            });
            return finish('swap', [`효과 종류를 ‘${FXK[f]}’(으)로 바꿨어요.`]);
        }
    }
    // "시끄러워" — 계속 나는 소리를 3번만
    if (/시끄러|너무\s*(?:커|크|시끄)|귀\s*아파/.test(t) && !/음량|볼륨|작게|줄여/.test(t)) {
        let done = false;
        out.scripts.forEach(sc => {
            sc.body = sc.body.map(n => {
                let snd = false;
                if (n.t === 'forever') walk(n.body, x => { if (/^sound_play/.test(x.opcode || '')) snd = true; });
                if (snd && !done) { done = true; return {t: 'repeat', times: 3, body: n.body}; }
                return n;
            });
        });
        if (done) return finish('repeat', ['계속 나던 소리를 3번만 나오게 바꿨어요. 소리를 줄이려면 ‘볼륨 줄여’라고 말해 보세요.']);
    }
    // "볼륨 줄여서 다시" — 음량을 50%로 정하기를 맨 앞에(바꾸기 -10 은 거의 안 들린다)
    if (/(?:볼륨|음량|소리)(?:을|를)?\s*(?:좀\s*)?(?:줄여|작게|낮춰|줄이)/.test(t) && prevOps.some(op => /^sound_play/.test(op))) {
        const sc = out.scripts.find(x => { let h = false; walk(x.body, n => { if (/^sound_play/.test(n.opcode || '')) h = true; }); return h; }) || last;
        sc.body = sc.body.filter(n => !/^sound_(?:set|change)volume/.test(n.opcode || ''));
        sc.body.unshift(act('sound_setvolumeto', {inputs: {VOLUME: num(30)}, sig: 'vol=30', label: '음량을 30%로 정하기'}));
        return finish('number', ['맨 앞에 ‘음량을 30%로 정하기’를 넣었어요. 더 작게 하려면 ‘음량 10으로’라고 말해 보세요.']);
    }
    // "정답은 서울이야" — 퀴즈의 정답 글자를 바꾼다
    {
        const am = /^정답(?:은|는|이)?\s*['"“”‘’]?(?<a>[^\s'"“”‘’]+?)['"“”‘’]?(?:이야|야|이에요|예요|이다|다|이지|지|으로|로|이라고|라고)?(?:\s*(?:해|바꿔|해줘|할게|하자))?$/.exec(t);
        let eqs = [];
        out.scripts.forEach(sc => walk(sc.body, n => { if (n.cond && n.cond.opcode === 'operator_equals' && /^answer=/.test(String(n.cond.sig)) && !(n.cond.inputs.OPERAND2 && n.cond.inputs.OPERAND2.node)) eqs.push(n.cond); }));
        if (am && eqs.length) {
            const a = am.groups.a;
            eqs.forEach(c => { c.inputs.OPERAND2 = txt(a); c.sig = `answer=${a}`; c.label = `대답 = ${a}`; });
            return finish('swap', [`정답을 ‘${a}’(으)로 바꿨어요.`]);
        }
    }
    // "생명 3개 추가해"(키 스크립트만 있을 때) — 처음에 N으로 정하기(누를 때마다 늘리면 안 됨)
    {
        const lm = new RegExp(`^(?<v>점수|생명|목숨|체력|코인|에너지|라이프|HP)(?:을|를|도)?\\s*(?<n>\\d+)\\s*(?:개|점)?\\s*(?:추가|넣어|만들어|주고|줘|로\\s*시작|으로\\s*시작)[가-힣]*$`).exec(t);
        let used = false;
        if (lm) prev.scripts.forEach(sc => walk(sc.body, n => { if (n.varName === lm.groups.v) used = true; }));
        if (lm && !used) {
            const setN = setVar(lm.groups.v, toNum(lm.groups.n));
            const flag = out.scripts.find(sc => sc.hat && sc.hat.opcode === 'event_whenflagclicked');
            if (flag) flag.body.unshift(setN);
            else out.scripts.unshift({hat: act('event_whenflagclicked', {sig: 'flag', label: '깃발을 클릭했을 때'}), body: [setN]});
            return finish('add', [`깃발을 누르면 ‘${lm.groups.v}’을(를) ${lm.groups.n}(으)로 정하게 했어요. 줄이려면 ‘닿으면 ${lm.groups.v} 1 줄여’처럼 말해 보세요.`]);
        }
    }
    // "아니 반가워로 바꿔" — 말하기가 있고, 바꿀 말이 블록 이름이 아니면 할 말을 바꾼다
    {
        const sm = /^['"“”‘’]?(?<m>[가-힣A-Za-z!~?]{1,12}?)['"“”‘’]?\s*(?:이?라고|으로|로)\s*(?:바꿔|바꾸|고쳐|해)[가-힣]*$/.exec(t);
        const says0 = [];
        out.scripts.forEach(sc => walk(sc.body, n => { if (/^looks_(?:say|think)/.test(n.opcode || '') && n.inputs.MESSAGE && !n.inputs.MESSAGE.node) says0.push(n); }));
        if (sm && says0.length && !probeOps.length && !/색|쪽|초$|번$|배$|크기|모양|소리|빠르|느리|효과|방향|배경|반복|원래|처음/.test(sm.groups.m)) {
            const ch = setText(says0[says0.length - 1], sm.groups.m);
            delete says0[says0.length - 1].ask;
            return finish('say', [`할 말 ‘${ch.old}’ → ‘${ch.nv}’`]);
        }
    }
    if (/안\s*돼|안되|계속\s*켜져|안\s*꺼|안꺼|이상/.test(t) && prevOps.indexOf('cubroidlighttouch_getBrightness') >= 0) {
        return {kind: 'explain', ir: null, message: '밝기가 500보다 낮으면 켜져요. 교실이 어두우면 계속 켜져 있을 수 있어요. ‘밝기 계속 말해’로 교실 밝기를 재 보고, ‘밝기 300보다 작으면’처럼 기준을 바꿔 보세요.'};
    }
    // "5 4 3 2 1 카운트다운으로"(LED 숫자)
    const seqCd = /(?<seq>\d(?:\s*,?\s*\d){2,8})\s*(?:카운트\s*다운|로|으로|세기)/.exec(t);
    if (seqCd && prevOps.some(op => /^cubroidled_lceNumberControl$/.test(op))) {
        const nodes = [];
        seqCd.groups.seq.match(/\d/g).forEach(d => nodes.push(act('cubroidled_lceNumberControl', {fields: fld('MATRIX', LED_NUM_K[d]), sig: `led:${d}`, label: `LED에 숫자 ${d} 보여주기`}), waitS(1)));
        last.body = nodes;
        return finish('restate', ['LED에 숫자를 1초씩 차례로 보여주게 바꿨어요.']);
    }
    // "소리 너무 시끄러워 한번만 울리게"(장애물이면 삐) — 조건 안에서 한 번만
    if (/한\s*번만|한\s*번씩만|한번만/.test(t) && /울리|소리|삐|경고/.test(t) && prevOps.indexOf('cubroidproximity_isProximity') >= 0) {
        let br = null;
        walk(last.body, n => { if (!br && (n.t === 'if' || n.t === 'ifelse') && /obstacle|proximity/.test(String(n.cond && n.cond.sig))) br = n; });
        if (br) {
            const rp = br.body.find(b => b.t === 'repeat');
            if (rp) br.body.splice(br.body.indexOf(rp), 1, ...rp.body.filter(b => b.opcode !== 'control_wait'));
            if (!br.body.some(b => b.opcode === 'control_wait_until')) br.body.push(act('control_wait_until', {boolIn: {CONDITION: act('operator_not', {boolIn: {OPERAND: clone(br.cond)}, sig: `not:${br.cond.sig}`, label: `${br.cond.label}이(가) 아니다`})}, sig: `until:not:${br.cond.sig}`, label: '물체가 없어질 때까지 기다리기'}));
            return finish('restate', ['가까워질 때 한 번만 울리고, 물체가 없어질 때까지 기다렸다가 다시 확인해요.']);
        }
    }
    // "멈추면 빨간불로 바뀌게"(가면서 불) — 멈춘 뒤에 이어 붙이기
    if (/멈추면|멈춘\s*(?:다음|뒤)에?|서면|멈출\s*때/.test(t) && prevOps.some(op => /_dcMotorStop$/.test(op)) && probe0.scripts.length === 1) {
        const add = probe0.scripts[0].body.filter(x => x.t === 'act' && !/_dcMotor/.test(x.opcode || '') && x.opcode !== 'control_wait');
        if (add.length) { last.body.push(...add); return finish('append', ['멈춘 다음에 하도록 맨 뒤에 붙였어요.']); }
    }
    // "그거 지워줘 불 없이" — 불빛 블록 빼기
    if (/불(?:빛)?\s*(?:없이|빼|지워|없애|말고)|불(?:빛)?(?:은|을)?\s*(?:빼|지워|없애)/.test(t) && prevOps.some(op => /^cubroidmaster_/.test(op))) {
        let hit = 0;
        const rm = list => { for (let i = list.length - 1; i >= 0; i--) { const n = list[i]; if (n.body) rm(n.body); if (n.body2) rm(n.body2); if (/^cubroidmaster_/.test(n.opcode || '')) { list.splice(i, 1); hit++; } } };
        out.scripts.forEach(sc => rm(sc.body));
        if (hit) return finish('remove', [`불빛 블록 ${hit}개를 뺐어요.`]);
    }
    // "0 되면 삐 소리"(카운트다운 뒤)
    if (/^(?:0|영)\s*(?:이\s*)?되면|끝나면|다\s*세면/.test(t) && !/이?라고|하고\s*말|말하|말해|말하게|\s말$/.test(t) && probe0.scripts.length === 1 && probe0.scripts[0].body.length && !hasCond(probe0.scripts[0].body) && (prevOps.indexOf('cubroidled_lceNumberControl') >= 0 || prevOps.indexOf('data_changevariableby') >= 0)) {
        const add = probe0.scripts[0].body.filter(x => x.t === 'act' || x.t === 'repeat');
        let at = last.body.length;
        while (at > 0 && last.body[at - 1].cap) at--;
        last.body.splice(at, 0, ...add);
        return finish('append', ['끝난 다음에 하도록 맨 뒤에 붙였어요.']);
    }
    // ═══ 19차 이어 말하기(연습 11차 실패에서) ═══
    // "아니 그냥 마우스 쪽을 보기만 하게" — 방금 것을 새로 말한 것으로 바꾸기
    const stmOps = ir => opsOf(ir).filter(op => !/^(?:control_|operator_|sensing_|data_)/.test(op));
    if (/^(?:아니|아니야|아니요|아뇨)[\s,]+(?:그냥\s*)?.*만\s*(?:하게|해|해줘)$|^그냥\s.*만\s*(?:하게|해줘)$/.test(String(text).trim()) && !/한\s*번만|반만|\d/.test(t) &&
        probe0.scripts.length === 1 && probe0.scripts[0].implicit && probe0.scripts[0].body.length && !/말고|대신/.test(t) && stmOps(probe0).length && !stmOps(probe0).some(op => prevOps.indexOf(op) >= 0)) {
        last.body = probe0.scripts[0].body;
        return finish('restate', ['방금 만든 것을 새로 말한 것으로 바꿨어요(언제 시작하는지는 그대로).'], probe0.notes.filter(x => x !== NOTE.hat));
    }
    // "대답 앞에 반가워 붙여서 말해" / "대답 뒤에 ~ 붙여"
    const ansSide = /(?:대답|이름)\s*(?<pos>앞|뒤)에\s*[‘'"]?(?<w>[가-힣A-Za-z!~]{1,10}?)[’'"]?\s*(?:을|를|이라고|라고)?\s*붙여/.exec(t) || /[‘'"]?(?<w>[가-힣A-Za-z!~]{1,10}?)[’'"]?\s*(?:을|를)?\s*(?:대답|이름)\s*(?<pos>앞|뒤)에\s*붙여/.exec(t);
    if (ansSide && prevOps.indexOf('sensing_askandwait') >= 0) {
        const w = ansSide.groups.w;
        const a = act('sensing_answer', {sig: 'answer', label: '대답'});
        const j = ansSide.groups.pos === '앞' ? join(`${w} `, a) : join(a, ` ${w}`);
        last.body = last.body.filter(n => !(/^looks_say/.test(n.opcode || '') && n.inputs.MESSAGE && n.inputs.MESSAGE.node && n.inputs.MESSAGE.node.opcode === 'sensing_answer'));
        last.body.push(sayOf(j, 2, ansSide.groups.pos === '앞' ? `${w} (대답)` : `(대답) ${w}`));
        return finish('append', [`대답 ${ansSide.groups.pos}에 ‘${w}’를 붙여 말하게 했어요.`]);
    }
    // "화면 지우는것도 처음에 넣어줘"(펜) — 펜 모두 지우기를 맨 앞에
    if (/(?:화면|그림|선|펜|낙서)\S*\s*(?:다\s*)?지우/.test(t) && prevOps.some(op => /^pen_/.test(op)) && !prevOps.some(op => /^cubroidled_/.test(op))) {
        if (last.body.length && last.body[0].opcode === 'pen_clear') return {kind: 'explain', ir: null, message: '이미 맨 앞에 ‘모두 지우기’가 있어요.'};
        out.scripts.forEach(sc => { sc.body = sc.body.filter(x => x.opcode !== 'pen_clear'); });
        last.body.unshift(act('pen_clear', {sig: 'pen:clear', label: '모두 지우기'}));
        return finish('append', ['맨 앞에 ‘모두 지우기’를 넣어서 깃발을 누를 때마다 화면을 지우고 그려요.']);
    }
    // "바닥 닿으면 다시 위로"(떨어지기) — 계속 떨어지기
    if (/다시\s*(?:위로|위에서|올라)|위로\s*다시/.test(t) && /바닥|아래|밑/.test(t)) {
        const ui = last.body.findIndex(n => n.t === 'until' && n.body.some(b => b.opcode === 'motion_changeyby' && Number(b.inputs.DY.value) < 0));
        if (ui >= 0) {
            const pre = last.body.slice(0, ui).filter(n => n.opcode === 'motion_sety' || n.opcode === 'motion_gotoxy');
            const top = pre.length ? pre : [act('motion_sety', {inputs: {Y: num(170)}, sig: 'sety=170', label: 'y 좌표를 170(으)로 정하기'})];
            last.body = last.body.slice(0, ui).filter(n => pre.indexOf(n) < 0).concat([{t: 'forever', body: top.concat([last.body[ui]])}]);
            return finish('forever', ['바닥에 닿으면 다시 맨 위에서 떨어지게 계속 반복해요.']);
        }
    }
    // "멈추지 말고 피해서 가게"(장애물이면 멈춤) — 멈추는 대신 돌아서 피하기
    if (/피해|피하|피$|돌아서|비켜/.test(t) && prevOps.indexOf('cubroidproximity_isProximity') >= 0) {
        let br = null;
        walk(last.body, n => { if (!br && (n.t === 'if' || n.t === 'ifelse') && /obstacle|proximity/.test(String(n.cond && n.cond.sig)) && n.body.length && n.body.every(b => /_dcMotorStop$/.test(b.opcode || ''))) br = n; });
        if (br) {
            const d = /왼쪽/.test(t) ? 'left' : 'right';
            br.body = drive(d, 0.6, false);
            return finish('restate', [`장애물이 있으면 멈추는 대신 ${d === 'left' ? '왼쪽' : '오른쪽'}으로 0.6초 돌아서 피하게 바꿨어요.`]);
        }
    }
    // "왼쪽으로 피해"(장애물 피하기) — 피하는 방향
    const avoidDir = /(?<d>왼쪽|오른쪽)(?:으로)?\s*(?:피해|피하|피$|돌아|돌게|돌기|꺾)/.exec(t);
    if (avoidDir && prevOps.indexOf('cubroidproximity_isProximity') >= 0) {
        const v = avoidDir.groups.d === '왼쪽' ? 'Left' : 'Right';
        let hit = 0;
        walk(last.body, n => {
            if ((n.t === 'if' || n.t === 'ifelse') && /obstacle|proximity/.test(String(n.cond && n.cond.sig))) {
                const walkB = list => list.forEach((b, i) => { if (b.body) walkB(b.body); if (/_dcMotorControl$/.test(b.opcode || '') && list[i + 1] && /_dcMotorControl$/.test(list[i + 1].opcode || '') && b.fields.INDEX.value === list[i + 1].fields.INDEX.value) { [b, list[i + 1]].forEach(m => { m.fields.INDEX.value = v; m.sig = String(m.sig).replace(/Right|Left/, v); m.label = String(m.label).replace(/오른쪽|왼쪽/, avoidDir.groups.d); }); hit++; } });
                walkB(n.body);
            }
        });
        if (hit) return finish('flip', [`장애물을 만나면 ${avoidDir.groups.d}으로 돌아서 피하게 바꿨어요.`]);
    }
    // "빨간불도 같이 켜줘"(조건 안) — 그 조건 안에 넣기
    if (/도\s*(?:같이|함께)?\s*(?:켜|내|해|보여|띄워)|같이\s*(?:켜|내|해)/.test(t) && hasCond(last.body) && probe0.scripts.length === 1 && probe0.scripts[0].implicit && !hasCond(probe0.scripts[0].body)) {
        let br = null;
        walk(last.body, n => { if (!br && (n.t === 'if' || n.t === 'ifelse')) br = n; });
        let add = probe0.scripts[0].body.filter(x => x.t === 'act' || x.t === 'repeat');
        const colors = add.filter(x => x.opcode === 'cubroidmaster_cubroidMasterColorControl');
        if (colors.length > 1) add = add.filter(x => !(x.opcode === 'cubroidmaster_cubroidMasterColorControl' && /#ffffff/.test(String(x.sig))));
        if (br && add.length) { br.body.push(...add); return finish('append', [`조건(${br.cond.label}) 안에 같이 넣었어요.`]); }
    }
    // ═══ 18차 이어 말하기(연습 10차 실패에서) ═══
    // "둘 다 해줘"(방금 ‘A 말고 B’로 바꿈) — 뺀 것을 다시 넣기
    if (/^(?:그냥\s*)?(?:둘\s*다|두\s*개\s*다|같이\s*다|다\s*같이|둘다)\s*(?:해|하게|넣어|켜|내)?[가-힣]*$/.test(t) && prev.lastSwap && prev.lastSwap.removed.length) {
        let done = false;
        const ins = list => { for (let i = 0; i < list.length && !done; i++) { const n = list[i]; if (n.t === 'act' && n.sig === prev.lastSwap.addedSig) { list.splice(i, 0, ...prev.lastSwap.removed.map(x => clone(x))); done = true; return; } if (n.body) ins(n.body); if (!done && n.body2) ins(n.body2); } };
        ins(last.body);
        delete out.lastSwap;
        if (done) return finish('append', ['뺐던 것도 다시 넣어서 둘 다 하게 했어요.']);
    }
    // "빨간색 굵기 3"(펜) — 색과 굵기를 함께
    if (probeOps.indexOf('pen_setPenSizeTo') >= 0 && probeOps.indexOf('pen_setPenColorToColor') >= 0 && prevOps.indexOf('pen_penDown') >= 0) {
        const adds = probe0.scripts[0].body.filter(x => /^pen_setPen(?:Size|Color)To/.test(x.opcode || ''));
        out.scripts.forEach(sc => { sc.body = sc.body.filter(x => !/^pen_setPen(?:Size|Color)To/.test(x.opcode || '')); });
        last.body.unshift(...adds);
        return finish('append', [`${adds.map(x => `‘${x.label}’`).join(', ')}를 맨 앞에 넣었어요.`]);
    }
    // "크기 더 크게 한변 150" — 말한 숫자가 먼저
    const sideM = /한\s*변(?:은|을|의\s*길이는?|이|길이)?\s*(?<n>\d+)/.exec(t);
    if (sideM && prevOps.indexOf('pen_penDown') >= 0) {
        let rp1 = null;
        walk(last.body, n => { if (!rp1 && n.t === 'repeat' && n.body.some(b => b.opcode === 'motion_movesteps')) rp1 = n; });
        if (rp1) {
            const mv = rp1.body.find(b => b.opcode === 'motion_movesteps');
            const old = mv.inputs.STEPS.value;
            setNumber(mv, 'STEPS', Number(sideM.groups.n));
            return finish('number', [`한 변 ${old} → ${sideM.groups.n}(으)로 바꿨어요.`]);
        }
    }
    // "더 동글게 부드럽게"(원) — 더 잘게 나눠 그리기
    if (/동글|부드럽|매끄럽|둥글/.test(t) && prevOps.indexOf('pen_penDown') >= 0) {
        let rp2 = null;
        walk(last.body, n => { if (!rp2 && n.t === 'repeat' && n.times >= 30 && n.body.some(b => /^motion_turn/.test(b.opcode || ''))) rp2 = n; });
        if (rp2) {
            rp2.times *= 2;
            rp2.body.forEach(b => { if (b.opcode === 'motion_movesteps') setNumber(b, 'STEPS', Number(b.inputs.STEPS.value) / 2); if (/^motion_turn/.test(b.opcode || '')) setNumber(b, 'DEGREES', Number(b.inputs.DEGREES.value) / 2); });
            return finish('number', [`${rp2.times}번 잘게 나눠 그려서 더 동글게 했어요.`]);
        }
    }
    // "벽에 닿으면 튕기게도 추가"(방향키 스크립트들) — 키마다 붙이기
    const keyScs = out.scripts.filter(sc => sc.hat.opcode === 'event_whenkeypressed');
    if (keyScs.length >= 2 && probeOps.length && probeOps.every(op => op === 'motion_ifonedgebounce')) {
        keyScs.forEach(sc => { if (!sc.body.some(b => b.opcode === 'motion_ifonedgebounce')) sc.body.push(act('motion_ifonedgebounce', {sig: 'bounce', label: '벽에 닿으면 튕기기'})); });
        return finish('append', ['방향키 스크립트마다 ‘벽에 닿으면 튕기기’를 붙였어요.']);
    }
    // "10초 말고 20초"(카운트다운) — 시작 값과 반복 횟수 둘 다
    const cntM = /(?<a>\d+)\s*초?\s*말고\s*(?<b>\d+)\s*초?|(?<c>\d+)\s*초\s*(?:로|으로)\s*(?:바꿔|해|늘려|줄여)/.exec(t);
    if (cntM) {
        let setN = null;
        let rp3 = null;
        walk(last.body, n => { if (!setN && n.opcode === 'data_setvariableto') setN = n; if (!rp3 && n.t === 'repeat' && n.body.some(b => b.opcode === 'data_changevariableby' && Number(b.inputs.VALUE.value) === -1)) rp3 = n; });
        const nv = Number(cntM.groups.b || cntM.groups.c);
        if (setN && rp3 && String(setN.inputs.VALUE.value) === String(rp3.times)) {
            setNumber(setN, 'VALUE', nv);
            rp3.times = nv;
            return finish('number', [`${nv}초부터 세도록 시작 값과 반복 횟수를 둘 다 ${nv}(으)로 바꿨어요.`]);
        }
    }
    // "세는 속도 더 빠르게"(카운트다운) — 숫자는 그대로, 한 번에 걸리는 시간만
    if (/세는\s*(?:속도|거|게)|빨리\s*세|빠르게\s*세|세는\s*걸/.test(t) && prevOps.indexOf('data_changevariableby') >= 0) {
        const slow = /느리게|천천히/.test(t);
        let hit = 0;
        walk(last.body, n => {
            if (n.t === 'repeat') n.body.forEach(b => {
                if (/forsecs$/.test(b.opcode || '')) { setNumber(b, 'SECS', slow ? Number(b.inputs.SECS.value) * 2 : Number(b.inputs.SECS.value) / 2); hit++; }
                if (b.opcode === 'control_wait') { setNumber(b, 'DURATION', slow ? Number(b.inputs.DURATION.value) * 2 : Number(b.inputs.DURATION.value) / 2); hit++; }
            });
        });
        if (hit) return finish('number', [`숫자는 그대로 두고, 한 번 세는 시간을 ${slow ? '두 배로' : '절반으로'} 바꿨어요.`]);
    }
    // "위로 말고 아래" — 방향 바꾸기
    const dirSwap = /(?<a>위|아래|왼쪽|오른쪽|앞|뒤)(?:로|으로|쪽으로|쪽)?\s*(?:말고|대신에?|아니고|아니라)\s*(?<b>위|아래|왼쪽|오른쪽|앞|뒤)/.exec(t);
    if (dirSwap && !prevOps.some(op => /^cubroid/.test(op))) {
        const ax = d => (/위|아래/.test(d) ? 'y' : 'x');
        const sgn = d => (/위|오른쪽|앞/.test(d) ? 1 : -1);
        let hit = 0;
        out.scripts.forEach(sc => walk(sc.body, n => {
            if (n.opcode === 'motion_changeyby' || n.opcode === 'motion_changexby') {
                const k = n.inputs.DY ? 'DY' : 'DX';
                const v = Math.abs(Number(n.inputs[k].value)) * sgn(dirSwap.groups.b);
                if (ax(dirSwap.groups.b) === (k === 'DY' ? 'y' : 'x')) { setNumber(n, k, v); hit++; }
                else {
                    const nk = k === 'DY' ? 'DX' : 'DY';
                    n.opcode = nk === 'DY' ? 'motion_changeyby' : 'motion_changexby';
                    n.inputs = {[nk]: num(v)};
                    n.sig = `${nk === 'DY' ? 'y' : 'x'}${v >= 0 ? '+' : ''}${v}`;
                    n.label = `${nk === 'DY' ? 'y' : 'x'} 좌표를 ${v}만큼 바꾸기`;
                    hit++;
                }
            }
        }));
        if (hit) return finish('flip', [`${dirSwap.groups.a} 대신 ${dirSwap.groups.b}(으)로 움직이게 바꿨어요.`]);
    }
    // "뒤집어지는거 싫어 거꾸로 안되게" — 회전 방식 왼쪽-오른쪽을 맨 앞에
    if (/뒤집|거꾸로\s*(?:안|되지|말고)|물구나무/.test(t) && /싫|안\s*되|안되|않게|말고|이상/.test(t) && prevOps.some(op => /^motion_/.test(op))) {
        if (prevOps.indexOf('motion_setrotationstyle') >= 0) return {kind: 'explain', ir: null, message: '이미 ‘회전 방식을 왼쪽-오른쪽으로 정하기’가 있어서 뒤집히지 않아요.'};
        last.body.unshift(act('motion_setrotationstyle', {fields: fld('STYLE', 'left-right'), sig: 'rotstyle:left-right', label: '회전 방식을 왼쪽-오른쪽으로 정하기'}));
        return finish('append', ['맨 앞에 ‘회전 방식을 왼쪽-오른쪽으로 정하기’를 넣어서 벽에서 돌아도 뒤집히지 않게 했어요.']);
    }
    // "좀 더 멀리 왔다갔다"(벽에서 튕기기) — 이미 벽 끝까지
    if (/멀리|넓게|크게/.test(t) && /왔다\s*갔다|왕복|오가/.test(t) && prevOps.indexOf('motion_ifonedgebounce') >= 0) {
        return {kind: 'explain', ir: null, message: '지금도 벽에 닿을 때까지 가서 튕겨요 — 무대 끝에서 끝까지 왔다갔다 해요. 더 빠르게 하려면 ‘더 빠르게’라고 말해 보세요.'};
    }
    // "너무 많아 3마리만"(복제) — 복제 수
    const cloneK = /(?<n>\d+)\s*(?:마리|개)(?:만)?/.exec(t) || /(?<less>너무\s*많|줄여|적게)/.exec(t);
    if (cloneK && prevOps.indexOf('control_create_clone_of') >= 0) {
        let rp4 = null;
        let fe4 = null;
        out.scripts.forEach(sc => walk(sc.body, n => { if (!rp4 && n.t === 'repeat' && n.body.some(b => b.opcode === 'control_create_clone_of')) rp4 = n; if (!fe4 && n.t === 'forever' && n.body.some(b => b.opcode === 'control_create_clone_of')) fe4 = n; }));
        const nv = cloneK.groups.n ? Number(cloneK.groups.n) : (rp4 ? Math.max(1, Math.round(rp4.times / 2)) : 3);
        if (rp4) { rp4.times = nv; return finish('repeat', [`${nv}마리만 복제하게 바꿨어요.`]); }
        if (fe4) { fe4.t = 'repeat'; fe4.times = nv; return finish('repeat', [`계속 복제하던 것을 ${nv}번만 복제하게 바꿨어요.`]); }
    }
    // "바닥에 닿으면 없어지게"(떨어지는 복제본) — 이미
    if (/바닥|아래\s*끝|밑에/.test(t) && /없어|사라|지워|삭제/.test(t) && prevOps.indexOf('control_delete_this_clone') >= 0) {
        return {kind: 'explain', ir: null, message: '이미 복제본이 바닥(y < -170)에 닿으면 ‘이 복제본 삭제하기’로 없어져요.'};
    }
    // "대답하면 이름이랑 같이 안녕 해줘" — 대답을 붙여 말하기
    const ansJoin = /(?:대답|이름)(?:하면|이랑|하고|을|를)?\s*(?:같이|붙여서?|넣어서?)?\s*[‘'"]?(?<w>[가-힣A-Za-z!]{1,10}?)[’'"]?(?:\s*(?:이?라고\s*)?(?:해|말해|인사해|말하게)[가-힣]*)?$/.exec(t);
    if (ansJoin && prevOps.indexOf('sensing_askandwait') >= 0 && /대답|이름/.test(t) && /같이|붙|이랑|하고|넣/.test(t)) {
        const w = ansJoin.groups.w.replace(/^(?:같이|대답|이름)$/, '안녕');
        last.body = last.body.filter(n => !n.cap).concat([sayOf(join(`${w} `, act('sensing_answer', {sig: 'answer', label: '대답'})), 2, `${w} (대답)`)], last.body.filter(n => n.cap));
        return finish('append', [`‘${w}’ 뒤에 대답(이름)을 붙여 말하게 했어요.`]);
    }
    // "틀리면 다시 물어보게"(퀴즈) — 맞을 때까지 반복
    if (/틀리면|못\s*맞추면|오답이면/.test(t) && /다시|또|맞을\s*때까지/.test(t)) {
        const ai = last.body.findIndex(n => n.opcode === 'sensing_askandwait');
        const ie = last.body.findIndex((n, k) => k > ai && n.t === 'ifelse' && n.cond && /^answer=/.test(String(n.cond.sig)));
        if (ai >= 0 && ie > ai) {
            const q = last.body[ai];
            const br = last.body[ie];
            const loop = {t: 'until', cond: clone(br.cond), body: [q, {t: 'if', noLoop: true, cond: act('operator_not', {boolIn: {OPERAND: clone(br.cond)}, sig: `not:${br.cond.sig}`, label: `${br.cond.label}이(가) 아니다`}), body: br.body2}]};
            last.body.splice(ai, ie - ai + 1, loop, ...br.body);
            return finish('repeat', ['맞힐 때까지 다시 묻고, 맞히면 정답 말을 하게 바꿨어요.']);
        }
        let rq = null;
        walk(last.body, n => { if (!rq && n.t === 'repeat' && n.body.some(b => b.opcode === 'sensing_askandwait')) rq = n; });
        if (rq) return {kind: 'explain', ir: null, message: '문제마다 한 번씩 물어요. 틀린 문제를 다시 묻게 하려면 ‘5 곱하기 3 문제 내고 틀리면 다시 물어봐’처럼 한 문제씩 만들어 보세요.'};
    }
    // "순간이동 말고 스르륵"(이미 미끄러지기) · "1초 동안"
    if (/스르륵|스르르|미끄러지|부드럽게|순간\s*이동\s*말고/.test(t) && prevOps.some(op => /^motion_glide/.test(op))) {
        return {kind: 'explain', ir: null, message: '이미 ‘~초 동안 ~로 이동하기’로 스르륵 가요. 더 천천히 가려면 ‘1초 동안’처럼 시간을 말해 보세요.'};
    }
    const glideSec = /^(?<n>\d+(?:\.\d+)?)\s*초\s*(?:동안|만에|로)?$/.exec(t);
    if (glideSec && prevOps.some(op => /^motion_glide/.test(op))) {
        let hit = 0;
        walk(last.body, n => { if (/^motion_glide/.test(n.opcode || '')) { setNumber(n, 'SECS', Number(glideSec.groups.n)); hit++; } });
        if (hit) return finish('number', [`${glideSec.groups.n}초 동안 미끄러지게 바꿨어요.`]);
    }
    // "좀 더 신나게"(춤) — 더 빠르게
    if (/신나게|신나|흥겹게|경쾌하게/.test(t) && !/느리게|천천히/.test(t)) {
        const r = refine(prev, '더 빠르게', ctx);
        if (r && r.ir) return Object.assign(r, {changes: ['신나게 = 더 빠르게: '].concat(r.changes || [])});
    }
    // "불 켤때 삐 소리도 내" — 켜지는 쪽에 넣기
    if (/켤\s*때|켜질\s*때|켜지면|켜면/.test(t) && probe0.scripts.length && probe0.scripts[0].body.length) {
        let br = null;
        walk(last.body, n => { if (!br && (n.t === 'if' || n.t === 'ifelse') && n.body.some(b => b.opcode === 'cubroidmaster_cubroidMasterColorControl')) br = n; });
        const add = probe0.scripts[0].body.filter(x => x.t === 'act' && x.opcode !== 'cubroidmaster_cubroidMasterColorControl');
        if (br && add.length) { br.body.push(...add); return finish('append', ['불이 켜지는 쪽(만약 ~라면)에 넣었어요.']); }
    }
    // "뒤로 갔다가 오른쪽으로 돌고 다시 앞으로"(장애물까지 가서 멈춤) — 멈춘 뒤에 할 일로
    const waitObs = last.body.findIndex(n => n.opcode === 'control_wait_until' && /obstacle|proximity/.test(String(n.sig)));
    if (waitObs >= 0 && probe0.scripts.length === 1 && probe0.scripts[0].implicit && probeOps.length && probeOps.every(op => /_dcMotor/.test(op) || op === 'control_wait') && !hasCond(probe0.scripts[0].body)) {
        const keep = last.body.slice(0, waitObs + 1);
        const noStop = /멈추지\s*말고|안\s*멈추고|멈추지\s*않/.test(text);
        const stops = noStop ? [] : [act(`${M1}_dcMotorStop`, {sig: 'm1:off', label: 'DC 모터 1번을 끄기'}), act(`${M2}_dcMotorStop`, {sig: 'm2:off', label: 'DC 모터 2번을 끄기'})];
        last.body = keep.concat(stops, probe0.scripts[0].body);
        dedupeStops(last.body);
        return finish('append', ['장애물을 만나 멈춘 다음에 하도록 뒤에 붙였어요.']);
    }
    // 불평(이상해요·안 돼요) — 무엇을 확인할지 알려 주기
    if (/이상해|이상하게|이상함|안\s*돼|안돼|안\s*되|아니라|안\s*그려|선만|엉뚱|왜\s*안/.test(t) && !/말고|대신|바꿔|해줘|되게|하게\s*해/.test(t)) {
        if (prevOps.some(op => /^pen_/.test(op))) return {kind: 'explain', ir: null, message: '블록은 ‘펜 내리기 → 움직이기·돌기 반복’으로 되어 있어요. 깃발을 다시 눌러 보세요. 모양이 안 보이면 펜 확장이 추가됐는지, 고양이가 무대 밖으로 나가지 않았는지 확인하고 ‘천천히’라고 말해 보세요.'};
        if (prevOps.some(op => /_dcMotorControl$/.test(op)) && /돌|회전|네모|모양|각|꺾/.test(t)) return {kind: 'explain', ir: null, message: '로봇마다 도는 정도가 달라요. 90도 도는 시간(보통 0.6초)을 ‘회전 시간 줄여’·‘회전 시간 늘려’로 맞춰 보세요. 배터리가 약하면 덜 돌아요.'};
    }
    // ═══ 16차 이어 말하기(연습 9차 실패에서) ═══
    // "벽에 부딪혀 좀 더 일찍 멈추게"(자율주행) — 속도를 줄이고 더 자주 확인
    if (/일찍|더\s*멀리서|가까이\s*가서|부딪/.test(t) && prevOps.indexOf('cubroidproximity_isProximity') >= 0 && !/바꿔|왼쪽|오른쪽/.test(t)) {
        const sp = (id, k) => act(`${id}_dcMotorSetSpeed`, {inputs: {SPEED: num(50)}, sig: `m${k}:speed50`, label: `DC 모터 ${k}번 속도를 50%로 정하기`});
        last.body = last.body.filter(n => !/_dcMotorSetSpeed$/.test(n.opcode || ''));
        last.body.unshift(sp('cubroiddcmotor01', 1), sp('cubroiddcmotor02', 2));
        walk(last.body, n => { if (n.opcode === 'control_wait' && Number(n.inputs.DURATION.value) <= 0.3) { n.inputs.DURATION = num(0.1, 'math_positive_number'); n.sig = 'wait0.1'; n.label = '0.1초 기다리기'; } });
        return finish('number', ['근접 센서가 감지하는 거리는 바꿀 수 없어서, 속도를 50%로 줄이고 더 자주(0.1초마다) 확인하게 했어요. 그러면 더 일찍 멈춰요.']);
    }
    // "10걸음 말고 3걸음씩" — 그 숫자만 바꾸기
    const numSwap = /(?<a>\d+(?:\.\d+)?)\s*(?:걸음|번|초|도|만큼|칸|%|점|개)?\s*(?:말고|대신|아니고|아니라)\s*(?<b>\d+(?:\.\d+)?)\s*(?<u>걸음|번|초|도|만큼|칸|%|점|개)?/.exec(t);
    if (numSwap) {
        const a = Number(numSwap.groups.a);
        const b = Number(numSwap.groups.b);
        const items = numberItems(out);
        let hit = items.filter(it => it.input && Math.abs(Number(it.node.inputs[it.input].value)) === a);
        if (!hit.length) {
            const cat = catOfWord(t, numSwap.groups.u);
            hit = items.filter(it => it.input && (!cat || it.cat === cat));
        }
        if (hit.length) {
            hit.forEach(it => setNumber(it.node, it.input, Number(it.node.inputs[it.input].value) < 0 ? -b : b));
            return finish('number', [`${fmt(a)} → ${fmt(b)}(으)로 바꿨어요.`]);
        }
    }
    // "처음엔 0으로 해줘"(변수) — 시작 값
    const startV = /처음(?:엔|에는|에|부터)?\s*(?<n>-?\d+)\s*(?:으로|로|에서)\s*(?:해|시작|정해)?[가-힣]*/.exec(t);
    if (startV) {
        let setN = null;
        out.scripts.forEach(sc => walk(sc.body, n => { if (n.opcode === 'data_setvariableto') setN = setN || n; }));
        if (setN && String(setN.inputs.VALUE.value) === startV.groups.n) return {kind: 'explain', ir: null, message: `이미 깃발을 누르면 ${setN.varName}을(를) ${startV.groups.n}(으)로 정하고 시작해요.`};
        if (setN) { setN.inputs.VALUE = txt(startV.groups.n); setN.label = `${eul(setN.varName)} ${euro(startV.groups.n)} 정하기`; return finish('number', [`${setN.varName}의 처음 값을 ${startV.groups.n}(으)로 바꿨어요.`]); }
        if (lastVar) {
            out.scripts.unshift({hat: act('event_whenflagclicked', {sig: 'flag', label: '깃발을 클릭했을 때'}), body: [setVar(lastVar, toNum(startV.groups.n))], implicit: false});
            return finish('append', [`깃발을 누르면 ${lastVar}을(를) ${startV.groups.n}(으)로 정하고 시작하게 했어요.`]);
        }
    }
    // "끝까지 가면 다시 아래로 내려오게" — 계속 한쪽으로 움직이던 걸 끝까지 갔다 되돌아오는 왕복으로
    {
        const fe0 = last.body.find(n => n.t === 'forever');
        const mv = fe0 && fe0.body.length === 1 && fe0.body[0].t === 'act' && /^motion_change[xy]by$/.test(fe0.body[0].opcode) ? fe0.body[0] : null;
        const pb = probe0.scripts.length === 1 ? probe0.scripts[0].body : [];
        const mv2 = pb.length === 1 && pb[0].t === 'act' && pb[0].opcode === (mv && mv.opcode) ? pb[0] : null;
        if (mv && mv2 && /(?:끝에|끝까지|벽에|가장자리에)\s*(?:가면|가서|닿으면|도착하면)|도착하면/.test(t)) {
            const y = mv.opcode === 'motion_changeyby';
            const ax = y ? 'y' : 'x';
            const lim = y ? 160 : 220;
            const v1 = Number(mv.inputs[y ? 'DY' : 'DX'].value);
            const v2 = Number(mv2.inputs[y ? 'DY' : 'DX'].value);
            if (v1 * v2 < 0) {
                const cmp = v => {
                    const gt = v > 0;
                    const r = act(y ? 'motion_yposition' : 'motion_xposition', {sig: y ? 'ypos' : 'xpos', label: `${ax} 좌표`});
                    const n = gt ? lim : -lim;
                    return act(gt ? 'operator_gt' : 'operator_lt', {inputs: {OPERAND1: inT(r), OPERAND2: txt(String(n))}, sig: `${ax}${gt ? '>' : '<'}${n}`, label: `${ax} 좌표 ${gt ? '>' : '<'} ${n}`});
                };
                fe0.body = [{t: 'until', cond: cmp(v1), body: [mv]}, {t: 'until', cond: cmp(v2), body: [mv2]}];
                return finish('restate', [`끝(${ax} 좌표 ${v1 > 0 ? lim : -lim})까지 간 다음 반대쪽 끝까지 되돌아오기를 계속 되풀이하게 했어요.`]);
            }
        }
    }
    // "거꾸로 10부터 1까지로"(방금 결과가 0부터 1씩 올라가며 세기) — 내려가며 세기로 뒤집기
    if (/거꾸로|카운트\s*다운|줄어들|내려가(?:게|면서)|\d+\s*부터\s*(?:0|1)\s*까지/.test(t)) {
        let rp = null, setN = null;
        walk(last.body, n => { if (!rp && n.t === 'repeat' && n.body.some(b => b.opcode === 'data_changevariableby' && Number(b.inputs.VALUE.value) === 1)) rp = n; });
        const ch = rp && rp.body.find(b => b.opcode === 'data_changevariableby');
        if (ch) walk(last.body, n => { if (!setN && n.opcode === 'data_setvariableto' && n.varName === ch.varName) setN = n; });
        if (ch && setN) {
            const from = /(\d+)\s*부터/.exec(t);
            const top = from ? Number(from[1]) : rp.times;
            const say = rp.body.find(b => /^looks_say/.test(b.opcode || ''));
            const rest = rp.body.filter(b => b !== ch && b !== say);
            const nm = ch.varName;
            const set2 = setVar(nm, top);
            last.body[last.body.indexOf(setN)] = set2;
            rp.times = top;
            rp.body = (say ? [say] : []).concat(rest, [chVar(nm, -1)]);
            return finish('restate', [`${eul(nm)} ${top}에서 시작해 1씩 줄이며 세게 바꿨어요(${top}, ${top - 1}, … 1).`]);
        }
    }
    // "끝나면 땡! 이라고 해" — 맨 끝에 말하기 이어 붙이기
    {
        const endSay = /^(?:그리고\s*)?(?:다\s*)?(?:끝나면|끝나고|끝날\s*때|다\s*하면|다\s*세면|마지막에|맨\s*끝에)\s*(?:고양이가\s*)?['"‘“]?(?<w>[^'"’”]{1,20}?)['"’”]?\s*(?:(?:이?라고|하고)\s*(?:말|해|하|외치)[가-힣]*|이?라고\s*$)/.exec(t);
        if (endSay && !last.body.some(n => n.t === 'forever')) {
            const w = endSay.groups.w.trim();
            const capI = last.body.findIndex(n => n.cap);
            const node = sayOf(w, 2, w);
            if (capI >= 0) last.body.splice(capI, 0, node); else last.body.push(node);
            return finish('append', [`끝나면 ‘${w}’(이)라고 2초 동안 말하게 맨 끝에 붙였어요.`]);
        }
    }
    // "끝에 가면 다시 왼쪽으로 오게" — 이어 붙이기(덮어쓰지 않기)
    if (/(?:끝에|끝까지)\s*(?:가면|가서|닿으면)|도착하면|다\s*가면/.test(t) && probe0.scripts.length === 1 && probe0.scripts[0].implicit && probe0.scripts[0].body.length && !hasCond(probe0.scripts[0].body)) {
        const fe = last.body.find(n => n.t === 'forever');
        (fe ? fe.body : last.body).push(...probe0.scripts[0].body);
        return finish('append', ['끝에 간 다음에 하도록 뒤에 이어 붙였어요.']);
    }
    // "하트 지워줘"(깜빡이는 중) — 반복을 멈추고 LED 지우기
    if (/(?:하트|얼굴|그림|LED|숫자|글자)(?:를|을|도)?\s*(?:지워|지우|꺼|끄|없애)/.test(t) && prevOps.some(op => /^cubroidled_/.test(op))) {
        last.body = [act('cubroidled_ledAllClearControl', {sig: 'led:clear', label: 'LED 화면 지우기'})];
        return finish('restate', ['LED 화면을 지우게 바꿨어요(깜빡이기도 멈춰요).']);
    }
    // "안들려 더 키워 70" — 음량
    if (prevOps.some(op => /^sound_(?:set|change)volume/.test(op)) && /\d/.test(t) && /키워|크게|줄여|작게|소리|음량|볼륨|들려|안들려/.test(t)) {
        const v = Number(/(\d+)/.exec(t)[1]);
        walk(last.body, n => { if (/^sound_(?:set|change)volume/.test(n.opcode || '')) { n.opcode = 'sound_setvolumeto'; n.inputs = {VOLUME: num(v)}; n.sig = `vol=${v}`; n.label = `음량을 ${v}%로 정하기`; } });
        return finish('number', [`음량을 ${v}%로 정했어요.`]);
    }
    // "너무 빨리 켜져 더 어두워야 켜지게" — 밝기 기준 바꾸기
    const brightT = /더\s*어두워야|더\s*어둡게\s*되면|많이\s*어두워야|더\s*밝아야|더\s*밝게\s*되면/.exec(t);
    if (brightT && prevOps.indexOf('cubroidlighttouch_getBrightness') >= 0) {
        const darker = /어두/.test(brightT[0]);
        let changes = [];
        walk(last.body, n => {
            if ((n.opcode === 'operator_lt' || n.opcode === 'operator_gt') && /bright/.test(n.sig || '')) {
                const o = Number(n.inputs.OPERAND2.value);
                const v = darker ? Math.max(50, Math.round(o * 0.6)) : Math.round(o * 1.4);
                n.inputs.OPERAND2 = txt(String(v));
                n.sig = n.sig.replace(/\d+/, String(v));
                n.label = String(n.label).replace(/\d+/, String(v));
                changes.push(`밝기 기준 ${o} → ${v}`);
            }
        });
        if (changes.length) return finish('number', changes);
    }
    // "소리 말고 불 켜지게 바꿔" — 한 가지 동작을 다른 동작으로
    const actSwap = /^(?<a>소리|삐\s*소리|경고음|야옹|음악|불|불빛|말|움직임)\s*(?:말고|대신에?|아니고)\s*(?<b>.+?)$/.exec(t);
    if (actSwap) {
        const pr = translate(actSwap.groups.b.replace(/(?:로|으로)?\s*바꿔[가-힣]*$/, ''), rctx);
        const add = pr.scripts.length ? pr.scripts[0].body.filter(x => x.t === 'act') : [];
        const re = /소리|삐|경고|야옹|음악/.test(actSwap.groups.a) ? /^(?:sound_|cubroidsound_)/ : /불/.test(actSwap.groups.a) ? /^cubroidmaster_/ : /말/.test(actSwap.groups.a) ? /^looks_(?:say|think)/ : /^motion_|_dcMotorControl$/;
        let done = false;
        const removedA = [];
        const fx = list => { for (let i = 0; i < list.length && !done; i++) { const n = list[i]; if (n.body) fx(n.body); if (n.body2) fx(n.body2); if (!done && re.test(n.opcode || '')) { removedA.push(n); list.splice(i, 1, ...add); done = true; } } };
        if (add.length) fx(last.body);
        if (done) {
            const fx2 = list => { for (let i = list.length - 1; i >= 0; i--) { const n = list[i]; if (n.body) fx2(n.body); if (n.body2) fx2(n.body2); if (re.test(n.opcode || '') && add.indexOf(n) < 0) list.splice(i, 1); } };
            fx2(last.body);
            out.lastSwap = {removed: removedA.map(x => clone(x)), addedSig: add.length ? add[0].sig : null};
            return finish('swap', [`${actSwap.groups.a} 대신 ‘${add.map(x => x.label).join(', ')}’(으)로 바꿨어요.`]);
        }
    }
    // "돌때 왼쪽으로 돌아"(로봇) — 도는 방향
    const turnDir = /(?:돌\s*때|회전할\s*때|피할\s*때)\s*(?<d>왼쪽|오른쪽)(?:으로)?/.exec(t);
    if (turnDir && prevOps.some(op => /_dcMotorControl$/.test(op))) {
        const v = turnDir.groups.d === '왼쪽' ? 'Left' : 'Right';
        let hit = 0;
        const fx = list => { list.forEach(n => { if (n.body) fx(n.body); if (n.body2) fx(n.body2); }); for (let i = 0; i + 1 < list.length; i++) { const a = list[i]; const b = list[i + 1]; if (/01_dcMotorControl$/.test(a.opcode || '') && /02_dcMotorControl$/.test(b.opcode || '') && a.fields.INDEX.value === b.fields.INDEX.value) { [a, b].forEach(m => { m.fields.INDEX.value = v; m.sig = m.sig.replace(/Right|Left/, v); m.label = m.label.replace(/오른쪽|왼쪽/, v === 'Right' ? '오른쪽' : '왼쪽'); }); hit++; } } };
        fx(last.body);
        if (hit) return finish('flip', [`돌 때는 ${turnDir.groups.d}으로 돌게 바꿨어요.`]);
    }
    // "한번 더 누르면 멈추게"(버튼으로 출발) — 누를 때마다 가고 멈추기
    if (/(?:한\s*번\s*더|다시|또)\s*누르면\s*(?:멈|정지|서|꺼)/.test(t) && prevOps.indexOf('cubroidlighttouch_isButtonPressed') >= 0 && prevOps.some(op => /_dcMotorControl$/.test(op))) {
        const tg = translate('버튼 누르면 앞으로 가고 다시 누르면 멈추게', rctx);
        if (tg.scripts.length) { last.body = tg.scripts[0].body; return finish('restate', ['버튼을 누를 때마다 가고·멈추기를 번갈아 하게 바꿨어요(‘상태’ 변수로 기억해요).'], tg.notes.filter(x => x !== NOTE.hat)); }
    }
    // "가까울수록 더 빨리 삐삐" — 센서는 가깝다/아니다만 알아서, 가까우면 빠르게 연달아
    if (/가까울수록|가까이\s*갈수록|다가갈수록/.test(t) && prevOps.indexOf('cubroidproximity_isProximity') >= 0) {
        let hit = null;
        walk(last.body, n => { if (!hit && (n.t === 'if' || n.t === 'ifelse') && n.cond && n.cond.sig === 'obstacle') hit = n; });
        if (hit) {
            hit.body = [beep('경고'), waitS(0.1)];
            return finish('restate', ['근접 센서는 거리 숫자를 몰라서, 가까이 있는 동안 0.1초마다 빠르게 삐삐 울리게 했어요.']);
        }
    }
    // "10초 되면 끝 이라고 말하게"(초 세는 타이머 뒤)
    const atSec = /(?<n>\d+)\s*초\s*(?:되면|지나면)\s*(?<rest>.+)$/.exec(t);
    if (atSec && out.scripts.some(sc => { let f = false; walk(sc.body, n => { if (n.varName === '시간') f = true; }); return f; })) {
        const pr = translate(atSec.groups.rest, rctx);
        const body = pr.scripts.length ? pr.scripts[0].body.slice() : [];
        const fe = last.body.find(n => n.t === 'forever');
        if (body.length && fe) {
            body.push(act('control_stop', {fields: fld('STOP_OPTION', 'all'), mutation: {tagName: 'mutation', children: [], hasnext: 'false'}, sig: 'stop', label: '멈추기 (모두)', cap: true}));
            fe.body.push({t: 'if', noLoop: true, cond: act('operator_equals', {inputs: {OPERAND1: inT(V('시간')), OPERAND2: txt(atSec.groups.n)}, sig: `시간=${atSec.groups.n}`, label: `시간 = ${atSec.groups.n}`}), body});
            fixOverwrite(fe.body, () => {});
            return finish('append', [`‘시간’이 ${atSec.groups.n}이 되면 그렇게 하고 멈추게 했어요.`]);
        }
    }
    // "너무 많아 2마리만"(복제) — 복제 수
    const cloneN = /(?<n>\d+|한|두|세|네|다섯)\s*(?:마리|개)(?:만)?/.exec(t);
    if (cloneN && prevOps.indexOf('control_create_clone_of') >= 0) {
        const n = toNum(cloneN.groups.n) || KNUM[cloneN.groups.n];
        let hit = 0;
        out.scripts.forEach(sc => walk(sc.body, x => { if (x.t === 'repeat' && x.body.some(b => b.opcode === 'control_create_clone_of')) { x.times = n; hit++; } }));
        if (hit) return finish('repeat', [`복제본을 ${n}개 만들게 바꿨어요.`]);
    }
    // "복제된 애들 다른 위치에 있게"(이미 그렇게 되어 있으면)
    if (/복제(?:된|본)/.test(t) && /다른\s*(?:위치|곳|데)|흩어|여기저기|랜덤|무작위/.test(t) && out.scripts.some(sc => sc.hat.opcode === 'control_start_as_clone' && sc.body.some(n => n.sig === 'gotorandom'))) {
        return {kind: 'explain', ir: null, message: '이미 복제본마다 ‘복제되었을 때 → 무작위 위치로 이동하기’가 있어서 서로 다른 곳에 나타나요.'};
    }
    // "1부터 9까지 차례로 나오게"(LED 숫자 뒤)
    const ledRange = /(?<a>\d)\s*(?:부터|에서)\s*(?<b>\d)\s*까지/.exec(t);
    if (ledRange && prevOps.indexOf('cubroidled_lceNumberControl') >= 0) {
        const a = Number(ledRange.groups.a);
        const b = Number(ledRange.groups.b);
        const nodes = [];
        for (let v = a; a <= b ? v <= b : v >= b; v += a <= b ? 1 : -1) nodes.push(act('cubroidled_lceNumberControl', {fields: fld('MATRIX', LED_NUM_K[String(v)]), sig: `led:${v}`, label: `LED에 숫자 ${v} 보여주기`}), waitS(1));
        last.body = last.body.filter(n => n.opcode !== 'cubroidled_lceNumberControl').concat(nodes);
        return finish('restate', [`LED에 ${a}부터 ${b}까지 1초씩 차례로 보여 줘요.`]);
    }
    // "노란불 시간 좀 짧게 1초로" — 그 불이 켜져 있는 전체 시간을 맞춘다(경고음 뒤 기다리기까지)
    const lightT2 = new RegExp(`(?<c>빨간|빨강|초록|녹색|파란|파랑|노란|노랑)\\s*(?:불|불빛|색)?[가-힣\\s]*?(?<n>${RE_SEC})\\s*초`).exec(t);
    if (lightT2 && prevOps.indexOf('cubroidmaster_cubroidMasterColorControl') >= 0) {
        const HEX = {빨간: '#ff0000', 빨강: '#ff0000', 초록: '#00ff00', 녹색: '#00ff00', 파란: '#0000ff', 파랑: '#0000ff', 노란: '#ffff00', 노랑: '#ffff00'};
        const hex = HEX[lightT2.groups.c];
        const sec = toNum(lightT2.groups.n);
        let hit = 0;
        const fx = list => list.forEach((n, i) => {
            if (n.body) fx(n.body);
            if (n.sig !== `light:${hex}`) return;
            let j = i + 1;
            let first = true;
            while (j < list.length && list[j].opcode !== 'cubroidmaster_cubroidMasterColorControl' && list[j].opcode !== 'cubroidmaster_cubroidColorOffControl') {
                if (list[j].opcode === 'control_wait') {
                    if (first) { list[j].inputs.DURATION = num(sec, 'math_positive_number'); list[j].sig = `wait${fmt(sec)}`; list[j].label = `${fmt(sec)}초 기다리기`; first = false; j++; continue; }
                    list.splice(j, 1);
                    continue;
                }
                j++;
            }
            hit++;
        });
        fx(last.body);
        if (hit) return finish('number', [`${lightT2.groups.c}불이 켜져 있는 시간을 ${fmt(sec)}초로 맞췄어요.`]);
    }
    // ═══ 14차 이어 말하기(연습 8차 실패에서) ═══
    // "3번 말고 계속" — 반복 횟수 대신 무한 반복
    if (/(?:말고|대신|아니고)\s*(?:계속|무한|영원히|끝없이)/.test(t) && last.body.some(n => n.t === 'repeat')) {
        last.body = last.body.map(n => (n.t === 'repeat' ? {t: 'forever', body: n.body} : n));
        return finish('forever', ['‘N번 반복하기’를 ‘무한 반복하기’로 바꿨어요.']);
    }
    // "아 시끄러 그만" — 앞말이 있어도 멈춰 달라는 말이면 안내
    if (/(?:그만|멈춰|멈추|멈춤|정지|스톱|끝내)(?:게)?(?:\s*해)?(?:줘|요|라|어|주세요)?$/.test(t) && opsOf(probe0).every(op => op === 'control_stop') &&
        !prevOps.some(op => /_dcMotorControl$/.test(op)) && !/\d|번|초|모터|바퀴|로봇|도\s|하고|되면|닿으면|면\s/.test(t)) {
        return {kind: 'explain', ir: null, message: '실행 중인 걸 멈추려면 무대 위 빨간 정지 버튼을 눌러요. 몇 번만 하고 멈추게 하려면 ‘3번만’처럼 말해 보세요.'};
    }
    // "웃는얼굴 말고 하트" — LED 그림 바꾸기
    const imgSwap = new RegExp(`(?:말고|대신에?|아니고|아니라)\\s*(?<b>[가-힣]+?)(?:\\s*(?:얼굴|모양|그림))?(?:으로|로)?(?:\\s*(?:바꿔|해|보여|해줘|바꿔줘)[가-힣]*)?$`).exec(t);
    if (imgSwap && prevOps.indexOf('cubroidled_lceImgControl') >= 0) {
        const hit = LED_IMG.find(e => e[0].test(imgSwap.groups.b));
        if (hit) {
            let tgt = null;
            walk(last.body, n => { if (n.opcode === 'cubroidled_lceImgControl') tgt = tgt || n; });
            tgt.prevImg = {fields: clone(tgt.fields), sig: tgt.sig, label: tgt.label};
            tgt.fields = fld('MATRIX', hit[2]);
            tgt.sig = `led:${hit[1]}`;
            tgt.label = `LED에 ‘${hit[1]}’ 그림 보여주기`;
            return finish('swap', [`LED 그림을 ‘${hit[1]}’(으)로 바꿨어요.`]);
        }
    }
    // "끝에 도 한번 더" — 음 하나 붙이기
    const oneNote = /(?:(?:끝에|마지막에|뒤에)\s*|^|\s)(?<hi>높은\s*)?(?<n>도|레|미|파|솔|라|시)(?:\s*음)?\s*(?:를|을)?\s*(?:한\s*번\s*더|하나\s*더|추가|더\s*(?:붙여|넣어|해))/.exec(t);
    if (oneNote && prevOps.indexOf('cubroidsound_soundBeatControl') >= 0) {
        const nl = [];
        walk(last.body, x => { if (x.opcode === 'cubroidsound_soundBeatControl') nl.push(x); });
        const lastN = nl[nl.length - 1];
        const nn = clone(lastN);
        const HI = {도: '도 C(72)', 레: '레 D(74)', 미: '미 E(76)', 파: '파 F(77)', 솔: '솔 G(79)'};
        const pv = /도 C\(72\)/.test(lastN.inputs.SOUND.value) || oneNote.groups.hi ? (HI[oneNote.groups.n] || SOLFA[oneNote.groups.n]) : SOLFA[oneNote.groups.n];
        nn.inputs.SOUND = Object.assign({}, nn.inputs.SOUND, {value: pv});
        nn.sig = `note:${oneNote.groups.n}${/72|74|76|77|79/.test(pv) ? '^' : ''}`;
        nn.label = String(nn.label).replace(/^\S+ \S+/, pv);
        const addIn = list => { const i = list.indexOf(lastN); if (i >= 0) { list.splice(i + 1, 0, nn); return true; } return list.some(x => (x.body && addIn(x.body)) || (x.body2 && addIn(x.body2))); };
        if (addIn(last.body)) return finish('append', [`끝에 ${pv} 음을 하나 더 붙였어요.`]);
    }
    // "반대로 됐어요 밝을때 켜져요" — 조건의 크다/작다를 뒤집기
    if (/반대로\s*(?:됐|돼|되|해)|거꾸로\s*(?:됐|돼|되)/.test(t) && prevOps.indexOf('cubroidlighttouch_getBrightness') >= 0) {
        let hit = 0;
        walk(last.body, n => {
            if ((n.opcode === 'operator_lt' || n.opcode === 'operator_gt') && /bright/.test(n.sig || '')) {
                const lt = n.opcode === 'operator_lt';
                n.opcode = lt ? 'operator_gt' : 'operator_lt';
                n.sig = n.sig.replace(/[<>]/, lt ? '>' : '<');
                n.label = String(n.label).replace(/[<>]/, lt ? '>' : '<');
                hit++;
            }
        });
        if (hit) return finish('restate', ['조건의 크다/작다를 반대로 바꿨어요.']);
    }
    // "반대쪽으로 돌려줘", "반대방향으로" — 도는 방향만 뒤집기(각도는 그대로)
    if (/^반대(?:쪽|방향)?(?:으로|로)?\s*(?:돌려|돌아|바꿔|가|돌게|해)?[가-힣]*$/.test(t)) {
        let hit = 0;
        const fix = list => {
            list.forEach(n => { if (n.body) fix(n.body); if (n.body2) fix(n.body2); });
            for (let i = 0; i + 1 < list.length; i++) {
                const a = list[i];
                const b = list[i + 1];
                if (/01_dcMotorControl$/.test(a.opcode || '') && /02_dcMotorControl$/.test(b.opcode || '')) {
                    [a, b].forEach(m => { const v = m.fields.INDEX.value === 'Right' ? 'Left' : 'Right'; m.fields.INDEX.value = v; m.sig = m.sig.replace(/Right|Left/, v); m.label = m.label.replace(/오른쪽|왼쪽/, v === 'Right' ? '오른쪽' : '왼쪽'); });
                    hit++;
                    i++;
                }
            }
            list.forEach(n => {
                if (/^motion_turn(?:left|right)$/.test(n.opcode || '')) {
                    const left = n.opcode === 'motion_turnright';
                    n.opcode = left ? 'motion_turnleft' : 'motion_turnright';
                    n.sig = n.sig.replace(/^(?:left|right)/, left ? 'left' : 'right');
                    n.label = n.label.replace(/오른쪽|왼쪽/, left ? '왼쪽' : '오른쪽');
                    hit++;
                }
            });
        };
        fix(last.body);
        if (hit) return finish('flip', ['도는 방향을 반대로 바꿨어요(각도·시간은 그대로).']);
    }
    // "소리가 너무 높아 낮게"(로봇 경고음) — 경고음은 높이가 없어서 낮은 도로
    if (/낮게|낮아|낮은|높게|높아|높은/.test(t) && prevOps.indexOf('cubroidsound_errorSoundControl') >= 0 && !prevOps.some(op => op === 'cubroidsound_soundBeatControl')) {
        const low = /낮게|높아|낮은/.test(t);
        const v = low ? '도 C(48)' : '도 C(72)';
        walk(last.body, n => { if (n.opcode === 'cubroidsound_errorSoundControl') {
            n.opcode = 'cubroidsound_soundBeatControl';
            n.inputs = {SOUND: menu('cubroidsound_menu_cubroidSoundAction', 'cubroidSoundAction', v), BEATS: num(0.5)};
            n.sig = `note:도${low ? '_' : '^'}`;
            n.label = `${v} 음을 0.5박자 연주하기`;
        } });
        return finish('restate', [`경고음은 높이를 바꿀 수 없어서 ${low ? '낮은' : '높은'} 도(${v}) 음으로 바꿨어요.`]);
    }
    // "다 지우는것도 c키 누르면 되게" — 뒤에 말한 키로 새 스크립트
    const keyAfter = new RegExp(`^(?<act>.+?)(?:도|는|은)?\\s+${RE_KEY}\\s*(?:키|버튼)?(?:를|을)?\\s*(?:누르면|눌렀을\\s*때|누를\\s*때)(?:마다)?\\s*(?:되게|하게|돼|되도록|해줘|해|되게\\s*해줘)?$`).exec(t);
    if (keyAfter) {
        const k = keyValue(keyAfter.groups.key);
        const pr = translate(keyAfter.groups.act.replace(/(?:는|은)?\s*(?:것|거)$/, '').replace(/지우는$/, '지우기'), rctx);
        const body = pr.scripts.length ? pr.scripts[0].body : [];
        if (k && body.length) {
            out.scripts.push({hat: act('event_whenkeypressed', {fields: fld('KEY_OPTION', k), sig: `key:${k}`, label: `${keyKo(k)} 키를 눌렀을 때`}), body, implicit: false});
            return finish('append', [`‘${keyKo(k)} 키를 눌렀을 때’ 스크립트를 새로 만들었어요.`], pr.notes.filter(x => x !== NOTE.hat));
        }
    }
    // "한번 더 그려"(펜) — 지우기는 밖에, 다른 곳에 한 번 더
    if (/한\s*번\s*더|하나\s*더|또\s*그려/.test(t) && prevOps.indexOf('pen_penDown') >= 0 && !last.body.some(n => n.t === 'forever')) {
        const pre = [];
        let k = 0;
        while (k < last.body.length && /^(?:pen_clear|pen_set)/.test(last.body[k].opcode || '')) pre.push(last.body[k++]);
        const rest = last.body.slice(k).filter(n => n.opcode !== 'pen_penDown');
        last.body = pre.concat([{t: 'repeat', times: 2, body: [penUpN(), act('motion_goto', {inputs: {TO: menu('motion_goto_menu', 'TO', '_random_')}, sig: 'gotorandom', label: '무작위 위치로 이동하기'}), penDownN()].concat(rest)}]);
        return finish('repeat', ['‘모두 지우기’는 처음에 한 번만 하고, 다른 곳으로 옮겨 가서 두 번 그리게 했어요.']);
    }
    if (/아닌데|아니잖아|아니야|아닌\s*것\s*같/.test(t)) {
        const pr = translate(String(text).replace(/(?:이거|이게|그거)\s*/g, '').replace(/\s*(?:아닌데|아니잖아|아니야|아닌\s*것\s*같[가-힣]*)[가-힣?!.\s]*$/, ' 그려'), rctx);
        const ps = [];
        walk(pr.scripts.length ? pr.scripts[0].body : [], x => { if (x.t === 'act') ps.push(x.sig); });
        const have = new Set();
        out.scripts.forEach(sc => walk(sc.body, x => { if (x.t === 'act') have.add(x.sig); }));
        if (ps.length && ps.every(sg => have.has(sg))) {
            return {kind: 'explain', ir: null, message: '지금 블록이 바로 그 모양이에요. 깃발을 눌러 무대에서 확인해 보세요. 크기나 방향이 다르면 ‘더 크게’, ‘왼쪽으로 돌게’처럼 말해 주세요.'};
        }
    }
    if (/다시\s*(?:나타|보이|나와|나오)/.test(t) && prevOps.some(op => /^looks_(?:changeeffectby|seteffectto|hide)$/.test(op)) && prevOps.indexOf('looks_show') < 0) {
        const secM = new RegExp(`(?<n>${RE_SEC})\\s*초`).exec(t);
        const add = [waitS(secM ? toNum(secM.groups.n) : 1)];
        if (prevOps.some(op => /effect/.test(op))) add.push(act('looks_cleargraphiceffects', {sig: 'cleareffects', label: '그래픽 효과 지우기'}));
        add.push(act('looks_show', {sig: 'show', label: '보이기'}));
        const ap = last.body.some(n => n.t === 'forever') ? appendPoint(last, null) : last.body;
        ap.push(...add);
        return finish('append', [`${secM ? secM.groups.n : 1}초 뒤에 다시 보이게${prevOps.some(op => /effect/.test(op)) ? '(효과도 지우고)' : ''} 붙였어요.`]);
    }
    // "너무 조금 가 50걸음으로 해줘" — 숫자를 분명히 말했으면 불평 말("조금")보다 숫자가 먼저
    const numM = /(?:^|\s)(\d+(?:\.\d+)?\s*(?:걸음|만큼|칸|초|번|도|%|퍼센트|점|개)?\s*(?:으로|로)\s*(?:해|바꿔|하자|해줘|해주세요|가|돌|정해|가게|해봐)?[가-힣]*)$/.exec(t) ||
        /(?:^|\s)(\d+(?:\.\d+)?\s*(?:걸음|만큼|칸|초|번|도|%|퍼센트)\s*(?:가게|가|움직이게|돌게|해|하게|해줘|가게해|가게\s*해)?[가-힣]*)$/.exec(t);
    if (numM && numM.index > 0 && /너무|좀|조금|많이|더|적어|작아|짧아|길어|느려|빨라|부족/.test(t.slice(0, numM.index)) && !/(?:빨간|빨강|초록|녹색|파란|파랑|노란|노랑)\s*불/.test(t)) {
        const nu = /(\d+(?:\.\d+)?)\s*(걸음|만큼|칸|초|번|도|%|퍼센트|점|개)?/.exec(numM[1]);
        const r2 = refine(prev, `${nu[1]}${nu[2] || ''}으로 해`, ctx) || refine(prev, numM[1].trim(), ctx);
        if (r2 && r2.ir) return r2;
    }
    // "아 시끄러워 한번만" 같은 앞말 — "다시 한번만"은 다시 실행하라는 뜻
    if (/(?:^|\s)다시\s*(?:한\s*번|한번)(?:만)?(?:\s*(?:해|해줘|들려|보여|해봐))?$/.test(t)) {
        return {kind: 'explain', ir: null, message: '초록 깃발을 다시 누르면 처음부터 한 번 더 실행돼요.'};
    }
    // "떨어질 때 좀 천천히" — 점프의 내려오기만 부드럽게
    if (/떨어|내려오|내려올|착지/.test(t) && /천천히|느리게|부드럽|살살|천천|빨라|빠르|급해/.test(t) && prevOps.indexOf('motion_changeyby') >= 0) {
        let hit = 0;
        const fix = list => list.forEach((n, i) => {
            if (n.body) fix(n.body);
            if (n.part === 'jumpDown') {
                const h = Math.abs(Number(n.inputs.DY.value)) || 50;
                list[i] = {t: 'repeat', times: 10, body: [act('motion_changeyby', {inputs: {DY: num(-h / 10)}, sig: `y-${fmt(h / 10)}`, label: `y 좌표를 -${fmt(h / 10)}만큼 바꾸기`}), waitS(0.04)]};
                hit++;
            }
        });
        fix(last.body);
        if (hit) return finish('restate', ['내려올 때는 조금씩 10번 나눠 내려오게 했어요(0.04초씩).']);
    }
    // "절반으로", "반만" — 크기
    if (/절반|반으로|반만|반\s*크기/.test(t) && prevOps.some(op => /^looks_(?:changesizeby|setsizeto)$/.test(op))) {
        last.body = last.body.filter(n => !(n.opcode === 'looks_changesizeby' || n.opcode === 'looks_setsizeto'));
        last.body.push(act('looks_setsizeto', {inputs: {SIZE: num(50)}, sig: 'size=50', label: '크기를 50 %로 정하기'}));
        return finish('number', ['크기를 원래의 절반(50%)으로 정하게 바꿨어요.']);
    }
    // "선이 너무 얇아" — 펜 굵기
    if (/선|펜|줄/.test(t) && /얇|가늘|굵|두껍|두꺼/.test(t) && prevOps.some(op => /^pen_/.test(op))) {
        const vm0 = /(\d+)/.exec(t);
        const v = vm0 ? Number(vm0[1]) : (/너무\s*(?:굵|두껍|두꺼)|얇게|가늘게/.test(t) ? 1 : 5);
        let set = null;
        walk(last.body, n => { if (n.opcode === 'pen_setPenSizeTo') set = n; });
        if (set) { set.inputs.SIZE = num(v); set.sig = `pen:size${v}`; set.label = `펜 굵기를 ${v}(으)로 정하기`; } else {
            const i = last.body.findIndex(n => n.opcode === 'pen_clear');
            last.body.splice(i + 1, 0, act('pen_setPenSizeTo', {inputs: {SIZE: num(v)}, sig: `pen:size${v}`, label: `펜 굵기를 ${v}(으)로 정하기`}));
        }
        return finish('number', [`펜 굵기를 ${v}(으)로 정했어요.`]);
    }
    // "너무 빨리 사라짐 천천히"(유령 효과) — 조금씩 투명해지게
    if (/천천히|서서히|느리게|점점/.test(t) && prevOps.indexOf('looks_seteffectto') >= 0 && /사라|투명|없어/.test(t + JSON.stringify(prev.scripts))) {
        let hit = 0;
        const fix = list => list.forEach((n, i) => {
            if (n.body) fix(n.body);
            if (n.opcode === 'looks_seteffectto' && n.fields.EFFECT.value === 'GHOST') {
                list[i] = {t: 'repeat', times: 10, body: [act('looks_changeeffectby', {fields: fld('EFFECT', 'GHOST'), inputs: {CHANGE: num(10)}, sig: 'ghost+10', label: '유령 효과를 10만큼 바꾸기'}), waitS(0.1)]};
                hit++;
            }
        });
        fix(last.body);
        if (hit) return finish('restate', ['한 번에 투명해지지 않고 0.1초마다 조금씩(10번) 투명해지게 했어요.']);
    }
    // "번갈아가면서" — 바꾼 것들(LED 그림·불빛·모양)을 계속 번갈아
    if (/번갈아|왔다\s*갔다|교대로|반복해서\s*바뀌/.test(t) && !last.body.some(n => n.t === 'forever')) {
        const outs = last.body.filter(n => n.t === 'act' && /^(?:cubroidled_|cubroidmaster_cubroidMasterColorControl|looks_switchcostumeto|looks_nextcostume)/.test(n.opcode));
        if (outs.length >= 2) {
            const body = last.body.slice();
            if (body[body.length - 1].opcode !== 'control_wait') body.push(waitS(1));
            last.body = [{t: 'forever', body}];
            return finish('forever', ['계속 번갈아 바뀌게 ‘무한 반복하기’로 감쌌어요.']);
        }
    }
    // "돌 때 너무 많이 돌아", "회전 시간 줄여줘" — 로봇이 도는 시간만
    if (/돌|회전|턴/.test(t) && prevOps.some(op => /_dcMotorControl$/.test(op))) {
        const half = /반만|절반|반\s*정도/.test(t);
        const less = half || /너무\s*많이|많이\s*돌|줄여|줄이|짧게|덜\s*돌게|과하/.test(t);
        const more = /덜\s*돌아|부족|모자라|더\s*돌|조금\s*밖에|늘려|길게/.test(t);
        if (less || more) {
            const f = half ? 0.5 : less ? 0.75 : 1.3;
            const changes = [];
            const fix = list => {
                list.forEach(n => { if (n.body) fix(n.body); if (n.body2) fix(n.body2); });
                for (let i = 2; i < list.length; i++) {
                    const a = list[i - 2];
                    const b = list[i - 1];
                    if (list[i].opcode === 'control_wait' && /01_dcMotorControl$/.test(a.opcode || '') && /02_dcMotorControl$/.test(b.opcode || '') && a.fields.INDEX.value === b.fields.INDEX.value) {
                        const o = Number(list[i].inputs.DURATION.value);
                        const v = Math.max(0.1, Math.round(o * f * 100) / 100);
                        list[i].inputs.DURATION = num(v, 'math_positive_number');
                        list[i].sig = `wait${fmt(v)}`;
                        list[i].label = `${fmt(v)}초 기다리기`;
                        changes.push(`도는 시간 ${fmt(o)}초 → ${fmt(v)}초`);
                    }
                }
            };
            fix(last.body);
            if (changes.length) return finish('number', changes.slice(0, 1).concat(changes.length > 1 ? [`(도는 곳 ${changes.length}군데 모두)`] : []));
        }
    }
    // "너무 빨라 50으로" — 로봇 속도(% 없이)
    if (prevOps.some(op => /_dcMotorSetSpeed$/.test(op)) && !/초|번|도(?:\s|$)|걸음|만큼/.test(t)) {
        const m = /(?:^|\s)(?<n>\d+)\s*(?:%|퍼센트|퍼|프로)?\s*(?:으로|로)?(?:\s|$)/.exec(t);
        if (m) {
            const v = Math.max(0, Math.min(100, Number(m.groups.n)));
            walk(last.body, n => { if (/_dcMotorSetSpeed$/.test(n.opcode || '')) { n.inputs.SPEED = num(v); n.sig = n.sig.replace(/speed[\d.]+/, `speed${v}`); n.label = n.label.replace(/[\d.]+%/, `${v}%`); } });
            return finish('number', [`로봇 속도를 ${v}%로 바꿨어요.`]);
        }
    }
    // "키 떼면 멈추게"(방향키 로봇 스크립트 여러 개) — 하나의 계속 확인하는 스크립트로
    if (/떼면|놓으면|누르는\s*동안만|누르고\s*있을\s*때만/.test(t)) {
        const ks = out.scripts.filter(sc => sc.hat.opcode === 'event_whenkeypressed' && sc.body.some(n => /_dcMotorControl$/.test(n.opcode || '')));
        if (ks.length >= 2) {
            let chain = stopBoth();
            ks.slice().reverse().forEach(sc => {
                const k = sc.hat.fields.KEY_OPTION.value;
                const moves = sc.body.filter(n => /_dcMotorControl$/.test(n.opcode || '')).map(clone);
                chain = [{t: 'ifelse', cond: act('sensing_keypressed', {inputs: {KEY_OPTION: menu('sensing_keyoptions', 'KEY_OPTION', k)}, sig: `keyheld:${k}`, label: `${keyKo(k)} 키를 눌렀는가?`}), noLoop: true, body: moves, body2: chain}];
            });
            out.scripts = out.scripts.filter(sc => ks.indexOf(sc) < 0);
            out.scripts.push({hat: act('event_whenflagclicked', {sig: 'flag', label: '깃발을 클릭했을 때'}), body: [{t: 'forever', body: chain}], implicit: false});
            return finish('restate', [`방향키 스크립트 ${ks.length}개를 ‘계속 확인하기’ 하나로 합쳤어요: 누르고 있는 키대로 가고, 아무 키도 안 누르면 멈춰요.`]);
        }
    }
    // "야옹 소리 가끔", "음악도 틀어줘"(계속 움직이는 중) — 소리는 따로 도는 스크립트로
    const probeSnd = probe0.scripts.length === 1 && probe0.scripts[0].implicit && probe0.scripts[0].body.length &&
        opsOf(probe0).every(op => /^sound_/.test(op));
    if (probeSnd && (/가끔|이따금|때때로|가끔씩/.test(t) || (last.body.some(n => n.t === 'forever') && prevOps.some(op => /^(?:motion_|looks_)/.test(op)) && /음악|노래|배경\s*음|소리/.test(t)))) {
        const snd = [];
        walk(probe0.scripts[0].body, n => { if (/^sound_play/.test(n.opcode || '')) snd.push(n); });
        if (snd.length) {
            const play = clone(snd[0]);
            play.opcode = 'sound_playuntildone';
            play.sig = String(play.sig).replace(/^sound\(/, 'soundwait(');
            play.label = String(play.label).replace(/ 재생하기$/, ' 끝까지 재생하기').replace(/끝까지 끝까지/, '끝까지');
            const rnd = act('operator_random', {inputs: {FROM: num(2), TO: num(5)}, sig: 'random2-5', label: '2부터 5 사이의 난수'});
            const body = /가끔|이따금|때때로/.test(t) ? [act('control_wait', {inputs: {DURATION: Object.assign(num(3, 'math_positive_number'), {node: rnd})}, sig: 'wait:random', label: '(2~5 난수)초 기다리기'}), play] : [play];
            out.scripts.push({hat: clone(last.hat), body: [{t: 'forever', body}], implicit: false});
            return finish('parallel', [/가끔/.test(t) ? '소리는 따로 도는 스크립트로 만들어, 2~5초마다 한 번씩 나게 했어요.' : '소리는 따로 도는 스크립트로 만들었어요. 움직임은 끊기지 않고 소리는 계속 나요.']);
        }
    }
    if (prevOps.indexOf('cubroidsound_soundBeatControl') >= 0 && /거꾸로|반대로|내려오|내려가|올라가|거꾸로\s*연주|[도레미파솔라시]{2,}/.test(t)) {
        const notesOf = list => { const a = []; walk(list, x => { if (x.opcode === 'cubroidsound_soundBeatControl') a.push(x); }); return a; };
        const old = notesOf(last.body);
        let seq = /[도레미파솔라시]{2,}/.exec(t);
        const appendMode = /까지|이어서|계속해서|추가|더\s|붙여|넣어/.test(t) || (seq && /도$/.test(seq[0]) && /^\s*(?:추가|넣|해|붙|같이|까지|이어)/.test(t.slice(seq.index + seq[0].length)));
        if (seq && seq[0].length >= 3 && /도$/.test(seq[0]) && /^\s*(?:추가|넣|해|붙|같이|이어)/.test(t.slice(seq.index + seq[0].length))) seq = [seq[0].slice(0, -1)];
        let neu = null;
        if (seq) {
            const pr = translate(`${seq[0]} 연주해`, rctx);
            neu = pr.scripts.length ? notesOf(pr.scripts[0].body) : [];
            // "도시라솔" — 내려오는 도는 높은 도
            if (neu.length > 1 && /^도[시라솔]/.test(seq[0])) {
                neu[0].inputs.SOUND.value = '도 C(72)';
                neu[0].sig = 'note:도^';
                neu[0].label = neu[0].label.replace('도 C(60)', '도 C(72)');
            }
            if (!neu.length) neu = null;
        } else if (/거꾸로|반대로/.test(t)) neu = old.slice().reverse().map(clone);
        if (neu && old.length) {
            const beats = old[0].inputs.BEATS.value;
            neu.forEach(n => { n.inputs.BEATS = num(Number(beats)); n.label = n.label.replace(/[\d.]+박자/, `${beats}박자`); });
            // 음 블록이 이어져 있던 자리에 새 음들을 넣는다
            const replaceIn = list => {
                const i = list.indexOf(old[0]);
                if (i >= 0) { const cnt = list.filter(x => old.indexOf(x) >= 0).length; list.splice(i, cnt, ...neu); return true; }
                return list.some(x => (x.body && replaceIn(x.body)) || (x.body2 && replaceIn(x.body2)));
            };
            if (appendMode && seq) {
                const lastNote = old[old.length - 1];
                const addIn = list => { const i = list.indexOf(lastNote); if (i >= 0) { list.splice(i + 1, 0, ...neu); return true; } return list.some(x => (x.body && addIn(x.body)) || (x.body2 && addIn(x.body2))); };
                if (addIn(last.body)) return finish('append', [`${neu.map(n => n.label.split(' ')[0]).join(' ')}을(를) 이어서 연주하게 붙였어요.`]);
            }
            if (replaceIn(last.body)) return finish('restate', [`연주할 음을 ${neu.map(n => n.label.split(' ')[0]).join(' ')}(으)로 바꿨어요.`]);
        }
    }
    const tt = t.replace(/^(?:아니(?:야|요)?|음|그럼|그러면|그리고|근데)\s+/, '');
    // "스페이스바 눌렀을때만", "클릭할 때만" — 언제 시작할지(모자)만 바꾼다
    const hatOnly = new RegExp(`^(?:${RE_KEY})\\s*(?:키|버튼)?(?:를|을)?\\s*(?:눌렀을\\s*때|누르면|누를\\s*때)(?:만|에만)(?:\\s*[가-힣]+)?$`).exec(tt) ||
        /^(?<ck>(?:고양이|나|이거|얘)?(?:를|을)?\s*클릭(?:했을|할)\s*때(?:만|에만)|클릭하면)(?:\s*[가-힣]+)?$/.exec(tt);
    if (hatOnly && last.body.length) {
        const k = hatOnly.groups && hatOnly.groups.key ? keyValue(hatOnly.groups.key) : null;
        if (k) last.hat = act('event_whenkeypressed', {fields: fld('KEY_OPTION', k), sig: `key:${k}`, label: `${keyKo(k)} 키를 눌렀을 때`});
        else last.hat = act('event_whenthisspriteclicked', {sig: 'click', label: '이 스프라이트를 클릭했을 때'});
        last.implicit = false;
        return finish('hat', [`시작하는 때를 ‘${last.hat.label}’로 바꿨어요.`]);
    }
    // "3초 동안으로 바꿔" — 말하기를 N초 동안 말하기로
    const saySec = new RegExp(`^(?<n>${RE_SEC})\\s*초\\s*(?:동안|간)(?:\\s*(?:으로|로|만))?(?:\\s*[가-힣]+)?$`).exec(tt);
    if (saySec && prevOps.some(op => /^looks_(?:say|think)/.test(op))) {
        const sec = toNum(saySec.groups.n);
        let hit = 0;
        const fix = list => list.forEach(n => {
            if (n.body) fix(n.body);
            if (n.body2) fix(n.body2);
            if (n.opcode === 'looks_say' || n.opcode === 'looks_think') {
                const think = n.opcode === 'looks_think';
                n.opcode = think ? 'looks_thinkforsecs' : 'looks_sayforsecs';
                n.inputs.SECS = num(sec);
                n.sig = `${think ? 'thinkfor' : 'sayfor'}(${n.inputs.MESSAGE.value || ''},${fmt(sec)})`;
                n.label = n.label.replace(/ (말하기|생각하기)$/, (m0, v) => `을(를) ${fmt(sec)}초 동안 ${v}`);
                hit++;
            } else if (/^looks_(?:say|think)forsecs$/.test(n.opcode || '')) { n.inputs.SECS = num(sec); n.label = n.label.replace(/[\d.]+초 동안/, `${fmt(sec)}초 동안`); hit++; }
        });
        fix(last.body);
        if (hit) return finish('say', [`말하는 시간을 ${fmt(sec)}초로 정했어요.`]);
    }
    // "안녕 말고 반가워로" — 할 말 바꾸기
    const swapM = /^['"“‘]?(?<a>[^\s'"”’]+?)['"”’]?\s*(?:말고|대신에?|아니고)\s*['"“‘]?(?<b>.+?)['"”’]?\s*(?:이?라고|으로|로)?(?:\s*(?:바꿔|해|말하게|말해|해줘|하자)[가-힣]*)?$/.exec(tt);
    if (swapM) {
        let hit = 0;
        const b = swapM.groups.b.replace(/(?:이?라고|으로|로)$/, '');
        walk(last.body, n => {
            ['MESSAGE', 'QUESTION'].forEach(k => {
                const inp = n.inputs && n.inputs[k];
                if (inp && !inp.node && typeof inp.value === 'string' && inp.value.indexOf(swapM.groups.a) >= 0) {
                    const o = inp.value;
                    inp.value = o.split(swapM.groups.a).join(b);
                    n.sig = String(n.sig).split(o).join(inp.value);
                    n.label = String(n.label).split(o).join(inp.value);
                    hit++;
                }
            });
        });
        if (hit) return finish('say', [`할 말 ‘${swapM.groups.a}’ → ‘${b}’`]);
    }
    // "아니 2점씩" — 변수 바꾸는 양
    const byM = /^(?<n>\d+(?:\.\d+)?)\s*(?:점|개|씩|만큼)+\s*(?:씩)?(?:\s*[가-힣]+)?$/.exec(tt);
    if (byM && /씩/.test(tt) && prevOps.indexOf('data_changevariableby') >= 0) {
        const v = toNum(byM.groups.n);
        walk(last.body, n => {
            if (n.opcode === 'data_changevariableby') {
                const sg = Number(n.inputs.VALUE.value) < 0 ? -1 : 1;
                n.inputs.VALUE = num(sg * v);
                n.sig = `${n.varName}${sg > 0 ? '+' : '-'}${fmt(v)}`;
                n.label = `${eul(n.varName)} ${fmt(sg * v)}만큼 바꾸기`;
            }
        });
        return finish('number', [`한 번에 ${fmt(v)}씩 바뀌게 했어요.`]);
    }
    // "조금만 커지게 120" — 크기 숫자를 말하면 그 크기로 정한다
    const sizeTo = /(?:커|크게|크기|작아|작게)/.test(tt) && !/만큼|씩/.test(tt) ? /(?:^|\s)(?<n>\d+(?:\.\d+)?)\s*(?:%|퍼센트)?\s*(?:으로|로)?(?:\s|$)/.exec(tt) : null;
    if (sizeTo && toNum(sizeTo.groups.n) >= 20 && prevOps.some(op => /^looks_(?:changesizeby|setsizeto)$/.test(op))) {
        const v = toNum(sizeTo.groups.n);
        const set = act('looks_setsizeto', {inputs: {SIZE: num(v)}, sig: `size=${fmt(v)}`, label: `크기를 ${fmt(v)} %로 정하기`});
        const top = last.body.filter(n => !(n.opcode === 'looks_changesizeby' || n.opcode === 'looks_setsizeto'));
        const inLoop = top.length !== last.body.length || top.some(n => n.body);
        last.body = inLoop ? top.concat([set]) : top.concat([set]);
        return finish('number', [`크기를 ${fmt(v)}%로 정하게 바꿨어요.`]);
    }
    // "1초마다 계속" — 방금 것을 N초마다 반복
    const every10 = new RegExp(`^(?<n>${RE_SEC})\\s*초\\s*(?:마다|에\\s*한\\s*번씩|에\\s*한번씩|간격으로)(?:만)?(?:\\s*(?:계속|반복|무한))?(?:\\s*(?:해|하게|반복해|해줘|반복|돼|되게|움직이게|가게|바뀌게))?$`).exec(tt);
    const every10b = every10 || new RegExp(`^(?<n>${RE_SEC})\\s*초\\s*(?:마다|에\\s*한\\s*번씩|간격으로)(?:\\s*[가-힣]+){1,2}$`).exec(tt);
    if (every10b && !every10 && last.body.some(n => n.body && n.body.some(b => (b.opcode === 'control_wait' && Number(b.inputs.DURATION.value) === toNum(every10b.groups.n)) ||
        (/forsecs$/.test(b.opcode || '') && Number(b.inputs.SECS.value) === toNum(every10b.groups.n))))) {
        return {kind: 'explain', ir: null, message: `이미 ${every10b.groups.n}초마다 하고 있어요.`};
    }
    const manyActs = last.body.filter(n => n.t === 'act' && n.opcode !== 'control_wait').length >= 2 && !last.body.some(n => n.t === 'forever');
    if (every10 && last.body.length && !manyActs) {
        const sec = toNum(every10.groups.n);
        const fe = last.body.find(n => n.t === 'forever');
        const inner = fe ? fe.body : last.body.filter(n => !n.cap);
        const kept = inner.filter(n => n.opcode !== 'control_wait');
        const body = kept.concat([waitS(sec)]);
        last.body = fe ? last.body.map(n => (n === fe ? Object.assign(n, {body}) : n)) : [{t: 'forever', body}];
        return finish('forever', [`${fmt(sec)}초마다 계속 하게 ‘무한 반복하기’ 안에 ‘${fmt(sec)}초 기다리기’를 넣었어요.`]);
    }
    // "그냥 한번만 해" — 반복을 푼다
    if (/(?:^|\s)(?:그냥\s*|딱\s*)?(?:한\s*번만|한번만)(?:\s*(?:해|하게|해줘|돼|되게|하고\s*끝(?:내)?|움직이게|가게|하자|나게|울리게|해주세요))?$/.test(tt) && last.body.some(n => n.t === 'forever' || n.t === 'repeat')) {
        const flat = [];
        last.body.forEach(n => { if (n.t === 'forever' || n.t === 'repeat') flat.push(...n.body); else flat.push(n); });
        last.body = flat;
        return finish('repeat', ['반복을 빼서 한 번만 하게 했어요.']);
    }
    // "너무 빨라서 이상해 0.3초 기다리게" — 반복 안/동작 사이에 기다리기
    const slowM = new RegExp(`(?:빨라|빠르|정신\\s*없|휙휙|너무\\s*빨)[가-힣\\s]*?(?<n>${RE_SEC})\\s*초`).exec(tt) || new RegExp(`^(?<n>${RE_SEC})\\s*초\\s*(?:씩\\s*)?(?:기다리|쉬)[가-힣]*$`).exec(tt);
    if (slowM && last.body.length && !/^(?:control_wait)$/.test((last.body[last.body.length - 1] || {}).opcode || '') && !prevOps.some(op => /^cubroid/.test(op)) &&
        !prevOps.some(op => /forsecs$/.test(op))) {
        const sec = toNum(slowM.groups.n);
        const acts = last.body.filter(n => n.t === 'act');
        const loops = last.body.filter(n => n.body);
        let did = 0;
        loops.forEach(l => {
            const w = l.body.find(b => b.opcode === 'control_wait');
            if (w) { w.inputs.DURATION = num(sec, 'math_positive_number'); w.sig = `wait${fmt(sec)}`; w.label = `${fmt(sec)}초 기다리기`; did++; } else { l.body.push(waitS(sec)); did++; }
        });
        if (!did && acts.length >= 2) {
            const nb = [];
            last.body.forEach((n, i) => { nb.push(n); if (n.t === 'act' && i < last.body.length - 1 && n.opcode !== 'control_wait') nb.push(waitS(sec)); });
            last.body = nb;
            did++;
        }
        if (!did && acts.length) { last.body.push(waitS(sec)); did++; }
        if (did) return finish('wait', [`동작 사이에 ‘${fmt(sec)}초 기다리기’를 넣었어요.`]);
    }
    // "아니 왼쪽 위로" — 방금 정한 위치를 그 자리로
    const CORNER = {왼쪽위: [-200, 140], 오른쪽위: [200, 140], 왼쪽아래: [-200, -140], 오른쪽아래: [200, -140], 맨위: [0, 150], 위쪽끝: [0, 150], 맨아래: [0, -150], 아래쪽끝: [0, -150], 왼쪽끝: [-210, 0], 오른쪽끝: [210, 0]};
    const cornerM = /^(?<p>(?:왼쪽|오른쪽)\s*(?:위|아래)|맨\s*(?:위|아래)|(?:위쪽|아래쪽|왼쪽|오른쪽)\s*끝)(?:\s*(?:구석|모서리|끝))?(?:으로|로|에|쪽)?(?:\s*(?:가|보내|옮겨|이동|바꿔|해|가게|보내줘)[가-힣]*)?$/.exec(tt);
    if (cornerM && prevOps.some(op => /^motion_(?:gotoxy|goto|glidesecstoxy)$/.test(op))) {
        const xy = CORNER[cornerM.groups.p.replace(/\s+/g, '')];
        let hit = 0;
        walk(last.body, n => {
            if (hit || !/^motion_(?:gotoxy|goto|glidesecstoxy)$/.test(n.opcode || '')) return;
            n.opcode = n.opcode === 'motion_glidesecstoxy' ? n.opcode : 'motion_gotoxy';
            n.inputs = Object.assign({}, n.inputs, {X: num(xy[0]), Y: num(xy[1])});
            delete n.inputs.TO;
            n.sig = `goto${xy[0]},${xy[1]}`;
            n.label = n.opcode === 'motion_gotoxy' ? `x: ${xy[0]} y: ${xy[1]} 으로 이동하기` : n.label.replace(/x: -?\d+ y: -?\d+/, `x: ${xy[0]} y: ${xy[1]}`);
            hit++;
        });
        if (hit) return finish('number', [`위치를 ${cornerM.groups.p}(x: ${xy[0]}, y: ${xy[1]})로 바꿨어요.`]);
    }
    // "아래로 바꿔", "오른쪽으로 바꿔" — 방향만 뒤집는다
    const dirM = /^(?<d>위|위쪽|아래|아래쪽|밑|왼쪽|오른쪽|앞|앞쪽|뒤|뒤쪽)(?:으로|로)\s*(?:바꿔|가게|해|돌게|돌아|가|움직이게|바꿔줘)[가-힣]*$/.exec(tt);
    if (dirM && !/지\s*말고|대신/.test(String(text))) {
        const d = dirM.groups.d;
        let hit = 0;
        const robot = prevOps.some(op => /_dcMotorControl$/.test(op));
        if (robot) {
            const want = /왼/.test(d) ? ['Left', 'Left'] : /오른/.test(d) ? ['Right', 'Right'] : /앞/.test(d) ? ['Right', 'Left'] : /뒤/.test(d) ? ['Left', 'Right'] : null;
            const fix = list => {
                list.forEach(n => { if (n.body) fix(n.body); if (n.body2) fix(n.body2); });
                for (let i = 0; i + 1 < list.length; i++) {
                    const a = list[i];
                    const b = list[i + 1];
                    if (!want || !/01_dcMotorControl$/.test(a.opcode || '') || !/02_dcMotorControl$/.test(b.opcode || '')) continue;
                    const same = a.fields.INDEX.value === b.fields.INDEX.value;
                    const wantTurn = want[0] === want[1];
                    if (same !== wantTurn) continue;
                    [a, b].forEach((m, k) => { m.fields.INDEX.value = want[k]; m.sig = m.sig.replace(/Right|Left/, want[k]); m.label = m.label.replace(/오른쪽|왼쪽/, want[k] === 'Right' ? '오른쪽' : '왼쪽'); });
                    hit++;
                }
            };
            fix(last.body);
        } else {
            const ax = /위|아래|밑/.test(d) ? 'y' : /왼|오른/.test(d) ? 'x' : null;
            const sg = /위|오른/.test(d) ? 1 : -1;
            walk(last.body, n => {
                if (ax && n.opcode === `motion_change${ax}by`) {
                    const k = ax === 'x' ? 'DX' : 'DY';
                    const v = Math.abs(Number(n.inputs[k].value)) * sg;
                    n.inputs[k] = num(v);
                    n.sig = `${ax}${v >= 0 ? '+' : ''}${fmt(v)}`;
                    n.label = `${ax} 좌표를 ${fmt(v)}만큼 바꾸기`;
                    hit++;
                } else if (ax === 'x' && /^motion_turn(?:left|right)$/.test(n.opcode || '')) {
                    const left = sg < 0;
                    const deg = n.inputs.DEGREES.value;
                    n.opcode = left ? 'motion_turnleft' : 'motion_turnright';
                    n.sig = `${left ? 'left' : 'right'}${deg}`;
                    n.label = `${left ? '왼쪽' : '오른쪽'}으로 ${deg}도 돌기`;
                    hit++;
                }
            });
        }
        if (hit) return finish('number', [`방향을 ${d}(으)로 바꿨어요.`]);
    }
    // "복제된거 랜덤 위치로 가게" — 복제본이 할 일은 '복제되었을 때' 스크립트로
    const cloneM = /^복제(?:된|본|한|된\s*(?:거|것|애|애들|고양이))(?:\s*(?:거|것|애|애들|고양이|들))?(?:은|는|이|가|들은|들이|이랑)?\s+(?<rest>.+)$/.exec(tt);
    if (cloneM && prevOps.indexOf('control_create_clone_of') >= 0) {
        const pr = translate(cloneM.groups.rest, rctx);
        const body = pr.scripts.length ? pr.scripts[0].body : [];
        if (body.length) {
            out.scripts.push({hat: act('control_start_as_clone', {sig: 'clonestart', label: '복제되었을 때'}), body, implicit: false});
            return finish('append', ['복제본이 할 일은 ‘복제되었을 때’ 스크립트로 새로 만들었어요.'], pr.notes.filter(x => x !== NOTE.hat));
        }
    }
    // "노란불 시간 좀 짧게 1초" — 그 불 바로 뒤의 기다리기만
    const lightT = new RegExp(`(?<c>빨간|빨강|초록|녹색|파란|파랑|노란|노랑)\\s*(?:불|불빛|색)?[가-힣\\s]*?(?<n>${RE_SEC})\\s*초`).exec(tt);
    if (lightT && prevOps.indexOf('cubroidmaster_cubroidMasterColorControl') >= 0) {
        const HEX = {빨간: '#ff0000', 빨강: '#ff0000', 초록: '#00ff00', 녹색: '#00ff00', 파란: '#0000ff', 파랑: '#0000ff', 노란: '#ffff00', 노랑: '#ffff00'};
        const hex = HEX[lightT.groups.c];
        const sec = toNum(lightT.groups.n);
        let hit = 0;
        const fix = list => list.forEach((n, i) => {
            if (n.body) fix(n.body);
            if (n.body2) fix(n.body2);
            if (n.sig === `light:${hex}`) {
                const w = list.slice(i + 1).find(x => x.opcode === 'control_wait');
                if (w) { w.inputs.DURATION = num(sec, 'math_positive_number'); w.sig = `wait${fmt(sec)}`; w.label = `${fmt(sec)}초 기다리기`; hit++; }
            }
        });
        fix(last.body);
        if (hit) return finish('number', [`${lightT.groups.c}불 뒤의 기다리기만 ${fmt(sec)}초로 바꿨어요.`]);
    }
    // "너무 느려 70%로" — 로봇 속도
    const spM = /(?<n>\d+)\s*(?:%|퍼센트)/.exec(tt);
    if (spM && prevOps.some(op => /_dcMotorSetSpeed$/.test(op))) {
        const v = Math.max(0, Math.min(100, toNum(spM.groups.n)));
        walk(last.body, n => { if (/_dcMotorSetSpeed$/.test(n.opcode || '')) { n.inputs.SPEED = num(v); n.sig = n.sig.replace(/speed[\d.]+/, `speed${v}`); n.label = n.label.replace(/[\d.]+%/, `${v}%`); } });
        return finish('number', [`로봇 속도를 ${v}%로 바꿨어요.`]);
    }
    // "시끄러우니까 그만" — 멈추는 법 안내
    if (/^[가-힣\s]*(?:니까|어서|아서|해서|잖아)\s*(?:이제\s*)?(?:그만|멈춰|꺼)(?:해|줘|해줘)?$/.test(tt)) {
        return {kind: 'explain', ir: null, message: '실행 중인 걸 멈추려면 무대 위 빨간 정지 버튼을 눌러요. 소리를 줄이려면 ‘3번’처럼 횟수를 줄이거나 ‘한 번만’이라고 말해 보세요.'};
    }
    // "부딪혀요 더 빨리 멈춰야돼" — 자율주행이 늦게 멈추면 속도를 줄인다
    if (/부딪|박아|박혀|충돌|늦게\s*멈|빨리\s*멈|더\s*일찍/.test(tt) && prevOps.indexOf('cubroidproximity_isProximity') >= 0) {
        const cur = []; walk(last.body, n => { if (/_dcMotorSetSpeed$/.test(n.opcode || '') && !n.inputs.SPEED.node) cur.push(Number(n.inputs.SPEED.value)); });
        if (cur.length && cur[0] <= 50) {
            walk(last.body, n => { if (/_dcMotorSetSpeed$/.test(n.opcode || '')) { n.inputs.SPEED = num(30); n.sig = String(n.sig).replace(/speed\d+/, 'speed30'); n.label = String(n.label).replace(/\d+%/, '30%'); } });
            return finish('number', ['속도를 30%로 더 줄였어요. 센서가 보이자마자 멈추라고 하니, 느릴수록 짧게 밀려요.']);
        }
        const sp = (id, k) => act(`${id}_dcMotorSetSpeed`, {inputs: {SPEED: num(50)}, sig: `m${k}:speed50`, label: `DC 모터 ${k}번 속도를 50%로 정하기`});
        last.body = last.body.filter(n => !/_dcMotorSetSpeed$/.test(n.opcode || ''));
        last.body.unshift(sp('cubroiddcmotor01', 1), sp('cubroiddcmotor02', 2));
        walk(last.body, n => { if (n.opcode === 'control_wait' && Number(n.inputs.DURATION.value) <= 0.3) { n.inputs.DURATION = num(0.1, 'math_positive_number'); n.sig = 'wait0.1'; n.label = '0.1초 기다리기'; } });
        return finish('number', ['센서가 거리를 조절할 수는 없어서, 속도를 50%로 줄이고 센서를 더 자주(0.1초마다) 확인하게 했어요. 그러면 더 일찍 멈춰요.']);
    }

    // 계속 반복해 / N번 해 → 방금 만든 스크립트를 감싼다
    if (RE_FOREVER.test(t)) {
        if (last.body.some(n => n.t === 'forever')) return {kind: 'forever', ir: null, message: '이미 ‘무한 반복하기’ 안에 있어요.'};
        const tw = tailWait(last.body);
        last.body = [{t: 'forever', body: last.body}];
        last.body = flattenLoops(last.body, false);
        tidyStructure(last.body, () => {});
        return finish('forever', ['방금 만든 블록을 ‘무한 반복하기’로 감쌌어요.'].concat(tw ? [tw] : []));
    }
    // 기다리기·조건·값 블록(제어·감지·연산·변수)은 '종류'를 가르지 않는다 — 무엇을 하는지는 나머지 블록이 정한다
    const NEUTRAL = /^(?:control_|sensing_|operator_|data_)|_dcMotorStop$|^cubroidlighttouch_(?:getBrightness|isButtonPressed)$|^cubroidproximity_isProximity$/;
    const probeOpsNoStop = probeOps.filter(op => !NEUTRAL.test(op));
    const famOk = probeOpsNoStop.every(op => prevFams.indexOf(famOf(op)) >= 0);
    // "왜 한 번만 하고 끝나?", "멈추지 말고 계속", "계속 확인하게" — 계속하라는 불평
    if (RE_KEEP_GOING.test(t) && famOk) {
        if (last.body.some(n => n.t === 'forever')) {
            return {kind: 'forever', ir: null, message: '이미 ‘무한 반복하기’ 안에 있어서 계속 돼요. 초록 깃발을 눌러 실행했는지, 멈춤(빨간 버튼)을 누르지 않았는지 확인해 보세요.'};
        }
        last.body = flattenLoops([{t: 'forever', body: last.body.filter(n => !n.cap)}], false);
        tidyStructure(last.body, () => {});
        return finish('forever', ['한 번만 하고 끝나지 않게 ‘무한 반복하기’로 감쌌어요.']);
    }
    // "왼쪽 화살표도 만들어줘", "위아래도" — 이미 만든 방향키 스크립트를 보고 나머지 방향을 만든다
    const keyScripts = out.scripts.filter(sc => sc.hat.opcode === 'event_whenkeypressed' && KEYDIR[sc.hat.fields.KEY_OPTION.value]);
    const wantKeys = [];
    const km = /(?:^|\s)(?<k>위아래|아래위|좌우|양옆|위쪽|아래쪽|왼쪽|오른쪽|위|아래)\s*(?:화살표|방향키|키)?\s*(?:도|까지)(?:\s|$)/.exec(t);
    if (km && keyScripts.length) {
        const k = km.groups.k;
        if (/위아래|아래위/.test(k)) wantKeys.push('up arrow', 'down arrow');
        else if (/좌우|양옆/.test(k)) wantKeys.push('left arrow', 'right arrow');
        else wantKeys.push(keyValue(k));
        const base = keyScripts[keyScripts.length - 1];
        const baseAct = base.body.find(n => n.t === 'act');
        const mag = baseAct && baseAct.inputs && (baseAct.inputs.DX || baseAct.inputs.DY) ? Math.abs(Number((baseAct.inputs.DX || baseAct.inputs.DY).value)) : 10;
        const robot = base.body.some(n => /^cubroid/.test(n.opcode || ''));
        const have = keyScripts.map(sc => sc.hat.fields.KEY_OPTION.value);
        const made = [];
        wantKeys.filter(k2 => k2 && have.indexOf(k2) < 0).forEach(k2 => {
            const [ax, sg] = KEYDIR[k2];
            let body;
            if (robot) {
                const kind = {'up arrow': 'forward', 'down arrow': 'backward', 'left arrow': 'left', 'right arrow': 'right'}[k2];
                const PR = {forward: ['Right', 'Left'], backward: ['Left', 'Right'], right: ['Right', 'Right'], left: ['Left', 'Left']}[kind];
                body = clone(base.body);
                let mi = 0;
                walk(body, n => { if (/_dcMotorControl$/.test(n.opcode || '')) { n.fields.INDEX.value = PR[mi % 2]; n.sig = n.sig.replace(/Right|Left/, PR[mi % 2]); n.label = n.label.replace(/오른쪽|왼쪽/, PR[mi % 2] === 'Right' ? '오른쪽' : '왼쪽'); mi++; } });
            } else {
                const v = sg * mag;
                body = [act(ax === 'x' ? 'motion_changexby' : 'motion_changeyby', {inputs: {[ax === 'x' ? 'DX' : 'DY']: num(v)}, sig: `${ax}${v >= 0 ? '+' : ''}${v}`, label: `${ax} 좌표를 ${v}만큼 바꾸기`})];
            }
            out.scripts.push({hat: act('event_whenkeypressed', {fields: fld('KEY_OPTION', k2), sig: `key:${k2}`, label: `${keyKo(k2)} 키를 눌렀을 때`}), body, implicit: false});
            made.push(keyKo(k2));
        });
        if (made.length) return finish('mirror', [`${made.join(', ')} 키 스크립트를 같은 방식으로 만들었어요.`]);
        return {kind: 'mirror', ir: null, message: '그 방향키 스크립트는 이미 있어요.'};
    }
    const rp = t.match(RE_REPEAT);
    // "한 번 더", "두 번 더" — 지금보다 N번 더
    if (rp && /더/.test(t)) {
        const add = Math.max(1, Math.round(toNum(rp.groups.n)));
        const reps0 = last.body.filter(x => x.t === 'repeat');
        if (reps0.length === 1 && last.body.length === 1) { reps0[0].times += add; return finish('repeat', [`반복 횟수를 ${reps0[0].times}번으로 늘렸어요.`]); }
        last.body = [{t: 'repeat', times: add + 1, body: last.body.filter(x => !x.cap)}];
        tidyStructure(last.body, () => {});
        return finish('repeat', [`${add}번 더 하게 ‘${add + 1}번 반복하기’로 감쌌어요.`]);
    }
    if (rp) {
        const n = Math.round(toNum(rp.groups.n));
        if (!(n >= 1)) return null;
        if (last.body.length === 1 && last.body[0].t === 'repeat') {
            const o = last.body[0].times;
            last.body[0].times = n;
            return finish('repeat', [`반복 횟수 ${o} → ${n}`]);
        }
        if (last.body.some(x => x.t === 'forever')) return {kind: 'repeat', ir: null, message: '이미 ‘무한 반복하기’ 안에 있어서 횟수를 정할 수 없어요. 블록에서 직접 바꿔 보세요.'};
        const tw = tailWait(last.body);
        last.body = [{t: 'repeat', times: n, body: last.body}];
        tidyStructure(last.body, () => {});
        return finish('repeat', [`방금 만든 블록을 ‘${n}번 반복하기’로 감쌌어요.`].concat(tw ? [tw] : []));
    }
    // "10번만 커지게" — 같은 동작 이야기에 횟수만 바꿈
    const rOnly = t.match(new RegExp(`^(?<n>${RE_ANY})\\s*번(?:만|으로|씩)`));
    if (rOnly && sameKind) {
        const n = Math.round(toNum(rOnly.groups.n));
        const reps = [];
        out.scripts.forEach(sc => walk(sc.body, x => { if (x.t === 'repeat') reps.push(x); }));
        if (reps.length) {
            const o = reps[0].times;
            reps[0].times = n;
            return finish('repeat', [`반복 횟수 ${o} → ${n}`]);
        }
        if (!last.body.some(x => x.t === 'forever')) {
            last.body = [{t: 'repeat', times: n, body: last.body}];
            tidyStructure(last.body, () => {});
            return finish('repeat', [`방금 만든 블록을 ‘${n}번 반복하기’로 감쌌어요.`]);
        }
    }
    // "한 번 더" — 두 번 하게
    if (/한\s*번\s*더|두\s*번\s*하게|2단\s*점프/.test(t) && famOk) {
        const reps0 = last.body.filter(x => x.t === 'repeat');
        if (reps0.length === 1 && last.body.length === 1) { reps0[0].times += 1; return finish('repeat', [`반복 횟수를 ${reps0[0].times}번으로 늘렸어요.`]); }
        last.body = [{t: 'repeat', times: 2, body: last.body.filter(x => !x.cap)}];
        tidyStructure(last.body, () => {});
        return finish('repeat', ['한 번 더 하게 ‘2번 반복하기’로 감쌌어요.']);
    }
    // "2번도 같이"(모터 1번 뒤) — 다른 모터도
    const otherM = /(?:모터\s*)?([12])\s*번\s*(?:모터)?\s*도/.exec(t);
    if (otherM && rctx.robot && prevOps.some(op => /_dcMotorControl$/.test(op))) {
        const want = otherM[1] === '2' ? 'cubroiddcmotor02' : 'cubroiddcmotor01';
        const have = want === 'cubroiddcmotor02' ? 'cubroiddcmotor01' : 'cubroiddcmotor02';
        const src = [];
        walk(last.body, x => { if (x.opcode === `${have}_dcMotorControl`) src.push(x); });
        if (src.length) {
            const n0 = src[0];
            const dir = n0.fields.INDEX.value === 'Right' ? 'Left' : 'Right';
            const no = want === 'cubroiddcmotor02' ? 2 : 1;
            const mk = act(`${want}_dcMotorControl`, {fields: fld('INDEX', dir), sig: `m${no}:${dir}`, label: `DC 모터 ${no}번을 ${dir === 'Right' ? '오른쪽' : '왼쪽'}으로 돌리기`});
            const list = appendPoint(last, null);
            const at = list.indexOf(n0);
            list.splice(at >= 0 ? at + 1 : list.length, 0, mk);
            return finish('append', [`‘${mk.label}’를 넣었어요.`], ['두 바퀴가 마주 보고 있어서, 같이 앞으로 가려면 1번과 2번을 반대 방향으로 돌려요(파이썬 예제와 같은 규칙).']);
        }
    }
    const nTimes = new RegExp(`(?:^|\\s)(?<n>${RE_ANY})\\s*번(?<only>만)?(?:\\s|$|[가-힣])`).exec(t);
    if (nTimes && famOk && !/바퀴/.test(t) && !/^(?:한|1)$/.test(nTimes.groups.n)) {
        const n = Math.round(toNum(nTimes.groups.n));
        if (n >= 1) {
            const fi = last.body.findIndex(x => x.t === 'forever');
            if (fi >= 0) {
                last.body[fi] = {t: 'repeat', times: n, body: last.body[fi].body};
                return finish('repeat', [`‘무한 반복하기’를 ‘${n}번 반복하기’로 바꿨어요.`]);
            }
            const reps = last.body.filter(x => x.t === 'repeat');
            if (reps.length === 1 && last.body.length <= 2) {
                const o = reps[0].times;
                reps[0].times = n;
                return finish('repeat', [`반복 횟수 ${o} → ${n}`]);
            }
            last.body = [{t: 'repeat', times: n, body: last.body.filter(x => !x.cap)}];
            tidyStructure(last.body, () => {});
            return finish('repeat', [`방금 만든 블록을 ‘${n}번 반복하기’로 감쌌어요.`]);
        }
    }
    // "사이에 좀 쉬었다가" — 기다리기를 넣는다
    const gap = t.match(new RegExp(`(?:사이에|중간에|할\\s*때마다)\\s*(?:(?<n>${RE_SEC})\\s*초\\s*(?:씩|동안)?\\s*)?(?:좀\\s*)?(?:쉬|기다|멈)`)) ||
        t.match(new RegExp(`^(?<n>${RE_SEC})\\s*초\\s*씩\\s*(?:쉬|기다)`));
    if (gap) {
        const sec = toNum(gap.groups.n) || 0.5;
        let already = false;
        walk(last.body, x => { if ((x.opcode === 'looks_sayforsecs' || x.opcode === 'looks_thinkforsecs') && Number(x.inputs.SECS.value) === sec) already = true; });
        if (already) return {kind: 'wait', ir: null, message: `이미 ${fmt(sec)}초씩 말하고 넘어가요(‘${fmt(sec)}초 동안 말하기’). 더 쉬게 하려면 ‘2초씩’처럼 말해 보세요.`};
        if (insertWaits(out, sec)) return finish('wait', [`사이에 ‘${fmt(sec)}초 기다리기’를 넣었어요.`]);
        let pushed = 0;
        out.scripts.forEach(sc => walk(sc.body, n => { if ((n.t === 'repeat' || n.t === 'forever') && !pushed) { n.body.push(act('control_wait', {inputs: {DURATION: num(sec, 'math_positive_number')}, sig: `wait${fmt(sec)}`, label: `${fmt(sec)}초 기다리기`})); pushed++; } }));
        if (pushed) return finish('wait', [`한 번 할 때마다 끝에 ‘${fmt(sec)}초 기다리기’를 넣었어요.`]);
        return {kind: 'wait', ir: null, message: '기다리기를 넣을 자리를 못 찾았어요. ‘제어’에서 ‘기다리기’를 직접 끼워 넣어 보세요.'};
    }
    // "반대로 돌게" — 오른쪽 ↔ 왼쪽
    if (/반대로|반대\s*방향|거꾸로/.test(t) && (/돌|회전/.test(t) || t.length <= 8)) {
        let flipped = 0;
        out.scripts.forEach(sc => walk(sc.body, x => {
            if (x.opcode === 'motion_turnright' || x.opcode === 'motion_turnleft') {
                const toLeft = x.opcode === 'motion_turnright';
                x.opcode = toLeft ? 'motion_turnleft' : 'motion_turnright';
                x.sig = String(x.sig).replace(toLeft ? /^right/ : /^left/, toLeft ? 'left' : 'right');
                x.label = String(x.label).replace(toLeft ? '오른쪽' : '왼쪽', toLeft ? '왼쪽' : '오른쪽');
                flipped++;
            }
        }));
        if (!flipped && rctx.robot) {
            out.scripts.forEach(sc => walk(sc.body, x => {
                if (/_dcMotorControl$/.test(x.opcode || '')) {
                    const v = x.fields.INDEX.value === 'Right' ? 'Left' : 'Right';
                    x.fields.INDEX.value = v;
                    x.sig = x.sig.replace(/Right|Left/, v);
                    x.label = x.label.replace(/오른쪽|왼쪽/, v === 'Right' ? '오른쪽' : '왼쪽');
                    flipped++;
                }
            }));
            if (flipped) return finish('flip', ['모터 방향을 모두 반대로 바꿨어요.'], ['제자리 돌기는 두 모터를 같은 방향으로, 반대로 돌려면 둘 다 반대로 바꿔요.']);
        }
        if (flipped) return finish('flip', [`도는 방향을 반대로 바꿨어요(${flipped}개).`]);
    }
    // 값만 바꿔 끼우기 — "그거 말고 파란색", "하트로 바꿔줘", "파랑으로"
    const colorM = /^(?:말고\s*|대신\s*|아니\s*)?(?:(?:로봇|불빛|불|LED|색|색깔|마스터\s*블록|마스터)(?:을|은|는|의)?\s*)*(?<c>빨간|빨강|초록|녹색|파란|파랑|노란|노랑|하얀|흰|하양|보라|분홍|핑크|주황|하늘)\s*(?:색|색깔)?(?:\s*(?:으로|로))?(?:\s*(?:바꿔|해|켜|할래|줘)[가-힣]*)?$/.exec(t);
    if (colorM && prevOps.some(op => /cubroidmaster_cubroidMasterColorControl/.test(op))) {
        const HEX = {빨간: '#ff0000', 빨강: '#ff0000', 초록: '#00ff00', 녹색: '#00ff00', 파란: '#0000ff', 파랑: '#0000ff', 노란: '#ffff00', 노랑: '#ffff00', 하얀: '#ffffff', 흰: '#ffffff', 하양: '#ffffff', 보라: '#ff00ff', 분홍: '#ff66cc', 핑크: '#ff66cc', 주황: '#ff8800', 하늘: '#00ffff'};
        let tgt = null;
        out.scripts.forEach(sc => walk(sc.body, x => { if (x.opcode === 'cubroidmaster_cubroidMasterColorControl') tgt = x; }));
        const hex = HEX[colorM.groups.c];
        const old = tgt.label;
        tgt.prevHex = tgt.inputs.COLOR.value;
        tgt.inputs = {COLOR: {sh: 'colour_picker', field: 'COLOUR', value: hex}};
        tgt.sig = `light:${hex}`;
        tgt.label = `${colorM.groups.c}색 불빛 켜기`;
        return finish('swap', [`‘${old}’ → ‘${tgt.label}’`]);
    }
    // LED 그림 바꾸기 — "하트로 바꿔줘", "웃는 거 말고 별"
    const faceM = /^(?:말고\s*|대신\s*|아니\s*)?(?<w>윙크하는|윙크|웃는|웃음|스마일|행복한|행복|우는|슬픈|화난|자는|하트|별|해님|태양|달|꽃|네모|동그라미|세모|느낌표|물음표|음표)\s*(?:얼굴|모양|그림|표정)?(?:\s*(?:으로|로))?(?:\s*(?:바꿔|해|띄워|보여)[가-힣]*)?$/.exec(t);
    if (faceM && prevOps.indexOf('cubroidled_lceImgControl') >= 0) {
        const pr = translate(`LED에 ${faceM.groups.w} 보여줘`, rctx);
        let nn = null;
        pr.scripts.forEach(sc => walk(sc.body, x => { if (x.opcode === 'cubroidled_lceImgControl') nn = x; }));
        let tgt = null;
        out.scripts.forEach(sc => walk(sc.body, x => { if (x.opcode === 'cubroidled_lceImgControl') tgt = x; }));
        if (nn && tgt) {
            const old = tgt.label;
            tgt.prevImg = {fields: clone(tgt.fields), sig: tgt.sig, label: tgt.label};
            tgt.fields = clone(nn.fields);
            tgt.sig = nn.sig;
            tgt.label = nn.label;
            return finish('swap', [`‘${old}’ → ‘${tgt.label}’`]);
        }
    }
    const laps = /(?<n>한|두|세|네|1|2|3|4)\s*바퀴/.exec(t);
    if (laps && rctx.robot) {
        const sq = last.body.find(x => x.t === 'repeat' && x.times === 4 && x.body.some(y => /_dcMotorControl$/.test(y.opcode || '')));
        if (sq) {
            const k = toNum(laps.groups.n);
            sq.times = /더/.test(t) ? sq.times + 4 * k : 4 * k;
            return finish('repeat', [`사각형 반복 횟수를 ${sq.times}번(${sq.times / 4}바퀴)으로 바꿨어요.`]);
        }
    }
    // "뒤로 가는 키도"(누르는 동안 가기 뒤) — 아래쪽 화살표 누르는 동안 뒤로
    if (/(?:뒤로|후진)\s*(?:가는)?\s*키/.test(t) && last.hat.opcode !== 'event_whenkeypressed') {
        let ie = null;
        walk(last.body, x => { if (x.t === 'ifelse' && x.cond && /^keyheld/.test(x.cond.sig || '')) ie = x; });
        if (ie) {
            const back = [act('cubroiddcmotor01_dcMotorControl', {fields: fld('INDEX', 'Left'), sig: 'm1:Left', label: 'DC 모터 1번을 왼쪽으로 돌리기'}),
                act('cubroiddcmotor02_dcMotorControl', {fields: fld('INDEX', 'Right'), sig: 'm2:Right', label: 'DC 모터 2번을 오른쪽으로 돌리기'})];
            ie.body2 = [{t: 'ifelse', cond: act('sensing_keypressed', {inputs: {KEY_OPTION: menu('sensing_keyoptions', 'KEY_OPTION', 'down arrow')}, sig: 'keyheld:down arrow', label: '아래쪽 화살표 키를 눌렀는가?'}), body: back, body2: ie.body2}];
            return finish('if', ['‘아니면’ 안에 ‘아래쪽 화살표를 누르고 있으면 뒤로’를 넣었어요.']);
        }
    }
    // "1초마다 번갈아서" — 방금 바꾼 것과 예전 것을 번갈아
    if (/번갈아|교대로|왔다\s*갔다/.test(t)) {
        const secB = (/(\d+(?:\.\d+)?)\s*초/.exec(t) || [])[1];
        const sec = secB ? Number(secB) : 1;
        let alt = null;
        out.scripts.forEach(sc => walk(sc.body, x => { if (x.prevImg || x.prevHex) alt = x; }));
        if (alt) {
            const a = clone(alt);
            let b;
            if (alt.prevImg) b = Object.assign(clone(alt), alt.prevImg);
            else b = Object.assign(clone(alt), {inputs: {COLOR: {sh: 'colour_picker', field: 'COLOUR', value: alt.prevHex}}, sig: `light:${alt.prevHex}`, label: `${HEX_KO[alt.prevHex] || '예전'}색 불빛 켜기`});
            delete a.prevImg; delete a.prevHex; delete b.prevImg; delete b.prevHex;
            const w = () => act('control_wait', {inputs: {DURATION: num(sec, 'math_positive_number')}, sig: `wait${fmt(sec)}`, label: `${fmt(sec)}초 기다리기`});
            last.body = [{t: 'forever', body: [b, w(), a, w()]}];
            return finish('restate', [`예전 것과 지금 것을 ${fmt(sec)}초씩 번갈아 계속 보여줘요.`]);
        }
    }
    // "위아래는 빼줘 좌우만" — 방향키 스크립트 지우기
    const dropK = /(?<k>위아래|아래위|좌우|위|아래|왼쪽|오른쪽)(?:는|은|키는|키)?\s*(?:빼|지워|없애|필요\s*없)/.exec(t) || (/(?<only>좌우|위아래)\s*만/.exec(t));
    if (dropK && out.scripts.some(sc => sc.hat.opcode === 'event_whenkeypressed')) {
        const g0 = dropK.groups;
        let drop;
        if (g0.only) drop = g0.only === '좌우' ? ['up arrow', 'down arrow', 'w', 's'] : ['left arrow', 'right arrow', 'a', 'd'];
        else if (/위아래|아래위/.test(g0.k)) drop = ['up arrow', 'down arrow', 'w', 's'];
        else if (g0.k === '좌우') drop = ['left arrow', 'right arrow', 'a', 'd'];
        else drop = [keyValue(g0.k)];
        const before0 = out.scripts.length;
        out.scripts = out.scripts.filter(sc => !(sc.hat.opcode === 'event_whenkeypressed' && drop.indexOf(sc.hat.fields.KEY_OPTION.value) >= 0));
        if (out.scripts.length && out.scripts.length < before0) return finish('drop', [`${before0 - out.scripts.length}개의 방향키 스크립트를 뺐어요.`]);
    }
    // "스페이스는 멈추기", "q는 점프" — 키 하나에 할 일
    const keyMap = /^(?<k>스[페패]이스(?:바)?|[a-z]|위쪽|아래쪽|왼쪽|오른쪽)\s*(?:키)?(?:는|은|누르면)\s+(?<rest>.+)$/.exec(t);
    const probeMulti = probe0.scripts.filter(sc => !sc.implicit).length;
    if (probeMulti >= 2) {
        const keys = probe0.scripts.map(sc => sc.hat.sig);
        out.scripts = out.scripts.filter(sc => keys.indexOf(sc.hat.sig) < 0 || sc.hat.opcode !== 'event_whenkeypressed');
        probe0.scripts.forEach(sc => out.scripts.push(sc));
        return finish('mirror', [`스크립트 ${probe0.scripts.length}개를 더했어요.`], probe0.notes.filter(x => x !== NOTE.hat));
    }
    if (keyMap && out.scripts.some(sc => sc.hat.opcode === 'event_whenkeypressed')) {
        const k = keyValue(keyMap.groups.k);
        const pr = translate(keyMap.groups.rest, rctx);
        if (k && pr.scripts.length === 1 && pr.scripts[0].body.length) {
            out.scripts = out.scripts.filter(sc => !(sc.hat.opcode === 'event_whenkeypressed' && sc.hat.fields.KEY_OPTION.value === k));
            out.scripts.push({hat: act('event_whenkeypressed', {fields: fld('KEY_OPTION', k), sig: `key:${k}`, label: `${keyKo(k)} 키를 눌렀을 때`}), body: pr.scripts[0].body, implicit: false});
            return finish('mirror', [`${keyKo(k)} 키를 누르면 하는 스크립트를 만들었어요.`], pr.notes.filter(x => x !== NOTE.hat));
        }
    }
    // 펜 모양 바꾸기 — "삼각형으로 바꿔줘"
    const shapeM = /(?<sh>정사각형|사각형|네모|삼각형|세모|오각형|육각형|별|원|동그라미)(?:으로|로)?\s*(?:바꿔|해|그려)/.exec(t);
    if (shapeM && prevOps.indexOf('pen_penDown') >= 0) {
        const pr = translate(`펜으로 ${shapeM.groups.sh} 그려줘`, rctx);
        if (pr.scripts.length) { last.body = pr.scripts[0].body; return finish('restate', [`${shapeM.groups.sh} 그리기로 바꿨어요.`], pr.notes.filter(x => x !== NOTE.hat)); }
    }
    // 펜 색·굵기 — "빨간색으로 두껍게"
    if (prevOps.indexOf('pen_penDown') >= 0 && /(빨간|빨강|초록|녹색|파란|파랑|노란|노랑|보라|분홍|주황|하늘|검은|까만|검정)\s*(?:색)?|두껍게|굵게|얇게|가늘게|굵기\s*\d/.test(t)){
        const HEXP = {빨간: '#ff0000', 빨강: '#ff0000', 초록: '#00ff00', 녹색: '#00ff00', 파란: '#0000ff', 파랑: '#0000ff', 노란: '#ffff00', 노랑: '#ffff00', 보라: '#ff00ff', 분홍: '#ff66cc', 주황: '#ff8800', 하늘: '#00ffff', 검은: '#000000', 까만: '#000000', 검정: '#000000'};
        const cm = /(빨간|빨강|초록|녹색|파란|파랑|노란|노랑|보라|분홍|주황|하늘|검은|까만|검정)/.exec(t);
        const adds = [];
        if (cm) adds.push(act('pen_setPenColorToColor', {inputs: {COLOR: {sh: 'colour_picker', field: 'COLOUR', value: HEXP[cm[1]]}}, sig: `pen:color${HEXP[cm[1]]}`, label: `펜 색깔을 ${cm[1]}색으로 정하기`}));
        const szN = /(?:굵기|두께)[^\d]{0,8}?(\d+)|(?:두껍게|굵게)\s*(\d+)/.exec(t);
        if (/두껍게|굵게|얇게|가늘게/.test(t) || szN) {
            const sz = szN ? Number(szN[1] || szN[2]) : /얇게|가늘게/.test(t) ? 1 : 5;
            adds.push(act('pen_setPenSizeTo', {inputs: {SIZE: num(sz)}, sig: `pen:size${sz}`, label: `펜 굵기를 ${sz}(으)로 정하기`}));
        }
        last.body = last.body.filter(x => !/^pen_set/.test(x.opcode || ''));
        last.body.unshift(...adds);
        return finish('append', adds.map(x => `‘${x.label}’를 맨 앞에 넣었어요.`));
    }
    if (/지우고\s*다시|다시\s*그려|새로\s*그려/.test(t) && prevOps.indexOf('pen_penDown') >= 0) {
        if (prevOps.indexOf('pen_clear') >= 0) return {kind: 'explain', ir: null, message: '이미 맨 앞에 ‘모두 지우기’가 있어서, 초록 깃발을 누를 때마다 지우고 다시 그려요.'};
        last.body.unshift(act('pen_clear', {sig: 'pen:clear', label: '모두 지우기'}));
        return finish('append', ['맨 앞에 ‘모두 지우기’를 넣었어요. 이제 실행할 때마다 지우고 다시 그려요.']);
    }
    // "멈추는 방법", "그만 돌게" — 방법을 알려 준다
    if (/멈추는\s*방법|어떻게\s*멈|^그만\s*\S+/.test(t)) {
        return {kind: 'explain', ir: null, message: '실행 중인 걸 멈추려면 무대 위 빨간 정지 버튼을 눌러요. 몇 번만 하고 멈추게 하려면 ‘5번만’, 시간을 정하려면 ‘10초만 하고 멈춰’처럼 말해 보세요.'};
    }
    // "50까지만"(점점 작아지기·커지기 뒤) — 그 크기에 닿을 때까지 반복
    const upToSize = /(\d+)\s*(?:%|퍼센트)?\s*까지(?:만)?/.exec(t);
    if (upToSize && prevOps.indexOf('looks_changesizeby') >= 0) {
        const lim = Number(upToSize[1]);
        let rp = null;
        walk(last.body, x => { if (x.t === 'repeat' && x.body.some(y => y.opcode === 'looks_changesizeby')) rp = x; });
        if (rp) {
            const shrink = Number(rp.body.find(y => y.opcode === 'looks_changesizeby').inputs.CHANGE.value) < 0;
            rp.t = 'until';
            rp.cond = act(shrink ? 'operator_lt' : 'operator_gt', {inputs: {OPERAND1: inT(act('looks_size', {sig: 'size', label: '크기'})), OPERAND2: txt(String(lim))}, sig: `size${shrink ? '<' : '>'}${lim}`, label: `크기 ${shrink ? '<' : '>'} ${lim}`});
            delete rp.times;
            return finish('until', [`‘크기가 ${lim}${shrink ? '보다 작아질' : '보다 커질'} 때까지 반복하기’로 바꿨어요.`]);
        }
    }
    // "다시 누르면 멈추게"(버튼으로 앞으로 가기 뒤)
    if (/(?:다시\s*)?누르면\s*(?:멈추|멈춰|서|정지)/.test(t) && prevOps.indexOf('cubroidlighttouch_isButtonPressed') >= 0 && prevOps.some(op => /_dcMotorControl$/.test(op))) {
        const tg = translate('버튼 누르면 앞으로 가고 다시 누르면 멈추게', rctx);
        if (tg.scripts.length) { last.body = tg.scripts[0].body; return finish('restate', ['버튼을 누를 때마다 가기 ↔ 멈추기가 바뀌게 했어요(‘상태’ 변수로 기억해요).'], tg.notes.filter(x => x !== NOTE.hat)); }
    }
    // "가는 동안 초록불" — 모터를 켤 때 불도 켜고, 멈출 때 끈다
    const whileM = /(?:가는|달리는|움직이는|도는)\s*동안\s*(?<c>빨간|빨강|초록|녹색|파란|파랑|노란|노랑|흰|하얀)?\s*(?:색)?\s*불/.exec(t);
    if (whileM && prevOps.some(op => /_dcMotorControl$/.test(op))) {
        const HEXW = {빨간: '#ff0000', 빨강: '#ff0000', 초록: '#00ff00', 녹색: '#00ff00', 파란: '#0000ff', 파랑: '#0000ff', 노란: '#ffff00', 노랑: '#ffff00', 흰: '#ffffff', 하얀: '#ffffff'};
        const hex = HEXW[whileM.groups.c] || '#ffffff';
        let done = false;
        const put = list => {
            if (done) return;
            const i0 = list.findIndex(x => /_dcMotorControl$/.test(x.opcode || ''));
            if (i0 >= 0) {
                list.splice(i0, 0, colorOn(hex));
                let j = list.length - 1;
                while (j >= 0 && !/_dcMotorStop$/.test(list[j].opcode || '')) j--;
                if (j > i0) list.splice(j + 1, 0, colorOff());
                done = true;
                return;
            }
            list.forEach(x => { if (x.body) put(x.body); if (x.body2) put(x.body2); });
        };
        put(last.body);
        if (done) return finish('append', ['모터를 켤 때 불을 켜고, 멈출 때 끄게 했어요.']);
    }
    // "왜 안 멈춰?" — 멈추는 블록이 있으면 알려 준다
    if (/왜\s*안\s*(?:멈|서|꺼)/.test(t) && prevOps.some(op => /_dcMotorStop$|ColorOff|control_stop/.test(op))) {
        return {kind: 'explain', ir: null, message: '멈추는 블록(‘끄기’)이 이미 들어 있어요. 기다리기 시간이 지나면 멈춰요. 그래도 안 멈추면 로봇이 연결됐는지, 배터리가 충분한지 확인해 보세요.'};
    }
    // "말하는 내용 랜덤으로" — 할 말을 목록에서 무작위로
    if (/(?:말|대사|내용)\s*(?:을|를|은|이)?\s*(?:랜덤|무작위|아무거나|여러\s*가지)/.test(t) && prevOps.some(op => /^looks_(?:say|think)/.test(op))) {
        let tgt = null;
        out.scripts.forEach(sc => walk(sc.body, x => { if (/^looks_(?:say|think)/.test(x.opcode || '')) tgt = x; }));
        const words = ['안녕!', '반가워!', '좋은 하루!'];
        const L = (op, o) => act(op, Object.assign({listName: '할 말'}, o));
        const pick = L('data_itemoflist', {inputs: {INDEX: inN(act('operator_random', {inputs: {FROM: num(1), TO: num(words.length)}, sig: `random1-${words.length}`, label: `1부터 ${words.length} 사이의 난수`}))}, sig: 'item(할 말)', label: '할 말의 무작위 항목'});
        tgt.inputs = Object.assign({}, tgt.inputs, {MESSAGE: inT(pick)});
        tgt.sig = `${tgt.opcode === 'looks_think' ? 'think' : 'say'}(무작위)`;
        tgt.label = '‘할 말’ 목록에서 무작위로 골라 말하기';
        last.body.unshift(L('data_deletealloflist', {sig: 'list:할 말:clear', label: '할 말의 항목을 모두 삭제하기'}),
            ...words.map(w => L('data_addtolist', {inputs: {ITEM: txt(w)}, sig: `list:할 말+${w}`, label: `${w}을(를) 할 말에 추가하기`})));
        return finish('swap', ['‘할 말’ 리스트에 말 3개를 넣고, 말할 때 그중 하나를 무작위로 골라요. 리스트에 말을 더 넣어 보세요.']);
    }
    // "깜빡이게도" — 방금 켠 불을 깜빡이게
    if (/깜빡|깜박|반짝/.test(t) && prevOps.indexOf('cubroidmaster_cubroidMasterColorControl') >= 0 && !last.body.some(x => x.t === 'forever')) {
        let hex = '#ffffff';
        walk(last.body, x => { if (x.opcode === 'cubroidmaster_cubroidMasterColorControl') hex = x.inputs.COLOR.value; });
        const on = colorOn(hex);
        const off = act('cubroidmaster_cubroidColorOffControl', {sig: 'light:off', label: '불빛 끄기'});
        const w = () => act('control_wait', {inputs: {DURATION: num(0.5, 'math_positive_number')}, sig: 'wait0.5', label: '0.5초 기다리기'});
        last.body = last.body.filter(x => x.opcode !== 'cubroidmaster_cubroidMasterColorControl').concat([{t: 'forever', body: [on, w(), off, w()]}]);
        return finish('blink', ['불을 ‘켜기 → 0.5초 → 끄기 → 0.5초’로 계속 깜빡이게 했어요.']);
    }
    // "다시 누르면 꺼지게"(버튼으로 켜기 뒤) — 누를 때마다 켜졌다 꺼졌다
    if (/(?:다시\s*)?누르면\s*(?:꺼|끄)|누를\s*때마다\s*(?:켜졌다|껐다)/.test(t) && prevOps.indexOf('cubroidlighttouch_isButtonPressed') >= 0) {
        const tg = translate('버튼 누를때마다 불 켜졌다 꺼졌다', rctx);
        if (tg.scripts.length) { last.body = tg.scripts[0].body; return finish('restate', ['버튼을 누를 때마다 켜졌다 꺼졌다 하게 바꿨어요(‘불’ 변수로 기억해요).'], tg.notes.filter(x => x !== NOTE.hat)); }
    }
    // "떼면 멈추게"(버튼을 누르면 앞으로 뒤) — 누르고 있는 동안만 가게: 만약 버튼 → 켜기, 아니면 → 끄기
    if (/(?:손\s*)?떼면\s*(?:멈|정지|서|꺼)|누르는\s*동안만|누르고\s*있을\s*때만/.test(t)) {
        let hit = null;
        walk(last.body, x => { if (!hit && (x.t === 'if' || x.t === 'ifelse') && x.cond && x.cond.sig === 'button') hit = x; });
        if (hit) {
            const ons = [];
            walk(hit.body, x => { if (/_dcMotorControl$|^cubroidmaster_cubroidMasterColorControl$/.test(x.opcode || '')) ons.push(clone(x)); });
            if (ons.length) {
                const robotMv = ons.some(x => /_dcMotorControl$/.test(x.opcode));
                hit.t = 'ifelse';
                hit.body = ons;
                hit.body2 = robotMv ? stopBoth() : [colorOff()];
                return finish('restate', ['누르고 있는 동안만 하게 바꿨어요: 버튼을 누르고 있으면 켜고, 떼면(아니면) 꺼요.']);
            }
        }
    }
    // "떼면 멈추게"(키를 누르면 로봇 앞으로 뒤) — 누르고 있는 동안만 가게
    if (/(?:손\s*)?떼면\s*(?:멈|정지|서)|누르는\s*동안만|누르고\s*있을\s*때만/.test(t) && last.hat.opcode === 'event_whenkeypressed') {
        const key = last.hat.fields.KEY_OPTION.value;
        const moves = [];
        walk(last.body, x => { if (/_dcMotorControl$/.test(x.opcode || '') || /^motion_/.test(x.opcode || '')) moves.push(clone(x)); });
        if (moves.length) {
            const robotMv = moves.some(x => /_dcMotorControl$/.test(x.opcode));
            const cond = act('sensing_keypressed', {inputs: {KEY_OPTION: menu('sensing_keyoptions', 'KEY_OPTION', key)}, sig: `keyheld:${key}`, label: `${keyKo(key)} 키를 눌렀는가?`});
            last.hat = act('event_whenflagclicked', {sig: 'flag', label: '깃발을 클릭했을 때'});
            last.body = [{t: 'forever', body: [robotMv ?
                {t: 'ifelse', cond, body: moves, body2: [act('cubroiddcmotor01_dcMotorStop', {sig: 'm1:off', label: 'DC 모터 1번을 끄기'}), act('cubroiddcmotor02_dcMotorStop', {sig: 'm2:off', label: 'DC 모터 2번을 끄기'})]} :
                {t: 'if', cond, body: moves}]}];
            return finish('restate', ['‘키를 눌렀을 때’를 ‘누르고 있는 동안’으로 바꿨어요: 계속 확인하면서, 누르고 있으면 가고 떼면 멈춰요.']);
        }
    }
    // "마우스 클릭할 때만 따라오게" — 무한 반복 안을 '만약'으로 감싼다
    const onlyWhen = /마우스(?:를)?\s*(?:클릭|누를|누르고\s*있을)(?:할)?\s*때만|(?<k>스[페패]이스|위쪽|아래쪽|왼쪽|오른쪽|[a-z])\s*(?:키|화살표)?(?:를)?\s*(?:누를|누르고\s*있을)\s*때만/.exec(t);
    if (onlyWhen && last.body.some(x => x.t === 'forever')) {
        const fe = last.body.find(x => x.t === 'forever');
        const k = onlyWhen.groups && onlyWhen.groups.k ? keyValue(onlyWhen.groups.k) : null;
        const cond = k ? act('sensing_keypressed', {inputs: {KEY_OPTION: menu('sensing_keyoptions', 'KEY_OPTION', k)}, sig: `keyheld:${k}`, label: `${keyKo(k)} 키를 눌렀는가?`}) :
            act('sensing_mousedown', {sig: 'mousedown', label: '마우스를 클릭했는가?'});
        fe.body = [{t: 'if', cond, body: fe.body}];
        return finish('if', [`‘무한 반복하기’ 안을 ‘만약 ${cond.label} 라면’으로 감쌌어요.`]);
    }
    // "소리 끝나고 돌게" — 재생하기 → 끝까지 재생하기
    if (/소리(?:가)?\s*(?:끝나고|끝난\s*(?:다음|뒤)|다\s*나고)|끝까지\s*(?:재생|듣|나)/.test(t) && prevOps.indexOf('sound_play') >= 0) {
        walk(last.body, x => { if (x.opcode === 'sound_play') { x.opcode = 'sound_playuntildone'; x.sig = String(x.sig).replace(/^sound\(/, 'soundwait('); x.label = String(x.label).replace(' 재생하기', ' 끝까지 재생하기'); } });
        return finish('swap', ['‘재생하기’를 ‘끝까지 재생하기’로 바꿨어요. 이제 소리가 끝난 뒤에 다음 블록이 실행돼요.']);
    }
    // "고양이가 거꾸로 뒤집혀" — 튕기면 뒤집히는 건 회전 방식 때문
    if (/뒤집혀|거꾸로\s*(?:돼|됨|보여|되)|뒤집어져|뒤집어지/.test(t) && prevOps.some(op => /bounce|turn|pointtowards|goto|glide/.test(op))) {
        last.body.unshift(act('motion_setrotationstyle', {fields: fld('STYLE', 'left-right'), sig: 'rotstyle:left-right', label: '회전 방식을 왼쪽-오른쪽으로 정하기'}));
        return finish('rotstyle', ['맨 앞에 ‘회전 방식을 왼쪽-오른쪽으로 정하기’를 넣었어요. 이제 벽에서 튕겨도 거꾸로 뒤집히지 않아요.']);
    }
    // "뒤로 가는 키도" — 방향키 스크립트에 뒤로(아래쪽 화살표)
    if (/(?:뒤로|후진)\s*(?:가는)?\s*키/.test(t) && last.hat.opcode === 'event_whenkeypressed') {
        const robotK = prevOps.some(op => /_dcMotorControl$/.test(op));
        const body = robotK ? [act('cubroiddcmotor01_dcMotorControl', {fields: fld('INDEX', 'Left'), sig: 'm1:Left', label: 'DC 모터 1번을 왼쪽으로 돌리기'}),
            act('cubroiddcmotor02_dcMotorControl', {fields: fld('INDEX', 'Right'), sig: 'm2:Right', label: 'DC 모터 2번을 오른쪽으로 돌리기'}),
            act('control_wait', {inputs: {DURATION: num(1, 'math_positive_number')}, sig: 'wait1', label: '1초 기다리기'}),
            act('cubroiddcmotor01_dcMotorStop', {sig: 'm1:off', label: 'DC 모터 1번을 끄기'}), act('cubroiddcmotor02_dcMotorStop', {sig: 'm2:off', label: 'DC 모터 2번을 끄기'})] :
            [act('motion_changeyby', {inputs: {DY: num(-10)}, sig: 'y-10', label: 'y 좌표를 -10만큼 바꾸기'})];
        out.scripts.push({hat: act('event_whenkeypressed', {fields: fld('KEY_OPTION', 'down arrow'), sig: 'key:down arrow', label: '아래쪽 화살표 키를 눌렀을 때'}), body, implicit: false});
        return finish('mirror', ['아래쪽 화살표 키를 누르면 뒤로 가는 스크립트를 만들었어요.']);
    }
    // "1초마다"(동작 뒤) — 무한 반복 + 기다리기
    const everyM = new RegExp(`^(?<n>${RE_SEC})\\s*초\\s*(?:마다|에\\s*한\\s*번씩|간격으로)$`).exec(t);
    const shows = last.body.filter(x => x.t === 'act' && /lce|ColorControl|looks_say|looks_think|looks_switch|nextcostume/.test(x.opcode));
    if (everyM && shows.length >= 2 && !last.body.some(x => x.t !== 'act' || x.opcode === 'control_wait')) {
        const sec = toNum(everyM.groups.n);
        const nb = [];
        last.body.forEach((x, i) => { nb.push(x); if (i < last.body.length - 1) nb.push(act('control_wait', {inputs: {DURATION: num(sec, 'math_positive_number')}, sig: `wait${fmt(sec)}`, label: `${fmt(sec)}초 기다리기`})); });
        last.body = nb;
        return finish('wait', [`사이사이에 ‘${fmt(sec)}초 기다리기’를 넣었어요.`]);
    }
    if (everyM && shows.length >= 2 && last.body.some(x => x.opcode === 'control_wait') && !last.body.some(x => x.t !== 'act')) {
        const sec = toNum(everyM.groups.n);
        last.body.forEach(x => { if (x.opcode === 'control_wait') { x.inputs.DURATION = num(sec, 'math_positive_number'); x.sig = `wait${fmt(sec)}`; x.label = `${fmt(sec)}초 기다리기`; } });
        return finish('wait', [`사이사이 기다리기를 ${fmt(sec)}초로 맞췄어요.`]);
    }
    if (everyM && !last.body.some(x => x.t === 'forever')) {
        const sec = toNum(everyM.groups.n);
        last.body = [{t: 'forever', body: last.body.filter(x => !x.cap).concat([act('control_wait', {inputs: {DURATION: num(sec, 'math_positive_number')}, sig: `wait${fmt(sec)}`, label: `${fmt(sec)}초 기다리기`})])}];
        return finish('forever', [`${fmt(sec)}초마다 하도록 ‘무한 반복하기’와 ‘${fmt(sec)}초 기다리기’를 넣었어요.`]);
    }
    // "천천히 미끄러지게"(움직이기 뒤) — 미끄러지기로
    if (/미끄러/.test(t) && prevOps.indexOf('motion_movesteps') >= 0 && !prevOps.some(op => /goto/.test(op))) {
        let d0 = 0;
        walk(last.body, x => {
            if (x.opcode === 'motion_movesteps' && !d0) {
                const st = Number(x.inputs.STEPS.value);
                x.opcode = 'motion_glidesecstoxy';
                x.inputs = {SECS: num(/천천히|느리게/.test(t) ? 2 : 1), X: inN(opN('operator_add', act('motion_xposition', {sig: 'xpos', label: 'x 좌표'}), st)), Y: inN(act('motion_yposition', {sig: 'ypos', label: 'y 좌표'}))};
                x.sig = `glideby${st}`;
                x.label = `${/천천히|느리게/.test(t) ? 2 : 1}초 동안 x: (x 좌표 + ${st}) 로 이동하기`;
                d0++;
            }
        });
        if (d0) return finish('glide', ['‘움직이기’를 오른쪽으로 미끄러지는 ‘~초 동안 이동하기’로 바꿨어요.'], ['미끄러지기는 방향 대신 도착할 좌표를 정해요.']);
    }
    // "순간이동 이상해, 미끄러지듯이" — 이동하기를 미끄러지기로
    if (/미끄러지듯|스르르|스르륵|부드럽게|순간\s*이동(?:하는|이|은)?\s*(?:거|게|건)?\s*(?:이상|싫|별로)/.test(t)) {
        let n0 = 0;
        out.scripts.forEach(sc => walk(sc.body, x => { if (x.t === 'act' && toGlide(x)) n0++; }));
        if (n0) return finish('glide', ['‘이동하기’를 ‘1초 동안 이동하기’(미끄러지기)로 바꿨어요.']);
    }

    // 숫자 바꾸기 — "기다리는 거 2초로 바꿔", "20으로 해"
    // "3초만 가고 멈추게" — 같은 동작 이야기에 시간만 바꿈
    const secOnly = t.match(/(?<n>\d+(?:\.\d+)?)\s*초\s*만/) || (rctx.robot && famOk ? t.match(/(?<n>\d+(?:\.\d+)?)\s*초(?:\s*(?:동안|간))?(?:\s|$)/) : null);
    // "10초만 하고 꺼져" — 무한 반복을 그 시간만큼의 N번 반복으로 + 끝나면 끄기
    if (secOnly && /만/.test(t) && last.body.some(x => x.t === 'forever')) {
        const fi = last.body.findIndex(x => x.t === 'forever');
        const per = last.body[fi].body.reduce((a, x) => a + (x.opcode === 'control_wait' ? Number(x.inputs.DURATION.value) : 0), 0) || 1;
        const v = Number(secOnly.groups.n);
        const times = Math.max(1, Math.round(v / per));
        last.body[fi] = {t: 'repeat', times, body: last.body[fi].body};
        const offs = [];
        if (prevOps.some(op => /cubroidmaster/.test(op))) offs.push(act('cubroidmaster_cubroidColorOffControl', {sig: 'light:off', label: '불빛 끄기'}));
        if (prevOps.some(op => /_dcMotorControl$/.test(op))) offs.push(act('cubroiddcmotor01_dcMotorStop', {sig: 'm1:off', label: 'DC 모터 1번을 끄기'}), act('cubroiddcmotor02_dcMotorStop', {sig: 'm2:off', label: 'DC 모터 2번을 끄기'}));
        last.body.splice(fi + 1, 0, ...offs);
        return finish('repeat', [`한 번 도는 데 ${fmt(per)}초라서 ${v}초 동안 = ${times}번 반복으로 바꿨어요.${offs.length ? ' 끝나면 끄게 했어요.' : ''}`]);
    }
    if (secOnly && (sameKind || !probeOps.length || (rctx.robot && famOk))) {
        const v = Number(secOnly.groups.n);
        const waits = numberItems(out).filter(it => it.cat === 'wait');
        if (waits.length) {
            const chs = waits.map(it => setNumber(it.node, 'DURATION', v));
            return finish('setnum', chs.map(ch => `‘기다리기’ ${ch.old}초 → ${ch.nv}초`));
        }
    }
    // "20까지로 바꿔" — 세기 끝 숫자
    const upTo = /(?<n>\d+)\s*까지(?:로)?(?:\s*(?:바꿔|늘려|줄여|해|세)[가-힣]*)?$/.exec(t);
    let counter = null;
    out.scripts.forEach(sc => walk(sc.body, x => { if (x.t === 'repeat' && x.count) counter = x; }));
    if (upTo && counter) {
        const b = Number(upTo.groups.n);
        const o = counter.times;
        counter.times = Math.max(1, Math.min(100, Math.floor(Math.abs(b - counter.count.a) / Math.abs(counter.count.step)) + 1));
        return finish('setnum', [`반복 횟수 ${o} → ${counter.times} (${counter.count.a}부터 ${b}까지)`]);
    }
    const sn = t.match(RE_SETNUM) || t.match(RE_SETNUM2) || (probeOps.length === 0 ? t.match(RE_SETNUM3) : null);
    // 타이머 "5초로 줄여줘" — 세기 시작 숫자와 반복 횟수
    if (sn && counter && /초/.test(sn.groups.u || '') && !numberItems(out).some(it => it.cat === 'wait')) {
        const v = Math.round(Number(sn.groups.n));
        const o = counter.times;
        counter.times = v;
        out.scripts.forEach(sc => walk(sc.body, x => { if (x.countStart) { x.inputs.VALUE = txt(String(v)); x.sig = `숫자=${v}`; x.label = `숫자를 ${euro(String(v))} 정하기`; } }));
        counter.count.a = v;
        return finish('setnum', [`타이머 ${o}초 → ${v}초 (시작 숫자와 반복 횟수를 바꿨어요)`]);
    }
    if (sn) {
        const v = Number(sn.groups.n);
        const cat = catOfWord(sn.groups.tw || t, sn.groups.u);
        let items = numberItems(out);
        if (cat) items = items.filter(it => it.cat === cat);
        const cats = Array.from(new Set(items.map(it => it.cat)));
        // "0.3초로"(기다리기 없음) — 반복 안에 기다리기를 넣는다
        if (!items.length && cat === 'wait' && insertWaits(out, v)) return finish('wait', [`반복 안에 ‘${fmt(v)}초 기다리기’를 넣었어요.`]);
        // "100%로"(로봇) — 속도 블록
        if (!items.length && rctx.robot && /%|퍼센트/.test(sn.groups.u || '') && prevOps.some(op => /_dcMotorControl$/.test(op))) {
            const sp = Math.max(0, Math.min(100, Math.round(v)));
            const mk = id => act(`${id}_dcMotorSetSpeed`, {inputs: {SPEED: num(sp)}, sig: `m${/01$/.test(id) ? 1 : 2}:speed${sp}`, label: `DC 모터 ${/01$/.test(id) ? 1 : 2}번 속도를 ${sp}%로 정하기`});
            last.body = last.body.filter(x => !/_dcMotorSetSpeed$/.test(x.opcode || ''));
            last.body.unshift(mk('cubroiddcmotor01'), mk('cubroiddcmotor02'));
            return finish('setnum', [`로봇 속도를 ${sp}%로 정했어요.`]);
        }
        if (!items.length) return {kind: 'setnum', ir: null, message: '바꿀 숫자를 못 찾았어요. 블록의 숫자를 직접 눌러 바꿔도 돼요.'};
        if (!cat && cats.length > 1) {
            return {kind: 'setnum', ir: null, message: '어느 숫자를 바꿀까요? 예: ‘움직이는 거 20으로 바꿔’, ‘기다리는 거 2초로 바꿔’. 블록의 숫자를 직접 눌러 바꿔도 돼요.'};
        }
        const changes = [];
        items.forEach(it => {
            let ch;
            if (it.cat === 'times') ch = it.set(v);
            else if (it.cat === 'jump') ch = jumpSet(out, it.node, v);
            else {
                const neg = Number(it.node.inputs[it.input].value) < 0 && it.cat === 'move';
                ch = setNumber(it.node, it.input, neg ? -Math.abs(v) : v);
            }
            if (ch) changes.push(`${it.cat === 'jump' ? '점프 높이' : it.cat === 'times' ? '반복 횟수' : `‘${it.node.label}’의 숫자`} ${ch.old} → ${ch.nv}`);
            if (it.node && it.node.ask) delete it.node.ask;
        });
        return finish('setnum', changes);
    }

    // 할 말 바꾸기 — "말을 반가워로 바꿔", "대신 잘 가라고 말해"
    const says = [];
    out.scripts.forEach(sc => walk(sc.body, n => { if (n.t === 'act' && n.inputs && n.inputs.MESSAGE) says.push(n); }));
    // "2초동안만 말하게" — 말하기에 시간을 붙인다
    const secSay = t.match(new RegExp(`(?<n>${RE_SEC})\\s*초\\s*(?:동안|간)?\\s*만?\\s*(?:말|생각|보이)`));
    if (says.length && secSay) {
        const sec = toNum(secSay.groups.n);
        const x = says[says.length - 1];
        if (x.opcode === 'looks_say' || x.opcode === 'looks_think') {
            const think = x.opcode === 'looks_think';
            x.opcode = think ? 'looks_thinkforsecs' : 'looks_sayforsecs';
            x.inputs = Object.assign({}, x.inputs, {SECS: num(sec)});
            x.sig = `${think ? 'thinkfor' : 'sayfor'}(${x.inputs.MESSAGE.value},${fmt(sec)})`;
            x.label = `‘${x.inputs.MESSAGE.value}’을(를) ${fmt(sec)}초 동안 ${think ? '생각하기' : '말하기'}`;
            return finish('say', [`말하는 시간을 ${fmt(sec)}초로 정했어요.`]);
        }
        const ch = setNumber(x, 'SECS', sec);
        return finish('say', [`말하는 시간 ${ch.old}초 → ${ch.nv}초`]);
    }
    if (says.length) {
        for (const re of RE_SAYSWAP) {
            const m = t.match(re);
            if (m) {
                const ch = setText(says[says.length - 1], m.groups.m.trim());
                delete says[says.length - 1].ask;
                return finish('say', [`할 말 ‘${ch.old}’ → ‘${ch.nv}’`]);
            }
        }
    }

    // "파랑으로 바꿔" — 같은 종류 블록 하나를 바꿔 끼운다(불빛 색, 모양 …)
    if (/말고|바꿔|바꾸|대신|으로\s*해|로\s*해/.test(t)) {
        const pn = [];
        probe0.scripts.forEach(sc => walk(sc.body, x => { if (x.t === 'act') pn.push(x); }));
        if (pn.length === 1) {
            let target = null;
            out.scripts.forEach(sc => walk(sc.body, x => { if (x.t === 'act' && x.opcode === pn[0].opcode) target = x; }));
            if (target && target.sig !== pn[0].sig) {
                const old = target.label;
                target.inputs = clone(pn[0].inputs || {});
                target.fields = clone(pn[0].fields || {});
                target.sig = pn[0].sig;
                target.label = pn[0].label;
                return finish('swap', [`‘${old}’ → ‘${target.label}’`]);
            }
        }
    }

    // 빠르게·느리게·크게·작게 — 숫자를 몇 배로
    const probe = translate(text, rctx);
    const scale = /너무\s*(?:빠르|빨리|빨라|빨랐|정신\s*없)/.test(t) ? SCALE[1] : (/너무\s*(?:느리|느려|천천|답답)/.test(t) ? SCALE[0] : SCALE.find(sc => sc.re.test(t)));
    // "원이 너무 작아 더 크게" — 번역이 '크기'로 잡혀도 방금 것에 크기 블록이 없으면 방금 것을 키우는 말이다
    const sizeWord = probeOps.length && probeOps.every(op => /^looks_(?:change|set)sizeb?y?/.test(op) || op === 'looks_setsizeto') && !prevOps.some(op => /size/.test(op));
    if (scale && (sameKind || sizeWord || !probeOps.length)) {
        // 마우스 따라가기를 천천히 → 미끄러져 따라가기
        if (scale.what === '느리게') {
            let g0 = 0;
            out.scripts.forEach(sc => walk(sc.body, x => { if (x.opcode === 'motion_goto' && x.inputs.TO.value === '_mouse_' && toGlide(x)) g0++; }));
            if (g0) return finish('scale', ['마우스로 순간이동하던 것을 ‘1초 동안 마우스 포인터로 이동하기’로 바꿨어요.'], ['천천히 따라가게 하려면 미끄러지기 시간을 늘려요.']);
        }
        // 원의 크기는 한 번에 움직이는 거리로 정해진다(도는 각도는 그대로)
        const onlyMove = (/원/.test(t) || prevOps.indexOf('pen_penDown') >= 0) && prevOps.indexOf('motion_turnright') >= 0 && prevOps.indexOf('motion_movesteps') >= 0;
        // "노란불 더 짧게" — 그 불 다음의 기다리기만
        let lightWait = null;
        const lw = /(빨간|빨강|초록|녹색|노란|노랑|파란|파랑)\s*불/.exec(t);
        if (lw) {
            const hex = {빨간: '#ff0000', 빨강: '#ff0000', 초록: '#00ff00', 녹색: '#00ff00', 노란: '#ffff00', 노랑: '#ffff00', 파란: '#0000ff', 파랑: '#0000ff'}[lw[1]];
            const seek = list => list.forEach((x, i) => {
                if (lightWait) return;
                if (x.sig === `light:${hex}`) lightWait = list.slice(i + 1).find(y => y.opcode === 'control_wait') || null;
                if (x.body) seek(x.body);
                if (x.body2) seek(x.body2);
            });
            out.scripts.forEach(sc => seek(sc.body));
        }
        // "회전 짧게" — 제자리 돌기(두 모터 같은 방향) 뒤의 기다리기만
        const spinOnly = rctx.robot && /회전|도는|돌기|꺾|돌\s*때|돌때|많이\s*돌/.test(t);
        if (spinOnly) {
            const mark = list => list.forEach((x, i) => {
                if (x.body) mark(x.body);
                if (x.body2) mark(x.body2);
                const a = list[i - 2];
                const b = list[i - 1];
                if (x.opcode === 'control_wait' && a && b && /_dcMotorControl$/.test(a.opcode || '') && /_dcMotorControl$/.test(b.opcode || '') && a.fields.INDEX.value === b.fields.INDEX.value) x.spin = true;
            });
            out.scripts.forEach(sc => mark(sc.body));
        }
        if (rctx.robot && (scale.what === '빠르게' || scale.what === '느리게' || /속도/.test(t)) && !spinOnly && opsOf(prev).some(op => /dcMotor/.test(op))) {
            const sp = scale.what === '빠르게' ? 100 : 50;
            const mk = id => act(`${id}_dcMotorSetSpeed`, {inputs: {SPEED: num(sp)}, sig: `m${/01$/.test(id) ? 1 : 2}:speed${sp}`,
                label: `DC 모터 ${/01$/.test(id) ? 1 : 2}번 속도를 ${sp}%로 정하기`});
            const motorScripts = out.scripts.filter(sc => { let m = false; walk(sc.body, x => { if (/_dcMotorControl$/.test(x.opcode || '')) m = true; }); return m; });
            (motorScripts.length ? motorScripts : [last]).forEach(sc => {
                sc.body = sc.body.filter(x => !/_dcMotorSetSpeed$/.test(x.opcode || ''));
                sc.body.unshift(mk('cubroiddcmotor01'), mk('cubroiddcmotor02'));
            });
            return finish('scale', [`로봇 속도를 ${sp}%로 정했어요(모터를 쓰는 스크립트마다 맨 앞에 속도 블록을 넣었어요).`],
                ['로봇이 빠르기는 ‘속도’ 블록으로, 가는 거리는 ‘기다리기’ 시간으로 정해져요. 30% 아래로 내리면 바퀴가 안 돌 수 있어요.']);
        }
        const changes = [];
        numberItems(out).forEach(it => {
            if (it.cat === 'times') return;
            if (onlyMove && it.cat !== 'move') return;
            if (it.cat === 'wait') {
                // 로봇은 "조금만/더 많이" = 모터를 켜 두는 시간
                const f = (rctx.robot && scale.wait === 1) ? scale.amount : scale.wait;
                if (f === 1) return;
                if (lightWait && it.node !== lightWait) return;
                if (spinOnly && !it.node.spin) return;
                const old = Number(it.node.inputs[it.input].value);
                const ch = setNumber(it.node, it.input, Math.max(0.05, Math.round(old * f * 100) / 100));
                changes.push(`‘기다리기’ ${ch.old}초 → ${ch.nv}초`);
                return;
            }
            if (it.cat === 'jump') {
                const h = Number(it.node.inputs.DY.value);
                const ch = jumpSet(out, it.node, Math.max(1, Math.round(h * scale.amount)));
                if (ch) changes.push(`점프 높이 ${ch.old} → ${ch.nv}`);
                return;
            }
            const old = Number(it.node.inputs[it.input].value);
            const nv = Math.round(old * scale.amount) || Math.sign(old);
            const ch = setNumber(it.node, it.input, nv);
            changes.push(`‘${it.label}’ ${ch.old} → ${ch.nv}`);
            if (it.node.ask) delete it.node.ask;
        });
        if (!changes.length && scale.what === '느리게' && insertWaits(out, 0.5)) {
            return finish('scale', ['너무 빨라서 안 보였던 곳에 ‘0.5초 기다리기’를 넣었어요.'], ['눈에 보이게 하려면 동작 사이에 기다리기가 필요해요.']);
        }
        if (!changes.length && /소리|음량|볼륨/.test(t) && (scale.what === '더 작게' || scale.what === '더 크게')) {
            const vol = scale.what === '더 작게' ? 30 : 100;
            let done = false;
            const put = list => list.forEach((x, i) => {
                if (done) return;
                if (/^sound_play/.test(x.opcode || '')) {
                    list.splice(i, 0, act('sound_setvolumeto', {inputs: {VOLUME: num(vol)}, sig: `vol=${vol}`, label: `음량을 ${vol}%로 정하기`}));
                    done = true;
                } else if (x.body) put(x.body);
            });
            out.scripts.forEach(sc => put(sc.body));
            if (done) return finish('scale', [`소리 앞에 ‘음량을 ${vol}%로 정하기’를 넣었어요.`]);
        }
        if (!changes.length) return {kind: 'scale', ir: null, message: '바꿀 숫자가 없어요.'};
        const lesson = scale.what === '빠르게' || scale.what === '느리게' ?
            `‘${scale.what}’는 한 번에 움직이는 수와 기다리는 시간으로 정해져요. 바뀐 숫자를 블록에서 확인해 보세요.` : null;
        return finish('scale', changes, lesson ? [lesson] : []);
    }

    // "~고 나서 …" — 앞말은 방금 한 것을 가리키고, 뒷말이 붙일 것
    let hasCounter = false;
    walk(last.body, x => { if (x.t === 'repeat' && x.count) hasCounter = true; });
    const thenM = RE_THEN.exec(String(text).trim()) || (hasCounter ? /^(.*?)\d+\s*(?:이|가)?\s*되면\s+(.+)$/.exec(String(text).trim()) : null);
    const redC = /(?<c>빨간|빨강|초록|녹색|파란|파랑|노란|노랑)\s*불(?:빛)?\s*(?:일\s*때|이면|일때|켜졌을\s*때|켜질\s*때)/.exec(t);
    const onC = !redC && /(?:불(?:빛)?(?:이)?\s*)?켜질\s*때|켜졌을\s*때/.test(t);
    const COLOR_OF = {빨간: '#ff0000', 빨강: '#ff0000', 초록: '#00ff00', 녹색: '#00ff00', 파란: '#0000ff', 파랑: '#0000ff', 노란: '#ffff00', 노랑: '#ffff00'};
    const stripDo = x => String(x).replace(/([가-힣]{2,}|[가-힣]*[로에게])도(?=\s|$)/g, '$1').replace(/([A-Za-z0-9])\s+도(?=\s|$)/g, '$1');
    const tailText = thenM ? thenM[2] : text;
    const probeT = thenM ? translate(stripDo(tailText), rctx) : translate(stripDo(text), rctx);
    const addTo = (list, add0) => {
        let add = add0;
        const notes = probeT.notes.filter(x => x !== NOTE.hat);
        const note = x => { if (notes.indexOf(x) < 0) notes.push(x); };
        if (/(?:할|갈|움직일|이동할|갔을|움직였을)\s*때/.test(t) && add.length > 1 && add.some(n => /^sound_/.test(n.opcode || '')) && prevOps.some(op => /^motion_(?:goto|glide|move)/.test(op))) {
            add = add.filter(n => !(n.opcode === 'motion_movesteps' && Number(n.inputs.STEPS.value) === 10));
        }
        if (add[0] && add[0].t === 'forever' && list.length && list[list.length - 1].opcode === 'motion_movesteps') {
            const bare = list[list.length - 1];
            const inner = add[0].body.find(n => n.opcode === 'motion_movesteps');
            if (inner) { inner.inputs.STEPS = num(Number(bare.inputs.STEPS.value)); inner.sig = bare.sig; inner.label = bare.label; list.pop(); }
        }
        const tailOp = list.length ? list[list.length - 1].opcode : '';
        const headOp = add[0] ? add[0].opcode : '';
        if ((tailOp === 'looks_hide' && headOp === 'looks_show') || (tailOp === 'looks_show' && headOp === 'looks_hide')) {
            list.push(waitS(1));
            note('사라졌다 나오는 게 보이게 사이에 ‘1초 기다리기’를 넣었어요.');
        }
        list.push(...add);
        autoLoop(last, note);
        last.body = flattenLoops(last.body, false);
        tidyStructure(last.body, note);
        fixCaps(last.body, note);
        const li = out.scripts.length - 1;
        probeT.unparsed.forEach(u => out.unparsed.push(Object.assign({}, u, {script: li})));
        echo.segments = thenM ? [] : probeT.segments;
        const names = add.map(n => (n.t === 'act' ? n.label : n.t === 'repeat' ? `${n.times}번 반복하기` : n.t === 'forever' ? '무한 반복하기' : '만약 ~라면'));
        return finish('append', names.map(x => `‘${x}’ 블록을 이어 붙였어요.`), notes);
    };
    const whenM = /(?:할|될|바뀔|갈|움직일|돌|멈출|도망갈|켜질|말할|점프할|닿을)\s*때(?:마다)?/.test(t);
    const afterM = new RegExp(`^(?:${RE_SEC})\\s*초\\s*(?:있다가|후에|뒤에|지나서|쉬었다가|기다렸다가)`).test(t);
    // "멈추면 삐 소리도 나게" — 모터를 끄는 그 자리(만약 안) 뒤에 넣는다
    const stopM = /^(?:로봇이?\s*)?(?:멈추면|멈출\s*때(?:마다)?|멈췄을\s*때|멈추고\s*나서|멈추고|멈춘\s*(?:다음|뒤|후)(?:에)?)\s+(.+)$/.exec(String(text).trim());
    if (stopM) {
        let spot = null;
        const seek = list => list.forEach(x => {
            if (spot) return;
            if (x.body && x.body.some(y => /_dcMotorStop$/.test(y.opcode || ''))) spot = x.body;
            if (!spot && x.body) seek(x.body);
            if (!spot && x.body2) seek(x.body2);
        });
        seek(last.body);
        const pr = translate(stripDo(stopM[1]), rctx);
        const add = pr.scripts.length === 1 && pr.scripts[0].implicit ? pr.scripts[0].body.slice() : [];
        while (add.length && /_dcMotorStop$/.test(add[0].opcode || '')) add.shift();
        if (!spot && add.length) {
            const seek2 = list => list.forEach(x => {
                if (spot) return;
                if ((x.t === 'if' || x.t === 'ifelse') && x.cond && x.cond.sig === 'obstacle') { x.body.length = 0; x.body.push(...stopBoth()); spot = x.body; }
                if (!spot && x.body) seek2(x.body);
            });
            seek2(last.body);
        }
        if (spot && add.length) {
            spot.push(...add);
            const li = out.scripts.length - 1;
            pr.unparsed.forEach(u => out.unparsed.push(Object.assign({}, u, {script: li})));
            return finish('append', add.map(n => `‘${n.label || '블록'}’을 멈추는 블록 바로 뒤에 넣었어요.`), pr.notes.filter(x => x !== NOTE.hat));
        }
    }
    if (/멈추지\s*말고/.test(text) && probeT.scripts.length === 1 && probeT.scripts[0].implicit && probeT.scripts[0].body.length) {
        const tgt = appendPoint(last, null);
        for (let i = tgt.length - 1; i >= 0; i--) if (/_dcMotorStop$/.test(tgt[i].opcode || '') || tgt[i].opcode === 'control_stop') tgt.splice(i, 1);
        return addTo(tgt, probeT.scripts[0].body);
    }
    if ((thenM || redC || onC || whenM || afterM || /까지$/.test(t)) && probeT.scripts.length === 1 && probeT.scripts[0].implicit && probeT.scripts[0].body.length && !hasCond(probeT.scripts[0].body)) {
        // "점프할 때 소리도" — 앞의 '점프'는 이미 있는 것을 가리킨다 → 새 것만
        const prevJump = prevOps.indexOf('motion_changeyby') >= 0;
        const prevSigs = new Set();
        walk(last.body, x => { if (x.t === 'act') prevSigs.add(x.sig); });
        const isDup = x => x.t === 'act' && ((x.part && prevJump) || (prevSigs.has(x.sig) && x.opcode !== 'control_wait' && !/_dcMotor/.test(x.opcode) && !/Color|Img|say|think|play/.test(x.opcode)));
        const raw = probeT.scripts[0].body;
        const dup = raw.some(isDup);
        const add = (whenM || thenM) ? raw.filter(x => !isDup(x) && !(dup && x.t === 'act' && x.opcode === 'control_wait')) : raw;
        if (!add.length) return {kind: 'append', ir: null, message: '그건 이미 들어 있어요.'};
        // 불이 '켜질 때 / 빨간불일 때' — 그 불 바로 뒤에 넣는다(차례대로 켜는 신호등 같은 경우)
        const hexT = redC ? COLOR_OF[redC.groups.c] : null;
        if (hexT || onC) {
            let spot = null;
            const seek = list => list.forEach((x, i) => {
                if (spot) return;
                if (x.opcode === 'cubroidmaster_cubroidMasterColorControl' && (!hexT || x.sig === `light:${hexT}`)) spot = {list, i};
                if (x.body) seek(x.body);
                if (x.body2) seek(x.body2);
            });
            seek(last.body);
            if (spot && !(hexT && appendPoint(last, hexT) !== last.body && spot.list === appendPoint(last, hexT))) {
                spot.list.splice(spot.i + 1, 0, ...add);
                const li = out.scripts.length - 1;
                probeT.unparsed.forEach(u => out.unparsed.push(Object.assign({}, u, {script: li})));
                return finish('append', add.map(n => `‘${n.label || '블록'}’을 그 불을 켠 바로 뒤에 넣었어요.`), probeT.notes.filter(x => x !== NOTE.hat));
            }
        }
        return addTo(appendPoint(last, hexT), add);
    }
    // 이미 있는 걸 다시 말함("120도씩 돌아야지", "왼쪽 오른쪽만 보게") — 덮어쓰지 말고 알려 준다
    if (!/더|또|한\s*번\s*더|다시|추가|새로|하나\s*더|두\s*번|세\s*번|말고|대신|아니라|아니고|바꿔|바꾸|되돌|취소|반대/.test(t) && probeT.scripts.length === 1 && probeT.scripts[0].implicit && probeT.scripts[0].body.length) {
        const prevSigs = new Set();
        let hasAns = false;
        out.scripts.forEach(sc => walk(sc.body, x => { if (x.t === 'act') prevSigs.add(x.sig); if (x.opcode === 'sensing_answer') hasAns = true; }));
        const pSigs = [];
        let pAns = false;
        walk(probeT.scripts[0].body, x => { if (x.opcode === 'sensing_answer') pAns = true; });
        const stm = list => list.forEach(x => { if (x.t === 'act' && x.opcode !== 'control_wait') pSigs.push(x.sig); if (x.body) stm(x.body); if (x.body2) stm(x.body2); });
        stm(probeT.scripts[0].body);
        // 조건도 같아야 "이미 있다" — 자동차(모터 끄기 있음)에 "장애물 있으면 멈춰"를 이미 있다고 답하던 실측(2026-09-24 대표 데브 확인)
        const pConds = [];
        walk(probeT.scripts[0].body, x => { if (x.cond) walkNode(x.cond, y => { if (y.t === 'act' && y.sig) pConds.push(y.sig); }); });
        if (pConds.some(sg => !prevSigs.has(sg))) pSigs.length = 0;
        if ((pSigs.length && pSigs.every(sg => prevSigs.has(sg))) || (pAns && hasAns && opsOf(probeT).every(op => /^(?:looks_say|looks_think|sensing_answer|operator_join)/.test(op)))) {
            return {kind: 'explain', ir: null, message: '그건 이미 들어 있어요. 깃발을 눌러 실행해 보세요. 다르게 바꾸고 싶으면 무엇을 어떻게 바꿀지 말해 주세요(예: ‘5번으로’, ‘왼쪽으로 바꿔’).'};
        }
    }
    // 시간으로 움직이는 로봇(자동차 춤, "앞으로 3초")에 "장애물 있으면 멈춰" — 센서가 모터를 정하는 반복으로 바꾼다.
    // 둘을 한 무한 반복에 합치면 3.6초 춤이 끝난 뒤에야 센서를 한 번 봐서 센서가 쓸모없다(2026-09-24 실측).
    {
        const pb0 = probeT.scripts.length === 1 && probeT.scripts[0].implicit ? probeT.scripts[0].body : null;
        const MOT = /_dcMotor/;
        const motorIn = list => { let m = false; walk(list, x => { if (MOT.test(x.opcode || '')) m = true; }); return m; };
        const condMotor = pb0 && pb0.length === 1 && pb0[0].t === 'forever' && pb0[0].body.some(n => (n.t === 'if' || n.t === 'ifelse') && motorIn(n.body || []));
        const timedMotor = last.body.length && last.body.every(n => n.t === 'act') && last.body.some(n => MOT.test(n.opcode || ''));
        if (condMotor && timedMotor && !/더|또|추가|하나\s*더|같이|함께|동시에/.test(t)) {
            last.body = pb0;
            return finish('restate', ['시간으로 움직이던 걸 센서를 보고 움직이게 바꿨어요(계속 반복하며 확인). 예전 동작으로 돌아가려면 ‘되돌려’라고 말해 보세요.']);
        }
    }
    // 새 조건("끝에 닿으면 뒤돌아서 와")을 계속 도는 스크립트에 더하기 — 같은 무한 반복 안으로
    const lastForever = last.body.find(n => n.t === 'forever');
    const condProbe = probeT.scripts.length === 1 && probeT.scripts[0].implicit && hasCond(probeT.scripts[0].body);
    if (condProbe && (famOk || lastForever)) {
        const pb = probeT.scripts[0].body;
        const inner = pb.length === 1 && pb[0].t === 'forever' ? pb[0].body : pb;
        // 계속 도는 게 없으면 방금 것까지 함께 무한 반복으로("왼쪽으로 가게" + "벽 닿으면 멈추게" = 가다가 벽에서 멈춤)
        const opp = (a, b) => (a && b && ((/^bright</.test(a) && /^bright>/.test(b)) || (/^bright>/.test(a) && /^bright</.test(b)) ||
            (a === 'obstacle' && b === 'not:obstacle') || (a === 'not:obstacle' && b === 'obstacle') || (a === 'button' && b === 'not:button')));
        const newIf = inner.length === 1 && inner[0].t === 'if' ? inner[0] : null;
        const oldIf = lastForever ? lastForever.body.find(x => x.t === 'if' && newIf && opp(x.cond.sig, newIf.cond.sig)) : null;
        if (oldIf) {
            oldIf.t = 'ifelse';
            oldIf.body2 = newIf.body;
            const li0 = out.scripts.length - 1;
            probeT.unparsed.forEach(u => out.unparsed.push(Object.assign({}, u, {script: li0})));
            return finish('append', ['반대 경우라서 ‘만약 ~라면 … 아니면 …’으로 합쳤어요.'], probeT.notes.filter(x => x !== NOTE.hat));
        }
        if (!lastForever) {
            const SETUP = /^(?:data_setvariableto|looks_set|motion_goto|motion_set|motion_pointindirection|pen_clear|pen_set|sound_setvolumeto|looks_switch|looks_show|looks_hide|cubroiddcmotor0[12]_dcMotorSetSpeed)/;
            let k0 = 0;
            while (k0 < last.body.length && last.body[k0].t === 'act' && SETUP.test(last.body[k0].opcode)) k0++;
            last.body = last.body.slice(0, k0).concat([{t: 'forever', body: last.body.slice(k0).filter(x => !x.cap).concat(inner)}]);
        } else lastForever.body.push(...inner);
        const li = out.scripts.length - 1;
        probeT.unparsed.forEach(u => out.unparsed.push(Object.assign({}, u, {script: li})));
        echo.segments = probeT.segments;
        return finish('append', ['새 조건을 방금 만든 것과 함께 ‘무한 반복하기’ 안에 넣었어요.'], probeT.notes.filter(x => x !== NOTE.hat));
    }
    // 이어 붙이기 — "그리고 소리도 내줘", "점프도 해"
    const connective = /^(?:그리고|또|또한|그\s*다음에?|그다음에?|다음에|그러고\s*나서|그리고\s*나서|이어서|추가로|거기에|그\s*뒤에)(?:\s|$)/.test(t) || /(?:^|\s)추가(?:로|해|해줘|하기)?(?:\s|$)/.test(t);
    const also = /(?:^|\s)([가-힣A-Za-z0-9]+)\s?도(?:\s|$)/.exec(t);
    const alsoOk = also && !/^(?:속|각|온|정|방|몇|한|반|것|거)$/.test(also[1]);
    if (/같이|동시에|함께|면서/.test(t) && probeT.scripts.length === 1 && probeT.scripts[0].implicit && probeT.scripts[0].body.length &&
        (connective || alsoOk || /면서/.test(t)) && !(hasCond(last.body) && !/동시에|따로/.test(t))) {
        const body = probeT.scripts[0].body;
        const lastSigs0 = new Set();
        walk(last.body, x => { if (x.t === 'act') lastSigs0.add(x.sig); });
        const fresh0 = body.filter(x => !(x.t === 'act' && lastSigs0.has(x.sig) && x.opcode !== 'control_wait'));
        if (fresh0.length) {
            const loopy = last.body.some(x => x.t === 'forever');
            const nb = loopy && !fresh0.some(x => x.t === 'forever') ? [{t: 'forever', body: fresh0}] : fresh0;
            out.scripts.push({hat: clone(last.hat), body: nb, implicit: false});
            return finish('parallel', ['‘같이·동시에’는 스크립트를 하나 더 만들어요. 같은 모자 블록으로 시작해서 두 스크립트가 동시에 실행돼요.'], probeT.notes.filter(x => x !== NOTE.hat));
        }
    }
    if ((connective || alsoOk || /(?:^|\s)(?:것|거)도(?:\s|$)/.test(t)) && probeT.scripts.length === 1 && probeT.scripts[0].implicit) {
        let add = probeT.scripts[0].body;
        // "점프할때 야옹 소리도" — 이미 있는 동작은 가리키는 말일 뿐이다 → 새 동작만 붙인다
        const lastOps = [];
        walk(last.body, x => { if (x.t === 'act') lastOps.push(x.opcode); });
        const lastSigs = new Set();
        walk(last.body, x => { if (x.t === 'act') lastSigs.add(x.sig); });
        const allIn = x => { const ss = []; walk([x], y => { if (y.t === 'act' && y.opcode !== 'control_wait') ss.push(y.sig); }); return ss.length && ss.every(q => lastSigs.has(q)); };
        const isDup2 = x => (x.t === 'act' ? lastSigs.has(x.sig) && x.opcode !== 'control_wait' && !/_dcMotor/.test(x.opcode) : allIn(x));
        const dup2 = add.some(isDup2);
        const fresh = add.filter(x => !isDup2(x) && !(dup2 && x.t === 'act' && x.opcode === 'control_wait'));
        if (fresh.length && fresh.length < add.length) add = fresh;
        if (!fresh.length) return {kind: 'append', ir: null, message: '그건 이미 들어 있어요.'};
        const ap = appendPoint(last, null);
        while (add.length && add[0].t === 'act' && /_dcMotorStop$/.test(add[0].opcode) && ap.some(x => x.sig === add[0].sig)) add.shift();
        const lastN = ap[ap.length - 1];
        if (lastN && lastN.opcode === 'looks_hide' && add[0] && add[0].opcode === 'looks_show') add.unshift(act('control_wait', {inputs: {DURATION: num(1, 'math_positive_number')}, sig: 'wait1', label: '1초 기다리기'}));
        if (add[0] && add[0].t === 'forever' && ap === last.body && last.body.length && last.body[last.body.length - 1].opcode === 'motion_movesteps') {
            const bare = last.body[last.body.length - 1];
            const inner = add[0].body.find(n => n.opcode === 'motion_movesteps');
            if (inner) { inner.inputs.STEPS = num(Number(bare.inputs.STEPS.value)); inner.sig = bare.sig; inner.label = bare.label; last.body.pop(); }
        }
        ap.push(...add);
        const notes = probeT.notes.filter(x => x !== NOTE.hat);
        const note = x => { if (notes.indexOf(x) < 0) notes.push(x); };
        autoLoop(last, note);
        last.body = flattenLoops(last.body, false);
        tidyStructure(last.body, note);
        fixCaps(last.body, note);
        hintAnimation(last.body, note);
        hintBlink(last.body, note);
        const li = out.scripts.length - 1;
        probeT.unparsed.forEach(u => out.unparsed.push(Object.assign({}, u, {script: li})));
        echo.segments = probeT.segments;
        const names = add.map(n => (n.t === 'act' ? n.label : n.t === 'repeat' ? `${n.times}번 반복하기` : n.t === 'forever' ? '무한 반복하기' : '만약 ~라면'));
        return finish('append', names.map(x => `‘${x}’ 블록을 방금 만든 스크립트 뒤에 이어 붙였어요.`), notes);
    }
    const SETTING0 = /^(?:sound_(?:set|change)volume|motion_setrotationstyle|pen_set|cubroiddcmotor0[12]_dcMotorSetSpeed)/;
    if (probe.scripts.length === 1 && probe.scripts[0].implicit && probe.scripts[0].body.length && probe.scripts[0].body.every(x => x.t === 'act' && SETTING0.test(x.opcode))) {
        const have = new Set();
        walk(last.body, x => { if (x.t === 'act') have.add(x.sig); });
        const neu = probe.scripts[0].body.filter(x => !have.has(x.sig));
        if (!neu.length) return {kind: 'append', ir: null, message: '그건 이미 들어 있어요.'};
        last.body.unshift(...neu);
        return finish('append', neu.map(x => `‘${x.label}’를 맨 앞에 넣었어요.`), probe.notes.filter(x => x !== NOTE.hat));
    }
    // 다시 말하기 — 같은 종류를 새로 말했으면 방금 것을 통째로 바꾼다(새 스크립트를 따로 만들면 둘이 같이 돌아 뒤섞인다)
    const pOps = opsOf(probe).filter(op => !NEUTRAL.test(op));
    const overlap = pOps.some(op => prevFams.indexOf(famOf(op)) >= 0);
    // 방금 것을 전부 다시 말하고 더 붙였으면(“가운데 가서 아무말이나 하게”) 역시 다시 말하기다
    const superset = prevOps.filter(op => !NEUTRAL.test(op)).every(op => pOps.indexOf(op) >= 0);
    const PROP = op => (/sizeb?y|setsizeto/.test(op) ? 'size' : /volume/.test(op) ? 'vol' : /effect/.test(op) ? 'fx' : op);
    const sameOp = pOps.some(op => prevOps.map(PROP).indexOf(PROP(op)) >= 0);
    if (!connective && !alsoOk && probe.scripts.length === 1 && probe.scripts[0].implicit && probe.scripts[0].body.length && overlap && sameOp && (famOk || superset) && !hasCond(probe.scripts[0].body)) {
        const old = last.body.length;
        last.body = probe.scripts[0].body;
        const li = out.scripts.length - 1;
        probe.unparsed.forEach(u => out.unparsed.push(Object.assign({}, u, {script: li})));
        echo.segments = probe.segments;
        return finish('restate', [`방금 만든 블록(${old}개)을 새로 말한 대로 바꿨어요. 언제 시작하는지(모자 블록)는 그대로예요.`], probe.notes.filter(x => x !== NOTE.hat));
    }
    // 설정 블록(음량·회전 방식·펜 색 …)만 새로 말했으면 맨 앞에 넣는다("소리 좀 줄여줘")
    const SETTING = /^(?:sound_(?:set|change)volume|motion_setrotationstyle|pen_set|looks_set(?:size|effect)|looks_change(?:size|effect)|cubroiddcmotor0[12]_dcMotorSetSpeed)/;
    if (probe.scripts.length === 1 && probe.scripts[0].implicit && probe.scripts[0].body.length && probe.scripts[0].body.every(x => x.t === 'act' && SETTING.test(x.opcode))) {
        last.body.unshift(...probe.scripts[0].body);
        return finish('append', probe.scripts[0].body.map(x => `‘${x.label}’를 맨 앞에 넣었어요.`), probe.notes.filter(x => x !== NOTE.hat));
    }
    const linear = !hasCond(last.body) && !last.body.some(x => x.t === 'forever');
    if (linear && probe.scripts.length === 1 && probe.scripts[0].implicit && probe.scripts[0].body.length && !hasCond(probe.scripts[0].body) &&
        opsOf(probe).some(op => !NEUTRAL.test(op) || /^sensing_askandwait$|^looks_/.test(op)) && last.body.length && prev.scripts.length === 1) {
        return addTo(last.body, probe.scripts[0].body);
    }
    // "멈추고 불 초록색" — 이미 있는 동작을 가리키고 새 것을 덧붙인 말
    const exact = pOps.filter(op => prevOps.indexOf(op) >= 0);
    const freshOps = opsOf(probe).filter(op => prevOps.indexOf(op) < 0 && !NEUTRAL.test(op));
    if (probe.scripts.length === 1 && probe.scripts[0].implicit && !hasCond(probe.scripts[0].body) && (exact.length || opsOf(probe).some(op => /_dcMotorStop$/.test(op) && prevOps.indexOf(op) >= 0)) && freshOps.length) {
        const add = probe.scripts[0].body.filter(x => !(x.t === 'act' && prevOps.indexOf(x.opcode) >= 0));
        if (add.some(x => x.t !== 'act' || !NEUTRAL.test(x.opcode))) return addTo(appendPoint(last, null), add);
    }
    // 못 알아들은 고쳐 말하기 — 빈 새 스크립트를 만들지 말고 되묻는다(질문이면 도움말이 맡는다)
    if (!probe.scripts.length && probe.unparsed.length && !answerQuestion(text)) {
        return {kind: 'explain', ir: null, fallback: true, message: '방금 만든 걸 어떻게 바꿀지 잘 모르겠어요. ‘더 빠르게’, ‘3번만’, ‘빨간색으로 바꿔’, ‘소리도 내줘’처럼 말해 보세요. 새로 만들려면 무엇을 할지 한 문장으로 말해 주세요.'};
    }
    return null;
};

// ─────────────────────────────── 질문에 답하기 ───────────────────────────────
// "사운드 블록도 사용 가능해?" — 질문은 블록으로 만들지 않고(주석도 남기지 않고) 할 수 있는 말을 알려 준다.
// (2026-09-23 대표 실측: 질문을 '모르는 말'로 처리해 작업 영역에 주석까지 남겼다)
const HELP = [
    // 23차
    {re: /강아지로|다른\s*(?:캐릭터|동물|스프라이트)(?:로|으로)\s*바꿔|스프라이트\s*(?:를|을)?\s*바꿔|캐릭터\s*(?:를|을)?\s*바꿔/, title: '스프라이트 바꾸기', text: '말로는 스프라이트를 못 바꿔요. 왼쪽 아래 ‘스프라이트 고르기’(고양이 얼굴 버튼)에서 강아지를 고르고, 새 스프라이트를 선택한 채로 말하면 그 스프라이트의 블록이 만들어져요.', ex: ['앞으로 10만큼 움직여', '스페이스 누르면 점프해']},
    {re: /탁구|핑퐁|pong/i, title: '탁구(퐁) 게임', text: '공은 ‘벽돌깨기 게임’ 틀로 만들 수 있어요(비스듬히 날아가며 벽에서 튕겨요). 막대는 스프라이트를 하나 더 추가해서 방향키(w·s, 위·아래)로 움직이게 하고, ‘막대에 닿으면 튕기기’를 넣으면 돼요.', ex: ['벽돌깨기 게임 만들어줘', '위 아래 화살표로 움직이게', '막대에 닿으면 방향 바꿔']},
    {re: /(?:로봇|차)\s*(?:두\s*대|둘|2\s*대|여러\s*대)|두\s*대\s*(?:같이|동시에)/, title: '로봇 두 대', text: '에디터는 로봇 한 대와 연결해요. 두 대를 같이 움직이려면 컴퓨터(또는 태블릿) 두 대에서 각각 연결하고 같은 프로그램을 넣어 동시에 깃발을 누르면 돼요.', ex: ['로봇 춤추게 해줘', '로봇 앞으로 2초 가']},
    {re: /복제/, title: '복제하기', text: '‘나 자신 복제하기’는 같은 스프라이트를 여러 개 만들어요(눈송이·총알·적). 복제본이 할 일은 ‘복제되었을 때’ 밑에 넣고, 다 쓰면 ‘이 복제본 삭제하기’로 지워요.', ex: ['복제본 5개 만들어서 랜덤 위치로', '위에서 눈처럼 떨어지게', '스페이스 누르면 총알 발사']},
    // 21차
    {re: /한\s*쪽\s*으로(?:만)?\s*(?:돌|가|움직|휘|쏠|치우)|휘어\s*가|제자리에서만\s*(?:돌|빙빙)|똑바로\s*(?:안|못)\s*가|빙빙\s*돌기만/, title: '로봇이 한쪽으로만 돌 때', text: '두 모터가 같은 방향으로 돌면 앞으로 가지 않고 제자리에서 돌아요. 앞으로 가려면 1번 모터는 오른쪽, 2번 모터는 왼쪽으로 돌려야 해요(바퀴가 마주 보고 달려 있어서). 블록이 맞는데도 한쪽으로 휘면 한쪽 바퀴에 뭔가 걸렸거나 모터 힘이 달라서예요 — 속도를 30% 이상으로 올리고 바퀴를 확인해 보세요.', ex: ['로봇 앞으로 2초 가', '모터 속도 50%로 해', '로봇 오른쪽으로 돌아']},
    // 20차
    {re: /핸드폰|휴대폰|스마트폰|폰으로/, title: '핸드폰으로 조종', text: '핸드폰 앱으로 조종하는 기능은 없어요. 태블릿·노트북의 크롬에서 에디터를 열고 방향키로 조종할 수 있어요.', ex: ['방향키로 로봇 움직이게', '스페이스 누르면 로봇 멈춰']},
    {re: /(?:진짜|정말)\s*(?:말하면|되|돼)|말하면\s*블록|블록(?:이)?\s*만들어져|이거\s*(?:뭐|어떻게\s*써)/, title: '말로 코딩', text: '네! 만들고 싶은 걸 한 문장으로 쓰면 블록을 만들어 줘요. 만든 뒤에 ‘더 빠르게’, ‘3번만’처럼 고쳐 말할 수도 있어요.', ex: ['스페이스 누르면 점프', '고양이 10만큼 움직여', '로봇 앞으로 2초']},
    // 19차
    {re: /마인\s*크래프트|마크\s*같은|3\s*[dD]|쓰리디|입체/, title: '3D는 어려워요', text: '스크래치 무대는 평면(2D)이라 마인크래프트 같은 3D 게임은 만들 수 없어요. 대신 방향키로 움직이고 블록(사각형)을 도장 찍는 2D 게임은 만들 수 있어요.', ex: ['방향키로 움직이게', '스페이스 누르면 도장 찍기', '미로 게임 만들어줘']},
    {re: /온라인|친구랑\s*같이|멀티|같이\s*하는\s*게임|인터넷/, title: '온라인 게임', text: '다른 컴퓨터의 친구와 온라인으로 같이 하는 게임은 말로 코딩으로 만들 수 없어요. 한 컴퓨터에서 둘이 하려면 한 명은 방향키, 한 명은 w·a·s·d 키로 움직이게 해 보세요.', ex: ['방향키로 움직이게', 'a누르면 왼쪽 d누르면 오른쪽 w는 위 s는 아래']},
    {re: /블루투스|연결이?\s*(?:안|잘\s*안)|연결\s*(?:어떻게|방법)|페어링/, title: '로봇 연결하기', text: '① 로봇 전원을 켜요 ② 블록 목록 위 주황색 연결 버튼을 눌러요 ③ 목록에서 내 로봇을 골라요. 안 되면 크롬(또는 엣지) 브라우저인지, 컴퓨터 블루투스가 켜져 있는지, 다른 탭이 로봇을 잡고 있지 않은지 확인해 보세요.', ex: ['로봇 앞으로 1초', 'LED에 하트']},
    {re: /(?:LED|엘이디|매트릭스)[^?]*(?:이름|글자|영어|문장)|(?:이름|글자)[^?]*(?:LED|엘이디|매트릭스)/, title: 'LED에 글자 쓰기', text: 'LED에는 영어 대문자를 한 글자씩 띄울 수 있어요(한글은 안 돼요). 이름을 쓰려면 글자를 차례로 보여 주세요.', ex: ['LED에 A 보여줘', 'LED에 H 다음에 I 보여줘']},
    {re: /왜\s*안\s*(?:움직|가|돼|되|나와|켜|돌)|안\s*움직여|움직이지\s*않/, title: '안 움직일 때', text: '① 초록 깃발(또는 말한 키)을 눌렀는지 ② 블록이 모자 블록(깃발을 클릭했을 때) 아래에 붙어 있는지 ③ 고양이가 무대 밖으로 나가지 않았는지 ④ 로봇이면 연결 버튼으로 연결했는지 확인해 보세요.', ex: ['깃발 누르면 10만큼 움직여', '위치를 처음으로']},
    // 18차
    {re: /(?:근접|거리)[^?]*(?:단위|cm|센티|몇\s*(?:cm|센티)|얼마나\s*멀리|몇이면|값이\s*몇|얼마면|어느\s*정도)|거리를?\s*재/, title: '근접 센서 거리', text: '근접 센서는 앞에 무언가 가까이 있는지(예/아니오)만 알려줘요. 거리를 cm로 재지는 못해요. 조건으로 써 보세요.', ex: ['장애물 있으면 멈춰', '앞에 뭐 있으면 삐 소리']},
    {re: /진짜\s*목소리|목소리로|음성으로|읽어\s*(?:줘|주게)|말소리/, title: '진짜 목소리로 말하기', text: '왼쪽 아래 ‘확장 추가’에서 ‘텍스트 음성 변환(TTS)’을 넣으면 ‘말하기’ 블록이 글을 소리 내어 읽어요. 말로 코딩은 지금은 말풍선을 만들어요. 녹음한 내 목소리는 ‘소리’ 탭의 녹음으로 넣을 수 있어요.', ex: ['안녕 이라고 2초 말해', '야옹 소리 내']},
    {re: /로봇도\s*(?:똑같이|같이|따라)|고양이(?:가)?\s*움직이는\s*대로/, title: '고양이와 로봇 같이 움직이기', text: '로봇은 무대 위 고양이 위치를 몰라서 그대로 따라 하지는 못해요. 대신 같은 키로 둘 다 움직이게 할 수 있어요.', ex: ['위쪽 화살표 누르면 고양이 위로 10 로봇 앞으로 0.3초', '방향키로 로봇 움직이게']},
    {re: /3\s*D|쓰리디|입체/, title: '3D는 어려워요', text: '스크래치 무대는 평면(2D)이라 마인크래프트 같은 3D 게임은 만들 수 없어요. 대신 방향키로 움직이는 2D 게임을 한 문장씩 만들어 보세요.', ex: ['방향키로 움직이게', '사과가 하늘에서 떨어지게', '마우스에 닿으면 점수 1 올려']},
    {re: /한\s*글자씩|타자\s*치듯|타이핑\s*효과/, title: '한 글자씩 말하기', text: '가능해요: ‘글자’ 변수를 1부터 1씩 올리면서 ‘(문장)의 (글자)번째 글자’를 이어 붙여 말하면 한 글자씩 나와요. 연산의 ‘~의 ~번째 글자’와 ‘결합하기’ 블록을 써요.', ex: ['안녕이라고 말해', '1부터 10까지 세기']},
    {re: /속도.*(?:최대|최고|몇)|(?:최대|최고)\s*속도/, title: '모터 속도', text: '모터 속도는 0부터 100%까지예요. 100%가 가장 빠르고, 30%보다 낮으면 바퀴가 안 돌 수 있어요.', ex: ['로봇 속도 100%로 해', '로봇 속도 50%로 앞으로 2초 가']},
    {re: /블록.*(?:지우|지워|없애|삭제)|(?:지우|지워|없애|삭제).*블록/, title: '블록 지우기', text: '방금 말로 만든 블록은 ‘만든 블록 지우기’ 버튼을 누르거나 ‘방금 거 취소해’라고 말하면 지워져요. 다른 블록은 왼쪽 블록 목록 쪽으로 끌어다 놓거나, 블록을 오른쪽 클릭해서 ‘블록 삭제하기’를 눌러요.', ex: ['방금 거 취소해', '스페이스 키를 누르면 점프해']},
    {re: /(?:불|빛|램프|조명).*(?:안\s*켜|안\s*꺼|계속\s*켜|계속\s*꺼)|안\s*켜져|안\s*꺼져/, title: '불이 안 켜지거나 안 꺼질 때', text: '① 로봇이 연결됐는지 ② 깃발을 눌렀는지 확인해요. ③ 밝기로 켜는 경우 기준(500)이 교실 밝기와 안 맞을 수 있어요. ‘밝기 값 계속 말해’로 지금 밝기를 재 보고, 그 숫자에 맞게 기준을 바꿔 보세요.', ex: ['밝기 값 계속 말해', '밝기가 300보다 작으면 흰 불 켜']},
    {re: /저장|불러오|불러와|다운로드|내\s*컴퓨터|파일로/, title: '저장하기', text: '왼쪽 위 ‘파일’ 메뉴 → ‘컴퓨터에 저장하기’를 누르면 .sb3 파일로 저장돼요. 다시 열 때는 ‘파일 → 컴퓨터에서 가져오기’. 이 에디터는 블록을 자동으로 저장해 두어서, 창을 닫았다 열면 이어서 할지 물어봐요.', ex: ['스페이스 키를 누르면 점프해', '로봇 앞으로 3초 가']},
    {re: /(?:센서|밝기|빛).*(?:몇까지|얼마까지|범위|최대|최소|몇\s*부터)|몇까지\s*나와/, title: '센서 값의 범위', text: '밝기(빛 센서) 값은 어두우면 작고 밝으면 커요. 대략 0부터 1000쯤까지 나오지만 교실 조명마다 달라서, ‘밝기 값 계속 말해’로 직접 재 보고 기준을 정하는 게 제일 정확해요. 근접 센서는 숫자가 아니라 ‘감지됐다/아니다’만 알려 줘요.', ex: ['밝기 값을 계속 말해', '밝기가 300보다 작으면 흰 불 켜']},
    {re: /(?:모터|바퀴|로봇).*(?:안\s*돌|안\s*움직|안\s*가|안\s*돼|멈춰\s*있|꿈쩍)/, title: '모터가 안 돌 때', text: '① 연결(주황) 버튼으로 로봇이 연결됐는지 ② 배터리가 충분한지 ③ 모터 선이 1번·2번 자리에 꽂혔는지 ④ 속도 블록이 30%보다 낮지 않은지 ⑤ 모터 켜기 뒤에 바로 끄기가 붙어 있지 않은지(기다리기가 있어야 움직여요) 확인해 보세요.', ex: ['로봇 속도 60%로 해', '로봇 앞으로 2초 가']},
    {re: /왜\s*안|안\s*돼|안돼|뭐야\s*안|작동\s*안|안\s*움직|안\s*가요|안\s*나와|안\s*돌|안\s*켜|않는데|움직이지도\s*않|움직이지\s*않|반응(?:이)?\s*없|아무것도\s*안/, title: '안 될 때 확인할 것', text: '① 초록 깃발(또는 말한 키)을 눌러 실행했는지 ② 블록들이 모자 블록 아래에 붙어 있는지 ③ 로봇이면 연결 버튼으로 연결됐는지, 배터리가 충분한지 확인해 보세요. 모터 속도가 30%보다 낮으면 바퀴가 안 돌 수 있어요.', ex: ['로봇 앞으로 2초 가', '계속 마우스를 따라가']},
    {re: /한쪽만|한\s*쪽만|하나만\s*돌/, title: '바퀴가 한쪽만 돌 때', text: '속도가 낮으면(30% 아래) 한쪽만 돌 수 있어요. 속도를 50% 이상으로 올리고, 모터 선이 잘 꽂혔는지·배터리를 확인해 보세요. 앞으로 가려면 1번은 오른쪽, 2번은 왼쪽으로 돌려요.', ex: ['로봇 속도 60%로 해', '로봇 앞으로 2초 가']},
    {re: /좌표/, title: '좌표', text: 'x는 가로 위치(왼쪽 끝 -240 ~ 오른쪽 끝 240), y는 세로 위치(아래 끝 -180 ~ 위 끝 180)예요. 무대 한가운데가 x 0, y 0이에요.', ex: ['x 100 y 50으로 가', 'x좌표를 10만큼 바꾸기', '가운데로 가']},
    {re: /센서\s*값|값(?:은|을)?\s*어디/, title: '센서 값 보기', text: '블록 목록에서 값 블록(동그란 블록) 옆의 체크 칸을 켜면 무대에 값이 보여요. 말로는 ‘밝기 값을 계속 말해’처럼 할 수 있어요.', ex: ['밝기 값을 계속 말해', '근접센서 값 보여줘']},
    {re: /라인\s*트레이서|선\s*(?:을\s*)?따라/, title: '라인트레이서', text: '큐브로이드 키트에는 바닥의 선을 읽는 센서가 없어서 라인트레이서는 만들 수 없어요. 근접 센서로 장애물을 피하는 자율주행을 만들어 보세요.', ex: ['자율주행 자동차 만들어줘', '장애물 있으면 돌고 없으면 앞으로 가']},
    {re: /반복.*차이|차이.*반복/, title: '‘N번 반복하기’와 ‘무한 반복하기’', text: '‘N번 반복하기’는 정한 횟수만큼 하고 다음 블록으로 넘어가요. ‘무한 반복하기’는 멈추기 전까지 계속해서, 뒤에 블록을 붙일 수 없어요.', ex: ['10번 반복해서 점프해', '계속 빙글빙글 돌아', '벽에 닿으면 튕기게']},
    {re: /연결|블루투스|페어링/, title: '로봇 연결', text: '로봇 블록을 쓰면 확장이 자동으로 추가돼요. 블록 목록 위의 연결(주황색) 버튼을 누르고 로봇 이름을 골라 연결해요. 로봇 전원이 켜져 있어야 해요.', ex: ['로봇 앞으로 3초 가', '빨간 불 켜', 'LED에 하트 보여줘']},
    {re: /캐릭터|스프라이트|다른\s*(?:그림|동물)/, title: '다른 캐릭터(스프라이트)', text: '오른쪽 아래 ‘스프라이트 고르기’ 버튼으로 다른 캐릭터를 추가할 수 있어요. 말로 만든 블록은 지금 고른 캐릭터에 들어가요.', ex: ['스페이스 키를 누르면 점프해', '계속 마우스를 따라가']},
    {re: /카메라|손\s*인식|얼굴\s*인식|포즈|머신\s*러닝|인공\s*지능|AI/i, title: '카메라·AI 인식', text: '확장 추가(왼쪽 아래)에서 손 인식·얼굴 인식·머신러닝 확장을 추가해 쓸 수 있어요. 말로 코딩은 아직 이 블록들을 만들지 못해요.', ex: ['마우스 따라다니게', '게임 만들어줘']},
    {re: /게임|플래피|마리오|슈팅|미로|벽돌/, title: '게임 만들기', text: '게임은 규칙을 한 문장씩 나눠 말하면 만들 수 있어요. 예를 누르면 입력창에 들어가요.', ex: ['고양이 잡기 게임 만들어줘', '방향키로 움직이게', '마우스에 닿으면 점수 1 올려', '점수가 10점 되면 이겼다라고 말하고 멈춰', '사과가 하늘에서 떨어지게']},
    {re: /펜|그리기|그림|선/, title: '펜(그리기)', text: '펜을 내리면 움직이는 곳마다 선이 그려져요.', ex: ['정사각형 그려줘', '펜으로 별 그려줘', '지나간 자리에 선 그리기']},
    {re: /사운드\s*블록|부저|버저|음\s*연주|도레미|멜로디|계이름|피아노|연주/, title: '사운드 블록(로봇)', text: '큐브로이드 사운드 블록으로 음을 연주하거나 효과음을 낼 수 있어요.', ex: ['도레미 연주해', '경고음 내', '버튼을 누르면 삐 소리 내']},
    {re: /LED|엘이디|점판|전광판|표정|이모티콘/i, title: 'LED 블록(로봇)', text: '8×8 LED에 그림·숫자·글자를 띄울 수 있어요.', ex: ['LED에 웃는 얼굴 보여줘', 'LED에 숫자 3 보여줘', '화면 지우기']},
    {re: /불빛|조명|램프|마스터|신호등|불\s*(?:켜|끄|색)/, title: '마스터 블록 불빛(로봇)', text: '마스터 블록의 불빛을 켜고 끌 수 있어요. 말로는 빨강·주황·노랑·초록·파랑·하늘·보라·분홍·흰색을 쓸 수 있고, 블록의 색 칸을 누르면 어떤 색이든 골라요.', ex: ['빨간 불 켜', '불 꺼', '신호등 만들기']},
    {re: /모터|바퀴|자동차|로봇|운전|주행/, title: '모터(로봇)', text: '모터 두 개로 자동차처럼 움직여요. 앞으로 = 1번 오른쪽 + 2번 왼쪽이에요.', ex: ['모터 2개로 자동차 만들기', '로봇 앞으로 3초 가', '로봇 오른쪽으로 돌아', '로봇 속도 50%로 해']},
    {re: /센서|장애물|근접|거리/, title: '근접 센서(로봇)', text: '근접 센서는 로봇 앞에 뭔가 가까이 있는지(있다/없다)를 알려 줘요. 자율주행·장애물 피하기·도난 경보기에 써요.', ex: ['장애물이 있으면 멈추고 없으면 앞으로 가', '자율주행 자동차 만들기', '로봇이 앞으로 가다가 앞에 뭐가 있으면 멈춰']},
    {re: /버튼|밝기|빛\s*센서|라이트|터치|어두/, title: '라이트·터치 센서(로봇)', text: '버튼이 눌렸는지, 주변이 얼마나 밝은지 알아요.', ex: ['손전등 만들기', '가로등 만들기', '어두워지면 흰 불 켜고 밝으면 불 꺼']},
    {re: /소리|야옹|음량|볼륨|노래/, title: '소리(고양이)', text: '스프라이트가 가진 소리를 내요.', ex: ['야옹 소리 내', '야옹 소리 세 번 내', '음량을 50으로 해']},
    {re: /신호|메시지|방송/, title: '신호(이벤트)', text: '스프라이트끼리 신호를 주고받아요.', ex: ['시작 신호 보내', '시작 신호를 받으면 숨어']},
    {re: /복제|복사/, title: '복제', text: '스프라이트를 복제해서 여러 개로 만들어요.', ex: ['나 자신 복제하기', '복제되었을 때 아무 데나 가']},
    {re: /묻|질문|대답|물어/, title: '묻고 답하기', text: '질문하고 대답을 받아요.', ex: ["'이름이 뭐야?'라고 묻고 기다려", '대답을 말해']},
    {re: /점수|변수/, title: '변수(점수)', text: '변수는 숫자나 글자를 담아 두는 이름 붙은 상자예요. 점수·목숨·시간처럼 바뀌는 값을 기억해요. ‘점수를 1 올려’처럼 말하면 번역기가 변수를 만들어요.', ex: ['시작하면 점수를 0으로 정해', '마우스에 닿으면 점수를 1 올려', '점수가 10점 되면 이겼다라고 말하고 멈춰']},
    {re: /반복|조건|만약|기다|멈추/, title: '제어', text: '반복하고, 조건에 따라 하고, 기다려요.', ex: ['10번 반복해서 점프해', '벽에 닿으면 튕겨', '마우스를 클릭할 때까지 기다려']},
    {re: /모양|크기|색|숨|보이|말하|생각|효과/, title: '형태', text: '말하기·모양·크기·색깔을 바꿔요.', ex: ['안녕이라고 말해', '다음 모양으로 바꿔', '크기를 150으로 해', '색깔 바꿔']},
    {re: /움직|이동|동작|걷|달리|점프|돌/, title: '동작', text: '움직이고, 돌고, 따라가요.', ex: ['10만큼 움직여', '방향키로 움직이게', '계속 마우스를 따라가', '스페이스 키를 누르면 점프해']}
];
const RE_QUESTION = /어디에\s*써|어디다\s*써|어떻게\s*써|뭐에\s*써|언제\s*써|뭐뭐|뭐가?\s*있어|강아지로\s*바꿔|스프라이트\s*바꿔|캐릭터\s*바꿔|두\s*대|탁구|핑퐁|핸드폰으로|휴대폰으로|블록\s*만들어져|말하면\s*블록|마인\s*크래프트|3\s*[dD]로|온라인|블루투스|연결이?\s*안|왜\s*안|안\s*움직여|단위|몇\s*cm|센티|진짜\s*목소리|목소리로|음성으로|로봇도\s*똑같이|고양이(?:가)?\s*움직이는\s*대로|[?？]\s*$|뭔지|뭐지|뭐\s*하는|뭐하는|몇이에요|몇이야|몇인가요|않는데요?[ㅠㅜ.!~]*$|안\s*(?:켜|꺼|돌|움직|나와)[가-힣]*[ㅠㅜ.!~]*$|몇까지|얼마까지|어떻게\s*(?:해요|하나요|하죠)|왜\s*그래|왜그래|왜\s*이래|어디서\s*봐|뭐야\s*안|안\s*돼|안돼|뭐에요|뭐예요|차이(?:가|는|점)?|어케|어떻게\s*해|가능해|가능한가|할\s*수\s*있|되나요?|되니|있나요?|뭐야|뭐예요|뭐가\s*있|뭐\s*할\s*수|어떻게|알려\s*줘|도와\s*줘|사용법|쓰는\s*법|쓰는법|사용\s*가능/;
/**
 * 질문이면 답을 돌려준다(아니면 null). 명령인지 질문인지는 부르는 쪽이 번역 결과와 함께 판단한다.
 * @param {string} text 학생이 친 말
 * @returns {null|object} {title, text, examples}
 */
const RE_GOAL = /3\s*D\s*게임|쓰리디|(?:(?:검은|까만|검정)\s*)?선\s*(?:을\s*)?따라\s*(?:가|달리|움직)|(?:게임|앱|프로그램|마리오|플래피|슈팅|미로|벽돌\s*깨기|테트리스|퀴즈|라인\s*트레이서)[가-힣\s]*(?:만들|하고\s*싶|만들고\s*싶|같은)?|(?:카메라|손|얼굴)[가-힣\s]*(?:인식|따라)/;
const answerQuestion = (text, lang) => {
    if (lang && lang !== 'ko' && LANGS[lang] && LANGS[lang].answer) return LANGS[lang].answer(text);
    const t = String(text || '');
    if (!RE_QUESTION.test(t) && !RE_GOAL.test(t)) return null;
    const h = HELP.find(x => x.re.test(t));
    const strong = /어디에\s*써|어디다\s*써|뭐에\s*써|언제\s*써|어떻게\s*써|뭐뭐|뭐가?\s*있어|강아지로|스프라이트\s*바꿔|캐릭터\s*바꿔|두\s*대|탁구|핑퐁|핸드폰|휴대폰|블록\s*만들어져|말하면\s*블록|마인\s*크래프트|3\s*[dD]|온라인|블루투스|연결이?\s*안|왜\s*안|안\s*움직여|몇이면|단위|cm야|진짜\s*목소리|똑같이|움직이는\s*대로|왜|어떻게|어케|어디서|몇까지|뭔지|뭐\s*하는|뭐하는|몇이에요|몇이야|몇인가요|얼마까지|뭐에요|뭐예요|차이|되나요|돼\?|할\s*수\s*있어\?|가능해\?/.test(t) && !/(?:해줘|해\s*줘|만들어|하게)\s*[?？]?$/.test(t);
    if (h) return {title: `${h.title} — 이렇게 말해 보세요`, text: h.text, examples: h.ex, strong};
    return {strong: false, title: '이런 걸 말로 만들 수 있어요', text: '고양이(동작·형태·소리·점수)와 큐브로이드 로봇(모터·LED·불빛·소리·센서)을 말로 만들 수 있어요. 아래를 눌러 보세요.',
        examples: ['스페이스 키를 누르면 점프해', '마우스에 닿으면 점수를 1 올려', '모터 2개로 자동차 만들기', 'LED에 웃는 얼굴 보여줘', '도레미 연주해']};
};

// ═══ 확장 이름으로 부르기(2026-09-24 대표 데브 확인: "사운드 블록 넣어보자"를 못 알아들음) ═══
// 학생·선생님은 갤러리 카드 이름으로 말한다("LED 블록 써보자"). 할 일이 빠진 말이라 추측하지 않고,
// 그 확장으로 할 수 있는 말을 파란 도움 상자(누르면 입력칸에 들어감)로 보여 준다.
// 규칙이 못 알아들었을 때만 부른다(부르는 쪽이 판단) — 지금 잘 되는 말은 그대로.
// 예시 문장은 전부 test/vibe/run-ext-help.js 가 "누르면 블록이 제대로 생기는지" 확인한다. 예시를 바꾸면 그 시험도 돌릴 것.
const EXT_HELP_KO = [
    {id: 'sound', re: '사운드|부저|버저|스피커', title: '사운드 블록', text: '로봇 스피커로 음을 연주하거나 삐 소리(효과음)를 내요.',
        fresh: ['도레미 연주해', '작은 별 연주해', '버튼을 누르면 삐 소리 내'], add: ['도레미 연주도 해줘', '끝나면 삐 소리 내줘', '작은 별도 연주해줘']},
    {id: 'led', re: 'LED|엘이디|도트\\s*매트릭스|매트릭스|전광판|점판', title: 'LED 블록', text: '8×8 LED 화면에 그림·숫자·영어 글자를 띄워요.',
        fresh: ['LED에 하트 보여줘', 'LED에 숫자 3 보여줘', 'LED에 웃는 얼굴 보여줘'], add: ['LED에 웃는 얼굴도 보여줘', '끝나면 LED에 하트 보여줘']},
    {id: 'master', re: '마스터|불빛|램프|조명', title: '마스터 블록(불빛)', text: '마스터 블록의 불빛을 여러 색으로 켜고 꺼요.',
        fresh: ['빨간 불 켜', '신호등 만들기', '로봇 춤추게 해줘'], add: ['빨간 불도 켜줘', '끝나면 초록 불 켜줘', '끝나면 불 꺼줘']},
    {id: 'prox', re: '근접(?:\\s*센서)?|적외선(?:\\s*센서)?|거리\\s*센서|장애물\\s*센서', title: '근접 센서 블록', text: '로봇 앞에 뭔가 가까이 있는지(있다/없다)를 알아요. 거리를 숫자로 재지는 못해요.',
        fresh: ['장애물이 있으면 멈추고 없으면 앞으로 가', '자율주행 자동차 만들기', '앞에 손을 대면 삐 소리 내'], addMotor: ['장애물 있으면 멈추게 해줘', '달리다가 장애물 있으면 멈춰']},
    {id: 'touch', re: '라이트\\s*터치(?:\\s*센서)?|터치\\s*센서|터치\\s*버튼|버튼|밝기\\s*센서|빛\\s*센서|조도\\s*센서|라이트\\s*센서|라이트', title: '라이트 터치 센서 블록', text: '터치 버튼이 눌렸는지, 주변이 얼마나 밝은지 알아요.',
        fresh: ['버튼을 누르면 삐 소리 내', '손전등 만들기', '어두워지면 흰 불 켜고 밝으면 불 꺼'], addMotor: ['버튼 누르면 출발하게 해줘']},
    {id: 'motor', re: '(?:DC\\s*)?모터|바퀴', title: 'DC 모터 블록', text: '모터 두 개로 자동차처럼 움직여요. 앞으로 = 1번 오른쪽 + 2번 왼쪽이에요.',
        fresh: ['모터 2개로 자동차 만들기', '로봇 앞으로 3초 가', '로봇 춤추게 해줘'],
        addMotor: ['더 빨리', '3번 반복해', '로봇 속도 50%로 해'], hasText: '모터 블록은 이미 들어 있어요. 이렇게 바꿔 보세요.'},
    {id: 'sensor', re: '센서', title: '센서 블록', text: '근접 센서(앞에 뭐가 있나)와 라이트 터치 센서(버튼·밝기)가 있어요.',
        fresh: ['장애물이 있으면 멈추고 없으면 앞으로 가', '버튼을 누르면 삐 소리 내', '어두워지면 흰 불 켜고 밝으면 불 꺼'], addMotor: ['장애물 있으면 멈추게 해줘', '버튼 누르면 출발하게 해줘']},
    {id: 'ai', re: '머신\\s*러닝|AI\\s*학습|인공\\s*지능|손\\s*인식|얼굴\\s*인식|포즈|카메라|티처블', title: '카메라·AI 확장', text: '카메라로 얼굴·손·몸·색·물건을 알아보는 블록이에요.',
        fresh: ['웃으면 점프해', '검지 손가락 따라가', '카메라에 컵이 보이면 야옹 소리 내']},
    {id: 'weather', re: '날씨', title: '날씨 블록', text: '전 세계 도시의 실시간 날씨·기온·미세먼지를 가져와요.', fresh: ['서울 기온 말해', '비가 오면 우산 챙기라고 말해', '일주일 기온 차트 보여줘']},
    {id: 'chart', re: '(?:데이터\\s*)?차트|그래프', title: '데이터 차트 블록', text: '데이터를 모아 막대·꺾은선·원그래프로 보고 평균·합계를 구해요.', fresh: ['사과 5 바나나 3 포도 8 막대 그래프로 보여줘', '평균 말해', '점수를 차트에 추가해']},
    {id: 'gpt', re: '챗\\s*GPT|챗\\s*지피티|GPT', title: '챗GPT 블록', text: 'AI에게 물어보고 답을 받아요. 선생님의 OpenAI API 키가 필요해요.', fresh: ['챗GPT 챗봇 만들어줘', '대답을 챗GPT한테 물어보고 말해']},
    // 스크래치 ‘소리’ — 로봇을 만들던 중이면 사운드 블록으로 본다
    {id: 'catsound', re: '소리|음악|멜로디|노래|효과음', robotAs: 'sound', title: '소리 블록', text: '스프라이트가 가진 소리를 내요. 로봇 스피커로 음을 내려면 ‘도레미 연주해’처럼 말해요.',
        fresh: ['야옹 소리 내', '클릭하면 야옹 소리 내', '도레미 연주해'], add: ['야옹 소리도 내줘', '도레미 연주도 해줘']}
];
const RE_EXT_LEAD = '^(?:(?:그럼|이제|이번엔|이번에는|이번엔|다음엔|다음에는|다음은|여기|여기에|여기다|거기|거기에|자|우리|같이|한번|한\\s*번|좀|또|그리고|큐브로이드|로봇|로봇의|로봇에)\\s*)*';
const RE_EXT_TAIL = '\\s*(?:블록|블럭|확장|기능)?\\s*(?:들)?(?:도|을|를|이랑|랑|하고|은|는|이|가|으로|로)?\\s*(?:(?:한번|한\\s*번|좀|같이|이제|더|또)\\s*)*' +
    '(?<verb>(?:넣|써|쓰|사용|추가|붙|달|연결|활용|이용|가져|꺼내|해\\s*보)[가-힣]*(?:\\s*(?:볼까|보자|줘|주세요|봐|요))*)?\\s*[.!?~]*$';
const extensionHelpKo = (text, curIr) => {
    const t = fixKo(String(text || '')).trim();
    const ops = curIr ? opsOf(curIr) : [];
    const robot = ops.some(op => /^cubroid/.test(op));
    const motor = ops.some(op => /_dcMotor/.test(op));
    for (const e0 of EXT_HELP_KO) {
        const m = new RegExp(RE_EXT_LEAD + `(?:${e0.re})` + RE_EXT_TAIL, 'i').exec(t);
        // 할 일 말(넣어·써)이 있거나 '블록/확장'이라고 불렀을 때만 — "소리"만 친 건 여기서 받지 않는다
        // "마스터 블록으로 ~", "근접 센서로 ~" — 뒤의 할 일을 규칙이 못 알아들었을 때(부르는 쪽이 판단) 그 확장 예시를 보여 준다
        const mWith = new RegExp(RE_EXT_LEAD + `(?:${e0.re})\\s*(?:블록|블럭|확장)?\\s*(?:으로|로|을\\s*(?:써서|이용해서|사용해서)|를\\s*(?:써서|이용해서|사용해서))\\s+\\S`, 'i').exec(String(text || '').trim());
        const withOk = mWith && (/블록|블럭|확장|센서/.test(mWith[0]));
        if (!withOk && (!m || !(m.groups.verb || /블록|블럭|확장/.test(t)))) continue;
        const e = e0.robotAs && robot ? EXT_HELP_KO.find(x => x.id === e0.robotAs) : e0;
        const ex = !curIr ? e.fresh : (motor && e.addMotor) ? e.addMotor : (e.add || e.fresh);
        const text2 = motor && e.hasText ? e.hasText : `${e.text} 이렇게 말해 보세요.`;
        return {title: e.title, text: text2, examples: ex.slice(), strong: true, ext: e.id};
    }
    return null;
};
// ═══ AI·데이터 확장 지킴이(2026-09-24 대표 "이거 돼?") ═══
// "얼굴 인식해서 웃으면 말해" → 고양이가 그 문장을 그대로 말함, "색 감지해서 빨간색이면 멈춰" → 모두 멈추기 — 틀린 번역이 '모르겠어요'보다 나쁘다.
// 이 말들이 나왔는데 그 확장 블록이 결과에 하나도 없으면(= 아직 말로 못 만드는 것) 블록 대신 도움 상자를 보여 준다.
// 지원을 늘리면(그 확장 블록을 만들면) 지킴이는 저절로 비켜난다. ops = 이 확장의 opcode 접두사.
const AI_EXT = [
    {id: 'weather', ops: /^cubroidweather_/, ko: /날씨|기온|온도|미세\s*먼지|초미세|강수|습도|풍속|바람\s*세기|자외선/, en: /\b(?:weather|temperature|humidity|fine\s+dust|air\s+quality|rain(?:fall)?\s+(?:chance|amount)|wind\s+speed|uv\s+index)\b/,
        ko_t: ['날씨 블록', '전 세계 도시의 실시간 날씨·기온·미세먼지를 가져와요.'], en_t: ['Weather blocks', 'Live weather, temperature and air quality for cities around the world.']},
    {id: 'chart', ops: /^cubroidchart_/, ko: /차트|그래프|막대\s*그래프|꺾은\s*선|원\s*그래프|평균|최댓값|최솟값|합계/, en: /\b(?:chart|graph|bar\s+graph|line\s+graph|pie\s+chart|average|mean\s+of|maximum|minimum)\b/,
        ko_t: ['데이터 차트 블록', '데이터를 모아 막대·꺾은선·원그래프로 보고 평균·합계·최댓값을 구해요.'], en_t: ['Data chart blocks', 'Collect data, show bar/line/pie charts and get the average, sum or maximum.']},
    {id: 'chatgpt', ops: /^cubroidChatgpt_/, ko: /챗\s*GPT|챗\s*지피티|chat\s*gpt|GPT|지피티|인공지능(?:한테|에게)\s*물어|AI(?:한테|에게)\s*물어/i, en: /\b(?:chat\s*gpt|gpt|ask\s+(?:the\s+)?ai)\b/,
        ko_t: ['챗GPT 블록', 'AI에게 물어보고 답을 받아요. 선생님의 OpenAI API 키가 필요해요.'], en_t: ['ChatGPT blocks', 'Ask the AI and get an answer. Needs the teacher’s OpenAI API key.']},
    {id: 'dalle', ops: /^cubroidDalle3_/, ko: /달리\s*3|DALL|AI\s*(?:이미지|그림)|그림을?\s*그려\s*(?:달라|줘)\s*(?:AI|인공지능)|(?:AI|인공지능)(?:가|로|한테|에게|으로)?\s*(?:[가-힣]+\s*){0,2}그림/i, en: /\b(?:dall-?e|ai\s+(?:image|picture|drawing)|generate\s+an?\s+(?:image|picture))\b/,
        ko_t: ['AI 이미지 블록', '글로 설명하면 AI가 그림을 그려요. 선생님의 OpenAI API 키가 필요해요.'], en_t: ['AI image blocks', 'Describe a picture and the AI draws it. Needs the teacher’s OpenAI API key.']},
    {id: 'color', ops: /^cubroidcolor_/, ko: /색(?:깔)?\s*(?:감지|인식|알아\s*(?:내|맞))|카메라[^.]*색/, en: /\b(?:colou?r\s+(?:detection|sensor|recogni[sz]\w*)|detect\s+(?:the\s+)?colou?r)\b/,
        ko_t: ['색상 감지 블록', '카메라에 색을 보여 주면 AI가 어떤 색인지 알아내요.'], en_t: ['Colour detection blocks', 'Show a colour to the camera and the AI tells which colour it is.']},
    {id: 'object', ops: /^cubroidobject_/, ko: /사물\s*인식|물체\s*인식|물건\s*인식|(?:자전거|바나나|의자|컵|사과)[가-힣\s]*(?:인식|보이면|찾으면)/, en: /\b(?:object\s+(?:detection|recogni[sz]\w*)|detect\s+(?:an?\s+)?objects?)\b/,
        ko_t: ['사물 인식 블록', '자전거·고양이·바나나 같은 물건을 카메라로 찾아내요.'], en_t: ['Object recognition blocks', 'Finds everyday things like a bicycle, cat or banana with the camera.']},
    {id: 'face', ops: /^(?:facemesh2scratch|facerecognition|posenet2scratch)_/, ko: /얼굴\s*(?:인식|을?\s*찾|을?\s*따라|이?\s*보이면|을?\s*등록)|이름\s*(?:을\s*)?맞|(?:내|제|사람|친구)\s*표정|표정\s*(?:인식|을?\s*(?:알|읽|따라))|웃으면|웃는\s*얼굴이면|찡그리|감정\s*(?:인식|을?\s*알)|나이\s*(?:인식|맞)|성별/, en: /\b(?:face\s+(?:recognition|detection|tracking|mesh)|register\w*\s+(?:my\s+|a\s+)?face|recogni[sz]e\s+my\s+name|facial\s+expression|when\s+i\s+smile|if\s+i\s+smile|emotion|detect\s+(?:my\s+)?face)\b/,
        ko_t: ['얼굴 인식 블록', 'AI가 카메라로 얼굴의 특징을 읽고 표정과 감정을 알려 줘요.'], en_t: ['Face recognition blocks', 'The AI reads your face with the camera and tells the expression and emotion.']},
    {id: 'hand', ops: /^(?:handpose2scratch|xcxMpHand|posenet2scratch)_/, ko: /손\s*(?:인식|모양|동작|을?\s*따라|이?\s*보이면)|손가락|주먹\s*(?:쥐면|을?\s*보이면)|브이\s*(?:하면|모양)/, en: /\b(?:hand\s+(?:recognition|tracking|pose|gesture)|fingers?|make\s+a\s+fist)\b/,
        ko_t: ['손 인식 블록', '카메라로 손가락 위치를 알아내요.'], en_t: ['Hand recognition blocks', 'Finds your fingers with the camera.']},
    {id: 'pose', ops: /^(?:posenet2scratch|tmpose2scratch)_/, ko: /포즈|자세\s*(?:인식|를?\s*따라)|몸\s*(?:인식|동작|을?\s*따라)|코\s*위치|손목|어깨/, en: /\b(?:pose|body\s+(?:tracking|parts?)|nose\s+position|wrist|shoulder)\b/,
        ko_t: ['포즈 블록', '카메라로 몸의 각 부분(코·손목·어깨…) 위치를 알아내요.'], en_t: ['Pose blocks', 'Finds where each body part (nose, wrist, shoulder …) is with the camera.']},
    {id: 'speech', ops: /^(?:speech2text|speech2scratch)_/, ko: /음성\s*인식|말(?:을)?\s*알아\s*듣|목소리\s*(?:인식|를?\s*알아)|받아\s*쓰기|말하면\s*(?:글자|글로|알아)/, en: /\b(?:speech\s+recognition|voice\s+recognition|speech\s+to\s+text|recogni[sz]e\s+(?:my\s+)?(?:voice|speech)|when\s+i\s+say)\b/,
        ko_t: ['음성 인식 블록', '마이크로 들은 말을 글자로 바꿔요.'], en_t: ['Speech recognition blocks', 'Turns what the microphone hears into text.']},
    {id: 'ml', ops: /^(?:ml2scratch|ic2scratch|nn2scratch|tm2scratch)_/, ko: /머신\s*러닝|이미지\s*분류|AI\s*학습|학습시|학습\s*시켜|티처블|레이블|분류\s*(?:해|하기|모델)/i, en: /\b(?:machine\s+learning|image\s+classif\w*|teachable\s+machine|train(?:ing)?\s+(?:the\s+)?(?:ai|model)|label)\b/,
        ko_t: ['머신러닝·이미지 분류 블록', '카메라에 비친 것을 내가 가르친 대로 알아맞혀요.'], en_t: ['Machine learning / image classification', 'Recognises what the camera sees, the way you trained it.']}
];
const AI_EXT_EX = {"weather":[["서울 기온 말해","비가 오면 우산 챙기라고 말해","일주일 기온 차트 보여줘"],["say the temperature in Seoul","if it rains say take an umbrella","show a line chart of the week's temperatures"]],"chart":[["사과 5 바나나 3 포도 8 막대 그래프로 보여줘","평균 말해","점수를 차트에 추가해"],["show a bar chart of apple 5 banana 3 grape 8","say the average","hide the chart"]],"chatgpt":[["챗GPT 챗봇 만들어줘","대답을 챗GPT한테 물어보고 말해"],["make a chatgpt chatbot","ask chatgpt \"why is the sky blue\" and say the answer"]],"dalle":[["AI로 우주 고양이 그림 그려줘","어떤 그림을 그릴지 물어보고 그려줘"],["have the ai draw a picture of a space cat"]],"color":[["색 감지해서 빨간색이면 멈춰","감지된 색 말해"],["if the camera sees red stop","say the detected color"]],"object":[["카메라에 컵이 보이면 야옹 소리 내","사물 인식해서 이름 말해"],["if the camera sees a cup play meow","say the detected object"]],"face":[["웃으면 점프해","얼굴이 보이면 안녕 말해","내 나이 맞혀"],["when i smile jump","guess my age"]],"hand":[["검지 손가락 따라가","카메라로 내 손 따라가게"],["follow my index finger"]],"pose":[["코를 따라가","오른손 따라다녀"],["follow my nose"]],"speech":[["음성인식으로 '점프'라고 말하면 점프해","내 말 따라 말해"],["when i say jump, jump","repeat what i say"]],"ml":[["레이블 1이 인식되면 점프해","예측 결과 말해","이미지 분류 결과 말해"],["when label 1 is detected jump","say the prediction","say what the camera sees"]]};
// 규칙 모듈(vibe-aiext.js)이 등록됐는지 — 있으면 "이렇게 말해 보세요 + 되는 예시", 없으면 "아직 못 만들어요"
const aiExtGuard = (text, lang, ir) => {
    const en = lang === 'en';
    const t = String(text || '');
    const ops = ir ? opsOf(ir).concat(ir.scripts.map(sc => (sc.hat && sc.hat.opcode) || '')) : []; // 모자 블록(‘레이블 1 받았을 때’)도
    const hit = AI_EXT.find(e => (en ? e.en : e.ko).test(en ? t.toLowerCase() : t) && !ops.some(op => e.ops.test(op)));
    if (!hit) return null;
    const [title, what] = en ? hit.en_t : hit.ko_t;
    const ready = (EXTRA_INTENTS[en ? 'en' : 'ko'] || []).length > 0 && AI_EXT_EX[hit.id];
    if (ready) {
        return {title, strong: true, ext: hit.id, examples: AI_EXT_EX[hit.id][en ? 1 : 0].slice(),
            text: en ? `${what} I couldn’t turn this exact sentence into blocks. Try saying it like this:` : `${what} 이 문장은 블록으로 바꾸지 못했어요. 이렇게 말해 보세요.`};
    }
    return {title, strong: true, ext: hit.id, examples: [],
        text: en ? `${what} Vibe coding can’t build these blocks yet — add them from “Add Extension” (bottom left) and build that part by hand.` :
            `${what} 말로 코딩은 아직 이 블록을 못 만들어요. 왼쪽 아래 ‘확장 추가’에서 넣고 그 부분은 직접 만들어 보세요.`};
};
const extensionHelp = (text, lang, curIr) => {
    if (lang && lang !== 'ko') return (LANGS[lang] && LANGS[lang].extHelp) ? LANGS[lang].extHelp(text, curIr) : null;
    return extensionHelpKo(text, curIr);
};

const _kit = {
    act, num, txt, menu, fld, inT, inN, V, join, opN, setVar, chVar, sayOf, waitS, loopAll, toNum, fmt, keyValue,
    M1, M2, motor, motorOff, waitN, drive, stopBoth, colorOn, colorOff, obstacle, button, bright, noteBlock, beep, SOLFA,
    LED_IMG, LED_NUM, LED_ABC, walk, walkNode, clone, opsOf, famOf, numberItems, setNumber, jumpSet, insertWaits, hasCond,
    appendPoint, toGlide, flattenLoops, tidyStructure, autoLoop, fixCaps, collectAsks, collectExtensions, collectBroadcasts, collectLists,
    KEYDIR, RE_NUM, EXT_KO, needsLoop, moveGapWaits, dedupeStops, hoistSpeed, robotElse, hintAnimation, hintBlink, nest, splitSentences, NOTE, HEX_KO, COLOR_HEX_KO: null
};
const EXTRA_REFINE = [];
const registerRefine = f => { EXTRA_REFINE.push(f); };
const refineOut = (prev, text, ctx) => {
    let r = null;
    for (const f of EXTRA_REFINE) { r = f(prev, text, ctx); if (r) break; }
    if (!r) r = refine(prev, text, ctx);
    if (r && r.ir) runPost(r.ir, r.ir.lang || (prev && prev.lang) || 'ko');
    return r;
};
module.exports = {isIntact, translate, refine: refineOut, runPost, registerIntents, registerPost, registerRefine, collectExtensions, _extraIntents: EXTRA_INTENTS, setAsk, answerQuestion, extensionHelp, EXT_HELP_KO, aiExtGuard, AI_EXT, _aiExtEx: AI_EXT_EX, signature, describe, buildBlocks, applyToVm, undoApply, splitSentences, registerLang, _kit};
